/**
 * Shipping App - Main Controller
 * Handles the shipping page flow:
 * 1. config() - Fetch cart items
 * 2. Render products + address form
 * 3. User inputs: address, recipient, delivery date
 * 4. Multi-ship support
 * 5. generalGift(data) - Proceed to checkout
 * @updated 2026-06-17 FB902: restore delivery date/time after re-render in single mode
 */
(function($, window) {
  'use strict';

  // Prefecture name to code mapping (shared constant)
  var PREFECTURE_MAP = {
    '北海道': 'JP-01', '青森県': 'JP-02', '岩手県': 'JP-03', '宮城県': 'JP-04',
    '秋田県': 'JP-05', '山形県': 'JP-06', '福島県': 'JP-07', '茨城県': 'JP-08',
    '栃木県': 'JP-09', '群馬県': 'JP-10', '埼玉県': 'JP-11', '千葉県': 'JP-12',
    '東京都': 'JP-13', '神奈川県': 'JP-14', '新潟県': 'JP-15', '富山県': 'JP-16',
    '石川県': 'JP-17', '福井県': 'JP-18', '山梨県': 'JP-19', '長野県': 'JP-20',
    '岐阜県': 'JP-21', '静岡県': 'JP-22', '愛知県': 'JP-23', '三重県': 'JP-24',
    '滋賀県': 'JP-25', '京都府': 'JP-26', '大阪府': 'JP-27', '兵庫県': 'JP-28',
    '奈良県': 'JP-29', '和歌山県': 'JP-30', '鳥取県': 'JP-31', '島根県': 'JP-32',
    '岡山県': 'JP-33', '広島県': 'JP-34', '山口県': 'JP-35', '徳島県': 'JP-36',
    '香川県': 'JP-37', '愛媛県': 'JP-38', '高知県': 'JP-39', '福岡県': 'JP-40',
    '佐賀県': 'JP-41', '長崎県': 'JP-42', '熊本県': 'JP-43', '大分県': 'JP-44',
    '宮崎県': 'JP-45', '鹿児島県': 'JP-46', '沖縄県': 'JP-47'
  };

  // 2026-11-04 13:00 JST 以降は翌々日出荷（出荷が1日延びる）。API の BASE_LEAD_TIME と合わせる
  var EXTENDED_SHIP_FROM_MS = Date.UTC(2026, 10, 4, 4, 0, 0); // 2026-11-04 13:00 JST = 04:00 UTC

  /**
   * お届け希望日の最短オフセット（今日から何日後か）
   * 0:00-12:59 JST → +2 / 13:00-23:59 JST → +3。2026-11-04 13:00 JST 以降はそれぞれ +1
   */
  function getDeliveryStartOffset(now, jstHour) {
    var offset = jstHour >= 13 ? 3 : 2;
    if (now.getTime() >= EXTENDED_SHIP_FROM_MS) offset += 1;
    return offset;
  }

  // Shipping product variant IDs by temperature zone
  // Prefer values from shipment-master metaobject (window.shippingVariantIds),
  // fallback to defaults when missing.
  var DEFAULT_SHIPPING_VARIANT_IDS = {
    ambient: 53026172240237,  // 常温・冷蔵便
    frozen: 53026172273005,   // 冷凍便
    mail: 53026172305773      // メール便
  };

  // 冷凍便発送不可地域（郵便番号、ハイフンなし7桁）
  var FROZEN_RESTRICTED_ZIPS = [
    '1001701', // 伊豆諸島・小笠原諸島周辺
    '1000301',
    '1000511',
    '1001301',
    '1002100',
    '1002101',
    '1002211'
  ];

  // 冷凍便発送不可地域（住所キーワード）
  var FROZEN_RESTRICTED_ADDRESS_KEYWORDS = [
    '青ヶ島村',
    '利島村',
    '式根島',
    '御蔵島村',
    '小笠原村'
  ];

  function toShippingVariantId(value) {
    if (value === undefined || value === null || value === '') return null;
    if (typeof value === 'object') {
      if (value.id !== undefined && value.id !== null) return toShippingVariantId(value.id);
      if (value.shopifyProductVariantId !== undefined && value.shopifyProductVariantId !== null) {
        return toShippingVariantId(value.shopifyProductVariantId);
      }
      return null;
    }
    var text = String(value);
    var num = parseInt(text, 10);
    if (!isFinite(num) || num <= 0) {
      var match = text.match(/(\d+)(?!.*\d)/);
      if (match) num = parseInt(match[1], 10);
    }
    return isFinite(num) && num > 0 ? num : null;
  }

  function resolveShippingVariantIds(config) {
    var source = config || {};
    return {
      ambient: toShippingVariantId(source.ambient) || DEFAULT_SHIPPING_VARIANT_IDS.ambient,
      frozen: toShippingVariantId(source.frozen) || DEFAULT_SHIPPING_VARIANT_IDS.frozen,
      mail: toShippingVariantId(source.mail) || DEFAULT_SHIPPING_VARIANT_IDS.mail
    };
  }

  var SHIPPING_VARIANT_IDS = resolveShippingVariantIds(window.shippingVariantIds);
  var INTEROFFICE_SHIPPING_TITLE = '社内便';

  /**
   * Format a numeric amount as Japanese yen: "X,XXX円"
   * Standalone utility — safe to call from any scope (methods, callbacks, etc.)
   */
  function formatMoneyYen(value) {
    var amount = parseInt(value, 10) || 0;
    return amount.toLocaleString('ja-JP') + '円';
  }

  function renderDiscountPricePair(originalHtml, discountedHtml) {
    return (
      '<s class="price price--original">' + originalHtml + '</s>' +
      '&nbsp;&nbsp;' +
      '<span class="price price--discounted">' + discountedHtml + '</span>'
    );
  }

  function buildInterofficeRateDetails() {
    return [{
      title: INTEROFFICE_SHIPPING_TITLE,
      amount: 0,
      prefectureFee: 0,
      coolDeliveryFee: 0
    }];
  }

  function normalizeInterofficeAddresses(rawAddresses) {
    if (!Array.isArray(rawAddresses)) return [];

    return rawAddresses.map(function(raw, index) {
      if (!raw) return null;

      var prefecture = raw.prefecture || raw.province || '';
      return {
        id: raw.id ? String(raw.id) : ('interoffice_' + index),
        handle: raw.handle ? String(raw.handle) : '',
        lastName: raw.lastName || raw.lastname || '',
        firstName: raw.firstName || raw.firstname || '',
        zip: String(raw.zip || raw.zipcode || '').replace(/-/g, ''),
        prefecture: prefecture,
        prefectureCode: raw.prefectureCode || PREFECTURE_MAP[prefecture] || '',
        city: raw.city || '',
        address1: raw.address1 || '',
        address2: raw.address2 || '',
        company: raw.company || '',
        phone: String(raw.phone || raw.tel || '').replace(/-/g, ''),
        order: parseFloat(raw.order) || 999999
      };
    }).filter(function(addr) {
      return !!(addr && addr.zip && addr.prefecture && addr.city && addr.address1);
    }).sort(function(a, b) {
      return a.order - b.order;
    });
  }

  function buildAddress2WithCompany(address2, company) {
    var parts = [];
    var trimmedAddress2 = String(address2 || '').trim();
    var trimmedCompany = String(company || '').trim();
    if (trimmedAddress2) parts.push(trimmedAddress2);
    if (trimmedCompany) {
      var hasCompanyInAddress2 = trimmedAddress2 && trimmedAddress2.indexOf(trimmedCompany) !== -1;
      if (!hasCompanyInAddress2) {
        parts.push(trimmedCompany);
      }
    }
    return parts.join(' ');
  }

  function buildAddress1WithAddress2(address1, address2) {
    var base = String(address1 || '').trim();
    var suffix = String(address2 || '').trim();
    if (!base) return suffix;
    if (!suffix) return base;
    if (base.indexOf(suffix) !== -1) return base;
    return base + ' ' + suffix;
  }

  function hasPaidCustomerNumber(address) {
    var address2 = address && address.address2;
    return String(address2 || '').indexOf('顧客番号：') !== -1;
  }


  // ヨックモック default sender (used when orderer == recipient)
  var YOKUMOKU_SENDER = {
    lastName: 'ヨックモック公式オンラインショップ',
    firstName: '',
    zip: '1070062',
    prefecture: '東京都',
    prefectureCode: 'JP-13',
    city: '港区',
    address1: '南青山5-3-3',
    address2: '',
    phone: '0120220727'
  };

  function normalizePaidHandbagMasterVariants(rawVariants) {
    if (!Array.isArray(rawVariants)) return [];

    var normalized = [];
    rawVariants.forEach(function(raw) {
      if (!raw) return;

      var variantId = raw.shopifyProductVariantId !== undefined && raw.shopifyProductVariantId !== null
        ? String(raw.shopifyProductVariantId)
        : '';
      if (!variantId) return;

      var price = parseInt(raw.price, 10);
      if (!isFinite(price) || price <= 0) return;

      normalized.push({
        shopifyProductVariantId: variantId,
        title: raw.title ? String(raw.title) : '手提げ袋（紙）',
        price: String(price),
        image: raw.image ? String(raw.image) : '',
        priority: parseInt(raw.priority, 10) || 0,
        isRecommended: raw.isRecommended === true
      });
    });

    normalized.sort(function(a, b) {
      return (a.priority || 0) - (b.priority || 0);
    });

    return normalized;
  }

  function normalizeFreeHandbagVariant(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var variantId = raw.shopifyProductVariantId !== undefined && raw.shopifyProductVariantId !== null
      ? String(raw.shopifyProductVariantId)
      : '';
    if (!variantId) return null;

    return {
      shopifyProductVariantId: variantId,
      title: raw.name ? String(raw.name) : (raw.title ? String(raw.title) : '手提げ袋（クリア）'),
      price: '0',
      image: raw.image ? String(raw.image) : '',
      priority: -1
    };
  }

  var DEFAULT_PAID_HANDBAG_VARIANTS = [
    { shopifyProductVariantId: '53026172010861', title: '特小', price: '22', image: 'https://cdn.shopify.com/s/files/1/0942/6301/7837/files/paper_bag-20250801_png.webp?v=1771058426', priority: 10 },
    { shopifyProductVariantId: '53026172043629', title: '小', price: '33', image: 'https://cdn.shopify.com/s/files/1/0942/6301/7837/files/paper_bag-20250801_png.webp?v=1771058426', priority: 20 },
    { shopifyProductVariantId: '53026172076397', title: '中', price: '44', image: 'https://cdn.shopify.com/s/files/1/0942/6301/7837/files/paper_bag-20250801_png.webp?v=1771058426', priority: 30 }
  ];
  var masterPaidHandbagVariants = normalizePaidHandbagMasterVariants(window.bagAttachmentMasterVariants);
  var PAID_HANDBAG_VARIANTS = masterPaidHandbagVariants.length ? masterPaidHandbagVariants : DEFAULT_PAID_HANDBAG_VARIANTS;

  // Maximum quantity for paid handbags
  var PAID_BAG_MAX_QTY = 500;

  // Free shipping threshold (yen) — prefectureFee > 0 when below this amount
  var FREE_SHIP_THRESHOLD = window.freeShipThreshold || 3500;

  function normalizeProductId(value) {
    if (value === undefined || value === null || value === '') return '';
    var text = String(value).trim();
    if (!text) return '';
    var match = text.match(/(\d+)(?!.*\d)/);
    return match ? match[1] : text;
  }

  function normalizeShippingFreeExclusionRules(rawRules) {
    if (!Array.isArray(rawRules)) return [];

    return rawRules.map(function(rawRule) {
      if (!rawRule || typeof rawRule !== 'object') return null;

      var productsRaw = Array.isArray(rawRule.products) ? rawRule.products : (Array.isArray(rawRule.productIds) ? rawRule.productIds : []);
      var provincesRaw = Array.isArray(rawRule.provinces) ? rawRule.provinces : [];
      var customerTypesRaw = Array.isArray(rawRule.sellCustomerTypes) ? rawRule.sellCustomerTypes : (Array.isArray(rawRule.sell_customer_types) ? rawRule.sell_customer_types : []);

      var products = productsRaw.map(function(productId) {
        return normalizeProductId(productId);
      }).filter(function(productId) {
        return !!productId;
      });
      var provinces = provincesRaw.map(function(province) {
        return String(province || '').replace(/\s+/g, '');
      }).filter(function(province) {
        return !!province;
      });
      var sellCustomerTypes = customerTypesRaw.map(function(customerType) {
        return String(customerType || '').trim();
      }).filter(function(customerType) {
        return !!customerType;
      });

      if (!products.length || !provinces.length || !sellCustomerTypes.length) {
        return null;
      }

      return {
        handle: String(rawRule.handle || '').trim(),
        products: products,
        provinces: provinces,
        sellCustomerTypes: sellCustomerTypes
      };
    }).filter(function(rule) {
      return !!rule;
    });
  }

  var SHIPPING_FREE_EXCLUSION_RULES = normalizeShippingFreeExclusionRules(window.shippingFreeExclusionRules);
  var SHIPPING_FREE_EXCLUSION_DEBUG = window.shippingFreeExclusionDebug === true ||
    (window.location && typeof window.location.search === 'string' && window.location.search.indexOf('shippingFreeDebug=1') !== -1);

  if (SHIPPING_FREE_EXCLUSION_DEBUG) {
    console.log('[shipping-free-exclusion] loaded rules:', SHIPPING_FREE_EXCLUSION_RULES);
  }

  // Debug mode: activate via ?shippingDebug=1 in URL, or localStorage key 'shippingDebug'='1'.
  // Toggle at runtime: window.toggleShippingDebug()
  var SHIPPING_DEBUG = window.shippingDebug === true ||
    (window.location && typeof window.location.search === 'string' && window.location.search.indexOf('shippingDebug=1') !== -1) ||
    localStorage.getItem('shippingDebug') === '1';

  var ShippingApp = {
    // State
    cart: null,
    cartItems: [],
    sender: null,
    orderer: null, // Customer's default address (ご注文者) for sender auto-determination
    // Separate recipient storage for each mode
    singleRecipient: null, // Single mode: one recipient object or null
    multiRecipients: [],   // Multi mode: array of recipients
    deliverySettings: {},
    deliveryOptions: {}, // Delivery options from API keyed by shipping type (ambient, frozen, mail)
    deliveryOptionsMulti: {}, // Multi-mode: keyed by recipientId, then shipping type
    deliveryOptionsCache: {}, // Cache by postalCode_shippingType to avoid duplicate API calls
    shippingRatesCache: {}, // Cache shipping rates by recipientId_shippingType (e.g., "single_ambient": {rate, details})
    isMultiAddress: false,
    currentMode: 'single', // 'single' or 'multi'

    // Customer addresses fetched from API (for logged-in users)
    customerAddresses: [],

    // Saved addresses from localStorage (for guest users)
    savedAddresses: [],
    // Interoffice shipment addresses loaded from metaobject
    interofficeAddresses: [],

    // Gift Configuration from AllInGift API
    giftConfig: null,

    // Gift options per item (key: itemKey, value: { bags: [], wrapping: {} })
    itemGiftOptions: { single: {} },

    // Noshi settings per item (key: itemKey, value: { gid, name, category, type, omotegaki, subtitle, mode, texts, previewImage })
    noshiSettings: { single: {}, multi: {} },
    // Single-mode split lines by original item key.
    // { [itemKey]: { lines: [{ id, qty, settings:{ noshi, ribbon, wrapOnly, noWrap } }] } }
    singleSplitLines: {},

    // Current item key being edited for noshi modal
    currentNoshiItemKey: null,

    // Current recipient being edited for noshi modal (multi mode)
    currentNoshiRecipientId: null,
    // Current split line context (single mode split cards)
    currentNoshiSplitLineKey: null,
    currentNoshiSourceItemKey: null,

    // Currently selected noshi entry in modal
    selectedNoshiEntry: null,

    // Whether user has clicked preview button in current noshi modal session
    noshiPreviewConfirmed: false,

    // Currently selected noshi entry in bulk wrapping modal
    selectedBulkNoshiEntry: null,

    // Current noshi input mode in bulk wrapping modal ('normal', 'corporate', 'uchiiwai')
    currentBulkNoshiMode: null,

    // Current noshi input mode ('normal', 'corporate', 'uchiiwai')
    currentNoshiMode: 'normal',

    // Sticker/Ribbon settings per item (key: itemKey, value: { gid, name, category, image })
    stickerSettings: { single: {}, multi: {} },

    // Items with 包装のみ (key: itemKey, value: true)
    wrapOnlyItems: { single: {}, multi: {} },

    // Items with 包装なし (key: itemKey, value: true)
    noWrapItems: { single: {}, multi: {} },

    // Current item key being edited for sticker modal
    currentStickerItemKey: null,

    // Current recipient being edited for sticker modal (multi mode)
    currentStickerRecipientId: null,
    // Current split line context for sticker modal
    currentStickerSplitLineKey: null,
    currentStickerSourceItemKey: null,

    // Current sticker category selected in modal
    currentStickerCategory: null,

    // Current item being edited for bag modal
    currentBagItemKey: null,
    currentBagVariantId: null,

    // Current recipient being edited (for change address in multi mode)
    editingRecipientId: null,

    // Flag for sender selection mode
    selectingSender: false,

    // Flag for recipient selection mode (single mode)
    selectingRecipient: false,
    // Recipient ID currently selecting an interoffice address ('single' for single mode)
    selectingInterofficeRecipientId: null,

    // Flag for first access modal (prevent closing on click outside)
    isFirstAccessModal: false,

    // Current recipient being edited for product selection
    selectingProductsRecipientId: null,
    // Temporary product selections while modal is open
    tempProductSelections: {},
    // Temporary address-product quantity selections for modalSelectAddress
    tempAddrSelections: {},
    // Recipients selected in modalBulkListAdd for bulk setup
    bulkSelectedRecipients: [],
    // Whether the modal was opened from bulk setup button (お届け先を追加・選択)
    isBulkSetupMode: false,
    // Whether the modal was opened from edit button (お届け先を追加 - bottom button, edit based on existing data)
    isEditSetupMode: false,
    // Whether modalAddAddress was opened from modalBulkListAdd or modalChangeAddress
    openedAddAddressFromBulkList: false,
    openedAddAddressFromChangeModal: false,
    // Which address is being edited in #modalEditSavedAddress ('BulkListAdd' | 'ChangeAddress' | null)
    openedEditAddressFrom: null,
    // ID of the saved address currently being edited
    editingAddressId: null,
    // Whether data-edit-recipient was clicked from within modalSelectAddress (商品の振り分け)
    editingRecipientFromSelectAddress: false,
    // Pending tab switch that requires user confirmation
    pendingModeSwitch: null,

    // DOM Elements
    $page: null,
    freeBagImage: '',
    noshiImage: '',
    ribbonImage: '',
    paidBagImage: '',

    // Debounce timer for quantity updates
    quantityUpdateTimer: null,
    DEBOUNCE_DELAY: 300,

    // Inventory data (variant_id -> inventory_quantity)
    inventoryData: {},

    // Confirmed quantities per item (itemKey -> quantity)
    // Used to restore old quantity when API update fails
    confirmedQuantities: {},
    MAX_MULTI_RECIPIENTS: 50,
    MULTI_RECIPIENT_LIMIT_MESSAGE: '複数配送先は50件が上限です。',
    _lastMultiRecipientLimitAlertAt: 0,
    // Maximum allowed quantity per cart line item
    MAX_CART_ITEM_QTY: 500,
    CART_QTY_LIMIT_MESSAGE: '1回のご注文につき、最大500点までご購入いただけます。',
    _lastQtyLimitAlertAt: 0,

    /**
     * Lookup zipcode and auto-fill prefecture and city
     * @param {string} zipcode - 7-digit zipcode (without hyphen)
     * @param {jQuery} $prefecture - Prefecture select element
     * @param {jQuery} $city - City input element
     */
    /**
     * On mobile: expand the town field (textarea) to a full row + auto-grow
     * its height to the content via scrollHeight. On desktop: restore the
     * default single-line look.
     */
    _updateTownWidth: function($townInput) {
      if (!$townInput || !$townInput.length) return;
      var el = $townInput[0];
      if (!el) return;
      var isMobile = window.innerWidth < 768;
      if (!isMobile) {
        $townInput.removeClass('is-full-width');
        if (el.tagName === 'TEXTAREA') el.style.height = '';
        return;
      }
      var val = String($townInput.val() || '');
      $townInput.toggleClass('is-full-width', val.length >= 12);
      if (el.tagName === 'TEXTAREA') {
        el.style.height = 'auto';
        el.style.height = el.scrollHeight + 'px';
      }
    },

    lookupZipcode: function(zipcode, $prefecture, $city, $address1Town, $errorEl, $zipInput) {
      var self = this;

      // Clear any autofilled values — zip edits must invalidate stale prefecture/city/town.
      if ($prefecture && $prefecture.length) $prefecture.val('');
      if ($city && $city.length) $city.val('');
      if ($address1Town && $address1Town.length) $address1Town.val('');
      if ($errorEl && $errorEl.length) $errorEl.hide();

      // Remove any non-digits and validate
      var cleanZip = (zipcode || '').replace(/\D/g, '');
      if (cleanZip.length !== 7) return null;

      var applyResult = function(result) {
        var prefCode = PREFECTURE_MAP[result.address1];
        if (prefCode) $prefecture.val(prefCode);
        $city.val(result.address2 || '');
        if ($address1Town && $address1Town.length) {
          $address1Town.val(result.address3 || '');
          self._updateTownWidth($address1Town);
        }
        if ($errorEl && $errorEl.length) $errorEl.hide();
      };

      return $.ajax({
        url: '/apps/ping/postal-codes/search',
        dataType: 'jsonp',
        data: { zipcode: cleanZip },
        timeout: 3000,
        success: function(res) {
          if (res && res.status === 200 && res.results && res.results.length > 0) {
            if (res.results.length === 1) {
              applyResult(res.results[0]);
            } else {
              // Multiple towns map to this postal code — let the user pick.
              // Picker has no close button; user must confirm a selection.
              self.showZipCandidatePicker(res.results, applyResult);
            }
          } else {
            // Show inline error only if the field doesn't already have a
            // validation error (.p-form-error-txt). This prevents an
            // in-flight response from re-showing the inline after submit
            // validation has already placed a richer error message.
            if ($errorEl && $errorEl.length &&
                !$errorEl.closest('.p-row-item').find('.p-form-error-txt').length) {
              $errorEl.show();
            }
          }
        },
        error: function() {
          // Timeout or network failure — fail open (do not block conversion).
        }
      });
    },

    /**
     * Render the zip-candidate picker modal for a postal code that maps to
     * multiple towns (or multiple business entities). Calls onSelect(result)
     * when the user confirms.
     */
    showZipCandidatePicker: function(results, onSelect) {
      var $modal = $('#modalZipCandidatePicker');
      if (!$modal.length) {
        // Fallback: no picker available → just apply first result.
        onSelect(results[0]);
        return;
      }
      var self = this;
      var $list = $modal.find('[data-zip-candidate-list]');
      var $confirm = $modal.find('[data-zip-candidate-confirm]');

      $list.empty();
      $confirm.prop('disabled', true);

      results.forEach(function(r, i) {
        var label = (r.address2 || '') + (r.address3 || '');
        var $item = $(
          '<label class="p-zip-candidate__item">' +
            '<input type="radio" name="zip-candidate" value="' + i + '">' +
            '<span class="p-zip-candidate__label">' + self.escapeHtml(label) + '</span>' +
          '</label>'
        );
        $list.append($item);
      });

      $list.off('change.zipPicker').on('change.zipPicker', 'input[name="zip-candidate"]', function() {
        $list.find('.p-zip-candidate__item').removeClass('is-selected');
        $(this).closest('.p-zip-candidate__item').addClass('is-selected');
        $confirm.prop('disabled', false);
      });

      $confirm.off('click.zipPicker').on('click.zipPicker', function(e) {
        e.preventDefault();
        var idx = parseInt($list.find('input[name="zip-candidate"]:checked').val(), 10);
        if (isNaN(idx) || !results[idx]) return;
        $modal.removeClass('active');
        onSelect(results[idx]);
      });

      this.openModal('modalZipCandidatePicker');
    },

    /**
     * Modal zip-lookup loading-state registry. Tracks in-flight lookups per
     * modal (orderer / edit-saved-address) via a per-key counter, and toggles
     * the modal lock + submit button spinner accordingly.
     */
    _modalZipLoadingConfig: {
      orderer: { modalId: 'modalAddOrderer', btnSelector: '[data-add-orderer]' },
      esa:     { modalId: 'modalEditSavedAddress', btnSelector: '[data-save-edited-address]' }
    },
    _modalZipLookupCount: {},
    _beginModalZipLookup: function(key) {
      var n = (this._modalZipLookupCount[key] || 0) + 1;
      this._modalZipLookupCount[key] = n;
      if (n === 1) this._setModalZipLoading(key, true);
    },
    _endModalZipLookup: function(key) {
      var n = Math.max(0, (this._modalZipLookupCount[key] || 0) - 1);
      this._modalZipLookupCount[key] = n;
      if (n === 0) this._setModalZipLoading(key, false);
    },
    _setModalZipLoading: function(key, loading) {
      var cfg = this._modalZipLoadingConfig[key];
      if (!cfg) return;
      var $modal = $('#' + cfg.modalId);
      var $btn = $modal.find(cfg.btnSelector);
      if (!$btn.length) return;
      if (loading) {
        $modal.addClass('is-zip-loading');
        if (!$btn.data('original-html')) {
          $btn.data('original-html', $btn.html());
        }
        $btn.prop('disabled', true).addClass('is-loading').html(
          '<span class="c-btn-big__loading">' +
            '<span class="c-btn-spinner" aria-hidden="true"></span>' +
            '<span class="c-btn-big__loading-text">読み込み中…</span>' +
          '</span>'
        );
      } else {
        $modal.removeClass('is-zip-loading');
        $btn.prop('disabled', false).removeClass('is-loading');
        var origHtml = $btn.data('original-html');
        if (origHtml) {
          $btn.html(origHtml);
          $btn.removeData('original-html');
        }
      }
    },

    // Back-compat shims for orderer-specific callsites.
    _beginOrdererZipLookup: function() { this._beginModalZipLookup('orderer'); },
    _endOrdererZipLookup: function() { this._endModalZipLookup('orderer'); },

    /**
     * Pre-fill split address1 fields when editing an existing address.
     * Looks up the zip to get the town prefix, strips it from address1 to get the num part.
     * Falls back to putting full address1 in num if zip lookup fails or town doesn't match.
     *
     * @param {string} zip        - 7-digit zip code
     * @param {string} address1   - full stored address1 (e.g. '南青山5-3-3')
     * @param {string} townId     - DOM id of the town input (e.g. 'add-address1-town')
     * @param {string} numId      - DOM id of the num input  (e.g. 'add-address1-num')
     */
    prefillAddress1Split: function(zip, address1, townId, numId) {
      var self = this;
      var cleanZip = (zip || '').replace(/\D/g, '');
      var addr1 = address1 || '';

      function applyFallback() {
        $('#' + townId).val('');
        $('#' + numId).val(addr1);
        self._updateTownWidth($('#' + townId));
      }

      if (cleanZip.length !== 7) { applyFallback(); return null; }

      // Clear immediately so stale values from a previous edit don't linger during AJAX
      $('#' + townId).val('');
      $('#' + numId).val('');

      return $.ajax({
        url: '/apps/ping/postal-codes/search',
        dataType: 'jsonp',
        data: { zipcode: cleanZip },
        timeout: 3000,
        success: function(res) {
          if (res && res.status === 200 && res.results && res.results.length > 0) {
            var town = res.results[0].address3 || '';
            if (town && addr1.indexOf(town) === 0) {
              $('#' + townId).val(town);
              $('#' + numId).val(addr1.slice(town.length));
            } else {
              $('#' + townId).val(town);
              $('#' + numId).val(addr1);
            }
            self._updateTownWidth($('#' + townId));
          } else {
            applyFallback();
          }
        },
        error: function() { applyFallback(); }
      });
    },

    /**
     * Get recipients based on current mode
     * @returns {Array} Array of recipients for current mode
     */
    getRecipients: function() {
      if (this.currentMode === 'single') {
        return this.singleRecipient ? [this.singleRecipient] : [];
      }
      return this.multiRecipients || [];
    },

    /**
     * Set recipients based on current mode
     * @param {Array} recipients - Array of recipients to set
     */
    setRecipients: function(recipients) {
      if (this.currentMode === 'single') {
        this.singleRecipient = recipients && recipients.length > 0 ? recipients[0] : null;
      } else {
        this.multiRecipients = recipients || [];
      }
    },

    hasShauriCustomerTag: function() {
      var tags = (window.shippingCustomer && window.shippingCustomer.tags) || [];
      return tags.indexOf('社員売顧客') !== -1;
    },

    canUseInterofficeShipment: function() {
      return this.hasShauriCustomerTag() && (this.interofficeAddresses || []).length > 0;
    },

    /**
     * Check if items contain frozen or mail shipping types
     * @param {Array} items - cart items to check
     * @returns {boolean} true if any item is frozen or mail
     */
    hasNonInterofficeItems: function(items) {
      var self = this;
      return (items || []).some(function(item) {
        var shippingType = self.getItemShippingType(item);
        return shippingType === 'frozen' || shippingType === 'mail';
      });
    },

    isInterofficeRecipient: function(recipient) {
      return !!(recipient && recipient.isInteroffice === true);
    },

    getInterofficeContactName: function(fallbackRecipient) {
      var defaultAddress = window.shippingCustomer && window.shippingCustomer.defaultAddress;
      var lastName = '';
      var firstName = '';

      if (defaultAddress && (defaultAddress.lastName || defaultAddress.firstName)) {
        lastName = defaultAddress.lastName || '';
        firstName = defaultAddress.firstName || '';
      } else if (this.orderer && (this.orderer.lastName || this.orderer.firstName)) {
        lastName = this.orderer.lastName || '';
        firstName = this.orderer.firstName || '';
      } else if (fallbackRecipient) {
        lastName = fallbackRecipient.lastName || '';
        firstName = fallbackRecipient.firstName || '';
      }

      return { lastName: lastName, firstName: firstName };
    },

    getAddress2WithCompany: function(address) {
      if (!address) return '';
      return buildAddress2WithCompany(address.address2, address.company);
    },

    isAddressNameIncomplete: function(addr) {
      return addr && (!addr.lastName || !addr.firstName);
    },

    normalizeHalfWidthDigits: function(value) {
      return String(value || '').replace(/[０-９]/g, function(s) {
        return String.fromCharCode(s.charCodeAt(0) - 65248);
      });
    },

    normalizeZipInput: function(value) {
      return this.normalizeHalfWidthDigits(value).replace(/\D/g, '').slice(0, 7);
    },

    normalizePhoneInput: function(value) {
      var digits = this.normalizeHalfWidthDigits(value).replace(/\D/g, '');
      if (digits.indexOf('81') === 0) {
        var local = digits.slice(2);
        if (local && local.charAt(0) !== '0') {
          local = '0' + local;
        }
        digits = local;
      }
      return digits;
    },

    formatZipDisplay: function(zip) {
      if (!zip) return zip;
      var d = String(zip).replace(/\D/g, '');
      if (d.length === 7) return d.slice(0, 3) + '-' + d.slice(3);
      return d || zip;
    },

    formatPhoneDisplay: function(phone) {
      if (!phone) return phone;
      var d = String(phone).replace(/\D/g, '');
      if (!d) return phone;
      // Use libphonenumber-js for accurate Japanese area code formatting
      if (typeof libphonenumber !== 'undefined' && libphonenumber.parsePhoneNumberFromString) {
        try {
          var parsed = libphonenumber.parsePhoneNumberFromString(d, 'JP');
          if (parsed && parsed.isValid()) {
            return parsed.formatNational();
          }
        } catch (e) {}
      }
      // Fallback: basic formatting
      if (d.length === 11) {
        return d.slice(0,3) + '-' + d.slice(3,7) + '-' + d.slice(7);
      }
      if (d.length === 10) {
        if (d.slice(0,2) === '03' || d.slice(0,2) === '06') {
          return d.slice(0,2) + '-' + d.slice(2,6) + '-' + d.slice(6);
        }
        return d.slice(0,3) + '-' + d.slice(3,6) + '-' + d.slice(6);
      }
      return d || phone;
    },

    normalizeGiftSettingCountValue: function(value, max, fallbackValue) {
      var limit = Math.max(1, parseInt(max, 10) || 1);
      var fallback = Math.max(1, Math.min(parseInt(fallbackValue, 10) || limit, limit));
      var digits = this.normalizeHalfWidthDigits(value).replace(/\D/g, '');
      var parsed = parseInt(digits, 10);
      if (isNaN(parsed)) parsed = fallback;
      return Math.max(1, Math.min(parsed, limit));
    },

    getGiftSettingCountMax: function($input) {
      var $container = $input.closest('[data-noshi-count], [data-sticker-count]');
      var max = parseInt($container.find('[data-noshi-count-total], [data-sticker-count-total]').first().text(), 10);
      return Math.max(1, max || 1);
    },

    syncGiftSettingCountControl: function($input, fallbackValue) {
      if (!$input || !$input.length) return 1;
      var max = this.getGiftSettingCountMax($input);
      var normalized = this.normalizeGiftSettingCountValue($input.val(), max, fallbackValue == null ? max : fallbackValue);
      var isLocked = $input.prop('disabled');
      var $qty = $input.closest('.qty');
      var disableMinus = isLocked || normalized <= 1;
      var disablePlus = isLocked || normalized >= max;

      $input.val(normalized);
      $qty.find('[data-step="-1"]').prop('disabled', disableMinus).toggleClass('is-disabled', disableMinus);
      $qty.find('[data-step="1"]').prop('disabled', disablePlus).toggleClass('is-disabled', disablePlus);

      return normalized;
    },

    getAddressPersonType: function(prefix) {
      var value = $('input[name="' + prefix + '-personType"]:checked').val();
      return value === 'corporate' ? 'corporate' : 'individual';
    },

    setAddressPersonType: function(prefix, personType) {
      var type = personType === 'corporate' ? 'corporate' : 'individual';
      $('input[name="' + prefix + '-personType"][value="' + type + '"]').prop('checked', true);
      this.toggleAddressCompanyField(prefix, type === 'corporate');
    },

    toggleAddressCompanyField: function(prefix, show) {
      var $row = $('#' + prefix + '-company-row');
      var $company = $('#' + prefix + '-company');
      if (!$row.length) return;
      $row.toggle(!!show);
      if (!show) {
        $company.val('');
        $row.find('.p-row-item').removeClass('is-error');
        $row.find('.p-form-error-txt').remove();
      }
    },

    normalizeAddressText: function(value) {
      return String(value || '').replace(/\r?\n/g, ' ').trim();
    },

    normalizeAddressPayload: function(addr) {
      var normalized = $.extend({}, addr || {});
      normalized.lastName = this.normalizeAddressText(normalized.lastName);
      normalized.firstName = this.normalizeAddressText(normalized.firstName);
      normalized.zip = this.normalizeZipInput(normalized.zip);
      normalized.prefecture = this.normalizeAddressText(normalized.prefecture);
      normalized.prefectureCode = String(normalized.prefectureCode || '').trim();
      normalized.city = this.normalizeAddressText(normalized.city);
      normalized.address1 = this.normalizeAddressText(normalized.address1);
      normalized.address2 = this.normalizeAddressText(normalized.address2);
      normalized.company = this.normalizeAddressText(normalized.company);
      normalized.phone = this.normalizePhoneInput(normalized.phone);
      normalized.personType = normalized.personType === 'corporate' ? 'corporate' : 'individual';
      if (normalized.personType !== 'corporate') {
        normalized.company = '';
      }
      return normalized;
    },

    getTabIdForMode: function(mode) {
      return mode === 'multi' ? 'tab2' : 'tab1';
    },

    setActiveShippingTab: function(mode, animate) {
      var tabId = this.getTabIdForMode(mode);
      var $tabGroup = this.$page.find('.js-tab').first();
      if (!$tabGroup.length) return;

      var $scope = $tabGroup.closest('.p-checkout__item');
      if (!$scope.length) $scope = $tabGroup.parent();

      var $links = $tabGroup.find('ul li a');
      var $contents = $scope.find('.tab-content');
      var $targetLink = $links.filter('[data-tab="' + tabId + '"]').first();
      var $targetContent = $contents.filter('[data-tab-content="' + tabId + '"]').first();
      if (!$targetLink.length || !$targetContent.length) return;

      $links.removeClass('is-active');
      $targetLink.addClass('is-active');
      $contents.removeClass('is-active').hide();
      $targetContent.addClass('is-active');
      if (animate) {
        $targetContent.fadeIn(150);
      } else {
        $targetContent.show();
      }
    },

    getDefaultDeliverySettings: function() {
      return {
        ambient: { date: '', time: '' },
        frozen: { date: '', time: '' },
        mail: { date: '', time: '' }
      };
    },

    buildSingleRecipientFromOrderer: function() {
      if (!this.orderer || !this.orderer.lastName) return null;
      return {
        id: this.orderer.id || 'orderer_default',
        lastName: this.orderer.lastName || '',
        firstName: this.orderer.firstName || '',
        zip: this.orderer.zip || '',
        prefecture: this.orderer.prefecture || '',
        prefectureCode: this.orderer.prefectureCode || '',
        city: this.orderer.city || '',
        address1: this.orderer.address1 || '',
        address2: this.orderer.address2 || '',
        company: this.orderer.company || '',
        phone: this.orderer.phone || '',
        items: {},
        wrapping: { bag: 'off', type: 'paper' },
        deliverySettings: this.getDefaultDeliverySettings()
      };
    },

    applyModeSwitch: function(mode, options) {
      var opts = options || {};
      var nextMode = mode === 'multi' ? 'multi' : 'single';
      this.currentMode = nextMode;
      this.isMultiAddress = nextMode === 'multi';
      this.setActiveShippingTab(nextMode, !!opts.animateTab);
      if (!opts.skipRender) {
        this.renderCartItems();
      }
      this.updateSenderByRecipient();
      this.populateDeliveryDates();
      this.updateSummary();
      this.checkIncompleteSettings();
      this.updateCheckoutButtonState();
    },

    /**
     * Check if the user has made any edits in the current mode
     */
    hasUserEdits: function() {
      return this._userHasEdited === true;
    },

    /**
     * Mark that the user has made edits (called from saveData on user actions)
     */
    markUserEdited: function() {
      this._userHasEdited = true;
    },

    openModeSwitchConfirm: function(targetMode) {
      var nextMode = targetMode === 'multi' ? 'multi' : 'single';
      var currentMode = this.currentMode === 'multi' ? 'multi' : 'single';
      if (nextMode === currentMode) {
        this.applyModeSwitch(nextMode, { skipRender: true });
        return;
      }

      // Skip confirmation if user hasn't made any edits
      if (!this.hasUserEdits()) {
        this.pendingModeSwitch = { from: currentMode, to: nextMode };
        this.confirmModeSwitch();
        return;
      }

      this.pendingModeSwitch = { from: currentMode, to: nextMode };
      this.setActiveShippingTab(currentMode, false);

      var message = 'ご入力いただいた情報がクリアされます<br>よろしいですか？';
      $('#modalBulkSwitch [data-switch-confirm-message]').html(message);
      this.openModal('modalBulkSwitch');
    },

    cancelModeSwitchConfirm: function() {
      this.pendingModeSwitch = null;
      this.setActiveShippingTab(this.currentMode, false);
    },

    confirmModeSwitch: function() {
      var modeSwitch = this.pendingModeSwitch;
      if (!modeSwitch) {
        this.closeModal();
        return;
      }

      this.pendingModeSwitch = null;
      this.closeModal();

      this.clearCartSpecificData();

      if (modeSwitch.from === 'multi' && modeSwitch.to === 'single') {
        this.singleRecipient = this.buildSingleRecipientFromOrderer();
      }

      this.shippingRatesCache = {};
      this.clearDeliveryOptionsCache();
      this._userHasEdited = false;
      this._suppressEditFlag = true;
      this.applyModeSwitch(modeSwitch.to, { animateTab: false });
      this.saveData();
      this._suppressEditFlag = false;
    },

    /**
     * Initialize the shipping app
     */
    init: function() {
      this.$page = $('[data-shipping-page]');
      if (!this.$page.length) return;
      this.interofficeAddresses = normalizeInterofficeAddresses(window.interofficeShipmentAddresses);
      this.freeBagImage = this.$page.data('free-bag-img') || '';
      this.noshiImage = this.$page.data('noshi-img') || '';
      this.ribbonImage = this.$page.data('ribbon-img') || '';
      this.paidBagImage = this.$page.data('paid-bag-img') || '';

      this.bindEvents();

      // Auto-hide loading after 15s in case render never completes
      var self = this;
      this._loadingTimeout = setTimeout(function() {
        self.hideLoading();
      }, 15000);

      // Guest first access: show add address modal if no orderer (注文者) address exists
      if (!window.shippingCustomer) {
        try {
          var savedOrderer = localStorage.getItem('shipping_orderer');
          var ordererData = savedOrderer ? JSON.parse(savedOrderer) : null;

          if (!ordererData || !ordererData.lastName) {
            this.isFirstAccessModal = true;
            this.openModal('modalAddOrderer');
            // Add class to prevent closing on click outside
            $('#modalAddOrderer').addClass('no-outside-close');
          }
        } catch (e) {
          // localStorage parse error, skip
        }
      }

      this.config();
      this.debugInit();
    },

    /**
     * Bind all event handlers
     */
    bindEvents: function() {
      var self = this;

      // Zipcode auto-fill + normalization (supports pasted/autofill values with hyphens).
      // Guards:
      //   - Skip API call when length is not 7 (only valid Japanese postal code length).
      //   - Skip API call when the normalized value hasn't changed since the last fetch
      //     (input/change/blur all fire for the same value otherwise).
      //   - When length becomes invalid after having valid data, clear autofill but don't fetch.
      var bindZipInput = function(inputSelector, prefSelector, citySelector, addr1TownSelector, addr1NumSelector) {
        $(document).on('input change blur', inputSelector, function() {
          var $el = $(this);
          var $errorEl = $el.siblings('.zip-lookup-error');
          var normalizedZip = self.normalizeZipInput($el.val());
          if ($el.val() !== normalizedZip) {
            $el.val(normalizedZip);
          }

          var lastZip = $el.data('lastLookupZip') || '';

          // Impossible length → hide error, clear stale autofill once, no fetch.
          if (normalizedZip.length !== 7) {
            if ($errorEl.length) $errorEl.hide();
            if (lastZip) {
              $(prefSelector).val('');
              $(citySelector).val('');
              if (addr1TownSelector) $(addr1TownSelector).val('');
              $el.data('lastLookupZip', '');
            }
            return;
          }

          // No change since last successful dispatch → skip.
          if (lastZip === normalizedZip) {
            if ($errorEl.length) $errorEl.hide();
            return;
          }

          $el.data('lastLookupZip', normalizedZip);
          if ($errorEl.length) $errorEl.hide();

          var req = self.lookupZipcode(normalizedZip, $(prefSelector), $(citySelector),
            addr1TownSelector ? $(addr1TownSelector) : null, $errorEl, $el);
          // Modals with a submit-button loading UI: orderer + edit-saved-address.
          var modalKey = (inputSelector === '#orderer-zipCode') ? 'orderer'
                       : (inputSelector === '#esa-zipCode') ? 'esa'
                       : null;
          if (req && modalKey) {
            self._beginModalZipLookup(modalKey);
            req.always(function() { self._endModalZipLookup(modalKey); });
          }
        });
        // Lock prefecture select: only changeable via zip autofill
        $(document).on('mousedown', prefSelector, function(e) { e.preventDefault(); this.blur(); });
        $(document).on('keydown', prefSelector, function(e) { if (e.key !== 'Tab') e.preventDefault(); });
        // Lock address1-town: only changeable via zip autofill
        $(document).on('mousedown', addr1TownSelector, function(e) { e.preventDefault(); this.blur(); });
        $(document).on('keydown', addr1TownSelector, function(e) { if (e.key !== 'Tab') e.preventDefault(); });
      };
      bindZipInput('#sender-zipCode', '#sender-prefecture', '#sender-city', '#sender-address1-town', '#sender-address1-num');
      bindZipInput('#add-zipCode', '#add-prefecture', '#add-city', '#add-address1-town', '#add-address1-num');
      bindZipInput('#orderer-zipCode', '#orderer-prefecture', '#orderer-city', '#orderer-address1-town', '#orderer-address1-num');
      bindZipInput('#edit-recipient-zipCode', '#edit-recipient-prefecture', '#edit-recipient-city', '#edit-recipient-address1-town', '#edit-recipient-address1-num');
      bindZipInput('#esa-zipCode', '#esa-prefecture', '#esa-city', '#esa-address1-town', '#esa-address1-num');

      // Phone normalization (supports pasted/autofill values with hyphens)
      $(document).on('input change blur', '#sender-phone, #add-phone, #orderer-phone, #edit-recipient-phone, #esa-phone', function() {
        var normalizedPhone = self.normalizePhoneInput($(this).val());
        if ($(this).val() !== normalizedPhone) {
          $(this).val(normalizedPhone);
        }
      });

      // Individual/Corporate toggle for address forms
      $(document).on('change', 'input[name$="-personType"]', function() {
        var name = $(this).attr('name') || '';
        var match = name.match(/^(.*)-personType$/);
        if (!match) return;
        var prefix = match[1];
        self.toggleAddressCompanyField(prefix, $(this).val() === 'corporate');
      });

      ['sender', 'orderer', 'add', 'edit-recipient'].forEach(function(prefix) {
        self.setAddressPersonType(prefix, 'individual');
      });

      // Checkout button
      $(document).on('click', '[data-checkout-btn]', function(e) {
        e.preventDefault();
        self.validateAndCheckout();
      });

      // Airport address confirmation modal
      $(document).on('click', '[data-airport-confirm]', function() {
        $('#modalAirportConfirm').removeClass('active');
        self._airportConfirmed = true;
        self.generalGift();
      });
      $(document).on('click', '[data-airport-cancel]', function() {
        $('#modalAirportConfirm').removeClass('active');
      });

      // Privacy policy checkbox — update checkout button state on change
      $(document).on('change', '#privacy-policy-check', function() {
        self.updateCheckoutButtonState();
      });

      // Reset checkout state when user navigates back from checkout page (bfcache restore)
      window.addEventListener('pageshow', function(event) {
        if (event.persisted) {
          // Hide checkout loading overlay (was shown before navigating to checkout)
          self.hideCheckoutLoading();
          // Hide initial loading overlay (in case it was still visible)
          self.hideLoading();
          // Reset checkout button
          $('[data-checkout-btn]').prop('disabled', false).removeClass('is-disabled').text('お支払いに進む');
          self.updateCheckoutButtonState();

          // Re-fetch cart data and inventory (bfcache may have stale data)
          if (self._initialized) {
            $.ajax({
              url: window.routes.cart_url + '.js',
              type: 'GET',
              dataType: 'json'
            }).done(function(cart) {
              self.cart = cart;
              self.cartItems = cart.items || [];
              if (SHIPPING_DEBUG) {
                console.log('[ShippingApp][pageshow] Re-fetched cart via AJAX — item_count=' + (cart.item_count || 0));
              }
            });
            // Refresh inventory data from server
            self.refreshInventoryData();
          }

          // Re-fetch delivery dates (API calls were aborted during navigation)
          if (self._initialized && self.cartItems && self.cartItems.length > 0) {
            self.setDeliveryScheduleLoading(false);
            self.populateDeliveryDates();
          }
        }
      });

      // Save sender info
      $(document).on('click', '[data-save-sender]', function(e) {
        e.preventDefault();
        self.saveSenderInfo();
      });

      // Change sender - opens select modal if multiple addresses exist, otherwise edit modal
      $(document).on('click', '[data-change-sender]', function(e) {
        e.preventDefault();
        self.handleChangeSender();
      });

      // Select sender from address list
      $(document).on('click', '[data-select-sender-address]', function(e) {
        e.preventDefault();
        self.selectSenderAddress();
      });

      // Add new address
      $(document).on('click', '[data-add-address]', function(e) {
        e.preventDefault();
        self.addNewAddress();
      });

      // Register orderer info (guest first-access modal)
      $(document).on('click', '[data-add-orderer]', function(e) {
        e.preventDefault();
        self.addOrdererInfo();
      });

      // Open orderer registration modal from "お客様の情報を登録してください" / "ご注文者を変更する" button
      $(document).on('click', '[data-open-orderer-modal]', function(e) {
        e.preventDefault();
        // Reset submit button state in case a previous submission left it disabled
        $('#modalAddOrderer [data-add-orderer]').prop('disabled', false);
        self.fillOrdererModal(self.orderer);
        self.openModal('modalAddOrderer');
        if (self.orderer && self._ordererAddressFieldErrors && Object.keys(self._ordererAddressFieldErrors).length) {
          var fieldErrors = self._ordererAddressFieldErrors;
          if (fieldErrors.zip) {
            self.showFieldError('orderer-zipCode', fieldErrors.zip);
          }
          if (fieldErrors.phone) {
            self.showFieldError('orderer-phone', fieldErrors.phone);
          }
        }
      });

      // modalAddOrderer X button - show leave confirmation instead of direct close
      // The global shipping-common.js handler fires first and closes the modal,
      // then we reopen the confirmation dialog in setTimeout
      $(document).on('click', '#modalAddOrderer .js-close', function() {
        // If orderer is already registered (edit mode), just close without confirmation
        if (self.orderer && self.orderer.lastName) {
          return; // Let the global close handler handle it normally
        }
        // First-time registration: show leave confirmation
        var savedScrollPos = self._savedScrollPos || 0;
        setTimeout(function() {
          $('html, body').addClass('freezePage').css('top', -savedScrollPos + 'px');
          $('#modalOrdererLeaveConfirm').addClass('active');
        }, 0);
      });

      // "入力ページに戻る" in leave confirmation - close confirm and reopen orderer modal
      $(document).on('click', '[data-orderer-leave-return]', function(e) {
        e.preventDefault();
        $('#modalOrdererLeaveConfirm').removeClass('active');
        $('#modalAddOrderer').addClass('active');
        if (self.isFirstAccessModal) {
          $('#modalAddOrderer').addClass('no-outside-close');
        }
      });

      // Add selected addresses to recipients
      $(document).on('click', '[data-add-selected-addresses]', function(e) {
        e.preventDefault();
        self.addSelectedAddresses();
      });

      // Tab switching (requires confirmation because input data is reset)
      $(document).on('click', '[data-tab="tab1"], [data-tab="tab2"]', function(e) {
        e.preventDefault();
        var targetMode = $(this).data('tab') === 'tab2' ? 'multi' : 'single';
        self.openModeSwitchConfirm(targetMode);
      });

      // Recalculate sticky offsets when viewport changes
      $(window).on('resize.shippingSticky orientationchange.shippingSticky', function() {
        self.updateStickyBadgeOffsets();
      });

      // Quantity change in cart items (multi-address mode) - pool-based with optimistic UI
      // + : take from unassigned pool first (sync); if pool empty, debounced cart API
      // - : if pool > 0, unassign from recipient (sync); if pool = 0, debounced cart API
      $(document).on('click', '[data-qty-btn]', function() {
        var $btn = $(this);

        // Skip if button is disabled
        if ($btn.prop('disabled')) return;

        var $card = $btn.closest('[data-recipient-card]');
        var $itemWrapper = $btn.closest('[data-item-key]');
        var step = parseInt($btn.data('step'), 10);
        var recipientId = $card.data('recipient-id');
        var itemKey = $itemWrapper.data('item-key');

        var cartItem = self.cartItems.find(function(i) { return i.key === itemKey; });
        if (!cartItem) return;

        var recipient = (self.multiRecipients || []).find(function(r) { return r.id === recipientId; });
        if (!recipient) return;

        // Use intent qty for pool calculation so rapid clicks don't read stale cart data
        self._cartQtyIntent = self._cartQtyIntent || {};
        self._cartQtyTimers = self._cartQtyTimers || {};
        self._cartQtySnapshot = self._cartQtySnapshot || {};
        var intentCartQty = self._cartQtyIntent[itemKey] !== undefined
          ? self._cartQtyIntent[itemKey] : cartItem.quantity;

        // Unassigned pool based on current intent and all recipients' assignments
        var totalAssigned = 0;
        (self.multiRecipients || []).forEach(function(r) {
          totalAssigned += (r.items && r.items[itemKey]) ? r.items[itemKey] : 0;
        });
        var unassigned = intentCartQty - totalAssigned;
        var currentRecipientQty = (recipient.items && recipient.items[itemKey]) ? recipient.items[itemKey] : 0;

        if (step === 1) {
          if (unassigned > 0) {
            // Pool has items — assign 1 to this recipient, no cart API
            if (!recipient.items) recipient.items = {};
            recipient.items[itemKey] = currentRecipientQty + 1;
            self.syncRecipientUnsplitGiftCount(recipient, itemKey, currentRecipientQty + 1);
            self.saveData();
            self._refreshRecipientItemTotals(itemKey);
            self.updateSummary();
            self.checkIncompleteSettings();
          } else {
            // Pool empty — optimistic update + debounced cart API
            var nextIntentQty = intentCartQty + 1;
            var normalizedIntent = self.normalizeRequestedItemQuantity(itemKey, nextIntentQty, cartItem.variant_id, true);
            if (normalizedIntent.quantity <= intentCartQty) {
              return;
            }

            if (self._cartQtyIntent[itemKey] === undefined) {
              // First click in batch: save snapshot for rollback on AJAX fail
              var snap = { cartQty: cartItem.quantity, recipientQtys: {}, recipientOrder: [] };
              (self.multiRecipients || []).forEach(function(r) {
                snap.recipientQtys[r.id] = (r.items && r.items[itemKey]) ? r.items[itemKey] : 0;
              });
              self._cartQtySnapshot[itemKey] = snap;
              self._cartQtyIntent[itemKey] = cartItem.quantity;
            }
            self._cartQtyIntent[itemKey] = normalizedIntent.quantity;
            if (!recipient.items) recipient.items = {};
            recipient.items[itemKey] = currentRecipientQty + 1;
            // Only record if this click creates a net increase vs snapshot (- then + cancels out)
            var snapInc = self._cartQtySnapshot[itemKey];
            if (snapInc && snapInc.recipientOrder) {
              var snapRecipientQty = snapInc.recipientQtys[recipient.id] !== undefined ? snapInc.recipientQtys[recipient.id] : 0;
              if (currentRecipientQty >= snapRecipientQty) {
                snapInc.recipientOrder.push(recipient.id);
              }
            }
            self.syncRecipientUnsplitGiftCount(recipient, itemKey, currentRecipientQty + 1);
            self.saveData();
            self._updateRecipientItemUI($itemWrapper, currentRecipientQty + 1, cartItem.price / 100);
            self.updateSummary();
            clearTimeout(self._cartQtyTimers[itemKey]);
            self._cartQtyTimers[itemKey] = setTimeout(function() {
              self._flushCartQtyIntent(itemKey);
            }, 300);
          }
        } else {
          if (currentRecipientQty <= 0) return;

          if (unassigned === 0) {
            // All assigned — optimistic update + debounced cart API
            var currentIntent = self._cartQtyIntent[itemKey] !== undefined
              ? self._cartQtyIntent[itemKey] : cartItem.quantity;
            if (currentIntent <= 1) return; // At cart minimum, cannot reduce

            if (self._cartQtyIntent[itemKey] === undefined) {
              var snap = { cartQty: cartItem.quantity, recipientQtys: {}, recipientOrder: [] };
              (self.multiRecipients || []).forEach(function(r) {
                snap.recipientQtys[r.id] = (r.items && r.items[itemKey]) ? r.items[itemKey] : 0;
              });
              self._cartQtySnapshot[itemKey] = snap;
              self._cartQtyIntent[itemKey] = cartItem.quantity;
            }
            self._cartQtyIntent[itemKey]--;
            // Cancel the most recent + entry for this recipient in recipientOrder
            var snapDec = self._cartQtySnapshot[itemKey];
            if (snapDec && snapDec.recipientOrder) {
              var lastIdx = snapDec.recipientOrder.lastIndexOf(recipient.id);
              if (lastIdx !== -1) snapDec.recipientOrder.splice(lastIdx, 1);
            }
            var newRecipientQty = currentRecipientQty - 1;
            if (!recipient.items) recipient.items = {};
            recipient.items[itemKey] = newRecipientQty;
            // Clamp this recipient's noshi count if needed
            if (recipient.giftSettings && recipient.giftSettings[itemKey] &&
                recipient.giftSettings[itemKey].noshi &&
                recipient.giftSettings[itemKey].noshi.count > newRecipientQty) {
              recipient.giftSettings[itemKey].noshi.count = newRecipientQty;
            }
            if (recipient.giftSettings && recipient.giftSettings[itemKey] &&
                recipient.giftSettings[itemKey].ribbon &&
                recipient.giftSettings[itemKey].ribbon.count > newRecipientQty) {
              recipient.giftSettings[itemKey].ribbon.count = newRecipientQty;
            }
            // Normalize recipient split lines
            if (newRecipientQty > 1) {
              self.normalizeRecipientSplitLines(recipient, itemKey, newRecipientQty);
            } else if (recipient.splitLines) {
              delete recipient.splitLines[itemKey];
            }
            self.saveData();
            self._updateRecipientItemUI($itemWrapper, newRecipientQty, cartItem.price / 100);
            self.updateSummary();
            clearTimeout(self._cartQtyTimers[itemKey]);
            self._cartQtyTimers[itemKey] = setTimeout(function() {
              self._flushCartQtyIntent(itemKey);
            }, 300);
          } else {
            // Pool has items — unassign 1 from recipient, no cart API
            var newRecipientQty = currentRecipientQty - 1;
            if (!recipient.items) recipient.items = {};
            recipient.items[itemKey] = newRecipientQty;
            // Clamp this recipient's noshi count if needed
            if (recipient.giftSettings && recipient.giftSettings[itemKey] &&
                recipient.giftSettings[itemKey].noshi &&
                recipient.giftSettings[itemKey].noshi.count > newRecipientQty) {
              recipient.giftSettings[itemKey].noshi.count = newRecipientQty;
            }
            if (recipient.giftSettings && recipient.giftSettings[itemKey] &&
                recipient.giftSettings[itemKey].ribbon &&
                recipient.giftSettings[itemKey].ribbon.count > newRecipientQty) {
              recipient.giftSettings[itemKey].ribbon.count = newRecipientQty;
            }
            // Normalize recipient split lines
            if (newRecipientQty > 1) {
              self.normalizeRecipientSplitLines(recipient, itemKey, newRecipientQty);
            } else if (recipient.splitLines) {
              delete recipient.splitLines[itemKey];
            }
            self.saveData();
            self._refreshRecipientItemTotals(itemKey);
            self.updateSummary();
            self.checkIncompleteSettings();
          }
        }
      });

      // Quantity input change in cart items (multi-address mode) - calls Cart API directly
      $(document).on('change', '[data-qty-input]', function() {
        var $input = $(this);
        var $card = $input.closest('[data-recipient-card]');
        var $itemWrapper = $input.closest('[data-item-key]');
        var $decreaseBtn = $itemWrapper.find('[data-qty-btn][data-step="-1"]');
        var $increaseBtn = $itemWrapper.find('[data-qty-btn][data-step="1"]');
        var recipientId = $card.data('recipient-id');
        var itemKey = $itemWrapper.data('item-key');

        var cartItem = self.cartItems.find(function(i) { return i.key === itemKey; });
        if (!cartItem) return;

        var recipient = (self.multiRecipients || []).find(function(r) { return r.id === recipientId; });
        if (!recipient) return;

        var oldCartQty = cartItem.quantity;
        var oldRecipientQty = (recipient.items && recipient.items[itemKey]) ? recipient.items[itemKey] : 0;
        var rawQtyVal = String($input.val() || '').trim();
        if (rawQtyVal === '' || parseInt(rawQtyVal, 10) <= 0) {
          $input.val(oldRecipientQty);
          var $cartItemBottomMulti = $input.closest('.cart-item__bottom');
          $cartItemBottomMulti.next('.js-qty-error').remove();
          var errorMsgMulti = rawQtyVal === '' ? 'このアイテムは、1の増分でのみ追加できます' : 'このアイテムの最小値は1です';
          $cartItemBottomMulti.after('<p class="p-form-error-txt js-qty-error" role="alert">' + errorMsgMulti + '</p>');
          return;
        }
        $input.closest('.cart-item__bottom').next('.js-qty-error').remove();

        // Compute correct cart total: typed recipient qty + other recipients' existing allocations
        var rawRecipientQty = Math.max(1, parseInt(rawQtyVal, 10));
        var otherAssigned = 0;
        (self.multiRecipients || []).forEach(function(r) {
          if (r.id !== recipientId) {
            otherAssigned += (r.items && r.items[itemKey]) ? r.items[itemKey] : 0;
          }
        });
        var tentativeCartQty = rawRecipientQty + otherAssigned;
        var normalizedCartQty = self.normalizeRequestedItemQuantity(itemKey, tentativeCartQty, cartItem.variant_id, true);
        var newCartQty = normalizedCartQty.quantity;
        var newRecipientQty = Math.max(1, newCartQty - otherAssigned);

        // Cancel any pending pool-based debounced intent for this item
        self._cartQtyIntent = self._cartQtyIntent || {};
        self._cartQtyTimers = self._cartQtyTimers || {};
        if (self._cartQtyIntent[itemKey] !== undefined) {
          clearTimeout(self._cartQtyTimers[itemKey]);
          delete self._cartQtyIntent[itemKey];
          delete (self._cartQtySnapshot || {})[itemKey];
        }

        // Update UI immediately with recipient qty
        $input.val(newRecipientQty);
        if (newCartQty === oldCartQty) {
          // No cart change — just update recipient allocation and refresh sibling totals.
          if (!recipient.items) recipient.items = {};
          recipient.items[itemKey] = newRecipientQty;
          self.syncRecipientUnsplitGiftCount(recipient, itemKey, newRecipientQty);
          self.saveData();
          self._refreshRecipientItemTotals(itemKey);
          self.updateSummary();
          self.checkIncompleteSettings();
          return;
        }
        $itemWrapper.addClass('is-loading');
        $itemWrapper.find('[data-qty-btn]').prop('disabled', true).addClass('is-disabled');
        $itemWrapper.find('[data-qty-input]').prop('disabled', true);

        // Call cart API to update quantity
        $.ajax({
          url: '/cart/change.js',
          type: 'POST',
          dataType: 'json',
          data: { id: itemKey, quantity: newCartQty }
        })
        .done(function(cart) {
          self.cart = cart;
          self.cartItems = cart.items || [];

          var updatedItem = cart.items.find(function(i) { return i.key === itemKey; });
          if (!updatedItem) {
            var _reKeyedItem = self._findReKeyedCartItem(cartItem, cart.items);
            if (_reKeyedItem) {
              self.migrateItemKey(itemKey, _reKeyedItem.key);
              itemKey = _reKeyedItem.key;
              updatedItem = _reKeyedItem;
            }
          }
          if (updatedItem) {
            var actualQty = updatedItem.quantity;
            if (actualQty < newCartQty) {
              self.showCartQuantityLimitMessage();
            }
            self.confirmedQuantities[itemKey] = actualQty;
            var actualRecipientQty = Math.max(1, actualQty - otherAssigned);

            // Clamp noshi count if quantity decreased
            if (self.noshiSettings['single'][itemKey] && self.noshiSettings['single'][itemKey].count > actualQty) {
              self.noshiSettings['single'][itemKey].count = actualQty;
              self.saveData();
            }
            if (self.stickerSettings['single'][itemKey] && self.stickerSettings['single'][itemKey].count > actualQty) {
              self.stickerSettings['single'][itemKey].count = actualQty;
              self.saveData();
            }
            (self.multiRecipients || []).forEach(function(r) {
              if (r.giftSettings && r.giftSettings[itemKey] && r.giftSettings[itemKey].noshi &&
                  r.giftSettings[itemKey].noshi.count > actualQty) {
                r.giftSettings[itemKey].noshi.count = actualQty;
                self.saveData();
              }
              if (r.giftSettings && r.giftSettings[itemKey] && r.giftSettings[itemKey].ribbon &&
                  r.giftSettings[itemKey].ribbon.count > actualQty) {
                r.giftSettings[itemKey].ribbon.count = actualQty;
                self.saveData();
              }
            });
            if (self.singleSplitLines[itemKey]) {
              self.normalizeSingleSplitLines(itemKey, actualQty);
              self.saveData();
            }

            // Update UI with actual recipient qty
            $input.val(actualRecipientQty);

            // Update maxQty for all instances of this item (available = cart total - others)
            $('[data-item-key="' + itemKey + '"]').attr('data-max-qty', actualQty - otherAssigned);

            // Update price display from the actual cart line discount.
            var originalUnit = (updatedItem.original_price != null ? updatedItem.original_price : updatedItem.price) / 100;
            var finalUnit = (updatedItem.final_price != null ? updatedItem.final_price : updatedItem.price) / 100;
            var _hasLineDiscount1 = finalUnit < originalUnit || (updatedItem.original_line_price != null && updatedItem.original_line_price > updatedItem.final_line_price);
            $itemWrapper.find('.cart-item__unit')
              .attr('data-unit', originalUnit)
              .attr('data-discounted-unit', finalUnit < originalUnit ? finalUnit : '')
              .attr('data-has-discount', _hasLineDiscount1 ? 'true' : '');

            // Update recipient allocation first so the map rebuild in _refreshRecipientItemTotals
            // reads the correct per-recipient quantities from the authoritative cart response.
            self.updateRecipientItemQuantity(recipientId, itemKey, actualRecipientQty);
            // Rebuild map and refresh all recipient cards for this item (replaces single _updateRecipientItemUI).
            self._refreshRecipientItemTotals(itemKey);

            // Update button states
            var isDecreaseDisabled = actualRecipientQty <= 1;
            $decreaseBtn.prop('disabled', isDecreaseDisabled).toggleClass('is-disabled', isDecreaseDisabled);
            $increaseBtn.prop('disabled', false).removeClass('is-disabled');
          } else {
            // Item was removed (quantity = 0) - clean up and re-render
            delete self.itemGiftOptions['single'][itemKey];
            // Clean giftSettings for this item from all recipients
            (self.multiRecipients || []).forEach(function(r) {
              if (r.giftSettings) delete r.giftSettings[itemKey];
            });
            delete self.noshiSettings['single'][itemKey];
            delete self.stickerSettings['single'][itemKey];
            delete self.wrapOnlyItems['single'][itemKey];
            delete self.noWrapItems['single'][itemKey];
            delete self.singleSplitLines[itemKey];
            delete self.confirmedQuantities[itemKey];
            self.updateSingleRecipientItem(itemKey, 0);
            (self.multiRecipients || []).forEach(function(r) {
              if (r.items) delete r.items[itemKey];
            });
            self.saveData();
            self.renderCartItems();
          }
          self.updateSummary();
        })
        .fail(function() {
          // Restore old recipient qty on error
          $input.val(oldRecipientQty);
          var isDecreaseDisabled = oldRecipientQty <= 1;
          $decreaseBtn.prop('disabled', isDecreaseDisabled).toggleClass('is-disabled', isDecreaseDisabled);
          $increaseBtn.prop('disabled', false).removeClass('is-disabled');
        })
        .always(function() {
          $itemWrapper.removeClass('is-loading');
          $itemWrapper.find('[data-qty-input]').prop('disabled', false);
        });
      });

      // Remove recipient
      $(document).on('click', '[data-remove-recipient]', function(e) {
        e.preventDefault();
        var recipientId = $(this).closest('[data-recipient-card]').data('recipient-id');
        self.removeRecipient(recipientId);
      });

      // Remove single recipient (single mode)
      $(document).on('click', '[data-remove-single-recipient]', function(e) {
        e.preventDefault();
        self.singleRecipient = null;
        self.saveData();
        self.renderCartItems();
        self.updateSummary();
      });

      // Edit single recipient (single mode) - check address list first
      $(document).on('click', '[data-edit-single-recipient]', function(e) {
        e.preventDefault();
        self.handleChangeRecipient();
      });

      // Select interoffice shipment address
      $(document).on('click', '[data-open-interoffice-recipient]', function(e) {
        e.preventDefault();
        if ($(this).hasClass('is-disabled--interoffice')) {
          alert('冷凍またはメール便は社内便をご利用いただけません。');
          return;
        }
        var recipientId = $(this).data('open-interoffice-recipient');
        self.openInterofficeAddressModal(String(recipientId || 'single'));
      });

      // Select recipient from address list (single mode)
      $(document).on('click', '[data-select-recipient-address]', function(e) {
        e.preventDefault();
        self.selectRecipientAddress();
      });

      // Select interoffice address from modal
      $(document).on('click', '[data-select-interoffice-address]', function(e) {
        e.preventDefault();
        self.selectInterofficeAddress();
      });

      // Save edited recipient (single mode)
      $(document).on('click', '[data-save-recipient]', function(e) {
        e.preventDefault();
        self.saveEditedRecipient();
      });

      // Edit saved address from address list modals
      $(document).on('click', '[data-edit-saved-address]', function(e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        var addressId = $(this).data('edit-saved-address');
        self.openEditSavedAddressModal(String(addressId));
      });

      // ✕ close of edit modal: reopen parent modal after common.js restores scroll
      $(document).on('click', '#modalEditSavedAddress .js-close', function() {
        var fromModal = self.openedEditAddressFrom;
        self.editingAddressId = null;
        self.openedEditAddressFrom = null;
        if (!fromModal) return;
        setTimeout(function() {
          self._reopenEditAddressParentModal(fromModal);
        }, 0);
      });

      // Save edited saved address
      $(document).on('click', '[data-save-edited-address]', function(e) {
        e.preventDefault();
        self.saveEditedSavedAddress();
      });

      // Edit recipient in multi mode - open address selection popup
      $(document).on('click', '[data-edit-recipient]', function(e) {
        e.preventDefault();
        var recipientId = $(this).data('edit-recipient');
        self.openChangeAddressModal(recipientId);
      });

      // Change recipient address (multi mode) - replace with selected address
      $(document).on('click', '[data-change-recipient-address]', function(e) {
        e.preventDefault();
        self.changeRecipientAddress();
      });

      // Bulk delivery date save
      $(document).on('click', '[data-save-bulk-time]', function(e) {
        e.preventDefault();
        self.saveBulkDeliveryTime();
      });

      // Bulk wrapping save
      $(document).on('click', '[data-save-bulk-wrapping]', function(e) {
        e.preventDefault();
        self.saveBulkWrapping();
      });

      // Show alert when save button area is clicked while button is disabled
      $(document).on('click', '.js-bulk-wrapping-save-area', function() {
        if ($('[data-save-bulk-wrapping]').prop('disabled')) {
          self.handleBulkWrappingSaveClick();
        }
      });

      // Reset bulk shiptime modal when opened
      $(document).on('click', '[data-id="modalBulkShiptime"]', function() {
        self.resetBulkShiptimeModal();
      });

      // Render bulk wrapping modal when opened
      $(document).on('click', '[data-id="modalBulkWrapping"]', function() {
        self.renderBulkWrappingModal();
      });

      // Bulk wrapping type radio change → show/hide sub-options
      $(document).on('change', '#modalBulkWrapping input[name="bulk_wrapping"]', function() {
        var val = $(this).val();
        $('[data-bulk-noshi-options]').toggle(val === 'noshi');
        $('[data-bulk-ribbon-options]').toggle(val === 'ribbon');
        // Reset bulk noshi preview state when switching away from noshi
        if (val !== 'noshi') {
          self.bulkNoshiPreviewConfirmed = false;
          $('[data-bulk-noshi-preview-wrap]').hide();
          $('.js-bulkGiftPreview').hide();
        }
        self.updateBulkWrappingBtnState();
      });

      // Bulk noshi purpose select → populate tag select (same as individual: filter by entry.type)
      $(document).on('change', '#bulk-noshi-purpose', function() {
        var purpose = $(this).val();
        var data = self.getBulkApplicableEntries(window.noshiData, window.cartItemsApplicableNoshi);
        var $tag = $('#bulk-noshi-tag');
        $tag.html('<option value="">のしの種類をお選びください</option>');
        self.selectedBulkNoshiEntry = null;
        self.currentBulkNoshiMode = null;
        self.bulkNoshiPreviewConfirmed = false;
        $('[data-bulk-noshi-input-fields]').hide();
        $('[data-bulk-noshi-preview-wrap]').hide();
        $('.js-bulkGiftPreview').hide();
        self.updateBulkWrappingBtnState();
        if (!purpose) return;
        data.filter(function(e) { return e.kind === 'のし' && e.type === purpose; }).forEach(function(e) {
          $tag.append('<option value="' + self.escapeHtml(e.gid) + '">' + self.escapeHtml(e.name) + '</option>');
        });
      });

      // Bulk noshi tag select → set entry, show name fields
      $(document).on('change', '#bulk-noshi-tag', function() {
        var gid = $(this).val();
        var data = window.noshiData || [];
        self.selectedBulkNoshiEntry = null;
        self.currentBulkNoshiMode = null;
        self.bulkNoshiPreviewConfirmed = false;
        $('[data-bulk-noshi-input-fields]').hide();
        $('[data-bulk-noshi-preview-wrap]').hide();
        $('.js-bulkGiftPreview').hide();
        self.updateBulkWrappingBtnState();
        if (!gid) return;
        for (var i = 0; i < data.length; i++) {
          if (data[i].gid === gid) { self.selectedBulkNoshiEntry = data[i]; break; }
        }
        if (self.selectedBulkNoshiEntry) {
          self.showBulkNoshiInputFields(self.selectedBulkNoshiEntry);
          $('[data-bulk-noshi-preview-wrap]').show();
        }
      });

      $(document).on('input', '#bulk-noshi-line1, #bulk-noshi-line2', function() {
        if (window.NoshiDialogCommon) {
          window.NoshiDialogCommon.sanitizeNormalNoshiNameInput(this);
        }
      });

      $(document).on('input', '#bulk-noshi-uchiiwai-name, #bulk-noshi-uchiiwai-furigana', function() {
        if (window.NoshiDialogCommon) {
          window.NoshiDialogCommon.sanitizeUchiiwaiNoshiInput(this);
        }
      });

      $(document).on('input', '#bulk-corp-name', function() {
        if (window.NoshiDialogCommon) {
          window.NoshiDialogCommon.sanitizeCorporateNoshiNameInput(this);
        }
      });

      $(document).on('input', '#bulk-corp-company', function() {
        if (window.NoshiDialogCommon) {
          window.NoshiDialogCommon.sanitizeCorporateNoshiCompanyInput(this);
        }
      });

      $(document).on('input', '#bulk-corp-title', function() {
        if (window.NoshiDialogCommon) {
          window.NoshiDialogCommon.sanitizeCorporateNoshiTitleInput(this);
        }
      });

      // Bulk noshi name field input → reset preview confirmation
      $(document).on('input', '#bulk-noshi-line1, #bulk-noshi-line2, #bulk-noshi-uchiiwai-name, #bulk-noshi-uchiiwai-furigana, #bulk-corp-company, #bulk-corp-title, #bulk-corp-name', function() {
        self.bulkNoshiPreviewConfirmed = false;
        self.updateBulkWrappingBtnState();
      });

      // Bulk noshi preview button click
      $(document).on('click', '[data-bulk-noshi-preview-btn]', function() {
        self.handleBulkNoshiPreviewClick();
      });

      // Bulk noshi corporate toggle
      $(document).on('change', '#modalBulkWrapping .js-bulkCorpToggle', function() {
        self.handleBulkCorpToggle();
        self.bulkNoshiPreviewConfirmed = false;
        self.updateBulkWrappingBtnState();
      });

      // Bulk ribbon select → update button state
      $(document).on('change', '#bulk-ribbon-unified', function() {
        self.updateBulkWrappingBtnState();
      });

      // Error modal: wrapping radio → show/hide noshi/ribbon sub-options
      $(document).on('change', '.js-errWrapRadio', function() {
        var uid = $(this).data('uid');
        var val = $(this).val();
        var $block = $(this).closest('[data-fail-uid]');
        self.clearErrorWrapValidation($block);
        $('.js-err-noshi-opts[data-uid="' + uid + '"]').toggle(val === 'noshi');
        $('.js-err-ribbon-opts[data-uid="' + uid + '"]').toggle(val === 'ribbon');
        if (val === 'noshi') {
          var $purpose = $block.find('.js-errNoshiPurpose[data-uid="' + uid + '"]');
          if ($purpose.length && $purpose.val()) {
            $purpose.trigger('change');
          }
        }
      });

      // Error modal: noshi purpose → populate tag select (filter by entry.type like individual)
      $(document).on('change', '.js-errNoshiPurpose', function() {
        var uid = $(this).data('uid');
        var $block = $(this).closest('[data-fail-key]');
        var itemKey = $block.data('fail-key');
        var purpose = $(this).val();
        var data = self.getNoshiDataForItem(itemKey);
        var $tag = $('.js-errNoshiTag[data-uid="' + uid + '"]');
        var initialGid = String($tag.attr('data-selected-gid') || '');
        var filtered = [];
        var shouldLock = false;

        $tag.prop('disabled', false);
        $tag.html('<option value="">のしの種類をお選びください</option>');
        $('.js-err-noshi-fields[data-uid="' + uid + '"]').hide();
        if (!purpose) return;
        filtered = data.filter(function(n) { return n.type === purpose; });
        filtered.forEach(function(n) {
          $tag.append('<option value="' + self.escapeHtml(n.gid) + '">' + self.escapeHtml(n.name) + '</option>');
        });
        if (filtered.length === 1) {
          initialGid = filtered[0].gid;
          shouldLock = true;
        }
        if (initialGid && filtered.some(function(n) { return n.gid === initialGid; })) {
          $tag.val(initialGid);
        } else {
          $tag.val('');
        }
        $tag.removeAttr('data-selected-gid');
        if (shouldLock) {
          $tag.prop('disabled', true);
        }
        if ($tag.val()) {
          $tag.trigger('change');
        }
      });

      // Error modal: noshi tag → show name fields, handle uchiiwai
      $(document).on('change', '.js-errNoshiTag', function() {
        var uid = $(this).data('uid');
        var $block = $(this).closest('[data-fail-key]');
        var itemKey = $block.data('fail-key');
        var gid = $(this).val();
        var data = self.getNoshiDataForItem(itemKey);
        var $fields = $('.js-err-noshi-fields[data-uid="' + uid + '"]');
        $fields.hide();
        if (!gid) {
          $('.js-errNoshiCurrentMode[data-uid="' + uid + '"]').val('');
          return;
        }
        var entry = null;
        for (var i = 0; i < data.length; i++) { if (data[i].gid === gid) { entry = data[i]; break; } }
        if (!entry) return;
        self.showErrorNoshiInputFields(uid, entry);
      });

      // Error modal: corp toggle → show/hide corp fields
      $(document).on('change', '.js-errCorpToggle', function() {
        var uid = $(this).data('uid');
        self.handleErrorCorpToggle(uid);
      });

      // Error modal: ribbon unified select — no cascade handler needed (single select with GID values)

      // Error modal: bag option area stays visible; selecting "手提げ袋なし" clears bag checkboxes
      $(document).on('change', '.p-bulk-error-item input[name^="errBag_"]', function() {
        var $item = $(this).closest('.p-bulk-error-item');
        self.clearBagSelectionValidationError($item);
        if ($(this).val() === 'off') {
          $item.find('.js-errBagCb:checked').prop('checked', false).trigger('change');
        }
      });

      // Error modal: bag checkbox → enable/disable paid qty rows
      $(document).on('change', '.js-errBagCb', function() {
        var uid = $(this).data('uid');
        var type = $(this).data('err-bag-type');
        var isChecked = $(this).is(':checked');
        var $item = $(this).closest('.p-bulk-error-item');
        var $offRadio = $item.find('input[name="errBag_' + uid + '"][value="off"]');
        if (isChecked) {
          $offRadio.prop('checked', false);
          self.clearBagSelectionValidationError($item);
        } else if (!$item.find('.js-errBagCb:checked').length && $offRadio.length && !$offRadio.prop('disabled')) {
          $offRadio.prop('checked', true);
        }
        if (type === 'paid') {
          var $rows = $('[data-err-paid-row][data-uid="' + uid + '"]');
          $rows.toggleClass('is-disabled', !isChecked);
          $rows.find('input, button').prop('disabled', !isChecked);
        }
      });

      // Error modal: paid bag qty buttons
      $(document).on('click', '.js-errPaidQtyBtn', function(e) {
        e.preventDefault();
        var uid = $(this).data('uid');
        var vid = $(this).data('vid');
        var step = parseInt($(this).data('step'), 10);
        var $input = $('[data-err-paid-qty][data-uid="' + uid + '"][data-vid="' + vid + '"]');
        var val = Math.min(PAID_BAG_MAX_QTY, Math.max(0, (parseInt($input.val(), 10) || 0) + step));
        $input.val(val);
      });

      // Error modal: save button
      $(document).on('click', '[data-save-bulk-error]', function(e) {
        e.preventDefault();
        self.saveErrorWrappingItems();
      });

      // Bulk bag toggle "付ける" → show bag options & auto-check free bag / "手提げ袋なし" → hide
      $(document).on('change', '#modalBulkWrapping input[name="bulk_bag"]', function() {
        self.clearBagSelectionValidationError($('#modalBulkWrapping'));
        if ($(this).val() === 'on') {
          $('#modalBulkWrapping .js-bagBox').show();
          var $freeCb = $('#modalBulkWrapping [name="bulk_bag_free"]');
          if ($freeCb.length && !$freeCb.is(':checked')) {
            $freeCb.prop('checked', true).trigger('change');
          }
        } else {
          $('#modalBulkWrapping .js-bagBox').hide();
        }
      });

      // Bulk bag checkbox → enable/disable qty rows
      $(document).on('change', '#modalBulkWrapping [name="bulk_bag_free"], #modalBulkWrapping [name="bulk_bag_paid"]', function() {
        var isChecked = $(this).is(':checked');
        var $rows = $(this).closest('.c-card-01__item').find('.optionRow');
        $rows.toggleClass('is-disabled', !isChecked);
        $rows.find('input, button').prop('disabled', !isChecked);
      });

      // Bulk free bag qty buttons
      $(document).on('click', '#modalBulkWrapping .js-bulkFreeQtyBtn', function(e) {
        e.preventDefault();
        var step = parseInt($(this).data('step'), 10);
        var $input = $(this).siblings('.js-bulkFreeQtyInput');
        var val = parseInt($input.val(), 10) || 1;
        $input.val(Math.max(1, val + step));
      });

      // Bulk paid bag qty buttons
      $(document).on('click', '#modalBulkWrapping .js-bulkPaidQtyBtn', function(e) {
        e.preventDefault();
        var step = parseInt($(this).data('step'), 10);
        var $input = $(this).siblings('.js-bulkPaidQtyInput');
        var val = parseInt($input.val(), 10) || 0;
        $input.val(Math.min(PAID_BAG_MAX_QTY, Math.max(0, val + step)));
      });

      // Bulk free bag qty input validation
      $(document).on('change', '#modalBulkWrapping .js-bulkFreeQtyInput', function() {
        var val = parseInt($(this).val(), 10) || 1;
        $(this).val(Math.max(1, val));
      });

      // Bulk paid bag qty input validation
      $(document).on('change', '#modalBulkWrapping .js-bulkPaidQtyInput', function() {
        var val = parseInt($(this).val(), 10) || 0;
        $(this).val(Math.min(PAID_BAG_MAX_QTY, Math.max(0, val)));
      });

      // ── Noshi modal events ──

      // Open noshi modal for specific item
      $(document).on('click', '[data-open-noshi]', function(e) {
        e.preventDefault();
        // Block click if radio is disabled
        var $radio = $(this).closest('label').find('input[type="radio"]');
        if ($radio.prop('disabled')) return;
        var itemKey = $(this).attr('data-open-noshi');
        var recipientId = $(this).data('recipient-id') || $(this).closest('[data-recipient-card]').data('recipient-id') || null;
        var splitLineKey = $(this).data('split-line-key') || null;
        var sourceItemKey = $(this).data('source-item-key') || itemKey;
        self.openNoshiModal(sourceItemKey, recipientId, splitLineKey, sourceItemKey);
      });

      // Save noshi settings
      $(document).on('click', '[data-save-noshi]', function(e) {
        e.preventDefault();
        self.saveNoshi();
      });

      // Show alert when save button is clicked while disabled
      $(document).on('click', '.js-noshi-save-area', function() {
        if ($('#modalGift [data-save-noshi]').prop('disabled')) {
          window.alert('のしプレビューをご確認ください。');
        }
      });

      // Noshi card selection
      $(document).on('click', '#modalGift .js-choose-gift .p-gift-item', function(e) {
        e.preventDefault();
        self.handleNoshiCardSelect($(this));
      });

      // Noshi purpose filtering
      $(document).on('change', '#noshi-purpose-select', function() {
        var purpose = $(this).val() || '';
        self.handleNoshiPurposeChange(purpose);
      });

      // Corporate toggle
      $(document).on('change', '#modalGift .js-corpToggle', function() {
        self.handleCorpToggle();
      });

      $(document).on('input', '#noshi-line1, #noshi-line2', function() {
        if (window.NoshiDialogCommon) {
          window.NoshiDialogCommon.sanitizeNormalNoshiNameInput(this);
        }
      });

      $(document).on('input', '#noshi-uchiiwai-name, #noshi-uchiiwai-furigana', function() {
        if (window.NoshiDialogCommon) {
          window.NoshiDialogCommon.sanitizeUchiiwaiNoshiInput(this);
        }
      });

      $(document).on('input', '#corpName', function() {
        if (window.NoshiDialogCommon) {
          window.NoshiDialogCommon.sanitizeCorporateNoshiNameInput(this);
        }
      });

      $(document).on('input', '#corpCompany', function() {
        if (window.NoshiDialogCommon) {
          window.NoshiDialogCommon.sanitizeCorporateNoshiCompanyInput(this);
        }
      });

      $(document).on('input', '#corpTitle', function() {
        if (window.NoshiDialogCommon) {
          window.NoshiDialogCommon.sanitizeCorporateNoshiTitleInput(this);
        }
      });

      // Noshi input fields → update preview
      $(document).on('input', '#noshi-line1, #noshi-line2, #noshi-uchiiwai-name, #noshi-uchiiwai-furigana, #corpCompany, #corpTitle, #corpName', function() {
        self.updateNoshiPreview();
      });

      $(document).on('input', '.js-errNoshiLine1, .js-errNoshiLine2', function() {
        if (window.NoshiDialogCommon) {
          window.NoshiDialogCommon.sanitizeNormalNoshiNameInput(this);
        }
      });

      $(document).on('input', '.js-errUchiiwaiName, .js-errUchiiwaiKana', function() {
        if (window.NoshiDialogCommon) {
          window.NoshiDialogCommon.sanitizeUchiiwaiNoshiInput(this);
        }
      });

      $(document).on('input', '.js-errCorpName', function() {
        if (window.NoshiDialogCommon) {
          window.NoshiDialogCommon.sanitizeCorporateNoshiNameInput(this);
        }
      });

      $(document).on('input', '.js-errCorpCompany', function() {
        if (window.NoshiDialogCommon) {
          window.NoshiDialogCommon.sanitizeCorporateNoshiCompanyInput(this);
        }
      });

      $(document).on('input', '.js-errCorpTitle', function() {
        if (window.NoshiDialogCommon) {
          window.NoshiDialogCommon.sanitizeCorporateNoshiTitleInput(this);
        }
      });

      // Noshi preview button → fetch preview image from API
      $(document).on('click', '[data-noshi-preview-btn]', function() {
        self.handleNoshiPreviewClick();
      });

      // Open sticker modal for specific item
      $(document).on('click', '[data-open-sticker]', function(e) {
        e.preventDefault();
        // Block click if radio is disabled
        var $radio = $(this).closest('label').find('input[type="radio"]');
        if ($radio.prop('disabled')) return;
        var itemKey = $(this).attr('data-open-sticker');
        var recipientId = $(this).data('recipient-id') || $(this).closest('[data-recipient-card]').data('recipient-id') || null;
        var splitLineKey = $(this).data('split-line-key') || null;
        var sourceItemKey = $(this).data('source-item-key') || itemKey;
        self.openStickerModal(sourceItemKey, recipientId, splitLineKey, sourceItemKey);
      });

      // Save sticker settings
      $(document).on('click', '[data-save-sticker]', function(e) {
        e.preventDefault();
        self.saveSticker();
      });

      // Sticker select change → update preview + enable save
      $(document).on('change', '#sticker-unified-select', function() {
        self.handleStickerSelect($(this));
      });

      // Noshi / sticker count controls
      $(document).on('click', '.js-noshiCountBtn, .js-stickerCountBtn', function(e) {
        var $btn = $(this);
        var $input;
        var current;

        e.preventDefault();
        if ($btn.prop('disabled') || $btn.hasClass('is-disabled')) return;

        $input = $btn.siblings('[data-noshi-count-input], [data-sticker-count-input]').first();
        current = self.syncGiftSettingCountControl($input);
        $input.val(current + (parseInt($btn.data('step'), 10) || 0));
        self.syncGiftSettingCountControl($input);
      });

      $(document).on('input change', '[data-noshi-count-input], [data-sticker-count-input]', function(event) {
        var $input = $(this);
        if (String($input.val() || '').trim() === '') {
          // input イベント（入力中）は空のまま許可（入力継続のため）
          // change イベント（フォーカスアウト）はカートと同じ挙動：エラー表示 + 値を復元
          if (event.type === 'change') {
            var max = self.getGiftSettingCountMax($input);
            $input[0].setCustomValidity('数量を入力してください');
            $input[0].reportValidity();
            $input[0].setCustomValidity('');
            $input.val(max);
            self.syncGiftSettingCountControl($input);
            $input[0].select();
          }
          return;
        }
        self.syncGiftSettingCountControl($input);
      });

      // Package option change — only handle wrap/none (noshi/ribbon handled by overlay click)
      $(document).on('change', 'input[name^="packageOption_"]', function() {
        var val = $(this).val();
        var $packageList = $(this).closest('.js-packageList');
        var itemKey = $packageList.attr('data-item-key');
        var splitLineKey = $packageList.attr('data-split-line-key') || null;
        var sourceItemKey = $packageList.attr('data-source-item-key') || itemKey;
        if (!itemKey) return;

        if (val === 'wrap' || val === 'none') {
          if (self.currentMode === 'multi' && splitLineKey) {
            var recipientId = $(this).closest('[data-recipient-card]').data('recipient-id');
            var recipient = recipientId ? self.getMultiRecipientById(recipientId) : null;
            if (recipient) {
              var rSplitSettings = self.getRecipientSplitLineSettings(recipient, splitLineKey);
              if (rSplitSettings) {
                rSplitSettings.noshi = null; rSplitSettings.ribbon = null;
                rSplitSettings.wrapOnly = (val === 'wrap');
                rSplitSettings.noWrap = (val === 'none');
              }
            }
          } else if (self.currentMode === 'multi') {
            var recipientId = $(this).closest('[data-recipient-card]').data('recipient-id');
            var recipient = recipientId ? self.getMultiRecipientById(recipientId) : null;
            if (recipient) {
              var s = self.getRecipientItemSettings(recipient, itemKey);
              s.noshi = null; s.ribbon = null; s.wrapOnly = false; s.noWrap = false;
              if (val === 'wrap') s.wrapOnly = true;
              else s.noWrap = true;
              // Clear split lines when wrapping is set for all
              if (recipient.splitLines) delete recipient.splitLines[itemKey];
            }
          } else if (splitLineKey) {
            var splitSettings = self.getSingleSplitLineSettings(splitLineKey);
            if (splitSettings) {
              splitSettings.noshi = null;
              splitSettings.ribbon = null;
              splitSettings.wrapOnly = (val === 'wrap');
              splitSettings.noWrap = (val === 'none');
            }
          } else {
            delete self.noshiSettings[self.currentMode][sourceItemKey];
            delete self.stickerSettings[self.currentMode][sourceItemKey];
            delete self.noWrapItems[self.currentMode][sourceItemKey];
            delete self.wrapOnlyItems[self.currentMode][sourceItemKey];
            delete self.singleSplitLines[sourceItemKey];
            if (val === 'wrap') {
              self.wrapOnlyItems[self.currentMode][sourceItemKey] = true;
            } else {
              self.noWrapItems[self.currentMode][sourceItemKey] = true;
            }
          }

          self.saveData();
          self.renderCartItems();
          self.updateSummary();
        }
      });

      // Open bag modal with item context
      $(document).on('click', '[data-open-bag-modal]', function(e) {
        e.preventDefault();
        var itemKey = $(this).data('item-key');
        var variantId = $(this).data('variant-id');
        var recipientId = $(this).data('recipient-id') || null;
        var bagSettingsKey = $(this).data('bag-settings-key') || itemKey;
        self.openBagModal(itemKey, variantId, recipientId, bagSettingsKey);
      });

      // Save bag selection
      $(document).on('click', '[data-save-bag]', function(e) {
        e.preventDefault();
        self.saveBagSelection();
      });

      // Overlay click handlers — open modals without changing checkbox/radio state
      $(document).on('click', '[data-overlay-noshi]', function(e) {
        e.preventDefault();
        e.stopPropagation();
        var itemKey = $(this).attr('data-overlay-noshi');
        var recipientId = $(this).data('recipient-id') || null;
        var splitLineKey = $(this).data('split-line-key') || null;
        var sourceItemKey = $(this).data('source-item-key') || itemKey;
        self.openNoshiModal(sourceItemKey, recipientId, splitLineKey, sourceItemKey);
      });
      $(document).on('click', '[data-overlay-ribbon]', function(e) {
        e.preventDefault();
        e.stopPropagation();
        var itemKey = $(this).attr('data-overlay-ribbon');
        var recipientId = $(this).data('recipient-id') || null;
        var splitLineKey = $(this).data('split-line-key') || null;
        var sourceItemKey = $(this).data('source-item-key') || itemKey;
        self.openStickerModal(sourceItemKey, recipientId, splitLineKey, sourceItemKey);
      });
      $(document).on('click', '[data-overlay-paid-bag]', function(e) {
        e.preventDefault();
        e.stopPropagation();
        var itemKey = $(this).attr('data-item-key');
        var variantId = $(this).attr('data-variant-id');
        var recipientId = $(this).attr('data-recipient-id') || null;
        var bagSettingsKey = $(this).attr('data-bag-settings-key') || itemKey;
        self.openBagModal(itemKey, variantId, recipientId, bagSettingsKey);
      });

      // Bag quantity buttons in modal (paid bags — max 500)
      $(document).on('click', '[data-bag-qty-btn]', function() {
        var $btn = $(this);
        var $row = $btn.closest('.optionRow');
        var $input = $row.find('[data-bag-qty-input]');
        var step = parseInt($btn.data('step'), 10);
        var val = parseInt($input.val(), 10) || 0;
        $input.val(Math.min(PAID_BAG_MAX_QTY, Math.max(0, val + step)));
        self.updateBagModalSubtotal();
      });

      // Bag quantity input change in modal (paid bags — max 500)
      $(document).on('change', '[data-bag-qty-input]', function() {
        var $input = $(this);
        var val = parseInt($input.val(), 10) || 0;
        $input.val(Math.min(PAID_BAG_MAX_QTY, Math.max(0, val)));
        self.updateBagModalSubtotal();
      });

      // Paid bag checkbox — overlay blocks check (when unchecked), uncheck works directly (no overlay)
      $(document).on('change', '[data-bag-type="paid"]', function() {
        var $cb = $(this);
        var $wrapper = $cb.closest('[data-bag-settings-key], [data-item-key]');
        var bagSettingsKey = $wrapper.data('bag-settings-key') || $wrapper.data('item-key');
        var itemKey = $wrapper.data('item-key') || bagSettingsKey;
        if (!bagSettingsKey) return;
        var bagRuleConfig = self.getBagRuleConfigByItemKey(itemKey);
        if (bagRuleConfig.hidePaid || bagRuleConfig.disablePaid) {
          $cb.prop('checked', false);
          return;
        }

        if (!$cb.is(':checked')) {
          if (self.currentMode === 'multi') {
            var recipientId = $cb.closest('[data-recipient-card]').data('recipient-id');
            var recipient = recipientId ? self.getMultiRecipientById(recipientId) : null;
            if (recipient) {
              self.getRecipientItemSettings(recipient, itemKey).paidBagEnabled = false;
            }
          } else {
            self.getSingleItemGiftSettings(bagSettingsKey).paidBagEnabled = false;
          }
          // renderCartItems() でDOMが全置換され $wrapper が stale 化する前に、
          // 生きている $wrapper で bagSelection を確定させる（残りの無料袋が無ければ 'off'）。
          // これにより再描画が確定後の状態で行われ、有料袋を外した瞬間に
          // 「手提げ袋なし」へ自動チェックが入る（無料袋と挙動を揃える）。
          self.syncBagOffLabel($wrapper, bagSettingsKey);
          self.renderCartItems();
          self.updateSummary();
        } else {
          self.syncBagOffLabel($wrapper, bagSettingsKey);
        }
      });

      // Free bag checkbox change — save data + flag on check, save flag on uncheck (keep data)
      $(document).on('change', '[data-bag-type="free"]', function() {
        var $cb = $(this);
        var $wrapper = $cb.closest('[data-bag-settings-key], [data-item-key]');
        var bagSettingsKey = $wrapper.data('bag-settings-key') || $wrapper.data('item-key');
        var itemKey = $wrapper.data('item-key') || bagSettingsKey;
        var variantId = $cb.data('bag-variant-id');
        if (!bagSettingsKey || !variantId) return;
        var bagRuleConfig = self.getBagRuleConfigByItemKey(itemKey);
        var item = self.cartItems.find(function(i) { return i.key === itemKey; });
        var forcedQty = item ? item.quantity : 1;

        if (bagRuleConfig.lockFree) {
          var $forcedQtyInput = $cb.closest('.c-card-01__item').find('.js-bagQtyInput');
          $cb.prop('checked', true);
          if (SHIPPING_DEBUG) console.log('[BAG][freeCb change] lockFree発火:', { itemKey: itemKey, forcedQty: forcedQty, currentInputVal: $forcedQtyInput.val(), stack: new Error().stack.split('\n').slice(1, 4).join(' | ') });
          $forcedQtyInput.val(forcedQty);
        }

        var bagStatusHtml = '';
        if (self.currentMode === 'multi') {
          var recipientId = $cb.closest('[data-recipient-card]').data('recipient-id');
          var recipient = recipientId ? self.getMultiRecipientById(recipientId) : null;
          if (recipient) {
            var s = self.getRecipientItemSettings(recipient, itemKey);
            s.freeBagEnabled = bagRuleConfig.lockFree ? true : $cb.is(':checked');
            if (s.freeBagEnabled) {
              var qty = bagRuleConfig.lockFree ? forcedQty : (parseInt($cb.closest('.c-card-01__item').find('.js-bagQtyInput').val(), 10) || 1);
              s.bags[variantId] = qty;
            }
            bagStatusHtml = self.renderBagStatusSummary(itemKey, 'multi', recipient);
          }
        } else {
          var singleSettings = self.getSingleItemGiftSettings(bagSettingsKey);
          singleSettings.freeBagEnabled = bagRuleConfig.lockFree ? true : $cb.is(':checked');
          if (singleSettings.freeBagEnabled) {
            var qty = bagRuleConfig.lockFree ? forcedQty : (parseInt($cb.closest('.c-card-01__item').find('.js-bagQtyInput').val(), 10) || 1);
            singleSettings.bags[variantId] = qty;
          }
          bagStatusHtml = self.renderBagStatusSummary(bagSettingsKey, self.currentMode, null);
        }
        self.saveData();
        $wrapper.find('[data-bag-status]').html(bagStatusHtml);
        self.updateSummary();
        self.syncBagOffLabel($wrapper, bagSettingsKey);
      });

      // Free bag quantity buttons (max = product quantity)
      $(document).on('click', '.js-bagQtyBtn', function(e) {
        var $btn = $(this);
        e.preventDefault();
        e.stopPropagation();
        if ($btn.prop('disabled')) return;
        var $input = $btn.siblings('.js-bagQtyInput');
        var step = parseInt($btn.data('step'), 10);
        var val = parseInt($input.val(), 10) || 0;
        var newVal = Math.max(0, val + step);

        var $wrapper = $btn.closest('[data-bag-settings-key], [data-item-key]');
        var bagSettingsKey = $wrapper.data('bag-settings-key') || $wrapper.data('item-key');
        var itemKey = $wrapper.data('item-key') || bagSettingsKey;
        var bagRuleConfig = self.getBagRuleConfigByItemKey(itemKey);
        var maxQty = self.getMaxFreeBagQtyForWrapper($wrapper);
        if (SHIPPING_DEBUG) console.log('[BAG][bagQtyBtn] ボタン押下:', { itemKey: itemKey, bagSettingsKey: bagSettingsKey, step: step, before: val, after: newVal, maxQty: maxQty, lockFree: bagRuleConfig.lockFree });
        if (newVal > maxQty) newVal = maxQty;

        $input.val(newVal);
        var $qtyWrap = $btn.closest('.qty');
        $qtyWrap.find('[data-step="1"]').prop('disabled', newVal >= maxQty).toggleClass('is-disabled', newVal >= maxQty);
        $qtyWrap.find('[data-step="-1"]').prop('disabled', newVal <= 0).toggleClass('is-disabled', newVal <= 0);

        var $cb = $btn.closest('.c-card-01__item').find('[data-bag-type="free"]');
        var variantId = $cb.length ? $cb.data('bag-variant-id') : null;
        if (newVal === 0 && $cb.length && $cb.is(':checked')) {
          $cb.prop('checked', false).closest('.option-card').removeClass('is-active');
          $btn.closest('.js-freeBagQty').hide();
          if (bagSettingsKey) {
            if (self.currentMode === 'multi') {
              var recipientId = $btn.closest('[data-recipient-card]').data('recipient-id');
              var recipient = recipientId ? self.getMultiRecipientById(recipientId) : null;
              if (recipient) {
                self.getRecipientItemSettings(recipient, itemKey).freeBagEnabled = false;
                self.saveData();
                $wrapper.find('[data-bag-status]').html(self.renderBagStatusSummary(itemKey, 'multi', recipient));
                self.updateSummary();
              }
            } else {
              var _s = self.getSingleItemGiftSettings(bagSettingsKey);
              _s.freeBagEnabled = false;
              self.saveData();
              $wrapper.find('[data-bag-status]').html(self.renderBagStatusSummary(bagSettingsKey, self.currentMode, null));
              self.updateSummary();
            }
          }
          self.syncBagOffLabel($wrapper, bagSettingsKey);
        } else if (newVal > 0 && $cb.length && !$cb.is(':checked') && bagSettingsKey) {
          $cb.prop('checked', true).closest('.option-card').addClass('is-active');
          if (self.currentMode === 'multi') {
            var recipientId = $btn.closest('[data-recipient-card]').data('recipient-id');
            var recipient = recipientId ? self.getMultiRecipientById(recipientId) : null;
            if (recipient && variantId) {
              var s = self.getRecipientItemSettings(recipient, itemKey);
              s.freeBagEnabled = true;
              s.bags[variantId] = newVal;
              self.saveData();
              $wrapper.find('[data-bag-status]').html(self.renderBagStatusSummary(itemKey, 'multi', recipient));
              self.updateSummary();
            }
          } else {
            var _s = self.getSingleItemGiftSettings(bagSettingsKey);
            _s.freeBagEnabled = true;
            _s.bags[variantId] = newVal;
            self.saveData();
            $wrapper.find('[data-bag-status]').html(self.renderBagStatusSummary(bagSettingsKey, self.currentMode, null));
            self.updateSummary();
          }
        } else if ($cb.length && $cb.is(':checked') && bagSettingsKey) {
          if (self.currentMode === 'multi') {
            var recipientId = $btn.closest('[data-recipient-card]').data('recipient-id');
            var recipient = recipientId ? self.getMultiRecipientById(recipientId) : null;
            if (recipient && variantId) {
              self.getRecipientItemSettings(recipient, itemKey).bags[variantId] = newVal;
              self.saveData();
              $wrapper.find('[data-bag-status]').html(self.renderBagStatusSummary(itemKey, 'multi', recipient));
              self.updateSummary();
            }
          } else {
            var _s = self.getSingleItemGiftSettings(bagSettingsKey);
            _s.bags[variantId] = newVal;
            self.saveData();
            $wrapper.find('[data-bag-status]').html(self.renderBagStatusSummary(bagSettingsKey, self.currentMode, null));
            self.updateSummary();
          }
        }
      });

      // Free bag quantity input change (max = product quantity)
      $(document).on('change', '.js-bagQtyInput', function() {
        var $input = $(this);
        if ($input.prop('disabled')) return;
        var val = parseInt($input.val(), 10) || 0;
        val = Math.max(0, val);

        var $wrapper = $input.closest('[data-bag-settings-key], [data-item-key]');
        var bagSettingsKey = $wrapper.data('bag-settings-key') || $wrapper.data('item-key');
        var itemKey = $wrapper.data('item-key') || bagSettingsKey;
        var bagRuleConfig = self.getBagRuleConfigByItemKey(itemKey);
        var maxQty = self.getMaxFreeBagQtyForWrapper($wrapper);
        if (val > maxQty) val = maxQty;

        $input.val(val);
        var $qtyWrap = $input.closest('.qty');
        $qtyWrap.find('[data-step="1"]').prop('disabled', val >= maxQty).toggleClass('is-disabled', val >= maxQty);
        $qtyWrap.find('[data-step="-1"]').prop('disabled', val <= 0).toggleClass('is-disabled', val <= 0);

        var $cb = $input.closest('.c-card-01__item').find('[data-bag-type="free"]');
        var variantId = $cb.length ? $cb.data('bag-variant-id') : null;
        if (val === 0 && $cb.length && $cb.is(':checked')) {
          $cb.prop('checked', false).closest('.option-card').removeClass('is-active');
          if (bagSettingsKey) {
            if (self.currentMode === 'multi') {
              var recipientId = $input.closest('[data-recipient-card]').data('recipient-id');
              var recipient = recipientId ? self.getMultiRecipientById(recipientId) : null;
              if (recipient) {
                self.getRecipientItemSettings(recipient, itemKey).freeBagEnabled = false;
                self.saveData();
                $wrapper.find('[data-bag-status]').html(self.renderBagStatusSummary(itemKey, 'multi', recipient));
                self.updateSummary();
              }
            } else {
              var _s = self.getSingleItemGiftSettings(bagSettingsKey);
              _s.freeBagEnabled = false;
              self.saveData();
              $wrapper.find('[data-bag-status]').html(self.renderBagStatusSummary(bagSettingsKey, self.currentMode, null));
              self.updateSummary();
            }
          }
        } else if (val > 0 && $cb.length && !$cb.is(':checked') && bagSettingsKey) {
          $cb.prop('checked', true).closest('.option-card').addClass('is-active');
          if (self.currentMode === 'multi') {
            var recipientId = $input.closest('[data-recipient-card]').data('recipient-id');
            var recipient = recipientId ? self.getMultiRecipientById(recipientId) : null;
            if (recipient && variantId) {
              var s = self.getRecipientItemSettings(recipient, itemKey);
              s.freeBagEnabled = true;
              s.bags[variantId] = val;
              self.saveData();
              $wrapper.find('[data-bag-status]').html(self.renderBagStatusSummary(itemKey, 'multi', recipient));
              self.updateSummary();
            }
          } else {
            var _s = self.getSingleItemGiftSettings(bagSettingsKey);
            _s.freeBagEnabled = true;
            _s.bags[variantId] = val;
            self.saveData();
            $wrapper.find('[data-bag-status]').html(self.renderBagStatusSummary(bagSettingsKey, self.currentMode, null));
            self.updateSummary();
          }
        } else if ($cb.length && $cb.is(':checked') && bagSettingsKey) {
          if (self.currentMode === 'multi') {
            var recipientId = $input.closest('[data-recipient-card]').data('recipient-id');
            var recipient = recipientId ? self.getMultiRecipientById(recipientId) : null;
            if (recipient && variantId) {
              self.getRecipientItemSettings(recipient, itemKey).bags[variantId] = val;
              self.saveData();
              $wrapper.find('[data-bag-status]').html(self.renderBagStatusSummary(itemKey, 'multi', recipient));
              self.updateSummary();
            }
          } else {
            var _s = self.getSingleItemGiftSettings(bagSettingsKey);
            _s.bags[variantId] = val;
            self.saveData();
            $wrapper.find('[data-bag-status]').html(self.renderBagStatusSummary(bagSettingsKey, self.currentMode, null));
            self.updateSummary();
          }
        }
      });

      // Prevent qty controls inside bag cards from toggling the parent checkbox/label.
      $(document).on('click', '.qty-wrap', function(e) {
        e.preventDefault();
        e.stopPropagation();
      });
      $(document).on('pointerdown mousedown touchstart', '.qty-wrap, .js-bagQtyBtn, .js-bagQtyInput', function(e) {
        e.stopPropagation();
      });
      $(document).on('click', '.js-bagQtyInput', function(e) {
        e.stopPropagation();
      });

      // Free bag card click — toggle checkbox (label replaced with div)
      $(document).on('click', '.js-freeBag', function(e) {
        if ($(e.target).closest('.qty-wrap').length) return;
        if ($(e.target).is('.option-card__checkbox')) return;
        var $card = $(this);
        var $cb = $card.find('.option-card__checkbox');
        if ($cb.prop('disabled') || $card.hasClass('is-disable')) return;
        $cb.prop('checked', !$cb.is(':checked')).trigger('change');
      });

      // Free bag checkbox change — show/hide qty, sync off label
      $(document).on('change', '.js-freeBag [data-bag-type="free"]', function() {
        var $cb = $(this);
        var $card = $cb.closest('.js-freeBag');
        var $qty = $card.find('.js-freeBagQty');
        if ($cb.is(':checked')) {
          $qty.show();
          // Ensure minimum qty 1
          var $input = $qty.find('.js-bagQtyInput');
          if (parseInt($input.val(), 10) < 1) $input.val(1);
        } else {
          $qty.hide();
        }
      });

      // Bag toggle (付けない) - clear bags when "付けない" is selected
      $(document).on('change', '.js-bagToggle input[type="radio"]', function() {
        var $radio = $(this);
        var value = $radio.val();
        var $wrapper = $radio.closest('[data-bag-settings-key], [data-item-key]');
        var bagSettingsKey = $wrapper.data('bag-settings-key') || $wrapper.data('item-key');
        var itemKey = $wrapper.data('item-key') || bagSettingsKey;
        var bagRuleConfig = self.getBagRuleConfigByItemKey(itemKey);
        self.clearBagSelectionValidationError($wrapper);

        if (value === 'off' && bagRuleConfig.disableOff) {
          $wrapper.find('.js-bagToggle input[value="on"]').prop('checked', true);
          return;
        }
        if (value === 'on' && bagRuleConfig.disableOn) {
          $wrapper.find('.js-bagToggle input[value="off"]').prop('checked', true);
          return;
        }

        if (value === 'off' && bagSettingsKey) {
          // Clear bags for this item/split-line
          if (self.currentMode === 'multi') {
            var recipientId = $radio.closest('[data-recipient-card]').data('recipient-id');
            var recipient = recipientId ? self.getMultiRecipientById(recipientId) : null;
            if (recipient) {
              var s = self.getRecipientItemSettings(recipient, itemKey);
              s.bags = {}; s.freeBagEnabled = false; s.paidBagEnabled = false; s.bagSelection = 'off';
            }
          } else {
            var singleOffSettings = self.getSingleItemGiftSettings(bagSettingsKey);
            singleOffSettings.bags = {};
            singleOffSettings.freeBagEnabled = false;
            singleOffSettings.paidBagEnabled = false;
            singleOffSettings.bagSelection = 'off';
          }
          // Update UI: reset checkboxes, quantities, active states
          $wrapper.find('[data-bag-status]').html('');
          $wrapper.find('[data-bag-off-status]').show();
          $wrapper.find('[data-bag-type="paid"]').prop('checked', false).closest('.option-card').removeClass('is-active');
          $wrapper.find('[data-bag-type="free"]').prop('checked', false).closest('.option-card').removeClass('is-active');
          $wrapper.find('.js-freeBagQty').hide();
          $wrapper.find('.js-bagQtyInput').val(1);
          // Restore paid bag overlay
          $wrapper.find('.js-bagPaid').each(function() {
            var $label = $(this);
            if (!$label.next('[data-overlay-paid-bag]').length) {
              var ik = $wrapper.data('item-key');
              var vid = $label.find('[data-open-bag-modal]').data('variant-id') || '';
              $label.after('<div data-overlay-paid-bag data-item-key="' + ik + '" data-variant-id="' + vid + '" style="position:absolute;top:0;left:0;right:0;bottom:0;z-index:5;cursor:pointer;"></div>');
            }
          });
          self.saveData();
          self.updateSummary();
        }
      });

      // Open product selection modal
      $(document).on('click', '[data-id="modalSelectProducts"]', function(e) {
        e.preventDefault();
        var recipientId = $(this).data('recipient-id');
        self.openSelectProductsModal(recipientId);
      });

      // Product quantity buttons in select products modal (allocation only, no Cart API)
      $(document).on('click', '[data-select-qty-btn]', function() {
        var $btn = $(this);

        // Skip if button is disabled
        if ($btn.prop('disabled')) return;

        var $row = $btn.closest('.cart-item');
        var $input = $row.find('[data-select-qty-input]');
        var $decreaseBtn = $row.find('[data-select-qty-btn][data-step="-1"]');
        var $increaseBtn = $row.find('[data-select-qty-btn][data-step="1"]');
        var itemKey = $row.data('item-key');
        var maxQty = parseInt($row.data('max-qty'), 10) || 999;
        var step = parseInt($btn.data('step'), 10);
        var val = parseInt($input.val(), 10) || 0;
        val = Math.max(0, Math.min(maxQty, val + step));
        $input.val(val);

        // Update button states
        $decreaseBtn.prop('disabled', val <= 0).toggleClass('is-disabled', val <= 0);
        $increaseBtn.prop('disabled', val >= maxQty).toggleClass('is-disabled', val >= maxQty);

        self.updateTempProductSelection(itemKey, val);
        self.updateSelectProductsSummary();
      });

      // Product quantity input change in select products modal (allocation only, no Cart API)
      $(document).on('change', '[data-select-qty-input]', function() {
        var $input = $(this);
        var $row = $input.closest('.cart-item');
        var itemKey = $row.data('item-key');
        var maxQty = parseInt($row.data('max-qty'), 10) || 999;
        var val = parseInt($input.val(), 10) || 0;
        val = Math.max(0, Math.min(maxQty, val));
        $input.val(val);
        self.updateTempProductSelection(itemKey, val);
        self.updateSelectProductsSummary();
      });

      // Save selected products
      $(document).on('click', '[data-save-selected-products]', function(e) {
        e.preventDefault();
        self.saveSelectedProducts();
      });

      // Reset select products modal on close
      $(document).on('click', '#modalSelectProducts .js-close', function() {
        self.selectingProductsRecipientId = null;
        self.tempProductSelections = {};
      });

      // Confirm/cancel mode switch modal
      $(document).on('click', '[data-confirm-switch]', function(e) {
        e.preventDefault();
        self.confirmModeSwitch();
      });

      $(document).on('click', '[data-cancel-switch]', function(e) {
        e.preventDefault();
        self.cancelModeSwitchConfirm();
        self.closeModal();
      });

      $(document).on('click', '#modalBulkSwitch .js-close', function() {
        self.cancelModeSwitchConfirm();
      });

      // Outside-click-to-close disabled for modalBulkSwitch.

      // ── modalSelectAddress: quantity buttons ──
      $(document).on('click', '[data-addr-qty-btn]', function() {
        var $btn = $(this);
        if ($btn.prop('disabled')) return;

        var $item = $btn.closest('[data-addr-item]');
        var $input = $item.find('[data-addr-qty-input]');
        var step = parseInt($btn.data('step'), 10);
        var itemKey = $item.data('item-key');
        var recipientId = $btn.closest('[data-addr-card]').data('recipient-id');

        var val = parseInt($input.val(), 10) || 0;
        val = Math.max(0, val + step);
        $input.val(val);

        self.updateAddrProductQuantity(recipientId, itemKey, val);
      });

      // modalSelectAddress: quantity input change
      $(document).on('change', '[data-addr-qty-input]', function() {
        var $input = $(this);
        var $item = $input.closest('[data-addr-item]');
        var itemKey = $item.data('item-key');
        var recipientId = $input.closest('[data-addr-card]').data('recipient-id');
        var val = parseInt($input.val(), 10) || 0;
        val = Math.max(0, val);
        $input.val(val);

        self.updateAddrProductQuantity(recipientId, itemKey, val);
      });

      // modalSelectAddress: save button
      $(document).on('click', '[data-save-address-products]', function(e) {
        e.preventDefault();
        self.saveSelectAddressProducts();
      });

      // modalSelectAddress: close button (X) - discard, keep state as before
      $(document).on('click', '#modalSelectAddress .js-close', function() {
        self.tempAddrSelections = {};
        self.bulkSelectedRecipients = [];
      });

      // Address search
      $(document).on('input', '[data-address-search]', function() {
        var query = $(this).val().toLowerCase();
        self.filterSavedAddresses(query);
      });

      // modalBulkListAdd close button handler
      $(document).on('click', '#modalBulkListAdd .js-close', function() {
        self.isEditSetupMode = false;
      });

      // modalChangeAddress close button handler
      $(document).on('click', '#modalChangeAddress .js-close', function() {
        self.editingRecipientId = null;
        if (self.editingRecipientFromSelectAddress) {
          self.editingRecipientFromSelectAddress = false;
          // Restore modalSelectAddress after global js-close handler has run
          setTimeout(function() {
            var scrollPos = self._savedScrollPos || 0;
            $('html, body').addClass('freezePage').css('top', -scrollPos + 'px');
            $('#modalSelectAddress').addClass('active');
          }, 0);
          return;
        }
        self.editingRecipientFromSelectAddress = false;
      });

      // Track when "新しいお届け先を追加" is clicked from within modalBulkListAdd
      $(document).on('click', '#modalBulkListAdd .add-new.js-modal[data-id="modalAddAddress"]', function(e) {
        if (self.currentMode === 'multi' && (self.multiRecipients || []).length >= (self.MAX_MULTI_RECIPIENTS || 50)) {
          e.preventDefault();
          self.showMultiRecipientLimitMessage();
          return false;
        }
        self.openedAddAddressFromBulkList = true;
      });

      // Track when "新しいお届け先を追加" is clicked from within modalChangeAddress
      $(document).on('click', '#modalChangeAddress .add-new.js-modal[data-id="modalAddAddress"]', function() {
        self.openedAddAddressFromChangeModal = true;
      });

      // Open bulk add address modal (checkbox multi-select)
      $(document).on('click', '[data-id="modalBulkListAdd"]', function() {
        self.isBulkSetupMode = false;
        self.isEditSetupMode = self.currentMode === 'multi';
        self.renderSavedAddresses();
      });

      // ---- Bulk Import (Excel) ----
      var APP_PROXY_PREFIX = '/apps/ping';
      var bulkImportBatchId = null;
      self.jsValidRowIds = [];         // rowIds of rows that pass both server + client validation

      // Download blank template (CDN static file)
      $(document).on('click', '[data-download-template]', function(e) {
        e.preventDefault();
        var a = document.createElement('a');
        a.href = 'https://cdn.shopify.com/s/files/1/0942/6301/7837/files/YOKUMOKU_ADDRESS.xlsx?v=1774581817';
        a.download = 'YOKUMOKU_ADDRESS.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      });

      // Export existing saved addresses (API)
      $(document).on('click', '[data-export-addresses]', function(e) {
        e.preventDefault();
        var a = document.createElement('a');
        a.href = APP_PROXY_PREFIX + '/customers/addresses/export.xlsx';
        a.download = 'addresses.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      });

      // "ファイルを選択" → trigger hidden file input
      $(document).on('click', '[data-select-import-file]', function() {
        $('#import-file').val('').trigger('click');
      });

      // File selected → upload & validate
      $(document).on('change', '#import-file', function() {
        var file = this.files[0];
        if (!file) return;

        var $btn = $('#modalBulkImport [data-select-import-file]');
        var $btnReselect = $('#btn-import-reselect');
        $btn.prop('disabled', true).text('アップロード中...');
        $btnReselect.prop('disabled', true);

        var formData = new FormData();
        formData.append('file', file);

        $.ajax({
          url: APP_PROXY_PREFIX + '/customers/addresses/imports',
          method: 'POST',
          data: formData,
          processData: false,
          contentType: false,
          xhrFields: { withCredentials: true }
        }).then(function(data) {
          bulkImportBatchId = data.batchId;
          // Close modalBulkImport, open modalBulkImportReport
          $('.p-modal').removeClass('active');
          self.preValidateZipsAndRenderImport(data);
          $('#modalBulkImportReport').addClass('active');
        }).fail(function(xhr) {
          var msg = (xhr.responseJSON && xhr.responseJSON.message) || 'アップロードに失敗しました';
          alert(msg);
        }).always(function() {
          $btn.prop('disabled', false).text('ファイルを選択');
          $btnReselect.prop('disabled', false);
        });
      });

      // Commit valid rows
      $(document).on('click', '[data-import-commit]', function() {
        if (!bulkImportBatchId) return;

        var $btn = $(this);
        $btn.prop('disabled', true).text('登録中...');
        self.showLoading();

        var replaceAll = $('#import-replace-all').is(':checked');
        // Send rowIds so server commits only rows that passed client-side validation
        var commitBody = { replaceAll: replaceAll };
        if (self.jsValidRowIds.length > 0) {
          commitBody.rowIds = self.jsValidRowIds;
        }
        $.ajax({
          url: APP_PROXY_PREFIX + '/customers/addresses/imports/' + bulkImportBatchId + '/commit',
          method: 'POST',
          contentType: 'application/json',
          data: JSON.stringify(commitBody),
          xhrFields: { withCredentials: true }
        }).then(function(data) {
          if (data.jobId) {
            // Async job — poll for completion
            self.pollBulkCommitJob(bulkImportBatchId, data.jobId, $btn, APP_PROXY_PREFIX);
          } else {
            // Fallback: sync response
            self.hideLoading();
            self.showBulkCommitResult(data.summary, $btn);
          }
        }).fail(function(xhr) {
          self.hideLoading();
          var msg = (xhr.responseJSON && xhr.responseJSON.message) || '登録処理に失敗しました';
          $('.p-modal').removeClass('active');
          var $importFailModal = $('#modalBulkImportFail');
          $importFailModal.find('[data-import-fail-message]').text(msg);
          $importFailModal.addClass('active');
          $btn.prop('disabled', false).text('住所を登録する');
        });
      });
      // "ファイルを選択" button in import success/fail modals → open modalBulkImport
      $(document).on('click', '.js-open-import-modal', function(e) {
        e.preventDefault();
        $('.p-modal').removeClass('active');
        $('#modalBulkImport').addClass('active');
      });
      // ---- End Bulk Import ----

      // Search in modalChangeAddress
      $(document).on('input', '[data-change-address-search]', function() {
        var query = $(this).val().toLowerCase();
        var $cards = $('[data-change-address-list] .addr-card');
        if (!query) { $cards.show(); return; }
        $cards.each(function() {
          $(this).toggle($(this).text().toLowerCase().indexOf(query) !== -1);
        });
      });

      // Configure item from warning section
      $(document).on('click', '[data-configure-item]', function(e) {
        e.preventDefault();
        var itemKey = $(this).data('configure-item');
        var recipientId = $(this).data('recipient-id');
        self.scrollToItemConfig(itemKey, recipientId);
      });

      // Quantity change for single mode cart items (js-qtyBtn)
      $(document).on('click', '.js-qtyBtn', function() {
        var $btn = $(this);

        // Skip if button is disabled
        if ($btn.prop('disabled') || $btn.hasClass('is-disabled')) return;

        var $wrapper = $btn.closest('[data-item-key]');
        var $input = $wrapper.find('.qty__input:not(.js-bagQtyInput):not(.js-splitQtyInput)').first();
        var step = parseInt($btn.data('step'), 10);
        var itemKey = $wrapper.data('item-key');
        if (!itemKey || self.isSplitLineKey(itemKey)) return;

        var currentVal = parseInt($input.val(), 10) || 1;
        var requestedVal = Math.max(1, currentVal + step);
        var normalized = self.normalizeRequestedItemQuantity(itemKey, requestedVal, null, true);
        var newVal = normalized.quantity;
        if (newVal === currentVal && step > 0 && normalized.exceeded) return;

        // Update UI immediately
        $input.val(newVal);
        self.updateSingleQtyButtonState($wrapper, itemKey, newVal);

        // Update cart via API (debounced) - API response will set actual value
        self.debouncedUpdateCartQuantity(itemKey, newVal);

        // Update local state
        self.updateSingleRecipientItem(itemKey, newVal);
        self.updateCartItemUI($wrapper, newVal);
      });

      // Capture qty value on focus for single mode (used to restore on invalid change)
      $(document).on('focus', '.qty__input:not([data-select-qty-input]):not([data-qty-input]):not([data-addr-qty-input])', function() {
        var $input = $(this);
        if ($input.hasClass('js-splitQtyInput') || $input.hasClass('js-bagQtyInput') || $input.is('[data-bag-qty-input]')) return;
        $input.data('prevQty', $input.val());
      });

      // Quantity input change for single mode cart items (exclude modal inputs)
      $(document).on('input change', '.qty__input:not([data-select-qty-input]):not([data-qty-input]):not([data-addr-qty-input])', function(e) {
        var $input = $(this);
        if ($input.hasClass('js-splitQtyInput') || $input.hasClass('js-bagQtyInput') || $input.is('[data-bag-qty-input]')) return;
        var rawSingleVal = String($input.val() || '').trim();
        if (rawSingleVal === '' || parseInt(rawSingleVal, 10) <= 0) {
          if (e.type === 'change') {
            var $wrapperSingle = $input.closest('[data-item-key]');
            var itemKeySingle = $wrapperSingle.data('item-key');
            var cartItemSingle = self.cartItems.find(function(i) { return i.key === itemKeySingle; });
            var prevQty = parseInt($input.data('prevQty'), 10)
              || (cartItemSingle ? cartItemSingle.quantity : null)
              || parseInt($input.attr('value'), 10)
              || 1;
            $input.val(prevQty);
            var $cartItemBottomSingle = $input.closest('.cart-item__bottom');
            $cartItemBottomSingle.next('.js-qty-error').remove();
            var errorMsgSingle = rawSingleVal === '' ? 'このアイテムは、1の増分でのみ追加できます' : 'このアイテムの最小値は1です';
            $cartItemBottomSingle.after('<p class="p-form-error-txt js-qty-error" role="alert">' + errorMsgSingle + '</p>');
          }
          return;
        }
        var $wrapper = $input.closest('[data-item-key]');
        var itemKey = $wrapper.data('item-key');
        if (!itemKey || self.isSplitLineKey(itemKey)) return;

        // Only update cart on change (blur/Enter), like cart page - not on every keystroke
        if (e.type !== 'change') return;

        $input.closest('.cart-item__bottom').next('.js-qty-error').remove();
        var normalized = self.normalizeRequestedItemQuantity(itemKey, $input.val(), null, true);
        var newVal = normalized.quantity;

        // Update UI immediately
        $input.val(newVal);
        self.updateSingleQtyButtonState($wrapper, itemKey, newVal);

        // Update cart via API (debounced) - API response will set actual value
        self.debouncedUpdateCartQuantity(itemKey, newVal);

        // Update local state
        self.updateSingleRecipientItem(itemKey, newVal);
        self.updateCartItemUI($wrapper, newVal);
      });

      // Quantity controls for split pseudo-SKU lines (single mode only)
      // +/- はそのラインの数量だけを変更し、合計もその分変更して /cart/change.js に反映する。
      // 他ラインの数量は変えない（既存の updateSingleSplitLineQty による再配分は行わない）。
      $(document).on('click', '.js-splitQtyBtn', function(e) {
        e.preventDefault();
        var $btn = $(this);
        if ($btn.prop('disabled') || $btn.hasClass('is-disabled')) return;

        var $wrapper = $btn.closest('[data-item-key]');
        var splitLineKey = $wrapper.data('item-key');
        if (!self.isSplitLineKey(splitLineKey)) return;

        var parsed = self.parseSplitLineKey(splitLineKey);
        if (!parsed) return;
        var baseItemKey = parsed.itemKey;

        var $input = $wrapper.find('.js-splitQtyInput');
        var step = parseInt($btn.data('step'), 10) || 0;
        var currentLineQty = parseInt($input.val(), 10) || self.getSingleSplitLineQty(splitLineKey) || 1;
        var newLineQty = Math.max(1, currentLineQty + step);

        // 対象ラインの qty を直接更新（他ラインは変えない）
        var cfg = self.singleSplitLines[baseItemKey];
        if (!cfg || !Array.isArray(cfg.lines)) return;
        var targetLine = null;
        for (var i = 0; i < cfg.lines.length; i++) {
          if (String(cfg.lines[i].id) === parsed.lineId) { targetLine = cfg.lines[i]; break; }
        }
        if (!targetLine) return;
        targetLine.qty = newLineQty;
        // Sync noshi/ribbon count to match line qty (all units in a split line share the same gift setting)
        if (targetLine.settings && targetLine.settings.noshi && targetLine.settings.noshi.count) {
          targetLine.settings.noshi.count = newLineQty;
        }
        if (targetLine.settings && targetLine.settings.ribbon && targetLine.settings.ribbon.count) {
          targetLine.settings.ribbon.count = newLineQty;
        }

        // 新合計 = 全ラインの合計
        var newTotal = 0;
        cfg.lines.forEach(function(l) { newTotal += Math.max(1, parseInt(l.qty, 10) || 1); });

        // 合計が 1 以下になった場合は分割を解除
        if (newTotal <= 1) {
          delete self.singleSplitLines[baseItemKey];
        }

        // cartItems を楽観的更新（renderCartItems が正しい合計で normalizeSingleSplitLines を実行できるように）
        var cartItemInList = self.cartItems.find(function(i) { return i.key === baseItemKey; });
        if (cartItemInList) cartItemInList.quantity = newTotal;

        // UI を即時更新
        $input.val(newLineQty);

        if (SHIPPING_DEBUG) console.log('[SPLIT][splitQtyBtn] splitLineKey:', splitLineKey, 'step:', step, 'newLineQty:', newLineQty, 'newTotal:', newTotal);

        // Shopify カートを AJAX で更新（debounce 300ms）
        self.debouncedUpdateCartQuantity(baseItemKey, newTotal);

        // ローカル状態を更新（singleRecipient.items + saveData + updateSummary）
        self.updateSingleRecipientItem(baseItemKey, newTotal);
        self.renderCartItems();
      });

      $(document).on('change', '.js-splitQtyInput', function() {
        var $input = $(this);
        var $wrapper = $input.closest('[data-item-key]');
        var splitLineKey = $wrapper.data('item-key');
        if (!self.isSplitLineKey(splitLineKey)) return;

        var parsed = self.parseSplitLineKey(splitLineKey);
        if (!parsed) { self.renderCartItems(); return; }
        var baseItemKey = parsed.itemKey;

        var newLineQty = Math.max(1, parseInt($input.val(), 10) || 1);
        $input.val(newLineQty);

        var cfg = self.singleSplitLines[baseItemKey];
        if (!cfg || !Array.isArray(cfg.lines)) { self.renderCartItems(); return; }
        var targetLine = null;
        for (var i = 0; i < cfg.lines.length; i++) {
          if (String(cfg.lines[i].id) === parsed.lineId) { targetLine = cfg.lines[i]; break; }
        }
        if (!targetLine) { self.renderCartItems(); return; }

        targetLine.qty = newLineQty;
        // Sync noshi/ribbon count to match line qty
        if (targetLine.settings && targetLine.settings.noshi && targetLine.settings.noshi.count) {
          targetLine.settings.noshi.count = newLineQty;
        }
        if (targetLine.settings && targetLine.settings.ribbon && targetLine.settings.ribbon.count) {
          targetLine.settings.ribbon.count = newLineQty;
        }

        var newTotal = 0;
        cfg.lines.forEach(function(l) { newTotal += Math.max(1, parseInt(l.qty, 10) || 1); });

        if (newTotal <= 1) {
          delete self.singleSplitLines[baseItemKey];
        }

        var cartItemInList = self.cartItems.find(function(i) { return i.key === baseItemKey; });
        if (cartItemInList) cartItemInList.quantity = newTotal;

        self.debouncedUpdateCartQuantity(baseItemKey, newTotal);
        self.updateSingleRecipientItem(baseItemKey, newTotal);
        self.renderCartItems();
      });

      // Multi-mode split qty buttons: redistribute qty between split lines within recipient
      // Multi-mode split qty buttons: same pattern as single-mode js-splitQtyBtn
      // Changes target line qty only (other lines unchanged), updates recipient total
      $(document).on('click', '.js-multiSplitQtyBtn', function(e) {
        e.preventDefault();
        var $btn = $(this);
        if ($btn.prop('disabled') || $btn.hasClass('is-disabled')) return;

        var $wrapper = $btn.closest('[data-item-key]');
        var splitLineKey = $wrapper.data('item-key');
        if (!self.isSplitLineKey(splitLineKey)) return;

        var recipientId = $wrapper.closest('[data-recipient-card]').data('recipient-id');
        if (!recipientId) return;
        var recipient = self.getMultiRecipientById(recipientId);
        if (!recipient || !recipient.splitLines) return;

        var parsed = self.parseSplitLineKey(splitLineKey);
        if (!parsed) return;
        var baseItemKey = parsed.itemKey;
        var cfg = recipient.splitLines[baseItemKey];
        if (!cfg || !Array.isArray(cfg.lines)) return;

        var targetLine = null;
        for (var i = 0; i < cfg.lines.length; i++) {
          if (String(cfg.lines[i].id) === parsed.lineId) { targetLine = cfg.lines[i]; break; }
        }
        if (!targetLine) return;

        var step = parseInt($btn.data('step'), 10) || 0;
        var currentLineQty = parseInt(targetLine.qty, 10) || 1;
        var newLineQty = Math.max(1, currentLineQty + step);

        // Update target line qty only (other lines stay the same — same as single mode)
        targetLine.qty = newLineQty;
        // Sync noshi/ribbon count to match line qty
        if (targetLine.settings && targetLine.settings.noshi && targetLine.settings.noshi.count) {
          targetLine.settings.noshi.count = newLineQty;
        }
        if (targetLine.settings && targetLine.settings.ribbon && targetLine.settings.ribbon.count) {
          targetLine.settings.ribbon.count = newLineQty;
        }

        // New recipient total = sum of all split lines
        var newRecipientTotal = 0;
        cfg.lines.forEach(function(l) { newRecipientTotal += Math.max(1, parseInt(l.qty, 10) || 1); });

        // Collapse split if total <= 1
        if (newRecipientTotal <= 1) {
          delete recipient.splitLines[baseItemKey];
        }

        // Update recipient allocation
        if (!recipient.items) recipient.items = {};
        recipient.items[baseItemKey] = newRecipientTotal;

        // Update cart total (sum across all recipients + unassigned)
        var cartItem = self.cartItems.find(function(ci) { return ci.key === baseItemKey; });
        var newCartTotal = 0;
        self.multiRecipients.forEach(function(r) {
          newCartTotal += (r.items && r.items[baseItemKey]) || 0;
        });
        if (cartItem) cartItem.quantity = newCartTotal;

        // Update Shopify cart via API (debounced)
        self.debouncedUpdateCartQuantity(baseItemKey, newCartTotal);

        self.saveData();
        self.renderCartItems();
        self.updateSummary();
      });

      // Remove item button (X) click handler
      // Single mode (no split): removes entire item from cart (quantity = 0 via Cart API)
      // Single mode (split card): removes only that split line, reduces cart qty by that line's qty;
      //   if only 1 split line remains after removal, reverts to non-split and preserves its settings (B1)
      // Multi mode: removes item from that recipient's allocation only
      $(document).on('click', '.js-removeItem', function(e) {
        e.preventDefault();
        var $btn = $(this);
        var $wrapper = $btn.closest('[data-item-key]');
        var splitLineKey = $wrapper.data('item-key');
        var sourceItemKey = $wrapper.data('source-item-key');
        // For split cards: data-source-item-key is the real Shopify cart key,
        // data-item-key is the synthetic split key (e.g. "abc::split:1").
        var itemKey = sourceItemKey || splitLineKey;

        if (!itemKey) return;

        // Multi mode: remove item from recipient allocation, not from cart
        var $recipientCard = $btn.closest('[data-recipient-card]');
        if (self.isMultiAddress && $recipientCard.length > 0) {
          var recipientId = $recipientCard.data('recipient-id');
          var recipient = self.multiRecipients.find(function(r) { return r.id === recipientId; });
          if (!recipient) return;

          var isSplitCard = sourceItemKey && self.isSplitLineKey(String(splitLineKey));
          if (isSplitCard) {
            // Delete only this split line from recipient's split config
            var parsed = self.parseSplitLineKey(String(splitLineKey));
            if (!parsed) return;
            var cfg = recipient.splitLines && recipient.splitLines[sourceItemKey];
            if (!cfg || !Array.isArray(cfg.lines)) return;
            var lineToRemove = cfg.lines.find(function(l) { return String(l.id) === String(parsed.lineId); });
            if (!lineToRemove) return;

            var removeQty = Math.max(1, parseInt(lineToRemove.qty, 10) || 1);
            var remainingLines = cfg.lines.filter(function(l) { return String(l.id) !== String(parsed.lineId); });
            var removedSplitKey = splitLineKey;

            // Reduce recipient item allocation by the removed line's qty
            if (recipient.items && recipient.items[sourceItemKey]) {
              var newRecipientQty = (parseInt(recipient.items[sourceItemKey], 10) || 0) - removeQty;
              if (newRecipientQty <= 0) {
                delete recipient.items[sourceItemKey];
              } else {
                recipient.items[sourceItemKey] = newRecipientQty;
              }
            }

            if (remainingLines.length <= 1) {
              // Collapse to non-split: migrate remaining line's gift settings (bags + noshi/ribbon) to item-level
              if (remainingLines.length === 1) {
                var remaining = remainingLines[0];
                var settings = remaining.settings || {};
                var remainingSplitKey = self.makeSplitLineKey(sourceItemKey, remaining.id);
                // Clone bag settings from the remaining split line (or fall back to defaults)
                var remainingGiftSettings = recipient.giftSettings && recipient.giftSettings[remainingSplitKey];
                var mergedSettings = remainingGiftSettings
                  ? JSON.parse(JSON.stringify(remainingGiftSettings))
                  : self.createDefaultGiftSettings();
                // Overwrite gift-wrap fields from split line settings object
                mergedSettings.noshi = settings.noshi || null;
                mergedSettings.ribbon = settings.ribbon || null;
                mergedSettings.wrapOnly = settings.wrapOnly || false;
                mergedSettings.noWrap = settings.noWrap || false;
                if (!recipient.giftSettings) recipient.giftSettings = {};
                recipient.giftSettings[sourceItemKey] = mergedSettings;
                // Cleanup split-line-keyed gift settings
                delete recipient.giftSettings[remainingSplitKey];
                delete recipient.giftSettings[removedSplitKey];
              } else {
                // 0 lines remaining (edge case): cleanup removed line's gift settings
                if (recipient.giftSettings) delete recipient.giftSettings[removedSplitKey];
              }
              if (recipient.splitLines) delete recipient.splitLines[sourceItemKey];
            } else {
              // Still multiple lines: cleanup only the deleted line's gift settings
              if (recipient.giftSettings) delete recipient.giftSettings[removedSplitKey];
              cfg.lines = remainingLines;
            }
          } else {
            // Non-split card: remove entire item from recipient
            if (recipient.items) {
              delete recipient.items[itemKey];
            }
          }

          // Cancel any pending qty mutation for this item before modifying allocation
          clearTimeout(self._cartQtyTimers && self._cartQtyTimers[itemKey]);
          if (self._cartQtyIntent) delete self._cartQtyIntent[itemKey];
          if (self._cartQtySnapshot) delete self._cartQtySnapshot[itemKey];
          if (!self._cartQtySeq) self._cartQtySeq = {};
          self._cartQtySeq[itemKey] = (self._cartQtySeq[itemKey] || 0) + 1;
          clearTimeout(self._quantityTimers && self._quantityTimers[itemKey]);
          if (self._singleQtyIntent) delete self._singleQtyIntent[itemKey];
          if (!self._quantitySeq) self._quantitySeq = {};
          self._quantitySeq[itemKey] = (self._quantitySeq[itemKey] || 0) + 1;

          self.saveData();
          self.renderCartItems();
          self.updateSummary();
          self.checkIncompleteSettings();
          return;
        }

        // Single mode — split card: remove only this split line
        var isSplitCard = sourceItemKey && self.isSplitLineKey(String(splitLineKey));
        if (isSplitCard) {
          var parsed = self.parseSplitLineKey(String(splitLineKey));
          if (!parsed) return;
          var cfg = self.singleSplitLines[sourceItemKey];
          if (!cfg || !Array.isArray(cfg.lines)) return;
          var lineToRemove = cfg.lines.find(function(l) { return String(l.id) === String(parsed.lineId); });
          if (!lineToRemove) return;

          var removeQty = Math.max(1, parseInt(lineToRemove.qty, 10) || 1);
          var cartItem = (self.cartItems || []).find(function(i) { return i.key === sourceItemKey; });
          if (!cartItem) return;
          var newQty = (parseInt(cartItem.quantity, 10) || 0) - removeQty;

          // Reject concurrent deletes for the same source item to prevent stale quantity overwrites
          if (!self._splitDeleteLock) self._splitDeleteLock = {};
          if (self._splitDeleteLock[sourceItemKey]) return;
          self._splitDeleteLock[sourceItemKey] = true;

          // Cancel any pending qty debounce so the stale update cannot overwrite this delete
          clearTimeout(self._quantityTimers && self._quantityTimers[sourceItemKey]);
          if (self._singleQtyIntent) delete self._singleQtyIntent[sourceItemKey];
          if (self._quantitySeq) self._quantitySeq[sourceItemKey] = (self._quantitySeq[sourceItemKey] || 0) + 1;
          clearTimeout(self._cartQtyTimers && self._cartQtyTimers[sourceItemKey]);
          if (self._cartQtyIntent) delete self._cartQtyIntent[sourceItemKey];
          if (self._cartQtySnapshot) delete self._cartQtySnapshot[sourceItemKey];

          // Disable all split cards for this source item while the delete is in flight
          $('[data-source-item-key="' + sourceItemKey + '"]').addClass('is-loading').find('.js-removeItem').prop('disabled', true).addClass('is-disabled');

          var _splitDeleteAjax = $.ajax({
            url: '/cart/change.js',
            type: 'POST',
            dataType: 'json',
            data: { id: sourceItemKey, quantity: Math.max(0, newQty) }
          });
          if (!self._pendingMutation) self._pendingMutation = {};
          self._pendingMutation[sourceItemKey] = _splitDeleteAjax;
          _splitDeleteAjax.always(function() {
            if (self._pendingMutation && self._pendingMutation[sourceItemKey] === _splitDeleteAjax) {
              delete self._pendingMutation[sourceItemKey];
            }
          });
          _splitDeleteAjax.done(function(cart) {
            delete self._splitDeleteLock[sourceItemKey];
            self.cart = cart;
            self.cartItems = cart.items || [];

            // Re-key detection: automatic discount may change the cart line key on a quantity write.
            // Migrate all state/DOM before mutating the remaining split config.
            if (newQty > 0) {
              var _sdReturnedItem = self.getCartItemByKey(sourceItemKey);
              if (!_sdReturnedItem) {
                var _sdReKeyed = self._findReKeyedCartItem(cartItem, cart.items);
                if (_sdReKeyed) {
                  var _sdOldKey = sourceItemKey;
                  self.migrateItemKey(_sdOldKey, _sdReKeyed.key);
                  sourceItemKey = _sdReKeyed.key;
                  splitLineKey = self.makeSplitLineKey(sourceItemKey, parsed.lineId);
                  cfg = self.singleSplitLines[sourceItemKey];
                  if (self._pendingMutation && self._pendingMutation[_sdOldKey] === _splitDeleteAjax) {
                    self._pendingMutation[sourceItemKey] = _splitDeleteAjax;
                    delete self._pendingMutation[_sdOldKey];
                  }
                }
              }
            }

            if (newQty <= 0) {
              // All qty removed — full cleanup for the source item
              delete self.itemGiftOptions['single'][sourceItemKey];
              (self.multiRecipients || []).forEach(function(r) {
                if (r.giftSettings) delete r.giftSettings[sourceItemKey];
              });
              ['single', 'multi'].forEach(function(m) {
                delete self.noshiSettings[m][sourceItemKey];
                delete self.stickerSettings[m][sourceItemKey];
                delete self.wrapOnlyItems[m][sourceItemKey];
                delete self.noWrapItems[m][sourceItemKey];
              });
              delete self.singleSplitLines[sourceItemKey];
              delete self.confirmedQuantities[sourceItemKey];
              self.updateSingleRecipientItem(sourceItemKey, 0);
              (self.multiRecipients || []).forEach(function(r) {
                if (r.items) delete r.items[sourceItemKey];
              });
            } else {
              // Remove this split line from config
              var remainingLines = cfg.lines.filter(function(l) { return String(l.id) !== String(parsed.lineId); });
              // Clean up bag settings for the removed split line
              delete self.itemGiftOptions['single'][splitLineKey];

              if (remainingLines.length <= 1) {
                // B1: revert to non-split, migrate remaining line's settings to item-level settings
                if (remainingLines.length === 1) {
                  var remaining = remainingLines[0];
                  var settings = remaining.settings || {};
                  if (settings.noshi) self.noshiSettings['single'][sourceItemKey] = settings.noshi;
                  else delete self.noshiSettings['single'][sourceItemKey];
                  if (settings.ribbon) self.stickerSettings['single'][sourceItemKey] = settings.ribbon;
                  else delete self.stickerSettings['single'][sourceItemKey];
                  if (settings.wrapOnly) self.wrapOnlyItems['single'][sourceItemKey] = true;
                  else delete self.wrapOnlyItems['single'][sourceItemKey];
                  if (settings.noWrap) self.noWrapItems['single'][sourceItemKey] = true;
                  else delete self.noWrapItems['single'][sourceItemKey];
                  // Migrate remaining split-line bag settings to source item key
                  var remainingSplitKey = self.makeSplitLineKey(sourceItemKey, remaining.id);
                  if (self.itemGiftOptions['single'][remainingSplitKey]) {
                    self.itemGiftOptions['single'][sourceItemKey] = JSON.parse(JSON.stringify(self.itemGiftOptions['single'][remainingSplitKey]));
                  }
                  delete self.itemGiftOptions['single'][remainingSplitKey];
                }
                delete self.singleSplitLines[sourceItemKey];
              } else {
                cfg.lines = remainingLines;
              }
              delete self.confirmedQuantities[sourceItemKey];
            }

            self.saveData();
            self.renderCartItems();
            self.updateSummary();
          });
          _splitDeleteAjax.fail(function(xhr) {
            console.error('Failed to remove split line:', xhr);
            delete self._splitDeleteLock[sourceItemKey];
            $('[data-source-item-key="' + sourceItemKey + '"]').removeClass('is-loading').find('.js-removeItem').prop('disabled', false).removeClass('is-disabled');
          });
          return;
        }

        // Single mode — non-split card: remove entire item from cart
        // Cancel pending debounce and invalidate any in-flight qty request before deleting
        clearTimeout(self._quantityTimers && self._quantityTimers[itemKey]);
        if (self._singleQtyIntent) delete self._singleQtyIntent[itemKey];
        if (!self._quantitySeq) self._quantitySeq = {};
        self._quantitySeq[itemKey] = (self._quantitySeq[itemKey] || 0) + 1;
        clearTimeout(self._cartQtyTimers && self._cartQtyTimers[itemKey]);
        if (self._cartQtyIntent) delete self._cartQtyIntent[itemKey];
        if (self._cartQtySnapshot) delete self._cartQtySnapshot[itemKey];
        $wrapper.addClass('is-loading');

        var _removeAjax = $.ajax({
          url: '/cart/change.js',
          type: 'POST',
          dataType: 'json',
          data: { id: itemKey, quantity: 0 }
        });
        if (!self._pendingMutation) self._pendingMutation = {};
        self._pendingMutation[itemKey] = _removeAjax;
        _removeAjax.always(function() {
          if (self._pendingMutation && self._pendingMutation[itemKey] === _removeAjax) {
            delete self._pendingMutation[itemKey];
          }
        });
        _removeAjax.done(function(cart) {
          self.cart = cart;
          self.cartItems = cart.items || [];

          // Clean up gift options for removed item
          delete self.itemGiftOptions['single'][itemKey];
          // Clean giftSettings for this item from all recipients
          (self.multiRecipients || []).forEach(function(r) {
            if (r.giftSettings) delete r.giftSettings[itemKey];
          });
          ['single', 'multi'].forEach(function(m) {
            delete self.noshiSettings[m][itemKey];
            delete self.stickerSettings[m][itemKey];
            delete self.wrapOnlyItems[m][itemKey];
            delete self.noWrapItems[m][itemKey];
          });
          delete self.singleSplitLines[itemKey];
          delete self.confirmedQuantities[itemKey];

          // Clean up recipient item allocations
          self.updateSingleRecipientItem(itemKey, 0);
          (self.multiRecipients || []).forEach(function(r) {
            if (r.items) delete r.items[itemKey];
          });

          // Persist cleanup to localStorage (prevents fingerprint mismatch on reload)
          self.saveData();

          // Re-render cart items
          self.renderCartItems();
          self.updateSummary();
        });
        _removeAjax.fail(function(xhr) {
          console.error('Failed to remove cart item:', xhr);
          $wrapper.removeClass('is-loading');
        });
      });

      // Delivery date change handler - update time slots based on selected date and save settings
      $(document).on('change', '[data-shipping-type][data-delivery-date]', function() {
        var $dateSelect = $(this);
        var shippingType = $dateSelect.data('shipping-type');
        var selectedDate = $dateSelect.val();
        var recipientId = $dateSelect.data('recipient-id'); // For multi-mode or 'single'
        var shipmentKey = $dateSelect.data('shipment-key') || shippingType; // Use shipmentKey if available, fallback to shippingType

        $dateSelect.toggleClass('is-error', !selectedDate);
        self.updateTimeSlotsForDate(shippingType, selectedDate, recipientId, shipmentKey);

        // 最短お届け日ヒント: 日付「指定なし」なら表示、日付選択済みなら非表示
        var $hint = $('[data-shipment-key="' + shipmentKey + '"][data-earliest-delivery-hint]');
        if ($hint.length) {
          if (selectedDate) {
            $hint.hide();
          } else {
            $hint.show();
          }
        }

        // Save delivery date to the appropriate location (per shipment)
        self.saveInlineDeliverySettings(shipmentKey, recipientId, selectedDate, null);
      });

      // Delivery time change handler - save settings
      $(document).on('change', '[data-shipping-type][data-delivery-time]', function() {
        var $timeSelect = $(this);
        var shippingType = $timeSelect.data('shipping-type');
        var selectedTime = $timeSelect.val();
        var recipientId = $timeSelect.data('recipient-id'); // For multi-mode or 'single'
        var shipmentKey = $timeSelect.data('shipment-key') || shippingType; // Use shipmentKey if available

        $timeSelect.toggleClass('is-error', !selectedTime);
        // Save delivery time to the appropriate location (per shipment)
        self.saveInlineDeliverySettings(shipmentKey, recipientId, null, selectedTime);
      });

      // Flush pending cart qty updates before navigating away
      // Ensures qty changes made in shipping screen are reflected in cart screen
      $(window).on('pagehide', function() {
        self._flushAllPendingQtyUpdates();
        // Signal cart page to reload from server (not bfcache) when user navigates back
        sessionStorage.setItem('shippingQtyChanged', '1');
      });
    },

    /**
     * config() - Initialize cart data and All-In-Gift config
     * Uses cart data from Liquid (window.initialCart) to avoid AJAX call on page load
     */
    /**
     * Check if cart is empty and redirect to cart page if so
     * @returns {boolean} true if cart is empty (redirect initiated)
     */
    checkCartEmpty: function() {
      if (!this.cart || !this.cart.items || this.cart.items.length === 0) {
        this.clearGuestSessionData();
        window.location.replace('/cart');
        return true;
      }
      return false;
    },

    /**
     * Sync saved recipient quantities with actual cart quantities.
     * Called after loadSavedData() to fix stale quantities when user
     * changed qty in cart screen then returned to shipping screen.
     */
    reconcileQuantitiesWithCart: function() {
      if (!this.cartItems || !this.cartItems.length) return;

      var cartQtyMap = {};
      this.cartItems.forEach(function(item) {
        cartQtyMap[item.key] = item.quantity;
      });

      var changed = false;

      // Fix singleRecipient quantities
      if (this.singleRecipient && this.singleRecipient.items) {
        var self = this;
        Object.keys(this.singleRecipient.items).forEach(function(key) {
          if (cartQtyMap[key] !== undefined) {
            var cartQty = cartQtyMap[key];
            if (self.singleRecipient.items[key] !== cartQty) {
              self.singleRecipient.items[key] = cartQty;
              changed = true;
            }
          } else {
            delete self.singleRecipient.items[key];
            changed = true;
          }
        });
      }

      // Fix multiRecipients quantities
      if (this.multiRecipients && this.multiRecipients.length) {
        var self = this;

        // Helper: fully remove an item from a recipient, including split-line gift entries.
        // Split-key gift settings (e.g. itemKey::split:1) are orphaned if only the parent
        // splitLines config is deleted — iterate lines first to clean them.
        var removeRecipientItem = function(r, key) {
          var splitCfg = r.splitLines && r.splitLines[key];
          if (splitCfg && Array.isArray(splitCfg.lines)) {
            splitCfg.lines.forEach(function(l) {
              if (r.giftSettings) delete r.giftSettings[self.makeSplitLineKey(key, l.id)];
            });
          }
          delete r.items[key];
          if (r.splitLines) delete r.splitLines[key];
          if (r.giftSettings) delete r.giftSettings[key];
        };

        // Helper: reduce split-line quantities from the last line until they sum to newQty,
        // remove zero-qty lines with their gift settings, then collapse or sync gift state.
        var normalizeSplitLines = function(r, key, newQty) {
          var splitCfg = r.splitLines && r.splitLines[key];
          if (!splitCfg || !Array.isArray(splitCfg.lines) || splitCfg.lines.length < 2) {
            self.syncRecipientUnsplitGiftCount(r, key, newQty);
            return;
          }
          var lines = splitCfg.lines;
          var excess = 0;
          lines.forEach(function(l) { excess += (parseInt(l.qty, 10) || 0); });
          excess -= newQty;
          for (var i = lines.length - 1; i >= 0 && excess > 0; i--) {
            var lQty = parseInt(lines[i].qty, 10) || 0;
            var reduce = Math.min(lQty, excess);
            lines[i].qty = lQty - reduce;
            excess -= reduce;
          }
          // Remove zero-qty lines and their gift settings
          for (var i = lines.length - 1; i >= 0; i--) {
            if ((parseInt(lines[i].qty, 10) || 0) <= 0) {
              if (r.giftSettings) delete r.giftSettings[self.makeSplitLineKey(key, lines[i].id)];
              lines.splice(i, 1);
            }
          }
          if (lines.length === 1) {
            // B1 collapse: exactly one line remains — migrate its gift state to the parent key
            // and remove the split config, mirroring the explicit split-delete collapse path.
            var remaining = lines[0];
            var settings = remaining.settings || {};
            var remainingSplitKey = self.makeSplitLineKey(key, remaining.id);
            var remainingGiftSettings = r.giftSettings && r.giftSettings[remainingSplitKey];
            var mergedSettings = remainingGiftSettings
              ? JSON.parse(JSON.stringify(remainingGiftSettings))
              : self.createDefaultGiftSettings();
            mergedSettings.noshi = settings.noshi || null;
            mergedSettings.ribbon = settings.ribbon || null;
            mergedSettings.wrapOnly = settings.wrapOnly || false;
            mergedSettings.noWrap = settings.noWrap || false;
            if (!r.giftSettings) r.giftSettings = {};
            r.giftSettings[key] = mergedSettings;
            delete r.giftSettings[remainingSplitKey];
            if (r.splitLines) delete r.splitLines[key];
          } else {
            // 2+ lines remain: clamp noshi/ribbon counts and bag qtys per line
            lines.forEach(function(line) {
              var lineQty = parseInt(line.qty, 10) || 1;
              if (line.settings) {
                if (line.settings.noshi && line.settings.noshi.count) {
                  line.settings.noshi.count = Math.max(1, Math.min(parseInt(line.settings.noshi.count, 10) || lineQty, lineQty));
                }
                if (line.settings.ribbon && line.settings.ribbon.count) {
                  line.settings.ribbon.count = Math.max(1, Math.min(parseInt(line.settings.ribbon.count, 10) || lineQty, lineQty));
                }
              }
              var splitLineKey = self.makeSplitLineKey(key, line.id);
              var splitGs = r.giftSettings && r.giftSettings[splitLineKey];
              if (splitGs && splitGs.bags) {
                Object.keys(splitGs.bags).forEach(function(vid) {
                  var bagQty = parseInt(splitGs.bags[vid], 10) || 0;
                  if (bagQty > lineQty) splitGs.bags[vid] = lineQty;
                });
              }
            });
          }
        };

        // Step 1: Remove allocations for items no longer in cart — including split-key gift entries.
        this.multiRecipients.forEach(function(r) {
          if (!r.items) return;
          Object.keys(r.items).forEach(function(key) {
            if (cartQtyMap[key] === undefined) {
              removeRecipientItem(r, key);
              changed = true;
            }
          });
        });

        // Step 2: Clamp total assigned to cart qty; normalize split lines and gift state.
        Object.keys(cartQtyMap).forEach(function(key) {
          var cartQty = cartQtyMap[key];
          var totalAssigned = 0;
          (self.multiRecipients || []).forEach(function(r) {
            totalAssigned += (r.items && r.items[key]) ? r.items[key] : 0;
          });
          if (totalAssigned > cartQty) {
            var excess = totalAssigned - cartQty;
            for (var i = self.multiRecipients.length - 1; i >= 0 && excess > 0; i--) {
              var r = self.multiRecipients[i];
              if (r.items && r.items[key] > 0) {
                var canReduce = Math.min(r.items[key], excess);
                r.items[key] -= canReduce;
                excess -= canReduce;
                if (r.items[key] <= 0) {
                  removeRecipientItem(r, key);
                } else {
                  // Normalize split-line quantities to match the new allocation, then clamp noshi/ribbon.
                  normalizeSplitLines(r, key, r.items[key]);
                  var gs = r.giftSettings && r.giftSettings[key];
                  if (gs) {
                    var newQty = r.items[key];
                    if (gs.noshi && gs.noshi.count > newQty) gs.noshi.count = newQty;
                    if (gs.ribbon && gs.ribbon.count > newQty) gs.ribbon.count = newQty;
                  }
                }
              }
            }
            changed = true;
          }
        });
      }

      if (changed) {
        this.saveData();
      }
    },

    config: function() {
      var self = this;

      // Helper function to initialize cart data
      var initializeCart = function(cart, giftConfig) {
        self.cart = cart;
        self.cartItems = cart.items || [];
        self.giftConfig = giftConfig;

        if (SHIPPING_DEBUG) {
        }

        // Redirect to cart if empty
        if (self.checkCartEmpty()) return;

        self.loadSavedData();
        self.reconcileQuantitiesWithCart();

        // Capture inventory data from pre-rendered DOM
        self.captureInventoryData();

        // Fetch customer addresses if logged in, then render
        self.fetchCustomerAddresses().then(function() {
          self.initializeFirstAccess();
          self.render();
        });
      };

      // Use initial cart from Liquid if available (faster, no AJAX needed)
      var cart = window.initialCart || null;

      // Clean login_redirect param from URL if present
      if (new URLSearchParams(window.location.search).get('login_redirect') === '1') {
        var cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('login_redirect');
        history.replaceState(null, '', cleanUrl.pathname + cleanUrl.search + cleanUrl.hash);
      }

      // Snapshot per-line prices to detect any discount change after POST.
      // total_price comparison misses cases where Automatic Discount replaces another promotion
      // (total may stay the same or increase while line discounts change).
      var priceSnapshot = function (c) {
        var lines = c.items.map(function (item) {
          return { key: item.key, original_price: item.original_price, final_price: item.final_price, quantity: item.quantity };
        });
        lines.sort(function (a, b) { return String(a.key).localeCompare(String(b.key)); });
        return JSON.stringify(lines);
      };

      // Automatic Discounts only recalculate on cart WRITE. For any logged-in customer,
      // check API eligibility and Applies to scope before writing — Customer Tags are no
      // longer consulted.
      var needsDiscountRefresh = !!(window.shippingCustomer && window.shippingCustomer.id) && !!cart &&
        !!(cart.items && cart.items.length > 0);

      // Guest path: API-driven stale-discount detection.
      // When the cart has items and no authenticated customer, ask the discount API
      // whether any cart discount is customer-restricted (stale for a guest session).
      var isGuestWithCart = !needsDiscountRefresh && !!cart && !!(cart.items && cart.items.length);

      var preSnapshot = (needsDiscountRefresh || isGuestWithCart) ? priceSnapshot(cart) : null;

      if (needsDiscountRefresh || isGuestWithCart) {
        cart = null; // force AJAX branch below
      }

      // Returns a jQuery Deferred resolving to { should_refresh_cart, needs_discount_recalculation,
      // eligibility_fingerprint }. All decisions are computed server-side using cart facts.
      // Fails open (WRITE booleans true) when the API is unavailable.
      // appliedTitles, cartSubtotal, cartItems, and storedFingerprint must be collected before calling.
      var checkDiscountEligibility = function (appliedTitles, cartSubtotal, cartItems, storedFingerprint) {
        // Use flat JSON scalar params instead of bracket notation to preserve App Proxy HMAC
        // signature compatibility (Shopify signs the original key names; bracket keys are
        // parsed by Express into arrays/objects, causing signature verification to fail).
        var ajaxData = {
          discount_titles_json: JSON.stringify(appliedTitles || []),
          cart_subtotal: cartSubtotal || 0,
          cart_items_json: JSON.stringify((cartItems || []).map(function (item) {
            return {
              variant_id: String(item.variant_id),
              product_id: String(item.product_id),
              quantity: item.quantity
            };
          }))
        };
        if (storedFingerprint) { ajaxData.eligibility_fingerprint = storedFingerprint; }
        return $.ajax({
          url: '/apps/ping/discounts/automatic-rates',
          type: 'GET',
          dataType: 'json',
          traditional: false,
          data: ajaxData
        }).then(function (responseData) {
          return {
            should_refresh_cart: !!(responseData && responseData.should_refresh_cart),
            needs_discount_recalculation: !!(responseData && responseData.needs_discount_recalculation),
            eligibility_fingerprint: (responseData && responseData.eligibility_fingerprint) || null
          };
        }, function () { return { should_refresh_cart: true, needs_discount_recalculation: true, eligibility_fingerprint: null }; }); // API error → fail open
      };

      var fetchCurrentCart = function() {
        var initialCart = window.initialCart;
        if ((needsDiscountRefresh || isGuestWithCart) && initialCart && initialCart.items && initialCart.items.length) {
          if (isGuestWithCart) {
            // Guest path: ask the discount API whether any cart discount is customer-restricted.
            // Collect titles from cart-level and line-level fields (discount_applications is not
            // in the Ajax cart response).
            var _tSeen = {};
            (initialCart.cart_level_discount_applications || []).forEach(function(app) {
              if (app && app.title) _tSeen[app.title] = true;
            });
            (initialCart.items || []).forEach(function(item) {
              (item.line_level_discount_allocations || []).forEach(function(alloc) {
                var app = alloc.discount_application;
                if (app && app.title) _tSeen[app.title] = true;
              });
            });
            var discountTitlesArr = Object.keys(_tSeen);
            if (!discountTitlesArr.length) {
              return $.ajax({ url: window.routes.cart_url + '.js', type: 'GET', dataType: 'json' });
            }
            return $.ajax({
              url: '/apps/ping/discounts/automatic-rates',
              type: 'GET',
              dataType: 'json',
              data: { discount_titles_json: JSON.stringify(discountTitlesArr) }
            }).then(function(data) {
              if (!data || !data.should_refresh_cart) {
                return $.ajax({ url: window.routes.cart_url + '.js', type: 'GET', dataType: 'json' });
              }
              var guestUpdates = {};
              initialCart.items.forEach(function(item) { guestUpdates[item.key] = item.quantity; });
              return $.ajax({
                url: window.routes.cart_update_url + '.js',
                type: 'POST',
                contentType: 'application/json',
                dataType: 'json',
                data: JSON.stringify({ updates: guestUpdates })
              }).then(function(updatedCart) {
                if (priceSnapshot(updatedCart) !== preSnapshot) {
                  window.location.reload();
                  return $.Deferred().promise(); // pending — reload in progress
                }
                return updatedCart;
              });
            }, function() {
              // API error → skip refresh, proceed normally.
              return $.ajax({ url: window.routes.cart_url + '.js', type: 'GET', dataType: 'json' });
            });
          }
          // Collect applied titles and cart facts before the API call.
          // Also read the stored eligibility fingerprint so the API can detect eligibility changes.
          var _shAppliedMap = {};
          (initialCart.cart_level_discount_applications || []).forEach(function(app) {
            if (app && app.title) _shAppliedMap[app.title] = true;
          });
          (initialCart.items || []).forEach(function(item) {
            (item.line_level_discount_allocations || []).forEach(function(alloc) {
              var app = alloc.discount_application;
              if (app && app.title) _shAppliedMap[app.title] = true;
            });
          });
          var _shAppliedTitles = Object.keys(_shAppliedMap);
          var _shFpKey = 'yk_dfp_' + (window.shippingCustomer && window.shippingCustomer.id || '');
          var _shStoredFp = '';
          try { _shStoredFp = localStorage.getItem(_shFpKey) || ''; } catch (e) {}

          return checkDiscountEligibility(
            _shAppliedTitles,
            initialCart.total_price || 0,
            initialCart.items || [],
            _shStoredFp
          ).then(function (result) {
            // Hold the fingerprint in memory; commit only after we know no WRITE is required,
            // or after a WRITE succeeds. A failed WRITE must leave the old fingerprint so the
            // next page load retries recalculation.
            var _shPendingFp = result.eligibility_fingerprint != null
              ? String(result.eligibility_fingerprint)
              : null;
            // Skip WRITE when API confirms no stale discount AND no eligible rule with met minimum is absent.
            // No WRITE required → safe to commit the fingerprint immediately.
            if (!result.should_refresh_cart && !result.needs_discount_recalculation) {
              if (_shPendingFp !== null) { try { localStorage.setItem(_shFpKey, _shPendingFp); } catch (e) {} }
              return $.ajax({ url: window.routes.cart_url + '.js', type: 'GET', dataType: 'json' });
            }
            var updates = {};
            initialCart.items.forEach(function(item) { updates[item.key] = item.quantity; });
            return $.ajax({
              url: window.routes.cart_update_url + '.js',
              type: 'POST',
              contentType: 'application/json',
              dataType: 'json',
              data: JSON.stringify({ updates: updates })
            }).then(function(updatedCart) {
              // WRITE succeeded → commit the fingerprint so the next request does not retry unnecessarily.
              if (_shPendingFp !== null) { try { localStorage.setItem(_shFpKey, _shPendingFp); } catch (e) {} }
              if (priceSnapshot(updatedCart) !== preSnapshot) {
                window.location.reload();
                return $.Deferred().promise(); // pending — reload in progress
              }
              return updatedCart; // prices unchanged → no new discount → proceed normally
            });
          });
        }
        return $.ajax({
          url: window.routes.cart_url + '.js',
          type: 'GET',
          dataType: 'json'
        });
      };

      // Wait for AllInGift to be available
      // Track whether the cart fetch was already attempted so the AllInGift catch
      // does not issue a second /cart/update.js call when fetchCurrentCart() itself fails.
      var _cartFetchStarted = false;
      this.waitForAllInGift()
        .then(function() {
          return window.AllInGift.config();
        })
        .then(function(config) {
          if (cart) {
            // Use cart from Liquid
            initializeCart(cart, config);
          } else {
            // Fetch cart, forcing Script recalculation for a direct post-login visit
            _cartFetchStarted = true;
            return fetchCurrentCart().then(function(ajaxCart) {
              initializeCart(ajaxCart, config);
            });
          }
        })
        .catch(function(err) {
          console.error('Failed to load AllInGift config:', err);
          // Fallback without AllInGift — only fetch cart if not already attempted.
          // If _cartFetchStarted is true the failure came from fetchCurrentCart(), not
          // from AllInGift; calling it again would repeat the /cart/update.js write.
          if (cart) {
            // Use cart from Liquid
            self.cart = cart;
            self.cartItems = cart.items || [];
            if (self.checkCartEmpty()) return;
            self.loadSavedData();
            self.reconcileQuantitiesWithCart();
            self.fetchCustomerAddresses().then(function() {
              self.initializeFirstAccess();
              self.render();
            });
          } else if (!_cartFetchStarted) {
            // AllInGift failed before the cart fetch — safe to attempt once.
            fetchCurrentCart()
            .done(function(ajaxCart) {
              self.cart = ajaxCart;
              self.cartItems = ajaxCart.items || [];
              self.loadSavedData();
              self.reconcileQuantitiesWithCart();
              self.fetchCustomerAddresses().then(function() {
                self.initializeFirstAccess();
                self.render();
              });
            })
            .fail(function(err) {
              console.error('Failed to load cart:', err);
              self.showError('カートの読み込みに失敗しました。ページを再読み込みしてください。');
            });
          } else {
            // Cart fetch already failed — do not retry; show an error.
            console.error('Failed to load cart (not retrying):', err);
            self.showError('カートの読み込みに失敗しました。ページを再読み込みしてください。');
          }
        });
    },

    /**
     * Update cart item UI immediately (price calculation)
     * @param {jQuery} $wrapper - The item wrapper element
     * @param {number} newQuantity - New quantity value
     */
    updateCartItemUI: function($wrapper, newQuantity) {
      var $unit = $wrapper.find('.cart-item__unit').first();
      var unitPrice = parseFloat($unit.attr('data-unit')) || 0;
      var discountedUnit = parseFloat($unit.attr('data-discounted-unit'));
      // Look up authoritative cart item before computing discount flags.
      var _uciKey = $wrapper.data('item-key') || $wrapper.data('source-item-key');
      var _uciCartItem = _uciKey && (this.cartItems || []).find(function(i) { return i.key === _uciKey; });
      var hasUnitDiscount = Number.isFinite(discountedUnit) && discountedUnit < unitPrice;
      var hasDiscount = hasUnitDiscount || ($unit.attr('data-has-discount') === 'true') ||
        (_uciCartItem && _uciCartItem.original_line_price != null && _uciCartItem.original_line_price > _uciCartItem.final_line_price);
      // Update unit price display in case discount changed (e.g. tier pricing by quantity)
      if (hasUnitDiscount) {
        $unit.html(renderDiscountPricePair(formatMoneyYen(unitPrice), formatMoneyYen(discountedUnit)));
      } else {
        $unit.html('<span class="price">' + formatMoneyYen(unitPrice) + '</span>');
      }
      var $total = $wrapper.find('.cart-item__total');
      if (hasDiscount) {
        var origTotal = (_uciCartItem && _uciCartItem.original_line_price != null && _uciCartItem.quantity > 0)
          ? Math.round(_uciCartItem.original_line_price * newQuantity / _uciCartItem.quantity / 100)
          : Math.round(unitPrice * newQuantity);
        var discTotal = (_uciCartItem && _uciCartItem.final_line_price != null && _uciCartItem.quantity > 0)
          ? Math.round(_uciCartItem.final_line_price * newQuantity / _uciCartItem.quantity / 100)
          : Math.round((hasUnitDiscount ? discountedUnit : unitPrice) * newQuantity);
        $total.html(renderDiscountPricePair(formatMoneyYen(origTotal), formatMoneyYen(discTotal)));
      } else {
        var newTotal = (_uciCartItem && _uciCartItem.original_line_price != null && _uciCartItem.quantity > 0)
          ? Math.round(_uciCartItem.original_line_price * newQuantity / _uciCartItem.quantity / 100)
          : Math.round(unitPrice * newQuantity);
        $total.html('<span class="price">' + formatMoneyYen(newTotal) + '</span>');
      }
    },

    getCartItemByKey: function(itemKey) {
      return (this.cartItems || []).find(function(item) { return item.key === itemKey; }) || null;
    },

    // Search cartItems for a surviving line with the same stable identity as oldItem
    // (variant_id + properties + selling_plan) but a different key — indicates Shopify
    // automatic-discount re-key.
    _findReKeyedCartItem: function(oldItem, cartItems) {
      if (!oldItem) return null;
      var vid = oldItem.variant_id;
      var props = oldItem.properties || {};
      var spId = oldItem.selling_plan_allocation ? oldItem.selling_plan_allocation.selling_plan.id : null;
      var pKeys = Object.keys(props);
      var oldKey = oldItem.key;
      return (cartItems || []).find(function(i) {
        if (i.key === oldKey) return false;
        if (i.variant_id !== vid) return false;
        var iSpId = i.selling_plan_allocation ? i.selling_plan_allocation.selling_plan.id : null;
        if (iSpId !== spId) return false;
        var iProps = i.properties || {};
        if (pKeys.length !== Object.keys(iProps).length) return false;
        return pKeys.every(function(k) { return String(iProps[k]) === String(props[k]); });
      }) || null;
    },

    // Migrate all key-indexed state from oldKey to newKey after a Shopify automatic-discount
    // line re-key. Updates in-memory state, per-recipient allocations, timer/seq state,
    // window metafield maps, and DOM data-item-key attributes, then persists to localStorage.
    migrateItemKey: function(oldKey, newKey) {
      if (!oldKey || !newKey || oldKey === newKey) return;
      var self = this;
      var splitOldPrefix = oldKey + '::split:';

      // itemGiftOptions (single mode): parent key and split-derived keys (oldKey::split:*)
      if (self.itemGiftOptions && self.itemGiftOptions['single']) {
        var _gio = self.itemGiftOptions['single'];
        if (_gio[oldKey] !== undefined) {
          _gio[newKey] = _gio[oldKey];
          delete _gio[oldKey];
        }
        Object.keys(_gio).forEach(function(k) {
          if (k.indexOf(splitOldPrefix) === 0) {
            var lineId = k.slice(splitOldPrefix.length);
            _gio[newKey + '::split:' + lineId] = _gio[k];
            delete _gio[k];
          }
        });
      }

      // Nested mode state maps
      ['noshiSettings', 'stickerSettings', 'wrapOnlyItems', 'noWrapItems'].forEach(function(mapName) {
        ['single', 'multi'].forEach(function(mode) {
          if (self[mapName] && self[mapName][mode] && self[mapName][mode][oldKey] !== undefined) {
            self[mapName][mode][newKey] = self[mapName][mode][oldKey];
            delete self[mapName][mode][oldKey];
          }
        });
      });

      // Top-level keyed maps
      if (self.singleSplitLines && self.singleSplitLines[oldKey] !== undefined) {
        self.singleSplitLines[newKey] = self.singleSplitLines[oldKey];
        delete self.singleSplitLines[oldKey];
      }
      if (self.confirmedQuantities && self.confirmedQuantities[oldKey] !== undefined) {
        self.confirmedQuantities[newKey] = self.confirmedQuantities[oldKey];
        delete self.confirmedQuantities[oldKey];
      }

      // singleRecipient items
      if (self.singleRecipient && self.singleRecipient.items &&
          self.singleRecipient.items[oldKey] !== undefined) {
        self.singleRecipient.items[newKey] = self.singleRecipient.items[oldKey];
        delete self.singleRecipient.items[oldKey];
      }

      // multiRecipients: items, giftSettings (incl. split-line derived keys), splitLines
      (self.multiRecipients || []).forEach(function(r) {
        if (r.items && r.items[oldKey] !== undefined) {
          r.items[newKey] = r.items[oldKey];
          delete r.items[oldKey];
        }
        if (r.giftSettings) {
          if (r.giftSettings[oldKey] !== undefined) {
            r.giftSettings[newKey] = r.giftSettings[oldKey];
            delete r.giftSettings[oldKey];
          }
          Object.keys(r.giftSettings).forEach(function(gk) {
            if (gk.indexOf(splitOldPrefix) === 0) {
              var lineId = gk.slice(splitOldPrefix.length);
              r.giftSettings[newKey + '::split:' + lineId] = r.giftSettings[gk];
              delete r.giftSettings[gk];
            }
          });
        }
        if (r.splitLines && r.splitLines[oldKey] !== undefined) {
          r.splitLines[newKey] = r.splitLines[oldKey];
          delete r.splitLines[oldKey];
        }
      });

      // Timer/seq/intent/mutation state
      ['_cartQtyIntent', '_cartQtySnapshot', '_cartQtySeq', '_singleQtyIntent',
       '_quantitySeq', '_pendingMutation', '_cartQtyTimers', '_quantityTimers'].forEach(function(prop) {
        if (self[prop] && self[prop][oldKey] !== undefined) {
          self[prop][newKey] = self[prop][oldKey];
          delete self[prop][oldKey];
        }
      });

      // Window maps indexed by cart line key (set by Liquid on page load)
      [window.cartItemsMetafields, window.cartItemsApplicableNoshi,
       window.cartItemsApplicableRibbon, window.cartItemsNoshiSize].forEach(function(map) {
        if (map && map[oldKey] !== undefined) {
          map[newKey] = map[oldKey];
          delete map[oldKey];
        }
      });

      // Delivery shipment keys: non-mixable items use shippingType + '_' + itemKey.
      // Migrate deliverySettings, deliveryOptions and scheduleMeta caches for all shipping types.
      var _shipTypes = ['ambient', 'frozen', 'mail'];
      var _migrateRecipientDelivery = function(recipient) {
        if (!recipient || !recipient.deliverySettings) return;
        _shipTypes.forEach(function(st) {
          var oldShipKey = st + '_' + oldKey;
          var newShipKey = st + '_' + newKey;
          if (recipient.deliverySettings[oldShipKey] !== undefined) {
            recipient.deliverySettings[newShipKey] = recipient.deliverySettings[oldShipKey];
            delete recipient.deliverySettings[oldShipKey];
          }
        });
      };
      _migrateRecipientDelivery(self.singleRecipient);
      (self.multiRecipients || []).forEach(_migrateRecipientDelivery);

      // Single-mode: deliveryOptionsByShipment and deliveryScheduleMeta
      _shipTypes.forEach(function(st) {
        var oldShipKey = st + '_' + oldKey;
        var newShipKey = st + '_' + newKey;
        if (self.deliveryOptionsByShipment && self.deliveryOptionsByShipment[oldShipKey] !== undefined) {
          self.deliveryOptionsByShipment[newShipKey] = self.deliveryOptionsByShipment[oldShipKey];
          delete self.deliveryOptionsByShipment[oldShipKey];
        }
        if (self.deliveryScheduleMeta && self.deliveryScheduleMeta[oldShipKey] !== undefined) {
          self.deliveryScheduleMeta[newShipKey] = self.deliveryScheduleMeta[oldShipKey];
          delete self.deliveryScheduleMeta[oldShipKey];
        }
      });

      // Multi-mode: deliveryOptionsMulti and deliveryScheduleMetaMulti per recipient
      Object.keys(self.deliveryOptionsMulti || {}).forEach(function(rid) {
        _shipTypes.forEach(function(st) {
          var oldShipKey = st + '_' + oldKey;
          var newShipKey = st + '_' + newKey;
          if (self.deliveryOptionsMulti[rid][oldShipKey] !== undefined) {
            self.deliveryOptionsMulti[rid][newShipKey] = self.deliveryOptionsMulti[rid][oldShipKey];
            delete self.deliveryOptionsMulti[rid][oldShipKey];
          }
        });
      });
      Object.keys(self.deliveryScheduleMetaMulti || {}).forEach(function(rid) {
        _shipTypes.forEach(function(st) {
          var oldShipKey = st + '_' + oldKey;
          var newShipKey = st + '_' + newKey;
          if (self.deliveryScheduleMetaMulti[rid] && self.deliveryScheduleMetaMulti[rid][oldShipKey] !== undefined) {
            self.deliveryScheduleMetaMulti[rid][newShipKey] = self.deliveryScheduleMetaMulti[rid][oldShipKey];
            delete self.deliveryScheduleMetaMulti[rid][oldShipKey];
          }
        });
      });

      // DOM: update key attributes AND jQuery data cache for main wrapper, split cards,
      // and derived bag/split-line-key references. Both .attr() and .data() are required
      // because jQuery's .data() cache does not observe later .attr() writes.
      $('[data-item-key="' + oldKey + '"]')
        .attr('data-item-key', newKey).data('item-key', newKey);
      $('[data-source-item-key="' + oldKey + '"]')
        .attr('data-source-item-key', newKey).data('source-item-key', newKey);
      $('[data-item-key^="' + oldKey + '::split:"]').each(function() {
        var $el = $(this);
        var lineId = $el.attr('data-item-key').slice(splitOldPrefix.length);
        var newSk = newKey + '::split:' + lineId;
        $el.attr('data-item-key', newSk).data('item-key', newSk);
      });
      $('[data-bag-settings-key="' + oldKey + '"]')
        .attr('data-bag-settings-key', newKey).data('bag-settings-key', newKey);
      $('[data-bag-settings-key^="' + oldKey + '::split:"]').each(function() {
        var $el = $(this);
        var lineId = $el.attr('data-bag-settings-key').slice(splitOldPrefix.length);
        var newBk = newKey + '::split:' + lineId;
        $el.attr('data-bag-settings-key', newBk).data('bag-settings-key', newBk);
      });
      $('[data-split-line-key^="' + oldKey + '::split:"]').each(function() {
        var $el = $(this);
        var lineId = $el.attr('data-split-line-key').slice(splitOldPrefix.length);
        var newSlk = newKey + '::split:' + lineId;
        $el.attr('data-split-line-key', newSlk).data('split-line-key', newSlk);
      });
      // data-shipment-key for non-mixable delivery date/time sections
      _shipTypes.forEach(function(st) {
        var oldShipKey = st + '_' + oldKey;
        var newShipKey = st + '_' + newKey;
        $('[data-shipment-key="' + oldShipKey + '"]')
          .attr('data-shipment-key', newShipKey).data('shipment-key', newShipKey);
      });
      // Action attributes: Noshi/ribbon modal triggers and overlay entry points
      ['data-open-noshi', 'data-open-sticker', 'data-overlay-noshi', 'data-overlay-ribbon'].forEach(function(attr) {
        $('[' + attr + '="' + oldKey + '"]').attr(attr, newKey).data(attr, newKey);
      });

      // Persist
      this.saveData();
    },

    getMaxAllowedQuantityForItem: function(itemKey, fallbackVariantId) {
      var hardMax = this.MAX_CART_ITEM_QTY || 400;
      var maxQty = hardMax;
      var item = this.getCartItemByKey(itemKey);
      var variantId = item ? item.variant_id : fallbackVariantId;
      var inventoryMax = variantId != null ? parseInt(this.inventoryData[variantId], 10) : NaN;

      if (isFinite(inventoryMax) && inventoryMax > 0) {
        maxQty = Math.min(maxQty, inventoryMax);
      }

      // カートサジェスト商品の数量上限チェック
      var suggestMax = this.getCartSuggestMaxQty(variantId);
      if (suggestMax !== null) {
        maxQty = Math.min(maxQty, suggestMax);
      }

      // yokumoku.max-cart-pcs (商品単位のカート投入上限) チェック
      var mcpRemain = this.getMaxCartPcsRemainingForItem(itemKey);
      if (mcpRemain !== null) {
        maxQty = Math.min(maxQty, mcpRemain);
      }

      return Math.max(1, maxQty);
    },

    /**
     * yokumoku.max-cart-pcs に基づき、当該ライン分として許容できる残り数量を返す
     * (max_cart_pcs - 同一productIdの他ラインの合計qty)
     * 上限が設定されていない、またはMaxCartPcs不在のときは null
     */
    getMaxCartPcsRemainingForItem: function(itemKey) {
      if (!window.MaxCartPcs || !window.MaxCartPcs.getLimit) return null;
      var item = this.getCartItemByKey(itemKey);
      if (!item) return null;
      var limit = window.MaxCartPcs.getLimit(item.product_id);
      if (limit == null) return null;
      var otherQty = 0;
      (this.cartItems || []).forEach(function(ci) {
        if (String(ci.product_id) !== String(item.product_id)) return;
        if (ci.key === itemKey) return;
        otherQty += parseInt(ci.quantity, 10) || 0;
      });
      return Math.max(0, limit - otherQty);
    },

    /**
     * カートサジェストルールに基づく数量上限を返す
     * サジェスト対象でない場合は null を返す
     */
    getCartSuggestMaxQty: function(variantId) {
      if (!window.cartSuggestRules || !window.cartSuggestRules.length || variantId == null) return null;
      var self = this;
      for (var i = 0; i < window.cartSuggestRules.length; i++) {
        var rule = window.cartSuggestRules[i];
        if (rule.suggestVariantId !== variantId) continue;
        // 対象コレクションに含まれるカート内商品の合計数量を算出
        var total = 0;
        (self.cartItems || []).forEach(function(ci) {
          if (rule.collectionProductIds.indexOf(ci.product_id) !== -1) {
            total += ci.quantity;
          }
        });
        return total;
      }
      return null;
    },

    normalizeRequestedItemQuantity: function(itemKey, requestedQty, fallbackVariantId, notifyOnLimit) {
      var numeric = parseInt(String(requestedQty || '').replace(/[０-９]/g, function(ch) {
        return String.fromCharCode(ch.charCodeAt(0) - 65248);
      }), 10);
      if (!isFinite(numeric)) numeric = 1;
      numeric = Math.max(1, numeric);

      var maxAllowed = this.getMaxAllowedQuantityForItem(itemKey, fallbackVariantId);
      var exceeded = numeric > maxAllowed;
      if (exceeded) {
        numeric = maxAllowed;
        if (notifyOnLimit !== false) {
          var item = this.getCartItemByKey(itemKey);
          var vid = item ? item.variant_id : fallbackVariantId;
          // yokumoku.max-cart-pcs が制約になっている場合は専用メッセージ
          // maxAllowed >= mcpRemain: mcpRemainが最も厳しい制約（＝binding constraint）の場合
          var mcpRemain = this.getMaxCartPcsRemainingForItem(itemKey);
          if (mcpRemain !== null && maxAllowed >= mcpRemain) {
            var mcpLimit = (window.MaxCartPcs && window.MaxCartPcs.getLimit && item)
              ? window.MaxCartPcs.getLimit(item.product_id) : null;
            this.showMaxCartPcsLimitMessage(mcpLimit);
          } else {
            var suggestMax = this.getCartSuggestMaxQty(vid);
            if (suggestMax !== null) {
              this.showCartSuggestLimitMessage(suggestMax);
            } else {
              this.showCartQuantityLimitMessage();
            }
          }
        }
      }

      return {
        quantity: numeric,
        exceeded: exceeded,
        maxAllowed: maxAllowed
      };
    },

    showCartQuantityLimitMessage: function() {
      var now = Date.now();
      if (now - (this._lastQtyLimitAlertAt || 0) < 1200) return;
      this._lastQtyLimitAlertAt = now;
      window.alert(this.CART_QTY_LIMIT_MESSAGE);
    },

    showCartSuggestLimitMessage: function(maxQty) {
      var now = Date.now();
      if (now - (this._lastQtyLimitAlertAt || 0) < 1200) return;
      this._lastQtyLimitAlertAt = now;
      window.alert('こちらの商品は' + maxQty + '点までご注文いただけます');
    },

    showMaxCartPcsLimitMessage: function(limit) {
      var now = Date.now();
      if (now - (this._lastQtyLimitAlertAt || 0) < 1200) return;
      this._lastQtyLimitAlertAt = now;
      var msg = limit != null
        ? 'この商品は' + limit + '個までご購入いただけます。数量を' + limit + '個以下に変更してください。'
        : 'この商品はこれ以上カートに入れられません。';
      window.alert(msg);
    },

    showMultiRecipientLimitMessage: function() {
      var now = Date.now();
      if (now - (this._lastMultiRecipientLimitAlertAt || 0) < 1200) return;
      this._lastMultiRecipientLimitAlertAt = now;
      window.alert(this.MULTI_RECIPIENT_LIMIT_MESSAGE);
    },

    updateSingleQtyButtonState: function($wrapper, itemKey, quantity) {
      if (!$wrapper || !$wrapper.length) return;
      var maxAllowed = this.getMaxAllowedQuantityForItem(itemKey);
      var isDecreaseDisabled = quantity <= 1;
      var isIncreaseDisabled = quantity >= maxAllowed;

      $wrapper.find('.js-qtyBtn[data-step="-1"]')
        .prop('disabled', isDecreaseDisabled)
        .toggleClass('is-disabled', isDecreaseDisabled);
      $wrapper.find('.js-qtyBtn[data-step="1"]')
        .prop('disabled', isIncreaseDisabled)
        .toggleClass('is-disabled', isIncreaseDisabled);
    },

    /**
     * Update single recipient item quantity (local state only, no cart modification)
     * @param {string} itemKey - The cart item key
     * @param {number} quantity - New quantity value for this recipient
     */
    updateSingleRecipientItem: function(itemKey, quantity) {
      // Ensure we have a singleRecipient
      if (!this.singleRecipient) {
        // Create a placeholder recipient if none exists
        this.singleRecipient = {
          id: 'single_' + Date.now(),
          items: {}
        };
      }

      // Initialize items object if not exists
      if (!this.singleRecipient.items) {
        this.singleRecipient.items = {};
      }

      // Update the item quantity for the single recipient
      if (quantity > 0) {
        this.singleRecipient.items[itemKey] = quantity;
      } else {
        delete this.singleRecipient.items[itemKey];
      }

      // Save state and update summary
      if (SHIPPING_DEBUG) { var _dbgBi = $('[data-item-key="' + itemKey + '"]').find('.js-bagQtyInput'); console.log('[BAG][pin3] saveData前 bag DOM:', _dbgBi.val()); }
      this.saveData();
      if (SHIPPING_DEBUG) { var _dbgBi2 = $('[data-item-key="' + itemKey + '"]').find('.js-bagQtyInput'); console.log('[BAG][pin4] saveData後 bag DOM:', _dbgBi2.val()); }
      this.updateSummary();
      if (SHIPPING_DEBUG) { var _dbgBi3 = $('[data-item-key="' + itemKey + '"]').find('.js-bagQtyInput'); console.log('[BAG][pin5] updateSummary後 bag DOM:', _dbgBi3.val()); }
    },

    /**
     * Debounced wrapper for updateCartItemQuantity
     * @param {string} itemKey - The cart item key
     * @param {number} quantity - New quantity value
     */
    debouncedUpdateCartQuantity: function(itemKey, quantity) {
      var self = this;

      if (!this._quantityTimers) this._quantityTimers = {};
      if (!this._quantitySeq) this._quantitySeq = {};
      if (!this._singleQtyIntent) this._singleQtyIntent = {};

      // Per-item timer: clear only the timer for this specific item
      clearTimeout(this._quantityTimers[itemKey]);

      // Track pending intent so pagehide can flush it
      this._singleQtyIntent[itemKey] = quantity;

      // Bump sequence so any in-flight response for this item is treated as stale
      this._quantitySeq[itemKey] = (this._quantitySeq[itemKey] || 0) + 1;
      var seq = this._quantitySeq[itemKey];

      this._quantityTimers[itemKey] = setTimeout(function() {
        delete self._singleQtyIntent[itemKey];
        self.updateCartItemQuantity(itemKey, quantity, seq);
      }, self.DEBOUNCE_DELAY);
    },

    /**
     * Flush all pending (debounced) cart qty updates immediately via sendBeacon.
     * Called on pagehide to ensure cart is updated even if user navigates away
     * before the debounce timer fires.
     */
    _flushAllPendingQtyUpdates: function() {
      var self = this;

      // Flush single-mode pending intents (_singleQtyIntent)
      var singleIntents = self._singleQtyIntent || {};
      Object.keys(singleIntents).forEach(function(itemKey) {
        clearTimeout(self._quantityTimers && self._quantityTimers[itemKey]);
        var qty = singleIntents[itemKey];
        if (qty !== undefined && navigator.sendBeacon) {
          navigator.sendBeacon('/cart/change.js', new URLSearchParams({ id: itemKey, quantity: String(qty) }));
        }
        delete singleIntents[itemKey];
      });

      // Flush multi-mode pending intents (_cartQtyIntent)
      var multiIntents = self._cartQtyIntent || {};
      Object.keys(multiIntents).forEach(function(itemKey) {
        clearTimeout(self._cartQtyTimers && self._cartQtyTimers[itemKey]);
        var qty = multiIntents[itemKey];
        if (qty !== undefined && navigator.sendBeacon) {
          navigator.sendBeacon('/cart/change.js', new URLSearchParams({ id: itemKey, quantity: String(qty) }));
        }
        delete multiIntents[itemKey];
      });
    },

    /**
     * Update cart item quantity via Shopify API
     * @param {string} itemKey - The cart item key
     * @param {number} quantity - New quantity value
     */
    updateCartItemQuantity: function(itemKey, quantity, seq) {
      var self = this;
      var currentItem = this.getCartItemByKey(itemKey);
      var normalized = this.normalizeRequestedItemQuantity(
        itemKey,
        quantity,
        currentItem ? currentItem.variant_id : null,
        true
      );
      quantity = normalized.quantity;

      // Show loading state
      var $wrapper = $('[data-item-key="' + itemKey + '"]');
      $wrapper.find('.qty__input:not(.js-bagQtyInput):not(.js-splitQtyInput)').val(quantity);
      this.updateSingleQtyButtonState($wrapper, itemKey, quantity);

      if (currentItem && currentItem.quantity === quantity) {
        this.updateSingleRecipientItem(itemKey, quantity);
        this.updateCartItemUI($wrapper, quantity);
        return;
      }

      $wrapper.addClass('is-loading');
      $wrapper.find('.js-qtyBtn:not(.js-bagQtyBtn)').prop('disabled', true).addClass('is-disabled');
      $wrapper.find('.qty__input:not(.js-bagQtyInput):not(.js-splitQtyInput)').prop('disabled', true);

      var needsReconcile = false;
      $.ajax({
        url: '/cart/change.js',
        type: 'POST',
        dataType: 'json',
        data: {
          id: itemKey,
          quantity: quantity
        }
      })
      .done(function(cart) {
        // Stale response: delete incremented _quantitySeq while this request was in flight.
        // Shopify may have already applied the old mutation, so fetch a fresh cart to reconcile.
        if (seq !== undefined && self._quantitySeq && self._quantitySeq[itemKey] !== seq) {
          needsReconcile = true;
          var reconcileSeq = self._quantitySeq[itemKey];
          // Wait for any newer pending write to settle before reading authoritative cart state.
          var _pendingMut = self._pendingMutation && self._pendingMutation[itemKey];
          var _afterMut = _pendingMut ? $.when(_pendingMut) : $.when();
          _afterMut.always(function() {
            if (self._quantitySeq[itemKey] !== reconcileSeq) return;
            $.getJSON('/cart.js')
              .done(function(freshCart) {
                if (self._quantitySeq[itemKey] !== reconcileSeq) return;
                var _freshItems = freshCart.items || [];
                var _reKeyedFresh = self._findReKeyedCartItem(currentItem, _freshItems);
                if (_reKeyedFresh && _reKeyedFresh.key !== itemKey) {
                  self.migrateItemKey(itemKey, _reKeyedFresh.key);
                  itemKey = _reKeyedFresh.key;
                }
                self.cart = freshCart;
                self.cartItems = _freshItems;
                self.reconcileQuantitiesWithCart();
                self.renderCartItems();
                self.updateSummary();
                $wrapper.removeClass('is-loading');
                $wrapper.find('.qty__input:not(.js-bagQtyInput):not(.js-splitQtyInput)').prop('disabled', false);
              })
              .fail(function() {
                if (self._quantitySeq[itemKey] !== reconcileSeq) return;
                // Cart state is uncertain — force reload to recover authoritative state.
                window.location.reload();
              });
          });
          return;
        }

        if (self._singleQtyIntent) delete self._singleQtyIntent[itemKey];
        self.cart = cart;
        self.cartItems = cart.items || [];

        // Find the updated item in response
        var updatedItem = cart.items.find(function(item) {
          return item.key === itemKey;
        });
        if (!updatedItem) {
          var _reKeyedItem = self._findReKeyedCartItem(currentItem, cart.items);
          if (_reKeyedItem) {
            self.migrateItemKey(itemKey, _reKeyedItem.key);
            itemKey = _reKeyedItem.key;
            $wrapper = $('[data-item-key="' + itemKey + '"]');
            updatedItem = _reKeyedItem;
          }
        }

        if (updatedItem) {
          // Get the ACTUAL quantity from response (Shopify may have limited it)
          var actualQty = updatedItem.quantity;
          if (actualQty < quantity) {
            self.showCartQuantityLimitMessage();
            // Shopify が制限した実数量を inventoryData に記録し、以降の増加を防ぐ
            self.inventoryData[updatedItem.variant_id] = actualQty;
          } else {
            // Shopify が承認した場合はキャップを解除（さらに増加できるようにする）
            delete self.inventoryData[updatedItem.variant_id];
          }

          // Save confirmed quantity for potential rollback
          self.confirmedQuantities[itemKey] = actualQty;

          // Clamp noshi count if quantity decreased
          if (self.noshiSettings['single'][itemKey] && self.noshiSettings['single'][itemKey].count > actualQty) {
            self.noshiSettings['single'][itemKey].count = actualQty;
            self.saveData();
          }
          if (self.stickerSettings['single'][itemKey] && self.stickerSettings['single'][itemKey].count > actualQty) {
            self.stickerSettings['single'][itemKey].count = actualQty;
            self.saveData();
          }
          (self.multiRecipients || []).forEach(function(r) {
            if (r.giftSettings && r.giftSettings[itemKey] && r.giftSettings[itemKey].noshi &&
                r.giftSettings[itemKey].noshi.count > actualQty) {
              r.giftSettings[itemKey].noshi.count = actualQty;
              self.saveData();
            }
            if (r.giftSettings && r.giftSettings[itemKey] && r.giftSettings[itemKey].ribbon &&
                r.giftSettings[itemKey].ribbon.count > actualQty) {
              r.giftSettings[itemKey].ribbon.count = actualQty;
              self.saveData();
            }
          });
          if (self.singleSplitLines[itemKey]) {
            self.normalizeSingleSplitLines(itemKey, actualQty);
            self.saveData();
          }

          // Update price display from the actual cart line discount.
          var originalUnit = (updatedItem.original_price != null ? updatedItem.original_price : updatedItem.price) / 100;
          var finalUnit = (updatedItem.final_price != null ? updatedItem.final_price : updatedItem.price) / 100;
          var _hasLineDiscount2 = finalUnit < originalUnit || (updatedItem.original_line_price != null && updatedItem.original_line_price > updatedItem.final_line_price);
          $wrapper.find('.cart-item__unit')
            .attr('data-unit', originalUnit)
            .attr('data-discounted-unit', finalUnit < originalUnit ? finalUnit : '')
            .attr('data-has-discount', _hasLineDiscount2 ? 'true' : '');
          self.updateCartItemUI($wrapper, actualQty);
          $wrapper.find('.qty__input:not(.js-bagQtyInput):not(.js-splitQtyInput)').first().val(actualQty);

          // Sync bag qty display with updated product qty
          // Use settings as authoritative source, not DOM (DOM may be corrupted by unknown sync code)
          // Define BEFORE updateSingleRecipientItem to restore correct bag qty immediately after DOM corruption
          var _syncItemKey = itemKey;
          var _syncActualQty = actualQty;
          var _$syncWrapper = $wrapper;
          var syncBagQtyToDom = function() {
            var $bi = _$syncWrapper.find('.js-bagQtyInput').first();
            if (!$bi.length) return;
            var $cb = $bi.closest('.c-card-01__item').find('[data-bag-type="free"]');
            var vid = $cb.length ? String($cb.data('bag-variant-id')) : null;
            var s = vid ? self.getSingleItemGiftSettings(_syncItemKey) : null;
            var savedQty = (s && vid && s.bags) ? (parseInt(s.bags[vid], 10) || 0) : 0;
            if (savedQty < 1) savedQty = 1;
            if (savedQty > _syncActualQty) {
              savedQty = _syncActualQty;
              if (s && vid) { s.bags[vid] = _syncActualQty; self.saveData(); }
              _$syncWrapper.find('[data-bag-status]').html(self.renderBagStatusSummary(_syncItemKey, self.currentMode, null));
            }
            $bi.val(savedQty);
            var $plusBtn = $bi.closest('.qty').find('[data-step="1"]');
            var plusDisabled = savedQty >= _syncActualQty;
            $plusBtn.prop('disabled', plusDisabled).toggleClass('is-disabled', plusDisabled);
            if (SHIPPING_DEBUG) console.log('[BAG][updateCartItemQty] DOM修正完了:', { productQty: _syncActualQty, settingsBagQty: savedQty, plusDisabled: plusDisabled });
          };
          // 即座に実行（line 2878 が bag DOM を汚染した直後に上書きする）
          syncBagQtyToDom();

          // Update button states with max/inventory limits
          self.updateSingleQtyButtonState($wrapper, itemKey, actualQty);

          // Update local state
          if (SHIPPING_DEBUG) console.log('[BAG][pin1] updateSingleRecipientItem前 bag DOM:', $wrapper.find('.js-bagQtyInput').val());
          self.updateSingleRecipientItem(itemKey, actualQty);
          if (SHIPPING_DEBUG) console.log('[BAG][pin2] updateSingleRecipientItem後 bag DOM:', $wrapper.find('.js-bagQtyInput').val());

          // 念のため再実行（updateSingleRecipientItem が上書きした場合に備えて）
          setTimeout(syncBagQtyToDom, 0);
        } else {
          // Item was removed (quantity = 0) - clean up and re-render
          delete self.itemGiftOptions['single'][itemKey];
          // Clean giftSettings for this item from all recipients
          (self.multiRecipients || []).forEach(function(r) {
            if (r.giftSettings) delete r.giftSettings[itemKey];
          });
          ['single', 'multi'].forEach(function(m) {
            delete self.noshiSettings[m][itemKey];
            delete self.stickerSettings[m][itemKey];
            delete self.wrapOnlyItems[m][itemKey];
            delete self.noWrapItems[m][itemKey];
          });
          delete self.singleSplitLines[itemKey];
          delete self.confirmedQuantities[itemKey];
          self.updateSingleRecipientItem(itemKey, 0);
          (self.multiRecipients || []).forEach(function(r) {
            if (r.items) delete r.items[itemKey];
          });
          self.saveData();
          self.renderCartItems();
        }

        // Update summary
        self.updateSummary();
      })
      .fail(function(xhr) {
        // Ignore stale failure responses — a newer update is already in flight
        if (seq !== undefined && self._quantitySeq && self._quantitySeq[itemKey] !== seq) return;

        if (self._singleQtyIntent) delete self._singleQtyIntent[itemKey];
        console.error('Failed to update cart:', xhr);

        // Restore to last confirmed quantity (before user clicked +/-)
        var oldQty = self.confirmedQuantities[itemKey];
        if (oldQty) {
          // Restore old quantity in UI
          $wrapper.find('.qty__input:not(.js-bagQtyInput):not(.js-splitQtyInput)').first().val(oldQty);

          // Update button states
          self.updateSingleQtyButtonState($wrapper, itemKey, oldQty);

          // Update local state
          self.updateSingleRecipientItem(itemKey, oldQty);
          self.updateCartItemUI($wrapper, oldQty);
          self.updateSummary();
        } else {
          // No confirmed quantity stored, re-render to reset state
          self.renderCartItems();
          self.updateSummary();
        }
      })
      .always(function() {
        // Skip if reconciliation GET is in flight — it will handle unlock when it settles.
        if (needsReconcile) return;
        $wrapper.removeClass('is-loading');
        // Re-enable qty input (button states are restored by updateSingleQtyButtonState in done/fail)
        $wrapper.find('.qty__input:not(.js-bagQtyInput):not(.js-splitQtyInput)').prop('disabled', false);
      });
    },

    /**
     * Wait for AllInGift to be available
     */
    waitForAllInGift: function() {
      return new Promise(function(resolve, reject) {
        var attempts = 0;
        var maxAttempts = 50; // 5 seconds max

        var check = function() {
          if (window.AllInGift && typeof window.AllInGift.config === 'function') {
            resolve();
          } else if (attempts >= maxAttempts) {
            reject(new Error('AllInGift not available'));
          } else {
            attempts++;
            setTimeout(check, 100);
          }
        };
        check();
      });
    },

    /**
     * Generate a fingerprint from current cart items (sorted item keys)
     */
    getCartFingerprint: function() {
      if (!this.cartItems || this.cartItems.length === 0) return '';
      return this.cartItems.map(function(item) {
        return item.key;
      }).sort().join('|');
    },

    /**
     * Clear cart-specific saved data from localStorage
     * Keeps reusable data (sender, savedAddresses)
     */
    clearCartSpecificData: function() {
      localStorage.removeItem('shipping_single_recipient');
      localStorage.removeItem('shipping_multi_recipients');
      localStorage.removeItem('shipping_gift_options');
      localStorage.removeItem('shipping_noshi_settings');
      localStorage.removeItem('shipping_sticker_settings');
      localStorage.removeItem('shipping_wraponly_items');
      localStorage.removeItem('shipping_nowrap_items');
      localStorage.removeItem('shipping_single_split_lines');
      localStorage.removeItem('shipping_recipients');
      localStorage.removeItem('shipping_current_mode');
      localStorage.removeItem('shipping_sender_manually_edited');

      this.currentMode = 'single';
      this.senderManuallyEdited = false;
      this.singleRecipient = null;
      this.multiRecipients = [];
      this.itemGiftOptions = { single: {}, multi: {} };
      this.noshiSettings = { single: {}, multi: {} };
      this.stickerSettings = { single: {}, multi: {} };
      this.wrapOnlyItems = { single: {}, multi: {} };
      this.noWrapItems = { single: {}, multi: {} };
      this.singleSplitLines = {};
      this.shippingRatesCache = {};
      this.clearDeliveryOptionsCache();
      this.pendingModeSwitch = null;
    },

    /**
     * Clear all guest-specific data from localStorage.
     * Called when cart becomes empty for guest users so that
     * the next purchase session starts fresh.
     */
    clearGuestSessionData: function() {
      if (window.shippingCustomer) return; // logged-in users keep their account data
      localStorage.removeItem('shipping_orderer');
      localStorage.removeItem('shipping_sender');
      localStorage.removeItem('shipping_sender_manually_edited');
      localStorage.removeItem('shipping_addresses');
      localStorage.removeItem('shipping_cart_fingerprint');
      this.orderer = null;
      this.sender = null;
      this.senderManuallyEdited = false;
      this.savedAddresses = [];
    },

    /**
     * When cart changes (items added/removed), keep gift settings only for items
     * still present in the current cart. This preserves bag selections for
     * unchanged items instead of wiping everything.
     */
    reconcileCartSpecificData: function() {
      var currentKeys = {};
      (this.cartItems || []).forEach(function(item) {
        currentKeys[item.key] = true;
      });

      var filterByKeys = function(data) {
        if (!data) return { single: {}, multi: {} };
        var result = { single: {}, multi: {} };
        ['single', 'multi'].forEach(function(mode) {
          if (data[mode]) {
            Object.keys(data[mode]).forEach(function(k) {
              if (currentKeys[k]) result[mode][k] = data[mode][k];
            });
          }
        });
        return result;
      };

      this.itemGiftOptions = filterByKeys(this.itemGiftOptions);
      this.noshiSettings   = filterByKeys(this.noshiSettings);
      this.stickerSettings = filterByKeys(this.stickerSettings);
      this.wrapOnlyItems   = filterByKeys(this.wrapOnlyItems);
      this.noWrapItems     = filterByKeys(this.noWrapItems);

      // Filter singleSplitLines (keyed by itemKey directly)
      var filteredSplitLines = {};
      Object.keys(this.singleSplitLines || {}).forEach(function(k) {
        if (currentKeys[k]) filteredSplitLines[k] = this.singleSplitLines[k];
      }.bind(this));
      this.singleSplitLines = filteredSplitLines;

      // Persist filtered data back to localStorage
      localStorage.setItem('shipping_gift_options',       JSON.stringify(this.itemGiftOptions));
      localStorage.setItem('shipping_noshi_settings',     JSON.stringify(this.noshiSettings));
      localStorage.setItem('shipping_sticker_settings',   JSON.stringify(this.stickerSettings));
      localStorage.setItem('shipping_wraponly_items',     JSON.stringify(this.wrapOnlyItems));
      localStorage.setItem('shipping_nowrap_items',       JSON.stringify(this.noWrapItems));
      localStorage.setItem('shipping_single_split_lines', JSON.stringify(this.singleSplitLines));
    },

    /**
     * Load saved data from localStorage
     * Clears cart-specific data if cart has changed since last save
     */
    loadSavedData: function() {
      var self = this;
      try {
        // Fresh from cart (not reload): reinitialize cart-specific data
        if (window._freshFromCart) {
          this.clearCartSpecificData();
        }

        // Determine if fingerprint changed (cart items added/removed since last visit)
        var savedFingerprint = localStorage.getItem('shipping_cart_fingerprint');
        var currentFingerprint = this.getCartFingerprint();
        var _cartChanged = !window._freshFromCart && savedFingerprint && savedFingerprint !== currentFingerprint;

        // Save current fingerprint
        localStorage.setItem('shipping_cart_fingerprint', currentFingerprint);

        // Restore current mode (single/multi)
        var savedMode = localStorage.getItem('shipping_current_mode');
        if (savedMode === 'single' || savedMode === 'multi') {
          this.currentMode = savedMode;
          this.isMultiAddress = (savedMode === 'multi');
          if (SHIPPING_DEBUG) console.log('[loadSavedData] Restored currentMode:', savedMode);
        }

        var savedSender = localStorage.getItem('shipping_sender');
        if (savedSender) {
          this.sender = JSON.parse(savedSender);
          if (this.sender) this.sender.phone = this.normalizePhoneInput(this.sender.phone || '');
          this.senderManuallyEdited = !!localStorage.getItem('shipping_sender_manually_edited');
        } else {
          // Inconsistent state: sender missing but manuallyEdited flag was persisted.
          // Drop the orphaned flag so auto-sync from orderer/recipient can run again.
          if (localStorage.getItem('shipping_sender_manually_edited')) {
            localStorage.removeItem('shipping_sender_manually_edited');
          }
          this.senderManuallyEdited = false;
        }

        // Load single and multi recipients separately
        var savedSingleRecipient = localStorage.getItem('shipping_single_recipient');
        if (savedSingleRecipient) {
          this.singleRecipient = JSON.parse(savedSingleRecipient);
          if (this.singleRecipient) this.singleRecipient.phone = this.normalizePhoneInput(this.singleRecipient.phone || '');
        }

        var savedMultiRecipients = localStorage.getItem('shipping_multi_recipients');
        if (savedMultiRecipients) {
          this.multiRecipients = this.normalizeLoadedMultiRecipients(JSON.parse(savedMultiRecipients));
        } else {
          this.multiRecipients = [];
        }

        // Migration: if old shipping_recipients exists, migrate to new format
        var oldRecipients = localStorage.getItem('shipping_recipients');
        if (oldRecipients && !savedSingleRecipient && !savedMultiRecipients) {
          var recipients = JSON.parse(oldRecipients);
          if (recipients && recipients.length > 0) {
            this.singleRecipient = recipients[0];
            if (this.singleRecipient) this.singleRecipient.phone = this.normalizePhoneInput(this.singleRecipient.phone || '');
            this.multiRecipients = this.normalizeLoadedMultiRecipients(recipients);
          }
          // Remove old key after migration
          localStorage.removeItem('shipping_recipients');
        }

        var savedAddresses = localStorage.getItem('shipping_addresses');
        if (savedAddresses) {
          this.savedAddresses = JSON.parse(savedAddresses);
          this.savedAddresses = (this.savedAddresses || []).map(function(address) {
            if (address) address.phone = self.normalizePhoneInput(address.phone || '');
            return address;
          });
        } else {
          this.savedAddresses = [];
        }

        var savedGiftOptions = localStorage.getItem('shipping_gift_options');
        if (savedGiftOptions) {
          var _g = JSON.parse(savedGiftOptions);
          this.itemGiftOptions = (_g && _g.single) ? _g : { single: _g || {}, multi: {} };
          if (!this.itemGiftOptions.multi) this.itemGiftOptions.multi = {};
        }

        var savedNoshiSettings = localStorage.getItem('shipping_noshi_settings');
        if (savedNoshiSettings) {
          var _ns = JSON.parse(savedNoshiSettings);
          this.noshiSettings = (_ns && _ns.single) ? _ns : { single: _ns || {}, multi: {} };
        }

        var savedStickerSettings = localStorage.getItem('shipping_sticker_settings');
        if (savedStickerSettings) {
          var _ss = JSON.parse(savedStickerSettings);
          this.stickerSettings = (_ss && _ss.single) ? _ss : { single: _ss || {}, multi: {} };
        }

        var savedWrapOnlyItems = localStorage.getItem('shipping_wraponly_items');
        if (savedWrapOnlyItems) {
          var _wo = JSON.parse(savedWrapOnlyItems);
          this.wrapOnlyItems = (_wo && _wo.single) ? _wo : { single: _wo || {}, multi: {} };
        }

        var savedNoWrapItems = localStorage.getItem('shipping_nowrap_items');
        if (savedNoWrapItems) {
          var _nw = JSON.parse(savedNoWrapItems);
          this.noWrapItems = (_nw && _nw.single) ? _nw : { single: _nw || {}, multi: {} };
        }

        var savedSingleSplitLines = localStorage.getItem('shipping_single_split_lines');
        if (savedSingleSplitLines) {
          this.singleSplitLines = JSON.parse(savedSingleSplitLines) || {};
        } else {
          this.singleSplitLines = {};
        }

        // If cart changed (new items added or items removed), reconcile:
        // keep settings only for items still in cart, remove stale entries.
        if (_cartChanged) {
          this.reconcileCartSpecificData();
        }
      } catch (e) {
        console.warn('Failed to load saved data:', e);
      }

      // Store orderer (customer's default address) for sender auto-determination
      // Check if defaultAddress has meaningful data (not just countryCode from empty Shopify address)
      var customerDa = window.shippingCustomer && window.shippingCustomer.defaultAddress;
      if (customerDa && (customerDa.lastName || customerDa.address1)) {
        var da = customerDa;
        var pc = da.province || da.provinceCode || '';
        if (!pc) {
        }
        this.orderer = {
          id: da.id || 'orderer_default',
          lastName: da.lastName || '',
          firstName: da.firstName || '',
          zip: (da.zip || '').replace(/-/g, ''),
          prefecture: this.getProvinceNameByCode(pc),
          prefectureCode: pc,
          city: da.city || '',
          address1: da.address1 || '',
          address2: da.address2 || '',
          company: da.company || '',
          phone: this.normalizePhoneInput(da.phone || '')
        };
      } else {
        // No valid defaultAddress (guest or logged-in with empty address): load orderer from localStorage
        var savedOrderer = localStorage.getItem('shipping_orderer');
        if (savedOrderer) {
          this.orderer = JSON.parse(savedOrderer);
          if (this.orderer) this.orderer.phone = this.normalizePhoneInput(this.orderer.phone || '');
          if (this.orderer && !this.orderer.prefectureCode) {
          }
        } else {
        }
        // Note: no fallback to savedAddresses — orderer must be explicitly registered
      }

      // Default sender if not set - leave empty (will be auto-set by updateSenderByRecipient)
      if (!this.sender || (!this.sender.lastName && !this.sender.address1)) {
        this.sender = {
          lastName: '',
          firstName: '',
          zip: '',
          prefecture: '',
          prefectureCode: '',
          city: '',
          address1: '',
          address2: '',
          company: '',
          phone: ''
        };
      }

      // Update checkout button state based on loaded data
      this.updateCheckoutButtonState();
    },

    /**
     * Capture inventory data from pre-rendered DOM elements
     * data-max-qty 属性（Liquid 側で inventory_quantity から設定済み）を
     * inventoryData に読み込み、初回クリックから在庫上限を正しく適用する。
     * 通常版・eGift版は同じ shipping-app.js を共有しているためこの1箇所で両方カバーする。
     */
    captureInventoryData: function() {
      var self = this;

      // Populate inventoryData from data-max-qty set by Liquid on each item wrapper
      $('[data-item-key]').each(function() {
        var $wrapper = $(this);
        var maxQty = parseInt($wrapper.data('max-qty'), 10);
        if (!isFinite(maxQty) || maxQty <= 0) return;

        var itemKey = $wrapper.data('item-key');
        var item = self.getCartItemByKey(itemKey);
        if (item && item.variant_id) {
          self.inventoryData[item.variant_id] = maxQty;
        }
      });

      // Initialize button states based on current quantities and inventory
      this.initializeButtonStates();

      // Also fetch fresh inventory via Section Rendering API (async)
      // This ensures we have the latest inventory even if the page was served from cache
      self.refreshInventoryData();
    },

    /**
     * Fetch fresh inventory data via Section Rendering API.
     * cart-inventory-data section returns { variant_id: inventory_quantity } JSON.
     * Updates inventoryData and button states when the response arrives.
     */
    refreshInventoryData: function() {
      var self = this;
      $.ajax({
        url: '/?sections=cart-inventory-data',
        type: 'GET',
        dataType: 'json'
      }).done(function(resp) {
        try {
          var html = resp['cart-inventory-data'] || '';
          var data = JSON.parse(html);
          // Update inventoryData with fresh values
          for (var variantId in data) {
            if (data.hasOwnProperty(variantId)) {
              var qty = parseInt(data[variantId], 10);
              if (isFinite(qty) && qty > 0) {
                self.inventoryData[variantId] = qty;
              }
            }
          }
          // Re-apply button states with fresh inventory
          self.initializeButtonStates();
          if (SHIPPING_DEBUG) {
            console.log('[ShippingApp] refreshInventoryData — updated inventoryData from Section Rendering API', self.inventoryData);
          }
        } catch (e) {
          if (SHIPPING_DEBUG) {
            console.warn('[ShippingApp] refreshInventoryData parse error:', e);
          }
        }
      });
    },

    /**
     * Initialize button states for pre-rendered items
     */
    initializeButtonStates: function() {
      var self = this;
      $('[data-single-address-items] [data-item-key]').each(function() {
        var $wrapper = $(this);
        var $input = $wrapper.find('.qty__input:not(.js-bagQtyInput):not(.js-splitQtyInput)').first();
        var $decreaseBtn = $wrapper.find('.js-qtyBtn[data-step="-1"]');
        var $increaseBtn = $wrapper.find('.js-qtyBtn[data-step="1"]');
        var itemKey = $wrapper.data('item-key');

        var currentQty = parseInt($input.val(), 10) || 1;

        // Store initial quantity as confirmed (for rollback on API failure)
        self.confirmedQuantities[itemKey] = currentQty;

        // Only disable decrease button if at minimum
        var maxAllowed = self.getMaxAllowedQuantityForItem(itemKey);
        var isDecreaseDisabled = currentQty <= 1;
        var isIncreaseDisabled = currentQty >= maxAllowed;
        $decreaseBtn.prop('disabled', isDecreaseDisabled).toggleClass('is-disabled', isDecreaseDisabled);
        $increaseBtn.prop('disabled', isIncreaseDisabled).toggleClass('is-disabled', isIncreaseDisabled);
      });
    },

    /**
     * Save data to localStorage
     */
    normalizeBagSettingsByRules: function() {
      var self = this;
      var itemsByKey = {};
      (this.cartItems || []).forEach(function(item) {
        itemsByKey[item.key] = item;
      });

      if (!this.itemGiftOptions.single) this.itemGiftOptions.single = {};
      Object.keys(this.itemGiftOptions.single).forEach(function(itemKey) {
        var settings = self.itemGiftOptions.single[itemKey];
        var item = itemsByKey[itemKey];
        if (!settings || !item) return;
        var variants = self.getHandbagVariantsForProduct(item.variant_id, itemKey);
        var freeBag = variants.find(function(v) { return (parseInt(v.price, 10) || 0) === 0; });
        var paidBags = variants.filter(function(v) { return (parseInt(v.price, 10) || 0) > 0; });
        if (SHIPPING_DEBUG && freeBag) {
          var _vid = String(freeBag.shopifyProductVariantId);
          console.log('[BAG][normalize] 呼び出し:', { itemKey: itemKey, productQty: item.quantity, bagQty: parseInt(settings.bags && settings.bags[_vid], 10) || 0, caller: new Error().stack.split('\n')[2] });
        }
        self.applyBagRuleToSettings(itemKey, settings, freeBag, paidBags, item.quantity);
      });

      (this.multiRecipients || []).forEach(function(recipient) {
        if (!recipient || !recipient.items) return;
        Object.keys(recipient.items).forEach(function(itemKey) {
          var recipientQty = parseInt(recipient.items[itemKey], 10) || 0;
          if (recipientQty <= 0) return;
          var item = itemsByKey[itemKey];
          if (!item) return;
          var settings = self.getRecipientItemSettings(recipient, itemKey);
          var variants = self.getHandbagVariantsForProduct(item.variant_id, itemKey);
          var freeBag = variants.find(function(v) { return (parseInt(v.price, 10) || 0) === 0; });
          var paidBags = variants.filter(function(v) { return (parseInt(v.price, 10) || 0) > 0; });
          self.applyBagRuleToSettings(itemKey, settings, freeBag, paidBags, recipientQty);
        });
      });
    },

    saveData: function() {
      // Mark as user-edited (unless still in initialization phase or suppressed)
      if (this._initialized && !this._suppressEditFlag) this.markUserEdited();
      try {
        this.normalizeBagSettingsByRules();
        localStorage.setItem('shipping_cart_fingerprint', this.getCartFingerprint());
        localStorage.setItem('shipping_current_mode', this.currentMode);
        localStorage.setItem('shipping_sender', JSON.stringify(this.sender));
        localStorage.setItem('shipping_sender_manually_edited', this.senderManuallyEdited ? '1' : '');
        // Persist orderer (guest always, logged-in when defaultAddress is empty/invalid)
        if (this.orderer) {
          localStorage.setItem('shipping_orderer', JSON.stringify(this.orderer));
        }
        // Save single and multi recipients separately
        localStorage.setItem('shipping_single_recipient', JSON.stringify(this.singleRecipient));
        localStorage.setItem('shipping_multi_recipients', JSON.stringify(this.multiRecipients));
        localStorage.setItem('shipping_addresses', JSON.stringify(this.savedAddresses));
        localStorage.setItem('shipping_gift_options', JSON.stringify(this.itemGiftOptions));
        localStorage.setItem('shipping_noshi_settings', JSON.stringify(this.noshiSettings));
        localStorage.setItem('shipping_sticker_settings', JSON.stringify(this.stickerSettings));
        localStorage.setItem('shipping_wraponly_items', JSON.stringify(this.wrapOnlyItems));
        localStorage.setItem('shipping_nowrap_items', JSON.stringify(this.noWrapItems));
        localStorage.setItem('shipping_single_split_lines', JSON.stringify(this.singleSplitLines));
      } catch (e) {
        console.warn('Failed to save data:', e);
      }
      // Update checkout button state after data changes
      this.updateCheckoutButtonState();
    },

    /**
     * Fetch customer addresses from API (for logged-in users)
     * Used in multi-address mode to let user choose recipients
     */
    fetchCustomerAddresses: function() {
      var self = this;

      // Only fetch if customer is logged in
      if (!window.shippingCustomer) {
        return Promise.resolve();
      }

      var allAddresses = [];

      var fetchPage = function(after) {
        var url = '/apps/ping/customers/addresses?first=250';
        if (after) url += '&after=' + encodeURIComponent(after);

        return $.ajax({
          url: url,
          method: 'GET',
          xhrFields: { withCredentials: true }
        }).then(function(data) {
          var conn = data.customer && data.customer.addresses;
          if (!conn) return;

          var defaultAddrId = data.customer && data.customer.defaultAddress
            ? data.customer.defaultAddress.id : '';

          var edges = conn.edges || [];
          edges.forEach(function(edge) {
            var node = edge.node;
            allAddresses.push({
              id: node.id,
              lastName: node.lastName || '',
              firstName: node.firstName || '',
              zip: (node.zip || '').replace(/-/g, ''),
              prefecture: node.province || '',
              prefectureCode: node.provinceCode || '',
              city: node.city || '',
              address1: node.address1 || '',
              address2: node.address2 || '',
              company: node.company || '',
              phone: self.normalizePhoneInput(node.phone || ''),
              isDefault: node.id === defaultAddrId
            });
          });

          // Paginate if more pages
          if (conn.pageInfo && conn.pageInfo.hasNextPage) {
            return new Promise(function(resolve) {
              setTimeout(function() {
                resolve(fetchPage(conn.pageInfo.endCursor));
              }, 500); // Rate limit: 2 req/s
            });
          }
        });
      };

      return fetchPage(null).then(function() {
        // Store all customer addresses (including empty ones for reference)
        self.customerAddresses = allAddresses || [];

        // Filter valid addresses for savedAddresses
        // 個人: must have lastName; 法人: company is sufficient (legacy data may have no personal name)
        var validAddresses = (allAddresses || []).filter(function(a) {
          return (a.lastName || a.company) && a.address1;
        });

        // Merge API addresses with locally-added addresses (addr_* ids)
        // Local addresses from previous sessions are already cleared in init()
        var apiIds = {};
        validAddresses.forEach(function(a) { apiIds[a.id] = true; });
        var localOnly = (self.savedAddresses || []).filter(function(a) {
          return a.id && a.id.indexOf('addr_') === 0 && !apiIds[a.id];
        });
        // デフォルト住所を先頭に並べてから重複除去（デフォルト住所は必ず残す）
        var merged = validAddresses.concat(localOnly);
        merged.sort(function(a, b) {
          return (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0);
        });
        var seen = {};
        self.savedAddresses = merged.filter(function(a) {
          var phone = (a.phone || '').replace(/[^0-9]/g, '');
          var key = [a.lastName, a.firstName, a.zip, a.prefecture, a.city, a.address1, a.address2 || '', a.company || '', phone].join('|');
          if (seen[key]) return false;
          seen[key] = true;
          return true;
        });
        localStorage.setItem('shipping_addresses', JSON.stringify(self.savedAddresses));

        // Sync updated address data (phone, name, etc.) to existing recipients
        self.syncRecipientsFromSavedAddresses();
      }).catch(function(err) {
        console.warn('Failed to fetch customer addresses:', err);
        self.customerAddresses = [];
        // Keep locally-added addresses on error
        var localOnly = (self.savedAddresses || []).filter(function(a) {
          return a.id && a.id.indexOf('addr_') === 0;
        });
        self.savedAddresses = localOnly;
        localStorage.setItem('shipping_addresses', JSON.stringify(localOnly));
      });
    },

    /**
     * Sync updated address data from savedAddresses into existing recipients
     * Ensures fields like phone, name, address are up-to-date after API refresh
     */
    syncRecipientsFromSavedAddresses: function() {
      var addrMap = {};
      (this.savedAddresses || []).forEach(function(a) { addrMap[a.id] = a; });

      var addressFields = ['lastName', 'firstName', 'zip', 'prefecture', 'prefectureCode', 'city', 'address1', 'address2', 'company', 'phone'];

      var syncAddr = function(recipient) {
        if (!recipient || !recipient.id || !addrMap[recipient.id]) return false;
        if (recipient.isInteroffice) return false; // skip interoffice recipients — address is YM internal, not a saved customer address
        var fresh = addrMap[recipient.id];
        var changed = false;
        addressFields.forEach(function(f) {
          if (fresh[f] !== undefined && recipient[f] !== fresh[f]) {
            recipient[f] = fresh[f];
            changed = true;
          }
        });
        return changed;
      };

      var anyChanged = false;
      if (syncAddr(this.singleRecipient)) anyChanged = true;
      (this.multiRecipients || []).forEach(function(r) {
        if (syncAddr(r)) anyChanged = true;
      });

      if (anyChanged) {
        this.saveData();
        // アドレス帳の住所を編集してお届け先の中身が変わった場合も、
        // 注文者との一致判定が変わりうるのでご依頼主を再判定する。
        this.updateSenderByRecipient();
      }
    },

    /**
     * Initialize first access - set sender and recipient defaults before render
     * Guest: show modal in init() if no saved data
     * Logged-in with addresses: set first customer address as sender + singleRecipient
     * Logged-in without addresses: show modal to add address
     */
    initializeFirstAccess: function() {
      // Guest: if recipient is empty but orderer exists, copy orderer → recipient
      if (!window.shippingCustomer) {
        if ((!this.singleRecipient || !this.singleRecipient.lastName) && this.orderer && this.orderer.lastName) {
          this.singleRecipient = {
            lastName: this.orderer.lastName,
            firstName: this.orderer.firstName,
            zip: this.orderer.zip,
            prefecture: this.orderer.prefecture,
            prefectureCode: this.orderer.prefectureCode,
            city: this.orderer.city,
            address1: this.orderer.address1,
            address2: this.orderer.address2 || '',
            company: this.orderer.company || '',
            phone: this.orderer.phone || '',
            items: {},
            deliverySettings: {}
          };
          this.saveData();
        }
        // Auto-determine sender from orderer/recipient
        this.updateSenderByRecipient();
        return;
      }

      // Filter addresses that have valid data (at least lastName or company, and address1)
      var validAddresses = (this.savedAddresses || []).filter(function(a) {
        return (a.lastName || a.company) && a.address1;
      });

      if (validAddresses.length > 0) {
        // Logged-in mode: use first valid address (or default) for recipient
        var firstAddr = validAddresses.find(function(a) { return a.isDefault; }) || validAddresses[0];

        // Set singleRecipient if empty
        if (!this.singleRecipient || !this.singleRecipient.address1) {
          this.singleRecipient = {
            id: firstAddr.id,
            lastName: firstAddr.lastName,
            firstName: firstAddr.firstName,
            zip: firstAddr.zip,
            prefecture: firstAddr.prefecture,
            prefectureCode: firstAddr.prefectureCode,
            city: firstAddr.city,
            address1: firstAddr.address1,
            address2: firstAddr.address2 || '',
            company: firstAddr.company || '',
            phone: firstAddr.phone || '',
            items: {},
            deliverySettings: {}
          };
          this.saveData();
        }

        // Auto-determine sender based on orderer/recipient comparison
        this.updateSenderByRecipient();
      } else {
        // Logged-in but no valid addresses: show orderer registration modal only
        if (!this.orderer || !this.orderer.lastName) {
          this.isFirstAccessModal = true;
          this.openModal('modalAddOrderer');
          $('#modalAddOrderer').addClass('no-outside-close');
        }
      }

      // If orderer is still missing for logged-in user who has valid addresses (edge case:
      // e.g. customer has Shopify addresses but no default address set)
      if (window.shippingCustomer && validAddresses.length > 0 && (!this.orderer || !this.orderer.lastName)) {
        this.isFirstAccessModal = true;
        this.openModal('modalAddOrderer');
        $('#modalAddOrderer').addClass('no-outside-close');
      }
    },

    /**
     * Main render function
     */
    render: function() {
      // Restore active tab based on saved mode
      this.setActiveShippingTab(this.currentMode, false);

      this.renderOrdererInfo();
      this.renderSenderInfo();
      this.renderCartItems(); // This also calls populateDeliveryDates()
      this.renderSavedAddresses();
      this.updateSummary();
      this.hideLoading();
      this.debugRefresh();
      this._initialized = true;
    },

    showLoading: function() {
      var $el = $('#shipping-loading-overlay');
      $el.removeClass('is-hidden');
    },

    hideLoading: function() {
      var $el = $('#shipping-loading-overlay');
      $el.addClass('is-hidden');
      if (this._loadingTimeout) {
        clearTimeout(this._loadingTimeout);
        this._loadingTimeout = null;
      }
    },

    showCheckoutLoading: function() {
      var $el = $('#shipping-checkout-overlay');
      $el.removeClass('is-hidden');
    },

    hideCheckoutLoading: function() {
      var $el = $('#shipping-checkout-overlay');
      $el.addClass('is-hidden');
    },

    /**
     * Compare two addresses by lastName, zip, city, address1, address2
     */
    isSameAddress: function(addr1, addr2) {
      if (!addr1 || !addr2) return false;
      var n = function(s) { return (s || '').replace(/[\s\-]/g, ''); };
      var p1 = this.normalizePhoneInput(addr1.phone || '');
      var p2 = this.normalizePhoneInput(addr2.phone || '');
      return n(addr1.lastName) === n(addr2.lastName) &&
             n(addr1.firstName) === n(addr2.firstName) &&
             n(addr1.zip) === n(addr2.zip) &&
             n(addr1.prefecture) === n(addr2.prefecture) &&
             n(addr1.city) === n(addr2.city) &&
             n(addr1.address1) === n(addr2.address1) &&
             n(addr1.address2) === n(addr2.address2) &&
             p1 === p2;
    },

    buildChildOrderBillingAddress: function() {
      var billingSource = this.sender || {};

      // Keep the Yokumoku sender label in UI, but send the actual orderer
      // as the child-order billing address when orderer == recipient.
      if (this.orderer && this.isSameAddress(billingSource, YOKUMOKU_SENDER)) {
        billingSource = this.orderer;
      }

      var addr = {
        lastName: billingSource.lastName || '',
        firstName: billingSource.firstName || '',
        zip: (billingSource.zip || '').replace(/-/g, ''),
        countryCode: 'JP',
        provinceCode: billingSource.prefectureCode || '',
        city: billingSource.city || '',
        address1: billingSource.address1 || '',
        address2: this.getAddress2WithCompany(billingSource)
      };
      var phone = (billingSource.phone || '').replace(/-/g, '');
      if (phone) addr.phone = phone;
      return addr;
    },

    getCheckoutSenderName: function() {
      var senderSource = this.sender || {};

      // AIG procedure screen treats senderName as the parent order's orderer
      // when sender is the Yokumoku default label.
      if (this.orderer && this.isSameAddress(senderSource, YOKUMOKU_SENDER)) {
        senderSource = this.orderer;
      }

      return $.trim((senderSource.lastName || '') + ' ' + (senderSource.firstName || ''));
    },

    /**
     * 現在のお届け先がすべて注文者と同一住所か
     * （＝ご依頼主が「ヨックモック」になるべき状態か）を判定する
     */
    isEveryRecipientSameAsOrderer: function() {
      var self = this;
      if (!this.orderer) return false;
      var recipients = this.getRecipients();
      // お届け先が未設定のときは判定できないので現状維持（true）扱いにする
      if (!recipients.length) return true;
      return recipients.every(function(r) {
        return self.isSameAddress(self.orderer, r);
      });
    },

    /**
     * メモ欄・メールに出す依頼主として使う住所を返す。
     * ご依頼主が「ヨックモック」なのにお届け先が注文者と異なる場合は注文者に差し替える
     * （buildChildOrderBillingAddress / getCheckoutSenderName と同じ補正の保険）。
     */
    getNoteSenderSource: function() {
      var senderSource = this.sender || {};

      if (this.orderer &&
          this.isSameAddress(senderSource, YOKUMOKU_SENDER) &&
          !this.isEveryRecipientSameAsOrderer()) {
        return this.orderer;
      }

      return senderSource;
    },

    /**
     * Auto-determine sender based on orderer/recipient comparison
     * - 届け先 == 注文者 → sender = ヨックモック
     * - sender がヨックモックの状態で届け先 != 注文者になった → sender = 注文者
     * - sender が手動設定済み（ヨックモックでない） → 変更しない
     * - No orderer → skip (waiting for address input)
     */
    updateSenderByRecipient: function() {
      if (!this.orderer) {
        return;
      }

      // ユーザーが手動でご依頼主を変更済みの場合は自動切り替えしない
      if (this.senderManuallyEdited) {
        this.renderSenderInfo();
        this.saveData();
        return;
      }

      if (this.currentMode === 'multi') {
        if (!this.sender || (!this.sender.lastName && !this.sender.address1) || this.isSameAddress(this.sender, YOKUMOKU_SENDER)) {
          this.sender = $.extend({}, this.orderer);
        } else {
        }
        this.renderSenderInfo();
        this.saveData();
        return;
      }

      var recipient = this.singleRecipient;
      if (!recipient || !recipient.address1) {
        return;
      }

      if (this.isSameAddress(this.orderer, recipient)) {
        this.sender = $.extend({}, YOKUMOKU_SENDER);
      } else if (!this.sender || (!this.sender.lastName && !this.sender.address1) || this.isSameAddress(this.sender, YOKUMOKU_SENDER)) {
        this.sender = $.extend({}, this.orderer);
      } else {
      }

      this.renderSenderInfo();
      this.saveData();
    },

    /**
     * Render sender information
     */
    renderSenderInfo: function() {
      var $senderInfo = $('[data-sender-info]');
      var s = this.sender;

      if (!s.lastName && !s.firstName && !s.company && !s.address1) {
        $senderInfo.html('<p class="c-text-muted">ご注文者様を登録してください。</p>');
        $('[data-change-sender]').hide();
        return;
      }
      var isYokumoku = this.isSameAddress(s, YOKUMOKU_SENDER);
      $('[data-change-sender]').show();

      var fullName = (s.lastName || s.firstName)
        ? ((s.lastName || '') + ' ' + (s.firstName || '')).trim()
        : (s.company || '');
      var senderAddress2WithCompany = this.getAddress2WithCompany(s);
      var address = '〒' + this.formatZipDisplay(s.zip || '---') + '<br>' +
                    (s.prefecture || '') + (s.city || '') + (s.address1 || '') +
                    (senderAddress2WithCompany ? '<br>' + senderAddress2WithCompany : '') +
                    (!isYokumoku ? '<br>TEL: ' + (s.phone ? this.formatPhoneDisplay(s.phone) : '---') : '');

      $senderInfo.html(
        '<p class="p-address__name">' + this.escapeHtml(fullName) + '</p>' +
        '<p class="p-address__detail">' + address + '</p>'
      );

      // Always show the change sender button when sender info is present

      // Pre-fill edit modal (ヨックモック自動設定時は空欄にする)
      if (isYokumoku) {
        $('#sender-lastName').val('');
        $('#sender-firstName').val('');
        $('#sender-zipCode').val('');
        $('#sender-prefecture').val('');
        $('#sender-city').val('');
        $('#sender-address1-town').val('');
        $('#sender-address1-num').val('');
        $('#sender-address2').val('');
        $('#sender-company').val('');
        $('#sender-phone').val('');
        this.setAddressPersonType('sender', 'individual');
      } else {
        $('#sender-lastName').val(s.lastName || '');
        $('#sender-firstName').val(s.firstName || '');
        $('#sender-zipCode').val(s.zip || '');
        $('#sender-prefecture').val(s.prefectureCode || '');
        $('#sender-city').val(s.city || '');
        this.prefillAddress1Split(s.zip, s.address1, 'sender-address1-town', 'sender-address1-num');
        $('#sender-address2').val(s.address2 || '');
        $('#sender-company').val(s.company || '');
        $('#sender-phone').val(s.phone || '');
        this.setAddressPersonType('sender', s.company ? 'corporate' : 'individual');
      }
    },

    renderOrdererInfo: function() {
      var $ordererInfo = $('[data-orderer-info]');
      if (!$ordererInfo.length) return;
      this._ordererAddressFieldErrors = {};
      $('[data-orderer-validation-errors]').remove();

      var o = this.orderer;
      if (!o || (!o.lastName && !o.firstName)) {
        $ordererInfo.html(
          '<p class="c-text-muted">お客様の情報が登録されていません。</p>' +
          '<button type="button" class="c-btn c-btn--outline u-mt-8" data-open-orderer-modal>' +
          'お客様の情報を登録してください</button>'
        );
        $('[data-orderer-change-btn]').hide();
        return;
      }

      var fullName = (o.lastName || '') + ' ' + (o.firstName || '');
      var ordererAddress2WithCompany = this.getAddress2WithCompany(o);
      var address = '〒' + this.formatZipDisplay(o.zip || '---') + '<br>' +
                    (o.prefecture || '') + (o.city || '') + (o.address1 || '') +
                    (ordererAddress2WithCompany ? '<br>' + ordererAddress2WithCompany : '') +
                    '<br>TEL: ' + (o.phone ? this.formatPhoneDisplay(o.phone) : '---');

      $ordererInfo.html(
        '<p class="p-address__name">' + this.escapeHtml(fullName) + '</p>' +
        '<p class="p-address__detail">' + address + '</p>'
      );
      $('[data-orderer-change-btn]').show();
    },

    /**
     * Render cart items for both single and multi address modes
     * Detects pre-rendered content from Liquid and enhances it instead of replacing
     */
    renderCartItems: function() {
      var self = this;
      var items = this.cart.items;

      if (SHIPPING_DEBUG) console.log('[BAG][renderCartItems] 呼び出し:', new Error().stack.split('\n').slice(1, 4).join(' | '));

      if (!items || items.length === 0) {
        if (SHIPPING_DEBUG) {
          console.warn('[ShippingApp][renderCartItems] ⛔ Cart items empty → redirect to /cart. cart.items=', this.cart && this.cart.items);
        }
        this.clearGuestSessionData();
        window.location.href = '/cart';
        return;
      }

      // Update confirmedQuantities from current cart data
      items.forEach(function(item) {
        self.confirmedQuantities[item.key] = item.quantity;
      });

      // Always use JS full render for single mode.
      // Split-line cards and per-line controls rely on deterministic generated markup.
      var $singleContainer = $('[data-single-address-items]');
      var isPreRendered = false;

      if (isPreRendered) {
        // Enhance pre-rendered content with gift options
        this.enhancePreRenderedItems($singleContainer, items);
        // Update address card with recipient data
        this.updateAddressCard($singleContainer);
      } else {
        // Full render (fallback for when Liquid pre-render is not available, or items changed)
        var singleHtml = this.renderSingleAddressItems(items);
        $singleContainer.html(singleHtml);
      }

      // Multi address mode - always full render (complex logic)
      var multiHtml = this.renderMultiAddressItems(items);
      $('[data-multi-address-items]').html(multiHtml);

      // Populate delivery date/time selects after rendering
      this.populateDeliveryDates();

      // Check for incomplete settings and update warning
      this.checkIncompleteSettings();

      // Address card height can vary by line count; keep sticky badge offset in sync.
      this.updateStickyBadgeOffsets();
      this.updateMultiAddAddressCtaVisibility();

    },

    /**
     * Sync sticky top offset for each shipment badge list with its address card height
     * to prevent overlap when the address grows to multiple lines.
     */
    updateStickyBadgeOffsets: function() {
      $('.addr-sect').each(function() {
        var $sect = $(this);
        var $card = $sect.children('.addr-card').first();
        if (!$card.length) return;

        var cardHeight = Math.ceil($card.outerHeight() || 0);
        if (cardHeight <= 0) return;

        // Stick the shipment badge immediately below the sticky address card.
        $sect.css('--addr-card-sticky-offset', cardHeight + 'px');
      });
    },

    /**
     * Show bottom "add address" CTA only after at least one recipient exists in multi mode.
     */
    updateMultiAddAddressCtaVisibility: function() {
      // 複数配送モードでは常に非表示
      $('[data-multi-add-address-cta]').hide();
    },

    /**
     * Enhance pre-rendered cart items with gift options (bags, wrapping)
     */
    enhancePreRenderedItems: function($container, items) {
      var self = this;

      items.forEach(function(item) {
        var $itemWrapper = $container.find('[data-item-key="' + item.key + '"]');
        if ($itemWrapper.length === 0) return;

        var $placeholder = $itemWrapper.find('[data-gift-options-placeholder]');
        if ($placeholder.length === 0) return;

        var itemId = item.key.replace(/[^a-zA-Z0-9]/g, '_');
        var giftHtml = '';

        // Section 2: 手提げ袋 (Shopping bag)
        var handbagVariants = self.getHandbagVariantsForProduct(item.variant_id, item.key);
        var hasHandbagOptions = handbagVariants.length > 0;

        if (hasHandbagOptions) {
          var freeBag = handbagVariants.find(function(b) { return parseInt(b.price, 10) === 0; });
          var paidBags = handbagVariants.filter(function(b) { return parseInt(b.price, 10) > 0; });
          var savedItemBags = self.getSingleItemGiftSettings(item.key);
          self.applyBagRuleToSettings(item.key, savedItemBags, freeBag, paidBags, item.quantity);
          var bagRuleConfig = self.getBagRuleConfigByItemKey(item.key);
          var onDisabledAttr = bagRuleConfig.disableOn ? ' disabled' : '';
          var onDisabledClass = bagRuleConfig.disableOn ? ' is-disable' : '';
          var offDisabledAttr = bagRuleConfig.disableOff ? ' disabled' : '';
          var offDisabledClass = bagRuleConfig.disableOff ? ' is-disable' : '';
          var showBagReductionNote = bagRuleConfig.rule === '選択可能';
          var bagSelection = self.getBagSelectionState(savedItemBags, item.key, item.variant_id);
          var toggleOnChecked = bagSelection === 'on';
          var toggleOffChecked = bagSelection === 'off';

          giftHtml += '<div class="addr-content__item" data-item-key="' + item.key + '">';
          giftHtml += '<h4 class="c-ttl-03">手提げ袋';
          if (showBagReductionNote) {
            giftHtml += '<span class="p-bag-box__note u-sm-min">手提げ袋の削減にご協力ください</span>';
          }
          giftHtml += '</h4>';
          if (bagRuleConfig.noteText) {
            giftHtml += '<p class="p-bag-box__note">' + self.escapeHtml(bagRuleConfig.noteText) + '</p>';
          }
          if (!bagRuleConfig.forceOn) {
            giftHtml += '<div class="p-bag-toggle js-bagToggle">';
            giftHtml += '<label class="p-bag-toggle__item option-card' + offDisabledClass + '" data-bag-off-label>';
            giftHtml += '<input type="radio" name="bagToggle_' + itemId + '" class="option-card__radio" value="off"' + (toggleOffChecked ? ' checked' : '') + offDisabledAttr + '>';
            giftHtml += '<span>手提げ袋なし</span>';
            giftHtml += '</label>';
            giftHtml += '</div>';
          }
          giftHtml += '<div class="p-bag-box js-bagBox">';
          giftHtml += '<div class="c-card-01">';

          // 手提げ袋（クリア） (Free bag with qty control)
          if (freeBag) {
            var hasSavedFreeBag = bagRuleConfig.forceFree ? true : (savedItemBags && savedItemBags.freeBagEnabled === true);
            var savedFreeQty = (savedItemBags && savedItemBags.bags && savedItemBags.bags[freeBag.shopifyProductVariantId] > 0) ? savedItemBags.bags[freeBag.shopifyProductVariantId] : 1;
            var freeDisabledAttr = (bagRuleConfig.lockFree || bagRuleConfig.disableFree) ? ' disabled' : '';
            var freeQtyDisabledClass = bagRuleConfig.disableFree ? ' is-disabled' : '';
            var freeDisabledClass = bagRuleConfig.disableFree ? ' is-disable' : '';
            var freePlusBtnDisabledAttr = freeDisabledAttr || (savedFreeQty >= item.quantity ? ' disabled' : '');
            var freePlusBtnDisabledClass = freePlusBtnDisabledAttr ? ' is-disabled' : '';
            var freeMinusBtnDisabledAttr = freeDisabledAttr || (savedFreeQty <= 0 ? ' disabled' : '');
            var freeMinusBtnDisabledClass = freeMinusBtnDisabledAttr ? ' is-disabled' : '';
            if (SHIPPING_DEBUG) console.log('[BAG][render] 無料袋レンダリング:', { itemKey: item.key, productQty: item.quantity, savedBagQty: savedFreeQty, plusDisabled: !!freePlusBtnDisabledAttr, lockFree: bagRuleConfig.lockFree, forceFree: bagRuleConfig.forceFree });
            var freeCardLockStyle = bagRuleConfig.forceOn ? ' style="cursor:default;pointer-events:none;"' : '';
            giftHtml += '<div class="c-card-01__item">';
            giftHtml += '<div class="option-card js-freeBag' + (hasSavedFreeBag ? ' is-active' : '') + freeDisabledClass + '"' + freeCardLockStyle + '>';
            giftHtml += '<input type="checkbox" name="bagBox_' + itemId + '" class="option-card__checkbox" data-bag-variant-id="' + freeBag.shopifyProductVariantId + '" data-bag-type="free"' + (hasSavedFreeBag ? ' checked' : '') + freeDisabledAttr + '>';
            giftHtml += '<div class="option-card__inner">';
            var freeBagImage = freeBag.image || self.freeBagImage;
            giftHtml += '<div class="option-card__thumb"><img src="' + freeBagImage + '" alt=""></div>';
            giftHtml += '<div class="option-card__text">';
            giftHtml += '<p class="option-card__title">' + self.escapeHtml(bagRuleConfig.freeDisplayTitle) + '<span class="c-badge c-badge--free">無料サービス</span></p>';
            if (!bagRuleConfig.forceOn) {
              giftHtml += '<div class="qty-wrap js-freeBagQty" style="' + (hasSavedFreeBag ? '' : 'display:none;') + '">';
              giftHtml += '<div class="qty qty--small">';
              giftHtml += '<button type="button" class="qty__btn js-bagQtyBtn' + freeMinusBtnDisabledClass + '" data-step="-1" aria-label="decrease"' + freeMinusBtnDisabledAttr + '>−</button>';
              giftHtml += '<input class="qty__input js-bagQtyInput" type="text" value="' + (hasSavedFreeBag ? Math.max(1, savedFreeQty) : 1) + '" inputmode="numeric" aria-label="quantity"' + freeDisabledAttr + '>';
              giftHtml += '<button type="button" class="qty__btn js-bagQtyBtn' + freePlusBtnDisabledClass + '" data-step="1" aria-label="increase"' + freePlusBtnDisabledAttr + '>＋</button>';
              giftHtml += '</div>';
              giftHtml += '</div>';
            }
            giftHtml += '</div>';
            giftHtml += '</div>';
            giftHtml += '</div>';
            giftHtml += '</div>';
          }

          // 手提げ袋（紙） (Paid bag linking to modal)
          // Overlay blocks checkbox when unchecked — click opens modal, save sets checked
          // When checked — no overlay, user can uncheck directly
          if (!bagRuleConfig.hidePaid && paidBags.length > 0) {
            var hasSavedPaidBags = savedItemBags && savedItemBags.paidBagEnabled === true;
            var paidDisabledAttr = bagRuleConfig.disablePaid ? ' disabled' : '';
            var paidDisabledClass = bagRuleConfig.disablePaid ? ' is-disable' : '';
            var paidOpenAttr = bagRuleConfig.disablePaid ? '' : ' data-open-bag-modal';
            var paidAriaAttr = bagRuleConfig.disablePaid ? ' tabindex="-1" aria-disabled="true"' : '';
            giftHtml += '<div class="c-card-01__item" style="position: relative;">';
            giftHtml += '<label class="option-card js-bagPaid' + (hasSavedPaidBags ? ' is-active' : '') + paidDisabledClass + '">';
            giftHtml += '<input type="checkbox" name="bagBox_' + itemId + '" class="option-card__checkbox" data-bag-type="paid"' + (hasSavedPaidBags ? ' checked' : '') + paidDisabledAttr + '>';
            giftHtml += '<a href="#" class="option-card__inner"' + paidOpenAttr + paidAriaAttr + ' data-item-key="' + item.key + '" data-variant-id="' + item.variant_id + '">';
            if (self.paidBagImage) {
              giftHtml += '<div class="option-card__thumb"><img src="' + self.paidBagImage + '" alt=""></div>';
            } else {
              giftHtml += '<div class="option-card__thumb"></div>';
            }
            giftHtml += '<div class="option-card__text">';
            giftHtml += '<p class="option-card__title">手提げ袋（紙） </p>';
            // giftHtml += '<p class="option-card__desc">手提げ袋（紙）のサイズを選択</p>';
            giftHtml += '</div>';
            giftHtml += '</a>';
            giftHtml += '</label>';
            if (!hasSavedPaidBags && !bagRuleConfig.disablePaid) {
              giftHtml += '<div data-overlay-paid-bag data-item-key="' + item.key + '" data-variant-id="' + item.variant_id + '" style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 5; cursor: pointer;"></div>';
            }
            giftHtml += '</div>';
          }

          giftHtml += '</div>'; // end c-card-01

          // Bag status summary placeholder (rendered dynamically by renderBagStatusSummary)
          giftHtml += '<div data-bag-status class="bag-status">' + self.renderBagStatusSummary(item.key, 'single') + '</div>';
          giftHtml += '<div data-bag-off-status class="bag-off-status" style="' + (toggleOffChecked ? '' : 'display:none;') + '">';
          giftHtml += '<div class="p-alert p-alert--small p-alert--center">';
          giftHtml += '<div class="p-alert__icon"><img src="' + (window.shippingAssets ? window.shippingAssets.iconPackagingInfo : '') + '" alt=""></div>';
          giftHtml += '<div class="p-alert__info"><h4>手提げ袋は付属しておりません。</h4></div>';
          giftHtml += '</div></div>';

          giftHtml += '</div>'; // end p-bag-box
          if (showBagReductionNote) {
            giftHtml += '<span class="p-bag-box__note u-sm-max">手提げ袋の削減にご協力ください</span>';
          }
          giftHtml += '</div>'; // end addr-content__item
        }

        // Section 3: 包装・のし・リボン (Wrapping)
        giftHtml += '<div class="addr-content__item">';
        giftHtml += self.renderWrappingOptions(itemId, item.key, 'single');
        giftHtml += self.renderWrappingAlert(item.key, 'iconPackagingInfo');
        giftHtml += '</div>';

        $placeholder.html(giftHtml);
      });
    },

    updatePreRenderedNoshiSplit: function() {},

    /**
     * Update address card with recipient data
     */
    updateAddressCard: function($container) {
      var $addrCard = $container.find('.addr-card__body');
      if ($addrCard.length === 0) return;

      if (this.singleRecipient && this.singleRecipient.address1) {
        var r = this.singleRecipient;
        var address2WithCompany = this.getAddress2WithCompany(r);
        var html = '<h3 id="addr-title-single" class="addr-card__title">お届け先</h3>';
        html += '<p class="addr-card__line">〒' + this.escapeHtml(this.formatZipDisplay(r.zip || '')) + ' ' + this.escapeHtml(r.prefecture || '') + ' ' + this.escapeHtml(r.city || '') + ' ' + this.escapeHtml(r.address1 || '') + '</p>';
        if (address2WithCompany) {
          html += '<p class="addr-card__line">' + this.escapeHtml(address2WithCompany) + '</p>';
        }
        var _rn = (r.lastName || r.firstName) ? ((r.lastName || '') + ' ' + (r.firstName || '')).trim() : (r.company || '');
        html += '<p class="addr-card__line">' + this.escapeHtml(_rn) + ' 様</p>';
        // if (r.phone) {
        //   html += '<p class="addr-card__line">TEL: ' + this.escapeHtml(r.phone) + '</p>';
        // }
        $addrCard.html(html);
      } else {
        var html = '<h3 id="addr-title-single" class="addr-card__title">お届け先</h3>';
        html += '<p class="addr-card__line c-text-muted">お届け先を登録してください</p>';
        $addrCard.html(html);
      }

      this.updateStickyBadgeOffsets();
    },

    /**
     * Render items for single address mode
     */
    renderSingleAddressItems: function(items) {
      var self = this;
      var html = '';

      // Address card section
      html += '<section class="addr-card addr-card--big" aria-labelledby="addr-title-single">';
      html += '<div class="addr-card__body">';
      html += '<h3 id="addr-title-single" class="addr-card__title">お届け先</h3>';
      if (this.singleRecipient && this.singleRecipient.address1) {
        var r = this.singleRecipient;
        var address2WithCompany = this.getAddress2WithCompany(r);
        html += '<p class="addr-card__line">〒' + this.escapeHtml(this.formatZipDisplay(r.zip || '')) + ' ' + this.escapeHtml(r.prefecture || '') + ' ' + this.escapeHtml(r.city || '') + ' ' + this.escapeHtml(r.address1 || '') + '</p>';
        if (address2WithCompany) {
          html += '<p class="addr-card__line">' + this.escapeHtml(address2WithCompany) + '</p>';
        }
        var _rn = (r.lastName || r.firstName) ? ((r.lastName || '') + ' ' + (r.firstName || '')).trim() : (r.company || '');
        html += '<p class="addr-card__line">' + this.escapeHtml(_rn) + ' 様</p>';
        if (r.phone) {
          html += '<p class="addr-card__line">TEL: ' + this.escapeHtml(this.formatPhoneDisplay(r.phone)) + '</p>';
        }
      } else {
        html += '<p class="addr-card__line c-text-muted">お届け先を登録してください</p>';
      }
      html += '</div>';
      html += '<div class="addr-card__actions">';
      html += '<button class="c-btn c-btn-outline" type="button" data-edit-single-recipient>お届け先を変更する</button>';
      if (this.canUseInterofficeShipment()) {
        if (this.hasNonInterofficeItems(items)) {
          html += '<button class="c-btn c-btn-outline is-disabled is-disabled--interoffice" type="button" data-open-interoffice-recipient="single">社内便で送る</button>';
        } else {
          html += '<button class="c-btn c-btn-outline" type="button" data-open-interoffice-recipient="single">社内便で送る</button>';
        }
      }
      html += '</div>';
      html += '</section>';

      // Group items into shipments (respects mixed_loading_disable flag)
      var shipments = this.groupItemsIntoShipments(items);

      // Use 'single' as recipientId for single mode
      var singleRecipientId = 'single';

      // Badge labels and icons for each shipping type
      var shippingBadges = {
        ambient: { label: '常温便', icon: window.shippingAssets ? window.shippingAssets.iconTruck : '', cssClass: 'ship-badge--ambient' },
        frozen: { label: '冷凍便', icon: window.shippingAssets ? window.shippingAssets.iconFrozen : '', cssClass: 'ship-badge--frozen' },
        mail: { label: 'メール便', icon: window.shippingAssets ? window.shippingAssets.iconMail : '', cssClass: 'ship-badge--mail' }
      };

      // Content section with interleaved badges (badge → items per shipment)
      html += '<section class="addr-content">';

      if (shipments.length > 0) {
        shipments.forEach(function(shipment, shipmentIndex) {
          var badge = shippingBadges[shipment.shippingType];

          // Shipment badge
          html += '<div class="ship-badge-list">';
          html += '<div class="ship-badge ' + badge.cssClass + '">';
          html += '<span class="ship-badge__icon" aria-hidden="true">';
          html += '<img src="' + badge.icon + '" alt="">';
          html += '</span>';
          html += '<span class="ship-badge__text">' + badge.label + '</span>';
          html += '</div>';
          html += '</div>';

          // Shipment items
          html += '<div class="addr-content__inner">';
          shipment.items.forEach(function(item, index) {
            html += self.renderSingleItemCards(item);
            if (index < shipment.items.length - 1) {
              html += '<div class="linegap u-sm-min"></div>';
            }
          });

          // Delivery time for this shipment
          html += self.renderDeliveryTimeSectionForShipment(singleRecipientId, shipment.shipmentKey, shipment.shippingType);
          html += '</div>';
        });
      } else {
        // Fallback: no shipments, show all items with default delivery time section
        html += '<div class="addr-content__inner">';
        items.forEach(function(item, index) {
          html += self.renderSingleItemCards(item);
          if (index < items.length - 1) {
            html += '<div class="linegap u-sm-min"></div>';
          }
        });
        html += '</div>';
        html += '<div class="addr-content__inner">';
        html += this.renderDeliveryTimeSection('single');
        html += '</div>';
      }

      html += '</section>';

      return html;
    },

    /**
     * Allocate an integer-yen line total proportionally across recipient quantities.
     * An explicit unassigned bucket absorbs the difference between cartQty and assigned qty,
     * so the rounding remainder is distributed across the full cart quantity and assigned
     * recipients receive only their proportional slice.
     * Returns { recipients: [...], unassigned: n }.
     */
    allocateYenByQuantity: function(totalYen, recipientQtys, cartQty) {
      var assignedQty = recipientQtys.reduce(function(s, q) { return s + q; }, 0);
      var unassignedQty = Math.max(0, cartQty - assignedQty);
      var bucketQtys = recipientQtys.concat([unassignedQty]);
      var allocations = bucketQtys.map(function(q) { return Math.floor(totalYen * q / cartQty); });
      var remainder = totalYen - allocations.reduce(function(s, v) { return s + v; }, 0);
      for (var i = bucketQtys.length - 1; i >= 0; i--) {
        if (bucketQtys[i] > 0) { allocations[i] += remainder; break; }
      }
      return {
        recipients: allocations.slice(0, recipientQtys.length),
        unassigned: allocations[allocations.length - 1]
      };
    },

    /**
     * Render items for multi address mode
     */
    renderMultiAddressItems: function(items) {
      var self = this;
      var html = '';

      if (this.multiRecipients.length === 0) {
        html += '<div class="empty-recipients">';
        html += '<p class="c-text-muted">お届け先が設定されていません。「お届け先の追加・変更/商品の振り分け」ボタンから追加してください。</p>';
        html += '</div>';
        return html;
      }

      this.buildRecipientAllocMap(items);

      this.multiRecipients.forEach(function(recipient, index) {
        html += self.renderRecipientCard(recipient, items, index);
      });

      return html;
    },

    /**
     * Build _recipientAllocMap from cart items and current recipient quantities.
     * Must be called after any mutation that changes recipient.items or cart quantities.
     */
    buildRecipientAllocMap: function(items) {
      var self = this;
      this._recipientAllocMap = {};
      (items || []).forEach(function(item) {
        var cartQty = parseInt(item.quantity, 10) || 0;
        if (cartQty === 0) return;
        var finalYen = Math.round((item.final_line_price || 0) / 100);
        var origYen  = Math.round((item.original_line_price || 0) / 100);
        var recipQtys = self.multiRecipients.map(function(r) {
          return Math.max(0, parseInt((r.items || {})[item.key], 10) || 0);
        });
        if (recipQtys.reduce(function(s, q) { return s + q; }, 0) === 0) return;
        var finalAlloc = self.allocateYenByQuantity(finalYen, recipQtys, cartQty);
        var origAlloc  = self.allocateYenByQuantity(origYen,  recipQtys, cartQty);
        self._recipientAllocMap[item.key] = {};
        self.multiRecipients.forEach(function(r, ri) {
          self._recipientAllocMap[item.key][r.id] = {
            discYen: finalAlloc.recipients[ri],
            origYen: origAlloc.recipients[ri],
            qty: recipQtys[ri]
          };
        });
      });
    },

    /**
     * Rebuild _recipientAllocMap and refresh displayed totals for every recipient card
     * that shows the given itemKey. Called after pool-only assign/unassign so sibling
     * recipients whose remainder ownership changes are also updated.
     */
    _refreshRecipientItemTotals: function(itemKey) {
      var self = this;
      this.buildRecipientAllocMap(this.cartItems || []);
      // Derive authoritative unit-price metadata from the updated cart item so all sibling
      // wrappers reflect the same discount state (e.g. after crossing a quantity tier).
      var _rritCartItem = (self.cartItems || []).find(function(i) { return i.key === itemKey; });
      var _rritOrigUnit = _rritCartItem
        ? ((_rritCartItem.original_price != null ? _rritCartItem.original_price : _rritCartItem.price) / 100) : null;
      var _rritFinalUnit = _rritCartItem
        ? ((_rritCartItem.final_price != null ? _rritCartItem.final_price : _rritCartItem.price) / 100) : null;
      $('[data-recipient-card]').each(function() {
        var $card = $(this);
        var recipId = String($card.data('recipient-id') || '');
        var $wrapper = $card.find('[data-item-key="' + itemKey + '"]');
        if (!$wrapper.length || !recipId) return;
        var recip = (self.multiRecipients || []).find(function(r) {
          return String(r.id) === recipId;
        });
        if (!recip) return;
        var qty = parseInt((recip.items || {})[itemKey], 10) || 0;
        if (qty <= 0) return;
        // Sync unit-price attributes on every sibling before _updateRecipientItemUI reads them.
        if (_rritOrigUnit !== null) {
          var _rritHasDisc = (_rritFinalUnit !== null && _rritFinalUnit < _rritOrigUnit) ||
            (_rritCartItem && _rritCartItem.original_line_price != null && _rritCartItem.original_line_price > _rritCartItem.final_line_price);
          $wrapper.find('.cart-item__unit')
            .attr('data-unit', _rritOrigUnit)
            .attr('data-discounted-unit', (_rritFinalUnit !== null && _rritFinalUnit < _rritOrigUnit) ? _rritFinalUnit : '')
            .attr('data-has-discount', _rritHasDisc ? 'true' : '');
        }
        self._updateRecipientItemUI($wrapper, qty);
      });
    },

    /**
     * Render a single recipient card with items
     */
    renderRecipientCard: function(recipient, items, index) {
      var self = this;
      var totalRecipients = this.multiRecipients.length;
      var html = '<div class="addr-sect " data-recipient-card data-recipient-id="' + recipient.id + '">';

      // Address card header
      html += '<section class="addr-card addr-card--big" aria-labelledby="addr-title-' + recipient.id + '">';
      // html += '<div class="addr-card__step" aria-hidden="true">' + (index + 1) + '</div>';
      html += '<div class="addr-card__body">';
      html += '<h3 id="addr-title-' + recipient.id + '" class="addr-card__title">お届け先' + (index + 1) + '件目/' + totalRecipients + '件中</h3>';
      var recipientAddress2WithCompany = this.getAddress2WithCompany(recipient);
      html += '<p class="addr-card__line">〒' + this.escapeHtml(this.formatZipDisplay(recipient.zip)) + ' ' + this.escapeHtml(recipient.prefecture) + ' ' + this.escapeHtml(recipient.city) + ' ' + this.escapeHtml(recipient.address1);
      if (recipientAddress2WithCompany) html += ' ' + this.escapeHtml(recipientAddress2WithCompany);
      html += '</p>';
      var _rcn = (recipient.lastName || recipient.firstName) ? ((recipient.lastName || '') + ' ' + (recipient.firstName || '')).trim() : (recipient.company || '');
      html += '<p class="addr-card__line">' + this.escapeHtml(_rcn) + ' 様</p>';
      // チェックアウト検証エラーの表示を再描画後も復元する（_setRecipientErrors 参照）
      var storedErrors = this.getStoredRecipientErrors(recipient);
      var errorKindAttrs = {
        validation: 'data-recipient-validation-errors',
        zipProvince: 'data-recipient-zip-province-error',
        blacklist: 'data-recipient-blacklist-error'
      };
      Object.keys(errorKindAttrs).forEach(function(errorKind) {
        (storedErrors[errorKind] || []).forEach(function(msg) {
          html += '<p class="p-form-error-txt" ' + errorKindAttrs[errorKind] + '>' + self.escapeHtml(msg) + '</p>';
        });
      });
      html += '</div>';
      html += '<div class="addr-card__actions">';
      html += '<button class="c-btn c-btn-outline" type="button" data-edit-recipient="' + recipient.id + '">お届け先を変更する</button>';
      html += '</div>';
      html += '</section>';

      // Check if recipient has items assigned
      var hasItems = Object.values(recipient.items || {}).some(function(qty) { return qty > 0; });

      if (hasItems) {
        // Get recipient's items
        var recipientItems = [];
        items.forEach(function(item) {
          var qty = (recipient.items || {})[item.key] || 0;
          if (qty > 0) {
            recipientItems.push(item);
          }
        });

        // Group items into shipments (respects mixed_loading_disable flag)
        var shipments = this.groupItemsIntoShipments(recipientItems);

        // Badge labels and icons for each shipping type
        var shippingBadges = {
          ambient: { label: '常温便', icon: window.shippingAssets ? window.shippingAssets.iconTruck : '', cssClass: 'ship-badge--ambient' },
          frozen: { label: '冷凍便', icon: window.shippingAssets ? window.shippingAssets.iconFrozen : '', cssClass: 'ship-badge--frozen' },
          mail: { label: 'メール便', icon: window.shippingAssets ? window.shippingAssets.iconMail : '', cssClass: 'ship-badge--mail' }
        };

        // Content section with interleaved badges (badge → items per shipment)
        html += '<section class="addr-content">';

        shipments.forEach(function(shipment) {
          var badge = shippingBadges[shipment.shippingType];

          // Shipment badge
          html += '<div class="ship-badge-list">';
          html += '<div class="ship-badge ' + badge.cssClass + '">';
          html += '<span class="ship-badge__icon" aria-hidden="true">';
          html += '<img src="' + badge.icon + '" alt="">';
          html += '</span>';
          html += '<span class="ship-badge__text">' + badge.label + '</span>';
          html += '</div>';
          html += '</div>';

          // Shipment items
          html += '<div class="addr-content__inner">';
          shipment.items.forEach(function(item, index) {
            html += self.renderRecipientItemCards(item, recipient);
            if (index < shipment.items.length - 1) {
              html += '<div class="linegap u-sm-min"></div>';
            }
          });

          // Delivery time for this shipment
          html += self.renderDeliveryTimeSectionForShipment(recipient.id, shipment.shipmentKey, shipment.shippingType);
          html += '</div>';
        });

      } else {
        // No items assigned
        html += '<section class="addr-content">';
        html += '<div class="addr-content__inner">';
        html += '<div class="addr-content__btn addr-content__btn--noitem">';
        html += '<p>この住所に送る商品が選択されていません</p>';
        html += '</div>';
        html += '</div>';
      }

      html += '</section>';
      html += '</div>';
      return html;
    },

    /**
     * Render the shopping bag (手提げ袋) section for a cart item.
     * Shared between renderCartItemCard and renderSingleItemCards (split mode).
     * @param {Object} item - Cart item
     * @param {string} itemId - Sanitized item ID for radio names
     * @param {string} [splitLineKey] - If provided, bag settings are stored per split line
     */
    renderBagSection: function(item, itemId, splitLineKey) {
      var html = '';
      var handbagVariants = this.getHandbagVariantsForProduct(item.variant_id, item.key);
      var hasHandbagOptions = handbagVariants.length > 0;
      // Use splitLineKey as settings key when split, otherwise item.key
      var bagSettingsKey = splitLineKey || item.key;
      // Resolve the effective quantity for this bag section (split line qty or item qty)
      var effectiveQty = item.quantity;
      if (splitLineKey) {
        var splitQty = this.getSingleSplitLineQty(splitLineKey);
        if (splitQty) effectiveQty = splitQty;
      }

      if (hasHandbagOptions) {
        var freeBags = handbagVariants.filter(function(b) { return parseInt(b.price, 10) === 0; });
        var paidBags = handbagVariants.filter(function(b) { return parseInt(b.price, 10) > 0; });
        var hasFreeBag = freeBags.length > 0;
        var bagRuleConfig = this.getBagRuleConfigByItemKey(item.key);
        var hasPaidBag = !bagRuleConfig.hidePaid && paidBags.length > 0;
        var onDisabledAttr = bagRuleConfig.disableOn ? ' disabled' : '';
        var onDisabledClass = bagRuleConfig.disableOn ? ' is-disable' : '';
        var offDisabledAttr = bagRuleConfig.disableOff ? ' disabled' : '';
        var offDisabledClass = bagRuleConfig.disableOff ? ' is-disable' : '';
        var showBagReductionNote = bagRuleConfig.rule === '選択可能';

        var savedItemBags = this.getSingleItemGiftSettings(bagSettingsKey);
        this.applyBagRuleToSettings(item.key, savedItemBags, hasFreeBag ? freeBags[0] : null, paidBags, effectiveQty);
        var bagSelection = this.getBagSelectionState(savedItemBags, item.key, item.variant_id);
        var toggleOnChecked = bagSelection === 'on';
        var toggleOffChecked = bagSelection === 'off';

        html += '<div class="addr-content__sect" data-bag-settings-key="' + this.escapeHtml(bagSettingsKey) + '" data-item-key="' + item.key + '">';
        html += '<div class="addr-content__item">';
        html += '<h4 class="c-ttl-03">手提げ袋';
        if (showBagReductionNote) {
          html += '<span class="p-bag-box__note u-sm-min">手提げ袋の削減にご協力ください</span>';
        }
        html += '</h4>';
        if (bagRuleConfig.noteText) {
          html += '<p class="p-bag-box__note">' + this.escapeHtml(bagRuleConfig.noteText) + '</p>';
        }
        if (!bagRuleConfig.forceOn) {
          html += '<div class="p-bag-toggle js-bagToggle">';
          html += '<label class="p-bag-toggle__item option-card' + offDisabledClass + '" data-bag-off-label>';
          html += '<input type="radio" name="bagToggle_' + itemId + '" class="option-card__radio" value="off"' + (toggleOffChecked ? ' checked' : '') + offDisabledAttr + '>';
          html += '<span>手提げ袋なし</span>';
          html += '</label>';
          html += '</div>';
        }
        html += '<div class="p-bag-box js-bagBox">';
        html += '<div class="c-card-01">';

        if (hasFreeBag) {
          var freeBag = freeBags[0];
          var hasSavedFreeBag = bagRuleConfig.forceFree ? true : (savedItemBags && savedItemBags.freeBagEnabled === true);
          var savedFreeQty = (savedItemBags && savedItemBags.bags && savedItemBags.bags[freeBag.shopifyProductVariantId] > 0) ? savedItemBags.bags[freeBag.shopifyProductVariantId] : 1;
          if (savedFreeQty > effectiveQty) savedFreeQty = effectiveQty;
          var freeDisabledAttr = (bagRuleConfig.lockFree || bagRuleConfig.disableFree) ? ' disabled' : '';
          var freeQtyDisabledClass = bagRuleConfig.disableFree ? ' is-disabled' : '';
          var freeDisabledClass = bagRuleConfig.disableFree ? ' is-disable' : '';
          var freePlusBtnDisabledAttr = freeDisabledAttr || (savedFreeQty >= effectiveQty ? ' disabled' : '');
          var freePlusBtnDisabledClass = freePlusBtnDisabledAttr ? ' is-disabled' : '';
          var freeMinusBtnDisabledAttr = freeDisabledAttr || (savedFreeQty <= 0 ? ' disabled' : '');
          var freeMinusBtnDisabledClass = freeMinusBtnDisabledAttr ? ' is-disabled' : '';
          var freeCardLockStyle = bagRuleConfig.forceOn ? ' style="cursor:default;pointer-events:none;"' : '';
          html += '<div class="c-card-01__item">';
          html += '<div class="option-card js-freeBag' + (hasSavedFreeBag ? ' is-active' : '') + freeDisabledClass + '"' + freeCardLockStyle + '>';
          html += '<input type="checkbox" name="bagBox_' + itemId + '" class="option-card__checkbox" data-bag-variant-id="' + freeBag.shopifyProductVariantId + '" data-bag-type="free"' + (hasSavedFreeBag ? ' checked' : '') + freeDisabledAttr + '>';
          html += '<div class="option-card__inner">';
          var freeBagImage = freeBag.image || this.freeBagImage;
          html += '<div class="option-card__thumb"><img src="' + freeBagImage + '" alt=""></div>';
          html += '<div class="option-card__text">';
          html += '<p class="option-card__title">' + this.escapeHtml(bagRuleConfig.freeDisplayTitle) + '<span class="c-badge c-badge--free">無料サービス</span></p>';
          if (!bagRuleConfig.forceOn) {
            html += '<div class="qty-wrap js-freeBagQty" style="' + (hasSavedFreeBag ? '' : 'display:none;') + '">';
            html += '<div class="qty qty--small">';
            html += '<button type="button" class="qty__btn js-bagQtyBtn' + freeMinusBtnDisabledClass + '" data-step="-1" aria-label="decrease"' + freeMinusBtnDisabledAttr + '>−</button>';
            html += '<input class="qty__input js-bagQtyInput" type="text" value="' + (hasSavedFreeBag ? Math.max(1, savedFreeQty) : 1) + '" inputmode="numeric" aria-label="quantity"' + freeDisabledAttr + '>';
            html += '<button type="button" class="qty__btn js-bagQtyBtn' + freePlusBtnDisabledClass + '" data-step="1" aria-label="increase"' + freePlusBtnDisabledAttr + '>＋</button>';
            html += '</div>';
            html += '</div>';
          }
          html += '</div>';
          html += '</div>';
          html += '</div>';
          html += '</div>';
        }

        if (hasPaidBag) {
          var hasSavedPaidBags = savedItemBags && savedItemBags.paidBagEnabled === true;
          var paidDisabledAttr = bagRuleConfig.disablePaid ? ' disabled' : '';
          var paidDisabledClass = bagRuleConfig.disablePaid ? ' is-disable' : '';
          var paidOpenAttr = bagRuleConfig.disablePaid ? '' : ' data-open-bag-modal';
          var paidAriaAttr = bagRuleConfig.disablePaid ? ' tabindex="-1" aria-disabled="true"' : '';
          html += '<div class="c-card-01__item" style="position: relative;">';
          html += '<label class="option-card js-bagPaid' + (hasSavedPaidBags ? ' is-active' : '') + paidDisabledClass + '">';
          html += '<input type="checkbox" name="bagBox_' + itemId + '" class="option-card__checkbox" data-bag-type="paid"' + (hasSavedPaidBags ? ' checked' : '') + paidDisabledAttr + '>';
          html += '<a href="#" class="option-card__inner"' + paidOpenAttr + paidAriaAttr + ' data-item-key="' + item.key + '" data-variant-id="' + item.variant_id + '" data-bag-settings-key="' + this.escapeHtml(bagSettingsKey) + '">';
          if (this.paidBagImage) {
            html += '<div class="option-card__thumb"><img src="' + this.paidBagImage + '" alt=""></div>';
          } else {
            html += '<div class="option-card__thumb"></div>';
          }
          html += '<div class="option-card__text">';
          html += '<p class="option-card__title">手提げ袋（紙） </p>';
          html += '</div>';
          html += '</a>';
          html += '</label>';
          if (!hasSavedPaidBags && !bagRuleConfig.disablePaid) {
            html += '<div data-overlay-paid-bag data-item-key="' + item.key + '" data-variant-id="' + item.variant_id + '" data-bag-settings-key="' + this.escapeHtml(bagSettingsKey) + '" style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 5; cursor: pointer;"></div>';
          }
          html += '</div>';
        }

        html += '</div>';
        html += '<div data-bag-status class="bag-status">' + this.renderBagStatusSummary(bagSettingsKey, 'single') + '</div>';
        html += '<div data-bag-off-status class="bag-off-status" style="' + (toggleOffChecked ? '' : 'display:none;') + '">';
        html += '<div class="p-alert p-alert--small p-alert--center">';
        html += '<div class="p-alert__icon"><img src="' + (window.shippingAssets ? window.shippingAssets.iconPackagingInfo : '') + '" alt=""></div>';
        html += '<div class="p-alert__info"><h4>手提げ袋は付属しておりません。</h4></div>';
        html += '</div></div>';
        html += '</div>';
        if (showBagReductionNote) {
          html += '<span class="p-bag-box__note u-sm-max">手提げ袋の削減にご協力ください</span>';
        }
        html += '</div>';
        html += '</div>';
      }
      return html;
    },

    /**
     * Render a cart item card with all sections (product, bag, wrapping)
     */
    renderCartItemCard: function(item) {
      var price = (item.original_price != null ? item.original_price : item.price) / 100;
      var discountedUnitPrice = (item.final_price != null ? item.final_price : item.price) / 100;
      var hasUnitDiscount = discountedUnitPrice < price;
      var hasDiscount = hasUnitDiscount || (item.original_line_price != null && item.original_line_price > item.final_line_price);

      var itemId = item.key.replace(/[^a-zA-Z0-9]/g, '_');

      // Get shipping type tag
      var shippingType = this.getItemShippingType(item);
      var tempTag = '';
      var tempClass = '';
      if (shippingType === 'frozen') {
        tempTag = '冷凍';
        tempClass = 'c-badge--blue02';
      } else if (shippingType === 'mail') {
        tempTag = 'メール便';
        tempClass = 'c-badge--mail';
      } else {
        tempTag = '常温';
        tempClass = 'c-badge--green';
      }

      // Get current selected quantity for this item (from recipient items or default to cart quantity)
      var maxQty = this.getMaxAllowedQuantityForItem(item.key, item.variant_id);
      var currentQty = item.quantity; // Default: all items go to recipient
      if (this.singleRecipient && this.singleRecipient.items && this.singleRecipient.items[item.key] !== undefined) {
        currentQty = this.singleRecipient.items[item.key];
      }
      var currentTotal = (item.original_line_price != null && item.quantity > 0)
        ? Math.round(item.original_line_price * currentQty / item.quantity / 100)
        : Math.round(price * currentQty);
      var discountedTotal = hasDiscount
        ? ((item.final_line_price != null && item.quantity > 0)
          ? Math.round(item.final_line_price * currentQty / item.quantity / 100)
          : Math.round((hasUnitDiscount ? discountedUnitPrice : price) * currentQty))
        : null;

      // Disable at bounds (min=1, max=inventory/400 limit)
      var isDecreaseDisabled = currentQty <= 1;
      var isIncreaseDisabled = currentQty >= maxQty;

      var html = '<div class="wrap-product-item" data-item-key="' + item.key + '" data-max-qty="' + maxQty + '">';



      // Section 1: Cart item (product info)
      html += '<div class="addr-content__sect">';
      html += '<div class="addr-content__item">';
      html += '<div class="cart-item">';
      html += '<div class="cart-item__thumb">';
      html += '<img src="' + (item.image || window._defaultProductImage) + '" alt="' + this.escapeHtml(item.title) + '">';
      html += '</div>';
      html += '<div class="cart-item__main">';
      html += '<div class="cart-item__top">';
      html += '<span class="c-badge ' + tempClass + '">' + tempTag + '</span>';
      html += '<h4 class="cart-item__title">' + this.escapeHtml(item.product_title) + '</h4>';
      html += '<div class="cart-item__unit u-sm-max" data-unit="' + price + '" data-discounted-unit="' + (hasUnitDiscount ? discountedUnitPrice : '') + '" data-has-discount="' + (hasDiscount ? 'true' : '') + '">';
      if (hasUnitDiscount) {
        html += renderDiscountPricePair(formatMoneyYen(price), formatMoneyYen(discountedUnitPrice));
      } else {
        html += '<span class="price ">' + formatMoneyYen(price) + '</span>';
      }
      html += '</div>';
      html += '</div>';
      html += '</div>';
      html += '<div class="cart-item__bottom">';
      html += '<div class="cart-item__right">';
      html += '<div class="cart-item__unit u-sm-min" data-unit="' + price + '" data-discounted-unit="' + (hasUnitDiscount ? discountedUnitPrice : '') + '" data-has-discount="' + (hasDiscount ? 'true' : '') + '">';
      if (hasUnitDiscount) {
        html += renderDiscountPricePair(formatMoneyYen(price), formatMoneyYen(discountedUnitPrice));
      } else {
        html += '<span class="price ">' + formatMoneyYen(price) + '</span>';
      }
      html += '</div>';

      html += '<div class="qty">';
      html += '<button type="button" class="qty__btn js-qtyBtn' + (isDecreaseDisabled ? ' is-disabled' : '') + '" data-step="-1" aria-label="decrease"' + (isDecreaseDisabled ? ' disabled' : '') + '>−</button>';
      html += '<input class="qty__input" type="number" value="' + currentQty + '" inputmode="numeric" aria-label="quantity" min="1" max="' + maxQty + '">';
      html += '<button type="button" class="qty__btn js-qtyBtn' + (isIncreaseDisabled ? ' is-disabled' : '') + '" data-step="1" aria-label="increase"' + (isIncreaseDisabled ? ' disabled' : '') + '>＋</button>';
      html += '</div>';
      html += '<div class="cart-item__total">';
      if (hasDiscount) {
        html += renderDiscountPricePair(formatMoneyYen(currentTotal), formatMoneyYen(discountedTotal));
      } else {
        html += '<span class="price ">' + formatMoneyYen(currentTotal) + '</span>';
      }
      html += '</div>';
      html += '</div>';
      html += '</div>';
         // Remove button (X)
      html += '<button type="button" class="btn-remove-item js-removeItem" aria-label="' + this.escapeHtml(item.product_title) + 'を削除する">';
      html += '<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="24" height="24" rx="12" fill="white" stroke="#9DB2CE"></rect><path d="M17.7003 7.29687L7.30029 17.6969" stroke="#9DB2CE" stroke-linecap="round" stroke-linejoin="round"></path><path d="M7.30029 7.29688L17.7003 17.6969" stroke="#9DB2CE" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
      html += '</button>';
      html += '</div>';
      html += '</div>';
      html += '</div>'; // close addr-content__sect (Section 1)

      // Section 2: 手提げ袋 (Shopping bag)
      html += this.renderBagSection(item, itemId);

      // Section 3: 包装・のし・リボン (Wrapping)
      html += '<div class="addr-content__sect">';
      html += '<div class="addr-content__item">';
      html += this.renderWrappingOptions(itemId, item.key, 'single');
      html += this.renderWrappingAlert(item.key, 'iconPackagingInfo');
      html += '</div>';
      html += '</div>';

      html += '</div>'; // wrap-product-item

      return html;
    },

    renderSingleItemCards: function(item) {
      var lines = this.getSingleSplitLinesForItem(item);
      if (!lines || lines.length < 2) {
        return this.renderCartItemCard(item);
      }

      // Precompute floor+remainder in integer yen so split card totals sum exactly to the line total.
      var _ssDiscFloors = null, _ssOrigFloors = null;
      if (item.quantity > 0) {
        var _ssFinalYen = Math.round((item.final_line_price || 0) / 100);
        var _ssOrigYen  = Math.round((item.original_line_price || 0) / 100);
        _ssDiscFloors = lines.map(function(l) {
          return Math.floor(_ssFinalYen * (parseInt(l.qty, 10) || 0) / item.quantity);
        });
        _ssDiscFloors[_ssDiscFloors.length - 1] +=
          _ssFinalYen - _ssDiscFloors.reduce(function(s, v) { return s + v; }, 0);
        _ssOrigFloors = lines.map(function(l) {
          return Math.floor(_ssOrigYen * (parseInt(l.qty, 10) || 0) / item.quantity);
        });
        _ssOrigFloors[_ssOrigFloors.length - 1] +=
          _ssOrigYen - _ssOrigFloors.reduce(function(s, v) { return s + v; }, 0);
      }

      var html = '';
      for (var i = 0; i < lines.length; i++) {
        html += this.renderSingleSplitItemCard(item, lines[i], i, lines.length,
          _ssDiscFloors ? _ssDiscFloors[i] : undefined,
          _ssOrigFloors ? _ssOrigFloors[i] : undefined);
        if (i < lines.length - 1) html += '<div class="linegap u-sm-min"></div>';
      }
      return html;
    },

    renderSingleSplitItemCard: function(item, line, lineIndex, totalLines, allottedDiscYen, allottedOrigYen) {
      var price = (item.original_price != null ? item.original_price : item.price) / 100;
      var discountedUnitPrice = (item.final_price != null ? item.final_price : item.price) / 100;
      var hasUnitDiscount = discountedUnitPrice < price;
      var hasDiscount = hasUnitDiscount || (item.original_line_price != null && item.original_line_price > item.final_line_price);

      var qty = Math.max(1, parseInt(line.qty, 10) || 1);
      // Use precomputed integer-yen allocation when available; fall back to unit × qty.
      var total = allottedDiscYen !== undefined
        ? (hasDiscount ? allottedDiscYen : allottedOrigYen)
        : (hasDiscount ? (hasUnitDiscount ? discountedUnitPrice : price) * qty : price * qty);
      var origLineTotal = allottedOrigYen !== undefined ? allottedOrigYen : price * qty;
      // 在庫ベースの上限を使う（旧: item.quantity - 1 は再配分モード用で不要）
      var maxQty = this.getMaxAllowedQuantityForItem(item.key, item.variant_id);
      var itemId = (line.splitLineKey || '').replace(/[^a-zA-Z0-9]/g, '_');

      var shippingType = this.getItemShippingType(item);
      var tempTag = '';
      var tempClass = '';
      if (shippingType === 'frozen') {
        tempTag = '冷凍';
        tempClass = 'c-badge--blue02';
      } else if (shippingType === 'mail') {
        tempTag = 'メール便';
        tempClass = 'c-badge--mail';
      } else {
        tempTag = '常温';
        tempClass = 'c-badge--green';
      }

      var isDecreaseDisabled = qty <= 1;
      var isIncreaseDisabled = qty >= maxQty;

      var html = '<div class="wrap-product-item wrap-product-item--split" data-item-key="' + this.escapeHtml(line.splitLineKey) + '" data-source-item-key="' + this.escapeHtml(item.key) + '">';
      html += '<div class="addr-content__sect">';
      html += '<div class="addr-content__item">';
      html += '<div class="cart-item">';
      html += '<div class="cart-item__thumb">';
      html += '<img src="' + (item.image || window._defaultProductImage) + '" alt="' + this.escapeHtml(item.title) + '">';
      html += '</div>';
      html += '<div class="cart-item__main">';
      html += '<div class="cart-item__top">';
      html += '<span class="c-badge ' + tempClass + '">' + tempTag + '</span>';
      html += '<h4 class="cart-item__title">' + this.escapeHtml(item.product_title) + '</h4>';
      html += '<div class="cart-item__unit u-sm-max" data-unit="' + price + '" data-discounted-unit="' + (hasUnitDiscount ? discountedUnitPrice : '') + '" data-has-discount="' + (hasDiscount ? 'true' : '') + '">';
      if (hasUnitDiscount) {
        html += renderDiscountPricePair(formatMoneyYen(price), formatMoneyYen(discountedUnitPrice));
      } else {
        html += '<span class="price ">' + formatMoneyYen(price) + '</span>';
      }
      html += '</div>';
      html += '</div>';
      html += '</div>';
      html += '<div class="cart-item__bottom">';
      html += '<div class="cart-item__right">';
      html += '<div class="cart-item__unit u-sm-min" data-unit="' + price + '" data-discounted-unit="' + (hasUnitDiscount ? discountedUnitPrice : '') + '" data-has-discount="' + (hasDiscount ? 'true' : '') + '">';
      if (hasUnitDiscount) {
        html += renderDiscountPricePair(formatMoneyYen(price), formatMoneyYen(discountedUnitPrice));
      } else {
        html += '<span class="price ">' + formatMoneyYen(price) + '</span>';
      }
      html += '</div>';
      html += '<div class="qty">';
      html += '<button type="button" class="qty__btn js-splitQtyBtn' + (isDecreaseDisabled ? ' is-disabled' : '') + '" data-step="-1" aria-label="decrease"' + (isDecreaseDisabled ? ' disabled' : '') + '>−</button>';
      html += '<input class="qty__input js-splitQtyInput" type="number" value="' + qty + '" inputmode="numeric" aria-label="quantity" min="1" max="' + maxQty + '">';
      html += '<button type="button" class="qty__btn js-splitQtyBtn' + (isIncreaseDisabled ? ' is-disabled' : '') + '" data-step="1" aria-label="increase"' + (isIncreaseDisabled ? ' disabled' : '') + '>＋</button>';
      html += '</div>';
      if (hasDiscount) {
        html += '<div class="cart-item__total">' + renderDiscountPricePair(formatMoneyYen(origLineTotal), formatMoneyYen(total)) + '</div>';
      } else {
        html += '<div class="cart-item__total"><span class="price ">' + formatMoneyYen(total) + '</span></div>';
      }
      html += '</div>';
      html += '</div>';
      // Remove button (X) - each split card has its own delete button to remove that split line only
      html += '<button type="button" class="btn-remove-item js-removeItem" aria-label="' + this.escapeHtml(item.product_title) + 'を削除する">';
      html += '<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="24" height="24" rx="12" fill="white" stroke="#9DB2CE"></rect><path d="M17.7003 7.29687L7.30029 17.6969" stroke="#9DB2CE" stroke-linecap="round" stroke-linejoin="round"></path><path d="M7.30029 7.29688L17.7003 17.6969" stroke="#9DB2CE" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
      html += '</button>';
      html += '</div>';
      html += '</div>';
      html += '</div>';

      // 手提げ袋 section per split card (independent per split line)
      html += this.renderBagSection(item, itemId, line.splitLineKey);

      html += '<div class="addr-content__sect"><div class="addr-content__item">';
      html += this.renderWrappingOptions(itemId, item.key, 'single', null, { splitLineKey: line.splitLineKey, sourceItemKey: item.key });
      html += this.renderWrappingAlert(item.key, 'iconPackagingInfo', null, { splitLineKey: line.splitLineKey, sourceItemKey: item.key });
      html += '</div></div>';
      html += '</div>';
      return html;
    },

    /**
     * Render item card for a specific recipient (with quantity per recipient)
     */
    renderRecipientItemCard: function(item, recipient) {
      var price = (item.original_price != null ? item.original_price : item.price) / 100;
      var discountedUnitPrice = (item.final_price != null ? item.final_price : item.price) / 100;
      var hasUnitDiscount = discountedUnitPrice < price;
      var hasDiscount = hasUnitDiscount || (item.original_line_price != null && item.original_line_price > item.final_line_price);

      var recipientItems = recipient.items || {};
      var qty = recipientItems[item.key] !== undefined ? recipientItems[item.key] : 0;
      var origTotal = (item.original_line_price != null && item.quantity > 0)
        ? Math.round(item.original_line_price * qty / item.quantity / 100)
        : Math.round(price * qty);
      var total = hasDiscount
        ? ((item.final_line_price != null && item.quantity > 0)
          ? Math.round(item.final_line_price * qty / item.quantity / 100)
          : Math.round((hasUnitDiscount ? discountedUnitPrice : price) * qty))
        : origTotal;
      var itemId = recipient.id + '_' + item.key.replace(/[^a-zA-Z0-9]/g, '_');

      // Calculate max available quantity for this recipient
      var totalInCart = item.quantity;
      var assignedToOthers = 0;
      this.multiRecipients.forEach(function(r) {
        if (r.id === recipient.id) return; // Skip current recipient
        if (r.items && r.items[item.key]) {
          assignedToOthers += r.items[item.key];
        }
      });
      var maxQty = totalInCart - assignedToOthers;
      var cartQtyLimit = this.getMaxAllowedQuantityForItem(item.key, item.variant_id);
      var inputMaxQty = Math.max(1, Math.min(maxQty, cartQtyLimit));

      // Get shipping type tag
      var shippingType = this.getItemShippingType(item);
      var tempTag = '';
      var tempClass = '';
      if (shippingType === 'frozen') {
        tempTag = '冷凍';
        tempClass = 'c-badge--blue02';
      } else if (shippingType === 'mail') {
        tempTag = 'メール便';
        tempClass = 'c-badge--mail';
      } else {
        tempTag = '常温';
        tempClass = 'c-badge--green';
      }

      var html = '<div class="wrap-product-item" data-item-key="' + item.key + '" data-max-qty="' + maxQty + '">';



      // Section 1: Cart item (product info)
      html += '<div class="addr-content__sect">';
      html += '<div class="addr-content__item">';
      html += '<div class="cart-item">';
      html += '<div class="cart-item__thumb">';
      html += '<img src="' + (item.image || window._defaultProductImage) + '" alt="' + this.escapeHtml(item.title) + '">';
      html += '</div>';
      html += '<div class="cart-item__main">';
      html += '<div class="cart-item__top">';
      html += '<span class="c-badge ' + tempClass + '">' + tempTag + '</span>';
      html += '<h4 class="cart-item__title">' + this.escapeHtml(item.product_title) + '</h4>';
      html += '<div class="cart-item__unit u-sm-max" data-unit="' + price + '" data-discounted-unit="' + (hasUnitDiscount ? discountedUnitPrice : '') + '" data-has-discount="' + (hasDiscount ? 'true' : '') + '">';
      if (hasUnitDiscount) {
        html += renderDiscountPricePair(formatMoneyYen(price), formatMoneyYen(discountedUnitPrice));
      } else {
        html += '<span class="price ">' + formatMoneyYen(price) + '</span>';
      }
      html += '</div>';
      html += '</div>';
      html += '</div>';
      html += '<div class="cart-item__bottom">';
      html += '<div class="cart-item__right">';
      html += '<div class="cart-item__unit u-sm-min" data-unit="' + price + '" data-discounted-unit="' + (hasUnitDiscount ? discountedUnitPrice : '') + '" data-has-discount="' + (hasDiscount ? 'true' : '') + '">';
      if (hasUnitDiscount) {
        html += renderDiscountPricePair(formatMoneyYen(price), formatMoneyYen(discountedUnitPrice));
      } else {
        html += '<span class="price ">' + formatMoneyYen(price) + '</span>';
      }
      html += '</div>';
      var isIncreaseDisabled = qty >= cartQtyLimit;
      html += '<div class="qty">';
      html += '<button type="button" class="qty__btn' + (qty <= 1 ? ' is-disabled' : '') + '" data-qty-btn data-step="-1"' + (qty <= 1 ? ' disabled' : '') + ' aria-label="decrease">−</button>';
      html += '<input class="qty__input" type="number" data-qty-input value="' + qty + '" inputmode="numeric" aria-label="quantity" min="1" max="' + inputMaxQty + '">';
      html += '<button type="button" class="qty__btn' + (isIncreaseDisabled ? ' is-disabled' : '') + '" data-qty-btn data-step="1" aria-label="increase"' + (isIncreaseDisabled ? ' disabled' : '') + '>＋</button>';
      html += '</div>';
      html += '<div class="cart-item__total">';
      if (hasDiscount) {
        html += renderDiscountPricePair(formatMoneyYen(origTotal), formatMoneyYen(total));
      } else {
        html += '<span class="price ">' + formatMoneyYen(total) + '</span>';
      }
      html += '</div>';
      html += '</div>';
      html += '</div>';
       // Remove button (X)
      html += '<button type="button" class="btn-remove-item js-removeItem" aria-label="' + this.escapeHtml(item.product_title) + 'を削除する">';
      html += '<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="24" height="24" rx="12" fill="white" stroke="#9DB2CE"></rect><path d="M17.7003 7.29687L7.30029 17.6969" stroke="#9DB2CE" stroke-linecap="round" stroke-linejoin="round"></path><path d="M7.30029 7.29688L17.7003 17.6969" stroke="#9DB2CE" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
      html += '</button>';
      html += '</div>';
      html += '</div>';
      html += '</div>';

      // Section 2: 手提げ袋 (Shopping bag) - using handbagProductVariants from config
      var handbagVariants = this.getHandbagVariantsForProduct(item.variant_id, item.key);
      var hasHandbagOptions = handbagVariants.length > 0;

      if (hasHandbagOptions) {
        // Separate free and paid bags
        var freeBags = handbagVariants.filter(function(b) { return parseInt(b.price, 10) === 0; });
        var paidBags = handbagVariants.filter(function(b) { return parseInt(b.price, 10) > 0; });
        var hasFreeBag = freeBags.length > 0;
        var bagRuleConfig = this.getBagRuleConfigByItemKey(item.key);
        var hasPaidBag = !bagRuleConfig.hidePaid && paidBags.length > 0;
        var onDisabledAttr = bagRuleConfig.disableOn ? ' disabled' : '';
        var onDisabledClass = bagRuleConfig.disableOn ? ' is-disable' : '';
        var offDisabledAttr = bagRuleConfig.disableOff ? ' disabled' : '';
        var offDisabledClass = bagRuleConfig.disableOff ? ' is-disable' : '';
        var showBagReductionNote = bagRuleConfig.rule === '選択可能';

        // Check if there are saved bags for this item (from recipient.giftSettings)
        var savedItemBags = this.getRecipientItemSettings(recipient, item.key);
        this.applyBagRuleToSettings(item.key, savedItemBags, hasFreeBag ? freeBags[0] : null, paidBags, qty);
        var bagSelection = this.getBagSelectionState(savedItemBags, item.key, item.variant_id);
        var toggleOnChecked = bagSelection === 'on';
        var toggleOffChecked = bagSelection === 'off';

        html += '<div class="addr-content__sect" data-item-key="' + item.key + '">';
        html += '<div class="addr-content__item">';
        html += '<h4 class="c-ttl-03">手提げ袋';
        if (showBagReductionNote) {
          html += '<span class="p-bag-box__note u-sm-min">手提げ袋の削減にご協力ください</span>';
        }
        html += '</h4>';
        if (bagRuleConfig.noteText) {
          html += '<p class="p-bag-box__note">' + this.escapeHtml(bagRuleConfig.noteText) + '</p>';
        }
        if (!bagRuleConfig.forceOn) {
          html += '<div class="p-bag-toggle js-bagToggle">';
          html += '<label class="p-bag-toggle__item option-card' + offDisabledClass + '" data-bag-off-label>';
          html += '<input type="radio" name="bagToggle_' + itemId + '" class="option-card__radio" value="off"' + (toggleOffChecked ? ' checked' : '') + offDisabledAttr + '>';
          html += '<span>手提げ袋なし</span>';
          html += '</label>';
          html += '</div>';
        }
        html += '<div class="p-bag-box js-bagBox">';
        html += '<div class="c-card-01">';

        // 手提げ袋（クリア） (Free bag option)
        if (hasFreeBag) {
          var freeBag = freeBags[0];
          var hasSavedFreeBag = bagRuleConfig.forceFree ? true : (savedItemBags && savedItemBags.freeBagEnabled === true);
          var savedFreeQty = (savedItemBags && savedItemBags.bags && savedItemBags.bags[freeBag.shopifyProductVariantId] > 0) ? savedItemBags.bags[freeBag.shopifyProductVariantId] : 1;
          var freeDisabledAttr = (bagRuleConfig.lockFree || bagRuleConfig.disableFree) ? ' disabled' : '';
          var freeQtyDisabledClass = bagRuleConfig.disableFree ? ' is-disabled' : '';
          var freeDisabledClass = bagRuleConfig.disableFree ? ' is-disable' : '';
          var freePlusBtnDisabledAttr = freeDisabledAttr || (savedFreeQty >= qty ? ' disabled' : '');
          var freePlusBtnDisabledClass = freePlusBtnDisabledAttr ? ' is-disabled' : '';
          var freeCardLockStyle = bagRuleConfig.forceOn ? ' style="cursor:default;pointer-events:none;"' : '';
          html += '<div class="c-card-01__item">';
          html += '<div class="option-card js-freeBag' + (hasSavedFreeBag ? ' is-active' : '') + freeDisabledClass + '"' + freeCardLockStyle + '>';
          html += '<input type="checkbox" name="bagBox_' + itemId + '" class="option-card__checkbox" data-bag-variant-id="' + freeBag.shopifyProductVariantId + '" data-bag-type="free"' + (hasSavedFreeBag ? ' checked' : '') + freeDisabledAttr + '>';
          html += '<div class="option-card__inner">';
          var freeBagImage = freeBag.image || this.freeBagImage;
          html += '<div class="option-card__thumb"><img src="' + freeBagImage + '" alt=""></div>';
          html += '<div class="option-card__text">';
          html += '<p class="option-card__title">' + this.escapeHtml(bagRuleConfig.freeDisplayTitle) + '<span class="c-badge c-badge--free">無料サービス</span></p>';
          if (!bagRuleConfig.forceOn) {
            html += '<div class="qty-wrap js-freeBagQty" style="' + (hasSavedFreeBag ? '' : 'display:none;') + '">';
            html += '<div class="qty qty--small">';
            html += '<button type="button" class="qty__btn js-bagQtyBtn' + freeQtyDisabledClass + '" data-step="-1" aria-label="decrease"' + freeDisabledAttr + '>−</button>';
            html += '<input class="qty__input js-bagQtyInput" type="text" value="' + (hasSavedFreeBag ? Math.max(1, savedFreeQty) : 1) + '" inputmode="numeric" aria-label="quantity"' + freeDisabledAttr + '>';
            html += '<button type="button" class="qty__btn js-bagQtyBtn' + freePlusBtnDisabledClass + '" data-step="1" aria-label="increase"' + freePlusBtnDisabledAttr + '>＋</button>';
            html += '</div>';
            html += '</div>';
          }
          html += '</div>';
          html += '</div>';
          html += '</div>';
          html += '</div>';
        }

        // 手提げ袋（紙） (Paid bag option - opens modal)
        // Overlay blocks checkbox when unchecked — when checked, no overlay so user can uncheck
        if (hasPaidBag) {
          var hasSavedPaidBags = savedItemBags && savedItemBags.paidBagEnabled === true;
          var paidDisabledAttr = bagRuleConfig.disablePaid ? ' disabled' : '';
          var paidDisabledClass = bagRuleConfig.disablePaid ? ' is-disable' : '';
          var paidOpenAttr = bagRuleConfig.disablePaid ? '' : ' data-open-bag-modal';
          var paidAriaAttr = bagRuleConfig.disablePaid ? ' tabindex="-1" aria-disabled="true"' : '';
          html += '<div class="c-card-01__item" style="position: relative;">';
          html += '<label class="option-card js-bagPaid' + (hasSavedPaidBags ? ' is-active' : '') + paidDisabledClass + '">';
          html += '<input type="checkbox" name="bagBox_' + itemId + '" class="option-card__checkbox" data-bag-type="paid"' + (hasSavedPaidBags ? ' checked' : '') + paidDisabledAttr + '>';
          html += '<a href="#" class="option-card__inner"' + paidOpenAttr + paidAriaAttr + ' data-item-key="' + item.key + '" data-variant-id="' + item.variant_id + '" data-recipient-id="' + recipient.id + '">';
          if (this.paidBagImage) {
            html += '<div class="option-card__thumb"><img src="' + this.paidBagImage + '" alt=""></div>';
          } else {
            html += '<div class="option-card__thumb"></div>';
          }
          html += '<div class="option-card__text">';
          html += '<p class="option-card__title">手提げ袋（紙） </p>';
          // html += '<p class="option-card__desc">手提げ袋（紙）のサイズを選択</p>';
          html += '</div>';
          html += '</a>';
          html += '</label>';
          if (!hasSavedPaidBags && !bagRuleConfig.disablePaid) {
            html += '<div data-overlay-paid-bag data-item-key="' + item.key + '" data-variant-id="' + item.variant_id + '" data-recipient-id="' + recipient.id + '" style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 5; cursor: pointer;"></div>';
          }
          html += '</div>';
        }

        html += '</div>';
        // Bag status summary placeholder
        html += '<div data-bag-status class="bag-status">' + this.renderBagStatusSummary(item.key, 'multi', recipient) + '</div>';
        html += '<div data-bag-off-status class="bag-off-status" style="' + (toggleOffChecked ? '' : 'display:none;') + '">';
        html += '<div class="p-alert p-alert--small p-alert--center">';
        html += '<div class="p-alert__icon"><img src="' + (window.shippingAssets ? window.shippingAssets.iconPackagingInfo : '') + '" alt=""></div>';
        html += '<div class="p-alert__info"><h4>手提げ袋は付属しておりません。</h4></div>';
        html += '</div></div>';
        html += '</div>';
        html += '</div>';
        html += '</div>';
      }

      // Section 3: 包装・のし・リボン (Wrapping)
      html += '<div class="addr-content__sect">';
      html += '<div class="addr-content__item">';
      html += this.renderWrappingOptions(itemId, item.key, 'multi', recipient);
      html += this.renderWrappingAlert(item.key, 'iconInfo', recipient);
      html += '</div>';
      html += '</div>';

      html += '</div>'; // wrap-product-item

      return html;
    },

    /**
     * Render recipient item cards — handles split lines for multi mode
     */
    renderRecipientItemCards: function(item, recipient) {
      var lines = this.getRecipientSplitLinesForItem(recipient, item);
      if (!lines || lines.length < 2) {
        return this.renderRecipientItemCard(item, recipient);
      }

      // Two-level allocation: use the globally pre-allocated recipient yen from _recipientAllocMap
      // (built in renderMultiAddressItems) so all recipients together sum to the cart line total.
      // Fall back to independent proportional rounding when the map is not available.
      var _rsDiscFloors = null, _rsOrigFloors = null;
      var _rsRecipQty = lines.reduce(function(s, l) { return s + (parseInt(l.qty, 10) || 0); }, 0);
      if (_rsRecipQty > 0) {
        var _rsAllocEntry = this._recipientAllocMap &&
          this._recipientAllocMap[item.key] &&
          this._recipientAllocMap[item.key][recipient.id];
        var _rsRecipFinalYen = _rsAllocEntry ? _rsAllocEntry.discYen
          : (item.quantity > 0 ? Math.round((item.final_line_price || 0) * _rsRecipQty / item.quantity / 100) : 0);
        var _rsRecipOrigYen  = _rsAllocEntry ? _rsAllocEntry.origYen
          : (item.quantity > 0 ? Math.round((item.original_line_price || 0) * _rsRecipQty / item.quantity / 100) : 0);
        _rsDiscFloors = lines.map(function(l) {
          return Math.floor(_rsRecipFinalYen * (parseInt(l.qty, 10) || 0) / _rsRecipQty);
        });
        _rsDiscFloors[_rsDiscFloors.length - 1] +=
          _rsRecipFinalYen - _rsDiscFloors.reduce(function(s, v) { return s + v; }, 0);
        _rsOrigFloors = lines.map(function(l) {
          return Math.floor(_rsRecipOrigYen * (parseInt(l.qty, 10) || 0) / _rsRecipQty);
        });
        _rsOrigFloors[_rsOrigFloors.length - 1] +=
          _rsRecipOrigYen - _rsOrigFloors.reduce(function(s, v) { return s + v; }, 0);
      }

      var html = '';
      for (var i = 0; i < lines.length; i++) {
        html += this.renderRecipientSplitItemCard(item, recipient, lines[i], i, lines.length,
          _rsDiscFloors ? _rsDiscFloors[i] : undefined,
          _rsOrigFloors ? _rsOrigFloors[i] : undefined);
        if (i < lines.length - 1) html += '<div class="linegap u-sm-min"></div>';
      }
      return html;
    },

    renderRecipientSplitItemCard: function(item, recipient, line, lineIndex, totalLines, allottedDiscYen, allottedOrigYen) {
      var price = (item.original_price != null ? item.original_price : item.price) / 100;
      var discountedUnitPrice = (item.final_price != null ? item.final_price : item.price) / 100;
      var hasUnitDiscount = discountedUnitPrice < price;
      var hasDiscount = hasUnitDiscount || (item.original_line_price != null && item.original_line_price > item.final_line_price);
      var qty = Math.max(1, parseInt(line.qty, 10) || 1);
      // Use precomputed integer-yen allocation when available; fall back to unit × qty.
      var total = allottedDiscYen !== undefined
        ? (hasDiscount ? allottedDiscYen : allottedOrigYen)
        : (hasDiscount ? (hasUnitDiscount ? discountedUnitPrice : price) : price) * qty;
      var origLineTotal = allottedOrigYen !== undefined ? allottedOrigYen : price * qty;
      var itemId = recipient.id + '_' + (line.splitLineKey || '').replace(/[^a-zA-Z0-9]/g, '_');

      var shippingType = this.getItemShippingType(item);
      var tempTag = '';
      var tempClass = '';
      if (shippingType === 'frozen') {
        tempTag = '冷凍'; tempClass = 'c-badge--blue02';
      } else if (shippingType === 'mail') {
        tempTag = 'メール便'; tempClass = 'c-badge--mail';
      } else {
        tempTag = '常温'; tempClass = 'c-badge--green';
      }

      var html = '<div class="wrap-product-item wrap-product-item--split" data-item-key="' + this.escapeHtml(line.splitLineKey) + '" data-source-item-key="' + this.escapeHtml(item.key) + '" data-recipient-id="' + recipient.id + '">';

      // Section 1: Product info
      html += '<div class="addr-content__sect">';
      html += '<div class="addr-content__item">';
      html += '<div class="cart-item">';
      html += '<div class="cart-item__thumb">';
      html += '<img src="' + (item.image || window._defaultProductImage) + '" alt="' + this.escapeHtml(item.title) + '">';
      html += '</div>';
      html += '<div class="cart-item__main">';
      html += '<div class="cart-item__top">';
      html += '<span class="c-badge ' + tempClass + '">' + tempTag + '</span>';
      html += '<h4 class="cart-item__title">' + this.escapeHtml(item.product_title) + '</h4>';
      html += '<div class="cart-item__unit u-sm-max" data-unit="' + price + '" data-discounted-unit="' + (hasUnitDiscount ? discountedUnitPrice : '') + '" data-has-discount="' + (hasDiscount ? 'true' : '') + '">';
      if (hasUnitDiscount) {
        html += renderDiscountPricePair(formatMoneyYen(price), formatMoneyYen(discountedUnitPrice));
      } else {
        html += '<span class="price ">' + formatMoneyYen(price) + '</span>';
      }
      html += '</div>';
      html += '</div>';
      html += '</div>';
      html += '<div class="cart-item__bottom">';
      html += '<div class="cart-item__right">';
      html += '<div class="cart-item__unit u-sm-min" data-unit="' + price + '" data-discounted-unit="' + (hasUnitDiscount ? discountedUnitPrice : '') + '" data-has-discount="' + (hasDiscount ? 'true' : '') + '">';
      if (hasUnitDiscount) {
        html += renderDiscountPricePair(formatMoneyYen(price), formatMoneyYen(discountedUnitPrice));
      } else {
        html += '<span class="price ">' + formatMoneyYen(price) + '</span>';
      }
      html += '</div>';
      var maxQty = this.getMaxAllowedQuantityForItem(item.key, item.variant_id);
      var isDecreaseDisabled = qty <= 1;
      var isIncreaseDisabled = qty >= maxQty;
      html += '<div class="qty">';
      html += '<button type="button" class="qty__btn js-multiSplitQtyBtn' + (isDecreaseDisabled ? ' is-disabled' : '') + '" data-step="-1" aria-label="decrease"' + (isDecreaseDisabled ? ' disabled' : '') + '>−</button>';
      html += '<input class="qty__input js-multiSplitQtyInput" type="number" value="' + qty + '" inputmode="numeric" aria-label="quantity" min="1" max="' + maxQty + '">';
      html += '<button type="button" class="qty__btn js-multiSplitQtyBtn' + (isIncreaseDisabled ? ' is-disabled' : '') + '" data-step="1" aria-label="increase"' + (isIncreaseDisabled ? ' disabled' : '') + '>＋</button>';
      html += '</div>';
      html += '<div class="cart-item__total">';
      if (hasDiscount) {
        html += renderDiscountPricePair(formatMoneyYen(origLineTotal), formatMoneyYen(total));
      } else {
        html += '<span class="price ">' + formatMoneyYen(total) + '</span>';
      }
      html += '</div>';
      html += '</div>';
      html += '</div>';
      // Remove button (X) - each split card has its own delete button to remove that split line only
      html += '<button type="button" class="btn-remove-item js-removeItem" aria-label="' + this.escapeHtml(item.product_title) + 'を削除する">';
      html += '<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="24" height="24" rx="12" fill="white" stroke="#9DB2CE"></rect><path d="M17.7003 7.29687L7.30029 17.6969" stroke="#9DB2CE" stroke-linecap="round" stroke-linejoin="round"></path><path d="M7.30029 7.29688L17.7003 17.6969" stroke="#9DB2CE" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
      html += '</button>';
      html += '</div>';
      html += '</div>';
      html += '</div>';

      // Section 2: 手提げ袋 - use split line bag settings from recipient.giftSettings[splitLineKey]
      var handbagVariants = this.getHandbagVariantsForProduct(item.variant_id, item.key);
      if (handbagVariants.length > 0) {
        var freeBags = handbagVariants.filter(function(b) { return parseInt(b.price, 10) === 0; });
        var paidBags = handbagVariants.filter(function(b) { return parseInt(b.price, 10) > 0; });
        var hasFreeBag = freeBags.length > 0;
        var bagRuleConfig = this.getBagRuleConfigByItemKey(item.key);
        var hasPaidBag = !bagRuleConfig.hidePaid && paidBags.length > 0;
        var offDisabledAttr = bagRuleConfig.disableOff ? ' disabled' : '';
        var offDisabledClass = bagRuleConfig.disableOff ? ' is-disable' : '';
        var showBagReductionNote = bagRuleConfig.rule === '選択可能';

        var savedItemBags = this.getRecipientItemSettings(recipient, line.splitLineKey);
        this.applyBagRuleToSettings(item.key, savedItemBags, hasFreeBag ? freeBags[0] : null, paidBags, qty);
        var bagSelection = this.getBagSelectionState(savedItemBags, item.key, item.variant_id);
        var toggleOffChecked = bagSelection === 'off';

        html += '<div class="addr-content__sect" data-item-key="' + this.escapeHtml(line.splitLineKey) + '" data-source-item-key="' + this.escapeHtml(item.key) + '">';
        html += '<div class="addr-content__item">';
        html += '<h4 class="c-ttl-03">手提げ袋';
        if (showBagReductionNote) {
          html += '<span class="p-bag-box__note u-sm-min">手提げ袋の削減にご協力ください</span>';
        }
        html += '</h4>';
        if (bagRuleConfig.noteText) {
          html += '<p class="p-bag-box__note">' + this.escapeHtml(bagRuleConfig.noteText) + '</p>';
        }
        if (!bagRuleConfig.forceOn) {
          html += '<div class="p-bag-toggle js-bagToggle">';
          html += '<label class="p-bag-toggle__item option-card' + offDisabledClass + '" data-bag-off-label>';
          html += '<input type="radio" name="bagToggle_' + itemId + '" class="option-card__radio" value="off"' + (toggleOffChecked ? ' checked' : '') + offDisabledAttr + '>';
          html += '<span>手提げ袋なし</span>';
          html += '</label>';
          html += '</div>';
        }
        html += '<div class="p-bag-box js-bagBox">';
        html += '<div class="c-card-01">';

        if (hasFreeBag) {
          var freeBag = freeBags[0];
          var savedFreeQty = 1;
          if (savedItemBags.bags && savedItemBags.bags[freeBag.shopifyProductVariantId] > 0) {
            savedFreeQty = savedItemBags.bags[freeBag.shopifyProductVariantId];
          }
          var hasSavedFreeBag = savedItemBags.freeBagEnabled === true;
          var freeDisabledAttr = (bagRuleConfig.lockFree || bagRuleConfig.disableFree) ? ' disabled' : '';
          var freeDisabledClass = bagRuleConfig.disableFree ? ' is-disable' : '';
          var freePlusBtnDisabledAttr = freeDisabledAttr || (savedFreeQty >= qty ? ' disabled' : '');
          var freeMinusBtnDisabledAttr = freeDisabledAttr || (savedFreeQty <= 0 ? ' disabled' : '');
          var freeCardLockStyle = bagRuleConfig.forceOn ? ' style="cursor:default;pointer-events:none;"' : '';
          html += '<div class="c-card-01__item">';
          html += '<div class="option-card js-freeBag' + (hasSavedFreeBag ? ' is-active' : '') + freeDisabledClass + '"' + freeCardLockStyle + '>';
          html += '<input type="checkbox" name="bagBox_' + itemId + '" class="option-card__checkbox" data-bag-variant-id="' + freeBag.shopifyProductVariantId + '" data-bag-type="free"' + (hasSavedFreeBag ? ' checked' : '') + freeDisabledAttr + '>';
          html += '<div class="option-card__inner">';
          html += '<div class="option-card__thumb"><img src="' + (freeBag.image || this.freeBagImage) + '" alt=""></div>';
          html += '<div class="option-card__text">';
          html += '<p class="option-card__title">' + this.escapeHtml(bagRuleConfig.freeDisplayTitle) + '<span class="c-badge c-badge--free">無料サービス</span></p>';
          if (!bagRuleConfig.forceOn) {
            html += '<div class="qty-wrap js-freeBagQty" style="' + (hasSavedFreeBag ? '' : 'display:none;') + '">';
            html += '<div class="qty qty--small">';
            html += '<button type="button" class="qty__btn js-bagQtyBtn" data-step="-1" aria-label="decrease"' + freeMinusBtnDisabledAttr + '>−</button>';
            html += '<input class="qty__input js-bagQtyInput" type="text" value="' + (hasSavedFreeBag ? Math.max(1, savedFreeQty) : 1) + '" inputmode="numeric" aria-label="quantity"' + freeDisabledAttr + '>';
            html += '<button type="button" class="qty__btn js-bagQtyBtn" data-step="1" aria-label="increase"' + freePlusBtnDisabledAttr + '>＋</button>';
            html += '</div>';
            html += '</div>';
          }
          html += '</div>';
          html += '</div>';
          html += '</div>';
          html += '</div>';
        }

        if (hasPaidBag) {
          var hasSavedPaidBags = savedItemBags.paidBagEnabled === true;
          var paidImage = this.paidBagImage || '';
          var paidDisabledAttr = bagRuleConfig.disablePaid ? ' disabled' : '';
          var paidDisabledClass = bagRuleConfig.disablePaid ? ' is-disable' : '';
          html += '<div class="c-card-01__item" style="position: relative;">';
          html += '<label class="option-card js-bagPaid' + (hasSavedPaidBags ? ' is-active' : '') + paidDisabledClass + '">';
          html += '<input type="checkbox" name="bagBox_' + itemId + '" class="option-card__checkbox" data-bag-type="paid"' + (hasSavedPaidBags ? ' checked' : '') + paidDisabledAttr + '>';
          html += '<a href="#" class="option-card__inner"' + (bagRuleConfig.disablePaid ? '' : ' data-open-bag-modal') + ' data-item-key="' + this.escapeHtml(item.key) + '" data-variant-id="' + item.variant_id + '" data-recipient-id="' + recipient.id + '" data-bag-settings-key="' + this.escapeHtml(line.splitLineKey) + '">';
          html += '<div class="option-card__thumb">' + (paidImage ? '<img src="' + paidImage + '" alt="">' : '') + '</div>';
          html += '<div class="option-card__text"><p class="option-card__title">手提げ袋（紙） </p></div>';
          html += '</a>';
          html += '</label>';
          if (!hasSavedPaidBags && !bagRuleConfig.disablePaid) {
            html += '<div data-overlay-paid-bag data-item-key="' + this.escapeHtml(item.key) + '" data-variant-id="' + item.variant_id + '" data-recipient-id="' + recipient.id + '" data-bag-settings-key="' + this.escapeHtml(line.splitLineKey) + '" style="position:absolute;top:0;left:0;right:0;bottom:0;z-index:5;cursor:pointer;"></div>';
          }
          html += '</div>';
        }

        html += '</div>';
        html += '<div data-bag-status class="bag-status">' + this.renderBagStatusSummary(line.splitLineKey, 'multi', recipient) + '</div>';
        html += '<div data-bag-off-status class="bag-off-status" style="' + (toggleOffChecked ? '' : 'display:none;') + '">';
        html += '<div class="p-alert p-alert--small p-alert--center">';
        html += '<div class="p-alert__icon"><img src="' + (window.shippingAssets ? window.shippingAssets.iconPackagingInfo : '') + '" alt=""></div>';
        html += '<div class="p-alert__info"><h4>手提げ袋は付属しておりません。</h4></div>';
        html += '</div></div>';
        html += '</div>';
        html += '</div>';
        html += '</div>';
      }

      // Section 3: 包装・のし・リボン
      html += '<div class="addr-content__sect">';
      html += '<div class="addr-content__item">';
      html += this.renderWrappingOptions(itemId, item.key, 'multi', recipient, { splitLineKey: line.splitLineKey, sourceItemKey: item.key });
      html += this.renderWrappingAlert(item.key, 'iconInfo', recipient, { splitLineKey: line.splitLineKey, sourceItemKey: item.key });
      html += '</div>';
      html += '</div>';

      html += '</div>'; // wrap-product-item
      return html;
    },

    isSplitLineKey: function(itemKey) {
      return typeof itemKey === 'string' && itemKey.indexOf('::split:') !== -1;
    },

    makeSplitLineKey: function(itemKey, lineId) {
      return itemKey + '::split:' + lineId;
    },

    parseSplitLineKey: function(splitLineKey) {
      if (!this.isSplitLineKey(splitLineKey)) return null;
      var parts = String(splitLineKey).split('::split:');
      if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
      return { itemKey: parts[0], lineId: parts[1] };
    },

    getSingleSplitLinesByItemKey: function(itemKey) {
      var cfg = this.singleSplitLines[itemKey];
      if (!cfg || !Array.isArray(cfg.lines)) return [];
      return cfg.lines;
    },

    getSingleSplitLineByKey: function(splitLineKey) {
      var parsed = this.parseSplitLineKey(splitLineKey);
      if (!parsed) return null;
      var lines = this.getSingleSplitLinesByItemKey(parsed.itemKey);
      for (var i = 0; i < lines.length; i++) {
        if (String(lines[i].id) === parsed.lineId) return lines[i];
      }
      return null;
    },

    getSingleSplitLineSettings: function(splitLineKey) {
      var line = this.getSingleSplitLineByKey(splitLineKey);
      if (!line) return null;
      if (!line.settings) line.settings = { noshi: null, ribbon: null, wrapOnly: false, noWrap: false };
      return line.settings;
    },

    setSingleSplitLineSettings: function(splitLineKey, nextSettings) {
      var line = this.getSingleSplitLineByKey(splitLineKey);
      if (!line) return;
      line.settings = nextSettings || { noshi: null, ribbon: null, wrapOnly: false, noWrap: false };
    },

    /**
     * Copy bag settings from the parent item to each split line.
     * Called when split lines are first created so each split card inherits the parent's bag state.
     */
    copyBagSettingsToSplitLines: function(itemKey) {
      var parentBag = this.itemGiftOptions.single && this.itemGiftOptions.single[itemKey];
      if (!parentBag) return;
      var cfg = this.singleSplitLines[itemKey];
      if (!cfg || !Array.isArray(cfg.lines)) return;
      var self = this;
      cfg.lines.forEach(function(line) {
        var splitKey = self.makeSplitLineKey(itemKey, line.id);
        if (!self.itemGiftOptions.single[splitKey]) {
          self.itemGiftOptions.single[splitKey] = JSON.parse(JSON.stringify(parentBag));
        }
      });
    },

    clearSingleSplitItemLegacySettings: function(itemKey) {
      delete this.noshiSettings.single[itemKey];
      delete this.stickerSettings.single[itemKey];
      delete this.wrapOnlyItems.single[itemKey];
      delete this.noWrapItems.single[itemKey];
    },

    // =========================================================================
    // Multi-recipient split lines (per recipient per item)
    // =========================================================================

    getRecipientSplitLines: function(recipient, itemKey) {
      if (!recipient || !recipient.splitLines) return [];
      var cfg = recipient.splitLines[itemKey];
      if (!cfg || !Array.isArray(cfg.lines) || cfg.lines.length < 2) return [];
      var self = this;
      return cfg.lines.map(function(line) {
        return {
          id: String(line.id || ''),
          qty: line.qty,
          settings: line.settings,
          splitLineKey: self.makeSplitLineKey(itemKey, line.id)
        };
      });
    },

    getRecipientSplitLineByKey: function(recipient, splitLineKey) {
      var parsed = this.parseSplitLineKey(splitLineKey);
      if (!parsed || !recipient || !recipient.splitLines) return null;
      var cfg = recipient.splitLines[parsed.itemKey];
      if (!cfg || !Array.isArray(cfg.lines)) return null;
      for (var i = 0; i < cfg.lines.length; i++) {
        if (String(cfg.lines[i].id) === parsed.lineId) return cfg.lines[i];
      }
      return null;
    },

    getRecipientSplitLineSettings: function(recipient, splitLineKey) {
      var line = this.getRecipientSplitLineByKey(recipient, splitLineKey);
      if (!line) return null;
      if (!line.settings) line.settings = { noshi: null, ribbon: null, wrapOnly: false, noWrap: false };
      return line.settings;
    },

    getRecipientSplitLineQty: function(recipient, splitLineKey) {
      var line = this.getRecipientSplitLineByKey(recipient, splitLineKey);
      return line ? Math.max(1, parseInt(line.qty, 10) || 1) : null;
    },

    createRecipientSplitLinesFromNoshi: function(recipient, itemKey, totalQty, noshiData, noshiQty) {
      var qty = parseInt(totalQty, 10) || 1;
      var nQty = Math.max(1, Math.min(parseInt(noshiQty, 10) || qty, qty - 1));
      var plainQty = qty - nQty;
      if (SHIPPING_DEBUG) console.log('[SPLIT][multi][createFromNoshi] recipientId:', recipient.id, 'itemKey:', itemKey, 'totalQty:', qty, 'noshiQty:', noshiQty, 'nQty:', nQty, 'plainQty:', plainQty);
      if (plainQty <= 0) return;

      var noshiCopy = JSON.parse(JSON.stringify(noshiData || {}));
      noshiCopy.count = nQty;

      if (!recipient.splitLines) recipient.splitLines = {};
      recipient.splitLines[itemKey] = {
        lines: [
          { id: 'a', qty: nQty, settings: { noshi: noshiCopy, ribbon: null, wrapOnly: false, noWrap: false } },
          { id: 'b', qty: plainQty, settings: { noshi: null, ribbon: null, wrapOnly: false, noWrap: false } }
        ]
      };
      // Copy parent bag settings to each split line
      this.copyRecipientBagSettingsToSplitLines(recipient, itemKey);
      // Clear legacy settings on recipient.giftSettings[itemKey]
      this.clearRecipientSplitItemLegacySettings(recipient, itemKey);
    },

    createRecipientSplitLinesFromSticker: function(recipient, itemKey, totalQty, ribbonData, ribbonQty) {
      var qty = parseInt(totalQty, 10) || 1;
      var rQty = Math.max(1, Math.min(parseInt(ribbonQty, 10) || qty, qty - 1));
      var plainQty = qty - rQty;
      if (plainQty <= 0) return;

      var ribbonCopy = JSON.parse(JSON.stringify(ribbonData || {}));
      ribbonCopy.count = rQty;

      if (!recipient.splitLines) recipient.splitLines = {};
      recipient.splitLines[itemKey] = {
        lines: [
          { id: 'a', qty: rQty, settings: { noshi: null, ribbon: ribbonCopy, wrapOnly: false, noWrap: false } },
          { id: 'b', qty: plainQty, settings: { noshi: null, ribbon: null, wrapOnly: false, noWrap: false } }
        ]
      };
      this.copyRecipientBagSettingsToSplitLines(recipient, itemKey);
      this.clearRecipientSplitItemLegacySettings(recipient, itemKey);
    },

    splitExistingRecipientSplitLine: function(recipient, splitLineKey, noshiData, ribbonData, giftQty) {
      var parsed = this.parseSplitLineKey(splitLineKey);
      if (!parsed || !recipient || !recipient.splitLines) return;
      var cfg = recipient.splitLines[parsed.itemKey];
      if (!cfg || !Array.isArray(cfg.lines)) return;

      var targetIndex = -1;
      for (var i = 0; i < cfg.lines.length; i++) {
        if (String(cfg.lines[i].id) === parsed.lineId) { targetIndex = i; break; }
      }
      if (targetIndex === -1) return;

      var targetLine = cfg.lines[targetIndex];
      var targetQty = parseInt(targetLine.qty, 10) || 1;
      var gQty = Math.max(1, Math.min(parseInt(giftQty, 10) || 1, targetQty - 1));
      var remainQty = targetQty - gQty;
      if (remainQty <= 0) return;

      var existingIds = {};
      cfg.lines.forEach(function(l) { existingIds[String(l.id)] = true; });
      var newId = parsed.lineId;
      var suffix = 1;
      while (existingIds[newId + '_' + suffix]) suffix++;
      newId = newId + '_' + suffix;

      var giftNoshi = noshiData ? JSON.parse(JSON.stringify(noshiData)) : null;
      if (giftNoshi) giftNoshi.count = gQty;
      var giftRibbon = ribbonData ? JSON.parse(JSON.stringify(ribbonData)) : null;
      if (giftRibbon) giftRibbon.count = gQty;

      var newLine = {
        id: newId,
        qty: gQty,
        settings: { noshi: giftNoshi, ribbon: giftRibbon, wrapOnly: false, noWrap: false }
      };

      targetLine.qty = remainQty;
      cfg.lines.splice(targetIndex, 0, newLine);

      // Copy bag settings from original to new split line
      var origBagKey = this.makeSplitLineKey(parsed.itemKey, parsed.lineId);
      var newBagKey = this.makeSplitLineKey(parsed.itemKey, newId);
      var gs = recipient.giftSettings || {};
      if (gs[origBagKey]) {
        gs[newBagKey] = JSON.parse(JSON.stringify(gs[origBagKey]));
      }
    },

    normalizeRecipientSplitLines: function(recipient, itemKey, totalQty) {
      if (!recipient || !recipient.splitLines) return [];
      var cfg = recipient.splitLines[itemKey];
      var qty = Math.max(1, parseInt(totalQty, 10) || 1);
      if (!cfg || !Array.isArray(cfg.lines) || cfg.lines.length < 2) {
        if (recipient.splitLines) delete recipient.splitLines[itemKey];
        return [];
      }

      cfg.lines = cfg.lines.filter(function(line) {
        return line && (parseInt(line.qty, 10) || 0) > 0;
      });
      if (cfg.lines.length < 2) {
        delete recipient.splitLines[itemKey];
        return [];
      }

      var sum = 0;
      cfg.lines.forEach(function(line) {
        line.qty = Math.max(1, parseInt(line.qty, 10) || 1);
        if (!line.settings) line.settings = { noshi: null, ribbon: null, wrapOnly: false, noWrap: false };
        if (line.settings.noshi && line.settings.noshi.count) {
          line.settings.noshi.count = Math.max(1, Math.min(parseInt(line.settings.noshi.count, 10) || line.qty, line.qty));
        }
        if (line.settings.ribbon && line.settings.ribbon.count) {
          line.settings.ribbon.count = Math.max(1, Math.min(parseInt(line.settings.ribbon.count, 10) || line.qty, line.qty));
        }
        sum += line.qty;
      });

      if (sum !== qty) {
        var lastLine = cfg.lines[cfg.lines.length - 1];
        lastLine.qty = Math.max(1, lastLine.qty + (qty - sum));
      }
      sum = 0;
      cfg.lines.forEach(function(line) { sum += line.qty; });
      if (sum !== qty || cfg.lines.length < 2) {
        delete recipient.splitLines[itemKey];
        return [];
      }

      var self = this;
      return cfg.lines.map(function(line) {
        return {
          id: String(line.id || ''),
          qty: line.qty,
          settings: line.settings,
          splitLineKey: self.makeSplitLineKey(itemKey, line.id)
        };
      });
    },

    copyRecipientBagSettingsToSplitLines: function(recipient, itemKey) {
      var gs = recipient.giftSettings || {};
      var parentBag = gs[itemKey];
      if (!parentBag || !recipient.splitLines) return;
      var cfg = recipient.splitLines[itemKey];
      if (!cfg || !Array.isArray(cfg.lines)) return;
      var self = this;
      cfg.lines.forEach(function(line) {
        var splitKey = self.makeSplitLineKey(itemKey, line.id);
        if (!gs[splitKey]) {
          gs[splitKey] = JSON.parse(JSON.stringify(parentBag));
        }
      });
    },

    clearRecipientSplitItemLegacySettings: function(recipient, itemKey) {
      var gs = recipient.giftSettings || {};
      if (gs[itemKey]) {
        gs[itemKey].noshi = null;
        gs[itemKey].ribbon = null;
        gs[itemKey].wrapOnly = false;
        gs[itemKey].noWrap = false;
      }
    },

    getRecipientSplitLinesForItem: function(recipient, item) {
      if (!item || !item.key || !recipient) return [];
      var recipientQty = (recipient.items && recipient.items[item.key]) || 0;
      if (recipientQty <= 1) {
        if (recipient.splitLines) delete recipient.splitLines[item.key];
        return [];
      }
      var normalized = this.normalizeRecipientSplitLines(recipient, item.key, recipientQty);
      return normalized.length > 0 ? normalized : [];
    },

    createSingleSplitLinesFromNoshi: function(itemKey, totalQty, noshiData, noshiQty) {
      var qty = parseInt(totalQty, 10) || 1;
      var nQty = Math.max(1, Math.min(parseInt(noshiQty, 10) || qty, qty - 1));
      var plainQty = qty - nQty;
      if (SHIPPING_DEBUG) console.log('[SPLIT][createFromNoshi] itemKey:', itemKey, 'totalQty:', qty, 'noshiQty:', noshiQty, 'nQty:', nQty, 'plainQty:', plainQty, '\n' + (new Error().stack || '').split('\n').slice(1, 5).join(' | '));
      if (plainQty <= 0) return;

      var noshiCopy = JSON.parse(JSON.stringify(noshiData || {}));
      noshiCopy.count = nQty;

      this.singleSplitLines[itemKey] = {
        lines: [
          { id: 'a', qty: nQty, settings: { noshi: noshiCopy, ribbon: null, wrapOnly: false, noWrap: false } },
          { id: 'b', qty: plainQty, settings: { noshi: null, ribbon: null, wrapOnly: false, noWrap: false } }
        ]
      };
      // Copy parent bag settings to each split line
      this.copyBagSettingsToSplitLines(itemKey);
      this.clearSingleSplitItemLegacySettings(itemKey);
    },

    /**
     * Split an existing split line into two: one with gift settings, one without.
     * Used when user sets noshi/ribbon on a subset of an already-split line.
     * @param {string} splitLineKey - The split line key to further split
     * @param {Object|null} noshiData - Noshi data for the new line (or null)
     * @param {Object|null} ribbonData - Ribbon/sticker data for the new line (or null)
     * @param {number} giftQty - How many units get the gift setting
     */
    splitExistingSplitLine: function(splitLineKey, noshiData, ribbonData, giftQty) {
      var parsed = this.parseSplitLineKey(splitLineKey);
      if (!parsed) return;
      var itemKey = parsed.itemKey;
      var cfg = this.singleSplitLines[itemKey];
      if (!cfg || !Array.isArray(cfg.lines)) return;

      var targetIndex = -1;
      for (var i = 0; i < cfg.lines.length; i++) {
        if (String(cfg.lines[i].id) === parsed.lineId) {
          targetIndex = i;
          break;
        }
      }
      if (targetIndex === -1) return;

      var targetLine = cfg.lines[targetIndex];
      var targetQty = parseInt(targetLine.qty, 10) || 1;
      var gQty = Math.max(1, Math.min(parseInt(giftQty, 10) || 1, targetQty - 1));
      var remainQty = targetQty - gQty;
      if (remainQty <= 0) return;

      // Generate a unique line id
      var existingIds = {};
      cfg.lines.forEach(function(l) { existingIds[String(l.id)] = true; });
      var newId = parsed.lineId;
      var suffix = 1;
      while (existingIds[newId + '_' + suffix]) suffix++;
      newId = newId + '_' + suffix;

      // Build the new gift line
      var giftNoshi = noshiData ? JSON.parse(JSON.stringify(noshiData)) : null;
      if (giftNoshi) giftNoshi.count = gQty;
      var giftRibbon = ribbonData ? JSON.parse(JSON.stringify(ribbonData)) : null;
      if (giftRibbon) giftRibbon.count = gQty;

      var newLine = {
        id: newId,
        qty: gQty,
        settings: { noshi: giftNoshi, ribbon: giftRibbon, wrapOnly: false, noWrap: false }
      };

      // Shrink the original line
      targetLine.qty = remainQty;

      // Insert the new line before the target line
      cfg.lines.splice(targetIndex, 0, newLine);

      // Copy bag settings from the original split line to the new split line
      var origBagKey = this.makeSplitLineKey(itemKey, parsed.lineId);
      var newBagKey = this.makeSplitLineKey(itemKey, newId);
      var origBagSettings = this.itemGiftOptions.single && this.itemGiftOptions.single[origBagKey];
      if (origBagSettings) {
        this.itemGiftOptions.single[newBagKey] = JSON.parse(JSON.stringify(origBagSettings));
      }

      if (SHIPPING_DEBUG) console.log('[SPLIT][splitExisting] splitLineKey:', splitLineKey, 'giftQty:', gQty, 'remainQty:', remainQty, 'newLineId:', newId, 'lines:', JSON.stringify(cfg.lines));
    },

    createSingleSplitLinesFromSticker: function(itemKey, totalQty, ribbonData, ribbonQty) {
      var qty = parseInt(totalQty, 10) || 1;
      var rQty = Math.max(1, Math.min(parseInt(ribbonQty, 10) || qty, qty - 1));
      var plainQty = qty - rQty;
      if (plainQty <= 0) return;

      var ribbonCopy = JSON.parse(JSON.stringify(ribbonData || {}));
      ribbonCopy.count = rQty;

      this.singleSplitLines[itemKey] = {
        lines: [
          { id: 'a', qty: rQty, settings: { noshi: null, ribbon: ribbonCopy, wrapOnly: false, noWrap: false } },
          { id: 'b', qty: plainQty, settings: { noshi: null, ribbon: null, wrapOnly: false, noWrap: false } }
        ]
      };
      // Copy parent bag settings to each split line
      this.copyBagSettingsToSplitLines(itemKey);
      this.clearSingleSplitItemLegacySettings(itemKey);
    },

    normalizeSingleSplitLines: function(itemKey, totalQty) {
      var cfg = this.singleSplitLines[itemKey];
      var qty = Math.max(1, parseInt(totalQty, 10) || 1);
      if (SHIPPING_DEBUG) console.log('[SPLIT][normalize] called: itemKey:', itemKey, 'totalQty:', qty, 'hasCfg:', !!cfg, cfg ? 'lines:' + JSON.stringify(cfg.lines) : '');
      if (!cfg || !Array.isArray(cfg.lines) || cfg.lines.length < 2) {
        delete this.singleSplitLines[itemKey];
        return [];
      }

      cfg.lines = cfg.lines.filter(function(line) {
        return line && (parseInt(line.qty, 10) || 0) > 0;
      });
      if (cfg.lines.length < 2) {
        delete this.singleSplitLines[itemKey];
        return [];
      }

      var sum = 0;
      cfg.lines.forEach(function(line) {
        line.qty = Math.max(1, parseInt(line.qty, 10) || 1);
        if (!line.settings) line.settings = { noshi: null, ribbon: null, wrapOnly: false, noWrap: false };
        if (line.settings.noshi && line.settings.noshi.count) {
          line.settings.noshi.count = Math.max(1, Math.min(parseInt(line.settings.noshi.count, 10) || line.qty, line.qty));
        }
        if (line.settings.ribbon && line.settings.ribbon.count) {
          line.settings.ribbon.count = Math.max(1, Math.min(parseInt(line.settings.ribbon.count, 10) || line.qty, line.qty));
        }
        sum += line.qty;
      });

      if (sum !== qty) {
        // 差分は最後のライン（端数/非のし側）に吸収する。
        // lines[0]（のし数）はユーザーが意図的に設定した値なので変えない。
        // 最後のラインが 1 未満になる場合は分割を解除（collapse）する。
        var lastLine = cfg.lines[cfg.lines.length - 1];
        var prevLastQty = lastLine.qty;
        lastLine.qty = Math.max(1, lastLine.qty + (qty - sum));
        if (SHIPPING_DEBUG) console.log('[SPLIT][normalize] itemKey:', itemKey, 'totalQty:', qty, 'sum:', sum, 'diff:', qty - sum, 'lastLine before:', prevLastQty, '→ after:', lastLine.qty);
      }
      // Recompute sum and collapse if invalid after adjustment.
      sum = 0;
      cfg.lines.forEach(function(line) { sum += line.qty; });
      if (sum !== qty || cfg.lines.length < 2) {
        if (SHIPPING_DEBUG) console.log('[SPLIT][normalize] collapse → delete singleSplitLines for', itemKey);
        delete this.singleSplitLines[itemKey];
        return [];
      }

      var self = this;
      return cfg.lines.map(function(line) {
        return {
          id: String(line.id || ''),
          qty: line.qty,
          settings: line.settings,
          splitLineKey: self.makeSplitLineKey(itemKey, line.id)
        };
      });
    },

    getSingleSplitLinesForItem: function(item) {
      if (!item || !item.key) return [];
      var totalQty = Math.max(1, parseInt(item.quantity, 10) || 1);
      if (totalQty <= 1) {
        delete this.singleSplitLines[item.key];
        return [];
      }

      // If split config already exists, normalize and use it.
      var normalized = this.normalizeSingleSplitLines(item.key, totalQty);
      if (SHIPPING_DEBUG) console.log('[SPLIT][getSplitLines] itemKey:', item.key, 'totalQty:', totalQty, 'result:', normalized.length > 0 ? JSON.stringify(normalized.map(function(l){return {id:l.id,qty:l.qty};})) : 'none');
      if (normalized.length > 0) return normalized;

      return [];
    },

    getSingleSplitLineQty: function(splitLineKey) {
      var line = this.getSingleSplitLineByKey(splitLineKey);
      return line ? Math.max(1, parseInt(line.qty, 10) || 1) : null;
    },

    updateSingleSplitLineQty: function(splitLineKey, nextQty) {
      var parsed = this.parseSplitLineKey(splitLineKey);
      if (!parsed) return false;

      var cartItem = this.cartItems.find(function(i) { return i.key === parsed.itemKey; });
      if (!cartItem) return false;

      var totalQty = Math.max(1, parseInt(cartItem.quantity, 10) || 1);
      var lines = this.getSingleSplitLinesByItemKey(parsed.itemKey);
      if (lines.length < 2) return false;

      var targetIndex = -1;
      for (var i = 0; i < lines.length; i++) {
        if (String(lines[i].id) === parsed.lineId) {
          targetIndex = i;
          break;
        }
      }
      if (targetIndex === -1) return false;

      var otherIndex = targetIndex === 0 ? 1 : 0;
      if (!lines[otherIndex]) return false;

      var targetQty = Math.max(1, Math.min(parseInt(nextQty, 10) || 1, totalQty - 1));
      lines[targetIndex].qty = targetQty;
      lines[otherIndex].qty = totalQty - targetQty;

      // Keep noshi count aligned with line qty.
      [targetIndex, otherIndex].forEach(function(idx) {
        var line = lines[idx];
        if (line && line.settings && line.settings.noshi) {
          line.settings.noshi.count = Math.max(1, Math.min(parseInt(line.settings.noshi.count, 10) || line.qty, line.qty));
        }
        if (line && line.settings && line.settings.ribbon) {
          line.settings.ribbon.count = Math.max(1, Math.min(parseInt(line.settings.ribbon.count, 10) || line.qty, line.qty));
        }
      });

      return true;
    },

    /**
     * Render delivery time section (legacy - kept for compatibility)
     */
    renderDeliveryTimeSection: function(id) {
      var config = this.giftConfig || {};

      var html = '<hr class="p-sect-divider"><div class="addr-content__sect">';
      html += '<div class="addr-content__item shiptime">';
      html += '<h3 class="c-ttl-03">お届け日時</h3>';
      html += '<p class="shiptime__note">※「指定なし」の場合、最短でお届けいたします</p>';
      html += '<div class="shiptime__form">';

      // Delivery date select (always shown; API available flag controls enabled/disabled state)
      html += '<div class="shiptime__row">';
      html += '<label class="shiptime__label">お届け希望日</label>';
      html += '<select class="shiptime__select c-form-select" data-delivery-date="' + id + '">';
      html += '<option value="">指定なし</option>';
      html += '</select>';
      html += '</div>';

      // Delivery time select (only if enabled)
      if (config.enableDeliveryTime !== false) {
        html += '<div class="shiptime__row">';
        html += '<label class="shiptime__label">お届け希望時間</label>';
        html += '<select class="shiptime__select c-form-select" data-delivery-time="' + id + '">';
        html += '<option value="">指定なし</option>';
        html += this.generateDeliveryTimeOptions();
        html += '</select>';
        html += '</div>';
      }

      html += '</div>';
      html += '</div>';
      html += '</div>';
      return html;
    },

    /**
     * Render delivery time section for a specific shipping type (multi-mode)
     * @param {string} recipientId - Recipient ID
     * @param {string} shippingType - 'ambient', 'frozen', or 'mail'
     */
    renderDeliveryTimeSectionForShippingType: function(recipientId, shippingType) {
      var config = this.giftConfig || {};

      var html = '<hr class="p-sect-divider"><div class="addr-content__sect"><div class="addr-content__item shiptime shiptime--compact">';
      html += '<h4 class="c-ttl-04">お届け日時</h4>';
      html += '<div class="shiptime__form">';

      // Delivery date select (always shown; API available flag controls enabled/disabled state)
      html += '<div class="shiptime__row">';
      html += '<label class="shiptime__label">お届け希望日</label>';
      html += '<select class="shiptime__select c-form-select" data-recipient-id="' + recipientId + '" data-shipping-type="' + shippingType + '" data-delivery-date>';
      html += '<option value="">指定なし</option>';
      html += '</select>';
      html += '</div>';

      // Delivery time select (only if enabled)
      if (config.enableDeliveryTime !== false) {
        html += '<div class="shiptime__row">';
        html += '<label class="shiptime__label">お届け希望時間</label>';
        html += '<select class="shiptime__select c-form-select" data-recipient-id="' + recipientId + '" data-shipping-type="' + shippingType + '" data-delivery-time>';
        html += '<option value="">指定なし</option>';
        html += '</select>';
        html += '</div>';
      }

      html += '</div>';
      html += '</div>';
      html += '</div>';
      return html;
    },

    /**
     * Render delivery time section for a specific shipment
     * @param {string} recipientId - Recipient ID ('single' for single mode)
     * @param {string} shipmentKey - Unique shipment key (e.g., 'ambient', 'ambient_item123')
     * @param {string} shippingType - Shipping type for API calls ('ambient', 'frozen', 'mail')
     */
    renderDeliveryTimeSectionForShipment: function(recipientId, shipmentKey, shippingType) {
      var config = this.giftConfig || {};

      var html = '<hr class="p-sect-divider"><div class="addr-content__sect"><div class="addr-content__item shiptime shiptime--compact">';
      html += '<h4 class="c-ttl-04">お届け日時</h4>';
      html += '<div class="shiptime__form">';

      // Delivery date select (always shown; API available flag controls enabled/disabled state)
      html += '<div class="shiptime__row">';
      html += '<label class="shiptime__label">お届け希望日</label>';
      html += '<select class="shiptime__select c-form-select" data-recipient-id="' + recipientId + '" data-shipment-key="' + shipmentKey + '" data-shipping-type="' + shippingType + '" data-delivery-date>';
      html += '<option value="">指定なし</option>';
      html += '</select>';
      html += '</div>';

      // Delivery time select (only if enabled)
      if (config.enableDeliveryTime !== false) {
        html += '<div class="shiptime__row">';
        html += '<label class="shiptime__label">お届け希望時間</label>';
        html += '<select class="shiptime__select c-form-select" data-recipient-id="' + recipientId + '" data-shipment-key="' + shipmentKey + '" data-shipping-type="' + shippingType + '" data-delivery-time>';
        html += '<option value="">指定なし</option>';
        html += '</select>';
        html += '</div>';
      }

      html += '</div>';
      html += '<p class="shiptime__earliest-hint" data-shipment-key="' + shipmentKey + '" data-earliest-delivery-hint style="display:none;"></p>';
      html += '</div>';
      html += '</div>';
      return html;
    },

    deliveryUnavailableOptionHtml: '<option value="">この商品はご指定いただけません</option>',

    /**
     * Generate delivery time options based on giftConfig
     */
    generateDeliveryTimeOptions: function() {
      var config = this.giftConfig || {};
      var options = '';

      // Time slot definitions
      var timeSlots = [
        { key: 'enableShippingTimeToMorning', value: '0', label: '午前中' },
        { key: 'enableShippingTimeFrom12to14', value: '12', label: '12:00〜14:00' },
        { key: 'enableShippingTimeFrom14to16', value: '14', label: '14:00〜16:00' },
        { key: 'enableShippingTimeFrom16to18', value: '16', label: '16:00〜18:00' },
        { key: 'enableShippingTimeFrom18to20', value: '18', label: '18:00〜20:00' },
        { key: 'enableShippingTimeFrom19to21', value: '19', label: '19:00〜21:00' },
        { key: 'enableShippingTimeFrom20to21', value: '20', label: '20:00〜21:00' }
      ];

      timeSlots.forEach(function(slot) {
        if (config[slot.key] === true) {
          options += '<option value="' + slot.value + '">' + slot.label + '</option>';
        }
      });

      return options;
    },

    /**
     * Check for incomplete settings and update warning display
     */
    checkIncompleteSettings: function() {
      var incompleteRecipients = [];
      var unassignedItems = [];

      // Only check in multi-address mode
      if (!this.isMultiAddress || this.multiRecipients.length === 0) {
        this.hideWarning();
        return;
      }

      // Calculate total assigned quantities per item
      var assignedQty = {};
      this.cartItems.forEach(function(item) {
        assignedQty[item.key] = 0;
      });

      this.multiRecipients.forEach(function(recipient) {
        var items = recipient.items || {};
        Object.keys(items).forEach(function(key) {
          if (assignedQty[key] !== undefined) {
            assignedQty[key] += items[key] || 0;
          }
        });
      });

      // Check for unassigned items
      this.cartItems.forEach(function(item) {
        var totalQty = item.quantity;
        var assigned = assignedQty[item.key] || 0;
        if (assigned < totalQty) {
          unassignedItems.push({
            item: item,
            remaining: totalQty - assigned
          });
        }
      });

      // Check each recipient for items without configuration
      this.multiRecipients.forEach(function(recipient, index) {
        var hasItems = Object.values(recipient.items || {}).some(function(qty) { return qty > 0; });

        // Mark recipients without items
        if (!hasItems) {
          incompleteRecipients.push({
            recipient: recipient,
            index: index,
            items: [],
            noItems: true
          });
        }
      });

      // Show warning if there are unassigned items or recipients without items
      if (unassignedItems.length > 0 || incompleteRecipients.length > 0) {
        this.showWarningWithUnassigned(unassignedItems, incompleteRecipients);
      } else {
        this.hideWarning();
      }
    },

    /**
     * Show warning with unassigned items and incomplete recipients
     */
    showWarningWithUnassigned: function(unassignedItems, incompleteRecipients) {
      var self = this;
      var $warning = $('[data-ship-warning]');
      var $body = $('[data-warning-body]');
      var $title = $('[data-warning-title]');
      var $desc = $('[data-warning-desc]');

      if ($warning.length === 0) return;

      var html = '';

      // Update title and description based on issue type
      if (unassignedItems.length > 0 && incompleteRecipients.length > 0) {
        $title.text('複数配送の設定が完了していません');
        $desc.text('商品の配送先が設定されていない、またはお届け先に商品が選択されていません');
      } else if (unassignedItems.length > 0) {
        $title.text('商品の配送先が設定されていません');
        $desc.text('すべての商品をお届け先に割り当ててください');
      } else {
        $title.text('複数配送の設定が完了していません');
        $desc.text('お届け先に送る商品が選択されていません');
      }

      // Show unassigned items section
      if (unassignedItems.length > 0) {
        html += '<section class="ship-card ship-card--address">';
        html += '<div class="ship-card__title">未割り当ての商品</div>';
        html += '<div class="ship-address">';
        html += '<p class="ship-address__line">以下の商品がお届け先に割り当てられていません</p>';
        html += '</div>';
        html += '</section>';

        html += '<section class="ship-card ship-card--groups">';

        var ambientItems = [];
        var frozenItems = [];
        var mailItems = [];
        unassignedItems.forEach(function(itemData) {
          switch (self.getItemShippingType(itemData.item)) {
            case 'frozen':
              frozenItems.push(itemData);
              break;
            case 'mail':
              mailItems.push(itemData);
              break;
            default:
              ambientItems.push(itemData);
          }
        });

        if (ambientItems.length > 0) {
          html += '<div class="ship-group">';
          html += '<div class="ship-badge ship-badge--ambient">';
          html += '<span class="ship-badge__icon" aria-hidden="true">';
          html += '<img src="' + (window.shippingAssets ? window.shippingAssets.iconTruck : '') + '" alt="">';
          html += '</span>';
          html += '<span class="ship-badge__text">常温便</span><span class="js-open-item"></span>';
          html += '</div>';
          html += '<div class="ship-group__list">';
          ambientItems.forEach(function(itemData) {
            html += '<div class="ship-row">';
            html += '<div class="ship-row__name">' + self.escapeHtml(itemData.item.product_title) + ' (残り ' + itemData.remaining + '点)</div>';
            html += '</div>';
          });
          html += '</div>';
          html += '</div>';
        }

        if (frozenItems.length > 0) {
          html += '<div class="ship-group">';
          html += '<div class="ship-badge ship-badge--frozen">';
          html += '<span class="ship-badge__icon" aria-hidden="true">';
          html += '<img src="' + (window.shippingAssets ? window.shippingAssets.iconFrozen : '') + '" alt="">';
          html += '</span>';
          html += '<span class="ship-badge__text">冷凍便</span><span class="js-open-item"></span>';
          html += '</div>';
          html += '<div class="ship-group__list">';
          frozenItems.forEach(function(itemData) {
            html += '<div class="ship-row">';
            html += '<div class="ship-row__name">' + self.escapeHtml(itemData.item.product_title) + ' (残り ' + itemData.remaining + '点)</div>';
            html += '</div>';
          });
          html += '</div>';
          html += '</div>';
        }

        if (mailItems.length > 0) {
          html += '<div class="ship-group">';
          html += '<div class="ship-badge ship-badge--mail">';
          html += '<span class="ship-badge__icon" aria-hidden="true">';
          html += '<img src="' + (window.shippingAssets ? window.shippingAssets.iconMail : '') + '" alt="">';
          html += '</span>';
          html += '<span class="ship-badge__text">メール便</span>';
          html += '</div>';
          html += '<div class="ship-group__list">';
          mailItems.forEach(function(itemData) {
            html += '<div class="ship-row">';
            html += '<div class="ship-row__name">' + self.escapeHtml(itemData.item.product_title) + ' (残り ' + itemData.remaining + '点)</div>';
            html += '</div>';
          });
          html += '</div>';
          html += '</div>';
        }

        html += '</section>';
      }

      // Show incomplete recipients
      incompleteRecipients.forEach(function(data) {
        var recipient = data.recipient;
        var index = data.index;

        html += '<section class="ship-card ship-card--address">';
        html += '<div class="ship-card__title">お届け ' + (index + 1) + '件目</div>';
        html += '<div class="ship-address">';
        html += '<p class="ship-address__line">〒' + self.escapeHtml(self.formatZipDisplay(recipient.zip)) + '</p>';
        html += '<p class="ship-address__line">' + self.escapeHtml(recipient.prefecture) + ' ' + self.escapeHtml(recipient.city) + ' ' + self.escapeHtml(recipient.address1) + '</p>';
        var _scn = (recipient.lastName || recipient.firstName) ? ((recipient.lastName || '') + ' ' + (recipient.firstName || '')).trim() : (recipient.company || '');
        html += '<p class="ship-address__line">' + self.escapeHtml(_scn) + ' 様</p>';
        html += '</div>';
        html += '</section>';

        html += '<section class="ship-card ship-card--groups">';
        html += '<div class="ship-group">';
        html += '<div class="ship-group__list">';
        html += '<div class="ship-row">';
        html += '<div class="ship-row__name">送る商品が選択されていません</div>';
        html += '<a href="#" class="ship-row__btn c-text-link js-modal" data-id="modalSelectProducts" data-recipient-id="' + recipient.id + '">商品を選択</a>';
        html += '</div>';
        html += '</div>';
        html += '</div>';
        html += '</section>';
      });

      $body.html(html);
      $warning.show();
    },

    /**
     * Hide warning section
     */
    hideWarning: function() {
      var $warning = $('[data-ship-warning]');
      $warning.hide();
    },

    /**
     * Scroll to item configuration section
     */
    scrollToItemConfig: function(itemKey, recipientId) {
      // Find the recipient card and the product item within it
      var $recipientCard = $('[data-recipient-card][data-recipient-id="' + recipientId + '"]');
      if ($recipientCard.length === 0) return;

      var $productItem = $recipientCard.find('[data-item-key="' + itemKey + '"]');
      if ($productItem.length === 0) return;

      // Find the wrapping section (包装・のし・リボン) within the product item
      var $wrappingSection = $productItem.find('.js-packageList').closest('.addr-content__sect');
      if ($wrappingSection.length === 0) {
        $wrappingSection = $productItem;
      }

      // Scroll to the section
      $('html, body').animate({
        scrollTop: $wrappingSection.offset().top - 100
      }, 400);
    },

    /**
     * Render wrapping section for a recipient
     */
    renderWrappingSection: function(recipient) {
      var settings = recipient.wrapping || { bag: 'off', type: 'paper' };

      var html = '<div class="wrapping-section">';
      html += '<div class="wrapping-row">';

      // Shopping bag
      html += '<div class="wrapping-item">';
      html += '<span class="wrapping-label">手提げ袋</span>';
      html += '<div class="p-bag-toggle js-bagToggle">';
      html += '<label class="p-bag-toggle__item option-card' + (settings.bag === 'off' ? ' is-active' : '') + '">';
      html += '<input type="radio" name="bag_' + recipient.id + '" value="off"' + (settings.bag === 'off' ? ' checked' : '') + '>';
      html += '<span>手提げ袋なし</span>';
      html += '</label>';
      html += '<label class="p-bag-toggle__item option-card' + (settings.bag === 'on' ? ' is-active' : '') + '">';
      html += '<input type="radio" name="bag_' + recipient.id + '" value="on"' + (settings.bag === 'on' ? ' checked' : '') + '>';
      html += '<span>付ける</span>';
      html += '</label>';
      html += '</div>';
      html += '</div>';

      // Wrapping type
      html += '<div class="wrapping-item">';
      html += '<span class="wrapping-label">包装・のし・リボン</span>';
      html += '<div class="p-bag-toggle">';
      html += '<label class="p-bag-toggle__item option-card' + (settings.type === 'paper' ? ' is-active' : '') + '">';
      html += '<input type="radio" name="wrap_' + recipient.id + '" value="paper"' + (settings.type === 'paper' ? ' checked' : '') + '>';
      html += '<span>包装のみ</span>';
      html += '</label>';
      html += '<label class="p-bag-toggle__item option-card' + (settings.type === 'noshi' ? ' is-active' : '') + '">';
      html += '<input type="radio" name="wrap_' + recipient.id + '" value="noshi"' + (settings.type === 'noshi' ? ' checked' : '') + '>';
      html += '<span>のし</span>';
      html += '</label>';
      html += '<label class="p-bag-toggle__item option-card' + (settings.type === 'ribbon' ? ' is-active' : '') + '">';
      html += '<input type="radio" name="wrap_' + recipient.id + '" value="ribbon"' + (settings.type === 'ribbon' ? ' checked' : '') + '>';
      html += '<span>リボンシール・<br class="u-md-max">季節ラッピング</span>';
      html += '</label>';
      html += '</div>';
      html += '</div>';

      html += '</div>';
      html += '</div>';
      return html;
    },

    /**
     * Pre-validate zip codes via zipcloud before rendering import report
     */
    preValidateZipsAndRenderImport: function(data) {
      var self = this;
      if (!self._zipImportCache) self._zipImportCache = {};
      var rows = data.rows || [];
      var seenZips = {};
      var zipsToCheck = [];
      rows.forEach(function(row) {
        var z = ((row.payload || {}).zip || '').replace(/\D/g, '');
        if (z && /^\d{7}$/.test(z) && !(z in self._zipImportCache) && !seenZips[z]) {
          seenZips[z] = true;
          zipsToCheck.push(z);
        }
      });
      if (!zipsToCheck.length) { self.renderBulkImportReport(data); return; }
      var pending = zipsToCheck.length;
      zipsToCheck.forEach(function(zip) {
        $.ajax({
          url: '/apps/ping/postal-codes/search',
          dataType: 'jsonp',
          data: { zipcode: zip },
          timeout: 3000
        }).always(function(res) {
          self._zipImportCache[zip] = !!(res && res.results && res.results.length);
          if (--pending === 0) self.renderBulkImportReport(data);
        });
      });
    },

    /**
     * Render saved addresses in modal
     * Always uses savedAddresses (synced from API for logged-in users, localStorage for guests)
     * Renders checkbox list for multi-select (add addresses)
     */
    renderBulkImportReport: function(data) {
      var self = this;
      var s = data.summary || {};
      var rows = data.rows || [];
      var status = data.status || '';

      // Client-side validation for server-valid rows
      var replaceAll = $('#import-replace-all').is(':checked');
      var clientInvalidCount = 0;
      var duplicateCount = 0;
      self.jsValidRowIds = [];
      rows.forEach(function(row) {
        var isDuplicate = row.status === 'duplicate';
        if (isDuplicate) duplicateCount++;
        // When replaceAll: treat duplicate as valid (existing addresses will be wiped)
        var effectiveValid = row.status === 'valid' || (isDuplicate && replaceAll);
        if (!effectiveValid) return;
        // Clear server errors for promoted duplicate rows
        if (isDuplicate) row._promotedByReplaceAll = true;
        var p = row.payload || {};
        var clientErrors = [];

        // Name: 個人・法人ともに必須、combined ≤ 36 全角
        // ※ エラーメッセージは「全角」表記なし（文字数は通常カウント表示）
        var lastName = $.trim(p.lastName || '');
        var firstName = $.trim(p.firstName || '');
        if (!lastName) clientErrors.push({ message: '姓を入力してください' });
        if (!firstName) clientErrors.push({ message: '名を入力してください' });
        if (window.CharValidator) {
          var lastCvMsg = lastName ? window.CharValidator.validate(lastName, 'name') : null;
          if (lastCvMsg) clientErrors.push({ message: '姓: ' + lastCvMsg });
          var firstCvMsg = firstName ? window.CharValidator.validate(firstName, 'name') : null;
          if (firstCvMsg) clientErrors.push({ message: '名: ' + firstCvMsg });
        }
        if (lastName && firstName) {
          var nameCombined = countJaChars(lastName + firstName);
          if (nameCombined > 36) {
            var nameExcess = Math.ceil(nameCombined - 36);
            clientErrors.push({ message: 'お名前は「36文字以内」となるようご入力ください。（' + nameExcess + '文字超過しています。）' });
          }
        }

        // Address: combined ≤ 89 全角
        // ※ エラーメッセージは「全角」表記なし（文字数は通常カウント表示）
        var province = $.trim(p.province || '');
        var city = $.trim(p.city || '');
        var address1 = $.trim(p.address1 || '');
        var address2 = $.trim(p.address2 || '');
        var company = $.trim(p.company || '');
        if (window.CharValidator) {
          var cityCvMsg = city ? window.CharValidator.validate(city, 'global') : null;
          if (cityCvMsg) clientErrors.push({ message: '市区町村: ' + cityCvMsg });
          var addr1CvMsg = address1 ? window.CharValidator.validate(address1, 'address') : null;
          if (addr1CvMsg) clientErrors.push({ message: '住所1: ' + addr1CvMsg });
          var addr2CvMsg = address2 ? window.CharValidator.validate(address2, 'address') : null;
          if (addr2CvMsg) clientErrors.push({ message: '住所2: ' + addr2CvMsg });
          var companyCvMsg = company ? window.CharValidator.validate(company, 'address') : null;
          if (companyCvMsg) clientErrors.push({ message: '会社名: ' + companyCvMsg });
        }
        if (province && city && address1) {
          var combinedAddr = company
            ? (company + ' ' + province + city + address1 + address2)
            : (province + city + address1 + address2);
          if (countJaChars(combinedAddr) > 89) {
            var addrExcess = Math.ceil(countJaChars(combinedAddr) - 89);
            clientErrors.push({ message: company
              ? '「住所＋会社名」の合計が「89文字以内」となるようご入力ください。（' + addrExcess + '文字超過しています。）'
              : '「住所」は「89文字以内」となるようご入力ください。（' + addrExcess + '文字超過しています。）'
            });
          }
        }

        // Zip: check if found in zipcloud
        var zipImport = $.trim(p.zip || '').replace(/\D/g, '');
        if (zipImport && /^\d{7}$/.test(zipImport) && self._zipImportCache && self._zipImportCache[zipImport] === false) {
          clientErrors.push({ message: '郵便番号から住所が見つかりませんでした' });
        }

        // Phone: reject xxx-0000-0000 pattern
        var phoneImport = (p.phone || '').replace(/[-\s]/g, '');
        if (phoneImport && /0{8}$/.test(phoneImport)) {
          clientErrors.push({ message: '正しい電話番号を入力してください' });
        }

        if (clientErrors.length > 0) {
          row._clientErrors = clientErrors;
          clientInvalidCount++;
        } else if (row.rowId) {
          self.jsValidRowIds.push(row.rowId);
        }
      });

      // Status row
      var statusLabels = {
        'ready_to_commit': '登録可能',
        'validated': '検証済み',
        'committed': '登録完了',
        'validating': '検証中',
        'pending': '処理待ち'
      };
      var statusLabel = statusLabels[status] || status;
      $('[data-import-report-status]').text('ステータス：' + statusLabel + ' / 総件数：' + (s.total || 0) + '件');
      var promotedDuplicates = replaceAll ? duplicateCount : 0;
      $('[data-import-report-success]').text('成功：' + ((s.valid || 0) - clientInvalidCount + promotedDuplicates) + '件');
      $('[data-import-report-error]').text('エラー：' + ((s.invalid || 0) + clientInvalidCount) + '件');
      $('[data-import-report-skip]').text('スキップ：' + ((s.skipped || 0) + (replaceAll ? 0 : (s.duplicate || 0))) + '件');

      // Enable commit when there are JS-valid rows (allows partial commit even if some rows fail client validation)
      var canCommit = self.jsValidRowIds.length > 0 && (status === 'validated' || status === 'ready_to_commit');
      $('[data-import-commit]')
        .prop('disabled', !canCommit)
        .toggleClass('is-disabled', !canCommit)
        .text('住所を登録する');

      // Render address list
      var html = '';
      rows.forEach(function(row) {
        var p = row.payload || {};
        var allErrors = (row._promotedByReplaceAll ? [] : (row.errors || [])).concat(row._clientErrors || []);
        var isError = row.status === 'invalid' || row.status === 'commit_failed' || (row._clientErrors && row._clientErrors.length > 0);
        var isSuccess = (row.status === 'valid' || row.status === 'committed' || row._promotedByReplaceAll) && !row._clientErrors;
        var chipClass = isError ? 'chip danger' : (isSuccess ? 'chip success' : '');
        var chipText = isError
          ? 'エラー：' + allErrors.length + '件'
          : (isSuccess ? '成功しました' : '');
        var errorDetail = '';
        if (allErrors.length) {
          errorDetail = '<p class="import-error-detail">' +
            allErrors.map(function(e) { return e.message; }).join('<br>') +
            '</p>';
        }
        var addressLines = [
          p.company,
          '〒' + self.formatZipDisplay(p.zip || ''),
          (p.province || '') + (p.city || '') + (p.address1 || '') + (p.address2 || ''),
          p.phone ? 'TEL：' + self.formatPhoneDisplay(p.phone) : ''
        ].filter(Boolean).join('<br>');

        html += '<div class="item">' +
          '<div class="info">' +
            '<p class="name">' + (p.lastName || '') + ' ' + (p.firstName || '') + '</p>' +
            '<p class="lines">' + addressLines + '</p>' +
            errorDetail +
          '</div>' +
          '<span class="' + chipClass + '">' + chipText + '</span>' +
        '</div>';
      });
      $('[data-import-report-list]').html(html);
    },

    showBulkCommitResult: function(summary, $btn) {
      this.hideLoading();
      var s = summary || {};
      var successCount = s.committed != null ? s.committed : (s.valid || 0);
      var failCount = s.invalid || 0;
      var totalCount = s.total || 0;

      $('.p-modal').removeClass('active');

      if (successCount > 0) {
        var $importSuccessModal = $('#modalBulkImportSuccess');
        $importSuccessModal.find('[data-import-success-total]').text(totalCount);
        $importSuccessModal.find('[data-import-success-count]').text(successCount);
        $importSuccessModal.find('[data-import-fail-count]').text(failCount);
        $importSuccessModal.addClass('active');
      } else {
        var $importFailModal = $('#modalBulkImportFail');
        $importFailModal.find('[data-import-fail-message]').text('登録できる住所がありませんでした。');
        $importFailModal.addClass('active');
      }

      $('#import-replace-all').prop('checked', false);
      this.fetchCustomerAddresses();
      if ($btn) $btn.prop('disabled', false).text('住所を登録する');
    },

    pollBulkCommitJob: function(batchId, jobId, $btn, appProxyPrefix) {
      var self = this;
      var baseUrl = appProxyPrefix || '/apps/ping';
      var POLL_INTERVAL_MS = 3000;
      var POLL_TIMEOUT_MS = 5 * 60 * 1000;
      var startedAt = Date.now();

      function tick() {
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          self.hideLoading();
          $('.p-modal').removeClass('active');
          var $failModal = $('#modalBulkImportFail');
          $failModal.find('[data-import-fail-message]').text('登録処理がタイムアウトしました。ページを再読み込みして状態を確認してください。');
          $failModal.addClass('active');
          if ($btn) $btn.prop('disabled', false).text('住所を登録する');
          return;
        }
        $.ajax({
          url: baseUrl + '/customers/addresses/imports/' + batchId + '/commit/status/' + jobId,
          method: 'GET',
          xhrFields: { withCredentials: true }
        }).then(function(data) {
          var s = data.status || '';
          if (s === 'committing') {
            setTimeout(tick, POLL_INTERVAL_MS);
          } else if (s === 'completed' || s === 'failed') {
            self.showBulkCommitResult(data.summary, $btn);
          } else {
            setTimeout(tick, POLL_INTERVAL_MS);
          }
        }).fail(function() {
          setTimeout(tick, POLL_INTERVAL_MS);
        });
      }

      setTimeout(tick, POLL_INTERVAL_MS);
    },

    renderSavedAddresses: function() {
      var self = this;
      var $container = $('[data-saved-addresses]');
      var addresses = this.savedAddresses || [];

      if (addresses.length === 0) {
        $container.html('<p class="c-text-muted">保存された住所がありません。新しいお届け先を追加してください。</p>');
        return;
      }

      // 選択中の住所を先頭に表示
      var recipients = self.getRecipients();
      var selectedIds = {};
      recipients.forEach(function(r) {
        var addressId = self.getRecipientAddressId(r);
        if (addressId) selectedIds[addressId] = true;
      });
      addresses = addresses.slice().sort(function(a, b) {
        return (selectedIds[b.id] ? 1 : 0) - (selectedIds[a.id] ? 1 : 0);
      });

      var html = '';
      addresses.forEach(function(addr) {
        var isSelected = selectedIds[addr.id] || false;
        var address2WithCompany = self.getAddress2WithCompany(addr);
        var isApiAddr = addr.id && addr.id.indexOf('addr_') !== 0;

        var nameIncomplete = self.isAddressNameIncomplete(addr);
        html += '<section class="addr-card' + (isSelected ? ' is-active' : '') + (nameIncomplete ? ' addr-card--name-warn' : '') + '" aria-labelledby="addr-title">';
        html += '<div class="addr-card__body">';
        html += '<p class="addr-card__small">';
        if (addr.isDefault) html += '<span class="c-badge c-badge--blue02" style="margin-right:6px;vertical-align:middle;">会員様ご住所</span>';
        var addrDisplayName = (addr.lastName || addr.firstName)
          ? ((addr.lastName || '') + ' ' + (addr.firstName || '')).trim()
          : (addr.company || '');
        html += '<strong>' + self.escapeHtml(addrDisplayName) + ' 様</strong></p>';
        if (nameIncomplete) {
          html += '<p class="addr-card__line addr-card__name-warn"><span class="c-badge c-badge--red" style="font-size:1rem;">お名前（姓・名）が未登録です。編集してください。</span></p>';
        }
        html += '<p class="addr-card__line">〒' + self.formatZipDisplay(addr.zip || '') + ' <br>' + (addr.prefecture || '') + ' ' + (addr.city || '') + ' ' + (addr.address1 || '');
        if (address2WithCompany) html += ' ' + address2WithCompany;
        html += '</p>';
        if (addr.phone) {
          html += '<p class="addr-card__line">TEL: ' + self.formatPhoneDisplay(addr.phone) + '</p>';
        }
        html += '</div>';
        html += '<input type="checkbox" class="option-card__checkbox"' + (isSelected ? ' checked' : '') + ' data-address-id="' + addr.id + '">';
        html += '<button type="button" class="addr-card__edit-btn" data-edit-saved-address="' + self.escapeHtml(addr.id) + '">編集</button>';
        html += '</section>';
      });

      $container.html(html);
    },

    /**
     * Render address list with radio for change address modal (#modalChangeAddress)
     */
    renderChangeAddressList: function() {
      var self = this;
      var $container = $('[data-change-address-list]');
      var addresses = this.savedAddresses || [];

      if (addresses.length === 0) {
        $container.html('<p class="c-text-muted">保存された住所がありません。新しいお届け先を追加してください。</p>');
        return;
      }

      var editingRecipient = this.multiRecipients.find(function(r) { return r.id === self.editingRecipientId; });
      // 選択中の住所を先頭に表示
      if (editingRecipient) {
        var selectedId = self.getRecipientAddressId(editingRecipient);
        addresses = addresses.slice().sort(function(a, b) {
          return (b.id === selectedId ? 1 : 0) - (a.id === selectedId ? 1 : 0);
        });
      }
      var html = '';
      addresses.forEach(function(addr) {
        var isSelected = editingRecipient && self.getRecipientAddressId(editingRecipient) === addr.id;
        var address2WithCompany = self.getAddress2WithCompany(addr);
        var isApiAddr = addr.id && addr.id.indexOf('addr_') !== 0;

        var nameIncomplete = self.isAddressNameIncomplete(addr);
        html += '<section class="addr-card' + (isSelected ? ' is-active' : '') + (nameIncomplete ? ' addr-card--name-warn' : '') + '" aria-labelledby="addr-title">';
        html += '<div class="addr-card__body">';
        html += '<p class="addr-card__small">';
        if (addr.isDefault) html += '<span class="c-badge c-badge--blue02" style="margin-right:6px;vertical-align:middle;">会員様ご住所</span>';
        var changeAddrDisplayName = (addr.lastName || addr.firstName)
          ? ((addr.lastName || '') + ' ' + (addr.firstName || '')).trim()
          : (addr.company || '');
        html += '<strong>' + self.escapeHtml(changeAddrDisplayName) + ' 様</strong></p>';
        if (nameIncomplete) {
          html += '<p class="addr-card__line addr-card__name-warn"><span class="c-badge c-badge--red" style="font-size:1rem;">お名前（姓・名）が未登録です。編集してください。</span></p>';
        }
        html += '<p class="addr-card__line">〒' + self.formatZipDisplay(addr.zip || '') + ' <br>' + (addr.prefecture || '') + ' ' + (addr.city || '') + ' ' + (addr.address1 || '');
        if (address2WithCompany) html += ' ' + address2WithCompany;
        html += '</p>';
        if (addr.phone) {
          html += '<p class="addr-card__line">TEL: ' + self.formatPhoneDisplay(addr.phone) + '</p>';
        }
        html += '</div>';
        html += '<input type="radio" name="change-address-select" class="option-card__radio"' + (isSelected ? ' checked' : '') + ' data-address-id="' + addr.id + '">';
        html += '<button type="button" class="addr-card__edit-btn" data-edit-saved-address="' + self.escapeHtml(addr.id) + '">編集</button>';
        html += '</section>';
      });

      $container.html(html);
    },

    /**
     * Filter saved addresses by search query
     */
    filterSavedAddresses: function(query) {
      var addresses = this.savedAddresses || [];
      var $cards = $('[data-saved-addresses] .addr-card');

      if (!query) {
        $cards.show();
        return;
      }

      $cards.each(function() {
        var $card = $(this);
        var text = $card.text().toLowerCase();
        $card.toggle(text.indexOf(query) !== -1);
      });
    },

    /**
     * Populate delivery date dropdowns by fetching from API
     * Calls API separately for each shipment (respects mixed_loading_disable flag)
     * For multi-mode, calls API per recipient with their postal code
     */
    populateDeliveryDates: function() {
      var self = this;

      if (!this.cartItems || this.cartItems.length === 0) {
        console.warn('No cart items to fetch delivery options');
        return;
      }

      // Multi-mode: call API per recipient
      if (this.isMultiAddress && this.multiRecipients.length > 0) {
        this.populateDeliveryDatesMultiMode();
        return;
      }

      // Single mode: use first recipient's postal code
      var postalCode = this.getRecipientPostalCode();

      if (!postalCode || postalCode.length !== 7) {
        console.warn('No valid postal code for delivery options');
        $('[data-delivery-date]').html('<option value="">指定なし</option>');
        $('[data-delivery-time]').html('<option value="">指定なし</option>');
        return;
      }

      // Group items into shipments (respects mixed_loading_disable flag)
      var shipments = this.groupItemsIntoShipments(this.cartItems);

      if (shipments.length === 0) {
        console.warn('No shipments to fetch delivery options for');
        return;
      }

      // Initialize storage
      if (!this.deliveryOptionsByShipment) {
        this.deliveryOptionsByShipment = {};
      }
      if (!this.deliveryScheduleMeta) {
        this.deliveryScheduleMeta = {};
      }
      if (!this.deliveryOptionsCache) {
        this.deliveryOptionsCache = {};
      }
      if (!this.deliveryScheduleMetaCache) {
        this.deliveryScheduleMetaCache = {};
      }

      // Separate shipments into cached and uncached
      var cachedShipments = [];
      var uncachedShipments = [];

      shipments.forEach(function(shipment) {
        var variantIds = shipment.items.map(function(item) { return String(item.variant_id); }).sort().join(',');
        var cacheKey = postalCode + '_' + variantIds;
        if (self.deliveryOptionsCache[cacheKey]) {
          cachedShipments.push({ shipment: shipment, cacheKey: cacheKey });
        } else {
          uncachedShipments.push({ shipment: shipment, cacheKey: cacheKey });
        }
      });

      // Apply cached data immediately (no API call needed)
      cachedShipments.forEach(function(work) {
        var shipment = work.shipment;
        var cachedOptions = self.deliveryOptionsCache[work.cacheKey];
        var cachedMeta = self.deliveryScheduleMetaCache[work.cacheKey] || { dateAvailable: true, dateRequired: false, timeAvailable: true, timeRequired: false };

        self.deliveryOptionsByShipment[shipment.shipmentKey] = cachedOptions;
        self.deliveryScheduleMeta[shipment.shipmentKey] = cachedMeta;
        if (!self.deliveryScheduleMeta[shipment.shippingType]) {
          self.deliveryScheduleMeta[shipment.shippingType] = cachedMeta;
        }
        if (!self.deliveryOptions[shipment.shippingType]) {
          self.deliveryOptions[shipment.shippingType] = cachedOptions;
        }

        self.populateDeliveryDatesForShipment(shipment.shipmentKey, shipment.shippingType, cachedOptions, cachedMeta);
      });

      // If all shipments were cached, no API calls needed
      if (uncachedShipments.length === 0) {
        this.populateBulkTimeChips();
        return;
      }

      // Show loading state only if we need API calls
      this.setDeliveryScheduleLoading(true);

      var completedCalls = 0;
      var totalCalls = uncachedShipments.length;

      uncachedShipments.forEach(function(work) {
        var shipment = work.shipment;
        var variantIds = shipment.items.map(function(item) {
          return String(item.variant_id);
        });

        self.fetchDeliveryOptions(postalCode, variantIds, function(options, scheduleMeta) {
          // Store in cache
          self.deliveryOptionsCache[work.cacheKey] = options || [];
          self.deliveryScheduleMetaCache[work.cacheKey] = scheduleMeta;

          // Store options for this shipment
          self.deliveryOptionsByShipment[shipment.shipmentKey] = options || [];

          // Store schedule metadata (available/required flags)
          self.deliveryScheduleMeta[shipment.shipmentKey] = scheduleMeta;
          if (!self.deliveryScheduleMeta[shipment.shippingType]) {
            self.deliveryScheduleMeta[shipment.shippingType] = scheduleMeta;
          }

          // Also store by shippingType for backwards compatibility (use first shipment's options)
          if (!self.deliveryOptions[shipment.shippingType]) {
            self.deliveryOptions[shipment.shippingType] = options || [];
          }

          // Populate date/time dropdowns for this shipment
          self.populateDeliveryDatesForShipment(shipment.shipmentKey, shipment.shippingType, options, scheduleMeta);

          completedCalls++;

          // After all calls complete, populate bulk time chips and remove loading
          if (completedCalls === totalCalls) {
            self.populateBulkTimeChips();
            self.setDeliveryScheduleLoading(false);
          }
        });
      });
    },

    /**
     * Populate delivery dates for multi-mode (per recipient)
     * Each recipient has their own postal code and delivery options
     * Uses caching to avoid unnecessary API calls
     * Calls API per shipment (respects mixed_loading_disable flag)
     */
    populateDeliveryDatesMultiMode: function() {
      var self = this;

      // Show loading state on all delivery date/time selects
      this.setDeliveryScheduleLoading(true);

      // Initialize storage
      if (!this.deliveryOptionsMulti) {
        this.deliveryOptionsMulti = {};
      }
      // Cache by postalCode + shipmentKey (shared across recipients with same postal code and items)
      if (!this.deliveryOptionsCache) {
        this.deliveryOptionsCache = {};
      }
      if (!this.deliveryScheduleMetaMulti) {
        this.deliveryScheduleMetaMulti = {};
      }
      if (!this.deliveryScheduleMetaCache) {
        this.deliveryScheduleMetaCache = {};
      }

      // First pass: count total shipments that need API calls (not cached)
      var totalApiCalls = 0;
      var completedApiCalls = 0;
      var hasAnyCalls = false;

      // Collect all work items first
      var workItems = [];
      this.multiRecipients.forEach(function(recipient) {
        var postalCode = (recipient.zip || '').replace(/-/g, '');
        if (!postalCode || postalCode.length !== 7) return;

        var recipientItems = [];
        Object.keys(recipient.items || {}).forEach(function(itemKey) {
          if (recipient.items[itemKey] > 0) {
            var item = self.cartItems.find(function(i) { return i.key === itemKey; });
            if (item) recipientItems.push(item);
          }
        });
        if (recipientItems.length === 0) return;

        var shipments = self.groupItemsIntoShipments(recipientItems);
        shipments.forEach(function(shipment) {
          var variantIds = shipment.items.map(function(item) { return String(item.variant_id); }).sort().join(',');
          var cacheKey = postalCode + '_' + variantIds;
          var isCached = !!self.deliveryOptionsCache[cacheKey];
          if (!isCached) totalApiCalls++;
          workItems.push({ recipient: recipient, shipment: shipment, postalCode: postalCode, cacheKey: cacheKey, isCached: isCached });
        });
      });

      hasAnyCalls = totalApiCalls > 0;

      // Process work items
      workItems.forEach(function(work) {
        var recipient = work.recipient;
        var shipment = work.shipment;

        // Initialize storage for this recipient
        if (!self.deliveryOptionsMulti[recipient.id]) {
          self.deliveryOptionsMulti[recipient.id] = {};
        }
        if (!self.deliveryScheduleMetaMulti[recipient.id]) {
          self.deliveryScheduleMetaMulti[recipient.id] = {};
        }

        if (work.isCached) {
          // Use cached data
          var cachedOptions = self.deliveryOptionsCache[work.cacheKey];
          var cachedMeta = self.deliveryScheduleMetaCache[work.cacheKey] || { dateAvailable: true, dateRequired: false, timeAvailable: true, timeRequired: false };
          self.deliveryOptionsMulti[recipient.id][shipment.shipmentKey] = cachedOptions;
          self.deliveryScheduleMetaMulti[recipient.id][shipment.shipmentKey] = cachedMeta;

          if (!self.deliveryOptionsMulti[recipient.id][shipment.shippingType]) {
            self.deliveryOptionsMulti[recipient.id][shipment.shippingType] = cachedOptions;
          }

          self.populateDeliveryDatesForRecipientShipment(recipient.id, shipment.shipmentKey, shipment.shippingType, cachedOptions, cachedMeta);
          return;
        }

        // Not cached - call API
        var variantIds = shipment.items.map(function(item) {
          return String(item.variant_id);
        });

        self.fetchDeliveryOptions(work.postalCode, variantIds, function(options, scheduleMeta) {
          self.deliveryOptionsCache[work.cacheKey] = options || [];
          self.deliveryScheduleMetaCache[work.cacheKey] = scheduleMeta;

          self.deliveryOptionsMulti[recipient.id][shipment.shipmentKey] = options || [];
          self.deliveryScheduleMetaMulti[recipient.id][shipment.shipmentKey] = scheduleMeta;

          if (!self.deliveryOptionsMulti[recipient.id][shipment.shippingType]) {
            self.deliveryOptionsMulti[recipient.id][shipment.shippingType] = options || [];
          }

          self.populateDeliveryDatesForRecipientShipment(recipient.id, shipment.shipmentKey, shipment.shippingType, options, scheduleMeta);

          completedApiCalls++;
          if (completedApiCalls === totalApiCalls) {
            self.setDeliveryScheduleLoading(false);
          }
        });
      });

      // If all were cached (no API calls), remove loading immediately
      if (!hasAnyCalls) {
        this.setDeliveryScheduleLoading(false);
      }
    },

    /**
     * Populate delivery date/time for a specific recipient and shipping type (multi-mode)
     */
    populateDeliveryDatesForRecipient: function(recipientId, shippingType, options) {
      var weekdays = ['日', '月', '火', '水', '木', '金', '土'];

      // Build date options HTML
      var optionsHtml = '<option value="">指定なし</option>';

      if (options && options.length > 0) {
        options.forEach(function(opt) {
          var dateStr = opt.deliveryDate; // Format: YYYY/MM/DD
          var date = new Date(dateStr.replace(/\//g, '-'));
          var displayText = (date.getMonth() + 1) + '月' + date.getDate() + '日(' + weekdays[date.getDay()] + ')';
          var value = dateStr.replace(/\//g, '-'); // Convert to YYYY-MM-DD
          optionsHtml += '<option value="' + value + '">' + displayText + '</option>';
        });
      }

      // Populate date select for this recipient + shipping type
      var $dateSelect = $('[data-recipient-id="' + recipientId + '"][data-shipping-type="' + shippingType + '"][data-delivery-date]');
      if ($dateSelect.length) {
        $dateSelect.html(optionsHtml);
      }

      // Set time select to empty (depends on selected date)
      var $timeSelect = $('[data-recipient-id="' + recipientId + '"][data-shipping-type="' + shippingType + '"][data-delivery-time]');
      if ($timeSelect.length) {
        $timeSelect.html('<option value="">指定なし</option>');
      }
    },

    /**
     * Populate delivery date/time for a specific recipient and shipment (multi-mode)
     * @param {string} recipientId - Recipient ID
     * @param {string} shipmentKey - Shipment key (e.g., 'ambient', 'ambient_item123')
     * @param {string} shippingType - Shipping type for fallback selector
     * @param {array} options - Delivery options from API
     * @param {object} scheduleMeta - Schedule metadata (dateAvailable, dateRequired, timeAvailable, timeRequired)
     */
    populateDeliveryDatesForRecipientShipment: function(recipientId, shipmentKey, shippingType, options, scheduleMeta) {
      var weekdays = ['日', '月', '火', '水', '木', '金', '土'];

      // Populate date select for this recipient + shipment (using shipmentKey)
      var $dateSelect = $('[data-recipient-id="' + recipientId + '"][data-shipment-key="' + shipmentKey + '"][data-delivery-date]');
      if (!$dateSelect.length) {
        $dateSelect = $('[data-recipient-id="' + recipientId + '"][data-shipping-type="' + shippingType + '"][data-delivery-date]');
      }

      var $timeSelect = $('[data-recipient-id="' + recipientId + '"][data-shipment-key="' + shipmentKey + '"][data-delivery-time]');
      if (!$timeSelect.length) {
        $timeSelect = $('[data-recipient-id="' + recipientId + '"][data-shipping-type="' + shippingType + '"][data-delivery-time]');
      }

      // Enable/disable date and time selects based on API available flags
      var dateAvailable = scheduleMeta && scheduleMeta.dateAvailable !== false;
      var timeAvailable = scheduleMeta && scheduleMeta.timeAvailable !== false;
      $dateSelect.closest('.shiptime__row').show();
      $timeSelect.closest('.shiptime__row').show();
      $dateSelect.closest('.shiptime').show();
      if ($dateSelect.length) {
        $dateSelect.attr('data-delivery-available', dateAvailable ? '1' : '0');
        $dateSelect.prop('disabled', !dateAvailable);
      }
      if ($timeSelect.length) {
        $timeSelect.attr('data-delivery-available', timeAvailable ? '1' : '0');
        $timeSelect.prop('disabled', !timeAvailable);
      }
      if (!dateAvailable && !timeAvailable) {
        if ($dateSelect.length) {
          $dateSelect.html(this.deliveryUnavailableOptionHtml);
        }
        if ($timeSelect.length) {
          $timeSelect.html(this.deliveryUnavailableOptionHtml);
        }
        return;
      }

      // Always show the shiptime section
      $dateSelect.closest('.shiptime').show();

      // Include 指定なし only if date is not required; show 指定不可 if unavailable
      var dateRequired = scheduleMeta && scheduleMeta.dateRequired === true;
      var optionsHtml;
      if (!dateAvailable) {
        optionsHtml = '<option value="">指定不可</option>';
      } else {
        optionsHtml = dateRequired ? '<option value="">指定してください</option>' : '<option value="">指定なし</option>';
        if (options && options.length > 0) {
          options.forEach(function(opt) {
            var dateStr = opt.deliveryDate; // Format: YYYY/MM/DD
            var date = new Date(dateStr.replace(/\//g, '-'));
            var displayText = (date.getMonth() + 1) + '月' + date.getDate() + '日(' + weekdays[date.getDay()] + ')';
            var value = dateStr.replace(/\//g, '-'); // Convert to YYYY-MM-DD
            optionsHtml += '<option value="' + value + '">' + displayText + '</option>';
          });
        }
      }

      if ($dateSelect.length) {
        $dateSelect.html(optionsHtml);
      }

      // Restore previously saved date/time for this recipient/shipment
      var savedDeliveryDate = '';
      var savedDeliveryTime = '';
      var matchedRecipient = this.multiRecipients && this.multiRecipients.find(function(r) { return r.id === recipientId; });
      if (matchedRecipient && matchedRecipient.deliverySettings) {
        var ds = matchedRecipient.deliverySettings[shipmentKey] || matchedRecipient.deliverySettings[shippingType] || {};
        savedDeliveryDate = ds.date || '';
        savedDeliveryTime = ds.time || '';
      }
      if (savedDeliveryDate && $dateSelect.length) {
        $dateSelect.val(savedDeliveryDate);
      }

      // Include 指定なし only if time is not required; show 指定不可 if unavailable
      var timeRequired = scheduleMeta && scheduleMeta.timeRequired === true;
      var timeDefaultHtml = !timeAvailable ? '<option value="">指定不可</option>'
        : (timeRequired ? '<option value="">指定してください</option>' : '<option value="">指定なし</option>');
      if ($timeSelect.length) {
        $timeSelect.html(timeDefaultHtml);
        $timeSelect.toggleClass('is-error', timeRequired && !$timeSelect.val());
      }
      // Populate time slots for the saved date, then restore saved time value
      this.updateTimeSlotsForDate(shippingType, savedDeliveryDate || '', recipientId, shipmentKey);
      if (savedDeliveryTime && $timeSelect.length) {
        $timeSelect.val(savedDeliveryTime);
      }
      if ($dateSelect.length) {
        $dateSelect.toggleClass('is-error', dateRequired && !$dateSelect.val());
      }
    },

    /**
     * Populate delivery date/time dropdowns for a specific shipment (single mode)
     * @param {string} shipmentKey - Shipment key (e.g., 'ambient', 'ambient_item123')
     * @param {string} shippingType - Shipping type for bulk date selector
     * @param {array} options - Delivery options from API
     * @param {object} scheduleMeta - Schedule metadata (dateAvailable, dateRequired, timeAvailable, timeRequired)
     */
    populateDeliveryDatesForShipment: function(shipmentKey, shippingType, options, scheduleMeta) {
      var weekdays = ['日', '月', '火', '水', '木', '金', '土'];

      // Find the shiptime container for this shipment to show/hide
      var $shipmentDateSelect = $('[data-shipment-key="' + shipmentKey + '"][data-delivery-date]');
      var $shipmentTimeSelect = $('[data-shipment-key="' + shipmentKey + '"][data-delivery-time]');
      var $groupDateSelect = $('[data-shipping-type="' + shippingType + '"][data-delivery-date]').not('[data-shipment-key]');
      var $groupTimeSelect = $('[data-shipping-type="' + shippingType + '"][data-delivery-time]').not('[data-shipment-key]');

      // Enable/disable date and time selects based on API available flags
      var dateAvailable = scheduleMeta && scheduleMeta.dateAvailable !== false;
      var timeAvailable = scheduleMeta && scheduleMeta.timeAvailable !== false;
      $shipmentDateSelect.closest('.shiptime__row').show();
      $groupDateSelect.closest('.shiptime__row').show();
      $shipmentTimeSelect.closest('.shiptime__row').show();
      $groupTimeSelect.closest('.shiptime__row').show();
      $shipmentDateSelect.closest('.shiptime').show();
      $groupDateSelect.closest('.shiptime').show();
      $shipmentDateSelect.attr('data-delivery-available', dateAvailable ? '1' : '0').prop('disabled', !dateAvailable);
      $groupDateSelect.attr('data-delivery-available', dateAvailable ? '1' : '0').prop('disabled', !dateAvailable);
      $shipmentTimeSelect.attr('data-delivery-available', timeAvailable ? '1' : '0').prop('disabled', !timeAvailable);
      $groupTimeSelect.attr('data-delivery-available', timeAvailable ? '1' : '0').prop('disabled', !timeAvailable);
      if (!dateAvailable && !timeAvailable) {
        $shipmentDateSelect.html(this.deliveryUnavailableOptionHtml);
        $groupDateSelect.html(this.deliveryUnavailableOptionHtml);
        $shipmentTimeSelect.html(this.deliveryUnavailableOptionHtml);
        $groupTimeSelect.html(this.deliveryUnavailableOptionHtml);
        return;
      }

      // Always show the shiptime section
      $shipmentDateSelect.closest('.shiptime').show();
      $groupDateSelect.closest('.shiptime').show();

      // No "指定なし" if dateRequired is true; show "指定不可" if unavailable
      var dateRequired = scheduleMeta && scheduleMeta.dateRequired === true;
      var optionsHtml;
      if (!dateAvailable) {
        optionsHtml = '<option value="">指定不可</option>';
      } else {
        optionsHtml = dateRequired ? '<option value="">指定してください</option>' : '<option value="">指定なし</option>';
        if (options && options.length > 0) {
          options.forEach(function(opt) {
            var dateStr = opt.deliveryDate; // Format: YYYY/MM/DD
            var date = new Date(dateStr.replace(/\//g, '-'));
            var displayText = (date.getMonth() + 1) + '月' + date.getDate() + '日(' + weekdays[date.getDay()] + ')';
            var value = dateStr.replace(/\//g, '-'); // Convert to YYYY-MM-DD
            optionsHtml += '<option value="' + value + '">' + displayText + '</option>';
          });
        }
      }

      // Populate date select for this shipment (using shipmentKey)
      if ($shipmentDateSelect.length) {
        $shipmentDateSelect.html(optionsHtml);
      }

      // Also populate by shippingType for legacy selectors without shipmentKey
      if ($groupDateSelect.length) {
        $groupDateSelect.html(optionsHtml);
      }

      // Restore previously saved date/time for this shipment (single mode)
      var savedDeliveryDate = '';
      var savedDeliveryTime = '';
      if (this.singleRecipient && this.singleRecipient.deliverySettings) {
        var ds = this.singleRecipient.deliverySettings[shipmentKey] || this.singleRecipient.deliverySettings[shippingType] || {};
        savedDeliveryDate = ds.date || '';
        savedDeliveryTime = ds.time || '';
      }
      if (savedDeliveryDate) {
        if ($shipmentDateSelect.length) { $shipmentDateSelect.val(savedDeliveryDate); }
        if ($groupDateSelect.length) { $groupDateSelect.val(savedDeliveryDate); }
      }

      // Update time slots based on saved date (or '' for 指定なし to show all slots)
      var recipientId = $shipmentDateSelect.data('recipient-id') || $groupDateSelect.data('recipient-id') || 'single';
      this.updateTimeSlotsForDate(shippingType, savedDeliveryDate || '', recipientId, shipmentKey);

      // Restore saved time after time slots are populated
      if (savedDeliveryTime) {
        if ($shipmentTimeSelect.length) { $shipmentTimeSelect.val(savedDeliveryTime); }
        if ($groupTimeSelect.length) { $groupTimeSelect.val(savedDeliveryTime); }
      }

      // 最短お届け日のヒントテキストを設定（日付指定必須の場合は非表示）
      var $hint = $('[data-shipment-key="' + shipmentKey + '"][data-earliest-delivery-hint]');
      if ($hint.length && dateAvailable && shippingType !== 'frozen' && options && options.length > 0 && !savedDeliveryDate) {
        var earliestDateStr = options[0].deliveryDate; // Format: YYYY/MM/DD (API returns sorted)
        var earliestDate = new Date(earliestDateStr.replace(/\//g, '-'));
        var hintText = '※「指定なし」の場合、最短' + (earliestDate.getMonth() + 1) + '月' + earliestDate.getDate() + '日にお届け予定';
        $hint.text(hintText).show();
      } else {
        $hint.hide();
      }

      if ($shipmentDateSelect.length) {
        $shipmentDateSelect.toggleClass('is-error', dateRequired && !$shipmentDateSelect.val());
      }
      if ($groupDateSelect.length) {
        $groupDateSelect.toggleClass('is-error', dateRequired && !$groupDateSelect.val());
      }
    },

    /**
     * Populate delivery date/time dropdowns for items of a specific shipping type
     * @param {string} shippingType - 'ambient', 'frozen', or 'mail'
     * @param {array} items - Cart items of this shipping type
     * @param {array} options - Delivery options from API
     */
    populateDeliveryDatesForShippingType: function(shippingType, items, options) {
      var weekdays = ['日', '月', '火', '水', '木', '金', '土'];

      // Build date options HTML
      var optionsHtml = '<option value="">指定なし</option>';

      if (options && options.length > 0) {
        options.forEach(function(opt) {
          var dateStr = opt.deliveryDate; // Format: YYYY/MM/DD
          var date = new Date(dateStr.replace(/\//g, '-'));
          var displayText = (date.getMonth() + 1) + '月' + date.getDate() + '日(' + weekdays[date.getDay()] + ')';
          var value = dateStr.replace(/\//g, '-'); // Convert to YYYY-MM-DD
          optionsHtml += '<option value="' + value + '">' + displayText + '</option>';
        });
      }

      // Populate date select for this shipping type group (single mode - one per shipping type)
      var $groupDateSelect = $('[data-shipping-type="' + shippingType + '"][data-delivery-date]');
      if ($groupDateSelect.length) {
        $groupDateSelect.html(optionsHtml);
      }

      // Populate time slots for this shipping type
      this.populateDeliveryTimeSlotsForShippingType(shippingType, options);
    },

    /**
     * Map API time slot values to display labels
     */
    timeSlotLabels: {
      '0': { value: '0', label: '午前中' },
      'am': { value: '0', label: '午前中' },  // API returns 'am', convert to '0'
      '12': { value: '12', label: '12:00〜14:00' },
      '14': { value: '14', label: '14:00〜16:00' },
      '16': { value: '16', label: '16:00〜18:00' },
      '18': { value: '18', label: '18:00〜20:00' },
      '19': { value: '19', label: '19:00〜21:00' },
      '20': { value: '20', label: '20:00〜21:00' }
    },

    /**
     * Map API time slot values to AIG numeric format
     * API returns: 'am', '14', '16', '18', '19'
     * AIG accepts: '0', '12', '14', '16', '18', '19', '20'
     */
    timeSlotToAIG: {
      '0': '0',
      'am': '0',
      '12': '12',
      '14': '14',
      '16': '16',
      '18': '18',
      '19': '19',
      '20': '20',
      'MORNING': '0',
      'FROM12TO14': '12',
      'FROM14TO16': '14',
      'FROM16TO18': '16',
      'FROM18TO20': '18',
      'FROM19TO21': '19',
      'FROM20TO21': '20'
    },

    /**
     * Convert time slot value to All-In-Gift API format
     * @param {string} timeValue - Time slot value from API or UI
     * @returns {string|null} - AIG format time slot or null
     */
    convertTimeSlotToAIG: function(timeValue) {
      if (!timeValue) return null;
      return this.timeSlotToAIG[timeValue] || null;
    },

    /**
     * Populate delivery time dropdowns for a specific shipping type
     * On init, set to empty (only 指定なし) - time slots depend on selected date
     * When user selects a date, updateTimeSlotsForDate will populate based on that date
     * @param {string} shippingType - 'ambient', 'frozen', or 'mail'
     * @param {array} options - Delivery options from API (stored in this.deliveryOptions)
     */
    populateDeliveryTimeSlotsForShippingType: function(shippingType, options) {
      // Time slots depend on selected date, so init with empty (only 指定なし)
      var $groupTimeSelect = $('[data-shipping-type="' + shippingType + '"][data-delivery-time]');
      if ($groupTimeSelect.length) {
        $groupTimeSelect.html('<option value="">指定なし</option>');
      }
    },

    /**
     * Update time slots based on selected delivery date
     * @param {string} shippingType - 'ambient', 'frozen', or 'mail' (used for API lookup)
     * @param {string} selectedDate - Selected date in YYYY-MM-DD format
     * @param {string} recipientId - (Optional) Recipient ID for multi-mode or 'single'
     * @param {string} shipmentKey - (Optional) Shipment key for per-shipment selector
     */
    updateTimeSlotsForDate: function(shippingType, selectedDate, recipientId, shipmentKey) {
      var self = this;

      // Get options from the correct storage based on mode
      // Try shipmentKey first, then fallback to shippingType for backwards compatibility
      var options;
      var meta;
      if (recipientId && recipientId !== 'single' && this.deliveryOptionsMulti && this.deliveryOptionsMulti[recipientId]) {
        // Multi-mode: try shipmentKey first, then shippingType
        options = (shipmentKey && this.deliveryOptionsMulti[recipientId][shipmentKey]) ||
                  this.deliveryOptionsMulti[recipientId][shippingType] || [];
        meta = (this.deliveryScheduleMetaMulti && this.deliveryScheduleMetaMulti[recipientId] &&
                (shipmentKey && this.deliveryScheduleMetaMulti[recipientId][shipmentKey])) || null;
      } else {
        // Single mode: try shipmentKey first (from deliveryOptionsByShipment), then shippingType
        options = (shipmentKey && this.deliveryOptionsByShipment && this.deliveryOptionsByShipment[shipmentKey]) ||
                  this.deliveryOptions[shippingType] || [];
        meta = (this.deliveryScheduleMeta &&
                ((shipmentKey && this.deliveryScheduleMeta[shipmentKey]) || this.deliveryScheduleMeta[shippingType])) || null;
      }

      var availableSlots = [];

      if (selectedDate && options.length > 0) {
        // Convert selectedDate to match API format (YYYY/MM/DD)
        var apiDateFormat = selectedDate.replace(/-/g, '/');

        // Find the option matching the selected date
        for (var i = 0; i < options.length; i++) {
          if (options[i].deliveryDate === apiDateFormat) {
            availableSlots = options[i].availableTimeSlots || [];
            break;
          }
        }
      } else if (!selectedDate && options.length > 0) {
        // 「指定なし」の場合: 最も時間帯が多い日程のスロットを表示
        // （離島等で最短日の時間帯が制限されている場合、フル日程の時間帯を見せる）
        var bestSlots = [];
        for (var i = 0; i < options.length; i++) {
          var slots = options[i].availableTimeSlots || [];
          if (slots.length > bestSlots.length) {
            bestSlots = slots;
          }
        }
        availableSlots = bestSlots;
      }


      // Show "指定してください" if time selection is mandatory, "指定なし" otherwise
      var timeRequired = meta && meta.timeRequired === true;
      var timeAvailable = !(meta && meta.timeAvailable === false);
      var timeOptionsHtml = timeRequired ? '<option value="">指定してください</option>' : '<option value="">指定なし</option>';
      availableSlots.forEach(function(slotValue) {
        var slotInfo = self.timeSlotLabels[slotValue];
        if (slotInfo) {
          timeOptionsHtml += '<option value="' + slotInfo.value + '">' + slotInfo.label + '</option>';
        }
      });

      // Update time select - use shipmentKey if available for more precise selection
      var selector;
      if (shipmentKey && recipientId) {
        // New per-shipment selector
        selector = '[data-recipient-id="' + recipientId + '"][data-shipment-key="' + shipmentKey + '"][data-delivery-time]';
      } else if (recipientId) {
        // Legacy multi-mode selector
        selector = '[data-recipient-id="' + recipientId + '"][data-shipping-type="' + shippingType + '"][data-delivery-time]';
      } else {
        // Fallback selector
        selector = '[data-shipping-type="' + shippingType + '"][data-delivery-time]:not([data-recipient-id])';
      }
      var $timeSelect = $(selector);
      if ($timeSelect.length) {
        $timeSelect.html(timeOptionsHtml);
        $timeSelect.attr('data-delivery-available', timeAvailable ? '1' : '0');
        $timeSelect.prop('disabled', !timeAvailable);
      }
    },

    /**
     * Populate delivery time dropdowns for items (legacy - not used in single mode)
     */
    populateDeliveryTimeSlotsForItems: function(items, options) {
      // Get time slots from first option or use giftConfig
      var timeSlots = [];

      if (options && options.length > 0 && options[0].deliveryTimeSlots) {
        timeSlots = options[0].deliveryTimeSlots;
      }

      if (timeSlots.length === 0) {
        // Fallback to giftConfig - no need to update time selects
        return;
      }

      // Build time options HTML
      var timeOptionsHtml = '<option value="">指定なし</option>';
      timeSlots.forEach(function(slot) {
        timeOptionsHtml += '<option value="' + slot.value + '">' + slot.label + '</option>';
      });

      // Populate time selects for each item in this shipping type
      items.forEach(function(item) {
        var itemKeyEscaped = item.key.replace(/:/g, '_').replace(/\//g, '_');
        var $timeSelect = $('#wish-time-' + itemKeyEscaped);
        if ($timeSelect.length) {
          $timeSelect.html(timeOptionsHtml);
        }
      });
    },

    /**
     * Get recipient postal code based on current mode
     * Always uses recipient's zip code (not sender's)
     */
    getRecipientPostalCode: function() {
      var postalCode = '';

      // Always use recipient's zip code based on current mode
      if (this.currentMode === 'single') {
        if (this.singleRecipient) {
          postalCode = this.singleRecipient.zip || '';
        }
      } else {
        if (this.multiRecipients.length > 0) {
          postalCode = this.multiRecipients[0].zip || '';
        }
      }

      // Remove hyphens from postal code
      return postalCode.replace(/-/g, '');
    },

    /**
     * Clear cached delivery options (call when recipient changes)
     */
    clearDeliveryOptionsCache: function() {
      this.deliveryOptions = {};
      this.deliveryOptionsByShipment = {};
      this.deliveryOptionsMulti = {};
      this.deliveryOptionsCache = {};
      this.deliveryScheduleMeta = {};
      this.deliveryScheduleMetaMulti = {};
      this.deliveryScheduleMetaCache = {};
      this._shippingApiCache = {};
    },

    /**
     * Set loading state on delivery date/time selects
     * @param {boolean} isLoading - true to show loading, false to remove
     */
    setDeliveryScheduleLoading: function(isLoading) {
      this._deliveryScheduleLoading = !!isLoading;
      var $dateSelects = $('[data-delivery-date]');
      var $timeSelects = $('[data-delivery-time]');
      var $shiptimeSections = $('.shiptime');

      if (isLoading) {
        $dateSelects.prop('disabled', true).html('<option value="">読み込み中...</option>');
        $timeSelects.prop('disabled', true).html('<option value="">読み込み中...</option>');
        $shiptimeSections.addClass('is-loading');
      } else {
        $dateSelects.each(function() {
          var isUnavailable = $(this).attr('data-delivery-available') === '0';
          $(this).prop('disabled', isUnavailable);
        });
        $timeSelects.each(function() {
          var isUnavailable = $(this).attr('data-delivery-available') === '0';
          $(this).prop('disabled', isUnavailable);
        });
        $shiptimeSections.removeClass('is-loading');
      }
    },

    /**
     * Fetch delivery options from API
     * @param {string} postalCode - 7-digit postal code
     * @param {array} variantIds - Array of shopify variant IDs
     * @param {function} callback - Callback function(options)
     */
    fetchDeliveryOptions: function(postalCode, variantIds, callback) {
      var apiEndpoint = '/apps/ping/shipping/delivery-schedule/options';

      $.ajax({
        url: apiEndpoint,
        method: 'POST',
        contentType: 'application/json',
        data: JSON.stringify({
          shopifyProductVariantIds: variantIds,
          postalCode: postalCode
        }),
        success: function(data) {
          var scheduleOptions = data.deliveryScheduleOptions || {};
          var options = scheduleOptions.deliveryDateTime &&
                        scheduleOptions.deliveryDateTime.options;
          var scheduleMeta = {
            dateAvailable: scheduleOptions.deliveryDate ? scheduleOptions.deliveryDate.available !== false : true,
            // required=true  => selection is mandatory (no "指定なし")
            // required=false => "指定なし" is allowed
            dateRequired: scheduleOptions.deliveryDate ? scheduleOptions.deliveryDate.required === true : false,
            timeAvailable: scheduleOptions.deliveryTime ? scheduleOptions.deliveryTime.available !== false : true,
            timeRequired: scheduleOptions.deliveryTime ? scheduleOptions.deliveryTime.required === true : false
          };
          callback(options || [], scheduleMeta);
        },
        error: function() {
          console.error('Failed to fetch delivery options');
          callback([], { dateAvailable: true, dateRequired: false, timeAvailable: true, timeRequired: false });
        }
      });
    },

    /**
     * Populate delivery time slots based on API response
     * @param {array} options - Delivery options from API
     */
    populateDeliveryTimeSlots: function(options) {
      // Get time slots from first option or use giftConfig
      var timeSlots = [];

      if (options && options.length > 0 && options[0].deliveryTimeSlots) {
        timeSlots = options[0].deliveryTimeSlots;
      }

      if (timeSlots.length === 0) {
        // Fallback to giftConfig
        return;
      }

      // Build time options HTML
      var timeOptionsHtml = '<option value="">指定なし</option>';
      timeSlots.forEach(function(slot) {
        timeOptionsHtml += '<option value="' + slot.value + '">' + slot.label + '</option>';
      });

      // Populate time selects
      $('[data-delivery-time]').html(timeOptionsHtml);
    },

    /**
     * Populate bulk time select in modal with all available time slots
     */
    populateBulkTimeChips: function() {
      var $select = $('#bulk-wish-time');

      if (!$select.length) return;

      var timeSlots = [
        { value: '0',  label: '午前中' },
        { value: '14', label: '14:00〜16:00' },
        { value: '16', label: '16:00〜18:00' },
        { value: '18', label: '18:00〜20:00' },
        { value: '19', label: '19:00〜21:00' }
      ];

      var optionsHtml = '<option value="">指定なし</option>';
      timeSlots.forEach(function(slot) {
        optionsHtml += '<option value="' + slot.value + '">' + slot.label + '</option>';
      });

      $select.html(optionsHtml);
    },

    /**
     * Validate a delivery date using cached options or API
     * For bulk operations, validates against all shipping types
     * @param {string} dateStr - Date in YYYY-MM-DD format
     * @param {function} callback - Callback function(result) where result = { valid: boolean, error: string|null, availableOptions: array }
     * @param {string} shippingType - Optional shipping type to validate for specific group
     */
    validateDeliveryDate: function(dateStr, callback, shippingType) {
      var self = this;
      var result = { valid: true, error: null, availableOptions: [] };

      // Empty date is valid (指定なし)
      if (!dateStr) {
        callback(result);
        return;
      }

      // Use cached deliveryOptions if available
      var cachedOptions = this.getCachedDeliveryOptions(shippingType);
      console.log('[validateDeliveryDate] dateStr=' + dateStr + ' shippingType=' + shippingType + ' cachedOptions.length=' + (cachedOptions ? cachedOptions.length : 0) + ' deliveryOptions=' + JSON.stringify(this.deliveryOptions));
      if (cachedOptions && cachedOptions.length > 0) {
        console.log('[validateDeliveryDate] using cache, dates=' + cachedOptions.map(function(o){ return o.deliveryDate; }).join(','));
        this.validateDateAgainstOptions(dateStr, cachedOptions, callback);
        return;
      }

      // Get variant IDs from cart items (optionally filtered by shipping type)
      var items = this.cartItems || [];
      if (shippingType) {
        items = items.filter(function(item) {
          return self.getItemShippingType(item) === shippingType;
        });
      }

      var variantIds = items.map(function(item) {
        return String(item.variant_id);
      });

      if (variantIds.length === 0) {
        result.valid = false;
        result.error = 'カート内の商品が見つかりません。';
        callback(result);
        return;
      }

      // Get postal code based on mode
      var postalCode = this.getRecipientPostalCode();

      if (!postalCode || postalCode.length !== 7) {
        result.valid = false;
        result.error = '有効な郵便番号が設定されていません。';
        callback(result);
        return;
      }

      // Fetch options from API
      this.fetchDeliveryOptions(postalCode, variantIds, function(options) {
        if (!options || options.length === 0) {
          result.valid = false;
          result.error = 'この地域への配送はできません。';
          callback(result);
          return;
        }

        // Cache the options for the shipping type
        if (shippingType) {
          self.deliveryOptions[shippingType] = options;
        }

        // Validate the date
        self.validateDateAgainstOptions(dateStr, options, callback);
      });
    },

    /**
     * Get cached delivery options for a shipping type or all types
     * @param {string} shippingType - Optional shipping type
     * @returns {array} - Delivery options
     */
    getCachedDeliveryOptions: function(shippingType) {
      if (!this.deliveryOptions) return [];

      if (shippingType) {
        return this.deliveryOptions[shippingType] || [];
      }

      // Return first available options (ambient > frozen > mail)
      return this.deliveryOptions.ambient ||
             this.deliveryOptions.frozen ||
             this.deliveryOptions.mail ||
             [];
    },

    /**
     * Validate a date against available options
     * @param {string} dateStr - Date in YYYY-MM-DD format
     * @param {array} options - Available delivery options
     * @param {function} callback - Callback function(result)
     */
    validateDateAgainstOptions: function(dateStr, options, callback) {
      var result = { valid: true, error: null, availableOptions: options };

      // Convert selected date to API format (YYYY/MM/DD)
      var apiDateFormat = dateStr.replace(/-/g, '/');

      // Check if selected date is in available options
      var isValidDate = options.some(function(opt) {
        return opt.deliveryDate === apiDateFormat;
      });

      if (!isValidDate) {
        // Format the selected date for error message
        var selectedDate = new Date(dateStr);
        var errorDateStr = (selectedDate.getMonth() + 1) + '月' + selectedDate.getDate() + '日';

        // Find first available date for suggestion
        var firstAvailable = options[0] ? options[0].deliveryDate : null;
        var suggestStr = '';
        if (firstAvailable) {
          var suggestDate = new Date(firstAvailable.replace(/\//g, '-'));
          suggestStr = (suggestDate.getMonth() + 1) + '月' + suggestDate.getDate() + '日';
        }

        result.valid = false;
        result.error = errorDateStr + 'はお届け不可です。' +
          (suggestStr ? suggestStr + '以降をお選びください。' : '別の日付をお選びください。');
      }

      callback(result);
    },

    /**
     * Show bulk delivery validation result modal
     * @param {object} validationResult - Result from validateDeliveryDate
     */
    showBulkDeliveryResultModal: function(validationResult) {
      var self = this;
      if (validationResult.valid) {
        // Show success modal
        var $successModal = $('#modalBulkDeliverySuccess');
        if (!$successModal.length) {
          console.warn('Modal #modalBulkDeliverySuccess not found');
          return;
        }

        var totalShipments = (validationResult.totalShipments != null) ? validationResult.totalShipments : 0;
        var successShipments = (validationResult.successShipments != null) ? validationResult.successShipments : 0;
        var totalAddresses = (validationResult.totalCount != null) ? validationResult.totalCount : (this.currentMode === 'single' ? 1 : this.multiRecipients.length);
        var successAddresses = (validationResult.successCount != null) ? validationResult.successCount : totalAddresses;
        $successModal.find('[data-pill-total]').text(totalShipments);
        $successModal.find('[data-pill-success]').text(successShipments);
        $successModal.find('[data-target-total]').text(totalShipments);
        $successModal.find('[data-success-count]').text(successShipments);

        // Show skipped (dateUnavailable) items if any
        var skippedErrors = validationResult.skippedErrors || [];
        var $skippedContainer = $successModal.find('[data-skipped-errors]');
        if ($skippedContainer.length) {
          if (skippedErrors.length > 0) {
            var skippedHtml = '';
            skippedErrors.forEach(function(e) {
              skippedHtml += '<li>' + self.escapeHtml(e.label) + '</li>';
            });
            $skippedContainer.find('[data-skipped-list]').html(skippedHtml);
            $skippedContainer.show();
          } else {
            $skippedContainer.hide();
          }
        }

        $('.p-modal.active').removeClass('active');
        $successModal.addClass('active');

        $successModal.find('.js-close').off('click').on('click', function(e) {
          e.preventDefault();
          $successModal.removeClass('active');
        });
      } else {
        // Date validation failed - all targets failed
        var shipments = this.groupItemsIntoShipments(this.cartItems || this.cart.items || []);
        var shippingTypeLabels = { ambient: '常温便', frozen: '冷凍便', mail: 'メール便' };
        var errors = [];
        var failedTargets = [];
        var totalShipments = 0;
        var uniqueRecipientIds = {};

        var self = this;
        if (this.currentMode === 'single') {
          shipments.forEach(function(shipment) {
            totalShipments++;
            uniqueRecipientIds['single'] = true;
            var sk = shipment.shipmentKey;
            var meta = (self.deliveryScheduleMeta && self.deliveryScheduleMeta[sk]) || null;
            if (!meta && self.deliveryOptionsByShipment && self.deliveryOptionsByShipment.hasOwnProperty(sk) && self.deliveryOptionsByShipment[sk].length === 0) {
              meta = { dateAvailable: false, timeAvailable: false };
            }
            if (meta && meta.dateAvailable === false && meta.timeAvailable === false) {
              errors.push({
                label: shippingTypeLabels[shipment.shippingType] || shipment.shippingType,
                reason: 'この便はお届け日時のご指定ができないためスキップされます',
                dateUnavailable: true
              });
              return;
            }
            errors.push({
              label: shippingTypeLabels[shipment.shippingType] || shipment.shippingType,
              reason: 'お届け希望日が設定できません'
            });
            failedTargets.push({
              recipientId: 'single',
              shipmentKey: sk,
              shippingType: shipment.shippingType,
              items: shipment.items || []
            });
          });
        } else {
          this.multiRecipients.forEach(function(r) {
            // Build per-recipient items (same as applyBulkDeliveryToShipments)
            var recipientItems = [];
            Object.keys(r.items || {}).forEach(function(itemKey) {
              if (r.items[itemKey] > 0) {
                var item = self.cartItems.find(function(i) { return i.key === itemKey; });
                if (item) recipientItems.push(item);
              }
            });
            var recipientShipments = self.groupItemsIntoShipments(recipientItems);
            recipientShipments.forEach(function(shipment) {
              totalShipments++;
              uniqueRecipientIds[r.id] = true;
              var sk = shipment.shipmentKey;
              var meta = (self.deliveryScheduleMetaMulti && self.deliveryScheduleMetaMulti[r.id] && self.deliveryScheduleMetaMulti[r.id][sk]) || null;
              if (!meta && self.deliveryOptionsMulti && self.deliveryOptionsMulti[r.id] && self.deliveryOptionsMulti[r.id].hasOwnProperty(sk) && self.deliveryOptionsMulti[r.id][sk].length === 0) {
                meta = { dateAvailable: false, timeAvailable: false };
              }
              var name = (r.lastName || '') + ' ' + (r.firstName || '');
              if (meta && meta.dateAvailable === false && meta.timeAvailable === false) {
                errors.push({
                  label: name + ' - ' + (shippingTypeLabels[shipment.shippingType] || shipment.shippingType),
                  reason: 'この便はお届け日時のご指定ができないためスキップされます',
                  dateUnavailable: true
                });
                return;
              }
              errors.push({
                label: name + ' - ' + (shippingTypeLabels[shipment.shippingType] || shipment.shippingType),
                reason: 'お届け希望日が設定できません'
              });
              failedTargets.push({
                recipientId: r.id,
                shipmentKey: sk,
                shippingType: shipment.shippingType,
                recipientName: name,
                items: shipment.items || []
              });
            });
          });
        }

        if (failedTargets.length === 0) {
          this.showBulkDeliveryResultModal({
            valid: true,
            totalShipments: totalShipments,
            successShipments: 0,
            totalCount: Object.keys(uniqueRecipientIds).length,
            successCount: 0
          });
        } else {
          this.showBulkPartialErrorModal(errors, 0, failedTargets);
        }
      }
    },

    /**
     * Show error modal with per-shipment blocks for re-selection
     * Each failed shipment gets its own date select + time chips
     * @param {array} errors - [{label: '常温便', reason: '...'}]
     * @param {number} successCount - Number of successfully set shipments
     * @param {array} failedTargets - [{recipientId, shipmentKey, shippingType, recipientName}]
     */
    showBulkPartialErrorModal: function(errors, successCount, failedTargets) {
      var self = this;
      var $modal = $('#modalBulkTimeError');
      if (!$modal.length) {
        console.warn('Modal #modalBulkTimeError not found');
        return;
      }

      var $container = $modal.find('[data-bulk-error-items]');
      $container.empty();

      var errorSvg = '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">' +
        '<path fill-rule="evenodd" clip-rule="evenodd" ' +
        'd="M1.5 9C1.5 4.86075 4.86 1.5 9 1.5C13.1475 1.5 16.5 4.86075 16.5 9C16.5 13.1407 13.1475 16.5 9 16.5C4.86 16.5 1.5 13.1407 1.5 9ZM8.34 6.1575C8.34 5.79825 8.64 5.4975 9 5.4975C9.36 5.4975 9.6525 5.79825 9.6525 6.1575V9.4725C9.6525 9.83325 9.36 10.125 9 10.125C8.64 10.125 8.34 9.83325 8.34 9.4725V6.1575ZM9.0075 12.5107C8.64 12.5107 8.3475 12.2107 8.3475 11.8507C8.3475 11.4907 8.64 11.1982 9 11.1982C9.3675 11.1982 9.66 11.4907 9.66 11.8507C9.66 12.2107 9.3675 12.5107 9.0075 12.5107Z" ' +
        'fill="#F75555" /></svg>';

      var weekdays = ['日', '月', '火', '水', '木', '金', '土'];

      var shippingBadges = {
        ambient: { label: '常温便', icon: window.shippingAssets ? window.shippingAssets.iconTruck : '', cssClass: 'ship-badge--ambient' },
        frozen: { label: '冷凍便', icon: window.shippingAssets ? window.shippingAssets.iconFrozen : '', cssClass: 'ship-badge--frozen' },
        mail: { label: 'メール便', icon: window.shippingAssets ? window.shippingAssets.iconMail : '', cssClass: 'ship-badge--mail' }
      };

      // Group failedTargets by recipientId preserving UI order
      var isSingleMode = (self.currentMode === 'single');
      var groupMap = {};
      var groupOrder = [];
      failedTargets.forEach(function(target, idx) {
        var rid = target.recipientId;
        if (!groupMap[rid]) { groupMap[rid] = []; groupOrder.push(rid); }
        groupMap[rid].push({ target: target, index: idx });
      });
      if (!isSingleMode) {
        var recipientOrder = self.multiRecipients.map(function(r) { return r.id; });
        groupOrder.sort(function(a, b) {
          return recipientOrder.indexOf(a) - recipientOrder.indexOf(b);
        });
      }

      // Render per-address group, then per-shipment block within each group
      groupOrder.forEach(function(rid) {
        var group = groupMap[rid];
        var $groupContainer;

        if (isSingleMode || rid === 'single') {
          // Single mode: render blocks directly into container (no address wrapper)
          $groupContainer = $container;
        } else {
          // Multi mode: render address header once, then shipment blocks inside
          var addrRecipient = null;
          for (var ri = 0; ri < self.multiRecipients.length; ri++) {
            if (self.multiRecipients[ri].id === rid) { addrRecipient = self.multiRecipients[ri]; break; }
          }
          var addrHtml = '<div class="p-bulk-error-address-group">';
          if (addrRecipient) {
            var addrRecipientAddress2 = self.getAddress2WithCompany(addrRecipient);
            var rAddr = '〒' + self.formatZipDisplay(addrRecipient.zip || '') + ' ' + (addrRecipient.prefecture || '') + ' ' + (addrRecipient.city || '') + ' ' + (addrRecipient.address1 || '');
            if (addrRecipientAddress2) rAddr += ' ' + addrRecipientAddress2;
            var rName = ((addrRecipient.lastName || addrRecipient.firstName) ? ((addrRecipient.lastName || '') + ' ' + (addrRecipient.firstName || '')).trim() : (addrRecipient.company || '')) + ' 様';
            addrHtml += '<div class="p-delivery-addr"><p class="p-product-small__address delivery">' + self.escapeHtml(rAddr.trim()) + '</p>';
            addrHtml += '<p class="p-product-small__recipient p-delivery-addr__text">' + self.escapeHtml(rName.trim()) + '</p></div>';
          }
          addrHtml += '</div>';
          $container.append(addrHtml);
          $groupContainer = $container.children().last();
        }

        group.forEach(function(entry) {
          var target = entry.target;
          var index = entry.index;
          var error = errors[index];
          var items = target.items || [];

          // Get API options for this specific shipment
          var apiOptions;
          if (target.recipientId === 'single' || self.currentMode === 'single') {
            apiOptions = (self.deliveryOptionsByShipment && self.deliveryOptionsByShipment[target.shipmentKey]) || [];
          } else {
            apiOptions = (self.deliveryOptionsMulti && self.deliveryOptionsMulti[target.recipientId] && self.deliveryOptionsMulti[target.recipientId][target.shipmentKey]) || [];
          }

          // Get scheduleMeta to check required flag
          var meta;
          if (target.recipientId === 'single' || self.currentMode === 'single') {
            meta = (self.deliveryScheduleMeta && self.deliveryScheduleMeta[target.shipmentKey]) || null;
          } else {
            meta = (self.deliveryScheduleMetaMulti && self.deliveryScheduleMetaMulti[target.recipientId] && self.deliveryScheduleMetaMulti[target.recipientId][target.shipmentKey]) || null;
          }
          var dateRequired = meta && meta.dateRequired === true;
          var timeRequired = meta && meta.timeRequired === true;

          // Build instruction text for second alert using earliest available API date
          var instructionMsg;
          var singleDateOption = apiOptions.length === 1;
          if (apiOptions.length > 0) {
            var firstDate = new Date(apiOptions[0].deliveryDate.replace(/\//g, '-'));
            var firstDateStr = (firstDate.getMonth() + 1) + '月' + firstDate.getDate() + '日';
            instructionMsg = singleDateOption
              ? firstDateStr + 'にお届け日を変更します'
              : firstDateStr + '以降の日付を選択ください';
          } else {
            instructionMsg = 'お届け希望日時を再度選択してください';
          }

          var blockHtml = '';
          blockHtml += '<div class="p-bulk-error-block" data-error-block-index="' + index + '">';

          // Shipping type badge
          var badge = shippingBadges[target.shippingType];
          if (badge) {
            blockHtml += '<div class="ship-badge-list">';
            blockHtml += '<div class="ship-badge ' + badge.cssClass + '">';
            blockHtml += '<span class="ship-badge__icon" aria-hidden="true"><img src="' + badge.icon + '" alt=""></span>';
            blockHtml += '<span class="ship-badge__text">' + badge.label + '</span>';
            blockHtml += '</div>';
            blockHtml += '</div>';
          }

          // Product info
          if (items.length > 0) {
            items.forEach(function(item) {
              blockHtml += '<div class="p-product-small">';
              blockHtml += '<img class="p-product-small__thumb" src="' + (item.image || window._defaultProductImage) + '" alt="">';
              blockHtml += '<div class="p-product-small__info"><p>' + self.escapeHtml(item.product_title || item.title || '') + '</p></div>';
              blockHtml += '</div>';
            });
          } else {
            blockHtml += '<div class="p-product-small">';
            blockHtml += '<div class="p-product-small__info"><p>' + self.escapeHtml(error.label) + '</p></div>';
            blockHtml += '</div>';
          }

          // First alert: error reason (what happened)
          blockHtml += '<div class="p-alert p-alert--error p-alert--small">';
          blockHtml += '<div class="p-alert__icon"><i class="c-ico-info-01">' + errorSvg + '</i></div>';
          blockHtml += '<div class="p-alert__info"><p>' + self.escapeHtml(error.reason) + '</p></div>';
          blockHtml += '</div>';

          if (!error.dateUnavailable) {
            // Second alert: instruction (what to do next)
            blockHtml += '<div class="p-alert p-alert--error p-alert--small">';
            blockHtml += '<div class="p-alert__icon"><i class="c-ico-info-01">' + errorSvg + '</i></div>';
            blockHtml += '<div class="p-alert__info"><p>' + instructionMsg + '</p></div>';
            blockHtml += '</div>';

            // Date select + time chips wrap
            blockHtml += '<div class="p-bulk-modal__wrap">';

            // Date select
            blockHtml += '<div class="p-bulk-modal__field">';
            blockHtml += '<label class="shiptime__label">お届け希望日</label>';
            blockHtml += '<select class="c-form-select js-errorDateSelect" data-error-index="' + index + '"' + (singleDateOption ? ' disabled' : '') + '></select>';
            blockHtml += '</div>';

            // Time chips
            blockHtml += '<div class="p-bulk-modal__field">';
            blockHtml += '<label class="p-bulk-modal__label">配送希望時間</label>';
            blockHtml += '<div class="p-bulk-modal__chips js-errorTimeChips" data-error-index="' + index + '"></div>';
            blockHtml += '</div>';

            blockHtml += '</div>'; // .p-bulk-modal__wrap
          }

          blockHtml += '</div>'; // .p-bulk-error-block

          $groupContainer.append(blockHtml);

          if (error.dateUnavailable) return;

          // Populate date select for this block (find recursively within $container)
          var $block = $container.find('[data-error-block-index="' + index + '"]');
          var $dateSelect = $block.find('.js-errorDateSelect');
          var $timeChips = $block.find('.js-errorTimeChips');

          if (apiOptions.length > 0) {
            var optionsHtml = dateRequired ? '<option value="">指定してください</option>' : '<option value="">指定なし</option>';
            apiOptions.forEach(function(opt) {
              var dateStr = opt.deliveryDate;
              var value = dateStr.replace(/\//g, '-');
              var date = new Date(value);
              var displayText = (date.getMonth() + 1) + '月' + date.getDate() + '日(' + weekdays[date.getDay()] + ')';
              optionsHtml += '<option value="' + value + '">' + displayText + '</option>';
            });
            $dateSelect.html(optionsHtml);
          } else {
            self.populateBulkDateSelect($dateSelect);
          }

          // Populate time chips for the initially selected date
          var initialDate = $dateSelect.val();
          self.populateErrorModalTimeChipsForBlock($timeChips, apiOptions, initialDate, timeRequired);

          // Update time chips when date changes
          $dateSelect.off('change.errorModal').on('change.errorModal', function() {
            $(this).toggleClass('is-error', !$(this).val());
            $timeChips.find('.js-errorTimeChip').removeClass('is-active');
            self.populateErrorModalTimeChipsForBlock($timeChips, apiOptions, $(this).val(), timeRequired);
          });
        });
      });

      $('.p-modal.active').removeClass('active');
      $modal.addClass('active');

      // Close handler
      $modal.find('.js-close').off('click').on('click', function(e) {
        e.preventDefault();
        $modal.removeClass('active');
      });

      // Save handler - use event delegation for reliability
      $modal.off('click.errorSave').on('click.errorSave', '.js-saveErrorBulkTime', function() {
        console.log('[errorModal save] clicked, failedTargets.length=' + failedTargets.length);
        var targetSettings = [];
        failedTargets.forEach(function(target, index) {
          var $block = $container.find('[data-error-block-index="' + index + '"]');
          var date = $block.find('.js-errorDateSelect').val();
          var $activeChip = $block.find('.js-errorTimeChips .js-errorTimeChip.is-active');
          var time = String($activeChip.attr('data-value') || '');
          console.log('[errorModal save] index=' + index + ' sk=' + target.shipmentKey + ' date=' + date + ' time=' + time + ' activeChip=' + $activeChip.length + ' activeChipVal=' + $activeChip.attr('data-value'));
          targetSettings.push({ target: target, date: date, time: time });
        });

        $modal.removeClass('active');
        self.retryBulkDeliveryForTargets(targetSettings);
      });
    },

    /**
     * Retry bulk delivery for specific failed targets with per-target date/time
     * @param {array} targetSettings - [{target: {recipientId, shipmentKey, shippingType, recipientName}, date: '', time: ''}]
     */
    retryBulkDeliveryForTargets: function(targetSettings) {
      var self = this;
      var shippingTypeLabels = { ambient: '常温便', frozen: '冷凍便', mail: 'メール便' };
      var errors = [];
      var successTargets = [];
      var failedTargets = [];

      targetSettings.forEach(function(setting) {
        var target = setting.target;
        var date = setting.date;
        var time = setting.time;
        var rid = target.recipientId;
        var sk = target.shipmentKey;
        var st = target.shippingType;

        var $dateSelect = $('[data-recipient-id="' + rid + '"][data-shipment-key="' + sk + '"][data-delivery-date]');
        if (!$dateSelect.length) {
          $dateSelect = $('[data-recipient-id="' + rid + '"][data-shipping-type="' + st + '"][data-delivery-date]');
        }
        var $timeSelect = $('[data-recipient-id="' + rid + '"][data-shipment-key="' + sk + '"][data-delivery-time]');
        if (!$timeSelect.length) {
          $timeSelect = $('[data-recipient-id="' + rid + '"][data-shipping-type="' + st + '"][data-delivery-time]');
        }

        if (!$dateSelect.length && !$timeSelect.length) return;

        var oldDate = $dateSelect.length ? $dateSelect.val() : '';
        var oldTime = $timeSelect.length ? $timeSelect.val() : '';

        var dateFailed = false;
        var timeFailed = false;

        if ($dateSelect.length && date) {
          $dateSelect.val(date);
          if ($dateSelect.val() !== date) dateFailed = true;
        }

        self.updateTimeSlotsForDate(st, date || '', rid, sk);

        if ($timeSelect.length && time) {
          $timeSelect.val(time);
          if ($timeSelect.val() !== time) timeFailed = true;
        }

        if (dateFailed || timeFailed) {
          if ($dateSelect.length) $dateSelect.val(oldDate);
          self.updateTimeSlotsForDate(st, oldDate || '', rid, sk);
          if ($timeSelect.length) $timeSelect.val(oldTime);

          var label = shippingTypeLabels[st] || st;
          var reason = dateFailed ? 'お届け希望日が設定できません' : 'お届け希望時間が設定できません';
          if (target.recipientName) label = target.recipientName + ' 様 - ' + label;
          errors.push({ label: label, reason: reason });
          failedTargets.push(target);
        } else {
          self.saveInlineDeliverySettings(sk, rid, date, time);
          successTargets.push(target);
        }
      });

      this.saveData();

      // Shipment-level counts (for pill)
      var totalShipments = targetSettings.length;
      var successShipments = successTargets.length;

      // Address-level counts (for box)
      var allRecipientIds = {};
      targetSettings.forEach(function(s) { allRecipientIds[s.target.recipientId] = true; });
      var totalAddresses = Object.keys(allRecipientIds).length;

      var failedRecipientIds = {};
      failedTargets.forEach(function(t) { failedRecipientIds[t.recipientId] = true; });
      var successRecipientIds = {};
      successTargets.forEach(function(t) {
        if (!failedRecipientIds[t.recipientId]) successRecipientIds[t.recipientId] = true;
      });
      var successAddresses = Object.keys(successRecipientIds).length;

      if (errors.length > 0) {
        this.showBulkPartialErrorModal(errors, successShipments, failedTargets);
      } else {
        this.showBulkDeliveryResultModal({
          valid: true,
          totalShipments: totalShipments,
          successShipments: successShipments,
          totalCount: totalAddresses,
          successCount: successAddresses
        });
      }
    },

    /**
     * Populate time chips for a specific error block container
     * Filters by available time slots for the selected date from API options
     * @param {jQuery} $container - The .js-errorTimeChips element for this block
     * @param {array} apiOptions - Delivery options from API for this shipment
     * @param {string} selectedDate - Currently selected date (YYYY-MM-DD)
     */
    populateErrorModalTimeChipsForBlock: function($container, apiOptions, selectedDate, timeRequired) {
      var self = this;
      if (!$container.length) return;

      // Build set of allowed chip values from API available slots for the selected date
      var allowedValues = null; // null = no API restriction (all slots enabled)
      if (apiOptions && apiOptions.length > 0 && selectedDate) {
        var apiDateFormat = selectedDate.replace(/-/g, '/');
        for (var i = 0; i < apiOptions.length; i++) {
          if (apiOptions[i].deliveryDate === apiDateFormat) {
            var slots = apiOptions[i].availableTimeSlots;
            // Only restrict if slots is a non-empty array; empty/null = no restriction
            if (slots && slots.length > 0) {
              allowedValues = {};
              slots.forEach(function(slotValue) {
                var slotInfo = self.timeSlotLabels[slotValue];
                if (slotInfo) allowedValues[slotInfo.value] = true;
              });
            }
            break;
          }
        }
      }

      var timeSlots = [
        { value: '0',  label: '午前中' },
        { value: '14', label: '14:00〜16:00' },
        { value: '16', label: '16:00〜18:00' },
        { value: '18', label: '18:00〜20:00' },
        { value: '19', label: '19:00〜21:00' }
      ];

      var chipsHtml = '';
      // If time is optional, prepend a "指定なし" chip
      if (!timeRequired) {
        chipsHtml += '<button type="button" class="p-time-chip js-errorTimeChip" data-value="">指定なし</button>';
      }
      timeSlots.forEach(function(slot) {
        var availableForDate = !allowedValues || allowedValues[slot.value];
        chipsHtml += '<button type="button" class="p-time-chip js-errorTimeChip' +
          (availableForDate ? '' : ' is-disabled') + '"' +
          (availableForDate ? '' : ' disabled') +
          ' data-value="' + slot.value + '">' + slot.label + '</button>';
      });

      $container.html(chipsHtml);
      // If time is required and nothing selected yet, mark container as error
      if (timeRequired) {
        $container.addClass('is-error');
      }
      $container.find('.js-errorTimeChip:not(.is-disabled)').off('click').on('click', function() {
        $container.find('.js-errorTimeChip').removeClass('is-active');
        $(this).addClass('is-active');
        $container.removeClass('is-error');
      });
    },

    /**
     * Fetch shipping rates from API
     * @param {array} lineItems - Array of {variantId, quantity}
     * @param {string} zip - Postal code (7 digits without hyphen)
     * @param {array} customerTags - Customer tags array
     * @param {boolean} freeShipping - Whether free shipping applies
     * @param {function} callback - Callback function(shippingRate)
     */
    fetchShippingRates: function(lineItems, zip, customerTags, freeShipping, callback) {
      var apiEndpoint = '/apps/ping/shipping/rates/calculate';

      $.ajax({
        url: apiEndpoint,
        method: 'POST',
        contentType: 'application/json',
        data: JSON.stringify({
          lineItems: lineItems,
          shippingAddress: { zip: zip },
          customer: { tags: customerTags || [] },
          freeShipping: freeShipping || false
        }),
        success: function(data) {
          // Return the shipping rate from the API response
          var rate = 0;
          var rateDetails = []; // Store full details for checkout

          if (data && data.availableShippingRate && data.availableShippingRate.length > 0) {
            // New API format: availableShippingRate[].amount with fee breakdown
            data.availableShippingRate.forEach(function(sr) {
              rate += parseFloat(sr.amount) || 0;
              rateDetails.push({
                title: sr.title || '送料',
                amount: parseFloat(sr.amount) || 0,
                prefectureFee: parseFloat(sr.prefectureFee) || 0,
                coolDeliveryFee: parseFloat(sr.coolDeliveryFee) || 0
              });
            });
          } else if (data && data.shippingRates && data.shippingRates.length > 0) {
            // Legacy format: shippingRates[].price
            data.shippingRates.forEach(function(sr) {
              rate += parseFloat(sr.price) || 0;
              rateDetails.push({
                title: sr.title || '送料',
                amount: parseFloat(sr.price) || 0,
                prefectureFee: parseFloat(sr.price) || 0,
                coolDeliveryFee: 0
              });
            });
          } else if (data && typeof data.totalShippingCost !== 'undefined') {
            rate = parseFloat(data.totalShippingCost) || 0;
            rateDetails.push({
              title: '送料',
              amount: rate,
              prefectureFee: rate,
              coolDeliveryFee: 0
            });
          }

          callback(rate, rateDetails);
        },
        error: function() {
          console.error('Failed to fetch shipping rates');
          callback(0, []);
        }
      });
    },

    /**
     * Calculate shipping rates for all recipients (based on shipments)
     * @param {function} callback - Callback function(totalShippingCost)
     */
    calculateShippingRates: function(callback) {
      var self = this;
      var customerTags = (window.shippingCustomer && window.shippingCustomer.tags) || [];
      var buildShippingHintContext = function(recipient, shipment) {
        var items = (shipment && shipment.items) || [];
        var itemKeys = [];
        var productIds = [];

        items.forEach(function(item) {
          if (!item) return;
          if (item.key !== undefined && item.key !== null) {
            itemKeys.push(String(item.key));
          }
          if (item.product_id !== undefined && item.product_id !== null && item.product_id !== '') {
            productIds.push(String(item.product_id));
          }
        });

        return {
          shippingType: shipment && shipment.shippingType ? String(shipment.shippingType) : '',
          recipientPrefecture: recipient && recipient.prefecture ? String(recipient.prefecture) : '',
          recipientPrefectureCode: recipient && recipient.prefectureCode ? String(recipient.prefectureCode) : '',
          itemKeys: itemKeys,
          productIds: productIds
        };
      };
      var writeInterofficeShipmentsToCache = function(cachePrefix, shipments, recipient) {
        (shipments || []).forEach(function(shipment) {
          if (!shipment || !shipment.items || shipment.items.length === 0) return;
          var shipmentSubtotal = shipment.items.reduce(function(sum, item) { return sum + (item.line_price / 100); }, 0);
          var hintContext = buildShippingHintContext(recipient, shipment);
          self.shippingRatesCache[cachePrefix + shipment.shipmentKey] = {
            rate: 0,
            details: buildInterofficeRateDetails(),
            subtotal: shipmentSubtotal,
            shippingTitle: INTEROFFICE_SHIPPING_TITLE,
            isInteroffice: true,
            shippingType: hintContext.shippingType,
            recipientPrefecture: hintContext.recipientPrefecture,
            recipientPrefectureCode: hintContext.recipientPrefectureCode,
            itemKeys: hintContext.itemKeys,
            productIds: hintContext.productIds
          };
        });
      };

      // No recipients or no cart items, return 0
      var recipients = this.getRecipients();
      if (!recipients || recipients.length === 0 || !this.cart || !this.cart.items || this.cart.items.length === 0) {
        callback(0, { prefectureFee: 0, coolDeliveryFee: 0, coolDeliveryCount: 0 });
        return;
      }

      // Initialize shipping API cache (keyed by zip + lineItems to avoid duplicate calls)
      if (!this._shippingApiCache) {
        this._shippingApiCache = {};
      }

      if (this.currentMode === 'single') {
        // Single mode: calculate shipping per shipment
        var recipient = this.singleRecipient;
        if (!recipient || !recipient.zip) {
          callback(0, { prefectureFee: 0, coolDeliveryFee: 0, coolDeliveryCount: 0 });
          return;
        }

        // Group items into shipments (respects mixed_loading_disable flag)
        var shipments = this.groupItemsIntoShipments(this.cart.items);

        // Interoffice shipment: skip shipping API and force 0 yen
        if (this.isInterofficeRecipient(recipient)) {
          self.shippingRatesCache = {};
          writeInterofficeShipmentsToCache('single_', shipments, recipient);
          callback(0, { prefectureFee: 0, coolDeliveryFee: 0, coolDeliveryCount: 0 });
          return;
        }

        // Fetch rates for each shipment separately
        var totalShipping = 0;
        var totalPrefectureFee = 0;
        var totalCoolDeliveryFee = 0;
        var coolDeliveryCount = 0;
        var pendingRequests = 0;
        var completedRequests = 0;

        // Clear shipment-level cache (rebuilt from API cache below)
        self.shippingRatesCache = {};

        shipments.forEach(function(shipment) {
          if (shipment.items.length === 0) return;

          pendingRequests++;

          var lineItems = shipment.items.map(function(item) {
            var li = {
              variantId: 'gid://shopify/ProductVariant/' + item.variant_id,
              quantity: item.quantity
            };
            if (item.line_price != null) li.linePrice = item.line_price;
            return li;
          });

          // Build API-level cache key from actual input
          var apiCacheKey = recipient.zip + '_' + lineItems.map(function(li) { return li.variantId + ':' + li.quantity; }).sort().join(',');

          if (self._shippingApiCache[apiCacheKey]) {
            // Use cached API response
            var cached = self._shippingApiCache[apiCacheKey];
            totalShipping += cached.rate;
            (cached.details || []).forEach(function(d) {
              totalPrefectureFee += d.prefectureFee;
              totalCoolDeliveryFee += d.coolDeliveryFee;
              if (d.coolDeliveryFee > 0) coolDeliveryCount++;
            });
            var shipmentSubtotal = shipment.items.reduce(function(sum, item) { return sum + (item.line_price / 100); }, 0);
            var cachedHintContext = buildShippingHintContext(recipient, shipment);
            self.shippingRatesCache['single_' + shipment.shipmentKey] = {
              rate: cached.rate,
              details: cached.details,
              subtotal: shipmentSubtotal,
              shippingType: cachedHintContext.shippingType,
              recipientPrefecture: cachedHintContext.recipientPrefecture,
              recipientPrefectureCode: cachedHintContext.recipientPrefectureCode,
              itemKeys: cachedHintContext.itemKeys,
              productIds: cachedHintContext.productIds
            };
            completedRequests++;
            if (completedRequests === pendingRequests) {
              callback(totalShipping, {
                prefectureFee: totalPrefectureFee,
                coolDeliveryFee: totalCoolDeliveryFee,
                coolDeliveryCount: coolDeliveryCount
              });
            }
            return;
          }

          self.fetchShippingRates(lineItems, recipient.zip, customerTags, false, function(rate, rateDetails) {
            // Store in API cache
            self._shippingApiCache[apiCacheKey] = { rate: rate, details: rateDetails || [] };

            totalShipping += rate;

            // Accumulate fee breakdown from API response
            (rateDetails || []).forEach(function(d) {
              totalPrefectureFee += d.prefectureFee;
              totalCoolDeliveryFee += d.coolDeliveryFee;
              if (d.coolDeliveryFee > 0) coolDeliveryCount++;
            });

            // Store rate for this shipment
            var shipmentSubtotal = shipment.items.reduce(function(sum, item) { return sum + (item.line_price / 100); }, 0);
            var hintContext = buildShippingHintContext(recipient, shipment);
            self.shippingRatesCache['single_' + shipment.shipmentKey] = {
              rate: rate,
              details: rateDetails || [],
              subtotal: shipmentSubtotal,
              shippingType: hintContext.shippingType,
              recipientPrefecture: hintContext.recipientPrefecture,
              recipientPrefectureCode: hintContext.recipientPrefectureCode,
              itemKeys: hintContext.itemKeys,
              productIds: hintContext.productIds
            };

            completedRequests++;
            if (completedRequests === pendingRequests) {
              callback(totalShipping, {
                prefectureFee: totalPrefectureFee,
                coolDeliveryFee: totalCoolDeliveryFee,
                coolDeliveryCount: coolDeliveryCount
              });
            }
          });
        });

        // If no shipments have items, callback immediately
        if (pendingRequests === 0) {
          callback(0, { prefectureFee: 0, coolDeliveryFee: 0, coolDeliveryCount: 0 });
        }
      } else {
        // Multi mode: calculate shipping for each recipient separately, grouped by shipment
        var totalShipping = 0;
        var totalPrefectureFee = 0;
        var totalCoolDeliveryFee = 0;
        var coolDeliveryCount = 0;
        var pendingRequests = 0;
        var completedRequests = 0;

        // Clear shipment-level cache (rebuilt from API cache below)
        self.shippingRatesCache = {};

        this.multiRecipients.forEach(function(recipient) {
          if (!recipient || !recipient.zip || !recipient.items) {
            return;
          }

          // Build items array for this recipient with quantities
          var recipientItems = [];
          Object.keys(recipient.items).forEach(function(itemKey) {
            var qty = recipient.items[itemKey];
            if (qty > 0) {
              var cartItem = self.cart.items.find(function(i) { return i.key === itemKey; });
              if (cartItem) {
                // Create a copy with the recipient's quantity
                recipientItems.push({
                  key: cartItem.key,
                  variant_id: cartItem.variant_id,
                  product_id: cartItem.product_id,
                  quantity: qty,
                  price: cartItem.price,
                  line_price: cartItem.price * qty,
                  product_type: cartItem.product_type,
                  tags: cartItem.tags
                });
              }
            }
          });

          if (recipientItems.length === 0) return;

          // Group items into shipments (respects mixed_loading_disable flag)
          var shipments = self.groupItemsIntoShipments(recipientItems);

          // Interoffice shipment: skip shipping API and force 0 yen
          if (self.isInterofficeRecipient(recipient)) {
            writeInterofficeShipmentsToCache(recipient.id + '_', shipments, recipient);
            return;
          }

          // Fetch rates for each shipment separately
          shipments.forEach(function(shipment) {
            if (shipment.items.length === 0) return;

            pendingRequests++;

            var lineItems = shipment.items.map(function(item) {
              var li = {
                variantId: 'gid://shopify/ProductVariant/' + item.variant_id,
                quantity: item.quantity
              };
              if (item.line_price != null) li.linePrice = item.line_price;
              return li;
            });

            // Build API-level cache key from actual input
            var apiCacheKey = recipient.zip + '_' + lineItems.map(function(li) { return li.variantId + ':' + li.quantity; }).sort().join(',');

            if (self._shippingApiCache[apiCacheKey]) {
              // Use cached API response
              var cached = self._shippingApiCache[apiCacheKey];
              totalShipping += cached.rate;
              (cached.details || []).forEach(function(d) {
                totalPrefectureFee += d.prefectureFee;
                totalCoolDeliveryFee += d.coolDeliveryFee;
                if (d.coolDeliveryFee > 0) coolDeliveryCount++;
              });
              var shipmentSubtotal = shipment.items.reduce(function(sum, item) { return sum + (item.line_price / 100); }, 0);
              var cachedHintContext = buildShippingHintContext(recipient, shipment);
              self.shippingRatesCache[recipient.id + '_' + shipment.shipmentKey] = {
                rate: cached.rate,
                details: cached.details,
                subtotal: shipmentSubtotal,
                shippingType: cachedHintContext.shippingType,
                recipientPrefecture: cachedHintContext.recipientPrefecture,
                recipientPrefectureCode: cachedHintContext.recipientPrefectureCode,
                itemKeys: cachedHintContext.itemKeys,
                productIds: cachedHintContext.productIds
              };
              completedRequests++;
              if (completedRequests === pendingRequests) {
                callback(totalShipping, {
                  prefectureFee: totalPrefectureFee,
                  coolDeliveryFee: totalCoolDeliveryFee,
                  coolDeliveryCount: coolDeliveryCount
                });
              }
              return;
            }

            self.fetchShippingRates(lineItems, recipient.zip, customerTags, false, function(rate, rateDetails) {
              // Store in API cache
              self._shippingApiCache[apiCacheKey] = { rate: rate, details: rateDetails || [] };

              totalShipping += rate;

              // Accumulate fee breakdown from API response
              (rateDetails || []).forEach(function(d) {
                totalPrefectureFee += d.prefectureFee;
                totalCoolDeliveryFee += d.coolDeliveryFee;
                if (d.coolDeliveryFee > 0) coolDeliveryCount++;
              });

              // Store rate for this recipient's shipment
              var shipmentSubtotal = shipment.items.reduce(function(sum, item) { return sum + (item.line_price / 100); }, 0);
              var hintContext = buildShippingHintContext(recipient, shipment);
              self.shippingRatesCache[recipient.id + '_' + shipment.shipmentKey] = {
                rate: rate,
                details: rateDetails || [],
                subtotal: shipmentSubtotal,
                shippingType: hintContext.shippingType,
                recipientPrefecture: hintContext.recipientPrefecture,
                recipientPrefectureCode: hintContext.recipientPrefectureCode,
                itemKeys: hintContext.itemKeys,
                productIds: hintContext.productIds
              };

              completedRequests++;

              if (completedRequests === pendingRequests) {
                callback(totalShipping, {
                  prefectureFee: totalPrefectureFee,
                  coolDeliveryFee: totalCoolDeliveryFee,
                  coolDeliveryCount: coolDeliveryCount
                });
              }
            });
          });
        });

        // If no pending requests, callback immediately
        if (pendingRequests === 0) {
          callback(0, { prefectureFee: 0, coolDeliveryFee: 0, coolDeliveryCount: 0 });
        }
      }
    },

    /**
     * Update order summary
     */
    updateSummary: function() {
      var self = this;
      var subtotal = 0;
      var bagTotal = 0;

      // Keep bulk action buttons in sync with current mode/data.
      this.updateBulkActionButtonState();

      if (this.cart && this.cart.items) {
        this.cart.items.forEach(function(item) {
          subtotal += item.line_price / 100;
        });
      }

      bagTotal = this.calculateTotalBagCost();

      // Subtotal includes bag cost; bag row removed from summary
      $('[data-subtotal]').text(formatMoneyYen(subtotal + bagTotal));

      // Sync checkout button state after every summary update
      this.updateCheckoutButtonState();

      // Show loading state for shipping
      $('[data-shipping-total]').text('計算中...');

      // Calculate shipping rates asynchronously (includes fee breakdown from API)
      this.calculateShippingRates(function(shippingTotal, feeBreakdown) {
        // 商品・手数料合計（商品代 + 袋代）
        $('[data-subtotal]').text(formatMoneyYen(subtotal + bagTotal));

        // 配送料（クール便手数料含む）
        $('[data-shipping-total]').text(formatMoneyYen(shippingTotal));

        // 送料無料プロモ表示: クール便手数料を除いた純粋な送料が0なら非表示
        var coolDeliveryFee = (feeBreakdown && feeBreakdown.coolDeliveryFee) || 0;
        var shippingOnly = shippingTotal - coolDeliveryFee;
        if (shippingOnly > 0) {
          $('[data-shipping-promo], [data-shipping-promo-note]').show();
        } else {
          $('[data-shipping-promo], [data-shipping-promo-note]').hide();
        }

        var total = subtotal + bagTotal + shippingTotal;
        $('[data-total]').text(formatMoneyYen(total));
      });
    },

    /**
     * Count distinct shipping types currently present in cart items.
     * ambient / frozen / mail
     */
    getDistinctShippingTypeCount: function() {
      var typeMap = {};
      (this.cartItems || []).forEach(function(item) {
        if (!item) return;
        var shippingType = this.getItemShippingType(item);
        if (shippingType) typeMap[shippingType] = true;
      }, this);
      return Object.keys(typeMap).length;
    },

    /**
     * Bulk settings are enabled only in multi mode and only when:
     * - multiple temperature zones exist, or
     * - multiple recipients exist.
     */
    getActiveShipmentCount: function() {
      var self = this;
      var cartItems = this.cartItems || [];
      var count = 0;
      (this.multiRecipients || []).forEach(function(r) {
        var recipientItems = [];
        Object.keys(r.items || {}).forEach(function(itemKey) {
          if ((r.items[itemKey] || 0) > 0) {
            var item = cartItems.find(function(i) { return i.key === itemKey; });
            if (item) recipientItems.push(item);
          }
        });
        if (recipientItems.length > 0) {
          count += self.groupItemsIntoShipments(recipientItems).length;
        }
      });
      return count;
    },

    getTotalAssignedItemCount: function() {
      var total = 0;
      (this.multiRecipients || []).forEach(function(r) {
        Object.keys(r.items || {}).forEach(function(key) {
          if ((r.items[key] || 0) > 0) total++;
        });
      });
      return total;
    },

    canUseBulkActions: function() {
      return this.currentMode === 'multi';
    },

    /**
     * Toggle "まとめて設定" buttons active/disabled state.
     * Shiptime button: enabled when there is at least 1 active shipment.
     * Wrapping button: enabled when at least 1 item is assigned to a recipient.
     */
    updateBulkActionButtonState: function() {
      if (!this.canUseBulkActions()) return;
      var shiptimeEnabled = this.getActiveShipmentCount() > 0;
      var wrappingEnabled = this.getTotalAssignedItemCount() > 0;

      var $shiptime = $('[data-id="modalBulkShiptime"]');
      $shiptime
        .prop('disabled', !shiptimeEnabled)
        .attr('aria-disabled', shiptimeEnabled ? 'false' : 'true')
        .toggleClass('is-disabled', !shiptimeEnabled);

      var $wrapping = $('[data-id="modalBulkWrapping"]');
      $wrapping
        .prop('disabled', !wrappingEnabled)
        .attr('aria-disabled', wrappingEnabled ? 'false' : 'true')
        .toggleClass('is-disabled', !wrappingEnabled);
    },

    normalizePrefectureName: function(prefecture) {
      return String(prefecture || '').replace(/\s+/g, '');
    },

    normalizeCustomerTypeName: function(customerType) {
      var raw = String(customerType || '').replace(/\s+/g, '');
      if (!raw) return '';
      // 社売系（旧タグ名・旧正規名も含む）
      if (raw === '社売' || raw.indexOf('社員売顧客') !== -1 || raw === '社売顧客') return '社売';
      // 外商顧客系
      if (raw.indexOf('旧外商顧客') !== -1 || raw === '外商顧客') return '外商顧客';
      // 法人顧客
      if (raw === '法人顧客') return '法人顧客';
      // 通販顧客・一般顧客は同一扱い
      if (raw.indexOf('通販') !== -1 || raw === '一般顧客') return '一般顧客';
      // ゲスト
      if (raw === 'ゲスト') return 'ゲスト';
      return raw;
    },

    getCurrentCustomerType: function() {
      var customerInfo = window.shippingCustomer || null;

      // 1. ゲスト（未ログイン）
      if (!customerInfo) return 'ゲスト';

      var tags = Array.isArray(customerInfo.tags) ? customerInfo.tags : [];

      // 2. 外商顧客（タグ判別）
      if (tags.indexOf('旧外商顧客') !== -1) return '外商顧客';

      // 2.5 社売15%顧客も外商顧客扱い（社員売の前に判定）
      // タグの「％」「数字」は全角/半角どちらの表記でも一致させる（運用上は全角％が正）
      var hasShauri15 = tags.some(function(tag) {
        var normalized = String(tag)
          .replace(/％/g, '%')
          .replace(/[０-９]/g, function(d) { return String.fromCharCode(d.charCodeAt(0) - 0xFEE0); });
        return normalized === '社売15%';
      });
      if (hasShauri15) return '外商顧客';

      // 2.6 社売0％顧客は法人顧客扱い（社員売の前に判定）。％・数字は全角/半角どちらでも一致
      var hasShauri0 = tags.some(function(tag) {
        var normalized = String(tag)
          .replace(/％/g, '%')
          .replace(/[０-９]/g, function(d) { return String.fromCharCode(d.charCodeAt(0) - 0xFEE0); });
        return normalized === '社売0%';
      });
      if (hasShauri0) return '法人顧客';

      // 3. 社売（タグ判別）
      if (tags.indexOf('社員売顧客') !== -1) return '社売';

      // 4. 法人顧客 / 5. 一般顧客（メタフィールド判別）
      var memberType = String(customerInfo.memberType || '').trim();
      if (memberType === '法人') return '法人顧客';
      if (memberType === '個人') return '一般顧客';

      return '一般顧客';
    },

    getProductIdsFromItemKeys: function(itemKeys) {
      var keys = Array.isArray(itemKeys) ? itemKeys : [];
      if (!keys.length) return [];

      var keyMap = {};
      keys.forEach(function(key) {
        keyMap[String(key)] = true;
      });

      var productIds = [];
      (this.cartItems || []).forEach(function(item) {
        if (!item || !keyMap[String(item.key)]) return;
        if (item.product_id === undefined || item.product_id === null || item.product_id === '') return;
        var normalizedProductId = normalizeProductId(item.product_id);
        if (normalizedProductId) {
          productIds.push(normalizedProductId);
        }
      });
      return productIds;
    },

    shouldHideFreeShipHintByExclusionRule: function(cached, debugInfo) {
      var debug = debugInfo || null;
      if (debug) {
        debug.cache = cached || null;
      }

      if (!cached || cached.shippingType !== 'ambient') {
        if (debug) debug.reason = 'not_ambient_or_no_cache';
        return false;
      }
      if (!SHIPPING_FREE_EXCLUSION_RULES.length) {
        if (debug) debug.reason = 'no_rules';
        return false;
      }

      var prefecture = this.normalizePrefectureName(
        cached.recipientPrefecture || this.getProvinceNameByCode(cached.recipientPrefectureCode)
      );
      if (debug) debug.prefecture = prefecture;
      if (!prefecture) {
        if (debug) debug.reason = 'no_prefecture';
        return false;
      }

      var customerType = this.getCurrentCustomerType();
      if (!customerType) {
        if (debug) debug.reason = 'no_customer_type';
        return false;
      }
      var normalizedCustomerType = this.normalizeCustomerTypeName(customerType);
      if (debug) {
        debug.customerType = customerType;
        debug.normalizedCustomerType = normalizedCustomerType;
      }

      var productIds = Array.isArray(cached.productIds)
        ? cached.productIds.map(function(productId) { return normalizeProductId(productId); }).filter(function(productId) { return !!productId; })
        : [];
      if (!productIds.length) {
        productIds = this.getProductIdsFromItemKeys(cached.itemKeys);
      }
      if (debug) debug.productIds = productIds.slice();
      if (!productIds.length) {
        if (debug) debug.reason = 'no_product_ids';
        return false;
      }

      for (var i = 0; i < SHIPPING_FREE_EXCLUSION_RULES.length; i++) {
        var rule = SHIPPING_FREE_EXCLUSION_RULES[i];
        if (!rule) continue;
        if (rule.provinces.indexOf(prefecture) === -1) continue;
        var matchedCustomerType = false;
        for (var c = 0; c < rule.sellCustomerTypes.length; c++) {
          if (this.normalizeCustomerTypeName(rule.sellCustomerTypes[c]) === normalizedCustomerType) {
            matchedCustomerType = true;
            break;
          }
        }
        if (!matchedCustomerType) continue;

        for (var p = 0; p < rule.products.length; p++) {
          if (productIds.indexOf(rule.products[p]) !== -1) {
            if (debug) {
              debug.reason = 'matched';
              debug.matchedRule = rule;
              debug.matchedProductId = rule.products[p];
            }
            return true;
          }
        }
      }

      if (debug) debug.reason = 'no_matching_rule';
      return false;
    },

    /**
     * Save sender info from modal
     */
    saveSenderInfo: function() {
      var self = this;
      this.ensureZipLookupThenCall('sender', function() {
        self._doSaveSenderInfo();
      });
    },

    _doSaveSenderInfo: function() {
      var senderData = this.normalizeAddressPayload({
        lastName: $('#sender-lastName').val(),
        firstName: $('#sender-firstName').val(),
        zip: String($('#sender-zipCode').val() || '').replace(/\D/g, '').slice(0, 7),
        prefecture: $('#sender-prefecture option:selected').text(),
        prefectureCode: $('#sender-prefecture').val(),
        city: $('#sender-city').val(),
        address1: ($('#sender-address1-town').val() + $('#sender-address1-num').val()).trim(),
        address2: $('#sender-address2').val(),
        company: $('#sender-company').val(),
        personType: this.getAddressPersonType('sender'),
        phone: $('#sender-phone').val()
      });

      $('#sender-zipCode').val(senderData.zip);
      $('#sender-phone').val(senderData.phone);
      $('#sender-company').val(senderData.company);

      // Preserve existing id if available, otherwise generate new one
      var existingId = this.sender && this.sender.id ? this.sender.id : null;
      senderData.id = existingId || ('addr_' + Date.now());
      senderData.prefectureCode = $('#sender-prefecture').val();

      // Validate
      if (!this.validateAddressFields(senderData, 'sender')) {
        return;
      }

      this.sender = senderData;
      this.senderManuallyEdited = true;
      this.saveData();
      this.renderSenderInfo();
      this.closeModal();
    },

    /**
     * Handle change sender button click
     * Opens select modal if multiple addresses exist, otherwise opens edit modal
     */
    handleChangeSender: function() {
      // Get all available addresses
      var addresses = this.savedAddresses || [];

      // If more than 1 address exists, open select modal
      if (addresses.length > 1) {
        this.openSenderSelectModal();
      } else {
        // Open edit modal directly
        this.openModal('modalEditAddress');
      }
    },

    /**
     * Open modal by ID
     */
    openModal: function(modalId) {
      var scrollpos = $(window).scrollTop();
      this._savedScrollPos = scrollpos;
      $('html, body').addClass('freezePage').css('top', -scrollpos + 'px');
      $('#' + modalId).addClass('active');
      $('#' + modalId + ' .p-modal-dialog').scrollTop(0);
      if (modalId === 'modalAddAddress') {
        $('#modalAddAddress [data-add-address]').prop('disabled', false);
      }
      this.focusModalSearchInput(modalId);

      // Update orderer modal title based on login state
      if (modalId === 'modalAddOrderer') {
        var ordererTitle = window.shippingCustomer ? '会員様ご住所' : 'ご注文者ご住所';
        $('#modalAddOrderer .p-modal-ttl').contents().first().replaceWith(ordererTitle + ' ');
      }

      // Pre-fill orderer modal with customer contact info (logged-in only)
      if (modalId === 'modalAddOrderer' && window.shippingCustomer) {
        var c = window.shippingCustomer;
        if (c.lastName && !$('#orderer-lastName').val()) {
          $('#orderer-lastName').val(c.lastName);
        }
        if (c.firstName && !$('#orderer-firstName').val()) {
          $('#orderer-firstName').val(c.firstName);
        }
      }
    },

    focusModalSearchInput: function(modalId) {
      if (modalId !== 'modalBulkListAdd' && modalId !== 'modalChangeAddress') return;

      window.setTimeout(function() {
        var $input = $('#' + modalId)
          .find('[data-address-search], [data-change-address-search]')
          .filter(':visible:not(:disabled)')
          .first();

        if (!$input.length) return;
        $input.trigger('focus');
      }, 0);
    },

    /**
     * Restore #modalChangeAddress common layout pieces
     */
    restoreChangeAddressModalLayout: function() {
      var $modal = $('#modalChangeAddress');
      $modal.find('.listadd__search').show();
      $modal.find('.add-new-wrapper').show();
      $modal.find('[data-change-address-search]').val('');
    },

    /**
     * Open sender select modal
     * Reuses modalBulkListAdd with sender-specific context
     */
    openSenderSelectModal: function() {
      this.selectingSender = true;
      this.restoreChangeAddressModalLayout();

      // Render addresses with radio buttons in the change address modal
      this.renderSenderAddressList();

      // Update modal title and button for sender context
      var $modal = $('#modalChangeAddress');
      $modal.find('.p-bulk-modal__title').text('ご依頼主を選択');
      var $submitBtn = $modal.find('[data-change-recipient-address]');
      $submitBtn.html('選択した住所を設定<i class="c-btn-big__ico c-ico-arrow-01"><svg><use xlink:href="#ico-arrow01"></use></svg></i>');
      $submitBtn.attr('data-select-sender-address', '').removeAttr('data-change-recipient-address');
      $modal.find('.add-new-wrapper [data-from-change-modal] span').text('新しい住所を追加');
      $('#modalAddAddress .p-add-address-modal__title').text('新しい住所を追加');

      // Open modal
      this.openModal('modalChangeAddress');
    },

    /**
     * Render address list for sender selection (radio buttons)
     * Uses same HTML structure as renderSavedAddresses for consistent UI
     */
    renderSenderAddressList: function() {
      var self = this;
      var $container = $('[data-change-address-list]');

      var addresses = this.savedAddresses || [];

      if (addresses.length === 0) {
        $container.html('<p class="c-text-muted">登録されている住所がありません。</p>');
        return;
      }

      // 選択中の住所を先頭に表示
      var selectedId = self.sender ? self.sender.id : null;
      if (selectedId) {
        addresses = addresses.slice().sort(function(a, b) {
          return (b.id === selectedId ? 1 : 0) - (a.id === selectedId ? 1 : 0);
        });
      }

      var html = '';
      addresses.forEach(function(addr) {
        var isSelected = self.sender && self.sender.id === addr.id;
        var address2WithCompany = self.getAddress2WithCompany(addr);
        var isApiAddr = addr.id && addr.id.indexOf('addr_') !== 0;

        var nameIncomplete = self.isAddressNameIncomplete(addr);
        html += '<section class="addr-card' + (isSelected ? ' is-active' : '') + (nameIncomplete ? ' addr-card--name-warn' : '') + '" aria-labelledby="addr-title">';
        html += '<div class="addr-card__body">';
        html += '<p class="addr-card__small">';
        if (addr.isDefault) html += '<span class="c-badge c-badge--blue02" style="margin-right:6px;vertical-align:middle;">会員様ご住所</span>';
        var _sdn = (addr.lastName || addr.firstName) ? ((addr.lastName || '') + ' ' + (addr.firstName || '')).trim() : (addr.company || '');
        html += '<strong>' + self.escapeHtml(_sdn) + ' 様</strong></p>';
        if (nameIncomplete) {
          html += '<p class="addr-card__line addr-card__name-warn"><span class="c-badge c-badge--red" style="font-size:1rem;">お名前（姓・名）が未登録です。編集してください。</span></p>';
        }
        html += '<p class="addr-card__line">〒' + self.formatZipDisplay(addr.zip || '') + ' <br>' + (addr.prefecture || '') + ' ' + (addr.city || '') + ' ' + (addr.address1 || '');
        if (address2WithCompany) html += ' ' + address2WithCompany;
        html += '</p>';
        if (addr.phone) {
          html += '<p class="addr-card__line">TEL: ' + self.formatPhoneDisplay(addr.phone) + '</p>';
        }
        html += '</div>';
        html += '<input type="radio" name="single-address-select" class="option-card__radio"' + (isSelected ? ' checked' : '') + ' data-address-id="' + addr.id + '">';
        html += '<button type="button" class="addr-card__edit-btn" data-edit-saved-address="' + self.escapeHtml(addr.id) + '">編集</button>';
        html += '</section>';
      });

      $container.html(html);
    },

    /**
     * Select sender from address list
     */
    selectSenderAddress: function() {
      var selectedId = $('[data-change-address-list] input:checked').data('address-id');

      if (!selectedId) {
        alert('住所を選択してください。');
        return;
      }

      // Find the address
      var addresses = this.savedAddresses || [];

      var selectedAddr = addresses.find(function(a) { return a.id === selectedId; });

      if (selectedAddr) {
        // Update sender
        this.sender = {
          id: selectedAddr.id,
          lastName: selectedAddr.lastName,
          firstName: selectedAddr.firstName,
          zip: selectedAddr.zip,
          prefecture: selectedAddr.prefecture,
          prefectureCode: selectedAddr.prefectureCode,
          city: selectedAddr.city,
          address1: selectedAddr.address1,
          address2: selectedAddr.address2 || '',
          company: selectedAddr.company || '',
          phone: selectedAddr.phone || ''
        };

        this.senderManuallyEdited = true;
        this.saveData();
        this.renderSenderInfo();
      }

      // Reset modal and close
      // restoreSenderSelectModal を非同期で実行し、ボタン属性の変更が
      // 現在のクリックイベントのバブリング中に data-change-recipient-address の
      // ハンドラを誤って発火させないようにする
      var self2 = this;
      this.closeModal();
      setTimeout(function() {
        self2.restoreSenderSelectModal();
      }, 0);
    },

    /**
     * Restore sender select modal to original state
     */
    restoreSenderSelectModal: function() {
      this.selectingSender = false;

      var $modal = $('#modalChangeAddress');
      this.restoreChangeAddressModalLayout();

      // Restore title and button to default change address state
      $modal.find('.p-bulk-modal__title').text('お届け先を変更');
      var $submitBtn = $modal.find('[data-select-sender-address]');
      $submitBtn.html('お届け先を変更<i class="c-btn-big__ico c-ico-arrow-01"><svg><use xlink:href="#ico-arrow01"></use></svg></i>');
      $submitBtn.attr('data-change-recipient-address', '').removeAttr('data-select-sender-address');
      $modal.find('.add-new-wrapper [data-from-change-modal] span').text('新しいお届け先を追加');
      $('#modalAddAddress .p-add-address-modal__title').text('新しいお届け先を追加');
    },

    /**
     * Handle change recipient button click (single mode)
     * Opens select modal if multiple addresses exist, otherwise opens edit modal
     */
    handleChangeRecipient: function() {
      // Get all available addresses
      var addresses = this.savedAddresses || [];

      // If more than 1 address exists, open select modal
      if (addresses.length > 1) {
        this.openRecipientSelectModal();
      } else {
        // Open edit modal directly
        this.openEditRecipientModal();
      }
    },

    /**
     * Open recipient select modal (single mode)
     * Reuses modalBulkListAdd with recipient-specific context
     */
    openRecipientSelectModal: function() {
      this.selectingRecipient = true;
      this.restoreChangeAddressModalLayout();

      // Render addresses with radio buttons in the change address modal
      this.renderRecipientAddressList();

      // Update modal title and button for recipient select context
      var $modal = $('#modalChangeAddress');
      $modal.find('.p-bulk-modal__title').text('お届け先を選択');
      var $submitBtn = $modal.find('[data-change-recipient-address]');
      $submitBtn.html('選択した住所を設定<i class="c-btn-big__ico c-ico-arrow-01"><svg><use xlink:href="#ico-arrow01"></use></svg></i>');
      $submitBtn.attr('data-select-recipient-address', '').removeAttr('data-change-recipient-address');

      // Open modal
      this.openModal('modalChangeAddress');
    },

    /**
     * Render address list for recipient selection (radio buttons)
     * Uses same HTML structure as renderSavedAddresses for consistent UI
     */
    renderRecipientAddressList: function() {
      var self = this;
      var $container = $('[data-change-address-list]');

      var addresses = this.savedAddresses || [];

      if (addresses.length === 0) {
        $container.html('<p class="c-text-muted">登録されている住所がありません。</p>');
        return;
      }

      // 選択中の住所を先頭に表示
      var selectedId = self.singleRecipient ? self.singleRecipient.id : null;
      if (selectedId) {
        addresses = addresses.slice().sort(function(a, b) {
          return (b.id === selectedId ? 1 : 0) - (a.id === selectedId ? 1 : 0);
        });
      }

      var html = '';
      addresses.forEach(function(addr) {
        var isSelected = self.singleRecipient && self.singleRecipient.id === addr.id;
        var address2WithCompany = self.getAddress2WithCompany(addr);
        var isApiAddr = addr.id && addr.id.indexOf('addr_') !== 0;

        var nameIncomplete = self.isAddressNameIncomplete(addr);
        html += '<section class="addr-card' + (isSelected ? ' is-active' : '') + (nameIncomplete ? ' addr-card--name-warn' : '') + '" aria-labelledby="addr-title">';
        html += '<div class="addr-card__body">';
        html += '<p class="addr-card__small">';
        if (addr.isDefault) html += '<span class="c-badge c-badge--blue02" style="margin-right:6px;vertical-align:middle;">会員様ご住所</span>';
        var _sdn = (addr.lastName || addr.firstName) ? ((addr.lastName || '') + ' ' + (addr.firstName || '')).trim() : (addr.company || '');
        html += '<strong>' + self.escapeHtml(_sdn) + ' 様</strong></p>';
        if (nameIncomplete) {
          html += '<p class="addr-card__line addr-card__name-warn"><span class="c-badge c-badge--red" style="font-size:1rem;">お名前（姓・名）が未登録です。編集してください。</span></p>';
        }
        html += '<p class="addr-card__line">〒' + self.formatZipDisplay(addr.zip || '') + ' <br>' + (addr.prefecture || '') + ' ' + (addr.city || '') + ' ' + (addr.address1 || '');
        if (address2WithCompany) html += ' ' + address2WithCompany;
        html += '</p>';
        if (addr.phone) {
          html += '<p class="addr-card__line">TEL: ' + self.formatPhoneDisplay(addr.phone) + '</p>';
        }
        html += '</div>';
        html += '<input type="radio" name="single-address-select" class="option-card__radio"' + (isSelected ? ' checked' : '') + ' data-address-id="' + addr.id + '">';
        html += '<button type="button" class="addr-card__edit-btn" data-edit-saved-address="' + self.escapeHtml(addr.id) + '">編集</button>';
        html += '</section>';
      });

      $container.html(html);
    },

    /**
     * Select recipient from address list (single mode)
     */
    selectRecipientAddress: function() {
      var selectedId = $('[data-change-address-list] input:checked').data('address-id');

      if (!selectedId) {
        alert('住所を選択してください。');
        return;
      }

      // Find the address
      var addresses = this.savedAddresses || [];

      var selectedAddr = addresses.find(function(a) { return a.id === selectedId; });

      if (selectedAddr) {
        // Keep existing items, wrapping, delivery settings if any
        var existingRecipient = this.singleRecipient || {};
        var defaultDeliverySettings = {
          ambient: { date: '', time: '' },
          frozen: { date: '', time: '' },
          mail: { date: '', time: '' }
        };

        // Update singleRecipient
        this.singleRecipient = {
          id: selectedAddr.id,
          lastName: selectedAddr.lastName,
          firstName: selectedAddr.firstName,
          zip: selectedAddr.zip,
          prefecture: selectedAddr.prefecture,
          prefectureCode: selectedAddr.prefectureCode,
          city: selectedAddr.city,
          address1: selectedAddr.address1,
          address2: selectedAddr.address2 || '',
          company: selectedAddr.company || '',
          phone: selectedAddr.phone || '',
          items: existingRecipient.items || {},
          wrapping: existingRecipient.wrapping || { bag: 'off', type: 'paper' },
          deliverySettings: existingRecipient.deliverySettings || defaultDeliverySettings
        };

        this.saveData();
        this.clearDeliveryOptionsCache();
        this.updateSenderByRecipient();
        this.renderCartItems();
        this.updateSummary();
      }

      // Reset modal and close
      this.restoreRecipientSelectModal();
      this.closeModal();
    },

    /**
     * Restore recipient select modal to original state
     */
    restoreRecipientSelectModal: function() {
      this.selectingRecipient = false;

      var $modal = $('#modalChangeAddress');
      this.restoreChangeAddressModalLayout();

      // Restore title and button to default change address state
      $modal.find('.p-bulk-modal__title').text('お届け先を変更');
      var $submitBtn = $modal.find('[data-select-recipient-address]');
      $submitBtn.html('お届け先を変更<i class="c-btn-big__ico c-ico-arrow-01"><svg><use xlink:href="#ico-arrow01"></use></svg></i>');
      $submitBtn.attr('data-change-recipient-address', '').removeAttr('data-select-recipient-address');
    },

    /**
     * Open interoffice shipment address modal
     * @param {string} recipientId - "single" or multi recipient id
     */
    openInterofficeAddressModal: function(recipientId) {
      if (!this.canUseInterofficeShipment()) {
        alert('社内便住所が設定されていません。');
        return;
      }

      this.selectingInterofficeRecipientId = recipientId || 'single';
      this.renderInterofficeAddressList(this.selectingInterofficeRecipientId);

      var $modal = $('#modalChangeAddress');
      $modal.find('.add-new-wrapper').hide();
      $modal.find('.p-bulk-modal__title').text('社内便送付先を選択');

      var $submitBtn = $modal.find('[data-change-recipient-address]');
      if (!$submitBtn.length) {
        $submitBtn = $modal.find('[data-select-sender-address], [data-select-recipient-address]');
      }
      $submitBtn.html('選択した住所を設定<i class="c-btn-big__ico c-ico-arrow-01"><svg><use xlink:href="#ico-arrow01"></use></svg></i>');
      $submitBtn.attr('data-select-interoffice-address', '')
        .removeAttr('data-change-recipient-address')
        .removeAttr('data-select-sender-address')
        .removeAttr('data-select-recipient-address');

      this.openModal('modalChangeAddress');
    },

    /**
     * Render interoffice address list in modalChangeAddress
     * @param {string} recipientId - "single" or multi recipient id
     */
    renderInterofficeAddressList: function(recipientId) {
      var self = this;
      var $container = $('[data-change-address-list]');
      var addresses = this.interofficeAddresses || [];

      if (addresses.length === 0) {
        $container.html('<p class="c-text-muted">社内便住所が登録されていません。</p>');
        return;
      }

      var targetRecipient = recipientId === 'single'
        ? this.singleRecipient
        : (this.multiRecipients || []).find(function(r) { return r.id === recipientId; });

      var html = '';
      addresses.forEach(function(addr, index) {
        var addressId = addr.id || ('interoffice_' + index);
        var isSelected = targetRecipient &&
          targetRecipient.isInteroffice === true &&
          targetRecipient.interofficeAddressId === addressId;
        var displayFullName = ((addr.lastName || '') + ' ' + (addr.firstName || '')).trim();
        var address2WithCompany = buildAddress2WithCompany(addr.address2, addr.company);

        html += '<section class="addr-card' + (isSelected ? ' is-active' : '') + '" aria-labelledby="addr-title">';
        html += '<div class="addr-card__body">';
        if (displayFullName) {
          html += '<p class="addr-card__small"><strong>' + self.escapeHtml(displayFullName) + ' 様</strong></p>';
        }
        html += '<p class="addr-card__line">〒' + self.escapeHtml(self.formatZipDisplay(addr.zip || '')) + ' <br>' + self.escapeHtml((addr.prefecture || '') + ' ' + (addr.city || '') + ' ' + (addr.address1 || ''));
        if (address2WithCompany) html += ' ' + self.escapeHtml(address2WithCompany);
        html += '</p>';
        if (addr.phone) {
          html += '<p class="addr-card__line">TEL: ' + self.escapeHtml(self.formatPhoneDisplay(addr.phone)) + '</p>';
        }
        html += '</div>';
        html += '<input type="radio" name="interoffice-address-select" class="option-card__radio"' + (isSelected ? ' checked' : '') + ' data-address-id="' + self.escapeHtml(addressId) + '">';
        html += '</section>';
      });

      $container.html(html);
    },

    /**
     * Apply selected interoffice address to recipient
     */
    selectInterofficeAddress: function() {
      var selectedId = $('[data-change-address-list] input:checked').data('address-id');
      if (!selectedId) {
        alert('住所を選択してください。');
        return;
      }

      var selectedAddr = (this.interofficeAddresses || []).find(function(addr) {
        return String(addr.id) === String(selectedId);
      });
      if (!selectedAddr) {
        alert('選択された住所が見つかりません。');
        return;
      }

      var targetRecipientId = this.selectingInterofficeRecipientId || 'single';
      var targetRecipient = targetRecipientId === 'single'
        ? (this.singleRecipient || {})
        : (this.multiRecipients || []).find(function(r) { return r.id === targetRecipientId; }) || {};
      var prefecture = selectedAddr.prefecture || '';
      var prefCode = selectedAddr.prefectureCode || this.prefectureToProvinceCode(prefecture);
      if (targetRecipientId === 'single') {
        var existingRecipient = this.singleRecipient || {};
        var defaultDeliverySettings = {
          ambient: { date: '', time: '' },
          frozen: { date: '', time: '' },
          mail: { date: '', time: '' }
        };
        this.singleRecipient = {
          id: existingRecipient.id || 'single_recipient',
          lastName: selectedAddr.lastName || '',
          firstName: selectedAddr.firstName || '',
          zip: selectedAddr.zip || '',
          prefecture: prefecture,
          prefectureCode: prefCode || '',
          city: selectedAddr.city || '',
          address1: selectedAddr.address1 || '',
          address2: selectedAddr.address2 || '',
          phone: selectedAddr.phone || '',
          company: selectedAddr.company || '',
          items: existingRecipient.items || {},
          wrapping: existingRecipient.wrapping || { bag: 'off', type: 'paper' },
          deliverySettings: existingRecipient.deliverySettings || defaultDeliverySettings,
          isInteroffice: true,
          interofficeAddressId: String(selectedId)
        };
      } else {
        var idx = (this.multiRecipients || []).findIndex(function(r) { return r.id === targetRecipientId; });
        if (idx !== -1) {
          var existing = this.multiRecipients[idx];
          this.multiRecipients[idx] = $.extend({}, existing, {
            lastName: selectedAddr.lastName || '',
            firstName: selectedAddr.firstName || '',
            zip: selectedAddr.zip || '',
            prefecture: prefecture,
            prefectureCode: prefCode || '',
            city: selectedAddr.city || '',
            address1: selectedAddr.address1 || '',
            address2: selectedAddr.address2 || '',
            phone: selectedAddr.phone || '',
            company: selectedAddr.company || '',
            isInteroffice: true,
            interofficeAddressId: String(selectedId),
            addressId: null // アドレス帳との紐付けを解除（社内便住所は別管理）
          });
        }
      }

      this.saveData();
      this.clearDeliveryOptionsCache();
      this.updateSenderByRecipient();
      this.renderCartItems();
      this.updateSummary();
      this.restoreInterofficeSelectModal();
      this.closeModal();
    },

    /**
     * Restore interoffice select modal state
     */
    restoreInterofficeSelectModal: function() {
      this.selectingInterofficeRecipientId = null;
      var $modal = $('#modalChangeAddress');
      this.restoreChangeAddressModalLayout();
      $modal.find('.p-bulk-modal__title').text('お届け先を変更');
      var $submitBtn = $modal.find('[data-select-interoffice-address]');
      $submitBtn.html('お届け先を変更<i class="c-btn-big__ico c-ico-arrow-01"><svg><use xlink:href="#ico-arrow01"></use></svg></i>');
      $submitBtn.attr('data-change-recipient-address', '').removeAttr('data-select-interoffice-address');
    },

    /**
     * Validate address form fields
     * @param {Object} addr - Address object with lastName, firstName, zip, etc.
     * @param {string} prefix - Field ID prefix ('add', 'edit-recipient', 'sender')
     * @returns {boolean} true if valid, false if has errors
     */
    validateAddressFields: function(addr, prefix) {
      var errors = {};
      var extraErrorFields = []; // fields to mark is-error without message (for combined group errors)
      var combinedNameError = null; // combined name error message, placed after .p-row (not inside .p-row-item)
      var normalized = this.normalizeAddressPayload(addr);
      // 文字種チェックは3層方式 (char-validator.js) に一本化。
      // scope: 'name'=姓名 / 'address'=住所1・住所2・会社名 / 'global'=市区町村（全体ルールのみ）
      // CharValidator 未ロード時は文字種チェックのみスキップ（必須・桁数チェックは維持）。
      var charError = function(value, scope) {
        if (!value || !window.CharValidator) return null;
        return window.CharValidator.validate(value, scope);
      };

      // --- 姓 (lastName) ---
      var lastNameCharError = charError(normalized.lastName, 'name');
      if (!normalized.lastName) {
        errors.lastName = '姓を入力してください。';
      } else if (lastNameCharError) {
        errors.lastName = lastNameCharError;
      }

      // --- 名 (firstName) ---
      var firstNameCharError = charError(normalized.firstName, 'name');
      if (!normalized.firstName) {
        errors.firstName = '名を入力してください。';
      } else if (firstNameCharError) {
        errors.firstName = firstNameCharError;
      }

      // --- お名前 combined ≤ 36 全角 ---
      if (normalized.lastName && normalized.firstName && !errors.lastName && !errors.firstName) {
        var nameCombined = countJaChars(normalized.lastName + normalized.firstName);
        if (nameCombined > 36) {
          var nameExcess = Math.ceil(nameCombined - 36);
          combinedNameError = 'お名前は「36文字以内」となるようご入力ください。（' + nameExcess + '文字超過しています。）';
          extraErrorFields.push('lastName', 'firstName');
        }
      }

      // --- 郵便番号 (zip) - exactly 7 half-width digits, no hyphens ---
      if (!normalized.zip) {
        errors.zip = '郵便番号を入力してください。';
      } else if (!/^\d{7}$/.test(normalized.zip)) {
        errors.zip = '郵便番号は半角数字7桁で入力してください。';
      }

      // --- 都道府県 (prefecture) ---
      if (!normalized.prefectureCode) {
        if (!errors.zip && normalized.zip) {
          errors.zip = '郵便番号から住所が見つかりませんでした。';
        } else {
          errors.prefecture = '都道府県を選択してください。';
        }
      }

      // --- 市区町村 (city) ---
      var cityCharError = charError(normalized.city, 'global');
      if (!normalized.city) {
        errors.city = '市区町村を入力してください。';
      } else if (normalized.city.length > 100) {
        errors.city = '市区町村は100文字以内で入力してください。';
      } else if (cityCharError) {
        errors.city = cityCharError;
      }

      // --- 住所 (address1) ---
      var address1CharError = charError(normalized.address1, 'address');
      if (!normalized.address1) {
        errors.address1 = '住所を入力してください。';
      } else if (normalized.address1.length > 200) {
        errors.address1 = '住所は200文字以内で入力してください。';
      } else if (address1CharError) {
        errors.address1 = address1CharError;
      }

      // --- 建物名 (address2) - optional ---
      var address2CharError = charError(normalized.address2, 'address');
      if (normalized.address2) {
        if (normalized.address2.length > 200) {
          errors.address2 = '建物名は200文字以内で入力してください。';
        } else if (address2CharError) {
          errors.address2 = address2CharError;
        }
      }

      // --- 会社名 (company) - required only for corporate ---
      var companyCharError = charError(normalized.company, 'address');
      if (normalized.personType === 'corporate') {
        if (!normalized.company) {
          errors.company = '会社名を入力してください。';
        } else if (normalized.company.length > 200) {
          errors.company = '会社名は200文字以内で入力してください。';
        } else if (companyCharError) {
          errors.company = companyCharError;
        }
      } else if (normalized.company && (normalized.company.length > 200 || companyCharError)) {
        errors.company = normalized.company.length > 200
          ? '会社名は200文字以内で入力してください。'
          : companyCharError;
      }

      // --- 住所 combined ≤ 89 全角 (個人) ---
      if (normalized.personType !== 'corporate' && normalized.prefecture && normalized.city && normalized.address1 &&
          !errors.prefecture && !errors.city && !errors.address1 && !errors.address2) {
        var combinedAddr = normalized.prefecture + normalized.city + normalized.address1 + (normalized.address2 || '');
        if (countJaChars(combinedAddr) > 89) {
          var addrExcess = Math.ceil(countJaChars(combinedAddr) - 89);
          errors.address2 = '「住所」は「89文字以内」となるようご入力ください。（' + addrExcess + '文字超過しています。）';
          extraErrorFields.push('prefecture', 'city', 'address1');
        }
      }

      // --- 住所＋会社名 combined ≤ 89 全角 (法人) ---
      if (normalized.personType === 'corporate' && normalized.company && normalized.prefecture && normalized.city && normalized.address1 &&
          !errors.company && !errors.prefecture && !errors.city && !errors.address1 && !errors.address2) {
        var combinedCorpAddr = normalized.company + ' ' + normalized.prefecture + normalized.city + normalized.address1 + (normalized.address2 || '');
        if (countJaChars(combinedCorpAddr) > 89) {
          var corpExcess = Math.ceil(countJaChars(combinedCorpAddr) - 89);
          errors.address2 = '「住所＋会社名」の合計が「89文字以内」となるようご入力ください。（' + corpExcess + '文字超過しています。）';
          extraErrorFields.push('company', 'prefecture', 'city', 'address1');
        }
      }

      // --- 電話番号 (phone) - Japan domestic only ---
      // 携帯: 070/080/090 + 8桁 = 11桁
      // IP電話: 050 + 8桁 = 11桁
      // 固定電話: 0[1-9] で始まる10桁 (市外局番+市内局番+加入者番号)
      // フリーダイヤル: 0120 + 6桁 = 10桁, 0800 + 7桁 = 11桁
      if (!normalized.phone) {
        errors.phone = '電話番号を入力してください。';
      } else if (!/^(0[5789]0\d{8}|0[1-9]\d{8}|0120\d{6}|0800\d{7})$/.test(normalized.phone)) {
        errors.phone = '正しい電話番号を入力してください。';
      } else if (/0{8}$/.test(normalized.phone)) {
        errors.phone = '正しい電話番号を入力してください。';
      }

      // Map field names to input IDs
      var fieldIdMap = {
        lastName: prefix + '-lastName',
        firstName: prefix + '-firstName',
        zip: prefix + '-zipCode',
        prefecture: prefix + '-prefecture',
        city: prefix + '-city',
        address1: prefix + '-address1-num',
        address2: prefix + '-address2',
        company: prefix + '-company',
        phone: prefix + '-phone'
      };

      // Apply normalized values back to form (address1 excluded — combined field, no write-back)
      var valueMap = {
        lastName: normalized.lastName,
        firstName: normalized.firstName,
        zip: normalized.zip,
        city: normalized.city,
        address2: normalized.address2,
        company: normalized.company,
        phone: normalized.phone
      };
      Object.keys(valueMap).forEach(function(field) {
        var $el = $('#' + fieldIdMap[field]);
        if ($el.length) {
          $el.val(valueMap[field]);
        }
      });

      // Write normalized payload back to caller object
      Object.keys(normalized).forEach(function(key) {
        addr[key] = normalized[key];
      });

      // Clear previous errors
      var allFields = Object.keys(fieldIdMap);
      allFields.forEach(function(field) {
        var $el = $('#' + fieldIdMap[field]);
        if (!$el.length) return;
        $el.closest('.p-row-item').removeClass('is-error');
        $el.closest('.p-row-item').find('.p-form-error-txt').remove();
      });
      // Hide inline zip-lookup-error (sibling to zip input) so it doesn't
      // double up with the validation error for the same field.
      $('#' + fieldIdMap.zip).siblings('.zip-lookup-error').hide();
      // Clear combined name error (placed after .p-row, not inside .p-row-item)
      $('#' + fieldIdMap.lastName).closest('.p-row').next('.p-combined-error').remove();

      // Apply errors
      var hasErrors = Object.keys(errors).length > 0 || combinedNameError !== null;
      if (hasErrors) {
        Object.keys(errors).forEach(function(field) {
          var $el = $('#' + fieldIdMap[field]);
          if (!$el.length) return;
          var $rowItem = $el.closest('.p-row-item');
          $rowItem.addClass('is-error');
          $rowItem.append('<p class="p-form-error-txt">' + errors[field] + '</p>');
        });

        // Apply extra error highlights (is-error without message, for combined field groups)
        extraErrorFields.forEach(function(field) {
          if (errors[field]) return; // already handled with message
          var $el = $('#' + fieldIdMap[field]);
          if (!$el.length) return;
          $el.closest('.p-row-item').addClass('is-error');
        });

        // Apply combined name error after the .p-row containing both name fields
        if (combinedNameError) {
          var $nameRow = $('#' + fieldIdMap.lastName).closest('.p-row');
          if ($nameRow.length) {
            $nameRow.after('<p class="p-form-error-txt p-combined-error">' + combinedNameError + '</p>');
          }
        }

        // Scroll to first error within the modal
        var firstErrorField = Object.keys(errors)[0];
        var $firstError = firstErrorField
          ? $('#' + fieldIdMap[firstErrorField])
          : $('#' + fieldIdMap.lastName);
        var $modal = $firstError.closest('.p-modal-content');
        if ($modal.length) {
          var scrollTo = $firstError.closest('.p-row').get(0);
          if (scrollTo) {
            scrollTo.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }

      return !hasErrors;
    },

    /**
     * Add new address from modal
     * - If first address, also assigns to sender and singleRecipient
     */
    addNewAddress: function() {
      var self = this;
      this.ensureZipLookupThenCall('add', function() {
        self._doAddNewAddress();
      });
    },

    _doAddNewAddress: function() {
      var self = this;
      var newAddr = this.normalizeAddressPayload({
        id: 'addr_' + Date.now(),
        lastName: $('#add-lastName').val(),
        firstName: $('#add-firstName').val(),
        zip: String($('#add-zipCode').val() || '').replace(/\D/g, '').slice(0, 7),
        prefecture: $('#add-prefecture option:selected').text(),
        prefectureCode: $('#add-prefecture').val(),
        city: $('#add-city').val(),
        address1: ($('#add-address1-town').val() + $('#add-address1-num').val()).trim(),
        address2: $('#add-address2').val(),
        company: $('#add-company').val(),
        personType: this.getAddressPersonType('add'),
        phone: $('#add-phone').val()
      });

      // Validate
      if (!this.validateAddressFields(newAddr, 'add')) {
        return;
      }

      // Logged-in: save via API
      if (window.shippingCustomer) {
        var $btn = $('#modalAddAddress [data-add-address]');
        $btn.prop('disabled', true);

        var payload = {
          lastName: newAddr.lastName,
          firstName: newAddr.firstName,
          company: newAddr.company || '',
          zip: newAddr.zip,
          province: newAddr.prefecture,
          city: newAddr.city,
          address1: newAddr.address1,
          address2: newAddr.address2 || '',
          phone: newAddr.phone || '',
          setAsDefault: false
        };

        $.ajax({
          url: '/apps/ping/customers/addresses' + window.location.search,
          method: 'POST',
          contentType: 'application/json',
          data: JSON.stringify(payload),
          xhrFields: { withCredentials: true }
        }).then(function(data) {
          self.debugNotify('[Address API] Customer address registered: ' + newAddr.lastName + ' ' + newAddr.firstName + ' (〒' + newAddr.zip + ')');
          // Re-fetch all addresses so savedAddresses has the real API ID
          self.fetchCustomerAddresses().then(function() {
            // Find the newly added address by matching key fields (search from end = newest)
            for (var i = self.savedAddresses.length - 1; i >= 0; i--) {
              var a = self.savedAddresses[i];
              if (a.lastName === newAddr.lastName &&
                  a.address1 === newAddr.address1 &&
                  a.zip === newAddr.zip) {
                newAddr = a;
                break;
              }
            }
            self.completeAddNewAddress(newAddr);
          });
        }).fail(function(xhr) {
          var json = xhr.responseJSON;
          var errMsg = '住所の登録に失敗しました。しばらくしてから再度お試しください。';
          if (json && json.errors && json.errors.length > 0) {
            errMsg = json.errors.map(function(e) { return e.message || JSON.stringify(e); }).join('\n');
          } else if (json && json.message) {
            errMsg = json.message;
          }
          if (errMsg === 'Address already exists') {
            errMsg = '「会員様ご住所」または、「アドレス帳」にすでに同じ情報が登録されています。重複登録はできません。';
          }
          alert(errMsg);
          $btn.prop('disabled', false);
        });
        return;
      }

      // Guest: save locally
      this.completeAddNewAddress(newAddr);
    },

    /**
     * Ensure zip-lookup-derived fields (prefecture, city, town) are filled before form submission.
     * Browser autofill often fills the zip field without firing input/change events,
     * leaving readonly fields empty. This helper detects that and runs the lookup synchronously-ish.
     * @param {string} prefix - form prefix (e.g. 'orderer', 'add', 'sender', 'edit-recipient', 'esa')
     * @param {Function} callback - called after fields are filled (or immediately if already filled)
     */
    ensureZipLookupThenCall: function(prefix, callback) {
      var self = this;
      var $zip = $('#' + prefix + '-zipCode');
      var $pref = $('#' + prefix + '-prefecture');
      var $city = $('#' + prefix + '-city');
      var $town = $('#' + prefix + '-address1-town');

      var rawZip = String($zip.val() || '').replace(/\D/g, '').slice(0, 7);
      if (rawZip.length === 7 && (!$town.val() || !$pref.val() || !$city.val())) {
        var modalKey = (prefix === 'orderer') ? 'orderer'
                     : (prefix === 'esa') ? 'esa'
                     : null;
        if (modalKey) self._beginModalZipLookup(modalKey);
        $.ajax({
          url: '/apps/ping/postal-codes/search',
          dataType: 'jsonp',
          data: { zipcode: rawZip },
          timeout: 3000,
          success: function(res) {
            if (res && res.status === 200 && res.results && res.results.length > 0) {
              var r = res.results[0];
              var prefCode = PREFECTURE_MAP[r.address1];
              if (prefCode) $pref.val(prefCode);
              $city.val(r.address2);
              $town.val(r.address3 || '');
              self._updateTownWidth($town);
            }
            callback();
          },
          error: function() {
            callback();
          }
        }).always(function() {
          if (modalKey) self._endModalZipLookup(modalKey);
        });
        return;
      }
      callback();
    },

    addOrdererInfo: function() {
      var self = this;
      this.ensureZipLookupThenCall('orderer', function() {
        self._doAddOrdererInfo();
      });
    },

    _doAddOrdererInfo: function() {
      // 顧客番号ロックされたフィールドは元の値を使う（esa-モーダルと同パターン）
      var townVal = $('#orderer-address1-town').val() || '';
      var numVal = $('#orderer-address1-num').val() || '';
      var lockedAddr1 = $('#orderer-address1-num').data('lockedValue');
      var addr1Val = lockedAddr1 || (townVal + numVal).trim();
      var addr2Val = $('#orderer-address2').data('lockedValue') || $('#orderer-address2').val();

      var newAddr = this.normalizeAddressPayload({
        id: 'addr_' + Date.now(),
        lastName: $('#orderer-lastName').val(),
        firstName: $('#orderer-firstName').val(),
        zip: String($('#orderer-zipCode').val() || '').replace(/\D/g, '').slice(0, 7),
        prefecture: $('#orderer-prefecture option:selected').text(),
        prefectureCode: $('#orderer-prefecture').val(),
        city: $('#orderer-city').val(),
        address1: addr1Val,
        address1Town: lockedAddr1 ? '' : townVal,
        address1Num: lockedAddr1 ? '' : numVal,
        address2: addr2Val,
        company: $('#orderer-company').val(),
        personType: this.getAddressPersonType('orderer'),
        phone: $('#orderer-phone').val()
      });

      // Reflect stripped zip back to the input field
      $('#orderer-zipCode').val(newAddr.zip);

      if (!this.validateAddressFields(newAddr, 'orderer')) {
        return;
      }

      // Logged-in: save via API as default address
      if (window.shippingCustomer) {
        var self = this;
        var $btn = $('#modalAddOrderer [data-add-orderer]');
        $btn.prop('disabled', true);

        var payload = {
          lastName: newAddr.lastName,
          firstName: newAddr.firstName,
          company: newAddr.company || '',
          zip: newAddr.zip,
          province: newAddr.prefecture,
          city: newAddr.city,
          address1: newAddr.address1,
          address2: newAddr.address2 || '',
          phone: newAddr.phone || '',
          setAsDefault: true
        };

        var existingId = this.orderer && this.orderer.id;
        var isApiId = existingId && existingId !== 'orderer_default' && !/^addr_/.test(String(existingId));
        var apiUrl, apiMethod;
        if (isApiId) {
          var match = String(existingId).match(/MailingAddress\/(\d+)/);
          var numericId = match ? match[1] : existingId;
          apiUrl = '/apps/ping/customers/addresses/' + numericId + window.location.search;
          apiMethod = 'PUT';
        } else {
          apiUrl = '/apps/ping/customers/addresses' + window.location.search;
          apiMethod = 'POST';
        }

        $.ajax({
          url: apiUrl,
          method: apiMethod,
          contentType: 'application/json',
          data: JSON.stringify(payload),
          xhrFields: { withCredentials: true }
        }).then(function(data) {
          if (isApiId) {
            // PUT: trust locally-built newAddr (server response strips prefectureCode);
            // only pull id from server to stay in sync.
            var serverId = data && data.address && data.address.id;
            var updatedAddr = $.extend({}, newAddr, { id: serverId || existingId });
            self.clearOrdererForm();
            self.completeOrdererRegistration(updatedAddr);
          } else {
            // POST: re-fetch to get the real API id of the new address
            self.fetchCustomerAddresses().then(function() {
              for (var i = self.savedAddresses.length - 1; i >= 0; i--) {
                var a = self.savedAddresses[i];
                if (a.lastName === newAddr.lastName &&
                    a.address1 === newAddr.address1 &&
                    a.zip === newAddr.zip) {
                  newAddr = a;
                  break;
                }
              }
              self.clearOrdererForm();
              self.completeOrdererRegistration(newAddr);
            });
          }
        }).fail(function(xhr) {
          var json = xhr.responseJSON;
          var errMsg = '住所の登録に失敗しました。しばらくしてから再度お試しください。';
          if (json && json.errors && json.errors.length > 0) {
            errMsg = json.errors.map(function(e) { return e.message || JSON.stringify(e); }).join('\n');
          } else if (json && json.message) {
            errMsg = json.message;
          }
          if (errMsg === 'Address already exists') {
            errMsg = '「会員様ご住所」または、「アドレス帳」にすでに同じ情報が登録されています。重複登録はできません。';
          }
          alert(errMsg);
          $btn.prop('disabled', false);
        });
        return;
      }

      // Guest: save locally only
      this.clearOrdererForm();
      this.completeOrdererRegistration(newAddr);
    },

    clearOrdererForm: function() {
      $('#orderer-lastName, #orderer-firstName, #orderer-zipCode, #orderer-city, #orderer-address1-town, #orderer-address1-num, #orderer-address2, #orderer-company, #orderer-phone').val('');
      $('#orderer-prefecture').val('');
      this.setAddressPersonType('orderer', 'individual');
    },

    /**
     * Pre-fill orderer modal with existing orderer data for editing
     */
    fillOrdererModal: function(o) {
      if (!o) return;
      var self = this;

      // Wipe any error state left from a previous open of this modal
      // (validation text, is-error highlights, inline zip-lookup message,
      // and the cached lastLookupZip so a valid value re-fetches cleanly).
      var $modal = $('#modalAddOrderer');
      $modal.find('.is-error').removeClass('is-error');
      $modal.find('.p-form-error-txt').remove();
      $modal.find('.p-combined-error').remove();
      $modal.find('.zip-lookup-error').hide();
      $('#orderer-zipCode').removeData('lastLookupZip');

      // Sync fill from stored data
      $('#orderer-lastName').val(o.lastName || '');
      $('#orderer-firstName').val(o.firstName || '');
      $('#orderer-zipCode').val(o.zip || '');
      $('#orderer-prefecture').val(o.prefectureCode || '');
      $('#orderer-city').val(o.city || '');
      $('#orderer-address2').val(o.address2 || '');
      $('#orderer-company').val(o.company || '');
      $('#orderer-phone').val(o.phone || '');
      this.setAddressPersonType('orderer', o.company ? 'corporate' : 'individual');

      // Town/num: use cached split if we have one, otherwise leave town empty and
      // stuff the full address1 into num. Cached split avoids an API round-trip.
      var hasCachedSplit = typeof o.address1Town === 'string' || typeof o.address1Num === 'string';
      $('#orderer-address1-town').val(hasCachedSplit ? (o.address1Town || '') : '');
      $('#orderer-address1-num').val(hasCachedSplit ? (o.address1Num || '') : (o.address1 || ''));
      this._updateTownWidth($('#orderer-address1-town'));

      // 顧客番号が含まれるフィールドのみ編集不可にする（esa-モーダルと同パターン）
      var lockedStyle = { background: '#f5f5f5', color: '#888', cursor: 'not-allowed' };
      var unlockedStyle = { background: '', color: '', cursor: '' };
      var lockedNotice = '<p class="js-orderer-locked-notice" style="margin-top:6px;font-size:1.2rem;color:#666;">Paidサービスをご利用のお客様は編集できません。カスタマーサポートまでお問い合わせください。</p>';
      $modal.find('.js-orderer-locked-notice').remove();

      var $oAddr1 = $modal.find('#orderer-address1-num');
      var $oAddr2 = $modal.find('#orderer-address2');
      if ((o.address1 || '').indexOf('顧客番号') !== -1) {
        $oAddr1.prop('readonly', true).css(lockedStyle).data('lockedValue', o.address1);
        $oAddr1.closest('.p-row-item').append(lockedNotice);
      } else {
        $oAddr1.prop('readonly', false).css(unlockedStyle).removeData('lockedValue');
      }
      if ((o.address2 || '').indexOf('顧客番号') !== -1) {
        $oAddr2.prop('readonly', true).css(lockedStyle).data('lockedValue', o.address2);
        $oAddr2.closest('.p-row-item').append(lockedNotice);
      } else {
        $oAddr2.prop('readonly', false).css(unlockedStyle).removeData('lockedValue');
      }

      // Single consolidated API call only when we're actually missing data:
      //   - prefectureCode empty (recovery), OR
      //   - no cached town/num split AND address1 exists (need to split)
      var normalizedZip = String(o.zip || '').replace(/\D/g, '');
      var hasValidZip = /^\d{7}$/.test(normalizedZip);
      var needsPrefRecovery = hasValidZip && !o.prefectureCode;
      var needsAddressSplit = hasValidZip && !hasCachedSplit && !!o.address1;

      if (!needsPrefRecovery && !needsAddressSplit) return; // nothing to fetch

      this._beginOrdererZipLookup();
      $.ajax({
        url: '/apps/ping/postal-codes/search',
        dataType: 'jsonp',
        data: { zipcode: normalizedZip },
        timeout: 3000
      }).then(function(res) {
        if (!(res && res.status === 200 && res.results && res.results.length > 0)) return;

        // For multi-result postal codes (esp. jigyosyo), prefer the candidate
        // whose address3 is a prefix of the saved o.address1. Falls back to
        // showing the picker when no candidate matches, or to the first entry
        // for single-result codes.
        var matched = null;
        if (needsAddressSplit && o.address1) {
          for (var i = 0; i < res.results.length; i++) {
            var a3 = res.results[i].address3 || '';
            if (a3 && o.address1.indexOf(a3) === 0) { matched = res.results[i]; break; }
          }
        }

        var applyResult = function(r) {
          if (needsPrefRecovery) {
            var code = PREFECTURE_MAP[r.address1];
            if (code && !$('#orderer-prefecture').val()) {
              $('#orderer-prefecture').val(code);
            }
            if (!$('#orderer-city').val()) $('#orderer-city').val(r.address2 || '');
          }
          if (needsAddressSplit) {
            var town = r.address3 || '';
            if (town && o.address1.indexOf(town) === 0) {
              $('#orderer-address1-town').val(town);
              $('#orderer-address1-num').val(o.address1.slice(town.length));
            } else {
              $('#orderer-address1-town').val(town);
              $('#orderer-address1-num').val(o.address1);
            }
            self._updateTownWidth($('#orderer-address1-town'));
          }
        };

        if (matched) {
          applyResult(matched);
        } else if (res.results.length > 1) {
          self.showZipCandidatePicker(res.results, applyResult);
        } else {
          applyResult(res.results[0]);
        }
      }).always(function() {
        self._endOrdererZipLookup();
      });
    },

    /**
     * Finalize orderer registration after API call (or locally for guests)
     */
    completeOrdererRegistration: function(newAddr) {
      if (!newAddr.prefectureCode) {
      }
      // Re-enable submit button so a future re-open of the modal starts in a fresh state
      $('#modalAddOrderer [data-add-orderer]').prop('disabled', false);
      this.orderer = {
        id: newAddr.id,
        lastName: newAddr.lastName || '',
        firstName: newAddr.firstName || '',
        zip: newAddr.zip || '',
        prefecture: newAddr.prefecture || '',
        prefectureCode: newAddr.prefectureCode || '',
        city: newAddr.city || '',
        address1: newAddr.address1 || '',
        address1Town: newAddr.address1Town || '',
        address1Num: newAddr.address1Num || '',
        address2: newAddr.address2 || '',
        company: newAddr.company || '',
        phone: newAddr.phone || ''
      };
      localStorage.setItem('shipping_orderer', JSON.stringify(this.orderer));

      // Copy orderer to singleRecipient if not set yet (guest/new user first-access flow)
      var self = this;
      if (!this.singleRecipient || !this.singleRecipient.lastName) {
        this.singleRecipient = {
          id: newAddr.id,
          lastName: newAddr.lastName || '',
          firstName: newAddr.firstName || '',
          zip: newAddr.zip || '',
          prefecture: newAddr.prefecture || '',
          prefectureCode: newAddr.prefectureCode || '',
          city: newAddr.city || '',
          address1: newAddr.address1 || '',
          address2: newAddr.address2 || '',
          company: newAddr.company || '',
          phone: newAddr.phone || '',
          items: {},
          deliverySettings: {}
        };
        // For guests: also add to savedAddresses in localStorage
        if (!window.shippingCustomer) {
          var alreadyExists = this.savedAddresses.some(function(a) { return a.id === newAddr.id; });
          if (!alreadyExists) {
            this.savedAddresses.push(newAddr);
            localStorage.setItem('shipping_addresses', JSON.stringify(this.savedAddresses));
          }
        }
        this.saveData();
      }

      // 注文者が変わったらご依頼主も必ず再判定する。
      // お届け先が既に入っている場合（＝上の if を通らない場合）でも呼ぶこと。
      // ここを if の中に閉じ込めていたため、「お届け先＝注文者」でご依頼主がヨックモックに
      // なった後で注文者だけを変更しても再判定されず、先様届けなのに依頼主がヨックモックの
      // まま確定してしまっていた。
      this.updateSenderByRecipient();

      this.isFirstAccessModal = false;
      this.closeModal();
      this.renderOrdererInfo();
      this.renderSenderInfo();
      this.renderCartItems();
      this.renderSavedAddresses();
      this.updateSummary();
      this.updateCheckoutButtonState();
      this.debugRefresh();
    },

    /**
     * Complete the add new address flow after API call (or if API not available)
     * @param {Object} newAddr - The new address object
     */
    completeAddNewAddress: function(newAddr) {
      // Capture whether this add was initiated from the sender (ご依頼主) change flow.
      // selectingSender is set true by openSenderSelectModal and is NOT reset when the
      // "新しい住所を追加" js-modal button merely swaps the active modal (it only toggles
      // the .active class, it does not call closeModal). So it is still true here, and it
      // is the reliable discriminator. (openedAddAddressFromChangeModal is unreliable: the
      // change-modal add button lacks the .add-new class its handler selector requires, so
      // that flag never flips.) Read before closeModal() → restoreSenderSelectModal()
      // resets it. In this flow the new address must become the sender — NOT a delivery
      // recipient (お届け先); otherwise お届け先 is silently overwritten.
      var isSenderContext = this.selectingSender;

      // Add to saved addresses (addressList) only if not already present (e.g. synced from API)
      var alreadyExists = this.savedAddresses.some(function(a) { return a.id === newAddr.id; });
      if (!alreadyExists) {
        this.savedAddresses.push(newAddr);
      }

      // First access (guest or logged-in without address): this address is the orderer (注文者)
      if (!this.orderer || !this.orderer.lastName) {
        if (!newAddr.prefectureCode) {
        }
        this.orderer = {
          id: newAddr.id,
          lastName: newAddr.lastName || '',
          firstName: newAddr.firstName || '',
          zip: newAddr.zip || '',
          prefecture: newAddr.prefecture || '',
          prefectureCode: newAddr.prefectureCode || '',
          city: newAddr.city || '',
          address1: newAddr.address1 || '',
          address2: newAddr.address2 || '',
          company: newAddr.company || '',
          phone: newAddr.phone || ''
        };
      }

      // Only auto-add to recipients if NOT opened from modalBulkListAdd
      // (When from bulk list, user should select the address manually)
      // Also skip when adding from the sender (ご依頼主) change flow — there the new
      // address is the sender, and overwriting the recipient would change お届け先.
      if (!this.openedAddAddressFromBulkList && !isSenderContext) {
        // Create recipient object with additional fields
        var newRecipient = {
          ...newAddr,
          items: {},
          wrapping: { bag: 'off', type: 'paper' },
          deliverySettings: {
            ambient: { date: '', time: '' },
            frozen: { date: '', time: '' },
            mail: { date: '', time: '' }
          }
        };

        // In single mode, replace singleRecipient; in multi mode, add to multiRecipients
        if (this.currentMode === 'single') {
          // Preserve existing delivery settings (mirror changeRecipientAddress multi-mode logic)
          if (this.singleRecipient && this.singleRecipient.deliverySettings) {
            newRecipient.deliverySettings = this.singleRecipient.deliverySettings;
          }
          this.singleRecipient = newRecipient;
        } else {
          if ((this.multiRecipients || []).length >= (this.MAX_MULTI_RECIPIENTS || 50)) {
            this.showMultiRecipientLimitMessage();
          } else {
            // multi は内部IDとアドレス帳IDを分離する（重複お届け先対策）
            newRecipient.id = this.generateRecipientId();
            newRecipient.addressId = newAddr.id;
            this.multiRecipients.push(newRecipient);
          }
        }
      }

      // Sender (ご依頼主) flow: assign the new address as the sender and mark it as
      // manually chosen so updateSenderByRecipient() won't auto-revert it below.
      if (isSenderContext) {
        this.sender = {
          id: newAddr.id,
          lastName: newAddr.lastName || '',
          firstName: newAddr.firstName || '',
          zip: newAddr.zip || '',
          prefecture: newAddr.prefecture || '',
          prefectureCode: newAddr.prefectureCode || '',
          city: newAddr.city || '',
          address1: newAddr.address1 || '',
          address2: newAddr.address2 || '',
          company: newAddr.company || '',
          phone: newAddr.phone || ''
        };
        this.senderManuallyEdited = true;
      }

      this.saveData();
      this.renderOrdererInfo();
      this.renderSavedAddresses();
      this.clearDeliveryOptionsCache(); // Clear cache so new postal code is used
      this.updateSenderByRecipient();
      this.renderCartItems();
      this.updateSummary();

      // First-access flag no longer controls outside-close (modal is always
      // locked via HTML class); just clear the flag here.
      if (this.isFirstAccessModal) {
        this.isFirstAccessModal = false;
      }

      this.closeModal();

      // Reset bulk setup mode
      this.isBulkSetupMode = false;

      // Reopen source modal after adding new address
      if (this.openedAddAddressFromChangeModal) {
        this.openedAddAddressFromChangeModal = false;
        // Sender flow: the new address is already confirmed as the sender, so close
        // out instead of reopening the change-address list.
        if (!isSenderContext) {
          this.renderChangeAddressList();
          this.openModal('modalChangeAddress');
        }
      } else if (this.openedAddAddressFromBulkList) {
        this.openedAddAddressFromBulkList = false;
        this.renderSavedAddresses();
        this.openModal('modalBulkListAdd');
      }

      // Clear form and re-enable submit button
      $('#add-lastName, #add-firstName, #add-zipCode, #add-city, #add-address1-town, #add-address1-num, #add-address2, #add-company, #add-phone').val('');
      $('#add-prefecture').val('');
      this.setAddressPersonType('add', 'individual');
      $('#modalAddAddress [data-add-address]').prop('disabled', false);
    },

    /**
     * Open edit saved address modal (#modalEditSavedAddress)
     * Pre-fills the form with the address's current data.
     */
    openEditSavedAddressModal: function(addressId) {
      var addr = (this.savedAddresses || []).find(function(a) { return a.id === addressId; });
      if (!addr) return;
      var self = this;

      this.editingAddressId = addressId;

      // Record the currently open parent modal, then swap to edit modal.
      // Page stays frozen — _savedScrollPos remains intact from when parent was opened.
      var $parentModal = $('.p-modal.active');
      this.openedEditAddressFrom = $parentModal.attr('id') || null;
      $parentModal.removeClass('active');

      var $modal = $('#modalEditSavedAddress');
      var isCorporate = !!addr.company;

      // Sync fill from stored data
      $('#esa-lastName').val(addr.lastName || '');
      $('#esa-firstName').val(addr.firstName || '');
      $('#esa-zipCode').val(addr.zip || '');
      $('#esa-prefecture').val(addr.prefectureCode || '');
      $('#esa-city').val(addr.city || '');
      $('#esa-address2').val(addr.address2 || '');
      $('#esa-phone').val(addr.phone || '');
      $('#esa-company').val(addr.company || '');
      this.setAddressPersonType('esa', isCorporate ? 'corporate' : 'individual');
      $('#esa-company-row').toggle(isCorporate);

      // Town/num: use cached split if we have one, otherwise full address1 into num.
      var hasCachedSplit = typeof addr.address1Town === 'string' || typeof addr.address1Num === 'string';
      $('#esa-address1-town').val(hasCachedSplit ? (addr.address1Town || '') : '');
      $('#esa-address1-num').val(hasCachedSplit ? (addr.address1Num || '') : (addr.address1 || ''));
      this._updateTownWidth($('#esa-address1-town'));

      // Clear previous errors (validation + inline + cached lookup)
      $modal.find('.is-error').removeClass('is-error');
      $modal.find('.p-form-error-txt').remove();
      $modal.find('.p-combined-error').remove();
      $modal.find('.zip-lookup-error').hide();
      $('#esa-zipCode').removeData('lastLookupZip');
      $modal.find('[data-save-edited-address]').prop('disabled', false);

      // 顧客番号が含まれるフィールドのみ編集不可にする
      var lockedStyle = { background: '#f5f5f5', color: '#888', cursor: 'not-allowed' };
      var unlockedStyle = { background: '', color: '', cursor: '' };
      var lockedNotice = '<p class="js-esa-locked-notice" style="margin-top:6px;font-size:1.2rem;color:#666;">Paidサービスをご利用のお客様は編集できません。カスタマーサポートまでお問い合わせください。</p>';
      $modal.find('.js-esa-locked-notice').remove();

      var $addr1 = $modal.find('#esa-address1-num');
      var $addr2 = $modal.find('#esa-address2');
      if ((addr.address1 || '').indexOf('顧客番号') !== -1) {
        $addr1.prop('readonly', true).css(lockedStyle).data('lockedValue', addr.address1);
        $addr1.closest('.p-row-item').append(lockedNotice);
      } else {
        $addr1.prop('readonly', false).css(unlockedStyle).removeData('lockedValue');
      }
      if ((addr.address2 || '').indexOf('顧客番号') !== -1) {
        $addr2.prop('readonly', true).css(lockedStyle).data('lockedValue', addr.address2);
        $addr2.closest('.p-row-item').append(lockedNotice);
      } else {
        $addr2.prop('readonly', false).css(unlockedStyle).removeData('lockedValue');
      }

      $modal.addClass('active');

      // Single consolidated API call only when data is missing.
      var normalizedZip = String(addr.zip || '').replace(/\D/g, '');
      var hasValidZip = /^\d{7}$/.test(normalizedZip);
      var needsPrefRecovery = hasValidZip && !addr.prefectureCode;
      var needsAddressSplit = hasValidZip && !hasCachedSplit && !!addr.address1;

      if (!needsPrefRecovery && !needsAddressSplit) return;

      this._beginModalZipLookup('esa');
      $.ajax({
        url: '/apps/ping/postal-codes/search',
        dataType: 'jsonp',
        data: { zipcode: normalizedZip },
        timeout: 3000
      }).then(function(res) {
        if (!(res && res.status === 200 && res.results && res.results.length > 0)) return;

        var matched = null;
        if (needsAddressSplit && addr.address1) {
          for (var i = 0; i < res.results.length; i++) {
            var a3 = res.results[i].address3 || '';
            if (a3 && addr.address1.indexOf(a3) === 0) { matched = res.results[i]; break; }
          }
        }

        var applyResult = function(r) {
          if (needsPrefRecovery) {
            var code = PREFECTURE_MAP[r.address1];
            if (code && !$('#esa-prefecture').val()) $('#esa-prefecture').val(code);
            if (!$('#esa-city').val()) $('#esa-city').val(r.address2 || '');
          }
          if (needsAddressSplit) {
            var town = r.address3 || '';
            if (town && addr.address1.indexOf(town) === 0) {
              $('#esa-address1-town').val(town);
              $('#esa-address1-num').val(addr.address1.slice(town.length));
            } else {
              $('#esa-address1-town').val(town);
              $('#esa-address1-num').val(addr.address1);
            }
            self._updateTownWidth($('#esa-address1-town'));
          }
        };

        if (matched) {
          applyResult(matched);
        } else if (res.results.length > 1) {
          self.showZipCandidatePicker(res.results, applyResult);
        } else {
          applyResult(res.results[0]);
        }
      }).always(function() {
        self._endModalZipLookup('esa');
      });
    },

    /**
     * Reopen the parent modal after the edit modal is closed.
     * Called both from the ✕ handler (via setTimeout) and save success.
     */
    _reopenEditAddressParentModal: function(fromModal) {
      if (fromModal === 'modalBulkListAdd') {
        this.renderSavedAddresses();
      } else {
        // #modalChangeAddress — render whichever list context was active
        if (this.selectingSender) {
          this.renderSenderAddressList();
        } else if (this.selectingRecipient) {
          this.renderRecipientAddressList();
        } else {
          this.renderChangeAddressList();
        }
      }
      this.openModal(fromModal);
    },

    /**
     * Save edited saved address via PUT API, then update savedAddresses and re-render.
     */
    saveEditedSavedAddress: function() {
      var self = this;
      this.ensureZipLookupThenCall('esa', function() {
        self._doSaveEditedSavedAddress();
      });
    },

    _doSaveEditedSavedAddress: function() {
      var self = this;
      var addressId = this.editingAddressId;
      if (!addressId) return;

      // 顧客番号ロックされたフィールドは元の値を使う
      var townVal = $('#esa-address1-town').val() || '';
      var numVal = $('#esa-address1-num').val() || '';
      var lockedAddr1 = $('#esa-address1-num').data('lockedValue');
      var addr1Val = lockedAddr1 || (townVal + numVal).trim();
      var addr2Val = $('#esa-address2').data('lockedValue') || $('#esa-address2').val();

      var editedAddr = this.normalizeAddressPayload({
        id: addressId,
        lastName: $('#esa-lastName').val(),
        firstName: $('#esa-firstName').val(),
        zip: String($('#esa-zipCode').val() || '').replace(/\D/g, '').slice(0, 7),
        prefecture: $('#esa-prefecture option:selected').text(),
        prefectureCode: $('#esa-prefecture').val(),
        city: $('#esa-city').val(),
        address1: addr1Val,
        address1Town: lockedAddr1 ? '' : townVal,
        address1Num: lockedAddr1 ? '' : numVal,
        address2: addr2Val,
        company: $('#esa-company').val(),
        personType: this.getAddressPersonType('esa'),
        phone: $('#esa-phone').val()
      });

      if (!this.validateAddressFields(editedAddr, 'esa')) {
        return;
      }

      var $btn = $('#modalEditSavedAddress [data-save-edited-address]');
      $btn.prop('disabled', true);

      var isLoggedIn = !!window.shippingCustomer;
      var isApiAddr = addressId && addressId.indexOf('addr_') !== 0;

      // Helper: update local data and UI after save
      function applyLocalUpdate(updated) {
        var idx = (self.savedAddresses || []).findIndex(function(a) { return a.id === addressId; });
        if (idx !== -1) {
          var existing = self.savedAddresses[idx];
          self.savedAddresses[idx] = {
            id: updated.id || addressId,
            lastName: updated.lastName || editedAddr.lastName,
            firstName: updated.firstName || editedAddr.firstName,
            zip: (updated.zip || editedAddr.zip || '').replace(/-/g, ''),
            prefecture: updated.province || updated.prefecture || editedAddr.prefecture,
            prefectureCode: updated.provinceCode || updated.prefectureCode || editedAddr.prefectureCode,
            city: updated.city || editedAddr.city,
            address1: updated.address1 || editedAddr.address1,
            address1Town: editedAddr.address1Town || '',
            address1Num: editedAddr.address1Num || '',
            address2: updated.address2 !== undefined ? updated.address2 : (editedAddr.address2 || ''),
            company: updated.company !== undefined ? updated.company : (editedAddr.company || ''),
            phone: self.normalizePhoneInput(updated.phone || editedAddr.phone || ''),
            isDefault: existing.isDefault || false
          };
        }
        localStorage.setItem('shipping_addresses', JSON.stringify(self.savedAddresses));
        self.syncRecipientsFromSavedAddresses();
        self.saveData();

        var fromModal = self.openedEditAddressFrom;
        self.editingAddressId = null;
        self.openedEditAddressFrom = null;
        $('#modalEditSavedAddress').removeClass('active');

        if (fromModal) {
          if (fromModal === 'modalBulkListAdd') {
            self.renderSavedAddresses();
          } else {
            if (self.selectingSender) {
              self.renderSenderAddressList();
            } else if (self.selectingRecipient) {
              self.renderRecipientAddressList();
            } else {
              self.renderChangeAddressList();
            }
          }
          $('#' + fromModal).addClass('active');
        }
        $btn.prop('disabled', false);
      }

      if (!isLoggedIn || !isApiAddr) {
        // Guest or locally-added address: update local only
        applyLocalUpdate(editedAddr);
        return;
      }

      // Logged-in user with API address: update via API
      var match = addressId.match(/MailingAddress\/(\d+)/);
      var numericId = match ? match[1] : addressId;
      var url = '/apps/ping/customers/addresses/' + numericId;

      fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          lastName: editedAddr.lastName,
          firstName: editedAddr.firstName,
          zip: editedAddr.zip,
          province: editedAddr.prefecture,
          city: editedAddr.city,
          address1: editedAddr.address1,
          address2: editedAddr.address2 || '',
          company: editedAddr.company || '',
          phone: editedAddr.phone || ''
        })
      }).then(function(response) {
        return response.json().then(function(data) {
          if (!response.ok) {
            var err = new Error('update failed');
            err.status = response.status;
            err.data = data;
            throw err;
          }
          return data;
        });
      }).then(function(data) {
        applyLocalUpdate(data.address || {});
      }).catch(function(err) {
        $btn.prop('disabled', false);
        if (err.status === 422 && err.data) {
          var displayMsg;
          if (err.data.errors && err.data.errors.length > 0) {
            displayMsg = err.data.errors.map(function(e) { return e.message || JSON.stringify(e); }).join('\n');
          } else if (err.data.message) {
            displayMsg = err.data.message;
          }
          displayMsg = displayMsg || '入力内容をご確認ください。';
          if (displayMsg === 'Address already exists') {
            displayMsg = '「会員様ご住所」または、「アドレス帳」にすでに同じ情報が登録されています。重複登録はできません。';
          }
          alert(displayMsg);
        } else {
          alert('住所の更新に失敗しました。しばらくしてからお試しください。');
        }
      });
    },

    /**
     * Open edit recipient modal (single mode)
     */
    openEditRecipientModal: function() {
      var r = this.singleRecipient || {};

      // Populate form with current recipient data
      $('#edit-recipient-lastName').val(r.lastName || '');
      $('#edit-recipient-firstName').val(r.firstName || '');
      $('#edit-recipient-zipCode').val(r.zip || '');
      $('#edit-recipient-prefecture').val(r.prefectureCode || '');
      $('#edit-recipient-city').val(r.city || '');
      this.prefillAddress1Split(r.zip, r.address1, 'edit-recipient-address1-town', 'edit-recipient-address1-num');
      $('#edit-recipient-address2').val(r.address2 || '');
      $('#edit-recipient-company').val(r.company || '');
      $('#edit-recipient-phone').val(r.phone || '');
      this.setAddressPersonType('edit-recipient', r.company ? 'corporate' : 'individual');

      // Open modal
      var scrollpos = $(window).scrollTop();
      this._savedScrollPos = scrollpos;
      $('html').addClass('freezePage');
      $('body').addClass('freezePage').css({ 'top': -scrollpos });
      $('#modalEditRecipient').addClass('active');
    },

    /**
     * Save edited recipient (single mode)
     */
    saveEditedRecipient: function() {
      var self = this;
      this.ensureZipLookupThenCall('edit-recipient', function() {
        self._doSaveEditedRecipient();
      });
    },

    _doSaveEditedRecipient: function() {
      var currentRecipient = this.singleRecipient;
      var editedAddr = this.normalizeAddressPayload({
        id: (currentRecipient && currentRecipient.id) || 'addr_' + Date.now(),
        lastName: $('#edit-recipient-lastName').val(),
        firstName: $('#edit-recipient-firstName').val(),
        zip: String($('#edit-recipient-zipCode').val() || '').replace(/\D/g, '').slice(0, 7),
        prefecture: $('#edit-recipient-prefecture option:selected').text(),
        prefectureCode: $('#edit-recipient-prefecture').val(),
        city: $('#edit-recipient-city').val(),
        address1: ($('#edit-recipient-address1-town').val() + $('#edit-recipient-address1-num').val()).trim(),
        address2: $('#edit-recipient-address2').val(),
        company: $('#edit-recipient-company').val(),
        personType: this.getAddressPersonType('edit-recipient'),
        phone: $('#edit-recipient-phone').val()
      });

      // Reflect stripped zip back to the input field
      $('#edit-recipient-zipCode').val(editedAddr.zip);

      // Validate
      if (!this.validateAddressFields(editedAddr, 'edit-recipient')) {
        return;
      }

      // Update or create recipient
      var defaultDeliverySettings = {
        ambient: { date: '', time: '' },
        frozen: { date: '', time: '' },
        mail: { date: '', time: '' }
      };
      this.singleRecipient = {
        ...editedAddr,
        items: (currentRecipient && currentRecipient.items) || {},
        wrapping: (currentRecipient && currentRecipient.wrapping) || { bag: 'off', type: 'paper' },
        deliverySettings: (currentRecipient && currentRecipient.deliverySettings) || defaultDeliverySettings
      };

      // If no orderer yet (guest first address), use this address as orderer
      if (!this.orderer || !this.orderer.lastName) {
        if (!editedAddr.prefectureCode) {
        }
        this.orderer = {
          id: editedAddr.id,
          lastName: editedAddr.lastName || '',
          firstName: editedAddr.firstName || '',
          zip: editedAddr.zip || '',
          prefecture: editedAddr.prefecture || '',
          prefectureCode: editedAddr.prefectureCode || '',
          city: editedAddr.city || '',
          address1: editedAddr.address1 || '',
          address2: editedAddr.address2 || '',
          company: editedAddr.company || '',
          phone: editedAddr.phone || ''
        };
      }

      // Add to savedAddresses if not already there
      var addrId = editedAddr.id;
      var existing = this.savedAddresses.find(function(a) { return a.id === addrId; });
      if (!existing) {
        this.savedAddresses.push(editedAddr);
      }

      this.saveData();
      this.clearDeliveryOptionsCache(); // Clear cache so new postal code is used
      this.renderOrdererInfo();
      this.updateSenderByRecipient();
      this.renderCartItems();
      this.updateSummary();
      this.closeModal();
    },

    /**
     * Add selected addresses as recipients
     */
    addSelectedAddresses: function() {
      var self = this;
      var selected = [];
      var maxRecipients = this.MAX_MULTI_RECIPIENTS || 50;

      // Get saved addresses
      var addresses = this.savedAddresses || [];

      $('[data-saved-addresses] input:checked').each(function() {
        var id = $(this).data('address-id');
        var addr = addresses.find(function(a) { return a.id === id; });
        if (addr) {
          // Check if this address already exists in multiRecipients (for edit mode)
          var existingRecipient = self.multiRecipients.find(function(r) { return self.getRecipientAddressId(r) === id; });
          if (existingRecipient) {
            // Preserve existing items, wrapping, and delivery settings
            selected.push({
              ...addr,
              id: existingRecipient.id,
              addressId: id,
              items: existingRecipient.items || {},
              wrapping: existingRecipient.wrapping || { bag: 'off', type: 'paper' },
              deliverySettings: existingRecipient.deliverySettings || {
                ambient: { date: '', time: '' },
                frozen: { date: '', time: '' },
                mail: { date: '', time: '' }
              }
            });
          } else {
            // New address - initialize with empty items
            selected.push({
              ...addr,
              id: self.generateRecipientId(),
              addressId: id,
              items: {},
              wrapping: { bag: 'off', type: 'paper' },
              deliverySettings: {
                ambient: { date: '', time: '' },
                frozen: { date: '', time: '' },
                mail: { date: '', time: '' }
              }
            });
          }
        }
      });

      if (this.currentMode === 'multi') {
        if (this.isBulkSetupMode || this.isEditSetupMode) {
          if (selected.length > maxRecipients) {
            this.showMultiRecipientLimitMessage();
            return;
          }
        } else {
          var existingIds = {};
          (this.multiRecipients || []).forEach(function(r) {
            var addressId = self.getRecipientAddressId(r);
            if (addressId) existingIds[addressId] = true;
          });
          var projectedCount = (this.multiRecipients || []).length;
          selected.forEach(function(addr) {
            if (!existingIds[addr.addressId]) {
              existingIds[addr.addressId] = true;
              projectedCount++;
            }
          });
          if (projectedCount > maxRecipients) {
            this.showMultiRecipientLimitMessage();
            return;
          }
        }
      }

      if (this.isBulkSetupMode && this.currentMode === 'multi') {
        // Bulk setup: don't add to multiRecipients yet, just pass to modalSelectAddress
        // Only saveSelectAddressProducts will commit to multiRecipients
        this.isBulkSetupMode = false;
        $('.p-modal.active').removeClass('active');
        this.openSelectAddressModal(selected);
      } else if (this.isEditSetupMode && this.currentMode === 'multi') {
        // Edit mode: open modalSelectAddress with existing data preserved
        this.isEditSetupMode = false;
        $('.p-modal.active').removeClass('active');
        this.openSelectAddressModal(selected, true); // true = edit mode
      } else {
        // Normal flow: add addresses and render
        if (this.currentMode === 'single') {
          // single は従来どおり id＝アドレス帳の住所ID（重複が構造上起きないため分離不要）
          var singleSelected = selected.length > 0 ? selected[0] : null;
          if (singleSelected && singleSelected.addressId) {
            singleSelected.id = singleSelected.addressId;
          }
          this.singleRecipient = singleSelected;
        } else {
          selected.forEach(function(addr) {
            if (!self.multiRecipients.some(function(r) { return r.id === addr.id; })) {
              self.multiRecipients.push(addr);
            }
          });
        }
        this.saveData();
        this.clearDeliveryOptionsCache();
        this.updateSenderByRecipient();
        this.renderCartItems();
        this.updateSummary();
        this.closeModal();
      }

      // Update target count in modals
      $('[data-target-count], [data-wrapping-target-count]').text(this.getRecipients().length);
    },

    /**
     * Open change address modal for a specific recipient (multi mode)
     * Shows address list with radio buttons for single selection
     */
    openChangeAddressModal: function(recipientId) {
      this.editingRecipientId = recipientId;
      // Track if opened from modalSelectAddress (商品の振り分け) so we can return to it after save
      this.editingRecipientFromSelectAddress = $('#modalSelectAddress').hasClass('active');
      // Hide modalSelectAddress so only modalChangeAddress is visible
      if (this.editingRecipientFromSelectAddress) {
        $('#modalSelectAddress').removeClass('active');
      }
      this.restoreChangeAddressModalLayout();

      // Render radio list in the change address modal
      this.renderChangeAddressList();

      // Open modal
      var $modal = $('#modalChangeAddress');
      var scrollpos = $(window).scrollTop();
      this._savedScrollPos = scrollpos;
      $('html').addClass('freezePage');
      $('body').addClass('freezePage').css({ 'top': -scrollpos });
      $modal.addClass('active');
    },

    /**
     * Change recipient address (multi mode)
     * Replaces the editing recipient's address with the selected one
     */
    changeRecipientAddress: function() {
      var self = this;

      // Get selected address from change address modal (radio, so only one)
      var selectedId = $('[data-change-address-list] input:checked').data('address-id');
      if (!selectedId) {
        alert('住所を選択してください。');
        return;
      }

      // Find the address from saved addresses
      var addresses = this.savedAddresses || [];

      var selectedAddr = addresses.find(function(a) { return a.id === selectedId; });
      if (!selectedAddr) {
        alert('選択された住所が見つかりません。');
        return;
      }

      // If editing from within modalSelectAddress (商品の振り分け), update bulkSelectedRecipients and return to that modal
      if (this.editingRecipientFromSelectAddress) {
        var oldId = this.editingRecipientId;
        var bulkIndex = this.bulkSelectedRecipients.findIndex(function(r) { return r.id === oldId; });
        if (bulkIndex !== -1) {
          // 内部IDを維持したまま住所だけ差し替える（tempAddrSelections のキーもそのまま有効）
          this.bulkSelectedRecipients[bulkIndex] = Object.assign({}, this.bulkSelectedRecipients[bulkIndex], selectedAddr, {
            id: oldId,
            addressId: selectedAddr.id
          });
        }
        this.editingRecipientId = null;
        this.editingRecipientFromSelectAddress = false;
        // Close modalChangeAddress and reopen modalSelectAddress
        $('#modalChangeAddress').removeClass('active');
        $('#modalSelectAddress').addClass('active');
        // Re-render the product distribution modal with updated address
        this.renderSelectAddressProducts();
        return;
      }

      // Find and update the recipient in multiRecipients (this function is only used in multi mode)
      var recipientIndex = this.multiRecipients.findIndex(function(r) { return r.id === self.editingRecipientId; });
      if (recipientIndex !== -1) {
        // Keep existing items, wrapping, delivery settings
        var existingRecipient = this.multiRecipients[recipientIndex];
        var defaultDeliverySettings = {
          ambient: { date: '', time: '' },
          frozen: { date: '', time: '' },
          mail: { date: '', time: '' }
        };
        this.multiRecipients[recipientIndex] = {
          ...selectedAddr,
          // 内部IDは維持する。selectedAddr.id（アドレス帳の住所ID）を id に使うと
          // 同じ住所を選んだ別のお届け先とIDが重複してしまう
          id: existingRecipient.id,
          addressId: selectedAddr.id,
          items: existingRecipient.items || {},
          wrapping: existingRecipient.wrapping || { bag: 'off', type: 'paper' },
          deliverySettings: existingRecipient.deliverySettings || defaultDeliverySettings
        };
      }

      // Reset editing state
      this.editingRecipientId = null;

      this.saveData();
      this.clearDeliveryOptionsCache(); // Clear cache so new postal code is used
      this.updateSenderByRecipient();
      this.renderCartItems();
      this.updateSummary();
      this.closeModal();
    },

    /**
     * Restore the add address modal to its original state
     */
    restoreAddAddressModal: function() {
      var $modal = $('#modalBulkListAdd');

      // Restore title
      $modal.find('.p-modal-ttl .p-bulk-modal__title').text('お届け先を追加');

      // Restore button
      var $submitBtn = $modal.find('[data-change-recipient-address]');
      $submitBtn.html('商品の振り分け<i class="c-btn-big__ico c-ico-arrow-01"><svg><use xlink:href="#ico-arrow01"></use></svg></i>');
      $submitBtn.attr('data-add-selected-addresses', '').removeAttr('data-change-recipient-address');
    },

    /**
     * Open select products modal for a recipient
     */
    openSelectProductsModal: function(recipientId) {
      var self = this;
      this.selectingProductsRecipientId = recipientId;

      // Find recipient (this function is only used in multi mode)
      var recipient = this.multiRecipients.find(function(r) { return r.id === recipientId; });
      if (!recipient) {
        console.warn('Recipient not found:', recipientId);
        return;
      }

      // Initialize temp selections from current recipient items
      this.tempProductSelections = {};
      if (recipient.items) {
        for (var key in recipient.items) {
          this.tempProductSelections[key] = recipient.items[key];
        }
      }

      // Update modal title
      var recipientName = (recipient.lastName || '') + ' ' + (recipient.firstName || '');
      $('[data-select-products-title]').text(recipientName + '様に送る商品を選択してください');
      $('[data-select-products-desc]').text(recipientName + '様に送る商品を選択してください');

      // Render product list
      this.renderSelectProductsList();
      this.updateSelectProductsSummary();

      // Open modal
      var scrollpos = $(window).scrollTop();
      this._savedScrollPos = scrollpos;
      $('html').addClass('freezePage');
      $('body').addClass('freezePage').css({ 'top': -scrollpos });
      $('#modalSelectProducts').addClass('active');
    },

    /**
     * Render the product list in select products modal
     */
    renderSelectProductsList: function() {
      var self = this;
      var items = this.cart.items || [];
      var $container = $('[data-select-products-list]');

      if (items.length === 0) {
        $container.html('<p class="c-text-muted">カートに商品がありません。</p>');
        return;
      }

      var html = '';
      items.forEach(function(item) {
        var originalPrice = (item.original_price != null ? item.original_price : item.price) / 100;
        var finalPrice = (item.final_price != null ? item.final_price : item.price) / 100;
        var hasItemDiscount = finalPrice < originalPrice;
        var currentQty = self.tempProductSelections[item.key] || 0;
        var maxQty = self.getAvailableQuantityForItem(item.key);
        var shippingType = self.getItemShippingType(item);

        // Temperature tag
        var tempTag = '常温';
        var tempClass = 'c-badge--green';
        if (shippingType === 'frozen') {
          tempTag = '冷凍';
          tempClass = 'c-badge--blue02';
        } else if (shippingType === 'mail') {
          tempTag = 'メール便';
          tempClass = 'c-badge--mail';
        }

        html += '<div class="cart-item" data-item-key="' + item.key + '" data-max-qty="' + maxQty + '">';
        html += '<div class="cart-item__thumb">';
        html += '<img src="' + (item.image || window._defaultProductImage) + '" alt="' + self.escapeHtml(item.product_title) + '">';
        html += '</div>';
        html += '<div class="cart-item__main">';
        html += '<div class="cart-item__top">';
        html += '<span class="c-badge ' + tempClass + '">' + tempTag + '</span>';
        html += '<h4 class="cart-item__title">' + self.escapeHtml(item.product_title) + '</h4>';
        html += '</div>';
        html += '</div>';
        html += '<div class="cart-item__last">';
        html += '<div class="d-flex">';
        html += '<div class="cart-item__unit" data-unit="' + originalPrice + '"' + (hasItemDiscount ? ' data-discounted-unit="' + finalPrice + '"' : '') + '>';
        if (hasItemDiscount) {
          html += renderDiscountPricePair(formatMoneyYen(originalPrice), formatMoneyYen(finalPrice));
        } else {
          html += '<span class="price ">' + formatMoneyYen(originalPrice) + '</span>';
        }
        html += '</div>';
        html += '<div class="qty qty--small">';
        html += '<button type="button" class="qty__btn' + (currentQty <= 0 ? ' is-disabled' : '') + '" data-select-qty-btn data-step="-1" aria-label="decrease"' + (currentQty <= 0 ? ' disabled' : '') + '>−</button>';
        html += '<input class="qty__input" type="number" value="' + currentQty + '" inputmode="numeric" aria-label="quantity" data-select-qty-input min="0">';
        html += '<button type="button" class="qty__btn' + (currentQty >= maxQty ? ' is-disabled' : '') + '" data-select-qty-btn data-step="1" aria-label="increase"' + (currentQty >= maxQty ? ' disabled' : '') + '>＋</button>';
        html += '</div>';
        html += '</div>';
        html += '<div class="qty-notes">';
        html += 'カート数量の「<strong>' + item.quantity + '</strong>」のうち（残り: ' + maxQty + '）';
        html += '</div>';
        html += '</div>';
        html += '</div>';
      });

      $container.html(html);
    },

    /**
     * Get available quantity for an item (total in cart minus assigned to other recipients)
     */
    getAvailableQuantityForItem: function(itemKey) {
      var self = this;
      var cartItem = this.cart.items.find(function(i) { return i.key === itemKey; });
      if (!cartItem) return 0;

      var totalInCart = cartItem.quantity;
      var assignedToOthers = 0;

      // This function is used in multi mode for product selection
      this.multiRecipients.forEach(function(r) {
        // Don't count current recipient being edited
        if (r.id === self.selectingProductsRecipientId) return;
        if (r.items && r.items[itemKey]) {
          assignedToOthers += r.items[itemKey];
        }
      });

      return totalInCart - assignedToOthers;
    },

    /**
     * Update temporary product selection
     */
    updateTempProductSelection: function(itemKey, quantity) {
      this.tempProductSelections[itemKey] = quantity;

      // Update button states
      var $row = $('[data-select-products-list] [data-item-key="' + itemKey + '"]');
      var maxQty = parseInt($row.data('max-qty'), 10) || 0;
      $row.find('[data-select-qty-btn][data-step="-1"]').prop('disabled', quantity <= 0).toggleClass('is-disabled', quantity <= 0);
      $row.find('[data-select-qty-btn][data-step="1"]').prop('disabled', quantity >= maxQty).toggleClass('is-disabled', quantity >= maxQty);
    },

    /**
     * Update the selected products summary in modal
     */
    updateSelectProductsSummary: function() {
      var self = this;
      var $summary = $('[data-selected-summary]');
      var $list = $('[data-selected-items-list]');
      var items = this.cart.items || [];

      var selectedItems = [];
      items.forEach(function(item) {
        var qty = self.tempProductSelections[item.key] || 0;
        if (qty > 0) {
          selectedItems.push({
            title: item.product_title,
            quantity: qty
          });
        }
      });

      if (selectedItems.length === 0) {
        $summary.hide();
        return;
      }

      var html = '';
      selectedItems.forEach(function(item) {
        html += '<div class="d-flex a-j-sb">';
        html += '<span>' + self.escapeHtml(item.title) + '</span>';
        html += '<span>×<strong>' + item.quantity + '</strong></span>';
        html += '</div>';
      });

      $list.html(html);
      $summary.show();
    },

    /**
     * Save selected products to recipient
     */
    saveSelectedProducts: function() {
      var recipientId = this.selectingProductsRecipientId;
      if (!recipientId) {
        console.warn('No recipient selected');
        this.closeModal();
        return;
      }

      // Find and update recipient (this function is only used in multi mode)
      var recipient = this.multiRecipients.find(function(r) { return r.id === recipientId; });
      if (recipient) {
        recipient.items = {};
        for (var key in this.tempProductSelections) {
          if (this.tempProductSelections[key] > 0) {
            recipient.items[key] = this.tempProductSelections[key];
          }
        }
      }

      // Reset state
      this.selectingProductsRecipientId = null;
      this.tempProductSelections = {};

      this.saveData();
      this.renderCartItems();
      this.updateSummary();
      this.checkIncompleteSettings();
      this.closeModal();
    },

    /**
     * Open modalSelectAddress for quantity distribution across selected addresses
     * @param {Array} selectedRecipients - Only the addresses checked in modalBulkListAdd
     * @param {boolean} isEditMode - If true, preserve existing product quantities; otherwise first address gets full cart quantity
     */
    openSelectAddressModal: function(selectedRecipients, isEditMode) {
      var self = this;
      var items = this.cart.items || [];
      var recipients = selectedRecipients || [];
      if (this.currentMode === 'multi' && recipients.length > (this.MAX_MULTI_RECIPIENTS || 50)) {
        this.showMultiRecipientLimitMessage();
        return;
      }

      // Store which recipients are being configured in this bulk session
      this.bulkSelectedRecipients = recipients;

      // Build temp selections: { recipientId: { itemKey: qty } }
      this.tempAddrSelections = {};
      this.bulkSelectedRecipients.forEach(function(r, idx) {
        self.tempAddrSelections[r.id] = {};
        items.forEach(function(item) {
          if (isEditMode) {
            // Edit mode: preserve existing items quantity, or 0 if not assigned
            self.tempAddrSelections[r.id][item.key] = (r.items && r.items[item.key]) || 0;
          } else if (idx === 0) {
            // Bulk setup mode: first address gets full cart quantity
            self.tempAddrSelections[r.id][item.key] = item.quantity;
          } else {
            self.tempAddrSelections[r.id][item.key] = 0;
          }
        });
      });

      this.renderSelectAddressProducts();
      this.openModal('modalSelectAddress');
    },

    /**
     * Render selected addresses with products in modalSelectAddress
     */
    renderSelectAddressProducts: function() {
      var self = this;
      var $container = $('[data-select-address-list]');
      var items = this.cart.items || [];
      var recipients = this.bulkSelectedRecipients || [];

      if (recipients.length === 0 || items.length === 0) {
        $container.html('<p class="c-text-muted">お届け先または商品がありません。</p>');
        return;
      }

      var html = '';
      recipients.forEach(function(recipient, idx) {
        var recipientName = self.escapeHtml((recipient.lastName || '') + ' ' + (recipient.firstName || ''));
        var recipientAddress2 = self.getAddress2WithCompany(recipient);
        var recipientAddr = '〒' + self.formatZipDisplay(recipient.zip || '') + ' ' + (recipient.prefecture || '') + ' ' + (recipient.city || '') + ' ' + (recipient.address1 || '');
        if (recipientAddress2) recipientAddr += ' ' + recipientAddress2;

        html += '<div class="p-delivery-addr02" data-addr-card data-recipient-id="' + recipient.id + '">';
        html += '<div class="p-delivery-addr p-delivery-addr--row">';
        html += '<div class="col"><div class="p-delivery-addr__head">お届け先' + (idx + 1) + '件目 /' + recipients.length + '件中</div>';
        html += '<div class="p-delivery-addr__text">';
        html += self.escapeHtml(recipientAddr) + '<br>';
        html += recipientName + ' 様';
        html += '</div></div>';
        html += '<button class="c-btn c-btn-outline" type="button" data-edit-recipient="' + recipient.id + '">お届け先を変更する</button>';
        html += '</div>';

        // Product list for this address
        html += '<div class="p-delivery__list">';
        items.forEach(function(item) {
          var currentQty = (self.tempAddrSelections[recipient.id] && self.tempAddrSelections[recipient.id][item.key]) || 0;
          var maxQty = item.quantity;
          var shippingType = self.getItemShippingType(item);

          // Temperature tag
          var tempTag = '常温';
          var tempClass = 'c-badge--green';
          if (shippingType === 'frozen') {
            tempTag = '冷凍';
            tempClass = 'c-badge--blue02';
          } else if (shippingType === 'mail') {
            tempTag = 'メール便';
            tempClass = 'c-badge--mail';
          }

          html += '<div class="p-delivery-item" data-addr-item data-item-key="' + item.key + '">';
          html += '<div class="p-delivery-item__top p-product-small">';
          html += '<div class="p-product-small__thumb">';
          html += '<img class="p-delivery-item__thumb" src="' + (item.image || window._defaultProductImage) + '" alt="' + self.escapeHtml(item.product_title) + '">';
          html += '</div>';
          html += '<div class="p-delivery-item__info p-product-small__info"><div class="p-delivery-item__top">';
          html += '<span class="c-tag ' + tempClass + '">' + tempTag + '</span>';
          html += '<p class="p-delivery-item__name">' + self.escapeHtml(item.product_title) + '</p></div>';
          html += '<div class="p-delivery-item__meta">';
          html += '<span class="p-delivery-item__qty-label">数量</span>';
          html += '<div class="p-delivery-item__qty">';
          html += '<div class="qty qty--small">';
          html += '<button type="button" class="qty__btn' + (currentQty <= 0 ? ' is-disabled' : '') + '" data-addr-qty-btn data-step="-1" aria-label="decrease"' + (currentQty <= 0 ? ' disabled' : '') + '>−</button>';
          html += '<input class="qty__input c-form-input" type="number" value="' + currentQty + '" inputmode="numeric" aria-label="quantity" data-addr-qty-input min="0">';
          html += '<button type="button" class="qty__btn" data-addr-qty-btn data-step="1" aria-label="increase">＋</button>';
          html += '</div>';
          html += '<span class="p-delivery-item__qty-max">/' + maxQty + '</span>';
          html += '</div>';
          html += '</div>';
          html += '</div>';
          html += '</div>';
          html += '</div>';
        });
        html += '</div>';
        html += '</div>';
      });

      $container.html(html);
      this.updateAddrQtyButtonStates();
    },

    /**
     * Update quantity for a product at a specific address in modalSelectAddress
     * Recalculates remaining quantities and updates button states
     */
    updateAddrProductQuantity: function(recipientId, itemKey, newQty) {
      if (!this.tempAddrSelections[recipientId]) return;

      this.tempAddrSelections[recipientId][itemKey] = newQty;
      this.updateAddrQtyButtonStates();
    },

    /**
     * Update all quantity button states in modalSelectAddress
     * Ensures total per item across all addresses doesn't exceed cart quantity
     */
    updateAddrQtyButtonStates: function() {
      var self = this;
      var items = this.cart.items || [];
      var recipients = this.bulkSelectedRecipients || [];

      items.forEach(function(item) {
        var totalAssigned = 0;
        recipients.forEach(function(r) {
          totalAssigned += (self.tempAddrSelections[r.id] && self.tempAddrSelections[r.id][item.key]) || 0;
        });
        var remaining = item.quantity - totalAssigned;

        // Update each address's buttons for this item
        recipients.forEach(function(r) {
          var $card = $('[data-addr-card][data-recipient-id="' + r.id + '"]');
          var $item = $card.find('[data-addr-item][data-item-key="' + item.key + '"]');
          var currentQty = (self.tempAddrSelections[r.id] && self.tempAddrSelections[r.id][item.key]) || 0;
          var maxForThis = currentQty + remaining;

          var $decrease = $item.find('[data-addr-qty-btn][data-step="-1"]');
          var $increase = $item.find('[data-addr-qty-btn][data-step="1"]');

          $decrease.prop('disabled', currentQty <= 0).toggleClass('is-disabled', currentQty <= 0);
          $increase.prop('disabled', currentQty >= maxForThis).toggleClass('is-disabled', currentQty >= maxForThis);
        });
      });
    },

    /**
     * Save product quantities from modalSelectAddress to multiRecipients
     */
    saveSelectAddressProducts: function() {
      var self = this;

      // Replace multiRecipients with only the selected addresses + their quantities
      var newRecipients = [];
      (this.bulkSelectedRecipients || []).forEach(function(selected) {
        var recipient = Object.assign({}, selected);
        // Apply quantity selections
        if (self.tempAddrSelections[selected.id]) {
          recipient.items = {};
          for (var key in self.tempAddrSelections[selected.id]) {
            if (self.tempAddrSelections[selected.id][key] > 0) {
              recipient.items[key] = self.tempAddrSelections[selected.id][key];
            }
          }
        }
        newRecipients.push(recipient);
      });

      if (this.currentMode === 'multi' && newRecipients.length > (this.MAX_MULTI_RECIPIENTS || 50)) {
        this.showMultiRecipientLimitMessage();
        return;
      }
      this.multiRecipients = newRecipients;

      // Reset temp state
      this.tempAddrSelections = {};
      this.bulkSelectedRecipients = [];

      this.saveData();
      this.clearDeliveryOptionsCache();
      this.updateSenderByRecipient();
      this.renderCartItems();
      this.updateSummary();
      this.checkIncompleteSettings();
      this.closeModal();
    },

    /**
     * Remove a recipient (only in multi mode)
     */
    removeRecipient: function(recipientId) {
      // Only remove from multiRecipients - this function is only used in multi mode
      this.multiRecipients = this.multiRecipients.filter(function(r) { return r.id !== recipientId; });
      this.saveData();
      this.clearDeliveryOptionsCache(); // Clear cache so new postal code is used
      this.renderCartItems();
      this.renderSavedAddresses();
      this.updateSummary();
    },

    /**
     * Update item quantity for a recipient
     */
    updateRecipientItemQuantity: function(recipientId, itemKey, quantity) {
      var recipients = this.getRecipients();
      var recipient = recipients.find(function(r) { return r.id === recipientId; });
      if (recipient) {
        if (!recipient.items) recipient.items = {};
        recipient.items[itemKey] = quantity;
        this.syncRecipientUnsplitGiftCount(recipient, itemKey, quantity);
        this.saveData();
        this.updateSummary();
      }
    },

    // Sync noshi/ribbon count to recipient qty for non-split items.
    // No-op when item still has >=2 active split lines (each line manages its own count).
    syncRecipientUnsplitGiftCount: function(recipient, itemKey, quantity) {
      var splitConfig = recipient.splitLines && recipient.splitLines[itemKey];
      if (splitConfig && Array.isArray(splitConfig.lines) && splitConfig.lines.length >= 2) return;
      var settings = recipient.giftSettings && recipient.giftSettings[itemKey];
      if (!settings) return;
      if (settings.noshi) settings.noshi.count = quantity;
      if (settings.ribbon) settings.ribbon.count = quantity;
    },

    /**
     * Partial DOM update for a recipient's item qty (avoids full re-render on each click)
     */
    _updateRecipientItemUI: function($itemWrapper, newQty) {
      var $input = $itemWrapper.find('[data-qty-input]');
      var $decreaseBtn = $itemWrapper.find('[data-qty-btn][data-step="-1"]');
      var $increaseBtn = $itemWrapper.find('[data-qty-btn][data-step="1"]');
      $input.val(newQty);
      // Read prices from first element; apply markup to all (mobile u-sm-max + desktop u-sm-min)
      var $units = $itemWrapper.find('.cart-item__unit');
      var $unit = $units.first();
      var originalUnit = parseFloat($unit.attr('data-unit')) || 0;
      var finalUnit = parseFloat($unit.attr('data-discounted-unit'));
      // Look up authoritative cart item before computing discount flags.
      var _ruiKey = $itemWrapper.data('source-item-key') || $itemWrapper.data('item-key');
      var _ruiCartItem = _ruiKey && (this.cartItems || []).find(function(i) { return i.key === _ruiKey; });
      var hasUnitDiscount = Number.isFinite(finalUnit) && finalUnit < originalUnit;
      var hasDiscount = hasUnitDiscount || ($unit.attr('data-has-discount') === 'true') ||
        (_ruiCartItem && _ruiCartItem.original_line_price != null && _ruiCartItem.original_line_price > _ruiCartItem.final_line_price);
      // Update unit price display in case discount changed (e.g. tier pricing by quantity)
      if (hasUnitDiscount) {
        $units.html(renderDiscountPricePair(formatMoneyYen(originalUnit), formatMoneyYen(finalUnit)));
      } else {
        $units.html('<span class="price">' + formatMoneyYen(originalUnit) + '</span>');
      }
      // Use the globally pre-allocated recipient yen when available so dynamic totals stay
      // consistent with the initial render (which uses _recipientAllocMap).
      var _ruiAllocEntry = this._recipientAllocMap &&
        _ruiKey &&
        this._recipientAllocMap[_ruiKey] &&
        this._recipientAllocMap[_ruiKey][$itemWrapper.data('recipient-id')];
      var $total = $itemWrapper.find('.cart-item__total');
      if (hasDiscount) {
        var _ruiDiscTotal = (_ruiAllocEntry && _ruiAllocEntry.qty > 0)
          ? Math.round(_ruiAllocEntry.discYen * newQty / _ruiAllocEntry.qty)
          : (_ruiCartItem && _ruiCartItem.final_line_price != null && _ruiCartItem.quantity > 0)
            ? Math.round(_ruiCartItem.final_line_price * newQty / _ruiCartItem.quantity / 100)
            : Math.round((hasUnitDiscount ? finalUnit : originalUnit) * newQty);
        $total.html(renderDiscountPricePair(formatMoneyYen(Math.round(originalUnit * newQty)), formatMoneyYen(_ruiDiscTotal)));
      } else {
        var _ruiTotal = (_ruiAllocEntry && _ruiAllocEntry.qty > 0)
          ? Math.round(_ruiAllocEntry.origYen * newQty / _ruiAllocEntry.qty)
          : (_ruiCartItem && _ruiCartItem.original_line_price != null && _ruiCartItem.quantity > 0)
            ? Math.round(_ruiCartItem.original_line_price * newQty / _ruiCartItem.quantity / 100)
            : Math.round(originalUnit * newQty);
        $total.html('<span class="price">' + formatMoneyYen(_ruiTotal) + '</span>');
      }
      var isDecreaseDisabled = newQty <= 1;
      var itemKey = $itemWrapper.data('item-key');
      var cartQtyLimit = this.getMaxAllowedQuantityForItem(itemKey);
      var isIncreaseDisabled = newQty >= cartQtyLimit;
      $decreaseBtn.prop('disabled', isDecreaseDisabled).toggleClass('is-disabled', isDecreaseDisabled);
      $increaseBtn.prop('disabled', isIncreaseDisabled).toggleClass('is-disabled', isIncreaseDisabled);
    },

    /**
     * Flush debounced cart qty intent: send a single AJAX call with the accumulated delta
     */
    _flushCartQtyIntent: function(itemKey) {
      var self = this;
      var intentQty = self._cartQtyIntent[itemKey];
      if (intentQty === undefined) return;

      var cartItem = self.cartItems.find(function(i) { return i.key === itemKey; });
      if (cartItem) {
        var normalizedIntent = self.normalizeRequestedItemQuantity(itemKey, intentQty, cartItem.variant_id, true);
        intentQty = normalizedIntent.quantity;
        self._cartQtyIntent[itemKey] = intentQty;
      }
      if (!cartItem || intentQty === cartItem.quantity) {
        delete self._cartQtyIntent[itemKey];
        delete self._cartQtySnapshot[itemKey];
        return;
      }

      // Capture generation before AJAX — bumped by delete to invalidate this request.
      if (!self._cartQtySeq) self._cartQtySeq = {};
      var flushSeq = self._cartQtySeq[itemKey] || 0;
      var needsReconcile = false;

      var $multiItems = $('[data-item-key="' + itemKey + '"]');
      $multiItems.addClass('is-loading');
      $multiItems.find('[data-qty-btn]').prop('disabled', true).addClass('is-disabled');
      $multiItems.find('[data-qty-input]').prop('disabled', true);

      $.ajax({
        url: '/cart/change.js',
        type: 'POST',
        dataType: 'json',
        data: { id: itemKey, quantity: intentQty }
      })
      .done(function(cart) {
        // If a delete invalidated this request after it was in flight, Shopify may have already
        // applied the mutation. Fetch a fresh cart to reconcile client state, then discard.
        if ((self._cartQtySeq[itemKey] || 0) !== flushSeq) {
          needsReconcile = true;
          var reconcileSeq = self._cartQtySeq[itemKey] || 0;
          // Wait for any newer pending write to settle before reading authoritative cart state.
          var _pendingMut = self._pendingMutation && self._pendingMutation[itemKey];
          var _afterMut = _pendingMut ? $.when(_pendingMut) : $.when();
          _afterMut.always(function() {
            if ((self._cartQtySeq[itemKey] || 0) !== reconcileSeq) return;
            $.getJSON('/cart.js')
              .done(function(freshCart) {
                if ((self._cartQtySeq[itemKey] || 0) !== reconcileSeq) return;
                var _freshItems = freshCart.items || [];
                var _reKeyedFresh = self._findReKeyedCartItem(cartItem, _freshItems);
                if (_reKeyedFresh && _reKeyedFresh.key !== itemKey) {
                  self.migrateItemKey(itemKey, _reKeyedFresh.key);
                  itemKey = _reKeyedFresh.key;
                }
                self.cart = freshCart;
                self.cartItems = _freshItems;
                self.reconcileQuantitiesWithCart();
                self.renderCartItems();
                self.updateSummary();
                $('[data-item-key="' + itemKey + '"]').removeClass('is-loading')
                  .find('[data-qty-input]').prop('disabled', false);
              })
              .fail(function() {
                if ((self._cartQtySeq[itemKey] || 0) !== reconcileSeq) return;
                window.location.reload();
              });
          });
          return;
        }
        delete self._cartQtyIntent[itemKey];
        var snapshot = self._cartQtySnapshot[itemKey];
        delete self._cartQtySnapshot[itemKey];
        self.cart = cart;
        self.cartItems = cart.items || [];
        var updatedItem = cart.items.find(function(i) { return i.key === itemKey; });
        if (!updatedItem) {
          var _reKeyedItem = self._findReKeyedCartItem(cartItem, cart.items);
          if (_reKeyedItem) {
            self.migrateItemKey(itemKey, _reKeyedItem.key);
            itemKey = _reKeyedItem.key;
            updatedItem = _reKeyedItem;
          }
        }
        if (updatedItem) {
          var actualQty = updatedItem.quantity;
          if (actualQty < intentQty) {
            self.showCartQuantityLimitMessage();
            // Reconcile recipient allocations: only allow the increase Shopify actually granted
            if (snapshot) {
              // Count quantity freed by net decreases within this batch (e.g. A- releases 1 unit)
              var releasedQty = 0;
              (self.multiRecipients || []).forEach(function(r) {
                var snapshotQty = snapshot.recipientQtys[r.id] || 0;
                var optimisticQty = (r.items && r.items[itemKey]) || 0;
                if (optimisticQty < snapshotQty) releasedQty += snapshotQty - optimisticQty;
              });
              var allowedIncrease = Math.max(0, actualQty - (snapshot.cartQty - releasedQty));
              // Replay clicks in order: each entry grants at most +1 to that recipient
              var grantedMap = {};
              (snapshot.recipientOrder || []).forEach(function(rid) {
                if (allowedIncrease <= 0) return;
                grantedMap[rid] = (grantedMap[rid] || 0) + 1;
                allowedIncrease--;
              });
              // Apply grants; recipients with no grant are clamped back to snapshot qty
              (self.multiRecipients || []).forEach(function(r) {
                var snapshotQty = snapshot.recipientQtys[r.id] !== undefined ? snapshot.recipientQtys[r.id] : 0;
                var optimisticQty = (r.items && r.items[itemKey]) || 0;
                var increase = optimisticQty - snapshotQty;
                if (increase > 0) {
                  var granted = grantedMap[r.id] || 0;
                  if (!r.items) r.items = {};
                  r.items[itemKey] = snapshotQty + granted;
                  self.syncRecipientUnsplitGiftCount(r, itemKey, r.items[itemKey]);
                }
              });
            }
          }
          self.confirmedQuantities[itemKey] = actualQty;
          // Clamp noshi counts after any decrease
          if (self.noshiSettings['single'][itemKey] && self.noshiSettings['single'][itemKey].count > actualQty) {
            self.noshiSettings['single'][itemKey].count = actualQty;
          }
          if (self.stickerSettings['single'][itemKey] && self.stickerSettings['single'][itemKey].count > actualQty) {
            self.stickerSettings['single'][itemKey].count = actualQty;
          }
          (self.multiRecipients || []).forEach(function(r) {
            if (r.giftSettings && r.giftSettings[itemKey] && r.giftSettings[itemKey].noshi &&
                r.giftSettings[itemKey].noshi.count > actualQty) {
              r.giftSettings[itemKey].noshi.count = actualQty;
            }
            if (r.giftSettings && r.giftSettings[itemKey] && r.giftSettings[itemKey].ribbon &&
                r.giftSettings[itemKey].ribbon.count > actualQty) {
              r.giftSettings[itemKey].ribbon.count = actualQty;
            }
          });
          self.saveData();
        } else {
          // Item fully removed from cart
          delete self.itemGiftOptions['single'][itemKey];
          (self.multiRecipients || []).forEach(function(r) {
            if (r.giftSettings) delete r.giftSettings[itemKey];
          });
          delete self.noshiSettings['single'][itemKey];
          delete self.stickerSettings['single'][itemKey];
          delete self.wrapOnlyItems['single'][itemKey];
          delete self.noWrapItems['single'][itemKey];
          delete self.singleSplitLines[itemKey];
          delete self.confirmedQuantities[itemKey];
          self.updateSingleRecipientItem(itemKey, 0);
          (self.multiRecipients || []).forEach(function(r) {
            if (r.items) delete r.items[itemKey];
          });
          self.saveData();
        }
        if (SHIPPING_DEBUG) console.log('[BAG][flushCartQtyIntent] renderCartItems 呼び出し:', { itemKey: itemKey, cartItems: self.cartItems.map(function(i) { return { key: i.key, qty: i.quantity }; }) });
        self.renderCartItems();
        self.updateSummary();
      })
      .fail(function() {
        // If a delete invalidated this request after it was in flight, discard response.
        if ((self._cartQtySeq[itemKey] || 0) !== flushSeq) return;
        // Rollback: restore recipient qtys from snapshot
        var snapshot = self._cartQtySnapshot[itemKey];
        if (snapshot) {
          (self.multiRecipients || []).forEach(function(r) {
            if (snapshot.recipientQtys[r.id] !== undefined) {
              if (!r.items) r.items = {};
              r.items[itemKey] = snapshot.recipientQtys[r.id];
              self.syncRecipientUnsplitGiftCount(r, itemKey, snapshot.recipientQtys[r.id]);
            }
          });
          self.saveData();
        }
        delete self._cartQtyIntent[itemKey];
        delete self._cartQtySnapshot[itemKey];
        self.renderCartItems();
        self.updateSummary();
      })
      .always(function() {
        // Skip if reconciliation GET is in flight — it will handle unlock when it settles.
        if (needsReconcile) return;
        // Remove is-loading only; button states were already set correctly by renderCartItems()
        // in done/fail — force-enabling here would override min/max limit states.
        $('[data-item-key="' + itemKey + '"]').removeClass('is-loading')
          .find('[data-qty-input]').prop('disabled', false);
      });
    },

    /**
     * Save bulk delivery time settings
     */
    saveBulkDeliveryTime: function() {
      var date = $('#bulk-wish-date').val();
      var rawTime = String($('#bulk-wish-time').val() || '');
      var time = this.timeSlotToAIG[rawTime] || rawTime;

      // Apply directly - per-shipment validation happens inside applyBulkDeliveryToShipments
      // (validateDeliveryDate used a separate cache unrelated to the per-shipment DOM selects)
      this.applyBulkDeliveryToShipments(date, time);
    },

    /**
     * Classify a single bulk target against cached API options (dry-run, no DOM changes).
     * @returns {{ target, status, resolvedDate, resolvedTime, meta, apiOptions }}
     *   status: 'valid' | 'forced' | 'needs-pick' | 'unavailable' | 'skip'
     */
    classifyBulkTarget: function(target, bulkDate, bulkTime) {
      var rid = target.recipientId;
      var sk  = target.shipmentKey;

      // Retrieve cached meta + options
      var meta, apiOptions;
      if (rid === 'single' || this.currentMode === 'single') {
        if (this.deliveryOptionsByShipment && !this.deliveryOptionsByShipment.hasOwnProperty(sk)) {
          return { target: target, status: 'skip' };
        }
        apiOptions = (this.deliveryOptionsByShipment && this.deliveryOptionsByShipment[sk]) || [];
        meta = (this.deliveryScheduleMeta && this.deliveryScheduleMeta[sk]) || null;
        if (!meta && apiOptions.length === 0) {
          meta = { dateAvailable: false, timeAvailable: false, dateRequired: false, timeRequired: false };
        }
      } else {
        var ridOptions = this.deliveryOptionsMulti && this.deliveryOptionsMulti[rid];
        if (!ridOptions || !ridOptions.hasOwnProperty(sk)) return { target: target, status: 'skip' };
        apiOptions = ridOptions[sk] || [];
        meta = (this.deliveryScheduleMetaMulti && this.deliveryScheduleMetaMulti[rid] && this.deliveryScheduleMetaMulti[rid][sk]) || null;
        if (!meta && apiOptions.length === 0) {
          meta = { dateAvailable: false, timeAvailable: false, dateRequired: false, timeRequired: false };
        }
      }

      var dateAvailable = !meta || meta.dateAvailable !== false;
      var timeAvailable = !meta || meta.timeAvailable !== false;
      var dateRequired  = !!(meta && meta.dateRequired === true);
      var timeRequired  = !!(meta && meta.timeRequired === true);
      var base = { target: target, meta: meta, apiOptions: apiOptions };

      // Both unavailable → informational only
      if (!dateAvailable && !timeAvailable) {
        return $.extend({}, base, { status: 'unavailable' });
      }

      // Date not available (time-only shipment, rare) → treat as valid
      if (!dateAvailable) {
        return $.extend({}, base, { status: 'valid', resolvedDate: '', resolvedTime: bulkTime });
      }

      // 指定なし selected
      if (!bulkDate) {
        if (dateRequired) {
          // date required but user picked 指定なし → must pick
          return $.extend({}, base, { status: 'needs-pick', resolvedDate: '', resolvedTime: '' });
        }
        return $.extend({}, base, { status: 'valid', resolvedDate: '', resolvedTime: timeRequired ? '' : bulkTime });
      }

      // No API options loaded → unavailable
      if (apiOptions.length === 0) {
        return $.extend({}, base, { status: 'unavailable' });
      }

      // Check if bulkDate is in apiOptions
      var apiDateFormat = bulkDate.replace(/-/g, '/');
      var matchedOption = null;
      for (var i = 0; i < apiOptions.length; i++) {
        if (apiOptions[i].deliveryDate === apiDateFormat) { matchedOption = apiOptions[i]; break; }
      }

      if (matchedOption) {
        // Date is valid — check time slot
        if (timeAvailable && bulkTime && timeRequired) {
          var self = this;
          var availSlots = matchedOption.availableTimeSlots || [];
          var timeFound = availSlots.some(function(slotValue) {
            var slotInfo = self.timeSlotLabels[slotValue];
            return slotInfo && slotInfo.value === bulkTime;
          });
          if (!timeFound) {
            // Date OK but requested time not available → needs time re-selection
            return $.extend({}, base, { status: 'needs-pick', resolvedDate: bulkDate, resolvedTime: '', matchedOption: matchedOption });
          }
        }
        return $.extend({}, base, { status: 'valid', resolvedDate: bulkDate, resolvedTime: bulkTime, matchedOption: matchedOption });
      }

      // Date not in options
      if (apiOptions.length === 1) {
        // Only one alternative → forced (user must confirm, cannot change)
        var forcedDate = apiOptions[0].deliveryDate.replace(/\//g, '-');
        return $.extend({}, base, { status: 'forced', resolvedDate: forcedDate, resolvedTime: '' });
      }
      // Multiple alternatives → user picks
      return $.extend({}, base, { status: 'needs-pick', resolvedDate: '', resolvedTime: '' });
    },

    /**
     * Core logic: classify all shipments dry-run, then either apply directly or show review modal.
     * No DOM changes until user confirms in review modal (全キャンセル対応).
     * Used by saveBulkDeliveryTime
     */
    applyBulkDeliveryToShipments: function(date, time) {
      var self = this;
      var shippingTypeLabels = { ambient: '常温便', frozen: '冷凍便', mail: 'メール便' };

      // Guard: delivery options API is still loading — cannot validate dates yet
      if (this._deliveryScheduleLoading) {
        alert('配送日時の情報を読み込み中です。しばらくお待ちください。');
        return;
      }

      // Build list of targets
      var targets = [];
      if (this.currentMode === 'single') {
        var shipments = this.groupItemsIntoShipments(this.cartItems || this.cart.items || []);
        shipments.forEach(function(shipment) {
          targets.push({ recipientId: 'single', shipmentKey: shipment.shipmentKey, shippingType: shipment.shippingType, items: shipment.items || [] });
        });
      } else {
        this.multiRecipients.forEach(function(r) {
          var recipientItems = [];
          Object.keys(r.items || {}).forEach(function(itemKey) {
            if (r.items[itemKey] > 0) {
              var item = self.cartItems.find(function(i) { return i.key === itemKey; });
              if (item) recipientItems.push(item);
            }
          });
          self.groupItemsIntoShipments(recipientItems).forEach(function(shipment) {
            targets.push({ recipientId: r.id, shipmentKey: shipment.shipmentKey, shippingType: shipment.shippingType, recipientName: (r.lastName || '') + ' ' + (r.firstName || ''), items: shipment.items || [] });
          });
        });
      }

      // Dry-run: classify each target without touching the DOM
      var classified = targets.map(function(t) { return self.classifyBulkTarget(t, date, time); }).filter(function(c) { return c.status !== 'skip'; });

      // Any forced/needs-pick → show review modal
      var needsReview = classified.filter(function(c) { return c.status === 'forced' || c.status === 'needs-pick'; });

      if (needsReview.length === 0) {
        // All valid or unavailable → apply directly and show success
        self.applyClassifiedTargets(classified, null);
        self.closeModal();
        var validCount = classified.filter(function(c) { return c.status === 'valid'; }).length;
        var skippedErrors = classified.filter(function(c) { return c.status === 'unavailable'; }).map(function(c) {
          var label = shippingTypeLabels[c.target.shippingType] || c.target.shippingType;
          if (c.target.recipientName) label = c.target.recipientName + ' 様 - ' + label;
          return { label: label, reason: 'この便はお届け日時のご指定ができないためスキップされます', dateUnavailable: true };
        });
        self.showBulkDeliveryResultModal({
          valid: true,
          totalShipments: classified.length,
          successShipments: validCount,
          totalCount: classified.length,
          successCount: validCount,
          skippedErrors: skippedErrors
        });
      } else {
        // Show review modal — no DOM changes yet (cancel = full cancel)
        self.closeModal();
        self.showBulkReviewModal(classified);
      }
    },

    /**
     * Apply classified targets to DOM and save.
     * For forced/needs-pick, reads resolved values from review modal container.
     * @param {array} classified - Output of classifyBulkTarget array
     * @param {jQuery|null} $reviewContainer - Review modal [data-bulk-error-items] container, or null
     */
    applyClassifiedTargets: function(classified, $reviewContainer) {
      var self = this;
      classified.forEach(function(c) {
        if (c.status === 'skip' || c.status === 'unavailable') return;

        var target = c.target;
        var rid = target.recipientId;
        var sk  = target.shipmentKey;
        var st  = target.shippingType;
        var resolvedDate = c.resolvedDate || '';
        var resolvedTime = c.resolvedTime || '';

        // For forced/needs-pick, read confirmed values from review modal UI
        if ($reviewContainer && (c.status === 'forced' || c.status === 'needs-pick')) {
          var blockKey = rid + '_' + sk;
          var $block = $reviewContainer.find('[data-review-key="' + blockKey + '"]');
          if (c.status === 'needs-pick') {
            var modalDate = $block.find('.js-reviewDateSelect').val();
            if (modalDate !== undefined && modalDate !== null) resolvedDate = modalDate;
          }
          var $activeChip = $block.find('.js-errorTimeChip.is-active');
          if ($activeChip.length) resolvedTime = String($activeChip.attr('data-value') || '');
        }

        // Apply to main page DOM selects
        var $dateSelect = $('[data-recipient-id="' + rid + '"][data-shipment-key="' + sk + '"][data-delivery-date]');
        if (!$dateSelect.length) $dateSelect = $('[data-recipient-id="' + rid + '"][data-shipping-type="' + st + '"][data-delivery-date]');
        var $timeSelect = $('[data-recipient-id="' + rid + '"][data-shipment-key="' + sk + '"][data-delivery-time]');
        if (!$timeSelect.length) $timeSelect = $('[data-recipient-id="' + rid + '"][data-shipping-type="' + st + '"][data-delivery-time]');

        if ($dateSelect.length) {
          $dateSelect.val(resolvedDate);
          $dateSelect.toggleClass('is-error', !resolvedDate && !!(c.meta && c.meta.dateRequired));
        }
        if (c.meta && c.meta.dateAvailable !== false) {
          self.updateTimeSlotsForDate(st, resolvedDate || '', rid, sk);
        }
        if ($timeSelect.length) {
          $timeSelect.val(resolvedTime);
          $timeSelect.toggleClass('is-error', !resolvedTime && !!(c.meta && c.meta.timeRequired));
        }

        self.saveInlineDeliverySettings(sk, rid, resolvedDate, resolvedTime);
      });
      self.saveData();
    },

    /**
     * Show review modal for forced/needs-pick/unavailable targets.
     * valid targets are applied silently on confirm; cancel = full cancel (no DOM changes).
     * @param {array} classified - Output of classifyBulkTarget array (all statuses)
     */
    showBulkReviewModal: function(classified) {
      var self = this;
      var $modal = $('#modalBulkTimeError');
      if (!$modal.length) { console.warn('Modal #modalBulkTimeError not found'); return; }

      var $container = $modal.find('[data-bulk-error-items]');
      $container.empty();

      var weekdays = ['日', '月', '火', '水', '木', '金', '土'];
      var shippingBadges = {
        ambient: { label: '常温便', icon: window.shippingAssets ? window.shippingAssets.iconTruck  : '', cssClass: 'ship-badge--ambient' },
        frozen:  { label: '冷凍便', icon: window.shippingAssets ? window.shippingAssets.iconFrozen : '', cssClass: 'ship-badge--frozen'  },
        mail:    { label: 'メール便', icon: window.shippingAssets ? window.shippingAssets.iconMail : '', cssClass: 'ship-badge--mail'    }
      };
      var errorSvg = '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M1.5 9C1.5 4.86075 4.86 1.5 9 1.5C13.1475 1.5 16.5 4.86075 16.5 9C16.5 13.1407 13.1475 16.5 9 16.5C4.86 16.5 1.5 13.1407 1.5 9ZM8.34 6.1575C8.34 5.79825 8.64 5.4975 9 5.4975C9.36 5.4975 9.6525 5.79825 9.6525 6.1575V9.4725C9.6525 9.83325 9.36 10.125 9 10.125C8.64 10.125 8.34 9.83325 8.34 9.4725V6.1575ZM9.0075 12.5107C8.64 12.5107 8.3475 12.2107 8.3475 11.8507C8.3475 11.4907 8.64 11.1982 9 11.1982C9.3675 11.1982 9.66 11.4907 9.66 11.8507C9.66 12.2107 9.3675 12.5107 9.0075 12.5107Z" fill="#F75555" /></svg>';

      // Summary: count of valid targets that will be applied silently
      var validCount = classified.filter(function(c) { return c.status === 'valid'; }).length;
      if (validCount > 0) {
        $container.append('<div class="p-bulk-review-summary"><p>' + validCount + '件はそのまま設定されます。以下の配送便をご確認ください。</p></div>');
      }

      // Only show forced/needs-pick/unavailable in the review modal
      var reviewTargets = classified.filter(function(c) { return c.status === 'forced' || c.status === 'needs-pick' || c.status === 'unavailable'; });

      // Group by recipient
      var isSingleMode = self.currentMode === 'single';
      var groupMap = {}, groupOrder = [];
      reviewTargets.forEach(function(c) {
        var rid = c.target.recipientId;
        if (!groupMap[rid]) { groupMap[rid] = []; groupOrder.push(rid); }
        groupMap[rid].push(c);
      });
      if (!isSingleMode) {
        var recipientOrder = self.multiRecipients.map(function(r) { return r.id; });
        groupOrder.sort(function(a, b) { return recipientOrder.indexOf(a) - recipientOrder.indexOf(b); });
      }

      groupOrder.forEach(function(rid) {
        var group = groupMap[rid];
        var $groupContainer;

        if (isSingleMode || rid === 'single') {
          $groupContainer = $container;
        } else {
          var addrRecipient = null;
          for (var ri = 0; ri < self.multiRecipients.length; ri++) {
            if (self.multiRecipients[ri].id === rid) { addrRecipient = self.multiRecipients[ri]; break; }
          }
          var addrHtml = '<div class="p-bulk-error-address-group">';
          if (addrRecipient) {
            var addrRecipientAddress2 = self.getAddress2WithCompany(addrRecipient);
            var rAddr = '〒' + self.formatZipDisplay(addrRecipient.zip || '') + ' ' + (addrRecipient.prefecture || '') + ' ' + (addrRecipient.city || '') + ' ' + (addrRecipient.address1 || '');
            if (addrRecipientAddress2) rAddr += ' ' + addrRecipientAddress2;
            var rName = ((addrRecipient.lastName || addrRecipient.firstName) ? ((addrRecipient.lastName || '') + ' ' + (addrRecipient.firstName || '')).trim() : (addrRecipient.company || '')) + ' 様';
            addrHtml += '<div class="p-delivery-addr"><p class="p-product-small__address delivery">' + self.escapeHtml(rAddr.trim()) + '</p>';
            addrHtml += '<p class="p-product-small__recipient p-delivery-addr__text">' + self.escapeHtml(rName.trim()) + '</p></div>';
          }
          addrHtml += '</div>';
          $container.append(addrHtml);
          $groupContainer = $container.children().last();
        }

        group.forEach(function(c) {
          var target    = c.target;
          var sk        = target.shipmentKey;
          var st        = target.shippingType;
          var blockKey  = target.recipientId + '_' + sk;
          var apiOptions = c.apiOptions || [];
          var meta       = c.meta || {};
          var dateRequired = !!(meta.dateRequired);
          var timeRequired = !!(meta.timeRequired);
          var timeAvailable = meta.timeAvailable !== false;
          var items = target.items || [];

          var blockHtml = '<div class="p-bulk-error-block" data-review-key="' + blockKey + '">';

          // Shipping badge
          var badge = shippingBadges[st];
          if (badge) {
            blockHtml += '<div class="ship-badge-list"><div class="ship-badge ' + badge.cssClass + '">';
            blockHtml += '<span class="ship-badge__icon" aria-hidden="true"><img src="' + badge.icon + '" alt=""></span>';
            blockHtml += '<span class="ship-badge__text">' + badge.label + '</span></div></div>';
          }

          // Product list
          if (items.length > 0) {
            items.forEach(function(item) {
              blockHtml += '<div class="p-product-small">';
              if (item.image) blockHtml += '<img class="p-product-small__thumb" src="' + item.image + '" alt="">';
              blockHtml += '<div class="p-product-small__info"><p>' + self.escapeHtml(item.product_title || item.title || '') + '</p></div></div>';
            });
          }

          if (c.status === 'unavailable') {
            var warningSvg = '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M1.5 9C1.5 4.86075 4.86 1.5 9 1.5C13.1475 1.5 16.5 4.86075 16.5 9C16.5 13.1407 13.1475 16.5 9 16.5C4.86 16.5 1.5 13.1407 1.5 9ZM8.34 6.1575C8.34 5.79825 8.64 5.4975 9 5.4975C9.36 5.4975 9.6525 5.79825 9.6525 6.1575V9.4725C9.6525 9.83325 9.36 10.125 9 10.125C8.64 10.125 8.34 9.83325 8.34 9.4725V6.1575ZM9.0075 12.5107C8.64 12.5107 8.3475 12.2107 8.3475 11.8507C8.3475 11.4907 8.64 11.1982 9 11.1982C9.3675 11.1982 9.66 11.4907 9.66 11.8507C9.66 12.2107 9.3675 12.5107 9.0075 12.5107Z" fill="#b45309" /></svg>';
            blockHtml += '<div class="p-alert p-alert--info p-alert--small"><div class="p-alert__icon"><i class="c-ico-info-01">' + warningSvg + '</i></div>';
            blockHtml += '<div class="p-alert__info"><p>この便はお届け日時のご指定ができないためスキップされます</p></div></div>';
          } else if (c.status === 'forced') {
            // A案: only 1 option, show locked date with confirmation message
            var fd = new Date(c.resolvedDate);
            var fdStr = (fd.getMonth() + 1) + '月' + fd.getDate() + '日';
            blockHtml += '<div class="p-alert p-alert--small"><div class="p-alert__info"><p>' + fdStr + 'のみ配送可能です。この日付で設定されます。</p></div></div>';
            blockHtml += '<div class="p-bulk-modal__wrap">';
            blockHtml += '<div class="p-bulk-modal__field"><label class="shiptime__label">お届け希望日</label>';
            blockHtml += '<p class="p-delivery-date-fixed">' + fdStr + '</p>';
            // Hidden input carries the resolved date for applyClassifiedTargets
            blockHtml += '<input type="hidden" class="js-reviewDateSelect" value="' + c.resolvedDate + '"></div>';
            if (timeAvailable) {
              blockHtml += '<div class="p-bulk-modal__field"><label class="p-bulk-modal__label">配送希望時間</label>';
              blockHtml += '<div class="p-bulk-modal__chips js-reviewTimeChips"></div></div>';
            }
            blockHtml += '</div>';
          } else {
            // needs-pick: date select (or locked date if only time needs re-selection) + time chips
            var alertMsg;
            if (c.resolvedDate) {
              var npd = new Date(c.resolvedDate);
              alertMsg = (npd.getMonth() + 1) + '月' + npd.getDate() + '日はお届け希望時間を選択してください。';
            } else if (apiOptions.length > 0) {
              var earliest = new Date(apiOptions[0].deliveryDate.replace(/\//g, '-'));
              alertMsg = (earliest.getMonth() + 1) + '月' + earliest.getDate() + '日以降の日付を選択ください';
            } else {
              alertMsg = 'お届け希望日時を再度選択してください';
            }
            blockHtml += '<div class="p-alert p-alert--error p-alert--small"><div class="p-alert__icon"><i class="c-ico-info-01">' + errorSvg + '</i></div>';
            blockHtml += '<div class="p-alert__info"><p>' + self.escapeHtml(alertMsg) + '</p></div></div>';
            blockHtml += '<div class="p-bulk-modal__wrap">';
            blockHtml += '<div class="p-bulk-modal__field"><label class="shiptime__label">お届け希望日</label>';
            if (c.resolvedDate) {
              // Date already determined, only time needs re-selection
              var npd2 = new Date(c.resolvedDate);
              blockHtml += '<p class="p-delivery-date-fixed">' + (npd2.getMonth() + 1) + '月' + npd2.getDate() + '日</p>';
              blockHtml += '<input type="hidden" class="js-reviewDateSelect" value="' + c.resolvedDate + '">';
            } else {
              blockHtml += '<select class="c-form-select js-reviewDateSelect' + (dateRequired ? ' is-error' : '') + '"></select>';
            }
            blockHtml += '</div>';
            if (timeAvailable) {
              blockHtml += '<div class="p-bulk-modal__field"><label class="p-bulk-modal__label">配送希望時間</label>';
              blockHtml += '<div class="p-bulk-modal__chips js-reviewTimeChips"></div></div>';
            }
            blockHtml += '</div>';
          }

          blockHtml += '</div>'; // .p-bulk-error-block
          $groupContainer.append(blockHtml);

          if (c.status === 'unavailable') return;

          var $block     = $container.find('[data-review-key="' + blockKey + '"]');
          var $timeChips = $block.find('.js-reviewTimeChips');

          // Populate date select for needs-pick (no pre-filled date)
          if (c.status === 'needs-pick' && !c.resolvedDate) {
            var $dateSelect = $block.find('.js-reviewDateSelect');
            var dateOptHtml = dateRequired ? '<option value="">指定してください</option>' : '<option value="">指定なし</option>';
            apiOptions.forEach(function(opt) {
              var optDate = opt.deliveryDate.replace(/\//g, '-');
              var d = new Date(optDate);
              var text = (d.getMonth() + 1) + '月' + d.getDate() + '日(' + weekdays[d.getDay()] + ')';
              dateOptHtml += '<option value="' + optDate + '">' + text + '</option>';
            });
            $dateSelect.html(dateOptHtml);

            // Update time chips when date changes
            $dateSelect.off('change.review').on('change.review', function() {
              $(this).toggleClass('is-error', !$(this).val());
              $timeChips.find('.js-errorTimeChip').removeClass('is-active');
              self.populateErrorModalTimeChipsForBlock($timeChips, apiOptions, $(this).val(), timeRequired);
            });
          }

          // Populate initial time chips
          var initialDate = c.resolvedDate || ($block.find('.js-reviewDateSelect').val() || '');
          self.populateErrorModalTimeChipsForBlock($timeChips, apiOptions, initialDate, timeRequired);
        });
      });

      $('.p-modal.active').removeClass('active');
      $modal.addClass('active');

      // Cancel → full cancel, nothing applied
      $modal.find('.js-close').off('click.review').on('click.review', function(e) {
        e.preventDefault();
        $modal.removeClass('active');
      });

      // Confirm → validate then apply all
      $modal.off('click.reviewSave').on('click.reviewSave', '.js-saveErrorBulkTime', function() {
        // Validate: needs-pick with dateRequired must have a date selected
        var hasError = false;
        classified.forEach(function(c) {
          if (c.status !== 'forced' && c.status !== 'needs-pick') return;
          var blockKey = c.target.recipientId + '_' + c.target.shipmentKey;
          var $block = $container.find('[data-review-key="' + blockKey + '"]');
          var selDate = $block.find('.js-reviewDateSelect').val();
          if (!selDate && c.meta && c.meta.dateRequired) {
            hasError = true;
            $block.find('.js-reviewDateSelect').addClass('is-error');
          }
          if (c.meta && c.meta.timeRequired && !$block.find('.js-errorTimeChip.is-active').length) {
            hasError = true;
          }
        });
        if (hasError) return;

        $modal.removeClass('active');
        self.applyClassifiedTargets(classified, $container);

        // Show success modal
        var appliedCount = classified.filter(function(c) { return c.status === 'valid' || c.status === 'forced' || c.status === 'needs-pick'; }).length;
        var skippedErrors = classified.filter(function(c) { return c.status === 'unavailable'; }).map(function(c) {
          var shippingTypeLabels = { ambient: '常温便', frozen: '冷凍便', mail: 'メール便' };
          var label = shippingTypeLabels[c.target.shippingType] || c.target.shippingType;
          if (c.target.recipientName) label = c.target.recipientName + ' 様 - ' + label;
          return { label: label, reason: 'この便はお届け日時のご指定ができないためスキップされます', dateUnavailable: true };
        });
        self.showBulkDeliveryResultModal({
          valid: true,
          totalShipments: classified.length,
          successShipments: appliedCount,
          totalCount: classified.length,
          successCount: appliedCount,
          skippedErrors: skippedErrors
        });
      });
    },

    /**
     * Save inline delivery date/time settings when user changes dropdown
     * @param {string} shipmentKey - The shipment key (e.g., 'ambient', 'ambient_item123')
     * @param {string} recipientId - The recipient ID (for multi-mode) or 'single'
     * @param {string|null} date - The selected date (null to keep existing)
     * @param {string|null} time - The selected time (null to keep existing)
     */
    saveInlineDeliverySettings: function(shipmentKey, recipientId, date, time) {
      var recipient;

      // Find the recipient to update
      if (this.currentMode === 'single' || recipientId === 'single') {
        // Single mode: use singleRecipient
        recipient = this.singleRecipient;
      } else {
        // Multi mode: find recipient by ID
        recipient = this.multiRecipients.find(function(r) {
          return r.id === recipientId;
        });
      }

      if (recipient) {
        // Ensure deliverySettings structure exists (now keyed by shipmentKey)
        if (!recipient.deliverySettings) {
          recipient.deliverySettings = {};
        }
        if (!recipient.deliverySettings[shipmentKey]) {
          recipient.deliverySettings[shipmentKey] = { date: '', time: '' };
        }

        // Update date and/or time
        if (date !== null) {
          recipient.deliverySettings[shipmentKey].date = date;
        }
        if (time !== null) {
          recipient.deliverySettings[shipmentKey].time = time;
        }
      }

      // Save to localStorage
      this.saveData();
    },

    /**
     * Reset bulk shiptime modal when opened
     */
    resetBulkShiptimeModal: function() {
      // Populate bulk date select (start offset from getDeliveryStartOffset, up to +24)
      this.populateBulkDateSelect();

      // Populate bulk time select from giftConfig flags
      this.populateBulkTimeChips();

      // Reset date select to default
      $('#bulk-wish-date').val('');

      // Reset time select
      $('#bulk-wish-time').val('');

      // Update target count: active recipients (with items assigned)
      if (this.currentMode === 'single') {
        $('[data-target-count]').text('1');
      } else {
        $('[data-target-count]').text(this.getActiveShipmentCount() || '0');
      }
    },

    /**
     * Populate bulk date select from getDeliveryStartOffset() to +24 days range
     */
    populateBulkDateSelect: function($target) {
      var $bulkDate = $target || $('#bulk-wish-date');
      if (!$bulkDate.length) return;

      var dayNames = ['日', '月', '火', '水', '木', '金', '土'];
      var today = new Date();
      // JST hour (UTC+9), independent of the browser's local timezone
      var jstHour = new Date(today.getTime() + (today.getTimezoneOffset() + 540) * 60000).getHours();
      var startOffset = getDeliveryStartOffset(today, jstHour);
      var optionsHtml = '<option value="">指定なし</option>';

      for (var i = startOffset; i <= 24; i++) {
        var date = new Date(today);
        date.setDate(date.getDate() + i);
        var year = date.getFullYear();
        var month = date.getMonth() + 1;
        var day = date.getDate();
        var dayName = dayNames[date.getDay()];
        var dateStr = year + '-' + String(month).padStart(2, '0') + '-' + String(day).padStart(2, '0');
        var displayText = month + '月' + day + '日(' + dayName + ')';
        optionsHtml += '<option value="' + dateStr + '">' + displayText + '</option>';
      }

      $bulkDate.html(optionsHtml);
    },

    /**
     * Render bulk bag options in the bulk wrapping modal
     * Shows 無料手提げ袋 and 手提げ袋（紙） options based on config
     */
    renderBulkBagOptions: function() {
      var self = this;
      var $container = $('[data-bulk-bag-options]');
      if (!$container.length) return;

      var paidBags = this.getPaidHandbagVariants();
      var hasFreeBag = (this.cartItems || []).some(function(item) {
        return !!self.getFreeHandbagVariantForItem(item.key);
      });
      var hasPaidBag = paidBags.length > 0;

      var html = '';

      // 無料手提げ袋 (Free bag option + qty)
      if (hasFreeBag) {
        html += '<div class="c-card-01__item">';
        html += '<label class="option-card">';
        html += '<input type="checkbox" name="bulk_bag_free" class="option-card__checkbox" data-bag-type="free">';
        html += '<div class="option-card__inner">';
        html += '<div class="option-card__text">';
        html += '<p class="option-card__title">手提げ袋（クリア）<span class="c-badge c-badge--free">無料</span></p>';
        html += '</div>';
        html += '</div>';
        html += '</label>';
        html += '</div>';
      }

      // 手提げ袋（紙） (Paid bag options — one row per variant with qty)
      if (hasPaidBag) {
        html += '<div class="c-card-01__item">';
        // Checkbox card (label only — no qty inside)
        html += '<label class="option-card">';
        html += '<input type="checkbox" name="bulk_bag_paid" class="option-card__checkbox" data-bag-type="paid">';
        html += '<div class="option-card__inner">';
        html += '<div class="option-card__text">';
        html += '<p class="option-card__title">手提げ袋（紙） </p>';
        html += '</div>';
        html += '</div>';
        html += '</label>';
        html += '<p class="c-text-note" style="margin-top:4px;font-size:11px;">※推奨サイズの手提げ袋が商品数と同数選択されます。</p>';
        html += '</div>';
      }

      // If no bag options available
      if (!hasFreeBag && !hasPaidBag) {
        html += '<p class="c-text-muted">手提げ袋オプションがありません</p>';
      }

      $container.html(html);

      // Update target count: single = 1, multi = sum of items per recipient
      if (this.currentMode === 'single') {
        $('[data-wrapping-target-count]').text('1');
      } else {
        var totalCount = 0;
        this.multiRecipients.forEach(function(r) {
          Object.keys(r.items || {}).forEach(function(key) {
            if ((r.items[key] || 0) > 0) totalCount++;
          });
        });
        $('[data-wrapping-target-count]').text(totalCount || '0');
      }
    },

    /**
     * Open and initialize the bulk wrapping modal
     */
    renderBulkWrappingModal: function() {
      // Clear any pending context from a previous partial-failure run
      this._pendingBulkContext = null;

      var self = this;
      var bulkItemKeys = [];
      (this.multiRecipients || []).forEach(function(recipient) {
        Object.keys(recipient.items || {}).forEach(function(itemKey) {
          if ((recipient.items[itemKey] || 0) > 0 && bulkItemKeys.indexOf(itemKey) === -1) {
            bulkItemKeys.push(itemKey);
          }
        });
      });
      var requiresBagSelection = bulkItemKeys.some(function(itemKey) {
        var item = self.cartItems.find(function(cartItem) { return cartItem.key === itemKey; });
        return item && self.isBagSelectionRequired(itemKey, item.variant_id);
      });
      var hasForceOnBag = bulkItemKeys.some(function(itemKey) {
        return self.getBagRuleConfigByItemKey(itemKey).forceOn;
      });
      var $bulkBagTitle = $('#modalBulkWrapping .p-delivery-item__row-ttl').first();
      if ($bulkBagTitle.length) {
        $bulkBagTitle.find('[data-bulk-bag-required-badge]').remove();
        if (hasForceOnBag) {
          $bulkBagTitle.append('<span class="c-badge c-badge--red c-badge--required" data-bulk-bag-required-badge>必須</span>');
        }
      }

      // Reset bag toggle and hide bag sub-options
      $('#modalBulkWrapping input[name="bulk_bag"][value="off"]').prop('checked', !requiresBagSelection);
      $('#modalBulkWrapping input[name="bulk_bag"][value="on"]').prop('checked', false);
      $('#modalBulkWrapping .js-bagToggle').removeClass('is-active');
      $('#modalBulkWrapping .js-bagBox').hide();
      this.clearBagSelectionValidationError($('#modalBulkWrapping'));

      // Reset wrapping radio to "paper" (default) and hide sub-options
      $('#modalBulkWrapping input[name="bulk_wrapping"][value="paper"]').prop('checked', true);
      $('[data-bulk-noshi-options]').hide();
      $('[data-bulk-ribbon-options]').hide();

      // Reset bulk noshi preview state + clear validation errors
      this.bulkNoshiPreviewConfirmed = false;
      if (window.NoshiDialogCommon) window.NoshiDialogCommon.clearValidationErrors({ selectors: this.getBulkNoshiSelectors() });
      $('[data-bulk-noshi-preview-wrap]').hide();
      $('.js-bulkGiftPreview').hide();
      this.updateBulkWrappingBtnState();

      // Render bag options (fresh HTML — checkboxes unchecked, qtys reset)
      this.renderBulkBagOptions();

      // Render noshi + ribbon options (pre-populate so they're ready when shown)
      this.renderBulkNoshiOptions();
      this.renderBulkRibbonOptions();
    },

    /**
     * Render noshi card options in the bulk modal.
     * Filtered to entries applicable to all bulk-target items.
     */
    renderBulkNoshiOptions: function() {
      var self = this;
      var data = this.getBulkApplicableEntries(window.noshiData, window.cartItemsApplicableNoshi);

      // Reset state
      this.selectedBulkNoshiEntry = null;
      this.currentBulkNoshiMode = null;
      $('[data-bulk-noshi-input-fields]').hide();
      $('#bulk-noshi-line1, #bulk-noshi-line2').val('');
      $('#bulk-noshi-uchiiwai-name, #bulk-noshi-uchiiwai-furigana').val('');
      $('[data-bulk-noshi-uchiiwai-fields]').hide();
      // Reset corp toggle
      $('#modalBulkWrapping .js-bulkCorpToggle').prop('checked', false);
      $('#modalBulkWrapping .js-bulkCorpFields').hide();
      $('#modalBulkWrapping .js-bulkCorpBlock').show();
      $('[data-bulk-noshi-normal-fields]').show();
      $('#bulk-corp-company, #bulk-corp-title, #bulk-corp-name').val('');
      $('[data-bulk-noshi-input-title]').text('お名前');

      // Populate purpose select (matching individual noshi modal first select)
      var $purpose = $('#bulk-noshi-purpose');
      $purpose.html('<option value="">用途をお選びください</option>');
      this.getNoshiPurposeOptions(data).forEach(function(opt) {
        $purpose.append('<option value="' + self.escapeHtml(opt.type) + '">' + self.escapeHtml(opt.type) + '</option>');
      });
      $('#bulk-noshi-tag').html('<option value="">のしの種類をお選びください</option>');
    },

    hasInvalidNoshiTextInput: function(value) {
      if (value === undefined || value === null) return false;
      var text = String(value);
      if (!text) return false;
      if (window.CharValidator) return !!window.CharValidator.validate(text, 'noshi');
      return false;
    },

    appendNoshiFieldValidationError: function($field, message) {
      var $container = $field.closest('.p-row-item, .p-corp__field');
      if (!$container.length) $container = $field.parent();
      $container.addClass('is-error');
      if ($container.find('.noshi-field-error').length === 0) {
        $container.append('<p class="p-form-error-txt noshi-field-error">' + message + '</p>');
      }
    },

    validateBulkNoshiFields: function(mode, texts) {
      var self = this;
      var invalidMessage = '絵文字や記号は入力できない文字です。他の文字に変更してください。';
      var checks = [];

      if (mode === 'uchiiwai') {
        if (window.NoshiDialogCommon && texts) {
          var normalizedBulkUchiiwaiTexts = window.NoshiDialogCommon.normalizeUchiiwaiNoshiTexts(this.getBulkNoshiDialogOptions());
          texts.name = normalizedBulkUchiiwaiTexts.name;
          texts.furigana = normalizedBulkUchiiwaiTexts.furigana;
        }
        checks = [
          { selector: '#bulk-noshi-uchiiwai-name', value: texts && texts.name },
          { selector: '#bulk-noshi-uchiiwai-furigana', value: texts && texts.furigana }
        ];
      } else if (mode === 'corporate') {
        if (window.NoshiDialogCommon && texts) {
          var normalizedBulkCorpTexts = window.NoshiDialogCommon.normalizeCorporateNoshiTexts(this.getBulkNoshiDialogOptions());
          texts.company = normalizedBulkCorpTexts.company;
          texts.title = normalizedBulkCorpTexts.title;
          texts.name = normalizedBulkCorpTexts.name;
        }
        checks = [
          { selector: '#bulk-corp-company', value: texts && texts.company },
          { selector: '#bulk-corp-title', value: texts && texts.title },
          { selector: '#bulk-corp-name', value: texts && texts.name }
        ];
      } else {
        if (window.NoshiDialogCommon && texts) {
          var normalizedBulkTexts = window.NoshiDialogCommon.normalizeNormalNoshiTexts(this.getBulkNoshiDialogOptions());
          texts.name1 = normalizedBulkTexts.name1;
          texts.name2 = normalizedBulkTexts.name2;
          texts.name = normalizedBulkTexts.name1;
        }
        checks = [
          { selector: '#bulk-noshi-line1', value: texts && texts.name1 },
          { selector: '#bulk-noshi-line2', value: texts && texts.name2 }
        ];
      }

      $('#modalBulkWrapping .noshi-field-error').remove();
      $('#modalBulkWrapping .is-error').removeClass('is-error');

      var hasError = false;
      checks.forEach(function(check) {
        var val = check.value || '';
        if (window.CharValidator) {
          var cvMsg = window.CharValidator.validate(String(val), 'noshi');
          if (cvMsg) {
            self.appendNoshiFieldValidationError($(check.selector), cvMsg);
            hasError = true;
          }
        } else if (self.hasInvalidNoshiTextInput(val)) {
          self.appendNoshiFieldValidationError($(check.selector), invalidMessage);
          hasError = true;
        }
      });

      return hasError;
    },

    getBulkNoshiDialogOptions: function() {
      return {
        selectors: {
          modal: '#modalBulkWrapping',
          inputFields: '[data-bulk-noshi-input-fields]',
          normalFields: '[data-bulk-noshi-normal-fields]',
          uchiiwaiFields: '[data-bulk-noshi-uchiiwai-fields]',
          corpBlock: '#modalBulkWrapping .js-bulkCorpBlock',
          corpToggle: '#modalBulkWrapping .js-bulkCorpToggle',
          corpFields: '#modalBulkWrapping .js-bulkCorpFields',
          nameNote: '[data-bulk-noshi-name-note]',
          inputTitle: '[data-bulk-noshi-input-title]',
          currentModeInput: '#bulk-noshi-current-mode',
          normalName1: '#bulk-noshi-line1',
          normalName2: '#bulk-noshi-line2',
          uchiiwaiName: '#bulk-noshi-uchiiwai-name',
          uchiiwaiFurigana: '#bulk-noshi-uchiiwai-furigana',
          corpCompany: '#bulk-corp-company',
          corpTitle: '#bulk-corp-title',
          corpName: '#bulk-corp-name'
        }
      };
    },

    getErrorNoshiDialogOptions: function(uid) {
      return {
        selectors: {
          modal: '[data-fail-uid="' + uid + '"]',
          inputFields: '.js-err-noshi-fields[data-uid="' + uid + '"]',
          normalFields: '.js-err-noshi-normal[data-uid="' + uid + '"]',
          uchiiwaiFields: '.js-err-noshi-uchiiwai[data-uid="' + uid + '"]',
          corpBlock: '.js-err-noshi-corp-block[data-uid="' + uid + '"]',
          corpToggle: '.js-errCorpToggle[data-uid="' + uid + '"]',
          corpFields: '.js-err-noshi-corp-fields[data-uid="' + uid + '"]',
          nameNote: '.js-err-noshi-name-note[data-uid="' + uid + '"]',
          inputTitle: '.js-err-noshi-input-title[data-uid="' + uid + '"]',
          currentModeInput: '.js-errNoshiCurrentMode[data-uid="' + uid + '"]',
          normalName1: '.js-errNoshiLine1[data-uid="' + uid + '"]',
          normalName2: '.js-errNoshiLine2[data-uid="' + uid + '"]',
          uchiiwaiName: '.js-errUchiiwaiName[data-uid="' + uid + '"]',
          uchiiwaiFurigana: '.js-errUchiiwaiKana[data-uid="' + uid + '"]',
          corpCompany: '.js-errCorpCompany[data-uid="' + uid + '"]',
          corpTitle: '.js-errCorpTitle[data-uid="' + uid + '"]',
          corpName: '.js-errCorpName[data-uid="' + uid + '"]'
        }
      };
    },

    getBulkWrappingChoicesForItem: function(itemKey) {
      var meta = (window.cartItemsMetafields && window.cartItemsMetafields[itemKey]) || {};
      var allowWrap = meta.flags_wrapping !== false;
      // Noshi/ribbon availability is independent of flags_wrapping (mirrors renderWrappingOptions)
      var noshiData = meta.flags_noshi !== false ? this.getNoshiDataForItem(itemKey) : [];
      var ribbonData = meta.flags_ribbon !== false ? this.getRibbonDataForItem(itemKey) : [];
      var choices = [];

      if (allowWrap) {
        choices.push({ value: 'paper', label: '包装のみ' });
      }
      if (meta.flags_noshi !== false && noshiData.length > 0) {
        choices.push({ value: 'noshi', label: 'のし' });
      }
      if (meta.flags_ribbon !== false && ribbonData.length > 0) {
        choices.push({ value: 'ribbon', label: 'リボンシール・<br class="u-sm-min">季節ラッピング' });
      }
      if (!allowWrap) {
        choices.push({ value: 'none', label: '包装なし' });
      }

      return {
        meta: meta,
        choices: choices,
        noshiData: noshiData,
        ribbonData: ribbonData
      };
    },

    clearErrorWrapValidation: function($block) {
      $block.find('.js-err-wrap-validation').remove();
      $block.find('.js-errWrapChoices').removeClass('is-error');
    },

    appendErrorWrapValidation: function($block, message) {
      var $container = $block.find('.js-errWrapChoices').first();
      if (!$container.length) return;
      $container.addClass('is-error');
      if ($container.siblings('.js-err-wrap-validation').length === 0) {
        $container.after('<p class="p-form-error-txt js-err-wrap-validation">' + message + '</p>');
      }
    },

    showErrorNoshiInputFields: function(uid, entry) {
      this.clearErrorWrapValidation($('[data-fail-uid="' + uid + '"]'));
      return window.NoshiDialogCommon.showInputFieldsForEntry(
        entry,
        this.getErrorNoshiDialogOptions(uid)
      );
    },

    handleErrorCorpToggle: function(uid) {
      var $block = $('[data-fail-uid="' + uid + '"]');
      var itemKey = $block.data('fail-key');
      var gid = $block.find('.js-errNoshiTag[data-uid="' + uid + '"]').val();
      var data = this.getNoshiDataForItem(itemKey);
      var entry = null;
      var i;

      for (i = 0; i < data.length; i++) {
        if (data[i].gid === gid) {
          entry = data[i];
          break;
        }
      }

      return window.NoshiDialogCommon.handleCorpToggle(
        entry,
        this.getErrorNoshiDialogOptions(uid)
      );
    },

    initializeBulkWrappingErrorModal: function($modal) {
      var self = this;
      $modal.find('[data-fail-uid]').each(function() {
        var $block = $(this);
        var uid = $block.data('fail-uid');
        var wrapVal = $block.find('input[name="errWrap_' + uid + '"]:checked').val();

        if (wrapVal === 'noshi') {
          var $purpose = $block.find('.js-errNoshiPurpose[data-uid="' + uid + '"]');
          if ($purpose.length && $purpose.val()) {
            $purpose.trigger('change');
          }
        }

        $block.find('.js-errBagCb:checked').each(function() {
          $(this).trigger('change');
        });
      });
    },

    getMaxFreeBagQtyForWrapper: function($wrapper) {
      var itemKey = $wrapper && $wrapper.length ? $wrapper.data('item-key') : null;
      var bagSettingsKey = $wrapper && $wrapper.length ? ($wrapper.data('bag-settings-key') || itemKey) : null;
      var item;
      var recipientId;
      var recipient;
      var recipientQty;
      var $itemQtyInput;
      var inputQty;

      if (!itemKey && !bagSettingsKey) return 1;

      if (this.currentMode === 'multi') {
        recipientId = $wrapper.closest('[data-recipient-card]').data('recipient-id');
        recipient = recipientId ? this.getMultiRecipientById(recipientId) : null;
        recipientQty = recipient && recipient.items ? (parseInt(recipient.items[itemKey], 10) || 0) : 0;
        if (recipientQty > 0) return recipientQty;
      }

      // For split cards, use split line qty as max
      if (bagSettingsKey && this.isSplitLineKey(bagSettingsKey)) {
        var splitQty = this.getSingleSplitLineQty(bagSettingsKey);
        if (splitQty) return splitQty;
      }

      $itemQtyInput = $wrapper.find('.qty__input:not(.js-bagQtyInput):not(.js-splitQtyInput)').first();
      inputQty = parseInt($itemQtyInput.val(), 10) || 0;
      if (inputQty > 0) return inputQty;

      item = this.cartItems.find(function(cartItem) { return cartItem.key === (itemKey || bagSettingsKey); });
      return item ? item.quantity : 1;
    },

    validateErrNoshiFields: function($block, uid, mode, texts) {
      var self = this;
      var invalidMessage = '絵文字や記号は入力できない文字です。他の文字に変更してください。';
      var requiredMessage = '入力してください。';
      $block.find('.noshi-field-error').remove();
      $block.find('.is-error').removeClass('is-error');

      var checks = [];
      if (mode === 'uchiiwai') {
        if (window.NoshiDialogCommon && texts) {
          var normalizedErrUchiiwaiTexts = window.NoshiDialogCommon.normalizeUchiiwaiNoshiTexts(this.getErrorNoshiDialogOptions(uid));
          texts.name = normalizedErrUchiiwaiTexts.name;
          texts.furigana = normalizedErrUchiiwaiTexts.furigana;
        }
        checks = [
          { selector: '.js-errUchiiwaiName[data-uid="' + uid + '"]', value: texts && texts.name, required: false },
          { selector: '.js-errUchiiwaiKana[data-uid="' + uid + '"]', value: texts && texts.furigana, required: false }
        ];
      } else if (mode === 'corporate') {
        if (window.NoshiDialogCommon && texts) {
          var normalizedErrCorpTexts = window.NoshiDialogCommon.normalizeCorporateNoshiTexts(this.getErrorNoshiDialogOptions(uid));
          texts.company = normalizedErrCorpTexts.company;
          texts.title = normalizedErrCorpTexts.title;
          texts.name = normalizedErrCorpTexts.name;
        }
        checks = [
          { selector: '.js-errCorpCompany[data-uid="' + uid + '"]', value: texts && texts.company, required: false },
          { selector: '.js-errCorpTitle[data-uid="' + uid + '"]', value: texts && texts.title, required: false },
          { selector: '.js-errCorpName[data-uid="' + uid + '"]', value: texts && texts.name, required: false }
        ];
      } else {
        if (window.NoshiDialogCommon && texts) {
          var normalizedErrTexts = window.NoshiDialogCommon.normalizeNormalNoshiTexts(this.getErrorNoshiDialogOptions(uid));
          texts.name1 = normalizedErrTexts.name1;
          texts.name2 = normalizedErrTexts.name2;
        }
        checks = [
          { selector: '.js-errNoshiLine1[data-uid="' + uid + '"]', value: texts && texts.name1, required: false },
          { selector: '.js-errNoshiLine2[data-uid="' + uid + '"]', value: texts && texts.name2, required: false }
        ];
      }

      var hasError = false;
      checks.forEach(function(check) {
        var val = check.value || '';
        if (check.required && !val.trim()) {
          self.appendNoshiFieldValidationError($block.find(check.selector), requiredMessage);
          hasError = true;
        } else if (window.CharValidator) {
          var cvMsg = window.CharValidator.validate(String(val), 'noshi');
          if (cvMsg) {
            self.appendNoshiFieldValidationError($block.find(check.selector), cvMsg);
            hasError = true;
          }
        } else if (self.hasInvalidNoshiTextInput(val)) {
          self.appendNoshiFieldValidationError($block.find(check.selector), invalidMessage);
          hasError = true;
        }
      });

      return hasError;
    },

    handleBulkCorpToggle: function() {
      this.currentBulkNoshiMode = window.NoshiDialogCommon.handleCorpToggle(
        this.selectedBulkNoshiEntry,
        this.getBulkNoshiDialogOptions()
      );
    },

    showBulkNoshiInputFields: function(entry) {
      this.currentBulkNoshiMode = window.NoshiDialogCommon.showInputFieldsForEntry(
        entry,
        this.getBulkNoshiDialogOptions()
      );
    },

    /**
     * Returns master entries (noshi/ribbon) applicable to EVERY item currently
     * targeted by bulk wrapping. Intersection across all multiRecipients items.
     * Prevents users from picking options (allowed_products / excluded_options)
     * that don't apply to part of the cart.
     */
    getBulkApplicableEntries: function(masterData, applicableMap) {
      var allData = masterData || [];
      if (!allData.length) return [];
      var map = applicableMap || {};
      var itemKeys = [];
      (this.multiRecipients || []).forEach(function(r) {
        Object.keys(r.items || {}).forEach(function(key) {
          if ((r.items[key] || 0) > 0 && itemKeys.indexOf(key) === -1) itemKeys.push(key);
        });
      });
      if (itemKeys.length === 0) return allData;
      return allData.filter(function(_entry, i) {
        return itemKeys.every(function(key) {
          var indices = map[key];
          return Array.isArray(indices) && indices.indexOf(i) !== -1;
        });
      });
    },

    /**
     * Populate ribbon category select in the bulk modal.
     * Filtered to entries applicable to all bulk-target items.
     */
    renderBulkRibbonOptions: function() {
      var data = this.getBulkApplicableEntries(window.ribbonData, window.cartItemsApplicableRibbon);
      this.populateStickerUnifiedSelect(data, '#bulk-ribbon-unified');
    },

    /**
     * Save bulk wrapping settings
     */
    saveBulkWrapping: function() {
      var self = this;

      // Step 1: Read inputs
      var bag      = $('input[name="bulk_bag"]:checked').val();       // 'on' | 'off'
      var freeBag  = $('input[name="bulk_bag_free"]').is(':checked');
      var paidBag  = $('input[name="bulk_bag_paid"]').is(':checked');
      var wrapping = $('input[name="bulk_wrapping"]:checked').val();  // 'paper' | 'noshi' | 'ribbon'

      var paidBags = this.getPaidHandbagVariants();

      // Bag qty is auto-set to rQty (recipient's item quantity) — no UI qty controls

      // Step 2: Collect unique item keys across all recipients (for meta flag checking)
      var itemKeys = [];
      this.multiRecipients.forEach(function(r) {
        Object.keys(r.items || {}).forEach(function(key) {
          if ((r.items[key] || 0) > 0 && itemKeys.indexOf(key) === -1) itemKeys.push(key);
        });
      });

      var requiresBulkBagSelection = itemKeys.some(function(itemKey) {
        var item = self.cartItems.find(function(cartItem) { return cartItem.key === itemKey; });
        return item && self.isBagSelectionRequired(itemKey, item.variant_id);
      });
      var $bulkBagSection = $('#modalBulkWrapping .js-bulkBagToggle').first().closest('.p-delivery-item__row');
      if (requiresBulkBagSelection && bag !== 'on' && bag !== 'off') {
        self.showBagSelectionValidationError($bulkBagSection);
        return;
      }
      self.clearBagSelectionValidationError($bulkBagSection);

      // Resolve bulk-selected entries up front so per-item validation can verify
      // the chosen entry is applicable to each item (allowed_products / excluded_options).
      var preBulkNoshiEntry = this.selectedBulkNoshiEntry;
      var preBulkRibbonGid = $('#bulk-ribbon-unified').val() || '';
      var preBulkRibbonEntry = null;
      if (preBulkRibbonGid && window.ribbonData) {
        for (var pri = 0; pri < window.ribbonData.length; pri++) {
          if (window.ribbonData[pri].gid === preBulkRibbonGid) {
            preBulkRibbonEntry = window.ribbonData[pri];
            break;
          }
        }
      }

      // Step 3: Determine success vs failed per unique item key (meta flags are per item, not per recipient)
      var successKeys = [];
      var failInfoMap = {}; // key -> { bagFail, wrapFail, cartItem }

      itemKeys.forEach(function(key) {
        var meta = (window.cartItemsMetafields && window.cartItemsMetafields[key]) || {};
        var bagFail  = false;
        var bagForceOnFail = false;
        var wrapFail = false;

        var bulkBagRuleConfig = self.getBagRuleConfigByItemKey(key);
        var freeBagFail = false;
        if (bag === 'off' && bulkBagRuleConfig.forceOn) {
          bagForceOnFail = true;
        } else if (bag === 'on' && (bulkBagRuleConfig.disableOn || bulkBagRuleConfig.clearAllBags)) {
          bagFail = true;
        } else if (bag === 'on' && freeBag && !self.getFreeHandbagVariantForItem(key)) {
          freeBagFail = true;
        }

        if (wrapping === 'paper' && meta.flags_wrapping === false) {
          wrapFail = true;
        } else if (wrapping === 'noshi') {
          if (meta.flags_noshi === false) {
            wrapFail = true;
          } else {
            var applicableNoshi = window.cartItemsApplicableNoshi && window.cartItemsApplicableNoshi[key];
            if (!applicableNoshi || applicableNoshi.length === 0) {
              wrapFail = true;
            } else if (preBulkNoshiEntry) {
              // Selected entry must also be applicable to THIS item (allowed_products / excluded_options).
              var applicableNoshiGids = applicableNoshi.map(function(idx) { return (window.noshiData[idx] || {}).gid; });
              if (applicableNoshiGids.indexOf(preBulkNoshiEntry.gid) === -1) wrapFail = true;
            }
          }
        } else if (wrapping === 'ribbon') {
          if (meta.flags_ribbon === false) {
            wrapFail = true;
          } else {
            var applicableRibbon = window.cartItemsApplicableRibbon && window.cartItemsApplicableRibbon[key];
            if (!applicableRibbon || applicableRibbon.length === 0) {
              wrapFail = true;
            } else if (preBulkRibbonEntry) {
              var applicableRibbonGids = applicableRibbon.map(function(idx) { return (window.ribbonData[idx] || {}).gid; });
              if (applicableRibbonGids.indexOf(preBulkRibbonEntry.gid) === -1) wrapFail = true;
            }
          }
        }

        if (bagFail || bagForceOnFail || wrapFail || freeBagFail) {
          var cartItem = null;
          self.cartItems.forEach(function(i) { if (i.key === key) cartItem = i; });
          failInfoMap[key] = { bagFail: bagFail, bagForceOnFail: bagForceOnFail, wrapFail: wrapFail, freeBagFail: freeBagFail, cartItem: cartItem };
        } else {
          successKeys.push(key);
        }
      });

      // Build failedPairs: one entry per (recipient × item) that fails
      var failedItems = [];
      self.multiRecipients.forEach(function(r) {
        Object.keys(r.items || {}).forEach(function(key) {
          if ((r.items[key] || 0) === 0) return;
          var rQty = r.items[key] || 0;
          if (failInfoMap[key]) {
            var info = failInfoMap[key];
            failedItems.push({ key: key, cartItem: info.cartItem, recipient: r, bagFail: info.bagFail, bagForceOnFail: info.bagForceOnFail, wrapFail: info.wrapFail, freeBagFail: info.freeBagFail, maxFreeQty: rQty });
          }
        });
      });

      // Read bulk noshi/ribbon selections once (shared across all items)
      var bulkNoshiEntry = this.selectedBulkNoshiEntry;
      var bulkNoshiMode = this.currentBulkNoshiMode || 'normal';
      var bulkNoshiTexts;
      if (bulkNoshiMode === 'uchiiwai') {
        bulkNoshiTexts = {
          name:     $('#bulk-noshi-uchiiwai-name').val()     || '',
          furigana: $('#bulk-noshi-uchiiwai-furigana').val() || ''
        };
      } else if (bulkNoshiMode === 'corporate') {
        bulkNoshiTexts = {
          company: $('#bulk-corp-company').val() || '',
          title:   $('#bulk-corp-title').val()   || '',
          name:    $('#bulk-corp-name').val()    || ''
        };
      } else {
        bulkNoshiMode = 'normal';
        bulkNoshiTexts = {
          name1: $('#bulk-noshi-line1').val() || '',
          name2: $('#bulk-noshi-line2').val() || ''
        };
      }

      // Validate noshi selection and text fields before saving
      if (wrapping === 'noshi') {
        $('#modalBulkWrapping .noshi-field-error').remove();
        $('#modalBulkWrapping .is-error').removeClass('is-error');
        if (!bulkNoshiEntry) {
          var $tag = $('#bulk-noshi-tag');
          $tag.addClass('is-error').after('<p class="p-form-error-txt noshi-field-error">のしの種類をお選びください。</p>');
          return;
        }
        var bulkNoshiError = this.validateBulkNoshiFields(bulkNoshiMode, bulkNoshiTexts);
        if (bulkNoshiError) return;
        if (!this.bulkNoshiPreviewConfirmed) {
          window.alert('のしプレビューをご確認ください。');
          return;
        }
      }

      var bulkRibbonGid = $('#bulk-ribbon-unified').val() || '';
      var bulkRibbonEntry = null;
      if (bulkRibbonGid && window.ribbonData) {
        for (var ri = 0; ri < window.ribbonData.length; ri++) {
          if (window.ribbonData[ri].gid === bulkRibbonGid) { bulkRibbonEntry = window.ribbonData[ri]; break; }
        }
      }

      // Validate ribbon selection before saving
      if (wrapping === 'ribbon') {
        $('#modalBulkWrapping .noshi-field-error').remove();
        $('#modalBulkWrapping .is-error').removeClass('is-error');
        if (!bulkRibbonEntry) {
          var $ribbonItem = $('#bulk-ribbon-unified');
          $ribbonItem.addClass('is-error').after('<p class="p-form-error-txt noshi-field-error">リボンをお選びください。</p>');
          return;
        }
      }

      // Step 4 + 5: Count first; only apply and save if ALL items pass (all-or-nothing)
      var total = 0;
      var successCount = 0;
      self.multiRecipients.forEach(function(r) {
        Object.keys(r.items || {}).forEach(function(key) {
          if ((r.items[key] || 0) === 0) return;
          total++;
          if (successKeys.indexOf(key) !== -1) successCount++;
        });
      });
      var failCount = total - successCount;

      if (failCount === 0) {
        // All items pass — clear any stale pending context then apply settings to every recipient
        this._pendingBulkContext = null;
        self.multiRecipients.forEach(function(r) {
          Object.keys(r.items || {}).forEach(function(key) {
            var rQty = r.items[key] || 0;
            if (rQty === 0) return;

            // Clear split lines — bulk setting applies to all units uniformly
            if (r.splitLines && r.splitLines[key]) {
              // Also clean up split-line-keyed giftSettings entries
              var splitCfg = r.splitLines[key];
              if (splitCfg && Array.isArray(splitCfg.lines)) {
                splitCfg.lines.forEach(function(line) {
                  var slk = self.makeSplitLineKey(key, line.id);
                  if (r.giftSettings && r.giftSettings[slk]) delete r.giftSettings[slk];
                });
              }
              delete r.splitLines[key];
            }

            var s = self.getRecipientItemSettings(r, key);

            // Bags
            s.bags = {};
            s.freeBagEnabled = false;
            s.paidBagEnabled = false;
            s.bagSelection = (bag === 'on' || bag === 'off') ? bag : null;

            var bagRuleConf = self.getBagRuleConfigByItemKey(key);
            if (bagRuleConf.forceOn) {
              s.bagSelection = 'on';
              // Always-attach item: ignore modal bag/freeBag selection, force free bag = item qty
              var forcedFreeVar = self.getFreeHandbagVariantForItem(key);
              if (forcedFreeVar) {
                s.freeBagEnabled = true;
                s.bags[forcedFreeVar.shopifyProductVariantId] = rQty;
              }
            } else if (bag === 'on') {
              var freeVariant = self.getFreeHandbagVariantForItem(key);
              if (freeBag && freeVariant) {
                var freeVid = freeVariant.shopifyProductVariantId;
                s.freeBagEnabled = true;
                s.bags[freeVid] = rQty;
              }
              if (paidBag && paidBags.length > 0) {
                var recommendedSize = self.getRecommendedBagSizeByItemKey(key);
                var matchedPb = null;
                paidBags.forEach(function(pb) {
                  var sizeMatch = pb.title.match(/[-−]\s*(.+)$/);
                  var sizeLabel = sizeMatch ? sizeMatch[1].trim() : pb.title;
                  if (recommendedSize && self.normalizeBagSizeLabel(sizeLabel) === recommendedSize) matchedPb = pb;
                });
                if (matchedPb) {
                  s.bags[matchedPb.shopifyProductVariantId] = rQty;
                  s.paidBagEnabled = true;
                }
              }
            }

            // Wrapping
            s.noshi = null; s.ribbon = null; s.wrapOnly = false; s.noWrap = false;

            if (wrapping === 'paper') {
              s.wrapOnly = true;
            } else if (wrapping === 'noshi') {
              if (bulkNoshiEntry) {
                s.noshi = {
                  gid:       bulkNoshiEntry.gid      || '',
                  name:      bulkNoshiEntry.name     || '',
                  category:  bulkNoshiEntry.category || '',
                  type:      bulkNoshiEntry.type     || '',
                  omotegaki: bulkNoshiEntry.omotegaki || '',
                  subtitle:  bulkNoshiEntry.subtitle  || '',
                  image:     bulkNoshiEntry.image     || '',
                  mode:      bulkNoshiMode,
                  texts:     bulkNoshiTexts,
                  previewImage: '',
                  count: rQty
                };
              }
            } else if (wrapping === 'ribbon') {
              if (bulkRibbonEntry) {
                s.ribbon = {
                  gid:      bulkRibbonEntry.gid   || '',
                  name:     bulkRibbonEntry.name  || '',
                  category: self.getRibbonCategory(bulkRibbonEntry),
                  image:    bulkRibbonEntry.image || ''
                };
              }
            }
          });
        });

        this.saveData();
        this.renderCartItems();
        this.updateSummary();
        var shipmentTotal = this.multiRecipients.filter(function(r) {
          return Object.keys(r.items || {}).some(function(key) { return (r.items[key] || 0) > 0; });
        }).length;
        this.showBulkWrappingSuccessModal(shipmentTotal, shipmentTotal);
      } else {
        // Store pending context so saveErrorWrappingItems can apply success-item settings later
        this._pendingBulkContext = {
          successKeys: successKeys,
          originalTotal: total,
          originalSuccessCount: successCount,
          bag: bag, freeBag: freeBag, paidBag: paidBag,
          paidBags: paidBags,
          wrapping: wrapping,
          bulkNoshiEntry: bulkNoshiEntry, bulkNoshiMode: bulkNoshiMode, bulkNoshiTexts: bulkNoshiTexts,
          bulkRibbonEntry: bulkRibbonEntry
        };
        if (successCount > 0) {
          this.showBulkWrappingErrorModal('partial', successCount, failedItems, total, bag, wrapping);
        } else {
          this.showBulkWrappingErrorModal('full', 0, failedItems, total, bag, wrapping);
        }
      }
    },

    showBulkWrappingSuccessModal: function(successCount, totalCount) {
      var $modal = $('#modalBulkWrappingSuccess');
      $modal.find('[data-wrap-pill-total]').text(totalCount);
      $modal.find('[data-wrap-pill-success]').text(successCount);
      $modal.find('[data-wrap-box-total]').text(totalCount);
      $modal.find('[data-wrap-box-success]').text(successCount);
      this._preservePendingBulkContextOnClose = true;
      this.closeModal();
      this.openModal('modalBulkWrappingSuccess');
    },

    showBulkWrappingErrorModal: function(mode, successCount, failedItems, totalCount, bag, wrapping) {
      var failCount = failedItems.length;
      var modalId = (mode === 'full') ? 'modalBulkError' : 'modalBulkWraning';
      var $modal = $('#' + modalId);

      $modal.find('[data-wrap-success-pill]').text(successCount);
      $modal.find('[data-wrap-fail-pill]').text(failCount);
      $modal.find('[data-wrap-total]').text(totalCount);
      $modal.find('[data-wrap-success]').text(successCount);
      $modal.find('[data-wrap-fail]').text(failCount);

      $modal.find('[data-wrap-error-list]').html(
        this.renderBulkWrappingErrorList(failedItems, bag, wrapping)
      );

      this._preservePendingBulkContextOnClose = true;
      this.closeModal();
      this.openModal(modalId);
      this.initializeBulkWrappingErrorModal($modal);
    },

    renderBulkWrappingErrorList: function(failedItems, bag, wrapping) {
      var self = this;
      var html = '';
      var shippingBadges = {
        ambient: { label: '常温便', icon: window.shippingAssets ? window.shippingAssets.iconTruck : '', cssClass: 'ship-badge--ambient' },
        frozen: { label: '冷凍便', icon: window.shippingAssets ? window.shippingAssets.iconFrozen : '', cssClass: 'ship-badge--frozen' },
        mail: { label: 'メール便', icon: window.shippingAssets ? window.shippingAssets.iconMail : '', cssClass: 'ship-badge--mail' }
      };
      var paidBags = this.getPaidHandbagVariants();

      // Group by recipient (address), then by shipmentKey (respects mixed_loading_disable),
      // preserving UI order matching groupItemsIntoShipments logic
      var groupMap = {};   // recipientId -> { shipmentKey -> { shippingType, items: [fi] } }
      var groupOrder = []; // recipientId order
      failedItems.forEach(function(fi) {
        var rid = fi.recipient ? fi.recipient.id : 'single';
        var st = self.getItemShippingType(fi.cartItem);
        var isMixed = self.getItemMixedLoadingDisabled(fi.cartItem);
        var sk = isMixed ? (st + '_' + fi.key) : st;
        if (!groupMap[rid]) { groupMap[rid] = {}; groupOrder.push(rid); }
        if (!groupMap[rid][sk]) groupMap[rid][sk] = { shippingType: st, items: [] };
        groupMap[rid][sk].items.push(fi);
      });
      // Sort address groups by multiRecipients UI order
      var recipientOrder = self.multiRecipients.map(function(r) { return r.id; });
      groupOrder.sort(function(a, b) {
        if (a === 'single') return -1;
        if (b === 'single') return 1;
        return recipientOrder.indexOf(a) - recipientOrder.indexOf(b);
      });

      groupOrder.forEach(function(rid) {
        var shipmentMap = groupMap[rid];

        // Find recipient object from first fi in this group
        var recipientObj = null;
        if (rid !== 'single') {
          for (var i = 0; i < failedItems.length; i++) {
            if (failedItems[i].recipient && failedItems[i].recipient.id === rid) {
              recipientObj = failedItems[i].recipient;
              break;
            }
          }
        }

        // Address group wrapper
        html += '<div class="p-bulk-error-address-group p-delivery__addr">';
        if (recipientObj) {
          var recipientObjAddress2 = self.getAddress2WithCompany(recipientObj);
          var rAddr = '〒' + self.formatZipDisplay(recipientObj.zip || '') + ' ' + (recipientObj.prefecture || '') + ' ' + (recipientObj.city || '') + ' ' + (recipientObj.address1 || '');
          if (recipientObjAddress2) rAddr += ' ' + recipientObjAddress2;
          var rName = (recipientObj.lastName || '') + ' ' + (recipientObj.firstName || '') + ' 様';
          html += '<div class="p-delivery-addr"><p class="p-product-small__address p-delivery-addr__text">' + self.escapeHtml(rAddr.trim()) + '</p>';
          html += '<p class="p-product-small__recipient p-delivery-addr__text">' + self.escapeHtml(rName.trim()) + '</p></div>';
        }

        // Shipment groups in order: ambient-mixable, ambient-nonmixable..., frozen-mixable, etc.
        var shipmentKeyOrder = [];
        ['ambient', 'frozen', 'mail'].forEach(function(st) {
          if (shipmentMap[st]) shipmentKeyOrder.push(st); // mixable group
          Object.keys(shipmentMap).forEach(function(sk) {
            if (sk !== st && sk.indexOf(st + '_') === 0) shipmentKeyOrder.push(sk); // non-mixable
          });
        });

        shipmentKeyOrder.forEach(function(sk) {
          var entry = shipmentMap[sk];
          if (!entry || entry.items.length === 0) return;
          var fiList = entry.items;
          var st = entry.shippingType;
          var badge = shippingBadges[st];

          html += '<div class="p-bulk-error-shipment-group">';
          if (badge) {
            html += '<div class="ship-badge-list">';
            html += '<div class="ship-badge ' + badge.cssClass + '">';
            html += '<span class="ship-badge__icon" aria-hidden="true"><img src="' + badge.icon + '" alt=""></span>';
            html += '<span class="ship-badge__text">' + badge.label + '</span>';
            html += '</div>';
            html += '</div>';
          }

          fiList.forEach(function(fi) {
            var item = fi.cartItem;
            if (!item) return;
            var meta = (window.cartItemsMetafields && window.cartItemsMetafields[fi.key]) || {};
            var uid = ((fi.recipient ? fi.recipient.id : '') + '_' + fi.key).replace(/[^a-zA-Z0-9]/g, '_');
            var itemKey = fi.key;

            html += '<div class="p-bulk-error-item" data-fail-uid="' + uid + '" data-fail-key="' + self.escapeHtml(itemKey) + '" data-fail-recipient-id="' + self.escapeHtml(fi.recipient ? fi.recipient.id : '') + '"' + (fi.locked ? ' data-freeqty-locked="1"' : '') + '>';

            // Product info
            html += '<div class="p-product-small">';
            var imgUrl = (item.featured_image && item.featured_image.url) ? item.featured_image.url : '';
            html += '<div class="p-product-small__thumb"><img src="' + imgUrl + '" alt></div>';
            html += '<div class="p-delivery-item__info p-product-small__info"><p>' + self.escapeHtml(item.product_title || item.title) + '</p></div>';
            html += '</div>';

            // ── Bag section ──
            var pendingCtx = self._pendingBulkContext || {};
            var bulkBagVal = pendingCtx.bag;
            var isLockedFreeQtyFail = fi.freeQtyFail && fi.locked;
            var itemBagRuleConf = self.getBagRuleConfigByItemKey(itemKey);
            var isForceOnBag = fi.bagForceOnFail || itemBagRuleConf.forceOn;
            var isDisableOnBag = itemBagRuleConf.disableOn; // 「付けられない」
            var bagSelectionRequired = !fi.bagFail && !isLockedFreeQtyFail && !isForceOnBag && !isDisableOnBag && self.isBagSelectionRequired(itemKey, item.variant_id);
            var preserveBagFromBulk = !fi.bagFail && !fi.freeBagFail && (bulkBagVal === 'on' || bulkBagVal === 'off');
            var bagOffChecked;
            if (isLockedFreeQtyFail || isForceOnBag) {
              bagOffChecked = '';
            } else if (preserveBagFromBulk) {
              bagOffChecked = bulkBagVal === 'off' ? ' checked' : '';
            } else {
              bagOffChecked = (isDisableOnBag || !bagSelectionRequired) ? ' checked' : '';
            }
            var bagOffDisabledAttr = (isLockedFreeQtyFail || isForceOnBag || isDisableOnBag) ? ' disabled' : '';
            html += '<div class="p-product-small__row">';
            html += '<div class="p-product-small__row-ttl">手提げ袋';
            if (isForceOnBag) {
              html += '<span class="c-badge c-badge--red c-badge--required">選択必須</span>';
            }
            html += '</div>';
            html += '<div>';
            html += '<div class="p-bag-toggle js-bagToggle">';
            html += '<label class="p-bag-toggle__item option-card' + (isForceOnBag ? ' is-disable' : '') + '"><input class="option-card__radio"" type="radio" name="errBag_' + uid + '" value="off"' + bagOffChecked + bagOffDisabledAttr + '><span>手提げ袋なし</span></label>';
            html += '</div>';
            if (isForceOnBag) {
              html += '<div class="p-bag-toggle__note">この商品は手提げ袋を必ず付ける必要がございます。</div>';
            } else if (fi.bagFail) {
              html += '<div class="p-bag-toggle__note">こちらの商品には手提げ袋をお付けできません。</div>';
            } else if (fi.freeBagFail) {
              html += '<div class="p-bag-toggle__note">こちらの商品には無料手提げ袋のご用意がございません。<br>有料の手提げ袋からお選びください。</div>';
            } else if (isLockedFreeQtyFail) {
              html += '<div class="p-bag-toggle__note">この商品には必ず無料手提げ袋が付きます。<br>数量はお届け先の商品数（' + fi.maxFreeQty + '個）に設定されます。</div>';
            } else if (fi.freeQtyFail) {
              html += '<div class="p-bag-toggle__note">無料手提げ袋の数量がお届け先の商品数を超えています。上限は' + fi.maxFreeQty + '個です。</div>';
            }
            // Bag sub-options stay visible in the error modal when available
            var freeBagForItem = self.getFreeHandbagVariantForItem(itemKey);
            var hasBagOptions = !fi.bagFail && (freeBagForItem || (!itemBagRuleConf.hidePaid && paidBags.length > 0));
            if (hasBagOptions) {
            html += '<div class="p-bag-box js-bagBox">';
            html += '<div class="c-card-01">';
            if (freeBagForItem) {
              var freeBagPreChecked = preserveBagFromBulk && pendingCtx.freeBag && bulkBagVal === 'on';
              var freeCbLockedAttr = (isLockedFreeQtyFail || isForceOnBag) ? ' checked disabled' : (freeBagPreChecked ? ' checked' : '');
              html += '<div class="c-card-01__item">';
              html += '<label class="option-card"><input type="checkbox" class="option-card__checkbox js-errBagCb" data-err-bag-type="free" data-uid="' + uid + '"' + freeCbLockedAttr + '><div class="option-card__inner"><div class="option-card__text"><p class="option-card__title">手提げ袋（クリア）<span class="c-badge c-badge--free">無料</span></p></div></div></label>';
              html += '</div>';
            }
            if (!itemBagRuleConf.hidePaid && paidBags.length > 0) {
              html += '<div class="c-card-01__item">';
              var paidBagPreChecked = preserveBagFromBulk && pendingCtx.paidBag && bulkBagVal === 'on';
              html += '<label class="option-card"><input type="checkbox" class="option-card__checkbox js-errBagCb" data-err-bag-type="paid" data-uid="' + uid + '"' + (paidBagPreChecked ? ' checked' : '') + '><div class="option-card__inner"><div class="option-card__text"><p class="option-card__title">手提げ袋（紙） </p></div></div></label>';
              html += '<p class="c-text-note" style="margin-top:4px;font-size:11px;">※推奨サイズの手提げ袋が商品数と同数選択されます。</p>';
              html += '</div>';
            }
            html += '</div></div>'; // c-card-01, p-bag-box
            } // end hasBagOptions
            html += '</div></div>'; // inner, p-product-small__row

            // ── Wrapping section ──
            var wrapState = self.getBulkWrappingChoicesForItem(itemKey);
            var wrapChoices = wrapState.choices;
            var itemNoshiData = wrapState.noshiData;
            var itemRibbonData = wrapState.ribbonData;
            var wrapLocked = wrapChoices.length === 1;
            var selectedWrapValue = '';
            var itemNoshiPurposeOpts = self.getNoshiPurposeOptions(itemNoshiData);
            var initialNoshiEntry = null;
            var initialRibbonEntry = null;
            var initialNoshiPurpose = '';
            var itemNoshiTagOpts = [];
            var initialNoshiGid = '';
            var purposeLocked = false;
            var tagLocked = false;
            var initialRibbonGid = '';
            var ribbonLocked = itemRibbonData.length === 1;

            if (wrapChoices.some(function(choice) { return choice.value === wrapping; })) {
              selectedWrapValue = wrapping;
            } else if (wrapLocked) {
              selectedWrapValue = wrapChoices[0].value;
            }

            if (pendingCtx.bulkNoshiEntry && itemNoshiData.length) {
              initialNoshiEntry = itemNoshiData.find(function(entryItem) {
                return entryItem.gid === pendingCtx.bulkNoshiEntry.gid;
              }) || null;
            }
            if (pendingCtx.bulkRibbonEntry && itemRibbonData.length) {
              initialRibbonEntry = itemRibbonData.find(function(entryItem) {
                return entryItem.gid === pendingCtx.bulkRibbonEntry.gid;
              }) || null;
            }

            if (initialNoshiEntry) {
              initialNoshiPurpose = initialNoshiEntry.type || '';
            } else if (itemNoshiPurposeOpts.length === 1) {
              initialNoshiPurpose = itemNoshiPurposeOpts[0].type || '';
              purposeLocked = true;
            }
            if (!purposeLocked && itemNoshiPurposeOpts.length === 1) {
              purposeLocked = true;
            }

            if (initialNoshiPurpose) {
              itemNoshiTagOpts = itemNoshiData.filter(function(entryItem) {
                return entryItem.type === initialNoshiPurpose;
              });
              if (initialNoshiEntry && itemNoshiTagOpts.some(function(entryItem) { return entryItem.gid === initialNoshiEntry.gid; })) {
                initialNoshiGid = initialNoshiEntry.gid;
              } else if (itemNoshiTagOpts.length === 1) {
                initialNoshiGid = itemNoshiTagOpts[0].gid || '';
                tagLocked = true;
              }
              if (!tagLocked && itemNoshiTagOpts.length === 1) {
                tagLocked = true;
              }
            }

            if (initialRibbonEntry) {
              initialRibbonGid = initialRibbonEntry.gid || '';
            } else if (itemRibbonData.length === 1) {
              initialRibbonGid = itemRibbonData[0].gid || '';
            }

            html += '<div class="p-product-small__row">';
            html += '<div class="p-product-small__row-ttl">包装・のし・リボン</div>';
            html += '<div>';
            html += '<div class="p-bag-toggle js-errWrapChoices" data-uid="' + uid + '">';
            wrapChoices.forEach(function(choice) {
              var checkedAttr = selectedWrapValue === choice.value ? ' checked' : '';
              var disabledAttr = wrapLocked ? ' disabled' : '';
              var disabledClass = wrapLocked ? ' is-disable' : '';
              html += '<label class="p-bag-toggle__item option-card' + disabledClass + '">';
              html += '<input class="option-card__radio js-errWrapRadio" type="radio" name="errWrap_' + uid + '" data-uid="' + uid + '" value="' + self.escapeHtml(choice.value) + '"' + checkedAttr + disabledAttr + '>';
              html += '<span>' + choice.label + '</span>';
              html += '</label>';
            });
            html += '</div>';

            // Noshi sub-options (always rendered, shown when noshi radio selected)
            html += '<div class="js-err-noshi-opts p-bulk-subopts" data-uid="' + uid + '" style="' + (selectedWrapValue === 'noshi' ? '' : 'display:none;') + '">';
            html += '<div class="p-row">';
            html += '<div class="p-row-item"><select class="c-form-select js-errNoshiPurpose" data-uid="' + uid + '"' + (purposeLocked ? ' disabled' : '') + '>';
            if (!purposeLocked) {
              html += '<option value="">用途をお選びください</option>';
            }
            itemNoshiPurposeOpts.forEach(function(opt) {
              var selectedAttr = initialNoshiPurpose === opt.type ? ' selected' : '';
              html += '<option value="' + self.escapeHtml(opt.type) + '"' + selectedAttr + '>' + self.escapeHtml(opt.type) + '</option>';
            });
            html += '</select></div>';

            html += '<div class="p-row-item"><select class="c-form-select js-errNoshiTag" data-uid="' + uid + '" data-selected-gid="' + self.escapeHtml(initialNoshiGid) + '"' + (tagLocked ? ' disabled' : '') + '>';
            if (!tagLocked) {
              html += '<option value="">のしの種類をお選びください</option>';
            }
            itemNoshiTagOpts.forEach(function(entryItem) {
              var selectedAttr = initialNoshiGid === entryItem.gid ? ' selected' : '';
              html += '<option value="' + self.escapeHtml(entryItem.gid) + '"' + selectedAttr + '>' + self.escapeHtml(entryItem.name) + '</option>';
            });
            html += '</select></div>';
            html += '</div>';

            // Name input fields
            html += '<div class="js-err-noshi-fields" data-uid="' + uid + '" style="display:none;">';
            html += '<h3 class="c-ttl-04 js-err-noshi-input-title" data-uid="' + uid + '">お名前</h3>';
            html += '<input type="hidden" class="js-errNoshiCurrentMode" data-uid="' + uid + '" value="">';
            html += '<div class="js-err-noshi-uchiiwai" data-uid="' + uid + '" style="display:none;">';
            html += '<div class="p-row"><div class="p-row-item"><label class="c-form-label">お名前</label><input type="text" class="c-form-input js-errUchiiwaiName" data-uid="' + uid + '" maxlength="8" placeholder="太郎"></div></div>';
            html += '<div class="p-row"><div class="p-row-item"><label class="c-form-label">よみがな</label><input type="text" class="c-form-input js-errUchiiwaiKana" data-uid="' + uid + '" maxlength="8" placeholder="たろう"></div></div>';
            html += '</div>';
            html += '<div class="js-err-noshi-normal" data-uid="' + uid + '">';
            html += '<div class="p-row"><div class="p-row-item"><label class="c-form-label">お名前（8文字以内）</label><input type="text" class="c-form-input js-errNoshiLine1" data-uid="' + uid + '" maxlength="8" placeholder="田中"></div></div>';
            html += '<div class="p-row"><div class="p-row-item"><label class="c-form-label">連名（8文字以内）</label><input type="text" class="c-form-input js-errNoshiLine2" data-uid="' + uid + '" maxlength="8" placeholder="太郎"></div></div>';
            html += '</div>';
            html += '<div class="js-err-noshi-corp-block" data-uid="' + uid + '">';
            html += '<label class="c-form-label"><input type="checkbox" class="js-errCorpToggle" data-uid="' + uid + '"> 法人名を使用する</label>';
            html += '<div class="js-err-noshi-corp-fields" data-uid="' + uid + '" style="display:none;">';
            html += '<div class="p-row"><div class="p-row-item"><label class="c-form-label">会社名</label><input type="text" class="c-form-input js-errCorpCompany" data-uid="' + uid + '" maxlength="18"></div></div>';
            html += '<div class="p-row"><div class="p-row-item"><label class="c-form-label">肩書き</label><input type="text" class="c-form-input js-errCorpTitle" data-uid="' + uid + '" maxlength="8"></div></div>';
            html += '<div class="p-row"><div class="p-row-item"><label class="c-form-label">お名前</label><input type="text" class="c-form-input js-errCorpName" data-uid="' + uid + '" maxlength="8"></div></div>';
            html += '</div>';
            html += '<p class="p-corp__note js-err-noshi-name-note" data-uid="' + uid + '">※お名前１のみの入力も可能です。 連名（8文字以内）を入力すると縦書きで2行表示されます。</p>';
            html += '</div>';
            html += '</div>'; // js-err-noshi-fields
            html += '</div>'; // js-err-noshi-opts

            // Ribbon sub-options (always rendered, shown when ribbon radio selected)
            html += '<div class="js-err-ribbon-opts p-bulk-subopts" data-uid="' + uid + '" style="' + (selectedWrapValue === 'ribbon' ? '' : 'display:none;') + '">';
            html += '<div class="p-row">';
            var errRibbonHtml = ribbonLocked ? '' : '<option value="">リボン・シール・ラッピングを選択してください</option>';
            itemRibbonData.forEach(function(r) {
              var cat = self.getRibbonCategory(r);
              var selectedAttr = initialRibbonGid === r.gid ? ' selected' : '';
              errRibbonHtml += '<option value="' + self.escapeHtml(r.gid || '') + '" data-category="' + self.escapeHtml(cat) + '" data-image="' + self.escapeHtml(r.image || '') + '"' + selectedAttr + '>[' + self.escapeHtml(cat) + '] ' + self.escapeHtml(r.name || '') + '</option>';
            });
            html += '<div class="p-row-item"><select class="c-form-select js-errRibbonUnified" data-uid="' + uid + '"' + (ribbonLocked ? ' disabled' : '') + '>' + errRibbonHtml + '</select></div>';
            html += '</div>';
            html += '</div>'; // js-err-ribbon-opts

            html += '</div></div>'; // inner, p-product-small__row
            html += '</div>'; // p-bulk-error-item
          });

          html += '</div>'; // p-bulk-error-shipment-group
        });

        html += '</div>'; // p-bulk-error-address-group
      });
      return html;
    },

    saveErrorWrappingItems: function() {
      var self = this;
      var paidBags = this.getPaidHandbagVariants();
      var successCount = 0;
      var stillFailed = [];
      var hasAnyFormError = false;

      $('[data-fail-uid]').each(function() {
        var $block = $(this);
        var uid = $block.data('fail-uid');
        var itemKey = $block.data('fail-key');
        var recipientId = $block.data('fail-recipient-id');
        if (!itemKey || !recipientId) return;

        var recipient = self.getMultiRecipientById(recipientId);
        if (!recipient) return;

        // Clear split lines for this item on this recipient
        if (recipient.splitLines && recipient.splitLines[itemKey]) {
          var splitCfg = recipient.splitLines[itemKey];
          if (splitCfg && Array.isArray(splitCfg.lines)) {
            splitCfg.lines.forEach(function(line) {
              var slk = self.makeSplitLineKey(itemKey, line.id);
              if (recipient.giftSettings && recipient.giftSettings[slk]) delete recipient.giftSettings[slk];
            });
          }
          delete recipient.splitLines[itemKey];
        }

        var s = self.getRecipientItemSettings(recipient, itemKey);

        // Bags
        s.bags = {}; s.freeBagEnabled = false; s.paidBagEnabled = false; s.bagSelection = null;
        var rItemQty = (recipient.items && recipient.items[itemKey]) || 1;
        var currentBagRuleConf = self.getBagRuleConfigByItemKey(itemKey);
        var bagVal = null;
        var bagStillFailed = false;
        var isLockedFreeQtyBlock = $block.data('freeqty-locked') == '1';
        var bagItem = self.cartItems.find(function(ci) { return ci.key === itemKey; });
        var hasCheckedBagOption = $block.find('.js-errBagCb:checked').length > 0;
        if (currentBagRuleConf.forceOn || isLockedFreeQtyBlock || hasCheckedBagOption) {
          bagVal = 'on';
        } else if ($block.find('input[name="errBag_' + uid + '"][value="off"]').is(':checked')) {
          bagVal = 'off';
        }
        var bagSelectionRequired = self.isBagSelectionRequired(itemKey, bagItem && bagItem.variant_id);
        if (bagSelectionRequired && bagVal !== 'on' && bagVal !== 'off') {
          self.showBagSelectionValidationError($block);
          hasAnyFormError = true;
          return;
        }
        self.clearBagSelectionValidationError($block);
        if (currentBagRuleConf.forceOn) {
          s.bagSelection = 'on';
          var forcedVariant = self.getFreeHandbagVariantForItem(itemKey);
          if (forcedVariant) {
            s.bags[forcedVariant.shopifyProductVariantId] = rItemQty;
            s.freeBagEnabled = true;
          }
        } else if (bagVal === 'off') {
          s.bagSelection = 'off';
        } else if (bagVal === 'on') {
          s.bagSelection = 'on';
          var freeVariant = self.getFreeHandbagVariantForItem(itemKey);
          if (isLockedFreeQtyBlock && freeVariant) {
            // Locked item: auto-apply rItemQty without reading from input
            var lockedFreeVid = freeVariant.shopifyProductVariantId;
            s.bags[lockedFreeVid] = rItemQty;
            s.freeBagEnabled = true;
          } else {
            var freeCb = $block.find('[data-err-bag-type="free"]');
            if (freeCb.is(':checked') && freeVariant) {
              var freeVid = freeVariant.shopifyProductVariantId;
              s.bags[freeVid] = rItemQty;
              s.freeBagEnabled = true;
            }
          }
          if ($block.find('[data-err-bag-type="paid"]').is(':checked') && paidBags.length > 0) {
            var errRecommendedSize = self.getRecommendedBagSizeByItemKey(itemKey);
            var errMatchedPb = null;
            paidBags.forEach(function(pb) {
              var sm = pb.title.match(/[-−]\s*(.+)$/);
              var sl = sm ? sm[1].trim() : pb.title;
              if (errRecommendedSize && self.normalizeBagSizeLabel(sl) === errRecommendedSize) errMatchedPb = pb;
            });
            var errTargetPb = errMatchedPb || paidBags[0];
            s.bags[errTargetPb.shopifyProductVariantId] = rItemQty;
            s.paidBagEnabled = true;
          }
        }

        // Wrapping
        // hasFormError: user-fixable validation error in this block (no re-render, just inline errors)
        var hasFormError = false;
        s.noshi = null; s.ribbon = null; s.wrapOnly = false; s.noWrap = false;
        var wrapState = self.getBulkWrappingChoicesForItem(itemKey);
        var wrapVal = $block.find('input[name="errWrap_' + uid + '"]:checked').val();
        if (!wrapVal && wrapState.choices.length === 1) {
          wrapVal = wrapState.choices[0].value;
        }
        self.clearErrorWrapValidation($block);
        if (!wrapVal && wrapState.choices.length > 1) {
          self.appendErrorWrapValidation($block, '包装方法を選択してください。');
          hasFormError = true;
        } else if (wrapVal === 'none') {
          s.noWrap = true;
        } else if (wrapVal === 'paper') {
          s.wrapOnly = true;
        } else if (wrapVal === 'noshi') {
          var noshiGid = $block.find('.js-errNoshiTag[data-uid="' + uid + '"]').val();
          var noshiData = wrapState.noshiData;
          var noshiEntry = null;
          for (var i = 0; i < noshiData.length; i++) { if (noshiData[i].gid === noshiGid) { noshiEntry = noshiData[i]; break; } }
          if (!noshiGid) {
            // Noshi type not selected → inline error, keep form
            $block.find('.noshi-field-error').remove();
            $block.find('.is-error').removeClass('is-error');
            self.appendNoshiFieldValidationError($block.find('.js-errNoshiTag[data-uid="' + uid + '"]'), 'のしの種類をお選びください。');
            hasFormError = true;
          } else if (noshiEntry) {
            var noshiMode = $block.find('.js-errNoshiCurrentMode[data-uid="' + uid + '"]').val() || 'normal';
            var noshiTexts;
            if (noshiMode === 'uchiiwai') {
              noshiTexts = { name: $block.find('.js-errUchiiwaiName[data-uid="' + uid + '"]').val() || '', furigana: $block.find('.js-errUchiiwaiKana[data-uid="' + uid + '"]').val() || '' };
            } else if (noshiMode === 'corporate') {
              noshiTexts = { company: $block.find('.js-errCorpCompany[data-uid="' + uid + '"]').val() || '', title: $block.find('.js-errCorpTitle[data-uid="' + uid + '"]').val() || '', name: $block.find('.js-errCorpName[data-uid="' + uid + '"]').val() || '' };
            } else {
              noshiMode = 'normal';
              noshiTexts = { name1: $block.find('.js-errNoshiLine1[data-uid="' + uid + '"]').val() || '', name2: $block.find('.js-errNoshiLine2[data-uid="' + uid + '"]').val() || '' };
            }
            var noshiFieldError = self.validateErrNoshiFields($block, uid, noshiMode, noshiTexts);
            if (!noshiFieldError) {
              s.noshi = { gid: noshiEntry.gid || '', name: noshiEntry.name || '', category: noshiEntry.category || '', type: noshiEntry.type || '', omotegaki: noshiEntry.omotegaki || '', subtitle: noshiEntry.subtitle || '', image: noshiEntry.image || '', mode: noshiMode, texts: noshiTexts, previewImage: '', count: rItemQty };
            } else {
              hasFormError = true;
            }
          }
        } else if (wrapVal === 'ribbon') {
          var ribbonGid = $block.find('.js-errRibbonUnified[data-uid="' + uid + '"]').val();
          var ribbonData = wrapState.ribbonData;
          var ribbonEntry = null;
          for (var j = 0; j < ribbonData.length; j++) { if (ribbonData[j].gid === ribbonGid) { ribbonEntry = ribbonData[j]; break; } }
          if (!ribbonGid) {
            // Ribbon not selected → inline error, keep form
            $block.find('.noshi-field-error').remove();
            $block.find('.is-error').removeClass('is-error');
            self.appendNoshiFieldValidationError($block.find('.js-errRibbonUnified[data-uid="' + uid + '"]'), 'リボンをお選びください。');
            hasFormError = true;
          } else if (ribbonEntry) {
            s.ribbon = { gid: ribbonEntry.gid || '', name: ribbonEntry.name || '', category: self.getRibbonCategory(ribbonEntry), image: ribbonEntry.image || '' };
          }
        }

        if (hasFormError) {
          // Form validation error: inline errors already shown, do not push to stillFailed (no re-render)
          hasAnyFormError = true;
        } else {
          // Genuine failure check (bag qty exceeded, item truly incompatible with chosen option)
          var isStillFailed = bagStillFailed || (wrapVal === 'noshi' && !s.noshi) || (wrapVal === 'ribbon' && !s.ribbon);
          if (isStillFailed) {
            var cartItem = null;
            self.cartItems.forEach(function(ci) { if (ci.key === itemKey) cartItem = ci; });
            stillFailed.push({ key: itemKey, cartItem: cartItem, recipient: recipient, bagFail: bagStillFailed, wrapFail: !bagStillFailed, freeBagFail: false, maxFreeQty: rItemQty });
          } else {
            successCount++;
          }
        }
      });

      // If there are only form validation errors (no genuine item failures), keep the current
      // modal DOM as-is so inline errors remain visible — do not re-render.
      if (stillFailed.length === 0 && hasAnyFormError) return;

      var ctx = self._pendingBulkContext;
      var origTotal = ctx ? ctx.originalTotal : (successCount + stillFailed.length);
      var origSuccessCount = ctx ? ctx.originalSuccessCount : 0;
      var finalSuccessCount = origSuccessCount + successCount;

      if (stillFailed.length === 0) {
        // All error items resolved — also apply pending bulk settings to originally-successful items
        if (self._pendingBulkContext) {
          var ctx = self._pendingBulkContext;
          var ctxPaidBags = ctx.paidBags;
          self.multiRecipients.forEach(function(r) {
            Object.keys(r.items || {}).forEach(function(key) {
              if (ctx.successKeys.indexOf(key) === -1) return;
              var rQty = r.items[key] || 0;
              if (rQty === 0) return;
              // Clear split lines for this item
              if (r.splitLines && r.splitLines[key]) {
                var splitCfg = r.splitLines[key];
                if (splitCfg && Array.isArray(splitCfg.lines)) {
                  splitCfg.lines.forEach(function(line) {
                    var slk = self.makeSplitLineKey(key, line.id);
                    if (r.giftSettings && r.giftSettings[slk]) delete r.giftSettings[slk];
                  });
                }
                delete r.splitLines[key];
              }
              var s = self.getRecipientItemSettings(r, key);

              s.bags = {}; s.freeBagEnabled = false; s.paidBagEnabled = false; s.bagSelection = (ctx.bag === 'on' || ctx.bag === 'off') ? ctx.bag : null;
              var ctxBagRuleConf = self.getBagRuleConfigByItemKey(key);
              if (ctxBagRuleConf.forceOn) {
                s.bagSelection = 'on';
                var ctxForcedFreeVar = self.getFreeHandbagVariantForItem(key);
                if (ctxForcedFreeVar) {
                  s.freeBagEnabled = true;
                  s.bags[ctxForcedFreeVar.shopifyProductVariantId] = rQty;
                }
              } else if (ctx.bag === 'on') {
                var freeVariant = self.getFreeHandbagVariantForItem(key);
                if (ctx.freeBag && freeVariant) {
                  s.freeBagEnabled = true;
                  s.bags[freeVariant.shopifyProductVariantId] = rQty;
                }
                if (ctx.paidBag && ctxPaidBags.length > 0) {
                  var ctxRecommendedSize = self.getRecommendedBagSizeByItemKey(key);
                  var ctxMatchedPb = null;
                  ctxPaidBags.forEach(function(pb) {
                    var sizeMatch = pb.title.match(/[-−]\s*(.+)$/);
                    var sizeLabel = sizeMatch ? sizeMatch[1].trim() : pb.title;
                    if (ctxRecommendedSize && self.normalizeBagSizeLabel(sizeLabel) === ctxRecommendedSize) ctxMatchedPb = pb;
                  });
                  if (ctxMatchedPb) {
                    s.bags[ctxMatchedPb.shopifyProductVariantId] = rQty;
                    s.paidBagEnabled = true;
                  }
                }
              }

              s.noshi = null; s.ribbon = null; s.wrapOnly = false; s.noWrap = false;
              if (ctx.wrapping === 'paper') {
                s.wrapOnly = true;
              } else if (ctx.wrapping === 'noshi' && ctx.bulkNoshiEntry) {
                s.noshi = { gid: ctx.bulkNoshiEntry.gid || '', name: ctx.bulkNoshiEntry.name || '', category: ctx.bulkNoshiEntry.category || '', type: ctx.bulkNoshiEntry.type || '', omotegaki: ctx.bulkNoshiEntry.omotegaki || '', subtitle: ctx.bulkNoshiEntry.subtitle || '', image: ctx.bulkNoshiEntry.image || '', mode: ctx.bulkNoshiMode, texts: ctx.bulkNoshiTexts, previewImage: '', count: rQty };
              } else if (ctx.wrapping === 'ribbon' && ctx.bulkRibbonEntry) {
                s.ribbon = { gid: ctx.bulkRibbonEntry.gid || '', name: ctx.bulkRibbonEntry.name || '', category: self.getRibbonCategory(ctx.bulkRibbonEntry), image: ctx.bulkRibbonEntry.image || '' };
              }
            });
          });
          self._pendingBulkContext = null;
        }

        self.saveData();
        self.renderCartItems();
        self.updateSummary();
        var shipmentTotal = self.multiRecipients.filter(function(r) {
          return Object.keys(r.items || {}).some(function(key) { return (r.items[key] || 0) > 0; });
        }).length;
        self.showBulkWrappingSuccessModal(shipmentTotal, shipmentTotal);
      } else {
        if (ctx) ctx.originalSuccessCount = finalSuccessCount;
        if (finalSuccessCount > 0) {
          self.showBulkWrappingErrorModal('partial', finalSuccessCount, stillFailed, origTotal, null, null);
        } else {
          self.showBulkWrappingErrorModal('full', 0, stillFailed, origTotal, null, null);
        }
      }
    },

    /**
     * Check if checkout is allowed in single mode
     * Validates based on checkoutData structure
     */
    canCheckoutSingle: function() {
      // Check orderer info (must be registered — Shopify default address)
      if (!this.orderer || !this.orderer.address1) {
        return false;
      }

      // Check sender info
      if (!this.sender || !this.sender.address1) {
        return false;
      }

      // Build checkoutData and validate
      var checkoutData = this.prepareAllInGiftData();

      // Must have at least one shipment
      if (!checkoutData || checkoutData.length === 0) {
        return false;
      }

      // Check each shipment has valid shippingAddress (name/zip/phone validated at click time)
      var allValid = checkoutData.every(function(shipment) {
        var addr = shipment.shippingAddress;
        return addr && addr.city && addr.address1;
      });

      return allValid;
    },

    /**
     * Check if checkout is allowed in multi mode
     * Validates based on checkoutData structure
     */
    canCheckoutMulti: function() {
      // Check orderer info (must be registered — Shopify default address)
      if (!this.orderer || !this.orderer.address1) {
        return false;
      }

      // Check sender info
      if (!this.sender || !this.sender.address1) {
        return false;
      }

      // Build checkoutData and validate
      var checkoutData = this.prepareAllInGiftData();

      // Must have at least one shipment
      if (!checkoutData || checkoutData.length === 0) {
        return false;
      }

      // Check each shipment has valid shippingAddress (name/zip/phone validated at click time)
      var allValid = checkoutData.every(function(shipment) {
        var addr = shipment.shippingAddress;
        return addr && addr.city && addr.address1;
      });
      if (!allValid) return false;

      // All cart items must be fully assigned (no unassigned pool remaining)
      var self = this;
      var allAssigned = (this.cartItems || []).every(function(item) {
        var totalAssigned = (self.multiRecipients || []).reduce(function(sum, r) {
          return sum + ((r.items && r.items[item.key]) || 0);
        }, 0);
        return totalAssigned >= item.quantity;
      });
      if (!allAssigned) return false;

      // No recipient should have zero items assigned (address with no items)
      var noEmptyRecipients = (this.multiRecipients || []).every(function(r) {
        return Object.values(r.items || {}).some(function(qty) { return qty > 0; });
      });
      if (!noEmptyRecipients) return false;

      return true;
    },

    /**
     * Update checkout button disabled state based on current mode
     */
    updateCheckoutButtonState: function() {
      var canCheckout = false;

      // Empty cart — always disable
      if (!this.cartItems || this.cartItems.length === 0) {
        canCheckout = false;
      } else if (this.currentMode === 'single') {
        canCheckout = this.canCheckoutSingle();
      } else {
        canCheckout = this.canCheckoutMulti();
      }

      // Require privacy policy checkbox to be checked (skip for logged-in users)
      var privacyChecked = window.shippingCustomer ? true : $('#privacy-policy-check').is(':checked');
      canCheckout = canCheckout && privacyChecked;

      $('[data-checkout-btn]').prop('disabled', !canCheckout).toggleClass('is-disabled', !canCheckout);
    },

    /**
     * Build parentOrderNote string for the parent draft order
     */
    buildParentOrderNote: function() {
      var lines = [];
      var customerInfo = window.shippingCustomer;

      // 顧客区分
      var category = this.getCurrentCustomerType();
      lines.push('顧客区分は' + category + 'です。');

      // 社員番号（顧客タグに「社員売顧客」がある場合のみ）
      if (customerInfo && customerInfo.tags) {
        var hasShauri = customerInfo.tags.indexOf('社員売顧客') !== -1;
        if (hasShauri) {
          var employeeId = customerInfo.employeeId || '';
          if (employeeId) {
            lines.push('社員番号は' + employeeId + 'です。');
          }
        }
      }

      // 要注意顧客チェック
      if (customerInfo && customerInfo.tags && customerInfo.tags.indexOf('要注意顧客') !== -1) {
        lines.push('この顧客は要注意タグがついています。');
      }

      // 注文者氏名
      var ordererName = ((this.orderer && this.orderer.lastName || '') + ' ' + (this.orderer && this.orderer.firstName || '')).trim();
      lines.push('注文者氏名は' + ordererName + 'です。');

      // 依頼主情報（ご依頼主＝senderから取得）。最終メール(Liquid)で split 抽出できるよう
      // 開始/終了マーカーで囲んだブロックにする。
      // お届け先が注文者と異なるのに sender がヨックモックのままだった場合は注文者に補正する。
      var s = this.getNoteSenderSource() || {};
      var senderName = ((s.lastName || '') + ' ' + (s.firstName || '')).trim();
      var senderZip = s.zip ? '〒' + this.formatZipDisplay(s.zip) + ' ' : '';
      var senderAddr2 = this.getAddress2WithCompany(s);
      var senderAddress = (senderZip + (s.prefecture || '') + (s.city || '') + (s.address1 || '') +
        (senderAddr2 ? ' ' + senderAddr2 : '')).trim();
      var senderPhone = s.phone ? this.formatPhoneDisplay(s.phone) : '';
      lines.push('━━━ 依頼主情報 ━━━');
      lines.push('依頼主名：' + senderName);
      lines.push('住所：' + senderAddress);
      lines.push('電話番号：' + senderPhone);
      lines.push('━━━ ここまで ━━━');

      // Yahoo広告 yclid（_yclid_ydn Cookie があれば parentOrderNote 末尾に追記）
      var yclidMatch = document.cookie.match(/(?:^|; )_yclid_ydn=([^;]+)/);
      if (yclidMatch && yclidMatch[1]) {
        lines.push('「Yahoo!広告効果計測用タグ：' + yclidMatch[1] + '」');
      }

      return '\n' + lines.join('\n');
    },

    /**
     * Check if any recipient address contains "空港" in address1 or address2
     */
    hasAirportAddress: function() {
      var recipients = this.getRecipients();
      for (var i = 0; i < recipients.length; i++) {
        var r = recipients[i];
        if (!r) continue;
        var addr1 = r.address1 || '';
        var addr2 = r.address2 || '';
        if (addr1.indexOf('空港') !== -1 || addr2.indexOf('空港') !== -1) {
          return true;
        }
      }
      return false;
    },

    /**
     * generalGift(data) - Proceed to checkout using All-In-Gift API
     * Creates checkout with all shipping data
     */
    generalGift: function() {
      var self = this;

      // Validate
      if (!this.validateCheckout()) {
        return;
      }

      // Airport address check — show confirmation modal if not yet confirmed
      if (!this._airportConfirmed && this.hasAirportAddress()) {
        this.openModal('modalAirportConfirm');
        return;
      }
      this._airportConfirmed = false;

      // Save default address before checkout (fire-and-forget, never blocks checkout)
      // Skip if metafield already has a saved address ID
      if (window.shippingCustomer && !window.shippingCustomer.defaultAddressIdAtCheckout) {
        fetch('/apps/ping/customers/default-address/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }).catch(function(err) {
          console.warn('Failed to save default address:', err);
        });
      }

      // Mark that checkout has started (so returning via back button won't reset settings)
      try { sessionStorage.setItem('shippingCheckoutStarted', '1'); } catch (e) {}

      // Push begin_checkout event to dataLayer for GTM/GA4
      try {
        var gtmItems = (this.cartItems || []).map(function (item, idx) {
          return {
            item_id: String(item.product_id),
            item_name: item.product_title,
            price: item.price / 100,
            item_brand: 'ヨックモック',
            item_variant: item.variant_title || '',
            quantity: item.quantity || 1,
            index: idx
          };
        });
        var gtmValue = gtmItems.reduce(function (sum, i) { return sum + i.price * i.quantity; }, 0);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'begin_checkout',
          ecommerce: {
            currency: 'JPY',
            value: gtmValue,
            items: gtmItems
          }
        });
      } catch (e) {}

      var self = this;
      $('[data-checkout-btn]').prop('disabled', true).addClass('is-disabled').text('処理中...');
      this.showCheckoutLoading();

      // Prepare checkout data for All-In-Gift API
      var checkoutData = this.prepareAllInGiftData();
      var parentOrderNote = this.buildParentOrderNote();

      // Attach parentOrderNote to the first cart item so the SDK hoists it to the top level
      if (checkoutData.length > 0) {
        checkoutData[0].parentOrderNote = parentOrderNote;
      }

      // checkoutData / parentOrderNote にはお届け先・請求先の住所や氏名が入る。
      // console に出すと Sentry のパンくずとして GlitchTip に送られるため、デバッグ時のみ出力する。
      if (SHIPPING_DEBUG) {
        console.log('checkoutData:', JSON.stringify(checkoutData, null, 2));
        console.log('parentOrderNote:', parentOrderNote);
      }

      // Helper: attach customer + checkout context to Sentry scope
      var customerInfo = window.shippingCustomer || null;
      function setSentryCheckoutContext(scope) {
        if (customerInfo && customerInfo.id) {
          scope.setUser({
            id: String(customerInfo.id),
            tags: (customerInfo.tags || []).join(', '),
            memberType: customerInfo.memberType || ''
          });
        }
        scope.setTag('checkout.mode', self.currentMode || 'unknown');
        scope.setExtra('checkoutDataLength', checkoutData.length);
        scope.setExtra('cartItemCount', (self.cartItems || []).length);
      }

      // Check if current customer is in the debug list for raw AIG error display
      var isDebugPaymentUser = (function() {
        var ids = (window.debugPaymentErrorUsers || '').split(',').map(function(s) { return s.trim(); }).filter(Boolean);
        var cid = customerInfo && String(customerInfo.id);
        return !!cid && ids.indexOf(cid) !== -1;
      })();

      // Ensure AllInGift is available (retry if it wasn't ready at page load)
      var isAllInGiftReady = window.AllInGift && window.AllInGift.checkout && typeof window.AllInGift.checkout.generalGift === 'function';
      var allInGiftReady = isAllInGiftReady ? Promise.resolve() : self.waitForAllInGift();

      // Use All-In-Gift API
      allInGiftReady
        .then(function() { return window.AllInGift.checkout.generalGift(checkoutData); })
        .then(function(response) {
          if (response.success) {
            sessionStorage.removeItem('fromCart');
            // Mark that checkout was initiated; actual localStorage cleanup happens
            // in theme.liquid when the user returns from the thank_you page.
            localStorage.setItem('shippingCheckoutPending', '1');
            window.location.href = response.invoiceUrl;
          } else {
            console.error('Checkout error response:', response);
            if (window.Sentry) {
              window.Sentry.withScope(function(scope) {
                setSentryCheckoutContext(scope);
                scope.setExtra('responseMessage', response.message || null);
                scope.setExtra('responseCode', response.code || null);
                scope.setExtra('response', response);
                scope.setLevel('error');
                window.Sentry.captureException(new Error('AllInGift checkout failed: ' + (response.message || 'no message')));
              });
            }
            var alertMsg;
            if (isDebugPaymentUser) {
              alertMsg = '[DEBUG] Raw AIG response:\n' + JSON.stringify(response, null, 2);
            } else {
              try {
                var msgStr = response.message || '';
                var bracketMatch = msgStr.match(/\[gift\]\s*(\[[\s\S]*\])/);
                var parsed = bracketMatch ? JSON.parse(bracketMatch[1]) : null;
                if (parsed && Array.isArray(parsed) && parsed.length > 0) {
                  var messages = parsed.map(function(e) { return e.message; }).filter(Boolean);
                  alertMsg = messages.length > 0 ? messages.join('\n') : 'チェックアウトに失敗しました';
                } else {
                  alertMsg = msgStr || 'チェックアウトに失敗しました';
                }
              } catch (e) {
                alertMsg = response.message || 'チェックアウトに失敗しました';
              }
            }
            alert(alertMsg);
            self.hideCheckoutLoading();
            $('[data-checkout-btn]').prop('disabled', false).removeClass('is-disabled').text('お支払いに進む');
          }
        })
        .catch(function(err) {
          console.error('Checkout failed:', err);
          self.hideCheckoutLoading();
          $('[data-checkout-btn]').prop('disabled', false).removeClass('is-disabled').text('お支払いに進む');

          var allInGiftUnavailable = !window.AllInGift || !window.AllInGift.checkout || typeof window.AllInGift.checkout.generalGift !== 'function';
          if (allInGiftUnavailable) {
            var reason = !window.AllInGift
              ? 'window.AllInGift is undefined'
              : !window.AllInGift.checkout
                ? 'window.AllInGift.checkout is undefined'
                : 'window.AllInGift.checkout.generalGift is not a function (type: ' + typeof window.AllInGift.checkout.generalGift + ')';
            console.error('AllInGift API not available:', reason);
            if (window.Sentry) {
              window.Sentry.withScope(function(scope) {
                setSentryCheckoutContext(scope);
                scope.setExtra('reason', reason);
                scope.setExtra('AllInGiftKeys', window.AllInGift ? Object.keys(window.AllInGift) : null);
                scope.setLevel('fatal');
                window.Sentry.captureException(new Error('AllInGift API not available: ' + reason));
              });
            }
            alert(isDebugPaymentUser ? '[DEBUG] AIG SDK unavailable: ' + reason : 'ただいまアクセスが集中しております。\nしばらく経ってから再度お試しください。');
          } else {
            if (SHIPPING_DEBUG) console.error('Error details:', JSON.stringify(err, null, 2));
            if (window.Sentry) {
              window.Sentry.withScope(function(scope) {
                setSentryCheckoutContext(scope);
                scope.setLevel('fatal');
                window.Sentry.captureException(err instanceof Error ? err : new Error('AllInGift checkout exception: ' + String(err)));
              });
            }
            alert(isDebugPaymentUser ? '[DEBUG] AIG checkout exception:\n' + String(err) : 'ただいまアクセスが集中しております。\nしばらく経ってから再度お試しください。');
          }
        });
    },

    /**
     * Prepare data in All-In-Gift API format
     */
    prepareAllInGiftData: function() {
      var self = this;
      var checkoutData = [];

      if (this.currentMode === 'single') {
        // Single address mode - group items by shipping type
        // Each shipping type becomes a separate shipment to the same address
        // Items with flags_mixed_loading_disable = true must be in separate shipments
        var recipient = this.singleRecipient || {};

        // Group items by shipping type
        var itemsByShippingType = {
          ambient: { mixable: [], nonMixable: [] },
          frozen: { mixable: [], nonMixable: [] },
          mail: { mixable: [], nonMixable: [] }
        };

        this.cartItems.forEach(function(item) {
          var shippingType = self.getItemShippingType(item);
          var isMixedLoadingDisabled = self.getItemMixedLoadingDisabled(item);
          if (isMixedLoadingDisabled) {
            itemsByShippingType[shippingType].nonMixable.push(item);
          } else {
            itemsByShippingType[shippingType].mixable.push(item);
          }
        });

        // Create shipments for each shipping type
        ['ambient', 'frozen', 'mail'].forEach(function(shippingType) {
          var group = itemsByShippingType[shippingType];

          // Create one shipment for all mixable items
          // shipmentKey = shippingType (e.g., "ambient")
          if (group.mixable.length > 0) {
            var shipmentKey = shippingType;
            checkoutData.push(self.formatRecipientData(recipient, group.mixable, shippingType, shipmentKey));
          }

          // Create separate shipment for each non-mixable item
          // shipmentKey = shippingType + "_" + item.key (e.g., "ambient_item123")
          group.nonMixable.forEach(function(item) {
            var shipmentKey = shippingType + '_' + item.key;
            checkoutData.push(self.formatRecipientData(recipient, [item], shippingType, shipmentKey));
          });
        });
      } else {
        // Multi-ship mode - multiple recipients
        // For each recipient, group items by shipping type
        // Items with flags_mixed_loading_disable = true must be in separate shipments
        this.multiRecipients.forEach(function(recipient) {
          // Group recipient's items by shipping type
          var itemsByShippingType = {
            ambient: { mixable: [], nonMixable: [] },
            frozen: { mixable: [], nonMixable: [] },
            mail: { mixable: [], nonMixable: [] }
          };

          self.cartItems.forEach(function(item) {
            var qty = (recipient.items || {})[item.key] || 0;
            if (qty > 0) {
              var shippingType = self.getItemShippingType(item);
              var isMixedLoadingDisabled = self.getItemMixedLoadingDisabled(item);
              var itemWithQty = {
                ...item,
                quantity: qty
              };
              if (isMixedLoadingDisabled) {
                itemsByShippingType[shippingType].nonMixable.push(itemWithQty);
              } else {
                itemsByShippingType[shippingType].mixable.push(itemWithQty);
              }
            }
          });

          // Create shipments for each shipping type
          ['ambient', 'frozen', 'mail'].forEach(function(shippingType) {
            var group = itemsByShippingType[shippingType];

            // Create one shipment for all mixable items
            // shipmentKey = shippingType (e.g., "ambient")
            if (group.mixable.length > 0) {
              var shipmentKey = shippingType;
              checkoutData.push(self.formatRecipientData(recipient, group.mixable, shippingType, shipmentKey));
            }

            // Create separate shipment for each non-mixable item
            // shipmentKey = shippingType + "_" + item.key (e.g., "ambient_item123")
            group.nonMixable.forEach(function(item) {
              var shipmentKey = shippingType + '_' + item.key;
              checkoutData.push(self.formatRecipientData(recipient, [item], shippingType, shipmentKey));
            });
          });
        });
      }

      // _hide_pay: 複数条件をカンマ区切りで追加
      var hidePayValues = [];

      // ご注文者≠お届け先の場合 → cod（複数配送時は常に付与）
      if (this.currentMode === 'multi' || !this.isSameAddress(this.sender, YOKUMOKU_SENDER)) {
        hidePayValues.push('cod');
      }

      // 注文者住所やデフォルト配送先住所に顧客番号が含まれる場合 → paid
      var hasPaidHideTarget = hasPaidCustomerNumber(this.sender) || checkoutData.some(function(shipment) {
        return hasPaidCustomerNumber(shipment && shipment.defaultShippingAddress);
      });
      if (hasPaidHideTarget) {
        hidePayValues.push('paid');
      }

      if (hidePayValues.length > 0) {
        var hidePayStr = hidePayValues.join(',');
        checkoutData.forEach(function(shipment) {
          (shipment.items || []).forEach(function(item) {
            item.properties = item.properties || {};
            item.properties['_hide_pay'] = hidePayStr;
          });
        });
      }

      return checkoutData;
    },

    /**
     * Format recipient data for All-In-Gift API
     * @param {Object} recipient - Recipient data
     * @param {Array} items - Items for this shipment
     * @param {string} shippingType - Shipping type (ambient, frozen, mail)
     * @param {string} shipmentKey - Unique shipment key for delivery settings lookup
     */
    formatRecipientData: function(recipient, items, shippingType, shipmentKey) {
      var self = this;
      var isInterofficeShipment = this.isInterofficeRecipient(recipient);
      var recipientAddress1ForCheckout = isInterofficeShipment
        ? buildAddress1WithAddress2(recipient.address1, recipient.address2)
        : (recipient.address1 || '');
      var recipientAddress2WithCompany = this.getAddress2WithCompany(recipient);

      // Get shipping config based on shipment's shipping type
      var shippingConfig = this.getDefaultShippingConfig(shippingType);
      if (isInterofficeShipment) {
        shippingConfig.defaultShippingMethod = $.extend({}, shippingConfig.defaultShippingMethod, {
          title: INTEROFFICE_SHIPPING_TITLE
        });
      }

      // Get shipping rate from cache using shipmentKey (handles non-mixable items)
      // Use null to distinguish "cache miss" from "rate = 0 (free shipping)"
      var cacheKey = (this.currentMode === 'single' ? 'single_' : (recipient.id + '_')) + shipmentKey;
      var shippingRateInfo = this.shippingRatesCache[cacheKey] || null;
      var shippingPrice = shippingRateInfo ? (shippingRateInfo.rate || 0) : (isInterofficeShipment ? 0 : null);
      var shippingTitle = shippingRateInfo && shippingRateInfo.shippingTitle
        ? shippingRateInfo.shippingTitle
        : (shippingConfig.defaultShippingMethod ? shippingConfig.defaultShippingMethod.title : '送料');

      var data = {
        // Always send full address data
        shippingAddress: (function() {
          var addr = {
            lastName: recipient.lastName || '',
            firstName: recipient.firstName || '',
            zip: (recipient.zip || '').replace(/-/g, ''),
            countryCode: 'JP',
            provinceCode: recipient.prefectureCode,
            city: recipient.city || '',
            address1: recipientAddress1ForCheckout,
            address2: recipientAddress2WithCompany
          };
          var phone = (recipient.phone || '').replace(/-/g, '');
          if (phone) addr.phone = phone;
          return addr;
        })(),
        items: (function() {
          var allItems = [];
          var isFirstItem = true;
          items.forEach(function(item) {
            var splitLines = (self.currentMode === 'single') ? self.getSingleSplitLinesForItem(item) : (self.currentMode === 'multi' ? self.getRecipientSplitLinesForItem(recipient, item) : []);
            if (splitLines && splitLines.length >= 2) {
              splitLines.forEach(function(line) {
                var lineQty = Math.max(1, parseInt(line.qty, 10) || 1);
                var splitGiftProps = self.buildGiftProperties(item.key, recipient, { splitLineKey: line.splitLineKey });
                var splitItemData = {
                  parsedVariantId: 'gid://shopify/ProductVariant/' + item.variant_id,
                  quantity: lineQty,
                  properties: Object.assign({}, item.properties || {}, splitGiftProps),
                  giftOptions: []
                };
                if (isFirstItem && shippingPrice !== null) {
                  splitItemData.shippingLine = {
                    price: String(shippingPrice),
                    title: shippingTitle
                  };
                }
                isFirstItem = false;
                allItems.push(splitItemData);
              });

              // Collect handbag items from each split line's independent bag settings
              var splitBagTotals = {};
              splitLines.forEach(function(line) {
                var lineBags = self.buildHandbagItems(line.splitLineKey, recipient.id);
                lineBags.forEach(function(bag) {
                  if (splitBagTotals[bag.parsedVariantId]) {
                    splitBagTotals[bag.parsedVariantId].quantity += bag.quantity;
                  } else {
                    splitBagTotals[bag.parsedVariantId] = { parsedVariantId: bag.parsedVariantId, quantity: bag.quantity, giftOptions: [], properties: {} };
                  }
                });
              });
              Object.keys(splitBagTotals).forEach(function(vid) {
                allItems.push(splitBagTotals[vid]);
              });
              return;
            }

            var giftProps = self.buildGiftProperties(item.key, recipient);
            var hasGiftProps = Object.keys(giftProps).length > 0;

            // Note: legacy noshi-count-based splitting has been removed.
            // Splits are now exclusively managed via singleSplitLines (handled above).
            // If getSingleSplitLinesForItem returned [], treat the whole item as unsplit.
            {
              // No split needed — all units share the same properties
              var itemData = {
                parsedVariantId: 'gid://shopify/ProductVariant/' + item.variant_id,
                quantity: item.quantity,
                properties: Object.assign({}, item.properties || {}, giftProps),
                giftOptions: []
              };
              if (isFirstItem && shippingPrice !== null) {
                itemData.shippingLine = {
                  price: String(shippingPrice),
                  title: shippingTitle
                };
              }
              isFirstItem = false;
              allItems.push(itemData);

              // Handbag items — inserted right after their main product item
              var handbagItems = self.buildHandbagItems(item.key, recipient.id);
              handbagItems.forEach(function(bag) {
                allItems.push(bag);
              });
            }
          });
          return allItems;
        })(),
        customAttribute: {
          senderName: self.getCheckoutSenderName(),
          destinationName: (recipient.lastName || '') + ' ' + (recipient.firstName || '') + '様宛',
          remark: ''
        },
        // Required shipping fields (determined by product type)
        defaultShippingMethod: shippingConfig.defaultShippingMethod,
        shippingLineProductVariantId: shippingConfig.shippingLineProductVariantId,
        // Sender address as billing address for child orders (依頼主/送り主)
        childOrderBillingAddress: self.buildChildOrderBillingAddress()
      };

      // defaultShippingAddress: always prefer this.orderer (注文者) because it
      // reflects the latest in-memory edits via the orderer modal.
      // window.shippingCustomer.defaultAddress is a snapshot from Liquid at
      // page render time and does NOT update when the user changes the orderer
      // mid-session, so using it silently sends stale data to Shopify checkout.
      // Fall back to customerInfo.defaultAddress only if orderer is empty
      // (edge case — normally this.orderer is seeded from defaultAddress on init).
      var customerInfo = window.shippingCustomer;
      var ordererSrc = this.orderer || {};
      var hasOrdererData = !!(ordererSrc.lastName || ordererSrc.address1);
      var addrSrc = hasOrdererData ? ordererSrc : (customerInfo && customerInfo.defaultAddress) || {};

      var checkoutAddr = {
        firstName: addrSrc.firstName || '',
        lastName: addrSrc.lastName || '',
        address1: addrSrc.address1 || '',
        address2: addrSrc.address2 || '',
        company: addrSrc.company || '',
        city: addrSrc.city || '',
        province: addrSrc.prefectureCode || addrSrc.province || '',
        countryCode: 'JP',
        zip: (addrSrc.zip || '').replace(/-/g, '')
      };
      var phoneNorm = this.normalizePhoneInput(addrSrc.phone || '').replace(/-/g, '');
      if (phoneNorm) checkoutAddr.phone = phoneNorm;
      data.defaultShippingAddress = checkoutAddr;

      if (customerInfo && customerInfo.id) {
        data.customer = {
          id: customerInfo.id,
          tags: customerInfo.tags || []
        };
      }

      // Add delivery date if specified (per shipment)
      // Read directly from DOM select to ensure checkout data matches what user sees
      var recipientId = recipient.id || 'single';
      var $dateSelect = $('[data-recipient-id="' + recipientId + '"][data-shipment-key="' + shipmentKey + '"][data-delivery-date]');
      if (!$dateSelect.length) {
        $dateSelect = $('[data-shipment-key="' + shipmentKey + '"][data-delivery-date]');
      }
      var $timeSelect = $('[data-recipient-id="' + recipientId + '"][data-shipment-key="' + shipmentKey + '"][data-delivery-time]');
      if (!$timeSelect.length) {
        $timeSelect = $('[data-shipment-key="' + shipmentKey + '"][data-delivery-time]');
      }
      var dateVal = $dateSelect.length ? $dateSelect.val() : '';
      var timeVal = $timeSelect.length ? $timeSelect.val() : '';
      if (dateVal || timeVal) {
        var timeAIG = this.convertTimeSlotToAIG(timeVal);
        data.deliveryDate = {
          setting: true
        };
        if (dateVal) {
          data.deliveryDate.date = dateVal;
        }
        // Send time as number so SDK's JSON.stringify(time) produces correct string
        // SDK does: time && { time: JSON.stringify(time) }
        // String '14' → JSON.stringify('14') = '"14"' (double-quoted, WRONG)
        // Number 14  → JSON.stringify(14)  = '14'    (correct)
        // Note: time=0 (morning/午前中) is falsy → SDK drops it (known SDK limitation)
        if (timeAIG !== null) {
          data.deliveryDate.time = parseInt(timeAIG, 10);
        }
      }

      return data;
    },

    /**
     * Convert prefecture name to province code
     */
    prefectureToProvinceCode: function(prefecture) {
      return PREFECTURE_MAP[prefecture] || 'JP-13';
    },

    clearBagSelectionValidationError: function($scope) {
      if (!$scope || !$scope.length) return;
      $scope.find('.js-bag-selection-error').remove();
      $scope.find('.js-bagToggle').removeClass('is-error');
    },

    showBagSelectionValidationError: function($scope) {
      if (!$scope || !$scope.length) return;
      this.clearBagSelectionValidationError($scope);
      var $toggles = $scope.find('.js-bagToggle');
      if (!$toggles.length) return;
      $toggles.each(function() {
        var $toggle = $(this);
        var $bagBox = $toggle.siblings('.js-bagBox').first();
        $toggle.addClass('is-error');
        if ($bagBox.length) {
          $bagBox.after('<p class="p-form-error-txt js-bag-selection-error">手提げ袋を選択してください。</p>');
        } else {
          $toggle.after('<p class="p-form-error-txt js-bag-selection-error">手提げ袋を選択してください。</p>');
        }
      });
    },

    /**
     * 冷凍便発送不可地域かどうかを判定する
     * @param {Object} address - zip, city, address1, address2 を含むアドレスオブジェクト
     * @returns {boolean}
     */
    isFrozenRestrictedAddress: function(address) {
      if (!address) return false;
      var zip = String(address.zip || '').replace(/\D/g, '');
      if (FROZEN_RESTRICTED_ZIPS.indexOf(zip) !== -1) return true;
      var combined = [address.city, address.address1, address.address2].join('');
      for (var i = 0; i < FROZEN_RESTRICTED_ADDRESS_KEYWORDS.length; i++) {
        if (combined.indexOf(FROZEN_RESTRICTED_ADDRESS_KEYWORDS[i]) !== -1) return true;
      }
      return false;
    },

    /**
     * 指定されたアイテムキーの中に冷凍便アイテムが含まれるか判定する
     * @param {Array} itemKeys - アイテムキーの配列
     * @returns {boolean}
     */
    hasFrozenItemsForKeys: function(itemKeys) {
      var self = this;
      return (itemKeys || []).some(function(key) {
        var item = (self.cartItems || []).filter(function(i) { return i.key === key; })[0];
        return item && self.getItemShippingType(item) === 'frozen';
      });
    },

    validateRequiredBagSelections: function() {
      var self = this;
      var hasError = false;
      var $firstInvalidInput = $();
      var cartItemsByKey = {};

      (this.cartItems || []).forEach(function(item) {
        cartItemsByKey[item.key] = item;
      });

      if (this.currentMode === 'multi') {
        (this.multiRecipients || []).forEach(function(recipient, recipientIndex) {
          Object.keys(recipient.items || {}).forEach(function(itemKey) {
            var qty = parseInt(recipient.items[itemKey], 10) || 0;
            if (qty <= 0) return;
            var item = cartItemsByKey[itemKey];
            if (!item || !self.isBagSelectionRequired(itemKey, item.variant_id)) return;
            var settings = self.getRecipientItemSettings(recipient, itemKey);
            var bagSelection = self.getBagSelectionState(settings, itemKey, item.variant_id);
            // お届け先重複時に id セレクタだと別カードにもマッチするため index で特定する
            var $wrapper = self.getRecipientCardByIndex(recipientIndex).find('[data-item-key="' + itemKey + '"]');
            if (bagSelection !== 'on' && bagSelection !== 'off') {
              hasError = true;
              self.showBagSelectionValidationError($wrapper);
              if (!$firstInvalidInput.length) {
                $firstInvalidInput = $wrapper.find('.js-bagToggle input').first();
              }
            } else {
              self.clearBagSelectionValidationError($wrapper);
            }
          });
        });
      } else {
        (this.cartItems || []).forEach(function(item) {
          if (!self.isBagSelectionRequired(item.key, item.variant_id)) return;

          // Check split lines individually
          var splitLines = self.getSingleSplitLinesForItem(item);
          if (splitLines && splitLines.length >= 2) {
            splitLines.forEach(function(line) {
              var bagSettingsKey = line.splitLineKey;
              var settings = self.getSingleItemGiftSettings(bagSettingsKey);
              var bagSelection = self.getBagSelectionState(settings, item.key, item.variant_id);
              var $wrapper = $('[data-single-address-items]').find('[data-bag-settings-key="' + bagSettingsKey + '"]');
              if (bagSelection !== 'on' && bagSelection !== 'off') {
                hasError = true;
                self.showBagSelectionValidationError($wrapper);
                if (!$firstInvalidInput.length) {
                  $firstInvalidInput = $wrapper.find('.js-bagToggle input').first();
                }
              } else {
                self.clearBagSelectionValidationError($wrapper);
              }
            });
          } else {
            var settings = self.getSingleItemGiftSettings(item.key);
            var bagSelection = self.getBagSelectionState(settings, item.key, item.variant_id);
            var $wrapper = $('[data-single-address-items]').find('[data-bag-settings-key="' + item.key + '"], [data-item-key="' + item.key + '"]');
            if (bagSelection !== 'on' && bagSelection !== 'off') {
              hasError = true;
              self.showBagSelectionValidationError($wrapper);
              if (!$firstInvalidInput.length) {
                $firstInvalidInput = $wrapper.find('.js-bagToggle input').first();
              }
            } else {
              self.clearBagSelectionValidationError($wrapper);
            }
          }
        });
      }

      if (!hasError) return true;

      if ($firstInvalidInput.length) {
        $('html, body').animate({ scrollTop: Math.max($firstInvalidInput.offset().top - 120, 0) }, 200);
        $firstInvalidInput.focus();
      }
      alert('手提げ袋を選択してください。');
      return false;
    },

    /**
     * Run sync validateCheckout, then async orderer address validation, then proceed
     */
    validateAndCheckout: function() {
      var self = this;
      if (!this.validateCheckout()) return;
      // 同期チェック（お名前/郵便番号形式/電話/JIS X 0208）で失敗しても早期returnせず、
      // 非同期チェック（郵便番号・住所不一致/ブラック会員）も必ず実行して
      // 全お届け先のエラーを出し切ってから可否判定する
      var senderOk = this.validateSenderForCheckout();
      var recipientsOk = this.validateRecipientsForCheckout();
      // yokumoku.max-cart-pcs Wチェック (チェックアウト直前)
      var mcpCheck = window.MaxCartPcs && window.MaxCartPcs.validateCart
        ? window.MaxCartPcs.validateCart()
        : Promise.resolve({ ok: true });
      mcpCheck.then(function(result) {
        if (!result.ok) return;
        Promise.all([
          self.validateOrdererAddress(),
          self.validateSenderZipProvince(),
          self.validateRecipientsZipProvince(),
          self.validateRecipientsBlacklist()
        ]).then(function(results) {
          if (senderOk && recipientsOk && results.every(Boolean)) {
            self.generalGift();
          } else {
            self.scrollToFirstCheckoutError();
          }
        });
      });
    },

    /**
     * チェックアウト検証エラーのうちページ最上部のものへスクロールする。
     * お届け先のエラーは該当のお届け先カードへ、それ以外は所属セクションへ移動する。
     */
    scrollToFirstCheckoutError: function() {
      var $topError = null;
      $('[data-orderer-validation-errors], [data-sender-validation-errors], [data-sender-zip-province-error], [data-recipient-validation-errors], [data-recipient-zip-province-error], [data-recipient-blacklist-error]').each(function() {
        var $el = $(this);
        if (!$topError || $el.offset().top < $topError.offset().top) {
          $topError = $el;
        }
      });
      if (!$topError || !$topError.length) return;
      var $scrollTarget = $topError.closest('[data-recipient-card]');
      if (!$scrollTarget.length) $scrollTarget = $topError.closest('.addr-card');
      if (!$scrollTarget.length) $scrollTarget = $topError.closest('.p-checkout__item');
      if (!$scrollTarget.length) $scrollTarget = $topError;
      $('html, body').stop(true, true).animate({ scrollTop: $scrollTarget.offset().top - 100 }, 400);
    },

    /**
     * multiRecipients の並び順で index 番目のお届け先カード要素を返す。
     * お届け先の重複（同一住所＝同一 data-recipient-id）があっても一意に特定できる。
     */
    getRecipientCardByIndex: function(index) {
      return $('[data-multi-address-items] [data-recipient-card]').eq(index);
    },

    /**
     * チェックアウト検証エラーを recipient オブジェクト単位で保持する。
     * カード再描画時に renderRecipientCard がここから表示を復元するため、
     * 別のお届け先を修正してもエラー表示が消えない。
     * 住所を変更した recipient はオブジェクトごと差し替わるため自動的にクリアされる。
     * @param {Object} recipient
     * @param {string} kind - 'validation' | 'zipProvince' | 'blacklist'
     * @param {Array<string>} messages - 空配列で該当種別のエラーをクリア
     */
    _setRecipientErrors: function(recipient, kind, messages) {
      if (!recipient) return;
      if (!this._recipientErrors) this._recipientErrors = new WeakMap();
      var entry = this._recipientErrors.get(recipient) || {};
      entry[kind] = messages || [];
      this._recipientErrors.set(recipient, entry);
    },

    getStoredRecipientErrors: function(recipient) {
      if (!recipient || !this._recipientErrors) return {};
      return this._recipientErrors.get(recipient) || {};
    },

    /**
     * Validate address object for JIS X 0208 compliance.
     * Called when address-book data is loaded and bypasses per-field input validation.
     * Checks for emojis, disallowed symbols, and characters outside JIS X 0208 range.
     * @param {Object} addr - Address object (sender or recipient)
     * @returns {Array<string>} Error messages; empty array if all fields are valid.
     */
    _validateAddressJisChars: function(addr) {
      if (!addr) return [];
      var errors = [];
      // 3層方式 (char-validator.js) で欄ごとのスコープを適用。未ロード時はスキップ。
      var checkField = function(value, label, scope) {
        if (!value || !window.CharValidator) return;
        var msg = window.CharValidator.validate(value, scope);
        if (msg) errors.push(label + '：' + msg + 'アドレス帳を修正してください。');
      };

      checkField(String(addr.lastName || ''), '姓', 'name');
      checkField(String(addr.firstName || ''), '名', 'name');
      checkField(String(addr.city || ''), '市区町村', 'global');
      checkField(String(addr.address1 || ''), '住所', 'address');
      if (addr.address2) checkField(String(addr.address2), '建物名', 'address');
      if (addr.company) checkField(String(addr.company), '会社名', 'address');

      return errors;
    },

    /**
     * Sync validate sender (name, zip, phone) with inline errors + scroll
     * @returns {boolean}
     */
    validateSenderForCheckout: function() {
      var self = this;
      var s = this.sender;
      if (!s) {
        return false;
      }

      var errors = [];

      if (!s.lastName && !s.firstName) {
        errors.push('お名前を入力してください。');
      }

      var sZip = this.normalizeZipInput(s.zip || '');
      if (!/^\d{7}$/.test(sZip)) {
        errors.push('正しい郵便番号を入力してください。');
      }

      var sPhone = this.normalizePhoneInput(s.phone || '').replace(/[-\s]/g, '');
      var sPhoneValid = sPhone &&
        /^(0[5789]0\d{8}|0[1-9]\d{8}|0120\d{6}|0800\d{7})$/.test(sPhone) &&
        !/0{8}$/.test(sPhone);
      if (!sPhoneValid) {
        errors.push('正しい電話番号を入力してください。');
      }

      // JIS X 0208 check: catch invalid chars from address-book data
      var sCharErrors = this._validateAddressJisChars(s);
      sCharErrors.forEach(function(msg) { errors.push(msg); });

      $('[data-sender-validation-errors]').remove();

      if (errors.length > 0) {
        var $pAddress = $('[data-sender-info]').closest('.p-address');
        errors.forEach(function(msg) {
          $pAddress.append('<p class="p-form-error-txt" data-sender-validation-errors>' + self.escapeHtml(msg) + '</p>');
        });
        // スクロールは全チェック完了後に scrollToFirstCheckoutError で一括して行う
        return false;
      }
      return true;
    },

    /**
     * Sync validate recipients (name, zip, phone) with inline errors + scroll
     * @returns {boolean}
     */
    validateRecipientsForCheckout: function() {
      var self = this;
      var hasError = false;

      $('[data-recipient-validation-errors]').remove();

      var checkAddr = function(r) {
        var errors = [];
        if (!r.lastName && !r.firstName) {
          errors.push('お名前を入力してください。');
        }
        var rZip = self.normalizeZipInput(r.zip || '');
        if (!/^\d{7}$/.test(rZip)) {
          errors.push('正しい郵便番号を入力してください。');
        }
        var rPhone = self.normalizePhoneInput(r.phone || '').replace(/[-\s]/g, '');
        var rPhoneValid = rPhone &&
          /^(0[5789]0\d{8}|0[1-9]\d{8}|0120\d{6}|0800\d{7})$/.test(rPhone) &&
          !/0{8}$/.test(rPhone);
        if (!rPhoneValid) {
          errors.push('正しい電話番号を入力してください。');
        }
        // JIS X 0208 check: catch invalid chars from address-book data
        var rCharErrors = self._validateAddressJisChars(r);
        rCharErrors.forEach(function(msg) { errors.push(msg); });
        return errors;
      };

      if (this.currentMode === 'single') {
        var r = this.singleRecipient;
        if (r) {
          var errors = checkAddr(r);
          if (errors.length > 0) {
            var $addrBody = $('[data-single-address-items] .addr-card--big .addr-card__body');
            errors.forEach(function(msg) {
              $addrBody.append('<p class="p-form-error-txt" data-recipient-validation-errors>' + self.escapeHtml(msg) + '</p>');
            });
            hasError = true;
          }
        }
      } else {
        // お届け先の重複（同一住所＝同一id）があるとidセレクタでは複数カードにマッチして
        // エラーが重複表示されるため、multiRecipientsの並び順indexでカードを特定する
        (this.multiRecipients || []).forEach(function(r, index) {
          var errors = checkAddr(r);
          self._setRecipientErrors(r, 'validation', errors);
          if (errors.length > 0) {
            var $addrBody = self.getRecipientCardByIndex(index).find('.addr-card--big .addr-card__body');
            errors.forEach(function(msg) {
              $addrBody.append('<p class="p-form-error-txt" data-recipient-validation-errors>' + self.escapeHtml(msg) + '</p>');
            });
            hasError = true;
          }
        });
      }

      // スクロールは全チェック完了後に scrollToFirstCheckoutError で一括して行う
      return !hasError;
    },

    /**
     * Async validate orderer address — name/phone/zip format/prefecture sync
     * then postal-codes API check for zip
     * @returns {Promise<boolean>}
     */
    validateOrdererAddress: function() {
      var self = this;
      var o = this.orderer;
      if (!o) {
        return Promise.resolve(true);
      }

      var errors = [];

      // Name validation
      if (!o.lastName && !o.firstName) {
        errors.push({ field: 'name', msg: 'お名前を入力してください。' });
      }

      // Phone validation (same logic as account address form)
      var oPhone = this.normalizePhoneInput(o.phone || '').replace(/[-\s]/g, '');
      var phoneValid = oPhone &&
        /^(0[5789]0\d{8}|0[1-9]\d{8}|0120\d{6}|0800\d{7})$/.test(oPhone) &&
        !/0{8}$/.test(oPhone);
      if (!phoneValid) {
        errors.push({ field: 'phone', msg: '正しい電話番号を入力してください。' });
      }

      // Zip format check (sync)
      var oZip = this.normalizeZipInput(o.zip || '');
      var zipFormatOk = /^\d{7}$/.test(oZip);
      if (!zipFormatOk) {
        errors.push({ field: 'zip', msg: '正しい郵便番号を入力してください。' });
      }

      // Prefecture check (separate message — zip can be valid while prefecture is missing)
      if (!o.prefectureCode) {
        errors.push({ field: 'prefecture', msg: '都道府県を選択してください。' });
      }

      // Any sync error → skip API check
      if (errors.length > 0) {
        this.showOrdererValidationErrors(errors);
        return Promise.resolve(false);
      }

      // Zip: async API check via self-hosted postal-codes API
      return new Promise(function(resolve) {
        $.ajax({
          url: '/apps/ping/postal-codes/search',
          dataType: 'jsonp',
          data: { zipcode: oZip },
          timeout: 3000,
          success: function(res) {
            if (!res || res.status !== 200 || !res.results || !res.results.length) {
              errors.push({ field: 'zip', msg: '正しい郵便番号を入力してください。' });
            } else {
              if (o.prefectureCode !== 'JP-' + res.results[0].prefcode) {
                errors.push({ field: 'prefecture', msg: '郵便番号と住所の組み合わせが一致していません。郵便番号または住所をご確認のうえ、再入力してください。' });
              }
            }
            if (errors.length > 0) {
              self.showOrdererValidationErrors(errors);
              resolve(false);
            } else {
              self._ordererAddressFieldErrors = {};
              $('[data-orderer-validation-errors]').remove();
              resolve(true);
            }
          },
          error: function(xhr, status) {
            // API unreachable — skip zip check, only surface phone errors
            if (errors.length > 0) {
              self.showOrdererValidationErrors(errors);
              resolve(false);
            } else {
              resolve(true);
            }
          }
        });
      });
    },

    validateSenderZipProvince: function() {
      var self = this;
      // 前回表示した自分のエラーのみクリア（同期チェックのエラー表示は消さない）
      $('[data-sender-zip-province-error]').remove();
      var s = this.sender;
      if (!s) return Promise.resolve(true);
      var sZip = this.normalizeZipInput(s.zip || '');
      if (!/^\d{7}$/.test(sZip)) return Promise.resolve(true); // already caught by sync check

      return new Promise(function(resolve) {
        $.ajax({
          url: '/apps/ping/postal-codes/search',
          dataType: 'jsonp',
          data: { zipcode: sZip },
          timeout: 3000,
          success: function(res) {
            var msg = null;
            if (!res || res.status !== 200 || !res.results || !res.results.length) {
              msg = '正しい郵便番号を入力してください。';
            } else {
              if (s.prefectureCode !== 'JP-' + res.results[0].prefcode) {
                msg = '郵便番号と住所の組み合わせが一致していません。郵便番号または住所をご確認のうえ、再入力してください。';
              }
            }
            if (msg) {
              var $pAddress = $('[data-sender-info]').closest('.p-address');
              $pAddress.append('<p class="p-form-error-txt" data-sender-zip-province-error>' + self.escapeHtml(msg) + '</p>');
              resolve(false);
            } else {
              resolve(true);
            }
          },
          error: function() {
            resolve(true); // API unreachable — skip
          }
        });
      });
    },

    validateRecipientsZipProvince: function() {
      var self = this;
      // 前回検証で表示したエラーを一旦クリア（修正後の再検証で残らないように）
      $('[data-recipient-zip-province-error]').remove();
      var recipients = [];
      if (this.currentMode === 'single') {
        if (this.singleRecipient) recipients.push(this.singleRecipient);
      } else {
        recipients = this.multiRecipients || [];
      }

      var checks = recipients.map(function(r, index) {
        var rZip = self.normalizeZipInput(r.zip || '');
        if (!/^\d{7}$/.test(rZip)) {
          self._setRecipientErrors(r, 'zipProvince', []);
          return Promise.resolve(true); // already caught by sync check
        }

        return new Promise(function(resolve) {
          $.ajax({
            url: '/apps/ping/postal-codes/search',
            dataType: 'jsonp',
            data: { zipcode: rZip },
            timeout: 3000,
            success: function(res) {
              var msg = null;
              if (!res || res.status !== 200 || !res.results || !res.results.length) {
                msg = '正しい郵便番号を入力してください。';
              } else {
                if (r.prefectureCode !== 'JP-' + res.results[0].prefcode) {
                  msg = '郵便番号と住所の組み合わせが一致していません。郵便番号または住所をご確認のうえ、再入力してください。';
                }
              }
              self._setRecipientErrors(r, 'zipProvince', msg ? [msg] : []);
              if (msg) {
                var $addrBody;
                if (self.currentMode === 'single') {
                  $addrBody = $('[data-single-address-items] .addr-card--big .addr-card__body');
                } else {
                  $addrBody = self.getRecipientCardByIndex(index).find('.addr-card--big .addr-card__body');
                }
                $addrBody.append('<p class="p-form-error-txt" data-recipient-zip-province-error>' + self.escapeHtml(msg) + '</p>');
                resolve(false);
              } else {
                resolve(true);
              }
            },
            error: function() {
              self._setRecipientErrors(r, 'zipProvince', []);
              resolve(true); // API unreachable — skip
            }
          });
        });
      });

      return Promise.all(checks).then(function(results) {
        return results.indexOf(false) === -1;
      });
    },

    /**
     * お届け先がブラック会員（配送ブロック対象）と一致しないか突合する。
     * App Proxy 経由で API の /v1/checkout/validate-recipient を呼び出す
     * (Shopify が署名を付与するためテーマ側で署名は不要)。
     * - allowed:false が返ったお届け先はブロック（進行不可）し、住所カードにエラー表示。
     * - タイムアウト/通信エラー/5xx は fail-open (通す)。機会損失回避のため
     *   完全な防御はタグ付与＋注文後手動キャンセルで担保する（設計 §6）。
     * @returns {Promise<boolean>} 全お届け先が通過なら true
     */
    validateRecipientsBlacklist: function() {
      var self = this;
      // 前回検証で表示したブロックエラーを一旦クリア（修正後の再検証で残らないように）
      $('[data-recipient-blacklist-error]').remove();
      var recipients = [];
      if (this.currentMode === 'single') {
        if (this.singleRecipient) recipients.push(this.singleRecipient);
      } else {
        recipients = this.multiRecipients || [];
      }

      var checks = recipients.map(function(r, index) {
        // 突合対象が何も無ければスキップ（通す）
        var hasInput = (r.zip || r.city || r.address1 || r.address2 || r.phone);
        if (!hasInput) {
          self._setRecipientErrors(r, 'blacklist', []);
          return Promise.resolve(true);
        }

        return new Promise(function(resolve) {
          $.ajax({
            url: '/apps/ping/checkout/validate-recipient',
            method: 'POST',
            contentType: 'application/json',
            dataType: 'json',
            timeout: 3000,
            data: JSON.stringify({
              phone: r.phone || '',
              zip: r.zip || '',
              city: r.city || '',
              address1: r.address1 || '',
              address2: r.address2 || ''
            }),
            success: function(res) {
              if (res && res.allowed === false) {
                var msg = (res && res.message)
                  ? res.message
                  : 'ご指定のお届け先には商品をお届けできません。お手数ですが別のお届け先をご指定ください。';
                self._setRecipientErrors(r, 'blacklist', [msg]);
                var $addrBody;
                if (self.currentMode === 'single') {
                  $addrBody = $('[data-single-address-items] .addr-card--big .addr-card__body');
                } else {
                  $addrBody = self.getRecipientCardByIndex(index).find('.addr-card--big .addr-card__body');
                }
                $addrBody.append('<p class="p-form-error-txt" data-recipient-blacklist-error>' + self.escapeHtml(msg) + '</p>');
                resolve(false);
              } else {
                self._setRecipientErrors(r, 'blacklist', []);
                resolve(true);
              }
            },
            error: function() {
              self._setRecipientErrors(r, 'blacklist', []);
              resolve(true); // fail-open: タイムアウト/5xx等は通す
            }
          });
        });
      });

      return Promise.all(checks).then(function(results) {
        return results.indexOf(false) === -1;
      });
    },

    /**
     * Show inline validation errors under the orderer info block and scroll to it
     */
    showFieldError: function(fieldId, msg) {
      var $el = $('#' + fieldId);
      if (!$el.length) return;
      var $rowItem = $el.closest('.p-row-item');
      $rowItem.addClass('is-error');
      $rowItem.find('.p-form-error-txt').remove();
      $rowItem.append('<p class="p-form-error-txt">' + msg + '</p>');
    },

    showOrdererValidationErrors: function(errors) {
      this._ordererAddressFieldErrors = {};
      var self = this;
      errors.forEach(function(e) {
        self._ordererAddressFieldErrors[e.field] = e.msg;
      });
      $('[data-orderer-validation-errors]').remove();
      var $pAddress = $('[data-orderer-info]').closest('.p-address');
      errors.forEach(function(e) {
        $pAddress.append('<p class="p-form-error-txt" data-orderer-validation-errors>' + e.msg + '</p>');
      });
      // スクロールは全チェック完了後に scrollToFirstCheckoutError で一括して行う
    },

    /**
     * Validate before checkout
     */
    validateCheckout: function() {
      // Check if delivery schedule is still loading
      if ($('.shiptime.is-loading').length > 0) {
        alert('お届け日時の情報を読み込み中です。しばらくお待ちください。');
        return false;
      }

      // Check sender info (name/zip/phone validated separately in validateSenderForCheckout)
      if (!this.sender || !this.sender.address1) {
        alert('ご依頼主情報を入力してください。');
        return false;
      }

      // Check recipients for single mode
      if (this.currentMode === 'single') {
        if (!this.singleRecipient) {
          alert('お届け先を選択してください。');
          return false;
        }
        var r = this.singleRecipient;
        if (!r.address1 || !r.city || !r.prefecture) {
          alert('お届け先の住所情報が不完全です。');
          return false;
        }
        if (!r.lastName || !r.firstName) {
          alert('お届け先のお名前（姓・名）が未登録です。\n「お届け先を変更する」から住所を編集してください。');
          $('[data-edit-single-recipient]').trigger('click');
          return false;
        }
      }

      // Check recipients for multi-ship mode
      if (this.currentMode === 'multi' && this.multiRecipients.length === 0) {
        alert('お届け先を追加してください。');
        return false;
      }

      if (!this.validateRequiredBagSelections()) {
        return false;
      }

      // Check if at least one item is assigned in multi-ship mode
      if (this.currentMode === 'multi') {
        var hasItems = this.multiRecipients.some(function(r) {
          return Object.values(r.items || {}).some(function(qty) { return qty > 0; });
        });

        if (!hasItems) {
          alert('各お届け先に商品を割り当ててください。');
          return false;
        }

        for (var ni = 0; ni < this.multiRecipients.length; ni++) {
          var mr = this.multiRecipients[ni];
          if (!mr.lastName || !mr.firstName) {
            alert('お届け先' + (ni + 1) + '件目のお名前（姓・名）が未登録です。\n住所を編集してください。');
            this.openChangeAddressModal(mr.id);
            return false;
          }
        }
      }

      // 冷凍便発送不可地域チェック
      var frozenRestrictedMsg = 'お届け先住所が冷凍便発送不可地域に該当するため、お届けができません。';
      if (this.currentMode === 'single') {
        var allItemKeys = (this.cartItems || []).map(function(i) { return i.key; });
        if (this.hasFrozenItemsForKeys(allItemKeys) && this.isFrozenRestrictedAddress(this.singleRecipient)) {
          alert(frozenRestrictedMsg);
          return false;
        }
      } else {
        var self = this;
        for (var ri = 0; ri < (this.multiRecipients || []).length; ri++) {
          var recipient = this.multiRecipients[ri];
          var assignedKeys = Object.keys(recipient.items || {}).filter(function(k) {
            return (parseInt(recipient.items[k], 10) || 0) > 0;
          });
          if (self.hasFrozenItemsForKeys(assignedKeys) && self.isFrozenRestrictedAddress(recipient)) {
            alert(frozenRestrictedMsg);
            return false;
          }
        }
      }

      // Check delivery date is still valid (not expired due to 13:00 JST cutoff)
      if (!this.validateDeliveryDateNotExpired()) {
        return false;
      }

      // Check required delivery date/time
      if (!this.validateRequiredDeliverySchedule()) {
        return false;
      }

      return true;
    },

    /**
     * Validate that each selected delivery date is still valid based on current JST time.
     * Rule: min date is today + getDeliveryStartOffset() (+2/+3, or +3/+4 from 2026-11-04 13:00 JST).
     * If any selected date is earlier than the minimum, clear the select and show inline error.
     * @returns {boolean} - true if all selected dates are valid (or no date selected)
     */
    validateDeliveryDateNotExpired: function() {
      var now = new Date();
      var jstNow = new Date(now.getTime() + (now.getTimezoneOffset() + 540) * 60000);
      var jstHour = jstNow.getHours();
      var startOffset = getDeliveryStartOffset(now, jstHour);

      var minDate = new Date(jstNow);
      minDate.setDate(minDate.getDate() + startOffset);
      var minDateStr = minDate.getFullYear() + '-' +
        String(minDate.getMonth() + 1).padStart(2, '0') + '-' +
        String(minDate.getDate()).padStart(2, '0');

      // Remove any previous expiry error messages
      $('.shiptime__date-error').remove();

      var hasError = false;

      $('[data-delivery-date]').each(function() {
        var $select = $(this);
        var val = $select.val();
        if (!val) return;

        if (val < minDateStr) {
          // Remove expired date options (skip blank placeholder option)
          $select.find('option').each(function() {
            var optVal = $(this).val();
            if (optVal && optVal < minDateStr) {
              $(this).remove();
            }
          });
          // Reset to empty after removing expired options
          $select.val('');

          var $form = $select.closest('.shiptime__form');
          if ($form.length) {
            $form.after('<p class="p-form-error-txt shiptime__date-error">選択可能な最短お届け希望日が更新されました。再度選択してください。</p>');
          }
          hasError = true;
        }
      });

      if (hasError) {
        alert('選択可能な最短お届け希望日が更新されました。再度選択してください。');
        var $firstError = $('.shiptime__date-error').first();
        if ($firstError.length) {
          $('html, body').animate({ scrollTop: Math.max($firstError.offset().top - 120, 0) }, 300);
        }
      }

      return !hasError;
    },

    /**
     * Validate that required delivery date/time are selected
     * @returns {boolean} - true if valid
     */
    validateRequiredDeliverySchedule: function() {
      var meta = this.deliveryScheduleMeta || {};

      if (this.currentMode === 'single' || !this.isMultiAddress) {
        // Single mode: check each shipment's meta
        var shipmentKeys = Object.keys(meta);
        for (var i = 0; i < shipmentKeys.length; i++) {
          var key = shipmentKeys[i];
          var m = meta[key];
          if (!m) continue;

          if (m.dateAvailable && m.dateRequired) {
            // Check if date is selected
            var $dateSelect = $('[data-shipment-key="' + key + '"][data-delivery-date]');
            if (!$dateSelect.length) {
              $dateSelect = $('[data-shipping-type="' + key + '"][data-delivery-date]');
            }
            if ($dateSelect.length && !$dateSelect.val()) {
              alert('お届け希望日を選択してください。');
              $dateSelect.focus();
              return false;
            }
          }

          if (m.timeAvailable && m.timeRequired) {
            var $timeSelect = $('[data-shipment-key="' + key + '"][data-delivery-time]');
            if (!$timeSelect.length) {
              $timeSelect = $('[data-shipping-type="' + key + '"][data-delivery-time]');
            }
            if ($timeSelect.length && !$timeSelect.val()) {
              alert('お届け希望時間を選択してください。');
              $timeSelect.focus();
              return false;
            }
          }
        }
      } else {
        // Multi mode: check each recipient's shipments
        var metaMulti = this.deliveryScheduleMetaMulti || {};
        for (var rid in metaMulti) {
          var recipientMeta = metaMulti[rid];
          for (var sk in recipientMeta) {
            var rm = recipientMeta[sk];
            if (!rm) continue;

            if (rm.dateAvailable && rm.dateRequired) {
              var $dSel = $('[data-recipient-id="' + rid + '"][data-shipment-key="' + sk + '"][data-delivery-date]');
              if (!$dSel.length) {
                $dSel = $('[data-recipient-id="' + rid + '"][data-shipping-type="' + sk + '"][data-delivery-date]');
              }
              if ($dSel.length && !$dSel.val()) {
                alert('お届け希望日を選択してください。');
                $dSel.focus();
                return false;
              }
            }

            if (rm.timeAvailable && rm.timeRequired) {
              var $tSel = $('[data-recipient-id="' + rid + '"][data-shipment-key="' + sk + '"][data-delivery-time]');
              if (!$tSel.length) {
                $tSel = $('[data-recipient-id="' + rid + '"][data-shipping-type="' + sk + '"][data-delivery-time]');
              }
              if ($tSel.length && !$tSel.val()) {
                alert('お届け希望時間を選択してください。');
                $tSel.focus();
                return false;
              }
            }
          }
        }
      }

      return true;
    },

    /**
     * Prepare checkout data
     */
    /**
     * Close active modal
     */
    closeModal: function() {
      var shouldResetPendingBulkContext = $('.p-modal.active#modalBulkError, .p-modal.active#modalBulkWraning').length > 0 && !this._preservePendingBulkContextOnClose;

      // Reset change address mode
      if (this.editingRecipientId) {
        this.editingRecipientId = null;
      }

      // If we were in sender select mode, reset and restore the modal
      if (this.selectingSender) {
        this.restoreSenderSelectModal();
      }

      // If we were in recipient select mode, reset and restore the modal
      if (this.selectingRecipient) {
        this.restoreRecipientSelectModal();
      }

      // If we were in interoffice select mode, reset and restore the modal
      if (this.selectingInterofficeRecipientId) {
        this.restoreInterofficeSelectModal();
      }

      // Reset bulk setup mode
      this.isBulkSetupMode = false;
      this.tempAddrSelections = {};
      this.bulkSelectedRecipients = [];

      if (shouldResetPendingBulkContext) {
        this._pendingBulkContext = null;
      }
      this._preservePendingBulkContextOnClose = false;

      $('.p-modal.active').removeClass('active');
      var scrollpos = parseInt($('body').css('top') || '0', 10) * -1 || this._savedScrollPos || 0;
      $('html, body').removeClass('freezePage').css('top', 0);
      window.scrollTo(0, scrollpos);
    },

    /**
     * Show error message
     */
    showError: function(message) {
      $('[data-sender-info]').html('<p class="error-message">' + message + '</p>');
    },

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml: function(str) {
      if (!str) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },

    _formatMoneyYen: function(value) { return formatMoneyYen(value); },

    /**
     * Province code to name mapping (JP prefectures)
     */
    PROVINCE_MAP: {
      'JP-01': '北海道', 'JP-02': '青森県', 'JP-03': '岩手県', 'JP-04': '宮城県',
      'JP-05': '秋田県', 'JP-06': '山形県', 'JP-07': '福島県', 'JP-08': '茨城県',
      'JP-09': '栃木県', 'JP-10': '群馬県', 'JP-11': '埼玉県', 'JP-12': '千葉県',
      'JP-13': '東京都', 'JP-14': '神奈川県', 'JP-15': '新潟県', 'JP-16': '富山県',
      'JP-17': '石川県', 'JP-18': '福井県', 'JP-19': '山梨県', 'JP-20': '長野県',
      'JP-21': '岐阜県', 'JP-22': '静岡県', 'JP-23': '愛知県', 'JP-24': '三重県',
      'JP-25': '滋賀県', 'JP-26': '京都府', 'JP-27': '大阪府', 'JP-28': '兵庫県',
      'JP-29': '奈良県', 'JP-30': '和歌山県', 'JP-31': '鳥取県', 'JP-32': '島根県',
      'JP-33': '岡山県', 'JP-34': '広島県', 'JP-35': '山口県', 'JP-36': '徳島県',
      'JP-37': '香川県', 'JP-38': '愛媛県', 'JP-39': '高知県', 'JP-40': '福岡県',
      'JP-41': '佐賀県', 'JP-42': '長崎県', 'JP-43': '熊本県', 'JP-44': '大分県',
      'JP-45': '宮崎県', 'JP-46': '鹿児島県', 'JP-47': '沖縄県'
    },

    /**
     * Get province name by code
     */
    getProvinceNameByCode: function(code) {
      return this.PROVINCE_MAP[code] || code || '';
    },

    /**
     * Get shipping type for a single item based on product_type and tags
     *
     * @param {Object} item - Cart item
     * @returns {String} - 'frozen', 'mail', or 'ambient'
     */
    getItemShippingType: function(item) {
      // First, check metafield data from Liquid (most accurate)
      if (window.cartItemsMetafields && window.cartItemsMetafields[item.key]) {
        var zone = window.cartItemsMetafields[item.key].temperature_zone;
        // Map temperature_zone values:
        // 冷凍 => frozen
        // 常温メール便 => mail
        // 常温, 冷蔵 => ambient
        if (zone === '冷凍') {
          return 'frozen';
        }
        if (zone === '常温メール便') {
          return 'mail';
        }
        // 常温 or 冷蔵 => ambient
        return 'ambient';
      }

      // Fallback: Check product_type and tags for shipping type keywords
      var productType = (item.product_type || '').toLowerCase();
      var productTags = (item.tags || []).join(' ').toLowerCase();
      var searchText = productType + ' ' + productTags;

      // Check for frozen keywords (冷凍便) - highest priority
      if (searchText.indexOf('冷凍') !== -1 || searchText.indexOf('frozen') !== -1) {
        return 'frozen';
      }

      // Check for mail keywords (メール便)
      if (searchText.indexOf('メール便') !== -1 || searchText.indexOf('mail') !== -1) {
        return 'mail';
      }

      // Default to ambient shipping (常温便)
      return 'ambient';
    },

    /**
     * Check if item has mixed loading disabled (cannot be grouped with other items)
     *
     * @param {Object} item - Cart item
     * @returns {Boolean} - true if item cannot be mixed with others
     */
    getItemMixedLoadingDisabled: function(item) {
      if (window.cartItemsMetafields && window.cartItemsMetafields[item.key]) {
        return window.cartItemsMetafields[item.key].flags_mixed_loading_disable === true;
      }
      return false;
    },

    /**
     * Group items into shipments based on shipping type and mixed_loading_disable flag
     * Mixable items of same shipping type → 1 shipment
     * Each non-mixable item → separate shipment
     *
     * @param {Array} items - Cart items to group
     * @returns {Array} - Array of shipment objects: { shipmentKey, shippingType, items, isNonMixable }
     */
    groupItemsIntoShipments: function(items) {
      var self = this;
      var shipments = [];

      // Group by shipping type first
      var itemsByShippingType = {
        ambient: { mixable: [], nonMixable: [] },
        frozen: { mixable: [], nonMixable: [] },
        mail: { mixable: [], nonMixable: [] }
      };

      items.forEach(function(item) {
        var shippingType = self.getItemShippingType(item);
        var isMixedLoadingDisabled = self.getItemMixedLoadingDisabled(item);
        if (isMixedLoadingDisabled) {
          itemsByShippingType[shippingType].nonMixable.push(item);
        } else {
          itemsByShippingType[shippingType].mixable.push(item);
        }
      });

      // Create shipments
      ['ambient', 'frozen', 'mail'].forEach(function(shippingType) {
        var group = itemsByShippingType[shippingType];

        // One shipment for all mixable items of this shipping type
        if (group.mixable.length > 0) {
          shipments.push({
            shipmentKey: shippingType, // e.g., "ambient"
            shippingType: shippingType,
            items: group.mixable,
            isNonMixable: false
          });
        }

        // Separate shipment for each non-mixable item
        group.nonMixable.forEach(function(item) {
          shipments.push({
            shipmentKey: shippingType + '_' + item.key, // e.g., "ambient_item123"
            shippingType: shippingType,
            items: [item],
            isNonMixable: true
          });
        });
      });

      return shipments;
    },

    /**
     * Get shipping config based on shipping type and items
     * Uses the first item's variant ID for shipping line
     *
     * @param {String} shippingType - 'frozen', 'mail', or 'ambient'
     * @param {Array} items - Cart items
     * @returns {Object} - { shippingLineProductVariantId, defaultShippingMethod: {title, variantId}, shippingType }
     */
    getDefaultShippingConfig: function(shippingType) {
      // Shipping method titles for each type
      var shippingTitles = {
        ambient: '常温・冷蔵便',
        frozen: '冷凍便',
        mail: 'メール便'
      };

      // Get shipping product variant ID from global constant
      var variantId = SHIPPING_VARIANT_IDS[shippingType] || SHIPPING_VARIANT_IDS.ambient;
      var gidVariantId = 'gid://shopify/ProductVariant/' + variantId;

      return {
        shippingLineProductVariantId: variantId,
        defaultShippingMethod: {
          title: shippingTitles[shippingType] || '常温・冷蔵便',
          variantId: gidVariantId
        },
        shippingType: shippingType
      };
    },

    getItemKeyByVariantId: function(variantId) {
      var normalized = String(variantId || '');
      if (!normalized) return '';
      var item = (this.cartItems || []).find(function(i) { return String(i.variant_id) === normalized; });
      return item ? item.key : '';
    },

    getPaidHandbagVariants: function() {
      return PAID_HANDBAG_VARIANTS.slice();
    },

    getFreeHandbagVariantForItem: function(itemKey) {
      var meta = (window.cartItemsMetafields && window.cartItemsMetafields[itemKey]) || {};
      var fromMetaobject = normalizeFreeHandbagVariant(meta.poly_bag);
      return fromMetaobject;
    },

    normalizeBagAttachmentRule: function(rawValue) {
      if (rawValue === false || rawValue === 'false') return 'legacy_disabled';
      var value = String(rawValue || '').trim();
      if (value === '必ず付ける' || value === '付けられない' || value === '選択可能') return value;
      return '選択可能';
    },

    getBagAttachmentRuleByItemKey: function(itemKey) {
      var meta = (window.cartItemsMetafields && window.cartItemsMetafields[itemKey]) || {};
      return this.normalizeBagAttachmentRule(meta.bag_attachment_rule);
    },

    getBagRuleConfigByItemKey: function(itemKey) {
      var rule = this.getBagAttachmentRuleByItemKey(itemKey);
      var isAlwaysIncluded = rule === '必ず付ける';
      var isSelectionDisabled = rule === '付けられない';
      var isLegacyDisabled = rule === 'legacy_disabled';
      return {
        rule: rule,
        forceOn: isAlwaysIncluded,
        forceFree: isAlwaysIncluded,
        disableOff: isAlwaysIncluded || isSelectionDisabled,
        disableOn: isSelectionDisabled || isLegacyDisabled,
        lockFree: isAlwaysIncluded,
        disableFree: isSelectionDisabled,
        hidePaid: isAlwaysIncluded || isLegacyDisabled,
        disablePaid: isSelectionDisabled,
        clearAllBags: isSelectionDisabled || isLegacyDisabled,
        freeDisplayTitle: isAlwaysIncluded ? '手提げ袋' : '手提げ袋（クリア）',
        noteText: isAlwaysIncluded ? '専用の手提げ袋を商品個数分お付けします。' : ''
      };
    },

    createDefaultGiftSettings: function() {
      return {
        bags: {},
        freeBagEnabled: false,
        paidBagEnabled: false,
        bagSelection: null,
        noshi: null,
        ribbon: null,
        wrapOnly: false,
        noWrap: false
      };
    },

    ensureGiftSettingsShape: function(settings) {
      if (!settings || typeof settings !== 'object') settings = {};
      if (!settings.bags || typeof settings.bags !== 'object') settings.bags = {};
      settings.freeBagEnabled = settings.freeBagEnabled === true;
      settings.paidBagEnabled = settings.paidBagEnabled === true;
      if (settings.bagSelection !== 'on' && settings.bagSelection !== 'off') {
        settings.bagSelection = (settings.freeBagEnabled || settings.paidBagEnabled) ? 'on' : null;
      }
      if (settings.wrapOnly !== true) settings.wrapOnly = false;
      if (settings.noWrap !== true) settings.noWrap = false;
      if (typeof settings.noshi === 'undefined') settings.noshi = null;
      if (typeof settings.ribbon === 'undefined') settings.ribbon = null;
      return settings;
    },

    getSingleItemGiftSettings: function(itemKey) {
      if (!this.itemGiftOptions.single) this.itemGiftOptions.single = {};
      if (!this.itemGiftOptions.single[itemKey]) {
        this.itemGiftOptions.single[itemKey] = this.createDefaultGiftSettings();
      }
      return this.ensureGiftSettingsShape(this.itemGiftOptions.single[itemKey]);
    },

    isBagSelectionRequired: function(itemKey, variantId) {
      var bagRuleConfig = this.getBagRuleConfigByItemKey(itemKey);
      if (!bagRuleConfig || bagRuleConfig.rule !== '選択可能') return false;
      return this.getHandbagVariantsForProduct(variantId, itemKey).length > 0;
    },

    /**
     * Sync bag off-status message and bagSelection based on whether any bag is checked.
     * If any bag checkbox is checked → hide off-status, uncheck "付けない", set bagSelection='on'
     * If all unchecked → show off-status, check "付けない", set bagSelection='off'
     */
    syncBagOffLabel: function($wrapper, bagSettingsKey) {
      var hasBag = $wrapper.find('[data-bag-type="free"]:checked, [data-bag-type="paid"]:checked').length > 0;
      var $offRadio = $wrapper.find('[data-bag-off-label] input[value="off"]');

      if (hasBag) {
        $offRadio.prop('checked', false);
        $wrapper.find('[data-bag-off-status]').hide();
        // Update bagSelection to 'on'
        if (this.currentMode === 'multi') {
          var recipientId = $wrapper.closest('[data-recipient-card]').data('recipient-id');
          var recipient = recipientId ? this.getMultiRecipientById(recipientId) : null;
          if (recipient) this.getRecipientItemSettings(recipient, $wrapper.data('item-key') || bagSettingsKey).bagSelection = 'on';
        } else {
          this.getSingleItemGiftSettings(bagSettingsKey).bagSelection = 'on';
        }
      } else {
        $offRadio.prop('checked', true);
        $wrapper.find('[data-bag-off-status]').show();
        $wrapper.find('[data-bag-status]').html('');
        // Update bagSelection to 'off'
        if (this.currentMode === 'multi') {
          var recipientId2 = $wrapper.closest('[data-recipient-card]').data('recipient-id');
          var recipient2 = recipientId2 ? this.getMultiRecipientById(recipientId2) : null;
          if (recipient2) {
            var s = this.getRecipientItemSettings(recipient2, $wrapper.data('item-key') || bagSettingsKey);
            s.bags = {}; s.freeBagEnabled = false; s.paidBagEnabled = false; s.bagSelection = 'off';
          }
        } else {
          var settings = this.getSingleItemGiftSettings(bagSettingsKey);
          settings.bags = {}; settings.freeBagEnabled = false; settings.paidBagEnabled = false; settings.bagSelection = 'off';
        }
      }
      this.saveData();
    },

    getBagSelectionState: function(settings, itemKey, variantId) {
      var normalized = this.ensureGiftSettingsShape(settings);
      var bagRuleConfig = this.getBagRuleConfigByItemKey(itemKey);
      if (bagRuleConfig.forceOn) return 'on';
      if (bagRuleConfig.clearAllBags || bagRuleConfig.disableOn) return 'off';
      if (normalized.bagSelection === 'on' || normalized.bagSelection === 'off') {
        return normalized.bagSelection;
      }
      if (normalized.freeBagEnabled || normalized.paidBagEnabled) return 'on';
      return 'off';
    },

    applyBagRuleToSettings: function(itemKey, settings, freeBag, paidBags, itemQty) {
      if (!settings) return;
      this.ensureGiftSettingsShape(settings);

      var ruleConfig = this.getBagRuleConfigByItemKey(itemKey);
      if (ruleConfig.clearAllBags) {
        settings.bags = {};
        settings.freeBagEnabled = false;
        settings.paidBagEnabled = false;
        settings.bagSelection = 'off';
        return;
      }

      if (ruleConfig.forceFree && freeBag) {
        // 「必ず付ける」: 袋数は常に商品数と同数で同期する
        var freeVariantId = String(freeBag.shopifyProductVariantId);
        settings.freeBagEnabled = true;
        var maxQtyForced = Math.max(1, parseInt(itemQty, 10) || 1);
        settings.bags[freeVariantId] = maxQtyForced;
        settings.bagSelection = 'on';
      }

      // For selectable free bags: cap qty at itemQty if it exceeds it.
      // Do NOT auto-increase — user controls qty independently (bag reduction policy).
      if (!ruleConfig.forceFree && freeBag && settings.freeBagEnabled) {
        var selectableVarId = String(freeBag.shopifyProductVariantId);
        var maxItemQty = Math.max(1, parseInt(itemQty, 10) || 1);
        var currentBagQty = parseInt(settings.bags[selectableVarId], 10) || 0;
        if (currentBagQty > maxItemQty) {
          settings.bags[selectableVarId] = maxItemQty;
        }
      }

      if (ruleConfig.hidePaid || ruleConfig.disablePaid) {
        settings.paidBagEnabled = false;
        (paidBags || []).forEach(function(paidBag) {
          delete settings.bags[String(paidBag.shopifyProductVariantId)];
        });
      }

      if ((settings.freeBagEnabled || settings.paidBagEnabled) && settings.bagSelection !== 'off') {
        settings.bagSelection = 'on';
      }
    },

    /**
     * Get handbag variants for a specific cart item (free bag differs by product)
     * @param {string|number} variantId - Product variant ID
     * @param {string} itemKey - Cart item key
     * @returns {Array}
     */
    getHandbagVariantsForProduct: function(variantId, itemKey) {
      var resolvedItemKey = itemKey || this.getItemKeyByVariantId(variantId);
      var variants = this.getPaidHandbagVariants();
      var freeBag = this.getFreeHandbagVariantForItem(resolvedItemKey);
      if (freeBag) variants.unshift(freeBag);
      return variants;
    },

    normalizeBagSizeLabel: function(value) {
      return String(value || '').replace(/\s+/g, '').replace(/　/g, '');
    },

    getRecommendedBagSizeByItemKey: function(itemKey) {
      var meta = (window.cartItemsMetafields && window.cartItemsMetafields[itemKey]) || {};
      var raw = meta.recommended_bag_size;
      if (Array.isArray(raw)) {
        raw = raw.length ? raw[0] : '';
      }
      return this.normalizeBagSizeLabel(raw);
    },

    /**
     * Open bag modal and render options for specific item
     * @param {string} itemKey - Cart item key
     * @param {string|number} variantId - Product variant ID
     */
    openBagModal: function(itemKey, variantId, recipientId, bagSettingsKey) {
      var bagRuleConfig = this.getBagRuleConfigByItemKey(itemKey);
      if (bagRuleConfig.hidePaid || bagRuleConfig.disablePaid) {
        return;
      }
      this.currentBagItemKey = itemKey;
      this.currentBagVariantId = variantId;
      this.currentBagRecipientId = recipientId || null;
      this.currentBagSettingsKey = bagSettingsKey || itemKey;

      var bags = this.getHandbagVariantsForProduct(variantId, itemKey);
      var paidBags = bags.filter(function(b) { return parseInt(b.price, 10) > 0; });
      this.renderBagOptionsInModal(paidBags, this.currentBagSettingsKey, recipientId);

      // Open modal
      var scrollpos = $(window).scrollTop();
      this._savedScrollPos = scrollpos;
      $('html').addClass('freezePage');
      $('body').addClass('freezePage').css({ 'top': -scrollpos });
      $('#modalShoppingBag').addClass('active');
    },

    /**
     * Render bag options in the shopping bag modal
     * @param {Array} bags - Array of handbag product variants
     * @param {string} itemKey - Cart item key
     */
    renderBagOptionsInModal: function(bags, itemKey, recipientId) {
      var self = this;
      var $container = $('[data-bag-options]');
      var html = '';
      var recommendedBagSize = this.getRecommendedBagSizeByItemKey(itemKey);

      if (bags.length === 0) {
        html = '<p class="no-bags-message">この商品には手提げ袋（紙）オプションがありません。</p>';
        $container.html(html);
        $('[data-bag-subtotal]').text(formatMoneyYen(0));
        return;
      }

      // Get current selections for this item
      var currentBags = {};
      if (this.currentMode === 'multi' && recipientId) {
        var recipient = this.getMultiRecipientById(recipientId);
        currentBags = (recipient && recipient.giftSettings && recipient.giftSettings[itemKey])
          ? (recipient.giftSettings[itemKey].bags || {})
          : {};
      } else {
        var currentOptions = this.itemGiftOptions[this.currentMode][itemKey] || {};
        currentBags = currentOptions.bags || {};
      }

      // Sort by priority
      bags.sort(function(a, b) {
        return (a.priority || 0) - (b.priority || 0);
      });

      // Only paid bags are passed here (filtered in openBagModal)
      bags.forEach(function(bag) {
        var bagId = bag.shopifyProductVariantId;
        var currentQty = currentBags[bagId] || 0;
        var price = parseInt(bag.price, 10) || 0;
        var priceText = formatMoneyYen(price);

        // Extract size from title (e.g., "手提げ袋（紙） - 特小" -> "特小")
        var sizeMatch = bag.title.match(/[-−]\s*(.+)$/);
        var sizeLabel = sizeMatch ? sizeMatch[1].trim() : bag.title;

        // Match product metafield yokumoku.recommended_bag_size: 特小/小/中
        var isRecommended = (recommendedBagSize && self.normalizeBagSizeLabel(sizeLabel) === recommendedBagSize) || bag.isRecommended === true;

        html += '<div class="optionRow" data-bag-id="' + self.escapeHtml(bagId) + '" data-bag-price="' + price + '">';
        html += '<div class="colLabel">' + self.escapeHtml(sizeLabel) + '</div>';
        html += '<div class="colPrice">';
        if (bag.image) {
          html += '<span class="priceDots"><img src="' + self.escapeHtml(bag.image) + '" alt=""></span>';
        }
        html += '<strong>' + priceText + '</strong>';
        if (isRecommended) {
          html += '<span class="c-badge c-badge--blue02">推奨</span>';
        }
        html += '</div>';
        html += '<div class="colQty">';
        html += '<span class="qtyText">数量</span>';
        html += '<div class="qty qty--small">';
        html += '<button type="button" class="qty__btn" data-bag-qty-btn data-step="-1" aria-label="decrease">−</button>';
        html += '<input class="qty__input" type="text" data-bag-qty-input value="' + currentQty + '" inputmode="numeric" aria-label="quantity" max="' + PAID_BAG_MAX_QTY + '">';
        html += '<button type="button" class="qty__btn" data-bag-qty-btn data-step="1" aria-label="increase">＋</button>';
        html += '</div>';
        html += '</div>';
        html += '</div>';
      });

      $container.html(html);
      this.updateBagModalSubtotal();
    },

    /**
     * Update the subtotal in bag modal
     */
    updateBagModalSubtotal: function() {
      var total = 0;
      $('[data-bag-options] .optionRow').each(function() {
        var $row = $(this);
        var price = parseInt($row.data('bag-price'), 10) || 0;
        var qty = parseInt($row.find('[data-bag-qty-input]').val(), 10) || 0;
        total += price * qty;
      });
      $('[data-bag-subtotal]').text(formatMoneyYen(total));
    },

    /**
     * Save bag selection from modal
     */
    saveBagSelection: function() {
      var self = this;
      var itemKey = this.currentBagItemKey;
      var bagSettingsKey = this.currentBagSettingsKey || itemKey;
      var recipientId = this.currentBagRecipientId || null;

      if (!itemKey) {
        console.warn('No item key set for bag selection');
        this.closeModal();
        return;
      }

      var _bagMode = this.currentMode;
      var bagRuleConfig = this.getBagRuleConfigByItemKey(itemKey);
      if (bagRuleConfig.hidePaid || bagRuleConfig.disablePaid) {
        this.closeModal();
        return;
      }

      // Collect paid bag selections from modal
      var paidBags = {};
      var variantId = null;
      var item = this.cartItems.find(function(i) { return i.key === itemKey; });
      if (item) variantId = item.variant_id;
      var bagVariants = this.getHandbagVariantsForProduct(variantId, itemKey);

      $('[data-bag-options] .optionRow').each(function() {
        var $row = $(this);
        var bagId = $row.data('bag-id');
        var qty = parseInt($row.find('[data-bag-qty-input]').val(), 10) || 0;
        if (qty > 0) {
          paidBags[bagId] = qty;
        }
      });

      var hasPaidBags = Object.keys(paidBags).length > 0;
      var mergedBags = {};
      var bagStatusRecipient = null;

      if (_bagMode === 'multi' && recipientId) {
        var recipient = this.getMultiRecipientById(recipientId);
        if (recipient) {
          bagStatusRecipient = recipient;
          var s = this.getRecipientItemSettings(recipient, itemKey);
          // Preserve free bag entries
          var existingBags = s.bags || {};
          Object.keys(existingBags).forEach(function(bagId) {
            var bagInfo = bagVariants.find(function(b) { return b.shopifyProductVariantId === bagId; });
            if (bagInfo && parseInt(bagInfo.price, 10) === 0) {
              mergedBags[bagId] = existingBags[bagId];
            }
          });
          Object.keys(paidBags).forEach(function(bagId) { mergedBags[bagId] = paidBags[bagId]; });
          s.bags = mergedBags;
          s.paidBagEnabled = hasPaidBags;
          if (hasPaidBags) s.bagSelection = 'on';
        }
      } else {
        var singleSettings = this.getSingleItemGiftSettings(bagSettingsKey);
        var existingBagsSingle = singleSettings.bags || {};
        Object.keys(existingBagsSingle).forEach(function(bagId) {
          var bagInfo = bagVariants.find(function(b) { return b.shopifyProductVariantId === bagId; });
          if (bagInfo && parseInt(bagInfo.price, 10) === 0) {
            mergedBags[bagId] = existingBagsSingle[bagId];
          }
        });
        Object.keys(paidBags).forEach(function(bagId) { mergedBags[bagId] = paidBags[bagId]; });
        singleSettings.bags = mergedBags;
        singleSettings.paidBagEnabled = hasPaidBags;
        if (hasPaidBags) singleSettings.bagSelection = 'on';
      }

      // Update UI
      var hasBags = Object.keys(mergedBags).length > 0;
      var _bagContainer = (_bagMode === 'multi') ? $('[data-multi-address-items]') : $('[data-single-address-items]');

      var $itemWrapper;
      if (_bagMode === 'multi' && recipientId) {
        $itemWrapper = $('[data-recipient-card][data-recipient-id="' + recipientId + '"]').find('[data-item-key="' + itemKey + '"]');
      } else {
        $itemWrapper = _bagContainer.find('[data-bag-settings-key="' + bagSettingsKey + '"]');
        if (!$itemWrapper.length) {
          $itemWrapper = _bagContainer.find('[data-item-key="' + itemKey + '"]');
        }
      }

      // Sync paid bag checkbox state — remove overlay when checked so user can uncheck later
      $itemWrapper.find('[data-bag-type="paid"]').prop('checked', hasPaidBags);
      $itemWrapper.find('label.js-bagPaid').toggleClass('is-active', hasPaidBags);
      if (hasPaidBags) {
        $itemWrapper.find('[data-overlay-paid-bag]').remove();
      }

      // Sync toggle state (付けない/付ける)
      if (hasBags) {
        $itemWrapper.find('.js-bagToggle input[value="on"]').prop('checked', true);
        $itemWrapper.find('.js-bagToggle input[value="off"]').prop('checked', false);
        $itemWrapper.find('.js-bagBox').show();
        $itemWrapper.find('[data-bag-off-status]').hide();
        $itemWrapper.find('[data-bag-status]').html(this.renderBagStatusSummary((_bagMode === 'multi') ? itemKey : bagSettingsKey, _bagMode, bagStatusRecipient));
      } else {
        $itemWrapper.find('[data-bag-off-status]').show();
        $itemWrapper.find('[data-bag-status]').html('');
      }

      this.saveData();
      this.updateSummary();
      this.closeModal();

    },

    /**
     * Render bag status summary for an item
     * @param {string} itemKey - Cart item key
     * @param {string} [mode] - Mode ('single' or 'multi')
     * @param {Object|null} [recipient] - Recipient object (multi mode) or null for single mode
     * @returns {string} - HTML string
     */
    renderBagStatusSummary: function(itemKey, mode, recipient) {
      var resolvedMode = mode || this.currentMode;
      var options;
      if (resolvedMode === 'multi' && recipient && typeof recipient === 'object') {
        options = (recipient.giftSettings && recipient.giftSettings[itemKey]) || null;
      } else {
        options = this.itemGiftOptions[resolvedMode][itemKey];
      }
      if (!options || !options.bags || Object.keys(options.bags).length === 0) {
        return '';
      }

      var self = this;
      var bags = options.bags;
      // Resolve original item key from splitLineKey if needed
      var origItemKey = itemKey;
      var parsed = this.parseSplitLineKey(itemKey);
      if (parsed) origItemKey = parsed.itemKey;
      var item = this.cartItems.find(function(i) { return i.key === origItemKey; });
      var variantId = item ? item.variant_id : null;
      var bagVariants = this.getHandbagVariantsForProduct(variantId, origItemKey);

      var paidTotal = 0;
      var paidLines = [];
      var freeBagCount = 0;
      var paidBagCount = 0;
      var bagRuleConfig = this.getBagRuleConfigByItemKey(origItemKey);

      var isFreeBagEnabled = options.freeBagEnabled === true;
      var isPaidBagEnabled = options.paidBagEnabled === true;

      Object.keys(bags).forEach(function(bagId) {
        var qty = bags[bagId];
        var bagInfo = bagVariants.find(function(b) { return b.shopifyProductVariantId === bagId; });
        if (bagInfo && qty > 0) {
          var price = parseInt(bagInfo.price, 10) || 0;

          if (price > 0 && isPaidBagEnabled) {
            // Paid bag
            var lineTotal = price * qty;
            paidTotal += lineTotal;
            paidBagCount += qty;

            var sizeMatch = bagInfo.title.match(/[-−]\s*(.+)$/);
            var sizeLabel = sizeMatch ? sizeMatch[1].trim() : bagInfo.title;
            paidLines.push({ label: sizeLabel + ' × ' + qty, price: formatMoneyYen(lineTotal) });
          } else if (price === 0 && isFreeBagEnabled) {
            // Free bag
            freeBagCount += qty;
          }
        }
      });

      var html = '';
      var totalBagCount = freeBagCount + paidBagCount;

      // p-fee section: Only show if there are paid bags
      if (paidBagCount > 0) {
        html += '<div class="p-fee u-mb-15 u-mt-15">';
        html += '<div class="p-fee__head">';
        html += '<h4 class="p-fee__title">選択中の手提げ袋（紙）</h4>';
        html += '<a href="#" class="p-fee__edit" data-open-bag-modal data-item-key="' + this.escapeHtml(itemKey) + '" data-variant-id="' + variantId + '">変更</a>';
        html += '</div>';
        html += '<ul class="p-fee__list">';
        paidLines.forEach(function(line) {
          html += '<li class="p-fee__row"><span class="p-fee__label">' + self.escapeHtml(line.label) + '</span><span class="p-fee__price">' + line.price + '</span></li>';
        });
        html += '</ul>';
        html += '<div class="p-fee__divider"></div>';
        html += '<div class="p-fee__foot">';
        html += '<span class="p-fee__subtotal-label">手提げ袋 小計</span>';
        html += '<span class="p-fee__subtotal">' + formatMoneyYen(paidTotal) + '</span>';
        html += '</div>';
        html += '</div>';
      }

      // p-alert section: Show bag combination summary
      // 必ず付ける ルール時は通知ボックスを表示しない
      if (totalBagCount > 0 && !bagRuleConfig.forceOn) {
        html += '<div class="p-alert p-alert--small">';
        html += '<div class="p-alert__icon">';
        html += '<img src="' + (window.shippingAssets ? window.shippingAssets.iconPackagingInfo : '') + '" alt="">';
        html += '</div>';
        html += '<div class="p-alert__info">';
        html += '<h4>手提げ袋の組み合わせ</h4>';

        var summaryParts = [];
        if (freeBagCount > 0) {
          summaryParts.push(bagRuleConfig.freeDisplayTitle);
        }
        if (paidBagCount > 0) {
          summaryParts.push('手提げ袋（紙）');
        }
        html += '<p>' + summaryParts.join(' + ') + '（<strong>計' + totalBagCount + '点</strong>）をお付けします。</p>';
        html += '</div>';
        html += '</div>';
      }

      return html;
    },

    /**
     * Calculate total bag cost for all items
     * @returns {number} - Total bag cost in yen
     */
    calculateTotalBagCost: function() {
      var self = this;
      var total = 0;

      if (this.currentMode === 'multi') {
        // Multi mode: iterate recipient.giftSettings
        this.multiRecipients.forEach(function(r) {
          if (!r.giftSettings) return;
          Object.keys(r.giftSettings).forEach(function(itemKey) {
            var options = r.giftSettings[itemKey];
            if (options && options.bags && options.paidBagEnabled === true) {
              var item = self.cartItems.find(function(i) { return i.key === itemKey; });
              var variantId = item ? item.variant_id : null;
              var bagVariants = self.getHandbagVariantsForProduct(variantId, itemKey);
              Object.keys(options.bags).forEach(function(bagId) {
                var qty = options.bags[bagId];
                var bagInfo = bagVariants.find(function(b) { return b.shopifyProductVariantId === bagId; });
                if (bagInfo && qty > 0) {
                  total += (parseInt(bagInfo.price, 10) || 0) * qty;
                }
              });
            }
          });
        });
      } else {
        Object.keys(this.itemGiftOptions[this.currentMode]).forEach(function(key) {
          var options = self.itemGiftOptions[self.currentMode][key];
          if (options && options.bags && options.paidBagEnabled === true) {
            // Resolve original item key from splitLineKey if needed
            var origKey = key;
            var parsed = self.parseSplitLineKey(key);
            if (parsed) origKey = parsed.itemKey;
            var item = self.cartItems.find(function(i) { return i.key === origKey; });
            var variantId = item ? item.variant_id : null;
            var bagVariants = self.getHandbagVariantsForProduct(variantId, origKey);
            Object.keys(options.bags).forEach(function(bagId) {
              var qty = options.bags[bagId];
              var bagInfo = bagVariants.find(function(b) { return b.shopifyProductVariantId === bagId; });
              if (bagInfo && qty > 0) {
                total += (parseInt(bagInfo.price, 10) || 0) * qty;
              }
            });
          }
        });
      }

      return total;
    },

    calculateTotalBagCount: function() {
      var self = this;
      var count = 0;

      if (this.currentMode === 'multi') {
        this.multiRecipients.forEach(function(r) {
          if (!r.giftSettings) return;
          Object.keys(r.giftSettings).forEach(function(itemKey) {
            var options = r.giftSettings[itemKey];
            if (options && options.bags && options.paidBagEnabled === true) {
              var item = self.cartItems.find(function(i) { return i.key === itemKey; });
              var variantId = item ? item.variant_id : null;
              var bagVariants = self.getHandbagVariantsForProduct(variantId, itemKey);
              Object.keys(options.bags).forEach(function(bagId) {
                var qty = options.bags[bagId];
                var bagInfo = bagVariants.find(function(b) { return b.shopifyProductVariantId === bagId; });
                if (bagInfo && qty > 0 && parseInt(bagInfo.price, 10) > 0) {
                  count += qty;
                }
              });
            }
          });
        });
      } else {
        Object.keys(this.itemGiftOptions[this.currentMode]).forEach(function(key) {
          var options = self.itemGiftOptions[self.currentMode][key];
          if (options && options.bags && options.paidBagEnabled === true) {
            var origKey = key;
            var parsed = self.parseSplitLineKey(key);
            if (parsed) origKey = parsed.itemKey;
            Object.keys(options.bags).forEach(function(bagId) {
              var qty = options.bags[bagId];
              if (qty > 0) {
                var item = self.cartItems.find(function(i) { return i.key === origKey; });
                var variantId = item ? item.variant_id : null;
                var bagVariants = self.getHandbagVariantsForProduct(variantId, origKey);
                var bagInfo = bagVariants.find(function(b) { return b.shopifyProductVariantId === bagId; });
                if (bagInfo && parseInt(bagInfo.price, 10) > 0) {
                  count += qty;
                }
              }
            });
          }
        });
      }
      return count;
    },

    /**
     * Build giftOptions array for checkout data
     * @param {string} itemKey - Cart item key
     * @returns {Array} - Array of gift option objects
     */

    /**
     * Get or initialize per-recipient per-item gift settings (multi mode)
     * @param {Object} recipient - Recipient object
     * @param {string} itemKey - Cart item key
     * @returns {Object} - Settings object (reference into recipient.giftSettings[itemKey])
     */
    getRecipientItemSettings: function(recipient, itemKey) {
      if (!recipient.giftSettings) recipient.giftSettings = {};
      if (!recipient.giftSettings[itemKey]) {
        recipient.giftSettings[itemKey] = this.createDefaultGiftSettings();
      }
      return this.ensureGiftSettingsShape(recipient.giftSettings[itemKey]);
    },

    /**
     * Find a recipient by ID in multiRecipients
     * @param {string} recipientId
     * @returns {Object|null}
     */
    getMultiRecipientById: function(recipientId) {
      return this.multiRecipients.find(function(r) { return r.id === recipientId; }) || null;
    },

    /**
     * 複数お届け先用の一意な内部IDを発行する。
     * 以前は recipient.id にアドレス帳の住所IDをそのまま使っていたため、
     * 同一住所を複数のお届け先に指定するとIDが重複し、id指定の操作
     * （変更・削除・商品の振り分け・各種設定）が別のカードに効いてしまっていた。
     * アドレス帳との紐付けは recipient.addressId が持つ。
     */
    _recipientIdSeq: 0,
    generateRecipientId: function() {
      this._recipientIdSeq += 1;
      return 'recipient_' + Date.now().toString(36) + '_' + this._recipientIdSeq;
    },

    /**
     * recipient が紐づくアドレス帳の住所IDを返す。
     * 内部ID分離前に保存されたデータは id が住所IDを兼ねるためフォールバックする。
     * 社内便のお届け先はアドレス帳と紐づかないため null。
     */
    getRecipientAddressId: function(recipient) {
      if (!recipient || recipient.isInteroffice) return null;
      return recipient.addressId || recipient.id;
    },

    /**
     * localStorage から読み込んだ複数お届け先の後方互換処理。
     * 電話番号の正規化に加え、内部ID分離前のデータ（id＝住所ID）へ addressId を補い、
     * id が重複している場合は一意な内部IDを振り直す。
     */
    normalizeLoadedMultiRecipients: function(list) {
      var self = this;
      var seenIds = {};
      return (list || []).map(function(recipient) {
        if (recipient) {
          recipient.phone = self.normalizePhoneInput(recipient.phone || '');
          if (!recipient.addressId && !recipient.isInteroffice) {
            recipient.addressId = recipient.id;
          }
          if (recipient.id && seenIds[recipient.id]) {
            recipient.id = self.generateRecipientId();
          }
          seenIds[recipient.id] = true;
        }
        return recipient;
      });
    },

    /**
     * Build handbag items for checkout — each bag is a separate item with its own quantity
     * Returns array of item objects to be inserted after the main product item
     * @param {string} itemKey - Cart item key
     * @param {string} [recipientId] - Recipient ID (multi mode only); reads from recipient.giftSettings
     * @returns {Array} - Array of handbag item objects { parsedVariantId, quantity, giftOptions, properties }
     */
    buildHandbagItems: function(itemKey, recipientId) {
      var options;
      var freeBagEnabled, paidBagEnabled;

      // Resolve original item key from splitLineKey if needed
      var origItemKey = itemKey;
      var parsed = this.parseSplitLineKey(itemKey);
      if (parsed) origItemKey = parsed.itemKey;

      if (this.currentMode === 'multi' && recipientId) {
        var recipient = this.getMultiRecipientById(recipientId);
        options = (recipient && recipient.giftSettings && recipient.giftSettings[itemKey]) || null;
        freeBagEnabled = options ? options.freeBagEnabled === true : false;
        paidBagEnabled = options ? options.paidBagEnabled === true : false;
      } else {
        options = this.itemGiftOptions[this.currentMode][itemKey];
        freeBagEnabled = options ? options.freeBagEnabled === true : false;
        paidBagEnabled = options ? options.paidBagEnabled === true : false;
        var $bagWrapper = $('[data-bag-settings-key="' + itemKey + '"]');
        if (!$bagWrapper.length) $bagWrapper = $('[data-item-key="' + itemKey + '"]');
        if ($bagWrapper.length) {
          freeBagEnabled = $bagWrapper.find('[data-bag-type="free"]').is(':checked');
          paidBagEnabled = $bagWrapper.find('[data-bag-type="paid"]').is(':checked');
        }
      }

      var handbagItems = [];
      if (!options || !options.bags) return handbagItems;

      var item = this.cartItems.find(function(i) { return i.key === origItemKey; });
      var variantId = item ? item.variant_id : null;
      var bagVariants = this.getHandbagVariantsForProduct(variantId, origItemKey);

      Object.keys(options.bags).forEach(function(bagId) {
        var qty = options.bags[bagId];
        var bagInfo = bagVariants.find(function(b) { return b.shopifyProductVariantId === bagId; });
        if (bagInfo && qty > 0) {
          var price = parseInt(bagInfo.price, 10) || 0;
          // Skip unchecked bags
          if (price === 0 && !freeBagEnabled) return;
          if (price > 0 && !paidBagEnabled) return;

          handbagItems.push({
            parsedVariantId: bagInfo.shopifyProductVariantId,
            quantity: qty,
            giftOptions: [],
            properties: {}
          });
        }
      });
      return handbagItems;
    },

    /**
     * Build gift properties for item — noshi/ribbon/wrap as properties (metadata on line item)
     * Following AIG SDK format: shopify-gift/front/src/model/cart.ts createGiftOptionProperties()
     * @param {string} itemKey - Cart item key
     * @param {Object} [recipient] - Recipient object (multi mode — reads from recipient.giftSettings)
     */
    getGiftSettingSnapshot: function(itemKey, recipient, options) {
      var opts = options || {};
      var splitLineKey = opts.splitLineKey || null;

      if (splitLineKey && this.currentMode === 'single') {
        var splitSettings = this.getSingleSplitLineSettings(splitLineKey) || {};
        return {
          noshi: splitSettings.noshi || null,
          sticker: splitSettings.ribbon || null,
          wrapOnly: !!splitSettings.wrapOnly,
          noWrap: !!splitSettings.noWrap
        };
      }

      if (splitLineKey && this.currentMode === 'multi' && recipient) {
        var rSplitSettings = this.getRecipientSplitLineSettings(recipient, splitLineKey) || {};
        return {
          noshi: rSplitSettings.noshi || null,
          sticker: rSplitSettings.ribbon || null,
          wrapOnly: !!rSplitSettings.wrapOnly,
          noWrap: !!rSplitSettings.noWrap
        };
      }

      if (this.currentMode === 'multi' && recipient) {
        var gs = (recipient.giftSettings && recipient.giftSettings[itemKey]) || {};
        return {
          noshi: gs.noshi || null,
          sticker: gs.ribbon || null,
          wrapOnly: !!gs.wrapOnly,
          noWrap: !!gs.noWrap
        };
      }

      return {
        noshi: (this.noshiSettings[this.currentMode] && this.noshiSettings[this.currentMode][itemKey]) || null,
        sticker: (this.stickerSettings[this.currentMode] && this.stickerSettings[this.currentMode][itemKey]) || null,
        wrapOnly: !!(this.wrapOnlyItems[this.currentMode] && this.wrapOnlyItems[this.currentMode][itemKey]),
        noWrap: !!(this.noWrapItems[this.currentMode] && this.noWrapItems[this.currentMode][itemKey])
      };
    },

    buildGiftPropertiesFromSnapshot: function(itemKey, snapshot) {
      var props = {};
      var state = snapshot || {};
      var noshi = state.noshi || null;
      var sticker = state.sticker || null;
      var wrapOnly = !!state.wrapOnly;
      var noWrap = !!state.noWrap;

      // Noshi — のし properties
      if (noshi) {
        props['_●のし、ラッピングについて'] = noshi.name || '';

        // _●名入れ fields based on mode
        if (noshi.texts) {
          var naire = [];
          if (noshi.mode === 'uchiiwai') {
            naire.push('●名入れ：お名前（のし選択者のみ）：' + (noshi.texts.name || ''));
            naire.push('●名入れ：よみがな（のし選択者かつ「出産内祝」希望者のみ）：' + (noshi.texts.furigana || ''));
          } else if (noshi.mode === 'corporate') {
            naire.push('●名入れ：会社名（のし選択者のみ）：' + (noshi.texts.company || ''));
            naire.push('●名入れ：肩書き（のし選択者のみ）：' + (noshi.texts.title || ''));
            naire.push('●名入れ：お名前（のし選択者のみ）：' + (noshi.texts.name || ''));
          } else {
            naire.push('●名入れ：お名前（のし選択者のみ）：' + (noshi.texts.name1 || ''));
            naire.push('●名入れ：連名（のし選択者のみ）：' + (noshi.texts.name2 || ''));
          }
          props['_●名入れ'] = naire.join('\n');
        }
      }
      // Ribbon/Sticker
      else if (sticker) {
        props['_●のし、ラッピングについて'] = sticker.name || '';
      }
      // 包装のみ
      else if (wrapOnly) {
        props['_●のし、ラッピングについて'] = '包装のみ';
      }
      // 包装なし
      else if (noWrap) {
        props['_●のし、ラッピングについて'] = '包装なし';
      }
      // Default — user hasn't explicitly selected any option
      else {
        var meta = (window.cartItemsMetafields && window.cartItemsMetafields[itemKey]) || {};
        var flagsWrapping = meta.flags_wrapping;
        if (flagsWrapping !== false) {
          props['_●のし、ラッピングについて'] = '包装のみ';
        } else {
          props['_●のし、ラッピングについて'] = '包装なし';
        }
      }

      return props;
    },

    buildGiftProperties: function(itemKey, recipient, options) {
      var snapshot = this.getGiftSettingSnapshot(itemKey, recipient, options);
      return this.buildGiftPropertiesFromSnapshot(itemKey, snapshot);
    },

    /**
     * Find shipping product in cart items by shipping type
     * Looks for products with product_type containing 'shipping' and matching shipping type
     *
     * @param {String} shippingType - 'frozen', 'mail', or 'ambient'
     * @returns {Object|null} - Cart item that is a shipping product, or null
     */
    findShippingProductByType: function(shippingType) {
      var cartItems = this.cartItems || [];

      // Keywords to identify shipping products
      var shippingKeywords = ['shipping', '配送', '送料', '運賃'];

      // Keywords for each shipping type
      var typeKeywords = {
        frozen: ['冷凍', 'frozen', 'クール', 'cool'],
        mail: ['メール便', 'mail', 'ネコポス'],
        ambient: ['通常', 'regular', '常温', 'normal', '宅配']
      };

      var targetKeywords = typeKeywords[shippingType] || typeKeywords.ambient;

      for (var i = 0; i < cartItems.length; i++) {
        var item = cartItems[i];
        var searchText = [
          item.product_type || '',
          (item.tags || []).join(' '),
          item.product_title || '',
          item.vendor || ''
        ].join(' ').toLowerCase();

        // Check if it's a shipping product
        var isShippingProduct = shippingKeywords.some(function(keyword) {
          return searchText.indexOf(keyword.toLowerCase()) !== -1;
        });

        if (isShippingProduct) {
          // Check if it matches the shipping type
          var matchesType = targetKeywords.some(function(keyword) {
            return searchText.indexOf(keyword.toLowerCase()) !== -1;
          });

          if (matchesType) {
            return item;
          }
        }
      }

      // Fallback: return any shipping product found
      for (var j = 0; j < cartItems.length; j++) {
        var item2 = cartItems[j];
        var searchText2 = [
          item2.product_type || '',
          (item2.tags || []).join(' '),
          item2.product_title || ''
        ].join(' ').toLowerCase();

        var isShippingProduct2 = shippingKeywords.some(function(keyword) {
          return searchText2.indexOf(keyword.toLowerCase()) !== -1;
        });

        if (isShippingProduct2) {
          return item2;
        }
      }

      return null;
    },

    // ════════════════════════════════════════════════════════
    // WRAPPING / NOSHI / RIBBON
    // ════════════════════════════════════════════════════════

    /**
     * Render wrapping options (包装のみ / のし / リボン他) radio cards
     * @param {string} itemId - Unique item identifier for radio name attribute
     * @param {string} itemKey - Cart item key for noshi modal binding
     * @param {string} [mode] - Mode override
     * @param {Object} [recipient] - Recipient object (multi mode — reads from recipient.giftSettings)
     * @returns {string} HTML string
     */
    renderWrappingOptions: function(itemId, itemKey, mode, recipient, options) {
      var opts = options || {};
      var sourceItemKey = opts.sourceItemKey || itemKey;
      var splitLineKey = opts.splitLineKey || null;

      // Product metafield flags
      var meta = (window.cartItemsMetafields && window.cartItemsMetafields[sourceItemKey]) || {};
      var flagsWrapping = meta.flags_wrapping;   // false = no wrap mode, true/null = wrap mode
      var flagsNoshi = meta.flags_noshi;         // false = disable noshi
      var flagsRibbon = meta.flags_ribbon;       // false = disable ribbon

      // flags_wrapping === false → hide Block1(包装のみ), show Block4(包装なし)
      // flags_wrapping !== false → show Block1(包装のみ), hide Block4(包装なし)
      var showWrap = flagsWrapping !== false;
      var showNone = !showWrap;

      // Check apply-items: only enable noshi/ribbon for products in metaobject apply-items
      // Default to enabled if mapping data is not available
      var hasNoshi = true;
      if (window.cartItemsApplicableNoshi && window.cartItemsApplicableNoshi.hasOwnProperty(sourceItemKey)) {
        hasNoshi = window.cartItemsApplicableNoshi[sourceItemKey].length > 0;
      }
      var hasRibbon = true;
      if (window.cartItemsApplicableRibbon && window.cartItemsApplicableRibbon.hasOwnProperty(sourceItemKey)) {
        hasRibbon = window.cartItemsApplicableRibbon[sourceItemKey].length > 0;
      }

      // flags_noshi/flags_ribbon override: false forces disable
      if (flagsNoshi === false) hasNoshi = false;
      if (flagsRibbon === false) hasRibbon = false;

      var noshiDisabled = hasNoshi ? '' : ' disabled';
      var noshiDisabledClass = hasNoshi ? '' : ' is-disable';
      var ribbonDisabled = hasRibbon ? '' : ' disabled';
      var ribbonDisabledClass = hasRibbon ? '' : ' is-disable';

      // Determine which option is currently selected based on saved settings
      var savedOption = showWrap ? 'wrap' : 'none'; // default based on flags_wrapping
      var resolvedMode = mode || this.currentMode;

      if (splitLineKey && resolvedMode === 'single') {
        var splitSettings = this.getSingleSplitLineSettings(splitLineKey) || {};
        if (splitSettings.wrapOnly) savedOption = 'wrap';
        else if (splitSettings.noWrap) savedOption = 'none';
        else if (splitSettings.noshi) savedOption = 'noshi';
        else if (splitSettings.ribbon) savedOption = 'ribbon';
      } else if (splitLineKey && resolvedMode === 'multi' && recipient) {
        var rSplitSettings = this.getRecipientSplitLineSettings(recipient, splitLineKey) || {};
        if (rSplitSettings.wrapOnly) savedOption = 'wrap';
        else if (rSplitSettings.noWrap) savedOption = 'none';
        else if (rSplitSettings.noshi) savedOption = 'noshi';
        else if (rSplitSettings.ribbon) savedOption = 'ribbon';
      } else if (resolvedMode === 'multi' && recipient) {
        // Multi mode: read from recipient.giftSettings[itemKey]
        var gs = (recipient.giftSettings && recipient.giftSettings[itemKey]) || {};
        if (gs.wrapOnly) savedOption = 'wrap';
        else if (gs.noWrap) savedOption = 'none';
        else if (gs.noshi) savedOption = 'noshi';
        else if (gs.ribbon) savedOption = 'ribbon';
      } else {
        if (this.wrapOnlyItems[resolvedMode] && this.wrapOnlyItems[resolvedMode][itemKey]) {
          savedOption = 'wrap';
        } else if (this.noWrapItems[resolvedMode] && this.noWrapItems[resolvedMode][itemKey]) {
          savedOption = 'none';
        } else if (this.noshiSettings && this.noshiSettings[resolvedMode][itemKey]) {
          savedOption = 'noshi';
        } else if (this.stickerSettings && this.stickerSettings[resolvedMode][itemKey]) {
          savedOption = 'ribbon';
        }
      }

      // recipientId attr for multi mode elements (so handlers can find the recipient)
      var recipientIdAttr = (resolvedMode === 'multi' && recipient) ? ' data-recipient-id="' + recipient.id + '"' : '';
      var splitAttr = splitLineKey ? (' data-split-line-key="' + splitLineKey + '" data-source-item-key="' + sourceItemKey + '"') : '';

      var html = '';
      html += '<h4 class="c-ttl-03">包装・のし・リボン</h4>';
      html += '<div class="c-card-01 js-packageList" data-item-key="' + (splitLineKey || itemKey) + '"' + (splitLineKey ? (' data-split-line-key="' + splitLineKey + '" data-source-item-key="' + sourceItemKey + '"') : '') + '>';

      // Block 1: 包装のみ (shown when flags_wrapping !== false)
      if (showWrap) {
        html += '<div class="c-card-01__item">';
        html += '<label class="option-card js-packageItem js-packageWrap">';
        html += '<input type="radio" name="packageOption_' + itemId + '" value="wrap" class="option-card__radio"' + (savedOption === 'wrap' ? ' checked' : '') + '>';
        html += '<div class="option-card__inner js-packageClick">';
        // Product-level override (metafield yokumoku.wrap_thumbnail), e.g. seasonal wrapping paper
        var wrapImage = meta.wrap_thumbnail || this.freeBagImage;
        if (wrapImage) {
          html += '<div class="option-card__thumb"><img src="' + wrapImage + '" alt=""></div>';
        } else {
          html += '<div class="option-card__thumb"></div>';
        }
        html += '<div class="option-card__text">';
        html += '<p class="option-card__title">包装のみ<span class="c-badge c-badge--free">無料サービス</span></p>';
        html += '</div>';
        html += '</div>';
        html += '</label>';
        html += '</div>';
      }

      // Block 2: のし (disabled if flags_noshi=false or product not in apply-items)
      // Overlay blocks radio check — click opens modal, save sets radio
      html += '<div class="c-card-01__item" style="position: relative;">';
      html += '<label class="option-card js-packageItem js-packageNoshi' + noshiDisabledClass + '">';
      html += '<input type="radio" name="packageOption_' + itemId + '" value="noshi" class="option-card__radio"' + (savedOption === 'noshi' ? ' checked' : '') + noshiDisabled + '>';
      html += '<a href="#" class="option-card__inner js-packageClick" data-open-noshi="' + sourceItemKey + '"' + recipientIdAttr + splitAttr + '>';
      if (this.noshiImage) {
        html += '<div class="option-card__thumb"><img src="' + this.noshiImage + '" alt=""></div>';
      } else {
        html += '<div class="option-card__thumb"></div>';
      }
      html += '<div class="option-card__text">';
      html += '<p class="option-card__title">のし<span class="c-badge c-badge--free">無料サービス</span></p>';
      html += '</div>';
      html += '</a>';
      html += '</label>';
      if (hasNoshi) {
        html += '<div data-overlay-noshi="' + sourceItemKey + '"' + recipientIdAttr + splitAttr + ' style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 5; cursor: pointer;"></div>';
      }
      html += '</div>';

      // Block 3: リボン他 (disabled if flags_ribbon=false or product not in apply-items)
      // Overlay blocks radio check — click opens modal, save sets radio
      html += '<div class="c-card-01__item" style="position: relative;">';
      html += '<label class="option-card js-packageItem js-packageRibbon' + ribbonDisabledClass + '">';
      html += '<input type="radio" name="packageOption_' + itemId + '" value="ribbon" class="option-card__radio"' + (savedOption === 'ribbon' ? ' checked' : '') + ribbonDisabled + '>';
      html += '<a href="#" class="option-card__inner js-packageClick" data-open-sticker="' + sourceItemKey + '"' + recipientIdAttr + splitAttr + '>';
      if (this.ribbonImage) {
        html += '<div class="option-card__thumb"><img src="' + this.ribbonImage + '" alt=""></div>';
      } else {
        html += '<div class="option-card__thumb"></div>';
      }
      html += '<div class="option-card__text">';
      html += '<p class="option-card__title"><span class="option-card__title--txt">リボンシール・<br class="u-md-max">季節ラッピング</span><span class="c-badge c-badge--free">無料サービス</span></p>';
      html += '</div>';
      html += '</a>';
      html += '</label>';
      if (hasRibbon) {
        html += '<div data-overlay-ribbon="' + sourceItemKey + '"' + recipientIdAttr + splitAttr + ' style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 5; cursor: pointer;"></div>';
      }
      html += '</div>';

      // Block 4: 包装なし (shown when flags_wrapping === false)
      if (showNone) {
        html += '<div class="c-card-01__item">';
        html += '<label class="option-card js-packageItem js-packageNone">';
        html += '<input type="radio" name="packageOption_' + itemId + '" value="none" class="option-card__radio"' + (savedOption === 'none' ? ' checked' : '') + '>';
        html += '<div class="option-card__inner js-packageClick">';
        html += '<div class="option-card__text">';
        html += '<p class="option-card__title">包装なし</p>';
        html += '</div>';
        html += '</div>';
        html += '</label>';
        html += '</div>';
      }

      html += '</div>';
      return html;
    },

    /**
     * Render wrapping alert + noshi status summary
     * @param {string} itemKey - Cart item key
     * @param {string} iconKey - shippingAssets icon key ('iconPackagingInfo' or 'iconInfo')
     * @param {Object} [recipient] - Recipient object (multi mode)
     * @returns {string} HTML string
     */
    renderWrappingAlert: function(itemKey, iconKey, recipient, options) {
      var opts = options || {};
      var html = '';
      html += this.renderNoshiStatusSummary(itemKey, recipient, opts);
      html += this.renderStickerStatusSummary(itemKey, recipient, opts);
      return html;
    },

    /**
     * Get noshi metaobject entries applicable to a cart item.
     * Uses pre-computed index mapping from Liquid (window.cartItemsApplicableNoshi).
     */
    getNoshiDataForItem: function(itemKey) {
      if (!window.noshiData) return [];

      // Use pre-computed index mapping if available
      if (window.cartItemsApplicableNoshi && window.cartItemsApplicableNoshi[itemKey]) {
        var indices = window.cartItemsApplicableNoshi[itemKey];
        return indices
          .map(function(i) { return window.noshiData[i]; })
          .filter(function(entry) { return entry && entry.kind === 'のし'; });
      }

      // No mapping found -> show all noshi (flags_noshi=true/empty means no restriction)
      return window.noshiData;
    },

    /**
     * Open noshi modal for a specific cart item
     * @param {string} itemKey
     * @param {string|null} [recipientId] - Recipient ID (multi mode)
     */
    openNoshiModal: function(itemKey, recipientId, splitLineKey, sourceItemKey) {
      var self = this;
      this.currentNoshiItemKey = itemKey;
      this.currentNoshiRecipientId = recipientId || null;
      this.currentNoshiSplitLineKey = splitLineKey || null;
      this.currentNoshiSourceItemKey = sourceItemKey || itemKey;
      this.selectedNoshiEntry = null;
      this.currentNoshiMode = 'normal';

      var data = this.getNoshiDataForItem(itemKey);

      // Resolve saved noshi: multi mode reads from recipient.giftSettings, single from flat object
      var savedNoshi;
      if (this.currentMode === 'multi' && recipientId && splitLineKey) {
        var recipientForNoshi = this.getMultiRecipientById(recipientId);
        var rSplitNoshiSettings = recipientForNoshi ? this.getRecipientSplitLineSettings(recipientForNoshi, splitLineKey) : null;
        savedNoshi = rSplitNoshiSettings ? rSplitNoshiSettings.noshi : null;
      } else if (this.currentMode === 'multi' && recipientId) {
        var recipient = this.getMultiRecipientById(recipientId);
        savedNoshi = recipient && recipient.giftSettings && recipient.giftSettings[itemKey]
          ? recipient.giftSettings[itemKey].noshi
          : null;
      } else if (this.currentMode === 'single' && splitLineKey) {
        var splitSettings = this.getSingleSplitLineSettings(splitLineKey) || {};
        savedNoshi = splitSettings.noshi || null;
      } else {
        savedNoshi = this.noshiSettings[this.currentMode][itemKey];
      }

      // Set count to saved noshi count or product quantity
      var item = this.cartItems.find(function(i) { return i.key === itemKey; });
      var totalQty = item ? item.quantity : 1;
      // In multi mode, use the quantity assigned to this recipient
      if (this.currentMode === 'multi' && recipientId) {
        var _noshiRecipient = this.getMultiRecipientById(recipientId);
        if (_noshiRecipient && _noshiRecipient.items && _noshiRecipient.items[itemKey] !== undefined) {
          totalQty = parseInt(_noshiRecipient.items[itemKey], 10) || 1;
        }
      }
      var splitQty = null;
      if (splitLineKey) {
        if (this.currentMode === 'multi' && recipientId) {
          var _splitNoshiR = this.getMultiRecipientById(recipientId);
          splitQty = _splitNoshiR ? this.getRecipientSplitLineQty(_splitNoshiR, splitLineKey) : null;
        } else {
          splitQty = this.getSingleSplitLineQty(splitLineKey);
        }
      }
      // Reset modal state FIRST (before setting count, since reset clears it)
      this.resetNoshiModal();

      // Set count AFTER reset
      var savedCount = splitQty || ((savedNoshi && savedNoshi.count) ? Math.min(savedNoshi.count, totalQty) : totalQty);
      $('[data-noshi-count-input]').val(savedCount);
      $('[data-noshi-count-input]').prop('disabled', false);
      $('[data-noshi-count-total]').text(splitQty || totalQty);
      this.syncGiftSettingCountControl($('[data-noshi-count-input]'), splitQty || totalQty);

      // Set hidden inputs
      $('#noshi-current-item-key').val(itemKey);
      $('#noshi-paper-size').val((window.cartItemsNoshiSize && window.cartItemsNoshiSize[itemKey]) || 'A4');
      $('#noshi-metaobject-gid').val('');
      $('#noshi-current-mode').val('normal');

      // Populate purpose select (initially cards remain hidden until purpose selected)
      this.populateNoshiPurposeSelect(data);

      // Restore saved settings if exists
      if (savedNoshi) {
        this.restoreNoshiSettings(itemKey, data, savedNoshi);
      }

      // Open modal
      this.openModal('modalGift');
    },

    /**
     * Reset noshi modal to initial state
     */
    resetNoshiModal: function() {
      // Clear validation errors from previous session
      if (window.NoshiDialogCommon) window.NoshiDialogCommon.clearValidationErrors();

      // Clear cards
      $('[data-gift-options]').empty().removeClass('c-card-02 c-card-03');
      $('[data-noshi-cards-block]').hide();
      $('[data-noshi-purpose-select]').val('');

      // Reset preview image (block-gift__img is static category image, don't clear)
      $('.js-preview-img').attr('src', '');

      // Hide input fields
      $('[data-noshi-input-fields]').hide();
      $('[data-noshi-normal-fields]').show();
      $('[data-noshi-uchiiwai-fields]').hide();

      // Clear input values + reset corp toggle (corp block stays visible, only fields hide)
      $('#noshi-line1, #noshi-line2, #noshi-uchiiwai-name, #noshi-uchiiwai-furigana, #corpCompany, #corpTitle, #corpName').val('');
      $('#modalGift .js-corpToggle').prop('checked', false);
      $('#modalGift .js-corpFields').hide();

      // Hide preview
      $('[data-noshi-preview-section]').hide();
      $('.js-giftPreview').hide();
      $('.js-preview-img').attr('src', '');

      // Count always visible
      $('[data-noshi-count-input]').prop('disabled', false);
      this.syncGiftSettingCountControl($('[data-noshi-count-input]'));
      $('[data-noshi-preview-btn]').hide();
      $('[data-noshi-preview-note]').hide();

      // Disable save button
      $('#modalGift [data-save-noshi]').prop('disabled', true);
      this.noshiPreviewConfirmed = false;
    },

    getNoshiPurposeOptions: function(data) {
      var displayOrder = window.noshiTypeDisplayOrder || {};
      var typeSet = {};
      (data || []).forEach(function(entry) {
        if (!entry || entry.kind !== 'のし' || !entry.type) return;
        if (!typeSet[entry.type]) {
          typeSet[entry.type] = {
            type: entry.type,
            category: entry.category || '',
            order: displayOrder.hasOwnProperty(entry.type) ? displayOrder[entry.type] : 999999
          };
        }
      });

      var options = Object.keys(typeSet).map(function(type) {
        return typeSet[type];
      });

      options.sort(function(a, b) {
        return a.order - b.order;
      });

      return options;
    },

    populateNoshiPurposeSelect: function(data) {
      var $select = $('[data-noshi-purpose-select]');
      if (!$select.length) return;

      $select.empty();
      $select.append('<option value="">用途をお選びください</option>');
      this.getNoshiPurposeOptions(data).forEach(function(option) {
        $select.append('<option value="' + option.type + '">' + option.type + '</option>');
      });
      $select.val('');
    },

    /**
     * Render noshi cards for selected purpose.
     */
    renderNoshiCardsByPurpose: function(data, purpose) {
      var filtered = (data || []).filter(function(entry) {
        return entry && entry.kind === 'のし' && entry.type === purpose;
      });

      var primaryCategory = filtered.length > 0 ? (filtered[0].category || '') : '';
      var isChouji = primaryCategory === '弔事';
      var cardClass = isChouji ? 'c-card-03__item' : 'c-card-02__item';
      var containerClass = isChouji ? 'c-card-03' : 'c-card-02';
      var $container = $('[data-gift-options]');
      $container.removeClass('c-card-02 c-card-03').addClass(containerClass);

      var html = '';
      filtered.forEach(function(entry, index) {
        var imgSrc = entry.image || '';
        html += '<div class="' + cardClass + '">';
        html += '<a href="#" class="p-gift-item" data-noshi-index="' + index + '"';
        html += ' data-noshi-gid="' + (entry.gid || '') + '"';
        html += ' data-img="' + imgSrc + '"';
        html += ' data-title="' + (entry.omotegaki || entry.name || '') + '"';
        html += ' data-desc="' + (entry.subtitle || '') + '">';
        html += '<div class="p-gift-img">';
        if (imgSrc) {
          html += '<img src="' + imgSrc + '" alt="' + (entry.name || '') + '">';
        }
        html += '</div>';
        html += '<div class="p-gift-info">';
        html += '<h4>' + (entry.omotegaki || '') + '</h4>';
        html += '<p>' + (entry.name || '') + '</p>';
        if (entry.type) html += '<small>' + entry.type + '</small>';
        html += '</div>';
        html += '</a>';
        html += '</div>';
      });

      $container.html(html);

      // Store filtered data for card selection
      this._currentNoshiFiltered = filtered;
    },

    /**
     * Handle noshi purpose selection
     */
    handleNoshiPurposeChange: function(purpose) {
      var data = this.getNoshiDataForItem(this.currentNoshiItemKey);

      if (purpose) {
        $('[data-noshi-cards-block]').show();
        this.renderNoshiCardsByPurpose(data, purpose);
      } else {
        $('[data-noshi-cards-block]').hide();
        $('[data-gift-options]').empty().removeClass('c-card-02 c-card-03');
        this._currentNoshiFiltered = [];
      }

      // Reset selection state
      this.selectedNoshiEntry = null;
      this.currentNoshiMode = null;

      // Clear validation errors + hide & clear shared elements
      if (window.NoshiDialogCommon) window.NoshiDialogCommon.clearValidationErrors();
      $('[data-noshi-input-fields]').hide();
      $('[data-noshi-preview-section]').hide();
      $('#noshi-line1, #noshi-line2').val('');
      $('#noshi-uchiiwai-name, #noshi-uchiiwai-furigana').val('');
      $('#corpCompany, #corpTitle, #corpName').val('');
      $('#modalGift .js-corpToggle').prop('checked', false);
      $('#modalGift .js-corpFields').hide();
      $('#noshi-metaobject-gid').val('');
      $('.js-preview-img').attr('src', '');
      $('[data-noshi-preview-btn]').hide();
      $('[data-noshi-preview-note]').hide();
      $('#modalGift [data-save-noshi]').prop('disabled', true);
      this.noshiPreviewConfirmed = false;
    },

    /**
     * Handle noshi card selection
     */
    handleNoshiCardSelect: function($card) {
      var index = parseInt($card.attr('data-noshi-index'), 10);
      var filtered = this._currentNoshiFiltered || [];
      var entry = filtered[index];
      if (!entry) return;

      // Update active state
      $('#modalGift .p-gift-item').removeClass('is-active');
      $card.addClass('is-active');

      // Store selection
      this.selectedNoshiEntry = entry;
      $('#noshi-metaobject-gid').val(entry.gid || '');
      this.noshiPreviewConfirmed = false;

      // Update preview image (not block-gift__img which is static category image)
      var imgSrc = entry.image || '';
      $('.js-preview-img').attr('src', imgSrc);

      // Show input fields based on inputTextSet
      this.showNoshiInputFields(entry);

      // Update preview
      this.updateNoshiPreview();
      $('[data-noshi-preview-section]').show();

      // Show count
      $('[data-noshi-count]').show();
      this.syncGiftSettingCountControl($('[data-noshi-count-input]'));
    },

    /**
     * Show appropriate input fields based on noshi inputTextSet
     */
    showNoshiInputFields: function(noshi) {
      this.currentNoshiMode = window.NoshiDialogCommon.showInputFieldsForEntry(noshi);
    },

    /**
     * Handle corporate toggle checkbox
     */
    handleCorpToggle: function() {
      this.currentNoshiMode = window.NoshiDialogCommon.handleCorpToggle(this.selectedNoshiEntry);
      this.updateNoshiPreview();
    },

    /**
     * Update the static noshi preview panel
     */
    updateNoshiPreview: function() {
      var entry = this.selectedNoshiEntry;
      if (!entry) return;
      window.NoshiDialogCommon.resetPreviewVisibility();
    },

    /**
     * Handle noshi preview button click → gather current state and fetch API image
     */
    /**
     * Validate noshi input fields. Returns texts object if valid, null if invalid.
     */
    validateNoshiFields: function() {
      return window.NoshiDialogCommon.validateFields(this.currentNoshiMode);
    },

    handleNoshiPreviewClick: function() {
      var entry = this.selectedNoshiEntry;
      if (!entry) return;

      var texts = this.validateNoshiFields();
      if (!texts) return;
      var mode = this.currentNoshiMode;

      window.NoshiDialogCommon.renderPreviewText(entry, mode, texts);
      $('.js-giftPreview').show();
      this.noshiPreviewConfirmed = true;
      $('#modalGift [data-save-noshi]').prop('disabled', false);
      this.fetchNoshiPreviewImage(entry, mode, texts);
    },

    /**
     * Bulk noshi preview selectors (maps to NoshiDialogCommon's selector system)
     */
    getBulkNoshiSelectors: function() {
      return {
        modal: '#modalBulkWrapping',
        previewTitle: '.js-bulk-preview-title',
        previewDesc: '.js-bulk-preview-subtitle',
        previewPersonName: '.js-bulk-preview-name',
        previewImage: '.js-bulk-preview-img',
        previewContent: '.js-bulkGiftPreview',
        paperSizeInput: '#noshi-paper-size',
        normalName1: '#bulk-noshi-line1',
        normalName2: '#bulk-noshi-line2',
        uchiiwaiName: '#bulk-noshi-uchiiwai-name',
        uchiiwaiFurigana: '#bulk-noshi-uchiiwai-furigana',
        corpCompany: '#bulk-corp-company',
        corpTitle: '#bulk-corp-title',
        corpName: '#bulk-corp-name',
        currentModeInput: '#bulk-noshi-current-mode'
      };
    },

    /**
     * Handle bulk noshi preview button click
     */
    handleBulkNoshiPreviewClick: function() {
      var entry = this.selectedBulkNoshiEntry;
      if (!entry) return;

      var mode = this.currentBulkNoshiMode || 'normal';
      var bulkSelectors = this.getBulkNoshiSelectors();

      var texts = window.NoshiDialogCommon
        ? window.NoshiDialogCommon.validateFields(mode, { selectors: bulkSelectors })
        : this.validateBulkNoshiFieldsForPreview();
      if (!texts) return;

      // Render preview text via NoshiDialogCommon
      window.NoshiDialogCommon.renderPreviewText(entry, mode, texts, { selectors: bulkSelectors });
      $('.js-bulkGiftPreview').show();

      // Fetch preview image via API
      window.NoshiDialogCommon.fetchPreviewImage(entry, mode, texts, { selectors: bulkSelectors });

      this.bulkNoshiPreviewConfirmed = true;
      this.updateBulkWrappingBtnState();
    },

    /**
     * Validate bulk noshi fields for preview (returns texts object or null)
     */
    validateBulkNoshiFieldsForPreview: function() {
      var mode = this.currentBulkNoshiMode || 'normal';
      if (mode === 'uchiiwai') {
        return window.NoshiDialogCommon
          ? window.NoshiDialogCommon.normalizeUchiiwaiNoshiTexts(this.getBulkNoshiDialogOptions())
          : {
              name: $('#bulk-noshi-uchiiwai-name').val() || '',
              furigana: $('#bulk-noshi-uchiiwai-furigana').val() || ''
            };
      } else if (mode === 'corporate') {
        var normalizedBulkCorpTexts = window.NoshiDialogCommon
          ? window.NoshiDialogCommon.normalizeCorporateNoshiTexts(this.getBulkNoshiDialogOptions())
          : {
              company: $('#bulk-corp-company').val() || '',
              title: $('#bulk-corp-title').val() || '',
              name: $('#bulk-corp-name').val() || ''
            };
        return {
          company: normalizedBulkCorpTexts.company,
          title: normalizedBulkCorpTexts.title,
          name: normalizedBulkCorpTexts.name,
          name1: normalizedBulkCorpTexts.name,
          name2: ''
        };
      } else {
        var normalizedBulkTexts = window.NoshiDialogCommon
          ? window.NoshiDialogCommon.normalizeNormalNoshiTexts(this.getBulkNoshiDialogOptions())
          : {
              name1: $('#bulk-noshi-line1').val() || '',
              name2: $('#bulk-noshi-line2').val() || ''
            };
        return {
          name1: normalizedBulkTexts.name1,
          name2: normalizedBulkTexts.name2,
          name: normalizedBulkTexts.name1
        };
      }
    },

    /**
     * Update the bulk wrapping save button state
     * Disabled when:
     *   - noshi is selected but preview hasn't been confirmed
     *   - ribbon is selected but no ribbon entry chosen
     */
    updateBulkWrappingBtnState: function() {
      var wrappingVal = $('#modalBulkWrapping input[name="bulk_wrapping"]:checked').val();
      var $btn = $('[data-save-bulk-wrapping]');
      var shouldDisable = false;

      if (wrappingVal === 'noshi') {
        if (!this.selectedBulkNoshiEntry || !this.bulkNoshiPreviewConfirmed) {
          shouldDisable = true;
        }
      } else if (wrappingVal === 'ribbon') {
        var ribbonGid = $('#bulk-ribbon-unified').val();
        if (!ribbonGid) {
          shouldDisable = true;
        }
      }

      $btn.prop('disabled', shouldDisable).toggleClass('is-disabled', shouldDisable);
    },

    /**
     * Show bulk noshi preview not confirmed error via click on disabled save button area
     */
    handleBulkWrappingSaveClick: function() {
      var wrappingVal = $('#modalBulkWrapping input[name="bulk_wrapping"]:checked').val();
      if (wrappingVal === 'noshi' && this.selectedBulkNoshiEntry && !this.bulkNoshiPreviewConfirmed) {
        window.alert('のしプレビューをご確認ください。');
      } else if (wrappingVal === 'ribbon' && !$('#bulk-ribbon-unified').val()) {
        window.alert('リボン・シール・ラッピングを選択してください。');
      }
    },

    /**
     * Fetch noshi preview image from API
     */
    fetchNoshiPreviewImage: function(entry, mode, texts) {
      window.NoshiDialogCommon.fetchPreviewImage(entry, mode, texts);
    },

    /**
     * Save noshi settings for current item
     */
    saveNoshi: function() {
      var itemKey = this.currentNoshiItemKey;
      var splitLineKey = this.currentNoshiSplitLineKey;
      var entry = this.selectedNoshiEntry;
      if (!entry) {
        window.alert('のしの種類を選択してください');
        return;
      }
      if (!itemKey) return;
      if (!this.noshiPreviewConfirmed) {
        window.alert('のしプレビューをご確認ください。');
        return;
      }

      var result = this.validateNoshiFields();
      if (!result) return;
      var mode = this.currentNoshiMode;
      var texts = result;

      // Read noshi count from input (how many units get noshi)
      var item = this.cartItems.find(function(i) { return i.key === itemKey; });
      var totalQty = item ? item.quantity : 1;
      if (this.currentMode === 'multi' && this.currentNoshiRecipientId) {
        var _saveNoshiRecipient = this.getMultiRecipientById(this.currentNoshiRecipientId);
        if (_saveNoshiRecipient && _saveNoshiRecipient.items && _saveNoshiRecipient.items[itemKey] !== undefined) {
          totalQty = parseInt(_saveNoshiRecipient.items[itemKey], 10) || 1;
        }
      }
      var splitQty = null;
      if (splitLineKey) {
        if (this.currentMode === 'multi' && this.currentNoshiRecipientId) {
          var _splitNoshiRecipient = this.getMultiRecipientById(this.currentNoshiRecipientId);
          splitQty = _splitNoshiRecipient ? this.getRecipientSplitLineQty(_splitNoshiRecipient, splitLineKey) : null;
        } else {
          splitQty = this.getSingleSplitLineQty(splitLineKey);
        }
      }
      var noshiCount = this.syncGiftSettingCountControl($('[data-noshi-count-input]'), splitQty || totalQty);

      var noshiData = {
        gid: entry.gid || '',
        name: entry.name || '',
        category: entry.category || '',
        type: entry.type || '',
        omotegaki: entry.omotegaki || '',
        subtitle: entry.subtitle || '',
        image: entry.image || '',
        mode: mode,
        texts: texts,
        previewImage: $('.js-preview-img').attr('src') || '',
        count: noshiCount
      };

      // Save noshi settings
      if (this.currentMode === 'multi' && this.currentNoshiRecipientId) {
        var recipient = this.getMultiRecipientById(this.currentNoshiRecipientId);
        if (recipient) {
          if (splitLineKey) {
            // Already split — update or further split
            var rSplitQty = this.getRecipientSplitLineQty(recipient, splitLineKey) || 1;
            if (noshiCount < rSplitQty) {
              this.splitExistingRecipientSplitLine(recipient, splitLineKey, noshiData, null, noshiCount);
            } else {
              var rSplitSettings = this.getRecipientSplitLineSettings(recipient, splitLineKey);
              if (rSplitSettings) {
                rSplitSettings.noshi = noshiData;
                rSplitSettings.ribbon = null;
                rSplitSettings.wrapOnly = false;
                rSplitSettings.noWrap = false;
              }
            }
          } else if (noshiCount < totalQty) {
            // Need to split
            this.createRecipientSplitLinesFromNoshi(recipient, itemKey, totalQty, noshiData, noshiCount);
          } else {
            // No split needed — all units get noshi
            var s = this.getRecipientItemSettings(recipient, itemKey);
            s.noshi = noshiData;
            s.ribbon = null; s.wrapOnly = false; s.noWrap = false;
            // Clear split lines if they existed
            if (recipient.splitLines) delete recipient.splitLines[itemKey];
          }
        }
      } else if (this.currentMode === 'single' && splitLineKey) {
        var splitQtyForSave = this.getSingleSplitLineQty(splitLineKey) || 1;
        if (noshiCount < splitQtyForSave) {
          // Further split this split line: noshiCount with noshi, remainder without
          this.splitExistingSplitLine(splitLineKey, noshiData, null, noshiCount);
        } else {
          var splitSettings = this.getSingleSplitLineSettings(splitLineKey);
          if (splitSettings) {
            splitSettings.noshi = noshiData;
            splitSettings.ribbon = null;
            splitSettings.wrapOnly = false;
            splitSettings.noWrap = false;
          }
        }
      } else {
        if (SHIPPING_DEBUG) console.log('[SPLIT][saveNoshi] mode:', this.currentMode, 'itemKey:', itemKey, 'noshiCount:', noshiCount, 'totalQty:', totalQty, 'willSplit:', this.currentMode === 'single' && noshiCount < totalQty);
        if (this.currentMode === 'single' && noshiCount < totalQty) {
          this.createSingleSplitLinesFromNoshi(itemKey, totalQty, noshiData, noshiCount);
        } else {
          this.noshiSettings[this.currentMode][itemKey] = noshiData;
          delete this.singleSplitLines[itemKey];
          // Noshi selected — clear other options
          delete this.stickerSettings[this.currentMode][itemKey];
          delete this.wrapOnlyItems[this.currentMode][itemKey];
          delete this.noWrapItems[this.currentMode][itemKey];
        }
      }

      // Persist and re-render (renderCartItems will set radio to noshi based on saved data)
      this.saveData();
      this.closeModal();
      this.renderCartItems();
      this.updateSummary();
    },

    /**
     * Restore noshi settings when opening modal for an item with saved data
     * @param {string} itemKey
     * @param {Array} data - Available noshi entries
     * @param {Object} [savedNoshi] - Pre-resolved saved noshi (avoids double lookup)
     */
    restoreNoshiSettings: function(itemKey, data, savedNoshi) {
      var saved = savedNoshi || (this.currentMode === 'multi' && this.currentNoshiRecipientId && this.currentNoshiSplitLineKey
        ? (function(self) {
            var r = self.getMultiRecipientById(self.currentNoshiRecipientId);
            var rss = r ? self.getRecipientSplitLineSettings(r, self.currentNoshiSplitLineKey) : null;
            return rss ? rss.noshi : null;
          })(this)
        : (this.currentMode === 'multi' && this.currentNoshiRecipientId
          ? (function(self) {
              var r = self.getMultiRecipientById(self.currentNoshiRecipientId);
              return r && r.giftSettings && r.giftSettings[itemKey] ? r.giftSettings[itemKey].noshi : null;
            })(this)
          : (this.currentMode === 'single' && this.currentNoshiSplitLineKey
            ? ((this.getSingleSplitLineSettings(this.currentNoshiSplitLineKey) || {}).noshi || null)
            : this.noshiSettings[this.currentMode][itemKey])));
      if (!saved) return;

      var restorePurpose = saved.type || '';
      if (!restorePurpose) return;

      // Restore purpose and re-render cards for that purpose
      $('[data-noshi-purpose-select]').val(restorePurpose);
      this.handleNoshiPurposeChange(restorePurpose);

      // Find matching entry in rendered list
      var matchedEntry = null;
      var matchedIndex = -1;
      var filtered = this._currentNoshiFiltered || [];
      filtered.forEach(function(entry, i) {
        if (entry.gid === saved.gid || entry.name === saved.name) {
          matchedEntry = entry;
          matchedIndex = i;
        }
      });

      if (!matchedEntry) return;

      // Select the card
      var $card = $('#modalGift .p-gift-item[data-noshi-index="' + matchedIndex + '"]');
      if ($card.length) {
        this.selectedNoshiEntry = matchedEntry;
        $card.addClass('is-active');

        // Update preview image (not block-gift__img which is static category image)
        $('.js-preview-img').attr('src', matchedEntry.image || '');

        // Show and populate input fields
        this.showNoshiInputFields(matchedEntry);
        var inputModes = window.NoshiDialogCommon.getEntryInputModes(matchedEntry);

        // Restore text values
        if (saved.mode === 'uchiiwai') {
          $('#noshi-uchiiwai-name').val(saved.texts.name || '');
          $('#noshi-uchiiwai-furigana').val(saved.texts.furigana || '');
          if (window.NoshiDialogCommon) {
            window.NoshiDialogCommon.normalizeUchiiwaiNoshiTexts();
          }
        } else if (saved.mode === 'corporate' && inputModes.allowCorporate) {
          this.currentNoshiMode = 'corporate';
          $('#modalGift .js-corpToggle').prop('checked', true);
          $('#modalGift .js-corpFields').show();
          $('[data-noshi-normal-fields]').hide();
          $('#corpCompany').val(saved.texts.company || '');
          $('#corpTitle').val(saved.texts.title || '');
          $('#corpName').val(saved.texts.name || '');
          if (window.NoshiDialogCommon) {
            window.NoshiDialogCommon.normalizeCorporateNoshiTexts();
          }
        } else {
          if (inputModes.defaultMode === 'uchiiwai') {
            $('#noshi-uchiiwai-name').val(saved.texts.name || saved.texts.name1 || '');
            $('#noshi-uchiiwai-furigana').val(saved.texts.furigana || '');
            if (window.NoshiDialogCommon) {
              window.NoshiDialogCommon.normalizeUchiiwaiNoshiTexts();
            }
          } else {
            $('#noshi-line1').val(saved.texts.name1 || saved.texts.name || '');
            $('#noshi-line2').val(saved.texts.name2 || '');
            if (window.NoshiDialogCommon) {
              window.NoshiDialogCommon.normalizeNormalNoshiTexts();
            }
          }
        }

        // Restore saved API preview image if available
        if (saved.previewImage) {
          $('.js-preview-img').attr('src', saved.previewImage);
        }

        // Restore noshi count
        var countItem = this.cartItems.find(function(i) { return i.key === itemKey; });
        var countTotal;
        if (this.currentNoshiSplitLineKey) {
          if (this.currentMode === 'multi' && this.currentNoshiRecipientId) {
            var _restoreR = this.getMultiRecipientById(this.currentNoshiRecipientId);
            countTotal = _restoreR ? (this.getRecipientSplitLineQty(_restoreR, this.currentNoshiSplitLineKey) || 1) : 1;
          } else {
            countTotal = this.getSingleSplitLineQty(this.currentNoshiSplitLineKey) || 1;
          }
        } else if (this.currentMode === 'multi' && this.currentNoshiRecipientId) {
          var _restoreRecipient = this.getMultiRecipientById(this.currentNoshiRecipientId);
          countTotal = (_restoreRecipient && _restoreRecipient.items && _restoreRecipient.items[itemKey] !== undefined)
            ? parseInt(_restoreRecipient.items[itemKey], 10) || 1
            : (countItem ? countItem.quantity : 1);
        } else {
          countTotal = countItem ? countItem.quantity : 1;
        }
        if (saved.count) {
          $('[data-noshi-count-input]').val(Math.min(saved.count, countTotal));
        }

        this.updateNoshiPreview();
        $('[data-noshi-preview-section]').show();
        $('[data-noshi-count]').show();
        this.syncGiftSettingCountControl($('[data-noshi-count-input]'), countTotal);
        $('#modalGift [data-save-noshi]').prop('disabled', false);
        this.noshiPreviewConfirmed = true;
      }
    },

    /**
     * Render noshi status summary for a cart item
     * @param {string} itemKey
     * @param {Object} [recipient] - Recipient object (multi mode)
     */
    renderNoshiStatusSummary: function(itemKey, recipient, options) {
      var opts = options || {};
      var splitLineKey = opts.splitLineKey || null;
      var saved;
      if (splitLineKey && this.currentMode === 'single') {
        var splitSettings = this.getSingleSplitLineSettings(splitLineKey) || {};
        saved = splitSettings.noshi || null;
      } else if (splitLineKey && this.currentMode === 'multi' && recipient) {
        var rSplitNoshiSummary = this.getRecipientSplitLineSettings(recipient, splitLineKey) || {};
        saved = rSplitNoshiSummary.noshi || null;
      } else if (this.currentMode === 'multi' && recipient) {
        saved = (recipient.giftSettings && recipient.giftSettings[itemKey] && recipient.giftSettings[itemKey].noshi) || null;
      } else {
        saved = this.noshiSettings[this.currentMode][itemKey];
      }
      if (!saved) return '';

      // Name line: name1 / name2
      var nameLine = '';
      if (saved.mode === 'uchiiwai') {
        nameLine = this.escapeHtml(saved.texts.name || '');
      } else if (saved.mode === 'corporate') {
        var parts = [saved.texts.company, saved.texts.title, saved.texts.name].filter(Boolean);
        nameLine = this.escapeHtml(parts.join(' / '));
      } else {
        var parts = [saved.texts.name1, saved.texts.name2].filter(Boolean);
        nameLine = this.escapeHtml(parts.join(' / '));
      }

      var recipientAttr = (this.currentMode === 'multi' && recipient) ? ' data-recipient-id="' + recipient.id + '"' : '';
      var splitAttr = splitLineKey ? (' data-split-line-key="' + splitLineKey + '" data-source-item-key="' + itemKey + '"') : '';
      var html = '<div class="p-fee u-mb-15 u-mt-15 noshi-status" data-noshi-status="' + itemKey + '">';
      html += '<div class="p-fee__head">';
      html += '<h4 class="p-fee__title">のし設定</h4>';
      html += '<a href="#" class="p-fee__edit" data-open-noshi="' + itemKey + '"' + recipientAttr + splitAttr + '>変更</a>';
      html += '</div>';
      html += '<ul class="p-fee__list">';
      html += '<li class="p-fee__row"><span class="p-fee__label">種類</span><span>' + this.escapeHtml(saved.category || '') + '</span></li>';
      html += '<li class="p-fee__row"><span class="p-fee__label">表書き</span><span>' + this.escapeHtml(saved.omotegaki || '') + '</span></li>';
      html += '<li class="p-fee__row"><span class="p-fee__label">水引</span><span>' + this.escapeHtml(saved.name || '') + '</span></li>';
      html += '<li class="p-fee__row"><span class="p-fee__label">お名前</span><span>' + nameLine + '</span></li>';
      html += '</ul>';
      html += '</div>';
      return html;
    },

    // ════════════════════════════════════════════════════════
    // STICKER / RIBBON SELECTION UI
    // ════════════════════════════════════════════════════════

    /**
     * Get ribbon/sticker metaobject entries applicable to a cart item.
     */
    /**
     * Derive category from entry fields.
     */
    getRibbonCategory: function(entry) {
      if (entry.kind === 'ラッピング') return '季節ラッピング';
      return entry.type || 'リボンシール';
    },

    getRibbonDataForItem: function(itemKey) {
      if (!window.ribbonData) return [];

      if (window.cartItemsApplicableRibbon && window.cartItemsApplicableRibbon[itemKey]) {
        var indices = window.cartItemsApplicableRibbon[itemKey];
        return indices.map(function(i) { return window.ribbonData[i]; }).filter(Boolean);
      }

      return window.ribbonData;
    },

    // Fixed sticker categories for bulk/error modals
    STICKER_CATEGORIES: ['リボンシール', 'メッセージ付きシール', '季節ラッピング'],

    // Fixed noshi categories (hardcoded, same as individual modal tabs)
    NOSHI_CATEGORIES: ['慶事', '弔事'],

    /**
     * Open sticker modal for a specific item
     * @param {string} itemKey
     * @param {string|null} [recipientId] - Recipient ID (multi mode)
     */
    openStickerModal: function(itemKey, recipientId, splitLineKey, sourceItemKey) {
      this.currentStickerItemKey = itemKey;
      this.currentStickerRecipientId = recipientId || null;
      this.currentStickerSplitLineKey = splitLineKey || null;
      this.currentStickerSourceItemKey = sourceItemKey || itemKey;

      // Resolve saved settings
      var savedRibbon;
      if (this.currentMode === 'multi' && recipientId && splitLineKey) {
        var recipientForSticker = this.getMultiRecipientById(recipientId);
        var rSplitStickerSettings = recipientForSticker ? this.getRecipientSplitLineSettings(recipientForSticker, splitLineKey) : null;
        savedRibbon = rSplitStickerSettings ? rSplitStickerSettings.ribbon : null;
      } else if (this.currentMode === 'multi' && recipientId) {
        var recipient = this.getMultiRecipientById(recipientId);
        savedRibbon = recipient && recipient.giftSettings && recipient.giftSettings[itemKey]
          ? recipient.giftSettings[itemKey].ribbon
          : null;
      } else if (this.currentMode === 'single' && splitLineKey) {
        var splitSettings = this.getSingleSplitLineSettings(splitLineKey) || {};
        savedRibbon = splitSettings.ribbon || null;
      } else {
        savedRibbon = this.stickerSettings[this.currentMode][itemKey];
      }

      var data = this.getRibbonDataForItem(itemKey);

      // Store all data for this session
      this._currentStickerData = data;

      // Set hidden inputs
      $('#sticker-current-item-key').val(itemKey);
      $('#sticker-metaobject-gid').val('');

      // Reset state
      $('[data-sticker-main-preview] img').attr('src', '').hide();
      $('[data-sticker-ribbon-preview] img').attr('src', '').hide();
      // sticker count is always visible
      $('[data-sticker-count-input]').prop('disabled', false);
      $('[data-save-sticker]').prop('disabled', true);

      // Initialize count (same UX as noshi)
      var item = this.cartItems.find(function(i) { return i.key === itemKey; });
      var totalQty = item ? item.quantity : 1;
      // In multi mode, use the quantity assigned to this recipient
      if (this.currentMode === 'multi' && recipientId) {
        var _stickerRecipient = this.getMultiRecipientById(recipientId);
        if (_stickerRecipient && _stickerRecipient.items && _stickerRecipient.items[itemKey] !== undefined) {
          totalQty = parseInt(_stickerRecipient.items[itemKey], 10) || 1;
        }
      }
      var splitQty = null;
      if (splitLineKey) {
        if (this.currentMode === 'multi' && recipientId) {
          var _splitStickerR = this.getMultiRecipientById(recipientId);
          splitQty = _splitStickerR ? this.getRecipientSplitLineQty(_splitStickerR, splitLineKey) : null;
        } else {
          splitQty = this.getSingleSplitLineQty(splitLineKey);
        }
      }
      var savedCount = splitQty || ((savedRibbon && savedRibbon.count) ? Math.min(savedRibbon.count, totalQty) : totalQty);
      $('[data-sticker-count-input]').val(savedCount);
      $('[data-sticker-count-input]').prop('disabled', false);
      $('[data-sticker-count-total]').text(splitQty || totalQty);
      this.syncGiftSettingCountControl($('[data-sticker-count-input]'), splitQty || totalQty);

      // Populate unified select
      this.populateStickerUnifiedSelect(data, '#sticker-unified-select');
      this.currentStickerCategory = '';

      // Restore saved settings if exists
      if (savedRibbon) {
        this.restoreStickerSettings(itemKey, savedRibbon);
      }

      // Open modal
      this.openModal('modalSticker');
    },

    /**
     * Populate unified sticker select dropdown.
     */
    populateStickerUnifiedSelect: function(data, selectSelector) {
      var self = this;
      var html = '<option value="">リボン・シール・ラッピングを選択してください</option>';
      (data || []).forEach(function(entry) {
        var category = self.getRibbonCategory(entry);
        var name = entry.name || '';
        html += '<option value="' + self.escapeHtml(entry.gid || '') + '" data-image="' + self.escapeHtml(entry.image || '') + '" data-category="' + self.escapeHtml(category) + '">' + self.escapeHtml(name) + '</option>';
      });
      $(selectSelector).html(html).val('');
    },

    /**
     * Get active sticker select element based on current category
     */
    getActiveStickerSelect: function() {
      return $('#sticker-unified-select');
    },

    /**
     * Handle sticker select change — update preview image and enable save
     */
    handleStickerSelect: function($select) {
      var selectedGid = $select.val();
      if (!selectedGid) {
        $('[data-sticker-main-preview] img').attr('src', '').hide();
        $('[data-sticker-ribbon-preview] img').attr('src', '').hide();
        // sticker count is always visible
        $('[data-save-sticker]').prop('disabled', true);
        $('#sticker-metaobject-gid').val('');
        return;
      }

      // Find entry from all data
      var entry = (this._currentStickerData || []).find(function(e) {
        return e.gid === selectedGid;
      });

      if (entry) {
        this.currentStickerCategory = this.getRibbonCategory(entry);
        $('#sticker-metaobject-gid').val(entry.gid);
        if (entry.image) {
          $('[data-sticker-main-preview] img').attr('src', entry.image).show();
        } else {
          $('[data-sticker-main-preview] img').attr('src', '').hide();
          $('[data-sticker-ribbon-preview] img').attr('src', '').hide();
        }
        // sticker count is always visible
        this.syncGiftSettingCountControl($('[data-sticker-count-input]'));
        $('[data-save-sticker]').prop('disabled', false);
      }
    },

    /**
     * Save sticker settings
     */
    saveSticker: function() {
      var itemKey = this.currentStickerItemKey;
      var splitLineKey = this.currentStickerSplitLineKey;
      var $select = this.getActiveStickerSelect();
      var selectedGid = $select.val();
      if (!itemKey || !selectedGid) return;

      var entry = (this._currentStickerData || []).find(function(e) {
        return e.gid === selectedGid;
      });
      if (!entry) return;

      var item = this.cartItems.find(function(i) { return i.key === itemKey; });
      var totalQty = item ? item.quantity : 1;
      if (this.currentMode === 'multi' && this.currentStickerRecipientId) {
        var _saveStickerRecipient = this.getMultiRecipientById(this.currentStickerRecipientId);
        if (_saveStickerRecipient && _saveStickerRecipient.items && _saveStickerRecipient.items[itemKey] !== undefined) {
          totalQty = parseInt(_saveStickerRecipient.items[itemKey], 10) || 1;
        }
      }
      var splitQty = null;
      if (splitLineKey) {
        if (this.currentMode === 'multi' && this.currentStickerRecipientId) {
          var _splitRecipient = this.getMultiRecipientById(this.currentStickerRecipientId);
          splitQty = _splitRecipient ? this.getRecipientSplitLineQty(_splitRecipient, splitLineKey) : null;
        } else {
          splitQty = this.getSingleSplitLineQty(splitLineKey);
        }
      }
      var stickerCount = this.syncGiftSettingCountControl($('[data-sticker-count-input]'), splitQty || totalQty);
      $('[data-sticker-count-input]').val(stickerCount);

      var derivedCategory = this.getRibbonCategory(entry);
      var ribbonData = {
        gid: entry.gid || '',
        name: entry.name || '',
        category: derivedCategory,
        image: entry.image || '',
        count: stickerCount
      };

      if (this.currentMode === 'multi' && this.currentStickerRecipientId) {
        var recipient = this.getMultiRecipientById(this.currentStickerRecipientId);
        if (recipient) {
          if (splitLineKey) {
            var rSplitQty = this.getRecipientSplitLineQty(recipient, splitLineKey) || 1;
            if (stickerCount < rSplitQty) {
              this.splitExistingRecipientSplitLine(recipient, splitLineKey, null, ribbonData, stickerCount);
            } else {
              var rSplitSettings = this.getRecipientSplitLineSettings(recipient, splitLineKey);
              if (rSplitSettings) {
                rSplitSettings.ribbon = ribbonData;
                rSplitSettings.noshi = null;
                rSplitSettings.wrapOnly = false;
                rSplitSettings.noWrap = false;
              }
            }
          } else if (stickerCount < totalQty) {
            this.createRecipientSplitLinesFromSticker(recipient, itemKey, totalQty, ribbonData, stickerCount);
          } else {
            var s = this.getRecipientItemSettings(recipient, itemKey);
            s.ribbon = ribbonData;
            s.noshi = null; s.wrapOnly = false; s.noWrap = false;
            if (recipient.splitLines) delete recipient.splitLines[itemKey];
          }
        }
      } else if (this.currentMode === 'single' && splitLineKey) {
        var splitQtyForSave = this.getSingleSplitLineQty(splitLineKey) || 1;
        if (stickerCount < splitQtyForSave) {
          this.splitExistingSplitLine(splitLineKey, null, ribbonData, stickerCount);
        } else {
          var splitSettings = this.getSingleSplitLineSettings(splitLineKey);
          if (splitSettings) {
            splitSettings.ribbon = ribbonData;
            splitSettings.noshi = null;
            splitSettings.wrapOnly = false;
            splitSettings.noWrap = false;
          }
        }
      } else {
        if (this.currentMode === 'single' && stickerCount < totalQty) {
          this.createSingleSplitLinesFromSticker(itemKey, totalQty, ribbonData, stickerCount);
        } else {
          this.stickerSettings[this.currentMode][itemKey] = ribbonData;
          delete this.singleSplitLines[itemKey];
          // Ribbon selected — clear other options
          delete this.noshiSettings[this.currentMode][itemKey];
          delete this.wrapOnlyItems[this.currentMode][itemKey];
          delete this.noWrapItems[this.currentMode][itemKey];
        }
      }

      // Persist and re-render (renderCartItems will set radio to ribbon based on saved data)
      this.saveData();
      this.closeModal();
      this.renderCartItems();
      this.updateSummary();
    },

    /**
     * Restore sticker settings when re-opening modal
     * @param {string} itemKey
     * @param {Object} [savedRibbon] - Pre-resolved saved ribbon data
     */
    restoreStickerSettings: function(itemKey, savedRibbon) {
      var saved = savedRibbon || (this.currentMode === 'multi' && this.currentStickerRecipientId && this.currentStickerSplitLineKey
        ? (function(self) {
            var r = self.getMultiRecipientById(self.currentStickerRecipientId);
            var rss = r ? self.getRecipientSplitLineSettings(r, self.currentStickerSplitLineKey) : null;
            return rss ? rss.ribbon : null;
          })(this)
        : (this.currentMode === 'multi' && this.currentStickerRecipientId
          ? (function(self) {
              var r = self.getMultiRecipientById(self.currentStickerRecipientId);
              return r && r.giftSettings && r.giftSettings[itemKey] ? r.giftSettings[itemKey].ribbon : null;
            })(this)
          : (this.currentMode === 'single' && this.currentStickerSplitLineKey
            ? ((this.getSingleSplitLineSettings(this.currentStickerSplitLineKey) || {}).ribbon || null)
            : this.stickerSettings[this.currentMode][itemKey])));
      if (!saved) return;

      this.currentStickerCategory = saved.category || '';

      // Restore select value in the correct select
      if (saved.gid) {
        var $select = this.getActiveStickerSelect();
        $select.val(saved.gid);
        $('#sticker-metaobject-gid').val(saved.gid);
        if (saved.image) {
          $('[data-sticker-main-preview] img').attr('src', saved.image).show();
        }
        var countItem = this.cartItems.find(function(i) { return i.key === itemKey; });
        var countTotal;
        if (this.currentStickerSplitLineKey) {
          if (this.currentMode === 'multi' && this.currentStickerRecipientId) {
            var _restoreSR = this.getMultiRecipientById(this.currentStickerRecipientId);
            countTotal = _restoreSR ? (this.getRecipientSplitLineQty(_restoreSR, this.currentStickerSplitLineKey) || 1) : 1;
          } else {
            countTotal = this.getSingleSplitLineQty(this.currentStickerSplitLineKey) || 1;
          }
        } else if (this.currentMode === 'multi' && this.currentStickerRecipientId) {
          var _restoreSRecipient = this.getMultiRecipientById(this.currentStickerRecipientId);
          countTotal = (_restoreSRecipient && _restoreSRecipient.items && _restoreSRecipient.items[itemKey] !== undefined)
            ? parseInt(_restoreSRecipient.items[itemKey], 10) || 1
            : (countItem ? countItem.quantity : 1);
        } else {
          countTotal = countItem ? countItem.quantity : 1;
        }
        if (saved.count) {
          $('[data-sticker-count-input]').val(Math.min(saved.count, countTotal));
          this.syncGiftSettingCountControl($('[data-sticker-count-input]'), countTotal);
        } else {
          this.syncGiftSettingCountControl($('[data-sticker-count-input]'));
        }
        // sticker count is always visible
        $('[data-save-sticker]').prop('disabled', false);
      }
    },

    /**
     * Render sticker status summary for a cart item
     * @param {string} itemKey
     * @param {Object} [recipient] - Recipient object (multi mode)
     */
    renderStickerStatusSummary: function(itemKey, recipient, options) {
      var opts = options || {};
      var splitLineKey = opts.splitLineKey || null;
      var saved;
      if (splitLineKey && this.currentMode === 'single') {
        var splitSettings = this.getSingleSplitLineSettings(splitLineKey) || {};
        saved = splitSettings.ribbon || null;
      } else if (splitLineKey && this.currentMode === 'multi' && recipient) {
        var rSplitStickerSummary = this.getRecipientSplitLineSettings(recipient, splitLineKey) || {};
        saved = rSplitStickerSummary.ribbon || null;
      } else if (this.currentMode === 'multi' && recipient) {
        saved = (recipient.giftSettings && recipient.giftSettings[itemKey] && recipient.giftSettings[itemKey].ribbon) || null;
      } else {
        saved = this.stickerSettings[this.currentMode][itemKey];
      }
      if (!saved) return '';

      var recipientAttr = (this.currentMode === 'multi' && recipient) ? ' data-recipient-id="' + recipient.id + '"' : '';
      var splitAttr = splitLineKey ? (' data-split-line-key="' + splitLineKey + '" data-source-item-key="' + itemKey + '"') : '';
      var html = '<div class="p-fee u-mb-15 u-mt-15 sticker-status" data-sticker-status="' + itemKey + '">';
      html += '<div class="p-fee__head">';
      html += '<h4 class="p-fee__title">リボン設定</h4>';
      html += '<a href="#" class="p-fee__edit" data-open-sticker="' + itemKey + '"' + recipientAttr + splitAttr + '>変更</a>';
      html += '</div>';
      html += '<ul class="p-fee__list">';
      html += '<li class="p-fee__row"><span class="p-fee__label">種類</span><span>' + this.escapeHtml(saved.category || '') + '</span></li>';
      html += '<li class="p-fee__row"><span class="p-fee__label">お名前</span><span>' + this.escapeHtml(saved.name || '') + '</span></li>';
      html += '</ul>';
      html += '</div>';
      return html;
    },

    // ── DEBUG ────────────────────────────────────────────────────────────────
    /**
     * Initialize the floating debug panel.
     *
     * How to enable:
     *   • URL param : ?shippingDebug=1
     *   • Persistent: localStorage.setItem('shippingDebug', '1') then reload
     *
     * Runtime toggle (browser console):
     *   window.toggleShippingDebug()
     */
    debugInit: function() {
      if (!SHIPPING_DEBUG) return;

      var panelHtml =
        '<div id="shipping-debug-panel" style="' +
          'position:fixed;bottom:0;right:0;width:440px;max-height:55vh;' +
          'background:#1e1e1e;color:#d4d4d4;font-family:monospace;font-size:11px;' +
          'z-index:99999;border-top-left-radius:6px;overflow:hidden;' +
          'display:flex;flex-direction:column;box-shadow:0 -2px 16px rgba(0,0,0,.6);">' +
          '<div id="shipping-debug-header" style="' +
            'background:#252526;padding:5px 10px;display:flex;align-items:center;' +
            'justify-content:space-between;flex-shrink:0;border-bottom:1px solid #3e3e42;">' +
            '<span style="font-weight:bold;color:#9cdcfe;letter-spacing:.04em;">' +
              '&#128270; ShippingApp Debug</span>' +
            '<span style="color:#6a9955;font-size:10px;margin-left:8px;">' +
              'disable: localStorage.removeItem(\'shippingDebug\') + reload</span>' +
            '<div style="margin-left:auto;display:flex;gap:4px;">' +
              '<button id="shipping-debug-refresh" title="Refresh state" style="' +
                'background:none;border:1px solid #555;color:#ccc;' +
                'cursor:pointer;padding:1px 7px;border-radius:3px;font-size:10px;">&#8635;</button>' +
              '<button id="shipping-debug-clear-cache" title="Clear all shipping_* localStorage + in-memory caches, then reload" style="' +
                'background:none;border:1px solid #c0392b;color:#e74c3c;' +
                'cursor:pointer;padding:1px 7px;border-radius:3px;font-size:10px;">&#128465; Clear Cache</button>' +
              '<button id="shipping-debug-minimize" title="Minimize" style="' +
                'background:none;border:1px solid #555;color:#ccc;' +
                'cursor:pointer;padding:1px 7px;border-radius:3px;font-size:10px;">&#8210;</button>' +
              '<button id="shipping-debug-close" title="Close" style="' +
                'background:none;border:1px solid #555;color:#f44;' +
                'cursor:pointer;padding:1px 7px;border-radius:3px;font-size:10px;">&#10005;</button>' +
            '</div>' +
          '</div>' +
          '<div id="shipping-debug-body" style="overflow-y:auto;flex:1;padding:8px 10px;">' +
            '<pre id="shipping-debug-pre" style="margin:0;white-space:pre-wrap;word-break:break-all;line-height:1.5;"></pre>' +
          '</div>' +
        '</div>';

      $('body').append(panelHtml);

      var self = this;

      $('#shipping-debug-close').on('click', function() {
        $('#shipping-debug-panel').remove();
      });

      $('#shipping-debug-refresh').on('click', function() {
        self.debugRefresh();
      });

      $('#shipping-debug-clear-cache').on('click', function() {
        self.debugClearCache();
      });

      var minimized = false;
      $('#shipping-debug-minimize').on('click', function() {
        minimized = !minimized;
        $('#shipping-debug-body').toggle(!minimized);
        $(this).html(minimized ? '&#9633;' : '&#8210;');
      });

      this.debugRefresh();

      // Click on product title → show item + metafield info dialog
      var self = this;
      $(document).on('click.shippingDebug', '.cart-item__title', function() {
        var $wrap = $(this).closest('[data-item-key]');
        var itemKey = $wrap.data('source-item-key') || $wrap.data('item-key');
        if (itemKey) self.debugShowItemInfo(String(itemKey));
      });
      // Visual hint
      $('<style id="shipping-debug-item-hint">.cart-item__title{cursor:pointer;outline:1px dashed #569cd6;outline-offset:2px;}</style>').appendTo('head');
    },

    /**
     * Refresh the debug panel with the current ShippingApp state snapshot.
     * Called automatically after render(); also triggered by the ↺ button.
     */
    debugRefresh: function() {
      var $pre = $('#shipping-debug-pre');
      if (!$pre.length) return;

      var state = {
        currentMode: this.currentMode,
        isMultiAddress: this.isMultiAddress,
        sender: this.sender,
        orderer: this.orderer,
        singleRecipient: this.singleRecipient,
        multiRecipients: this.multiRecipients,
        deliverySettings: this.deliverySettings,
        deliveryOptions: this.deliveryOptions,
        deliveryOptionsMulti: this.deliveryOptionsMulti,
        shippingRatesCache: this.shippingRatesCache,
        itemGiftOptions: this.itemGiftOptions,
        noshiSettings: this.noshiSettings,
        singleSplitLines: this.singleSplitLines
      };

      $pre.text(JSON.stringify(state, null, 2));
    },

    /**
     * Show a temporary toast notification (debug only).
     * @param {string} msg
     */
    debugNotify: function(msg) {
      if (!SHIPPING_DEBUG) return;


      var $toast = $('<div style="' +
        'position:fixed;top:20px;left:50%;transform:translateX(-50%);' +
        'background:#1b4332;border:1px solid #2d6a4f;color:#d8f3dc;' +
        'padding:10px 20px;border-radius:6px;font-size:13px;font-family:monospace;' +
        'z-index:100000;box-shadow:0 4px 12px rgba(0,0,0,.4);' +
        'max-width:80vw;word-break:break-all;pointer-events:none;">' +
        '[Debug] ' + msg +
        '</div>');

      $('body').append($toast);
      setTimeout(function() { $toast.remove(); }, 4000);
    },

    /**
     * Clear all shipping-related localStorage keys and in-memory caches, then reload.
     * Useful for resetting leftover address/session data during debugging.
     */
    debugClearCache: function() {
      var LS_KEYS = [
        'shipping_cart_fingerprint',
        'shipping_sender',
        'shipping_orderer',
        'shipping_single_recipient',
        'shipping_multi_recipients',
        'shipping_addresses',
        'shipping_gift_options',
        'shipping_noshi_settings',
        'shipping_sticker_settings',
        'shipping_wraponly_items',
        'shipping_nowrap_items',
        'shipping_single_split_lines',
        'shipping_recipients'
      ];

      LS_KEYS.forEach(function(key) {
        localStorage.removeItem(key);
      });

      // In-memory caches
      this.deliveryOptionsCache = {};
      this.shippingRatesCache = {};
      this.savedAddresses = [];
      this.customerAddresses = [];

      location.reload();
    },

    /**
     * Show a dialog with cart item info + metafield values for the given item key.
     * Triggered by clicking .cart-item__title when SHIPPING_DEBUG is enabled.
     * @param {string} itemKey
     */
    debugShowItemInfo: function(itemKey) {
      if (!SHIPPING_DEBUG) return;

      var item = (this.cartItems || []).find(function(i) { return i.key === itemKey; });
      var metafields = (window.cartItemsMetafields || {})[itemKey] || null;

      var info = {
        '--- cart item ---': '---',
        key: itemKey,
        product_id: item ? item.product_id : '(not found)',
        variant_id: item ? item.variant_id : '(not found)',
        product_title: item ? item.product_title : '(not found)',
        variant_title: item ? item.variant_title : '(not found)',
        product_type: item ? item.product_type : '(not found)',
        tags: item ? item.tags : '(not found)',
        quantity: item ? item.quantity : '(not found)',
        price: item ? item.price : '(not found)',
        '--- metafields ---': '---',
        metafields: metafields || '(none — key not in window.cartItemsMetafields)'
      };

      var json = JSON.stringify(info, null, 2);

      // Remove existing dialog if any
      $('#shipping-debug-item-dialog').remove();

      var $overlay = $('<div id="shipping-debug-item-dialog" style="' +
        'position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:100001;' +
        'display:flex;align-items:center;justify-content:center;padding:16px;box-sizing:border-box;">');

      var $box = $('<div style="' +
        'background:#1e1e1e;color:#d4d4d4;font-family:monospace;font-size:12px;' +
        'border-radius:8px;box-shadow:0 4px 32px rgba(0,0,0,.8);' +
        'width:100%;max-width:560px;max-height:80vh;display:flex;flex-direction:column;overflow:hidden;">');

      var title = item ? item.product_title : itemKey;
      $box.append(
        '<div style="background:#252526;padding:8px 14px;display:flex;justify-content:space-between;' +
        'align-items:center;flex-shrink:0;border-bottom:1px solid #3e3e42;">' +
          '<span style="color:#9cdcfe;font-weight:bold;">&#128270; ' + this.escapeHtml(title) + '</span>' +
          '<button id="shipping-debug-item-close" style="background:none;border:1px solid #555;' +
          'color:#f44;cursor:pointer;padding:1px 8px;border-radius:3px;font-size:12px;">✕</button>' +
        '</div>'
      );
      $box.append(
        '<div style="overflow-y:auto;flex:1;padding:12px 14px;">' +
          '<pre style="margin:0;white-space:pre-wrap;word-break:break-all;line-height:1.6;">' +
          this.escapeHtml(json) +
          '</pre></div>'
      );

      $overlay.append($box);
      $('body').append($overlay);

      // Close handlers
      $('#shipping-debug-item-close, #shipping-debug-item-dialog').on('click', function(e) {
        if (e.target === this) $('#shipping-debug-item-dialog').remove();
      });
      $box.on('click', function(e) { e.stopPropagation(); });
    }
    // ── END DEBUG ─────────────────────────────────────────────────────────────
  };

  // Initialize when DOM is ready
  $(function() {
    ShippingApp.init();
  });

  // Expose to global scope for debugging
  window.ShippingApp = ShippingApp;

  // Runtime debug toggle (browser console)
  window.toggleShippingDebug = function() {
    var current = localStorage.getItem('shippingDebug') === '1';
    if (current) {
      localStorage.removeItem('shippingDebug');
    } else {
      localStorage.setItem('shippingDebug', '1');
    }
  };

})(jQuery, window);
